import React, { useState, useEffect, useRef } from "react";
import { isPlayBillingAvailable, comprarConGooglePlay, syncPlayEntitlement } from "./playBilling";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

// --- SUPABASE CONFIG ---
const SUPABASE_URL = "https://migootbbyrgarpgjdrma.supabase.co";
const SUPABASE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im1pZ29vdGJieXJnYXJwZ2pkcm1hIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzcwMzI1MTYsImV4cCI6MjA5MjYwODUxNn0.2TVjsuKrL5keMTFXwOtmP3B_rIPF901J0w1oLyOena8";

// Simple Supabase REST client (no SDK needed)
const SB = {
  headers: {
    "Content-Type": "application/json",
    "apikey": SUPABASE_KEY,
    "Authorization": "Bearer " + SUPABASE_KEY,
  },
  
  // Generic fetch wrapper
  fetch: function(path, options) {
    return fetch(SUPABASE_URL + "/rest/v1/" + path, {
      ...options,
      headers: { ...SB.headers, ...(options && options.headers || {}) }
    }).then(function(r) { return r.json(); });
  },

  // SELECT
  select: function(table, query) {
    return SB.fetch(table + "?" + (query || ""), { method: "GET" });
  },

  // INSERT
  insert: function(table, data) {
    return fetch(SUPABASE_URL + "/rest/v1/" + table, {
      method: "POST",
      headers: { ...SB.headers, "Prefer": "return=representation" },
      body: JSON.stringify(data)
    }).then(function(r) { return r.json(); });
  },

  // UPSERT
  upsert: function(table, data) {
    return fetch(SUPABASE_URL + "/rest/v1/" + table, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "apikey": SUPABASE_KEY,
        "Authorization": "Bearer " + SUPABASE_KEY,
        "Prefer": "resolution=merge-duplicates,return=minimal"
      },
      body: JSON.stringify(data)
    }).then(function(r) {
      if (!r.ok) { return r.text().then(function(t) { console.error("SB upsert error:", t); return null; }); }
      return r.status === 204 ? {} : r.json().catch(function() { return {}; });
    });
  },

  // UPDATE
  update: function(table, match, data) {
    var query = Object.entries(match).map(function(e) { return e[0] + "=eq." + encodeURIComponent(e[1]); }).join("&");
    return fetch(SUPABASE_URL + "/rest/v1/" + table + "?" + query, {
      method: "PATCH",
      headers: { ...SB.headers, "Prefer": "return=representation" },
      body: JSON.stringify(data)
    }).then(function(r) {
      if (!r.ok) { return r.text().then(function(t) { console.error("SB update error:", t); return { error: t }; }); }
      return r.json().catch(function() { return {}; });
    });
  },

  // DELETE
  delete: function(table, match) {
    var query = Object.entries(match).map(function(e) { return e[0] + "=eq." + encodeURIComponent(e[1]); }).join("&");
    return fetch(SUPABASE_URL + "/rest/v1/" + table + "?" + query, {
      method: "DELETE",
      headers: SB.headers
    }).then(function(r) {
      if (!r.ok) { r.text().then(function(t) { console.error("SB delete error:", t); }); }
      return r.ok;
    });
  },

  // AUTH - Register
  signUp: function(email, password, metadata) {
    return fetch(SUPABASE_URL + "/auth/v1/signup", {
      method: "POST",
      headers: SB.headers,
      body: JSON.stringify({ email: email, password: password, data: metadata })
    }).then(function(r) { return r.json(); });
  },

  // AUTH - Login
  signIn: function(email, password) {
    return fetch(SUPABASE_URL + "/auth/v1/token?grant_type=password", {
      method: "POST",
      headers: SB.headers,
      body: JSON.stringify({ email: email, password: password })
    }).then(function(r) { return r.json(); });
  },

  // AUTH - reset password
  resetPassword: function(email) {
    return fetch(SUPABASE_URL + "/auth/v1/recover", {
      method: "POST",
      headers: SB.headers,
      body: JSON.stringify({ email: email })
    }).then(function(r) { return r.json(); });
  },

  // AUTH - with user token
  withToken: function(token) {
    return {
      ...SB,
      headers: { ...SB.headers, "Authorization": "Bearer " + token }
    };
  }
};

// --- STRIPE CONFIG ---
const STRIPE_PK = "pk_live_51TPn3JBJkSpNMW99FBV3ySCAybg77KNMdFLglzMZwUFSTYazTXvO5jpcwCze9tWv0VqQsHxlKnG2xwdHit73I3K600vVSVXmpD";
const APP_URL = "https://pollo-express-pwa.vercel.app";
const TRIAL_DAYS = 7;

function checkSubscription(email, callback) {
  fetch("/api/check-subscription", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email: email })
  }).then(function(r) { return r.json(); })
  .then(function(data) { callback(null, data); })
  .catch(function(e) { callback(e, null); });
}

// Verifica el acceso: dentro de la app de Android, primero sincroniza con Google Play
// (para detectar cancelaciones), y LUEGO revisa la suscripción. En web va directo.
function verificarAcceso(email, callback) {
  if (isPlayBillingAvailable()) {
    syncPlayEntitlement(email).then(
      function() { checkSubscription(email, callback); },
      function() { checkSubscription(email, callback); }
    );
  } else {
    checkSubscription(email, callback);
  }
}

function startCheckout(email, nombre, plan) {
  fetch("/api/create-checkout", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email: email, nombre: nombre, plan: plan || "monthly" })
  }).then(function(r) { return r.json(); })
  .then(function(data) {
    if (data.url) { window.location.href = data.url; }
    else { alert("Error al iniciar el pago. Intenta de nuevo."); }
  })
  .catch(function() { alert("Error de conexión. Verifica tu internet."); });
}



// --- TEMAS DE COLOR ---
const TEMAS = {
  gris:      { bg: "#1e1e24", surface: "#26262e", card: "#2e2e38", border: "#44444f", text: "#f0f0f5", textMuted: "#b0b0c0", textFaint: "#888898" },
  marino:    { bg: "#0d1117", surface: "#161b22", card: "#1c2128", border: "#30363d", text: "#f0f0f5", textMuted: "#b0b0c0", textFaint: "#888898" },
  verde:     { bg: "#0d1a0f", surface: "#122014", card: "#162818", border: "#2a4a2e", text: "#f0f0f5", textMuted: "#b0b8b0", textFaint: "#7a9a80" },
  azul:      { bg: "#0f1923", surface: "#16232f", card: "#1c2d3b", border: "#2a3f52", text: "#f0f0f5", textMuted: "#a0b8c8", textFaint: "#6a8898" },
  marron:    { bg: "#1a0a00", surface: "#231100", card: "#2c1500", border: "#5a2800", text: "#fff8ee", textMuted: "#c49a60", textFaint: "#c49a60" },
  negro:     { bg: "#0a0a0a", surface: "#111111", card: "#191919", border: "#2a2a2a", text: "#f5f5f5", textMuted: "#aaaaaa", textFaint: "#666666" },
  blanco:    { bg: "#f0f0f0", surface: "#ffffff", card: "#e8e8e8", border: "#cccccc", text: "#1a1a1a", textMuted: "#555555", textFaint: "#888888" },
  amarillo:  { bg: "#fdf8e1", surface: "#fffde7", card: "#fff9c4", border: "#f9e04b", text: "#2a2000", textMuted: "#7a6000", textFaint: "#b09020" },
  naranja:   { bg: "#fff3e0", surface: "#ffe0b2", card: "#ffcc80", border: "#ffa726", text: "#2a1000", textMuted: "#7a3000", textFaint: "#bf5000" },
  cafetinto: { bg: "#2c1810", surface: "#3d2318", card: "#4e2e20", border: "#7a4a30", text: "#fdf0e0", textMuted: "#d4a078", textFaint: "#a07050" },
};

const C_BASE = {
  accent: "#f5c200", accentDim: "#b88e00",
  warn: "#ff6b1a", danger: "#cc1a00",
  red: "#e8200a", green: "#4caf50",
};

// Default C (overridden inside MainApp with reactive theme)
const C = { ...C_BASE, ...TEMAS.gris };

const fonts = `@import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=Space+Mono:wght@400;700&display=swap');`;

// --- LA RECETA SECRETA DE ANDRÉS ---
// Colores: amarillo=agua limpia/betaina, verde=vitaminas/electrolitos, naranja=vacunas/probiotico, rojo=desparasitante
// --- SISTEMA DE TRADUCCIONES (4 idiomas) ---
const T = {
  es: {
    inicio: "Inicio", registro: "Registro", vacunas: "Vacunas", calendarioTab: "Calendario", costos: "Costos", ventas: "Ventas",
    diaActual: "día", pollitosVivos: "pollitos vivos", desde: "desde", galpon: "Galpón",
    hoySugerencia: "HOY", pesoIdealHoy: "PESO IDEAL HOY", programaLuz: "PROGRAMA DE LUZ",
    horasLuz: "h de luz", oscuridad: "h oscuridad", fase: "Fase",
    registroDiario: "Registro Diario", mortalidad: "Mortalidad", pesoMuestra: "Peso muestra",
    estadoCama: "Estado de cama", estadoHeces: "Estado de heces", suplemento: "Suplemento",
    notas: "Notas", guardar: "Guardar", guardado: "¡Guardado!",
    calendarioVacunacion: "Calendario de Vacunación", diaNum: "Día", producto: "Producto",
    funcion: "Función", dosis: "Dosis", via: "Vía", agregar: "Agregar evento",
    editar: "Editar", eliminar: "Eliminar", guardarCambios: "Guardar cambios", cancelar: "Cancelar",
    costosTotales: "Costos Totales", agregarGasto: "Agregar Gasto", descripcion: "Descripción",
    monto: "Monto", fecha: "Fecha", categoria: "Categoría", alimentacion: "Alimentación",
    medicamentos: "Medicamentos", manoObra: "Mano de obra", otros: "Otros",
    registrarVenta: "Registrar Venta", nuevaVenta: "Nueva venta", pollosVendidos: "Pollos vendidos",
    librasTotales: "Libras totales", precioPorLibra: "Precio por libra", comprador: "Comprador",
    guardarVenta: "Guardar venta", historialVentas: "Historial de ventas", factura: "Factura",
    totalIngreso: "Total ingreso", ganancia: "GANANCIA NETA", perdida: "PÉRDIDA NETA",
    ajustes: "Ajustes de la App", idioma: "IDIOMA", moneda: "MONEDA", unidadPeso: "UNIDAD DE PESO",
    zonaHoraria: "ZONA HORARIA", tema: "TEMA DE COLOR", listo: "Listo",
    logoGranja: "LOGO DE TU GRANJA", nombreGranja: "NOMBRE DE TU GRANJA",
    subirLogo: "Subir logo", cambiarLogo: "Cambiar logo",
    misCamadas: "Mis Camadas", crearCamada: "Crear Camada", cerrarCamada: "Cerrar Camada",
    nuevaCamada: "Nueva Camada", nombreCamada: "Nombre de camada", totalPollos: "Total de pollos",
    fechaInicio: "Fecha de inicio", crear: "Crear",
    cargando: "Cargando...", confirmar: "Confirmar",
    propietario: "propietario", empleado: "empleado", cerrarSesion: "Cerrar sesión",
    bienvenido: "¡Bienvenido a Pollo IA!", sinCamadas: "No tienes camadas activas.",
    crearPrimeraCamada: "Crear primera camada",
    // Alimentacion
    sacosTotales: "SACOS TOTALES", librasTotalesAlim: "LIBRAS TOTALES", kilogramosAlim: "KILOGRAMOS",
    pollosAlim: "POLLOS", lbPorPollo: "LB / POLLO", kgPorPollo: "KG / POLLO",
    estandar: "Estándar", sacosde100lb: "Sacos de 100 lb",
    referencia: "Referencia", unSaco: "Un saco = 100 lb = 45.4 kg",
    iniciadorDesc: "Iniciador (D1–10): alimento en polvo fino",
    crecimientoDesc: "Crecimiento (D11–24): pellet mediano",
    engordeDes: "Engorde (D25–35): pellet grueso",
    diasFaseIniciador: "Días 1–10", diasFaseCrecimiento: "Días 11–24", diasFaseEngorde: "Días 25–35",
    sacos: "sacos", sacos100lb: "Sacos 100lb", kilos: "Kilogramos", aves: "aves",
    resumenTotalAlimento: "RESUMEN TOTAL DE ALIMENTO",
    ajustarEstandar: "Ajustar Estándar",
    ajustarEstandarDesc: "Cambia las libras por pollo — el cálculo se actualiza al instante.",
    lbPorAve: "lb / pollo", delTotal: "del total",
    estandarRecomendado: "Recomendado: 7.5 lb/ave (tabla broiler, 35 días)",
    recibidos: "Recibidos", aguaLimpia: "Agua Limpia 💧", enMeta: "En meta",
    verFlecha: "Ver →",
    equipo: "Equipo", historial: "Historial", alimentacionMenu: "Alimentación",
    analisisHeces: "Análisis de Heces IA", analisisCamada: "Análisis IA Camada",
    configuracion: "Configuración", diasRestantes: "días restantes",
    engorde: "ENGORDE", crecimiento: "CRECIMIENTO", iniciador: "INICIADOR",
    diasCompletado: "% completado", vivosActuales: "vivos actuales",
    mortalidadAcum: "mortalidad acumulada", pesoEsperado: "Peso esperado",
    registroHoy: "Registro de hoy", sinRegistro: "Sin registro hoy",
    nuevoCampo: "Nueva Camada", galponLabel: "Galpón / Nave",
    cantidadPollos: "Cantidad de pollitos",
    // Textos adicionales UI
    eligePlan: "Elige tu plan — 7 días gratis incluidos", porMes: "por mes", porAnio: "por año",
    tienesCodigo: "Tengo un código de empleado", tuEmpleadorCodigo: "Tu empleador te comparte un código de 6 letras.",
    personalizaApp: "Personaliza cómo funciona Pollo Express IA",
    apareceFaturas: "Aparecerá en tus facturas. Máx 200KB.",
    sesionActiva: "SESIÓN ACTIVA", menu: "MENÚ",
    bajasDelDia: "Bajas del día", muestraRepresentativa: "Muestra representativa del día",
    notasDelDia: "Notas del Día", quedarian: "QUEDARÍAN",
    proximosEventos: "PRÓXIMOS", ultimosRegistros: "ÚLTIMOS REGISTROS",
    inversionTotal: "INVERSIÓN TOTAL", ingresoTotal: "INGRESO TOTAL",
    observacion: "Observación", accion: "Acción", posibleCausa: "Posible causa",
    tocaNuevafoto: "Toca \"Nueva foto\" para analizar directamente.",
    codigoAcceso: "CÓDIGO DE ACCESO", comparteEsteCodigo: "Comparte este código",
    guardarPDF: "Guardar como PDF", nombGalpon: "Nombre del Galpón",
    historialRegistros: "Historial de Registros", historialCamadas: "Historial de Camadas", miCuenta: "Mi Cuenta",
    ingresoBruto: "Ingreso bruto", totalInvertido: "Total invertido", gananciaNeta: "Ganancia neta",
    agregarGasto: "Agregar gasto", librasTotales: "Libras totales",
    resumenVentas: "Resumen de ventas", pollosVendidos: "Pollos vendidos",
    cuantosPollosMurieron: "¿Cuántos pollos murieron hoy?",
    sinVentas: "Sin ventas registradas. Toca '+ Nueva venta' para empezar.",
    llenarDatos: "Llenar datos de hoy", guardarRegistro: "Guardar registro",
    resumenVentas: "Resumen de ventas", pollosVendidosTotal: "Pollos vendidos",
    estadoFinanciero: "Estado Financiero", ventasDelDia: "Ventas del día",
    pesoPromedio: "Peso promedio", vivos: "vivos", muertos: "bajas",
    cantidadAves: "Cantidad de aves", custom: "Personalizado",
    precioCustom: "Ingresa precio por lb",
  },
  en: {
    inicio: "Home", registro: "Daily Log", vacunas: "Vaccines", calendarioTab: "Calendar", costos: "Costs", ventas: "Sales",
    diaActual: "day", pollitosVivos: "live chicks", desde: "since", galpon: "Barn",
    hoySugerencia: "TODAY", pesoIdealHoy: "IDEAL WEIGHT TODAY", programaLuz: "LIGHT PROGRAM",
    horasLuz: "h of light", oscuridad: "h darkness", fase: "Phase",
    registroDiario: "Daily Record", mortalidad: "Mortality", pesoMuestra: "Sample weight",
    estadoCama: "Litter condition", estadoHeces: "Droppings condition", suplemento: "Supplement",
    notas: "Notes", guardar: "Save", guardado: "Saved!",
    calendarioVacunacion: "Vaccination Calendar", diaNum: "Day", producto: "Product",
    funcion: "Function", dosis: "Dose", via: "Route", agregar: "Add event",
    editar: "Edit", eliminar: "Delete", guardarCambios: "Save changes", cancelar: "Cancel",
    costosTotales: "Total Costs", agregarGasto: "Add Expense", descripcion: "Description",
    monto: "Amount", fecha: "Date", categoria: "Category", alimentacion: "Feed",
    medicamentos: "Medications", manoObra: "Labor", otros: "Other",
    registrarVenta: "Register Sale", nuevaVenta: "New sale", pollosVendidos: "Chickens sold",
    librasTotales: "Total pounds", precioPorLibra: "Price per pound", comprador: "Buyer",
    guardarVenta: "Save sale", historialVentas: "Sales history", factura: "Invoice",
    totalIngreso: "Total income", ganancia: "NET PROFIT", perdida: "NET LOSS",
    ajustes: "App Settings", idioma: "LANGUAGE", moneda: "CURRENCY", unidadPeso: "WEIGHT UNIT",
    zonaHoraria: "TIME ZONE", tema: "COLOR THEME", listo: "Done",
    logoGranja: "YOUR FARM LOGO", nombreGranja: "YOUR FARM NAME",
    subirLogo: "Upload logo", cambiarLogo: "Change logo",
    misCamadas: "My Flocks", crearCamada: "Create Flock", cerrarCamada: "Close Flock",
    nuevaCamada: "New Flock", nombreCamada: "Flock name", totalPollos: "Total chickens",
    fechaInicio: "Start date", crear: "Create",
    cargando: "Loading...", confirmar: "Confirm",
    propietario: "owner", empleado: "employee", cerrarSesion: "Sign out",
    bienvenido: "Welcome to Pollo IA!", sinCamadas: "No active flocks.",
    crearPrimeraCamada: "Create first flock",
    limiteFlocks: "Limit of {n} active flocks reached. Close one to create a new one.",
    historialCamadas: "Flock History", analisisIACamada: "AI Flock Analysis", cerrarCamadaMenu: "Close Flock",
    cerrarCamadaDesc: "This flock will be archived. The next flock will open automatically.",
    sinHistorial: "When you close a flock it will appear here.",
    sinRegistrosEnCamada: "No records in this flock yet.",
    // Alimentacion
    sacosTotales: "TOTAL BAGS", librasTotalesAlim: "TOTAL POUNDS", kilogramosAlim: "KILOGRAMS",
    pollosAlim: "CHICKENS", lbPorPollo: "LB / BIRD", kgPorPollo: "KG / BIRD",
    estandar: "Standard", sacosde100lb: "100 lb bags",
    referencia: "Reference", unSaco: "One bag = 100 lb = 45.4 kg",
    iniciadorDesc: "Starter (D1–10): fine mash feed",
    crecimientoDesc: "Grower (D11–24): medium pellet",
    engordeDes: "Finisher (D25–35): coarse pellet",
    diasFaseIniciador: "Days 1–10", diasFaseCrecimiento: "Days 11–24", diasFaseEngorde: "Days 25–35",
    sacos: "bags", sacos100lb: "Bags 100lb", kilos: "Kilograms", aves: "birds",
    resumenTotalAlimento: "TOTAL FEED SUMMARY",
    ajustarEstandar: "Adjust Standard",
    ajustarEstandarDesc: "Change pounds per bird — the calculation updates instantly.",
    lbPorAve: "lb / bird", delTotal: "of total",
    estandarRecomendado: "Recommended: 7.5 lb/bird (broiler table, 35 days)",
    recibidos: "Received", aguaLimpia: "Clean Water 💧", enMeta: "On target",
    verFlecha: "View →",
    equipo: "Team", historial: "History", alimentacionMenu: "Feed Program",
    analisisHeces: "Droppings AI Analysis", analisisCamada: "Flock AI Analysis",
    configuracion: "Settings", diasRestantes: "days remaining",
    engorde: "FINISHING", crecimiento: "GROWING", iniciador: "STARTER",
    diasCompletado: "% complete", vivosActuales: "live birds",
    mortalidadAcum: "accumulated mortality", pesoEsperado: "Expected weight",
    registroHoy: "Today's record", sinRegistro: "No record today",
    nuevoCampo: "New Flock", galponLabel: "Barn / House",
    cantidadPollos: "Number of chicks",
    // Additional UI texts
    eligePlan: "Choose your plan — 7-day free trial included", porMes: "per month", porAnio: "per year",
    tienesCodigo: "I have an employee code", tuEmpleadorCodigo: "Your employer shares a 6-letter code with you.",
    personalizaApp: "Customize how Pollo Express IA works",
    apareceFaturas: "Will appear on your invoices. Max 200KB.",
    sesionActiva: "ACTIVE SESSION", menu: "MENU",
    bajasDelDia: "Daily losses", muestraRepresentativa: "Representative sample for the day",
    notasDelDia: "Day Notes", quedarian: "REMAINING",
    proximosEventos: "UPCOMING", ultimosRegistros: "LATEST RECORDS",
    inversionTotal: "TOTAL INVESTMENT", ingresoTotal: "TOTAL INCOME",
    observacion: "Observation", accion: "Action", posibleCausa: "Possible cause",
    tocaNuevafoto: "Tap \"New photo\" to analyze directly.",
    codigoAcceso: "ACCESS CODE", comparteEsteCodigo: "Share this code",
    guardarPDF: "Save as PDF", nombGalpon: "Barn Name",
    historialRegistros: "Records History", miCuenta: "My Account",
    ingresoBruto: "Gross income", totalInvertido: "Total invested", gananciaNeta: "Net profit",
    agregarGasto: "Add expense", ingresoBruto2: "Gross income", librasTotales: "Total pounds",
    resumenVentas: "Sales summary", pollosVendidos: "Chickens sold",
    cuantosPollosMurieron: "How many chickens died today?",
    sinVentas: "No sales recorded. Tap '+ New sale' to start.",
    llenarDatos: "Fill in today's data", guardarRegistro: "Save record",
    resumenVentas: "Sales Summary", pollosVendidosTotal: "Chickens sold",
    estadoFinanciero: "Financial Status", ventasDelDia: "Today's sales",
    pesoPromedio: "Average weight", vivos: "live", muertos: "losses",
    cantidadAves: "Number of birds", custom: "Custom",
    precioCustom: "Enter price per lb",
  },
  pt: {
    inicio: "Início", registro: "Registro", vacunas: "Vacinas", calendarioTab: "Calendário", costos: "Custos", ventas: "Vendas",
    diaActual: "dia", pollitosVivos: "pintinhos vivos", desde: "desde", galpon: "Galpão",
    hoySugerencia: "HOJE", pesoIdealHoy: "PESO IDEAL HOJE", programaLuz: "PROGRAMA DE LUZ",
    horasLuz: "h de luz", oscuridad: "h escuridão", fase: "Fase",
    registroDiario: "Registro Diário", mortalidad: "Mortalidade", pesoMuestra: "Peso amostra",
    estadoCama: "Estado da cama", estadoHeces: "Estado das fezes", suplemento: "Suplemento",
    notas: "Notas", guardar: "Salvar", guardado: "Salvo!",
    calendarioVacunacion: "Calendário de Vacinação", diaNum: "Dia", produto: "Produto",
    funcion: "Função", dosis: "Dose", via: "Via", agregar: "Adicionar evento",
    editar: "Editar", eliminar: "Excluir", guardarCambios: "Salvar alterações", cancelar: "Cancelar",
    costosTotales: "Custos Totais", agregarGasto: "Adicionar Gasto", descripcion: "Descrição",
    monto: "Valor", fecha: "Data", categoria: "Categoria", alimentacion: "Alimentação",
    medicamentos: "Medicamentos", manoObra: "Mão de obra", otros: "Outros",
    registrarVenta: "Registrar Venda", nuevaVenta: "Nova venda", pollosVendidos: "Frangos vendidos",
    librasTotales: "Libras totais", precioPorLibra: "Preço por libra", comprador: "Comprador",
    guardarVenta: "Salvar venda", historialVentas: "Histórico de vendas", historialCamadas: "Histórico de lotes", factura: "Fatura",
    totalIngreso: "Receita total", ganancia: "LUCRO LÍQUIDO", perdida: "PREJUÍZO LÍQUIDO",
    ajustes: "Configurações", idioma: "IDIOMA", moneda: "MOEDA", unidadPeso: "UNIDADE DE PESO",
    zonaHoraria: "FUSO HORÁRIO", tema: "TEMA DE COR", listo: "Pronto",
    logoGranja: "LOGO DA FAZENDA", nombreGranja: "NOME DA FAZENDA",
    subirLogo: "Enviar logo", cambiarLogo: "Mudar logo",
    misCamadas: "Meus Lotes", crearCamada: "Criar Lote", cerrarCamada: "Fechar Lote",
    nuevaCamada: "Novo Lote", nombreCamada: "Nome do lote", totalPollos: "Total de frangos",
    fechaInicio: "Data de início", crear: "Criar",
    cargando: "Carregando...", confirmar: "Confirmar",
    propietario: "proprietário", empleado: "funcionário", cerrarSesion: "Sair",
    bienvenido: "Bem-vindo ao Pollo IA!", sinCamadas: "Sem lotes ativos.",
    crearPrimeraCamada: "Criar primeiro lote",
    // Alimentacion
    sacosTotales: "SACOS TOTAIS", librasTotalesAlim: "LIBRAS TOTAIS", kilogramosAlim: "QUILOGRAMAS",
    pollosAlim: "FRANGOS", lbPorPollo: "LB / AVE", kgPorPollo: "KG / AVE",
    estandar: "Padrão", sacosde100lb: "Sacos de 100 lb",
    referencia: "Referência", unSaco: "Um saco = 100 lb = 45.4 kg",
    iniciadorDesc: "Inicial (D1–10): ração em pó fino",
    crecimientoDesc: "Crescimento (D11–24): pellet médio",
    engordeDes: "Engorda (D25–35): pellet grosso",
    diasFaseIniciador: "Dias 1–10", diasFaseCrecimiento: "Dias 11–24", diasFaseEngorde: "Dias 25–35",
    verFlecha: "Ver →",
    equipo: "Equipe", historial: "Histórico", alimentacionMenu: "Alimentação",
    analisisHeces: "Análise de Fezes IA", analisisCamada: "Análise IA do Lote",
    configuracion: "Configurações", diasRestantes: "dias restantes",
    engorde: "ENGORDA", crecimiento: "CRESCIMENTO", iniciador: "INICIAL",
    diasCompletado: "% concluído", vivosActuales: "aves vivas",
    mortalidadAcum: "mortalidade acumulada", pesoEsperado: "Peso esperado",
    registroHoy: "Registro de hoje", sinRegistro: "Sem registro hoje",
    nuevoCampo: "Novo Lote", galponLabel: "Galpão / Nave",
    cantidadPollos: "Quantidade de pintinhos",
  },
  hi: {
    inicio: "होम", registro: "दैनिक लॉग", vacunas: "टीके", calendarioTab: "कैलेंडर", costos: "लागत", ventas: "बिक्री",
    diaActual: "दिन", pollitosVivos: "जीवित चूजे", desde: "से", galpon: "शेड",
    hoySugerencia: "आज", pesoIdealHoy: "आज का आदर्श वजन", programaLuz: "प्रकाश कार्यक्रम",
    horasLuz: "घंटे प्रकाश", oscuridad: "घंटे अंधेरा", fase: "चरण",
    registroDiario: "दैनिक रिकॉर्ड", mortalidad: "मृत्यु दर", pesoMuestra: "नमूना वजन",
    estadoCama: "बिछावन स्थिति", estadoHeces: "मल स्थिति", suplemento: "पूरक",
    notas: "नोट्स", guardar: "सहेजें", guardado: "सहेजा गया!",
    calendarioVacunacion: "टीकाकरण कैलेंडर", diaNum: "दिन", producto: "उत्पाद",
    funcion: "कार्य", dosis: "खुराक", via: "मार्ग", agregar: "इवेंट जोड़ें",
    editar: "संपादित करें", eliminar: "हटाएं", guardarCambios: "परिवर्तन सहेजें", cancelar: "रद्द करें",
    costosTotales: "कुल लागत", agregarGasto: "खर्च जोड़ें", descripcion: "विवरण",
    monto: "राशि", fecha: "तारीख", categoria: "श्रेणी", alimentacion: "चारा",
    medicamentos: "दवाएं", manoObra: "श्रम", otros: "अन्य",
    registrarVenta: "बिक्री दर्ज करें", nuevaVenta: "नई बिक्री", pollosVendidos: "बेचे गए मुर्गे",
    librasTotales: "कुल पाउंड", precioPorLibra: "प्रति पाउंड कीमत", comprador: "खरीदार",
    guardarVenta: "बिक्री सहेजें", historialVentas: "बिक्री इतिहास", historialCamadas: "झुंड इतिहास", factura: "चालान",
    totalIngreso: "कुल आय", ganancia: "शुद्ध लाभ", perdida: "शुद्ध हानि",
    ajustes: "ऐप सेटिंग्स", idioma: "भाषा", moneda: "मुद्रा", unidadPeso: "वजन इकाई",
    zonaHoraria: "समय क्षेत्र", tema: "रंग थीम", listo: "हो गया",
    logoGranja: "फार्म लोगो", nombreGranja: "फार्म का नाम",
    subirLogo: "लोगो अपलोड करें", cambiarLogo: "लोगो बदलें",
    misCamadas: "मेरे झुंड", crearCamada: "झुंड बनाएं", cerrarCamada: "झुंड बंद करें",
    nuevaCamada: "नया झुंड", nombreCamada: "झुंड का नाम", totalPollos: "कुल मुर्गे",
    fechaInicio: "शुरू की तारीख", crear: "बनाएं",
    cargando: "लोड हो रहा है...", confirmar: "पुष्टि करें",
    propietario: "मालिक", empleado: "कर्मचारी", cerrarSesion: "साइन आउट",
    bienvenido: "Pollo IA में आपका स्वागत है!", sinCamadas: "कोई सक्रिय झुंड नहीं।",
    crearPrimeraCamada: "पहला झुंड बनाएं",
    // Alimentacion
    sacosTotales: "कुल बोरे", librasTotalesAlim: "कुल पाउंड", kilogramosAlim: "किलोग्राम",
    pollosAlim: "मुर्गे", lbPorPollo: "पाउंड / मुर्गा", kgPorPollo: "किग्रा / मुर्गा",
    estandar: "मानक", sacosde100lb: "100 पाउंड के बोरे",
    referencia: "संदर्भ", unSaco: "एक बोरा = 100 पाउंड = 45.4 किग्रा",
    iniciadorDesc: "स्टार्टर (D1–10): बारीक मैश चारा",
    crecimientoDesc: "ग्रोअर (D11–24): मीडियम पेलेट",
    engordeDes: "फिनिशर (D25–35): मोटा पेलेट",
    diasFaseIniciador: "दिन 1–10", diasFaseCrecimiento: "दिन 11–24", diasFaseEngorde: "दिन 25–35",
    verFlecha: "देखें →",
    equipo: "टीम", historial: "इतिहास", alimentacionMenu: "आहार कार्यक्रम",
    analisisHeces: "मल AI विश्लेषण", analisisCamada: "झुंड AI विश्लेषण",
    configuracion: "सेटिंग्स", diasRestantes: "दिन शेष",
    engorde: "फिनिशिंग", crecimiento: "विकास", iniciador: "स्टार्टर",
    diasCompletado: "% पूर्ण", vivosActuales: "जीवित मुर्गे",
    mortalidadAcum: "संचित मृत्यु दर", pesoEsperado: "अपेक्षित वजन",
    registroHoy: "आज का रिकॉर्ड", sinRegistro: "आज कोई रिकॉर्ड नहीं",
    nuevoCampo: "नया झुंड", galponLabel: "शेड / हाउस",
    cantidadPollos: "चूजों की संख्या",
  },
};
function useT(idioma) {
  return function(key) {
    var lang = idioma || "es";
    return (T[lang] && T[lang][key]) ? T[lang][key] : (T["es"] && T["es"][key] ? T["es"][key] : key);
  };
}

// --- TEXTOS DE PANTALLA EN INGLÉS ---
// El propio texto en español es la llave: tr("Guardar venta") → "Save sale" con la app en inglés.
// Si una frase no está en el diccionario, sale el español de siempre: nada se rompe.
// IDIOMA_APP lo fija MainApp en cada dibujo (según Ajustes). Antes de iniciar sesión se usa el
// último idioma elegido en este teléfono. Portugués e hindi: pendientes (sería añadir TR_PT / TR_HI).
let IDIOMA_APP = (function () { try { return localStorage.getItem("pe_idioma") || "es"; } catch (e) { return "es"; } })();
function fijarIdioma(i) {
  var nuevo = i || "es";
  if (nuevo === IDIOMA_APP) return;
  IDIOMA_APP = nuevo;
  try { localStorage.setItem("pe_idioma", nuevo); } catch (e) {}
}
function tr(textoEs, vars) {
  var s = (IDIOMA_APP === "en" && TR_EN[textoEs]) || textoEs;
  if (vars) Object.keys(vars).forEach(function (k) { s = s.split("{" + k + "}").join(vars[k]); });
  return s;
}
const TR_EN = {
  " ✅ META": " ✅ TARGET",
  " camadas...": " flocks...",
  " días": " days",
  " días gratis incluidos": " free days included",
  "— canastas:": "— crates:",
  "¡Contraseña actualizada!": "Password updated!",
  "¡Listo!": "Done!",
  "¿Aplicó algo hoy?": "Did you give anything today?",
  "¿Cuántos pollos murieron hoy?": "How many chickens died today?",
  "¿El galpón": "Is barn",
  "¿Olvidaste tu contraseña?": "Forgot your password?",
  "¿Resetear al calendario original?": "Reset to the original calendar?",
  ". Toca para ver el detalle.": ". Tap to see details.",
  "* Alimento estimado desde los gastos de la categoría \"Alimento\" (saco 100 lb ≈ RD$1,395). Lb producidas = vendidas + carne en pie (último peso × vivos).": "* Feed estimated from expenses in the \"Feed\" category (100 lb bag ≈ RD$1,395). Lb produced = sold + live weight (last weight × live birds).",
  "← Volver": "← Back",
  "↺ Resetear": "↺ Reset",
  "+ Agregar evento": "+ Add event",
  "= neto:": "= net:",
  "⏰ Los avisos llegan a las 7:00 AM y el recordatorio de registro a las 7:00 PM.": "⏰ Alerts arrive at 7:00 AM and the daily log reminder at 7:00 PM.",
  "⚠️ Alto": "⚠️ High",
  "⚠️ Consulta con el veterinario": "⚠️ Consult your veterinarian",
  "🇦🇷 Argentina (GMT-3)": "🇦🇷 Argentina (GMT-3)",
  "🇦🇷 Peso Argentino — $": "🇦🇷 Argentine Peso — $",
  "🇧🇩 Taka Bangladesí — ৳": "🇧🇩 Bangladeshi Taka — ৳",
  "🇧🇴 Boliviano — Bs.": "🇧🇴 Boliviano — Bs.",
  "🇧🇷 Brasil (GMT-3)": "🇧🇷 Brazil (GMT-3)",
  "🇧🇷 Real Brasileño — R$": "🇧🇷 Brazilian Real — R$",
  "🇨🇮 Franco CFA — CFA": "🇨🇮 CFA Franc — CFA",
  "🇨🇱 Chile (GMT-3/-4)": "🇨🇱 Chile (GMT-3/-4)",
  "🇨🇱 Peso Chileno — $": "🇨🇱 Chilean Peso — $",
  "🇨🇲 Franco CFA Central — FCFA": "🇨🇲 Central African CFA Franc — FCFA",
  "🇨🇴 Colombia (GMT-5)": "🇨🇴 Colombia (GMT-5)",
  "🇨🇴 Peso Colombiano — $": "🇨🇴 Colombian Peso — $",
  "🇨🇷 Colón Costarricense — ₡": "🇨🇷 Costa Rican Colón — ₡",
  "🇨🇷 Costa Rica (GMT-6)": "🇨🇷 Costa Rica (GMT-6)",
  "🇨🇺 Peso Cubano — ₱": "🇨🇺 Cuban Peso — ₱",
  "🇩🇴 Peso Dominicano — RD$": "🇩🇴 Dominican Peso — RD$",
  "🇩🇴 Rep. Dominicana (GMT-4)": "🇩🇴 Dominican Rep. (GMT-4)",
  "🇪🇨 Dólar Ecuador — $": "🇪🇨 Ecuador Dollar — $",
  "🇪🇨 Ecuador (GMT-5)": "🇪🇨 Ecuador (GMT-5)",
  "🇪🇬 Libra Egipcia — E£": "🇪🇬 Egyptian Pound — E£",
  "🇪🇸 España (GMT+1/+2)": "🇪🇸 Spain (GMT+1/+2)",
  "🇪🇹 Birr Etíope — Br": "🇪🇹 Ethiopian Birr — Br",
  "🇪🇺 Euro — €": "🇪🇺 Euro — €",
  "🇬🇧 Libra Esterlina — £": "🇬🇧 British Pound — £",
  "🇬🇧 Reino Unido (GMT+0/+1)": "🇬🇧 United Kingdom (GMT+0/+1)",
  "🇬🇭 Cedi Ghanés — ₵": "🇬🇭 Ghanaian Cedi — ₵",
  "🇬🇹 Guatemala (GMT-6)": "🇬🇹 Guatemala (GMT-6)",
  "🇬🇹 Quetzal — Q": "🇬🇹 Quetzal — Q",
  "🇭🇳 Honduras (GMT-6)": "🇭🇳 Honduras (GMT-6)",
  "🇭🇳 Lempira — L": "🇭🇳 Lempira — L",
  "🇭🇹 Gourde Haitiano — G": "🇭🇹 Haitian Gourde — G",
  "🇮🇩 Rupia Indonesia — Rp": "🇮🇩 Indonesian Rupiah — Rp",
  "🇮🇳 Rupia India — ₹": "🇮🇳 Indian Rupee — ₹",
  "🇯🇲 Dólar Jamaiquino — J$": "🇯🇲 Jamaican Dollar — J$",
  "🇰🇪 Chelín Keniano — KSh": "🇰🇪 Kenyan Shilling — KSh",
  "🇱🇰 Rupia Sri Lanka — Rs": "🇱🇰 Sri Lankan Rupee — Rs",
  "🇲🇦 Dírham Marroquí — MAD": "🇲🇦 Moroccan Dirham — MAD",
  "🇲🇽 México (GMT-6)": "🇲🇽 Mexico (GMT-6)",
  "🇲🇽 Peso Mexicano — $": "🇲🇽 Mexican Peso — $",
  "🇳🇬 Naira Nigeriana — ₦": "🇳🇬 Nigerian Naira — ₦",
  "🇳🇮 Córdoba — C$": "🇳🇮 Córdoba — C$",
  "🇳🇮 Nicaragua (GMT-6)": "🇳🇮 Nicaragua (GMT-6)",
  "🇵🇦 Balboa Panameño — B/.": "🇵🇦 Panamanian Balboa — B/.",
  "🇵🇦 Panamá (GMT-5)": "🇵🇦 Panama (GMT-5)",
  "🇵🇪 Perú (GMT-5)": "🇵🇪 Peru (GMT-5)",
  "🇵🇪 Sol Peruano — S/": "🇵🇪 Peruvian Sol — S/",
  "🇵🇭 Peso Filipino — ₱": "🇵🇭 Philippine Peso — ₱",
  "🇵🇰 Rupia Pakistaní — ₨": "🇵🇰 Pakistani Rupee — ₨",
  "🇵🇾 Guaraní — ₲": "🇵🇾 Guaraní — ₲",
  "🇸🇻 Dólar El Salvador — $": "🇸🇻 El Salvador Dollar — $",
  "🇸🇻 El Salvador (GMT-6)": "🇸🇻 El Salvador (GMT-6)",
  "🇹🇹 Dólar Trinidad — TT$": "🇹🇹 Trinidad Dollar — TT$",
  "🇹🇿 Chelín Tanzano — TSh": "🇹🇿 Tanzanian Shilling — TSh",
  "🇺🇬 Chelín Ugandés — USh": "🇺🇬 Ugandan Shilling — USh",
  "🇺🇸 Centro USA (GMT-5/-6)": "🇺🇸 US Central (GMT-5/-6)",
  "🇺🇸 Dólar USA — $": "🇺🇸 US Dollar — $",
  "🇺🇸 Este USA (GMT-4/-5)": "🇺🇸 US Eastern (GMT-4/-5)",
  "🇺🇸 Pacífico USA (GMT-7/-8)": "🇺🇸 US Pacific (GMT-7/-8)",
  "🇻🇪 Bolívar — Bs.": "🇻🇪 Bolívar — Bs.",
  "🇻🇪 Venezuela (GMT-4)": "🇻🇪 Venezuela (GMT-4)",
  "🇻🇳 Dong Vietnamita — ₫": "🇻🇳 Vietnamese Dong — ₫",
  "✅ Calendario guardado": "✅ Calendar saved",
  "✅ Excelente": "✅ Excellent",
  "✅ GANANCIA NETA": "✅ NET PROFIT",
  "✅ Guardar": "✅ Save",
  "✅ Te enviamos un link de recuperación a tu correo.": "✅ We sent a recovery link to your email.",
  "✅ Te reenviamos el correo de confirmación. Toca el enlace y vuelve a entrar.": "✅ We resent the confirmation email. Tap the link and sign in again.",
  "✓ Activadas": "✓ Enabled",
  "✓ Guardado": "✓ Saved",
  "✕ Cancelar": "✕ Cancel",
  "❌ PÉRDIDA NETA": "❌ NET LOSS",
  "🌍 África": "🌍 Africa",
  "🌎 El Caribe y Centroamérica": "🌎 Caribbean and Central America",
  "🌎 Sudamérica": "🌎 South America",
  "🌏 Asia": "🌏 Asia",
  "🌐 Otras": "🌐 Other",
  "🎁 Comenzar prueba gratis de ": "🎁 Start free trial of ",
  "🎁 Prueba gratis": "🎁 Free trial",
  "🎟️ Tengo un código promocional": "🎟️ I have a promo code",
  "🐔 AVANCE DE VENTA": "🐔 SALES PROGRESS",
  "🐔 RESUMEN DE VENTAS": "🐔 SALES SUMMARY",
  "👍 Bueno": "👍 Good",
  "👑 PANEL DEL DUEÑO DE LA APP": "👑 APP OWNER PANEL",
  "💡 Puedes usar cualquier número de día (40, 45, 50…) para adaptar el calendario a la duración de tu ciclo.": "💡 You can use any day number (40, 45, 50…) to fit the calendar to the length of your cycle.",
  "💰 ESTADO FINANCIERO": "💰 FINANCIAL STATUS",
  "💾 Guardar calendario": "💾 Save calendar",
  "📅 Duración de la prueba": "📅 Trial length",
  "📈 EFICIENCIA": "📈 EFFICIENCY",
  "📊 Reporte General Financiero": "📊 General Financial Report",
  "📋 Info de la Camada": "📋 Flock Info",
  "📋 Registro Diario": "📋 Daily Log",
  "📡 Ambiente ·": "📡 Environment ·",
  "📡 SENSOR AMBIENTAL": "📡 ENVIRONMENT SENSOR",
  "📦 Gastos": "📦 Expenses",
  "📧 Reenviar correo de confirmación": "📧 Resend confirmation email",
  "📲 Guardar y enviar por WhatsApp": "📲 Save and send via WhatsApp",
  "📲 Guardar y generar factura WhatsApp": "📲 Save and create WhatsApp invoice",
  "📸 Foto adjunta al registro": "📸 Photo attached to the record",
  "🔔 NOTIFICACIONES": "🔔 NOTIFICATIONS",
  "🖨️ Reporte PDF de Costos": "🖨️ Costs PDF Report",
  "🖨️ Reporte PDF de Ventas": "🖨️ Sales PDF Report",
  "🧺 Lb brutas:": "🧺 Gross lb:",
  "🧾 Factura": "🧾 Invoice",
  "$9.99/mes": "$9.99/month",
  "$99.99/año": "$99.99/year",
  "Activando...": "Activating...",
  "Activar": "Enable",
  "Agregar gasto": "Add expense",
  "AHORRA 17%": "SAVE 17%",
  "al día del último pesaje": "as of the last weigh-in",
  "Andrés Méndez": "John Smith",
  "Aparecerá en tus facturas. Máx 200KB.": "Will appear on your invoices. Max 200KB.",
  "aves": "birds",
  "aves ·": "birds ·",
  "aves vivas": "live birds",
  "Avisos de la granja": "Farm alerts",
  "Bajas del día": "Daily losses",
  "Calibrar sensor de amoniaco": "Calibrate ammonia sensor",
  "Cama:": "Litter:",
  "Cancelar": "Cancel",
  "Canjear código": "Redeem code",
  "Cantidad de pollos vendidos": "Number of chickens sold",
  "Cargando…": "Loading…",
  "Categoría": "Category",
  "Centro de Salud Avicola": "Poultry Health Center",
  "Cerrar sesión": "Sign out",
  "Código de Empleado": "Employee Code",
  "Código no válido.": "Invalid code.",
  "Código no válido. Verifica con tu jefe.": "Invalid code. Check with your boss.",
  "Completa todos los campos": "Please fill in all fields",
  "Comprador / referencia (opcional)": "Buyer / reference (optional)",
  "Comprobando...": "Checking...",
  "Conectar un Pollo Box": "Connect a Pollo Box",
  "Confirmar contraseña": "Confirm password",
  "Contraseña": "Password",
  "Contraseña mínimo 6 caracteres": "Password must be at least 6 characters",
  "Correo electrónico": "Email",
  "Correo o contraseña incorrectos. Si eres empleado, entra con \"Tengo un código de empleado\".": "Incorrect email or password. If you're an employee, sign in with \"I have an employee code\".",
  "COSTO POR LIBRA": "COST PER POUND",
  "Crear Cuenta": "Create Account",
  "Crear cuenta nueva": "Create new account",
  "Crecimiento": "Grower",
  "Debes permitir las notificaciones en tu teléfono para recibir avisos.": "You must allow notifications on your phone to receive alerts.",
  "Descripción (opcional)": "Description (optional)",
  "Día": "Day",
  "DÍA": "DAY",
  "días": "days",
  "Editando Día": "Editing Day",
  "Ej: 12.5 (peso total canastas vacías)": "e.g. 12.5 (total weight of empty crates)",
  "Ej: 200": "e.g. 200",
  "Ej: 4 sacos inicio a 1395": "e.g. 4 starter bags at 1395",
  "Ej: 42": "e.g. 42",
  "Ej: 450": "e.g. 450",
  "Ej: 49, 60, 75.5...": "e.g. 49, 60, 75.5...",
  "Ej: KT47": "e.g. KT47",
  "Ej: Lucas, Papolo, Amigo...": "e.g. Lucas, Papolo, Friend...",
  "Ej: Lucas, Papolo...": "e.g. Lucas, Papolo...",
  "El aparato": "The device",
  "Elige tu plan": "Choose your plan",
  "Elige tu plan — ": "Choose your plan — ",
  "empleado": "employee",
  "en": "in",
  "EN VIVO": "LIVE",
  "Engorde": "Finisher",
  "Entrando…": "Signing in…",
  "Entrar": "Sign in",
  "Error activando el trial. Intenta de nuevo.": "Error starting the trial. Try again.",
  "Error al cambiar contraseña": "Error changing password",
  "Error de conexión al confirmar la compra.": "Connection error while confirming the purchase.",
  "Error de conexión.": "Connection error.",
  "Error de conexión. Intenta de nuevo.": "Connection error. Try again.",
  "Error de conexión. Verifica tu internet.": "Connection error. Check your internet.",
  "Es tu primera vez. Escribe tu nombre para registrarte:": "It's your first time. Enter your name to sign up:",
  "Escribe aquí cualquier observación...": "Write any observation here...",
  "Escribe el código que viene con tu": "Enter the code that came with your",
  "Escribe el código que viene con tu caja.": "Enter the code that came with your box.",
  "Escribe tu correo primero": "Enter your email first",
  "Escribe tu nombre": "Enter your name",
  "Escribe tu nueva contraseña.": "Enter your new password.",
  "está limpio y vacío?": "clean and empty?",
  "Estado de la Cama": "Litter Condition",
  "Estado de las Heces": "Droppings Condition",
  "Este correo ya está registrado": "This email is already registered",
  "Este dispositivo no soporta notificaciones push.": "This device does not support push notifications.",
  "Este dispositivo o navegador no soporta notificaciones push.": "This device or browser does not support push notifications.",
  "Esto aplica a TODOS los usuarios nuevos de la app (solo tú ves esta sección).": "This applies to ALL new app users (only you can see this section).",
  "FCR (CONVERSIÓN)": "FCR (CONVERSION)",
  "FOTO": "PHOTO",
  "Gallinaza — indicador de salud": "Droppings — health indicator",
  "GANANCIA NETA": "NET PROFIT",
  "gastos ÷ lb producidas": "expenses ÷ lb produced",
  "GRANJA ENCONTRADA": "FARM FOUND",
  "Guardando...": "Saving...",
  "Guardar evento": "Save event",
  "Guardar gasto": "Save expense",
  "Guardar nueva contraseña": "Save new password",
  "Guardar Registro": "Save Record",
  "Guardar venta": "Save sale",
  "Heces:": "Droppings:",
  "INGRESO TOTAL": "TOTAL INCOME",
  "Iniciador": "Starter",
  "Iniciar Sesión": "Sign In",
  "Inventario": "Inventory",
  "INVERSIÓN TOTAL": "TOTAL INVESTMENT",
  "Ir al login": "Go to sign in",
  "La contraseña debe tener al menos 6 caracteres": "The password must be at least 6 characters",
  "Las contraseñas no coinciden": "Passwords don't match",
  "lb / ave vendida": "lb / bird sold",
  "Lb / ave:": "Lb / bird:",
  "lb / pollo": "lb / bird",
  "lb alimento / lb carne · meta": "lb feed / lb meat · target",
  "lb estándar": "lb standard",
  "Lb netas:": "Net lb:",
  "lb producidas · gastos": "lb produced · expenses",
  "lb/ave": "lb/bird",
  "lb/pollo": "lb/bird",
  "Libras totales de este lote": "Total pounds in this batch",
  "Libras totales vendidas en este lote": "Total pounds sold in this batch",
  "Listo": "Done",
  "Llena los datos de hoy": "Fill in today's data",
  "lote": "sale",
  "Manejo y Crianza de Pollos de Engorde": "Broiler Chicken Management and Raising",
  "Margen:": "Margin:",
  "MENÚ": "MENU",
  "Mínimo 6 caracteres": "Minimum 6 characters",
  "Mis Pollo Box": "My Pollo Box",
  "Mis Pollo Box (": "My Pollo Box (",
  "Modo demostración": "Demo mode",
  "Muestra heces": "Droppings sample",
  "Muestra la pantalla de Ambiente con datos de ejemplo, sin aparato conectado. Los datos NO son de tu galpón.": "Shows the Environment screen with sample data, without a device connected. The data is NOT from your barn.",
  "Muestra representativa del día": "Representative sample for the day",
  "No pudimos canjear ese código.": "We couldn't redeem that code.",
  "No pudimos enviar el correo. Verifica tu dirección.": "We couldn't send the email. Check your address.",
  "No pudimos iniciar sesión ahora mismo. Intenta de nuevo en un momento.": "We couldn't sign you in right now. Try again in a moment.",
  "No pudimos reenviarlo ahora mismo (se puede pedir una vez por minuto). Intenta en un rato.": "We couldn't resend it right now (you can request it once per minute). Try again later.",
  "no reporta. Última lectura": "is not reporting. Last reading",
  "No se pudieron activar las notificaciones: ": "Could not enable notifications: ",
  "No se pudo confirmar la compra. Si se te cobró, escríbenos y lo resolvemos.": "Could not confirm the purchase. If you were charged, contact us and we'll fix it.",
  "No se pudo iniciar el pago de Google Play. Intenta de nuevo.": "Could not start the Google Play payment. Try again.",
  "No, solo continuar": "No, just continue",
  "Notas del Día": "Day Notes",
  "Nueva contraseña": "New password",
  "Nueva Contraseña": "New Password",
  "Nueva venta": "New sale",
  "Nueva Venta": "New Sale",
  "Nuevo evento": "New event",
  "Nuevo Gasto": "New Expense",
  "nunca": "never",
  "Observaciones adicionales": "Additional observations",
  "Pago seguro con Stripe · Cancela cuando quieras": "Secure payment with Stripe · Cancel anytime",
  "Personaliza cómo funciona Pollo Express IA": "Customize how Pollo Express IA works",
  "Peso promedio": "Average weight",
  "PESO PROMEDIO": "AVERAGE WEIGHT",
  "pollos ·": "birds ·",
  "por año": "per year",
  "por mes": "per month",
  "Procesando...": "Processing...",
  "propietario": "owner",
  "PRÓXIMOS": "UPCOMING",
  "Prueba gratis no disponible por el momento.": "Free trial not available at the moment.",
  "QUEDAN POR VENDER": "LEFT TO SELL",
  "QUEDARÍAN": "REMAINING",
  "rango ideal": "ideal range",
  "registrados": "recorded",
  "Registrar venta": "Record sale",
  "Registrarme como Propietario": "Sign up as Owner",
  "Registro del Día": "Today's Record",
  "Repite la contraseña": "Repeat the password",
  "Restar peso de canastas (lb) — opcional": "Subtract crate weight (lb) — optional",
  "SESIÓN ACTIVA": "ACTIVE SESSION",
  "Sesión iniciada como": "Signed in as",
  "Si está limpio, este es el mejor momento para calibrar el sensor con aire limpio. Toma unos 30 minutos y no necesitas hacer nada más.": "If it's clean, this is the best time to calibrate the sensor with clean air. It takes about 30 minutes and you don't need to do anything else.",
  "Sí, calibrar": "Yes, calibrate",
  "sin datos": "no data",
  "Sin datos de alimento": "No feed data",
  "Sin gastos aún": "No expenses yet",
  "Sin movimientos registrados aún.": "No transactions recorded yet.",
  "SIN SEÑAL": "NO SIGNAL",
  "OFFLINE": "OFFLINE",
  "Última lectura": "Last reading",
  "Sin ventas registradas.": "No sales recorded.",
  "Suplemento / Vacuna": "Supplement / Vaccine",
  "Suscribirme — ": "Subscribe — ",
  "Temperatura Ideal": "Ideal Temperature",
  "Tengo un código de empleado": "I have an employee code",
  "TIPO": "TYPE",
  "Total lote:": "Batch total:",
  "Tu acceso fue removido por el dueño. Pídele un código nuevo.": "Your access was removed by the owner. Ask them for a new code.",
  "Tu correo todavía no está confirmado. Busca en tu bandeja de entrada (y en spam) el correo que te enviamos y toca el enlace.": "Your email isn't confirmed yet. Check your inbox (and spam) for the email we sent you and tap the link.",
  "Tu empleador te comparte un código de 6 letras.": "Your employer shares a 6-letter code with you.",
  "Tu nombre": "Your name",
  "tu@correo.com": "you@email.com",
  "ÚLTIMOS REGISTROS": "LATEST RECORDS",
  "Unirme como Empleado": "Join as Employee",
  "Vacunas, pesajes y alertas en este dispositivo": "Vaccines, weigh-ins and alerts on this device",
  "VENDIDOS": "SOLD",
  "venta": "sale",
  "Ventas del día": "Today's sales",
  "Ver estado, calibrar o agregar otro aparato": "View status, calibrate or add another device",
  "Verificar": "Verify",
  "Vincula tu caja de temperatura, humedad y amoniaco": "Link your temperature, humidity and ammonia box",
  "Viruta o material de piso": "Shavings or floor material",
  "Ya puedes iniciar sesión con tu nueva contraseña.": "You can now sign in with your new password.",
  // ── fase 1b ──
  "Tu período de prueba ha terminado": "Your free trial has ended",
  "Elige un plan para continuar": "Choose a plan to continue",
  "✅ Hasta 5 camadas activas": "✅ Up to 5 active flocks",
  "✅ Registro diario ilimitado": "✅ Unlimited daily logs",
  "✅ Análisis de heces con IA": "✅ AI droppings analysis",
  "✅ Historial completo": "✅ Full history",
  "✅ Empleados gratis con código": "✅ Free employee access with a code",
  "✅ Cámara con IA": "✅ AI camera",
  "0 aves (ninguna)": "0 birds (none)",
  "aves muertas": "dead birds",
  " (meta: 5.00 lb)": " (goal: 5.00 lb)",
  "No pesé hoy": "Didn't weigh today",
  "Seleccionar estado de la cama...": "Select litter condition...",
  "🟢 Excelente — seca y suelta": "🟢 Excellent — dry and loose",
  "🟡 Buena — algo húmeda": "🟡 Good — slightly damp",
  "🟠 Regular — húmeda en zonas": "🟠 Fair — damp in spots",
  "🔴 Mala — muy húmeda/apelmazada": "🔴 Poor — very wet/caked",
  "⚫ Crítica — requiere cambio urgente": "⚫ Critical — needs urgent change",
  "Seleccionar estado de las heces...": "Select droppings condition...",
  "🟢 Normal — firme y marrón": "🟢 Normal — firm and brown",
  "🟡 Blandas — algo líquidas": "🟡 Soft — somewhat runny",
  "🟠 Diarrea amarilla — líquidas": "🟠 Yellow diarrhea — watery",
  "🔴 Diarrea con sangre — rojizas": "🔴 Bloody diarrhea — reddish",
  "⚫ Cecales — marrón oscuro (normal)": "⚫ Cecal — dark brown (normal)",
  "🔵 Verdes — posible estrés/infección": "🔵 Green — possible stress/infection",
  "Sin suplemento hoy": "No supplement today",
  "💊 DYNAVIT-200 — Vitaminas + Electrolitos": "💊 DYNAVIT-200 — Vitamins + Electrolytes",
  "💊 AD3E — Vitaminas A, D3, E": "💊 AD3E — Vitamins A, D3, E",
  "🍋 Limón / Vitamina C": "🍋 Lemon / Vitamin C",
  "🧬 Betaína": "🧬 Betaine",
  "🔬 Enzimas digestivas": "🔬 Digestive enzymes",
  "💉 Hipragumboro — Vacuna Gumboro": "💉 Hipragumboro — Gumboro vaccine",
  "💉 Newcastle La Sota — Vacuna Newcastle": "💉 Newcastle La Sota — Newcastle vaccine",
  "💉 Bronquitis infecciosa": "💉 Infectious bronchitis",
  "🦠 Simbyos — Probiótico": "🦠 Simbyos — Probiotic",
  "🪱 Levamisol — Desparasitante": "🪱 Levamisole — Dewormer",
  "🍬 Azúcar (recepción)": "🍬 Sugar (chick arrival)",
  "💧 Clorox / Cloro en agua": "💧 Clorox / Chlorine in water",
  "🧪 Otro (ver notas)": "🧪 Other (see notes)",
  "Vitamina C (Limón)": "Vitamin C (Lemon)",
  "Betaína": "Betaine",
  "Enzimas digestivas": "Digestive enzymes",
  "Vacuna Gumboro": "Gumboro vaccine",
  "Vacuna Newcastle": "Newcastle vaccine",
  "Vacuna Bronquitis": "Bronchitis vaccine",
  "Vacuna Marek": "Marek vaccine",
  "Probiótico Simbyos": "Simbyos probiotic",
  "Levamisol": "Levamisole",
  "Azúcar": "Sugar",
  "Cloración agua": "Water chlorination",
  "Otro": "Other",
  "Costo de Pollitos": "Chick cost",
  "Transporte": "Transport",
  "Alimento": "Feed",
  "Paja / Cama": "Straw / Litter",
  "Vacunas": "Vaccines",
  "Vitaminas / Suplementos": "Vitamins / Supplements",
  "Probiótico": "Probiotic",
  "Desparasitante": "Dewormer",
  "Agua": "Water",
  "Cal": "Lime",
  "Vinagre": "Vinegar",
  "Otros gastos": "Other expenses",
  "POLLOS VENDIDOS": "BIRDS SOLD",
  "LIBRAS TOTALES": "TOTAL POUNDS",
  "LB / AVE PROM.": "AVG LB / BIRD",
  " aves": " birds",
  "Nombre": "Name",
  "Galpón": "House",
  "Fecha inicio": "Start date",
  "Total pollitos": "Total chicks",
  "Día actual": "Current day",
  "Vivos actuales": "Currently alive",
  "Mortalidad": "Mortality",
  "📊 Estado del inventario": "📊 Inventory status",
  "ÓPTIMO": "OPTIMAL",
  "BAJO": "LOW",
  "CRÍTICO": "CRITICAL",
  "Alimento Iniciador": "Starter feed",
  "Alimento Crecimiento": "Grower feed",
  "Alimento Engorde": "Finisher feed",
  "Paja de arroz": "Rice straw",
  "Vitaminas": "Vitamins",
  "Desparasitantes": "Dewormers",
  "Probióticos": "Probiotics",
  "Insumo": "Supply",
  "sacos": "bags",
  "unidades": "units",
  "galones": "gallons",
  "litros": "liters",
  "sobres": "packets",
  "frascos": "bottles",
  "libras": "pounds",
  // ── fase 1c ──
  "Vitam.+ Electrolitos": "Vitamins + Electrolytes",
  "Agua Limpia": "Clean water",
  "Agua limpia": "Clean water",
  "Vitaminas AD3E": "AD3E vitamins",
  "PROBIÓTICO": "PROBIOTIC",
  "Refuerzo Gumboro": "Gumboro booster",
  "Enzimas": "Enzymes",
  "Recepción": "Arrival",
  "Estrés traslado": "Transport stress",
  "Refuerzo": "Booster",
  "Intestino": "Gut health",
  "Mejora FCR": "Improves FCR",
  "Parásitos": "Parasites",
  "Azúcar blanca": "White sugar",
  "Media cucha. / litro": "½ tsp / liter",
  "1ML por Litro": "1 mL per liter",
  "1ML por litro": "1 mL per liter",
  "1ML / Litro": "1 mL / liter",
  "1g / 1 litro de agua": "1 g / 1 liter of water",
  "1000 pollos por frasco": "1000 birds per vial",
  "¼ cucha. por litro": "¼ tsp per liter",
  "Todos": "All",
  "Activos": "Active",
  "Desparasit.": "Deworm.",
  "vacuna": "vaccine",
  "vitamina": "vitamin",
  "probiotico": "probiotic",
  "desparasitante": "dewormer",
  "betaina": "betaine",
  "agua": "water",
  "recepcion": "arrival",
  "enzima": "enzyme",
  "NOMBRE DEL EVENTO": "EVENT NAME",
  "NOMBRE": "NAME",
  "FUNCIÓN": "PURPOSE",
  "PRODUCTO": "PRODUCT",
  "DOSIS": "DOSE",
  "VÍA": "ROUTE",
  "Ej: Newcastle": "E.g. Newcastle",
  "Ej: Prevención": "E.g. Prevention",
  "Ej: Hipragumboro": "E.g. Hipragumboro",
  "Ej: 1 frasco / 1000 pollos": "E.g. 1 vial / 1000 birds",
  "Agua / Inyectable": "Water / Injection",
  "Gris Oscuro": "Dark Gray",
  "Negro Azulado": "Bluish Black",
  "Verde Bosque": "Forest Green",
  "Azul Petróleo": "Petrol Blue",
  "Marrón Original": "Original Brown",
  "Negro Puro": "Pure Black",
  "Blanco Claro": "Light White",
  "Amarillo Claro": "Light Yellow",
  "Naranja Claro": "Light Orange",
  "Café/Marrón": "Coffee/Brown",
  "Peso kg": "Weight kg",
  // ── fases de alimento ──
  "Preinicio": "Pre-starter",
  "Alimento Preinicio": "Pre-starter feed",
  "Días {a}-{b}": "Days {a}-{b}",
  "¿Con cuántas fases crías?": "How many feed phases do you use?",
  "Vale para toda la granja y para la caja Pollo Box.": "Applies to the whole farm and to the Pollo Box.",
  "3 fases": "3 phases",
  "4 fases": "4 phases",
  "Iniciador · Crecimiento · Engorde": "Starter · Grower · Finisher",
  "Preinicio · Iniciador · Crecimiento · Engorde": "Pre-starter · Starter · Grower · Finisher",
  "AHORA": "NOW",
  "Nueva camada": "New flock",
  "Los 2 primeros sacos como preinicio": "First 2 bags as pre-starter",
  "1 pollo": "1 bird",
  "pollos": "birds",
  "en total": "in total",
  "ave": "bird",
  "Elige el peso de un pollo.": "Pick the weight of one bird.",
  "Pesa los pollos juntos y elige las libras que marcó la balanza.": "Weigh the birds together and pick the pounds the scale showed.",
  "Alimento gastado hoy": "Feed used today",
  "Se descuenta del inventario al guardar": "It is subtracted from inventory when you save",
  "en inventario": "in stock",
  "SACOS DE 100 LB": "100 LB BAGS",
  "Elige de cuál alimento fueron esos sacos.": "Pick which feed those bags came from.",
  "0.2 g por litro": "0.2 g per liter",
  "Escanear recibo con la cámara": "Scan receipt with the camera",
  "Leyendo el recibo…": "Reading the receipt…",
  "No se pudo leer el recibo.": "The receipt could not be read.",
  "Esa foto no parece un recibo.": "That photo doesn't look like a receipt.",
  "Leído del recibo: {t}. Revísalo y guarda.": "Read from the receipt: {t}. Check it and save.",
  "No se distinguió el total. Escríbelo a mano.": "The total could not be read. Type it in.",
  "Error de conexión al leer el recibo.": "Connection error while reading the receipt.",
  "Subir recibo desde la galería": "Upload receipt from the gallery",
  "no reporta.": "is not reporting.",
  "Estos son los últimos datos, de": "This is the last data, from",
  "Todavía no ha enviado ninguna lectura.": "It hasn't sent any reading yet.",
  "Toca para ver el detalle.": "Tap to see the details.",
  "Alimento gastado": "Feed used",
  "saco": "sack",
  "Cambia el plan de alimento al de esa raza.": "Changes the feed plan to that breed's.",
  "Reporte completo del ciclo (PDF)": "Full cycle report (PDF)",
  "Gráficas de bajas, alimento y vitaminas día por día. Se puede enviar por WhatsApp.": "Charts of deaths, feed and vitamins day by day. Can be sent on WhatsApp.",
  "Todavía no hay registros para el reporte.": "There are no records for the report yet.",
  "Reporte del ciclo": "Cycle report",
  "Bajas por día": "Deaths per day",
  "Mortalidad acumulada": "Cumulative mortality",
  "Sacos de alimento por día": "Feed sacks per day",
  "Calendario del ciclo": "Cycle calendar",
  "Vitaminas y suplementos": "Vitamins and supplements",
  "Todos los días": "Every day",
  "El día de más bajas fue el día": "The day with most deaths was day",
  "Día del ciclo": "Day of the cycle",
  "Sacos usados": "Sacks used",
  "Último peso": "Last weight",
  "Recibidos": "Received",
  "Vivos": "Alive",
  "Bajas": "Deaths",
  "Acum.": "Cum.",
  "Sacos": "Sacks",
  "Producto": "Product",
  "Notas": "Notes",
  "Generado": "Generated",
  "máximo": "max",
  "desde": "from",
  "con": "with",
  "Reporte de camada": "Flock report",
  "Indicadores clave": "Key figures",
  "Retorno (ROI)": "Return (ROI)",
  "sobre la inversión": "on the investment",
  "de cada peso vendido": "of every peso sold",
  "Costo por libra": "Cost per pound",
  "se vendió a": "sold at",
  "lb alimento por lb carne": "lb feed per lb meat",
  "Libras por ave": "Pounds per bird",
  "Resultado financiero": "Financial result",
  "Ingreso por ventas": "Sales income",
  "Inversión (gastos)": "Investment (expenses)",
  "Ganancia neta": "Net profit",
  "Pérdida neta": "Net loss",
  "Por cada": "For every",
  "vendidos, quedaron": "sold, you kept",
  "de ganancia.": "in profit.",
  "de pérdida.": "in loss.",
  "Cada ave costó": "Each bird cost",
  "Inversión vs ganancia": "Investment vs profit",
  "Ingreso vs pérdida": "Income vs loss",
  "En qué se fue el dinero": "Where the money went",
  "Gastos por categoría": "Expenses by category",
  "Detalle de gastos": "Expense detail",
  "Inversión total": "Total investment",
  "Registros diarios": "Daily records",
  "Comprador": "Buyer",
  "Libras": "Pounds",
  "Monto": "Amount",
  "Descripción": "Description",
  "lotes": "batches",
  "inversión": "investment",
  "de margen": "margin",
  "de pérdida": "loss",
  "de": "of",
  "Cómo subió la mortalidad": "How mortality went up",
  "% acumulado del lote, día por día": "% of the flock, day by day",
  "Cómo terminó el lote": "How the flock ended",
  "Cada barra es un día del ciclo": "Each bar is a day of the cycle",
  "El color es la fase del alimento": "The color is the feed phase",
  "Alimento por fase": "Feed by phase",
  "Sin alimento registrado": "No feed recorded",
  "Cómo fue subiendo el peso": "How the weight went up",
  "Kilos por ave en cada pesaje": "Kilos per bird at each weighing",
  "Qué se dio y en qué día": "What was given and on which day",
  "el peor día fue el": "the worst day was",
  "En total se perdió el": "In total you lost",
  "se usaron": "you used",
  "desde el día": "starting on day",
  "no se registró alimento en este ciclo.": "no feed was recorded in this cycle.",
  "el último pesaje fue de": "the last weighing was",
  "el día": "on day",
  "semana": "week",
  "mortalidad": "mortality",
  "Peso": "Weight",
  "día": "day",
  "Editar": "Edit",
  "Restaurar": "Restore",
  "Pollos muertos": "Dead chickens",
  "Cama": "Bedding",
  "Heces": "Droppings",
  "Foto": "Photo",
  "Sacos de comida": "Feed sacks",
  "GUARDAR": "SAVE",
  "LISTO": "DONE",
  "Registro diario sencillo": "Simple daily record",
  "Pantalla con números grandes, botones + y −, y caritas de colores. Para empleados que leen poco. Apágalo para volver al registro de siempre.": "Screen with big numbers, + and − buttons and colored faces. For workers who read little. Turn it off to go back to the usual record.",
  "📝 REGISTRO DIARIO": "📝 DAILY RECORD",
  "Seca": "Dry",
  "Casi seca": "Almost dry",
  "Húmeda": "Damp",
  "Mojada": "Wet",
  "Bien": "Good",
  "Blandas": "Soft",
  "Diarrea": "Diarrhea",
  "Con sangre": "With blood",
  "Toca un dibujo": "Tap a picture",
  "Qué más se gastó": "What else was used",
  "No hay insumos en el inventario": "No supplies in the inventory",
  "quedan": "left",
  "Insumos gastados": "Supplies used",
  "Peso de un pollo": "Weight of one chicken",
  "meta": "target",
  "Suplemento": "Supplement",
  "No hay vitaminas en el calendario": "No vitamins in the calendar",
  "Nada hoy": "Nothing today",
  "FALTA": "MISSING",
  "¿Pesaste hoy?": "Did you weigh today?",
  "NO PESÉ": "DIDN'T WEIGH",
  "🎟️ Tengo un código": "🎟️ I have a code",
  "cada pollo": "each chicken",
  "PAÍS": "COUNTRY",
  "Sin especificar": "Not set",
  "República Dominicana": "Dominican Republic",
  "Colombia": "Colombia",
  "México": "Mexico",
  "Costa Rica": "Costa Rica",
  "Ecuador": "Ecuador",
  "Facturación electrónica disponible": "E-invoicing available",
  "Facturación electrónica próximamente": "E-invoicing coming soon",
  "Facturación interna de Pollo IA": "Pollo IA internal invoicing",
  "Hispanoamérica": "Spanish-speaking America",
  "Brasil": "Brazil",
  "España": "Spain",
  "Caribe anglófono": "English-speaking Caribbean",
  "Norteamérica, Europa y Oceanía": "North America, Europe and Oceania",
  "África": "Africa",
  "Asia": "Asia",
  "Argentina": "Argentina",
  "Perú": "Peru",
  "Chile": "Chile",
  "Guatemala": "Guatemala",
  "Panamá": "Panama",
  "El Salvador": "El Salvador",
  "Honduras": "Honduras",
  "Nicaragua": "Nicaragua",
  "Bolivia": "Bolivia",
  "Paraguay": "Paraguay",
  "Uruguay": "Uruguay",
  "Venezuela": "Venezuela",
  "Cuba": "Cuba",
  "Puerto Rico": "Puerto Rico",
  "Guinea Ecuatorial": "Equatorial Guinea",
  "Jamaica": "Jamaica",
  "Trinidad y Tobago": "Trinidad and Tobago",
  "Barbados": "Barbados",
  "Bahamas": "Bahamas",
  "Guyana": "Guyana",
  "Belice": "Belize",
  "Antigua y Barbuda": "Antigua and Barbuda",
  "Santa Lucía": "Saint Lucia",
  "San Vicente y las Granadinas": "Saint Vincent and the Grenadines",
  "Granada": "Grenada",
  "San Cristóbal y Nieves": "Saint Kitts and Nevis",
  "Dominica": "Dominica",
  "Estados Unidos": "United States",
  "Canadá": "Canada",
  "Reino Unido": "United Kingdom",
  "Irlanda": "Ireland",
  "Australia": "Australia",
  "Nueva Zelanda": "New Zealand",
  "Nigeria": "Nigeria",
  "Ghana": "Ghana",
  "Kenia": "Kenya",
  "Sudáfrica": "South Africa",
  "Tanzania": "Tanzania",
  "Uganda": "Uganda",
  "India": "India",
  "Filipinas": "Philippines",
  "Malasia": "Malaysia",
  "Singapur": "Singapore",
  "Pakistán": "Pakistan",
  "Sri Lanka": "Sri Lanka",
  "Bangladés": "Bangladesh",
  "Vuelve a entrar para usar la facturación.": "Sign in again to use invoicing.",
  "No se pudo completar la operación.": "The operation could not be completed.",
  "Cliente": "Customer",
  "Cliente nuevo": "New customer",
  "Nombre del cliente": "Customer name",
  "RNC o cédula (opcional)": "Tax ID or national ID (optional)",
  "Guardar cliente": "Save customer",
  "Guardando…": "Saving…",
  "Solo esta vez": "Just this time",
  "Cuántos pollos": "How many chickens",
  "Libras en la balanza": "Pounds on the scale",
  "Pon primero cuántos pollos.": "Enter how many chickens first.",
  "esperado": "expected",
  "Precio por libra": "Price per pound",
  "Restar peso de canastas": "Subtract crate weight",
  "Peso de las canastas": "Crate weight",
  "Déjala en cero si pesaste los pollos sin canastas.": "Leave it at zero if you weighed the birds without crates.",
  "netas": "net",
  "empezar de cero": "start over",
  "Editar cliente": "Edit customer",
  "Guardar cambios": "Save changes",
  "TOTAL": "TOTAL",
  "Falta mover la barra de las libras.": "Move the pounds slider.",
  "Falta poner cuántos pollos.": "Enter how many chickens.",
  "Mis clientes": "My customers",
  "Atrás": "Back",
  "Teléfono": "Phone",
  "RNC o cédula": "Tax ID or national ID",
  "Todavía no tienes clientes guardados.": "You have no saved customers yet.",
  "Guardarlo en mis clientes": "Save to my customers",
  "Se guarda cuando guardes la venta": "Saved when you save the sale",
  "La venta se guardó bien, pero el cliente no se pudo guardar en tu lista.": "The sale was saved, but the customer could not be added to your list.",
  "Volver a entrar": "Sign in again",
  "Entendido": "Got it",
  "vuelve a entrar": "sign in again",
  "Puedes seguir con la venta: el nombre queda en la factura igual.": "You can still make the sale: the name goes on the invoice anyway.",
  "No hay sesión abierta.": "No session is open.",
  "La lista de clientes la maneja el dueño de la granja.": "The farm owner manages the customer list.",
  "Contraseña incorrecta.": "Wrong password.",
  "No pudimos confirmar tu contraseña ahora mismo. Revisa tu internet.": "We couldn't confirm your password right now. Check your internet.",
  "Confirma tu contraseña": "Confirm your password",
  "Es una sola vez. Tu sesión es vieja y hay que renovarla para poder guardar clientes y facturas. No pierdes nada: ni tus camadas, ni lo que ya escribiste aquí.": "Just this once. Your session is old and has to be renewed before customers and invoices can be saved. You lose nothing: not your flocks, not what you already typed here.",
  "Tu contraseña": "Your password",
  "Ahora no": "Not now",
  "Confirmando…": "Confirming…",
  "Confirmar": "Confirm",
  "Arreglar ahora": "Fix it now",
  "Confirma tu contraseña para usar tu lista de clientes": "Confirm your password to use your customer list",
  "← Atrás": "← Back",
  "Usar solo en esta factura, sin guardarlo": "Use on this invoice only, don't save",
  "solo en esta factura": "this invoice only",
  "Cambiar": "Change",
  "Quitar": "Remove",
  "La lista de clientes la maneja el dueño. Escribe el nombre y queda en la factura igual.": "The farm owner manages the customer list. Type the name and it still goes on the invoice.",
  "Mi Granja y Facturación": "My Farm & Invoicing",
  "Lo que sale impreso en tus facturas": "What gets printed on your invoices",
  "Esto lo maneja el dueño de la granja. Lo puedes ver, pero no cambiarlo.": "The farm owner manages this. You can see it, but not change it.",
  "Identidad de mi granja": "My farm identity",
  "El nombre y el logo que el comprador ve arriba en la factura.": "The name and logo the buyer sees at the top of the invoice.",
  "LOGO DE LA GRANJA": "FARM LOGO",
  "✏️ Cambiar logo": "✏️ Change logo",
  "📷 Subir logo": "📷 Upload logo",
  "NOMBRE COMERCIAL": "TRADE NAME",
  "Granja Los Matos": "Smith Family Farm",
  "Sale en grande arriba en la factura. Debajo va polloia.net, que es quien la genera.": "Shown large at the top of the invoice. Below it goes polloia.net, which generates it.",
  "TELÉFONO": "PHONE",
  "CORREO": "EMAIL",
  "DIRECCIÓN DE LA GRANJA": "FARM ADDRESS",
  "Calle, sector, provincia": "Street, area, province",
  "Es también la dirección fiscal que la DGII pide del emisor.": "This is also the tax address the DGII requires from the issuer.",
  "✓ Esto se guarda solo, al momento de escribirlo.": "✓ This saves itself as you type.",
  "Datos fiscales y moneda": "Tax details and currency",
  "Tu RNC y tu nombre legal. El país decide la ley que aplica; la moneda, en qué se escriben los importes. Son dos cosas distintas.": "Your tax ID and legal name. The country decides which law applies; the currency decides how amounts are written. They are two different things.",
  "PAÍS DE OPERACIÓN FISCAL": "COUNTRY OF TAX OPERATION",
  "MONEDA PARA FACTURAR": "INVOICING CURRENCY",
  "Cambiar la moneda no reconvierte lo que ya tienes guardado: cada factura se queda en la moneda con la que se hizo.": "Changing the currency does not convert what you already saved: each invoice stays in the currency it was made with.",
  "RAZÓN SOCIAL (NOMBRE LEGAL)": "LEGAL NAME",
  "Granja Los Matos SRL": "Smith Family Farm LLC",
  "El nombre con el que estás registrado. Puede ser distinto del comercial.": "The name you are registered under. It can differ from your trade name.",
  "RNC O CÉDULA DEL PROPIETARIO O EMPRESA": "TAX ID OF THE OWNER OR COMPANY",
  "El RNC tiene 9 dígitos y la cédula 11. Este es TU número, el del que vende — no el del comprador, que se pide aparte en cada venta.": "A Dominican RNC has 9 digits and an ID card 11. This is YOUR number, the seller's — not the buyer's, which is asked for separately on each sale.",
  "✓ Parece un RNC": "✓ Looks like a company tax ID",
  "✓ Parece una cédula": "✓ Looks like a personal ID",
  "⚠ Lleva 9 dígitos (RNC) u 11 (cédula). Tienes ": "⚠ It takes 9 digits (RNC) or 11 (ID card). You have ",
  "ITBIS EN TUS VENTAS": "SALES TAX ON YOUR SALES",
  "Exento / no aplica": "Exempt / not applicable",
  "El pollo fresco está exento en R.D.": "Fresh chicken is exempt in the D.R.",
  "Cobro ITBIS": "I charge sales tax",
  "Escribe la tasa que te toca": "Enter the rate that applies to you",
  "Esto no es asesoría fiscal. Lo que escojas aquí es lo que se imprime; confírmalo con tu contador.": "This is not tax advice. Whatever you choose here is what gets printed; confirm it with your accountant.",
  "Guardar datos fiscales": "Save tax details",
  "✓ Datos fiscales guardados": "✓ Tax details saved",
  "Datos fiscales guardados.": "Tax details saved.",
  "Tu RNC y tu razón social se guardan en el servidor, no en el teléfono, y solo tu granja los ve.": "Your tax ID and legal name are kept on the server, not on the phone, and only your farm can see them.",
  "Primero escoge el país de tu granja, aquí arriba.": "First choose your farm's country, up above.",
  "Escribe la tasa de ITBIS que te toca (por ejemplo 18).": "Enter the sales tax rate that applies to you (for example 18).",
  "Facturación electrónica — DGII": "Electronic invoicing — DGII",
  "Facturación electrónica": "Electronic invoicing",
  "Opcional. Tu facturación interna funciona igual sin esto.": "Optional. Your internal invoicing works the same without it.",
  "Escoge tu país arriba": "Choose your country above",
  "Hoy solo está lista la de República Dominicana (DGII)": "Only the Dominican Republic (DGII) is ready today",
  "La de tu país sería con ": "Yours would be with ",
  "La facturación electrónica depende del país, así que primero hay que saber cuál es.": "Electronic invoicing depends on the country, so we need to know which one first.",
  "Mientras tanto sigues facturando con Pollo IA, con tu nombre, tu logo y tu RNC.": "Meanwhile you keep invoicing with Pollo IA, with your name, your logo and your tax ID.",
  "Pendiente de configuración": "Pending setup",
  "Pendiente de autorización": "Pending authorization",
  "Lista para emitir": "Ready to issue",
  "Suspendida o con error": "Suspended or failing",
  "Suspendida": "Suspended",
  "Activar facturación electrónica": "Turn on electronic invoicing",
  "SÍ — activada": "YES — on",
  "NO — desactivada (como viene de fábrica)": "NO — off (how it ships)",
  "Activarla no emitió nada. Cada comprobante hay que mandarlo tocando «Emitir factura electrónica» en su factura.": "Turning it on issued nothing. Each receipt has to be sent by tapping «Issue electronic invoice» on its invoice.",
  "Mientras está apagada: Pollo IA sigue haciendo tus facturas, no se manda nada a la DGII ni a ningún proveedor, y no se te cobra nada por esto.": "While it is off: Pollo IA keeps making your invoices, nothing is sent to the DGII or any provider, and you are charged nothing for this.",
  "Falta esto para poder emitir": "This is missing before you can issue",
  "PROVEEDOR AUTORIZADO": "AUTHORIZED PROVIDER",
  "Disponible": "Available",
  "No disponible todavía": "Not available yet",
  "Conectar mi granja": "Connect my farm",
  "Tú no escribes ninguna clave: las credenciales del proveedor las instala el servidor de Pollo IA.": "You never type a key: the provider's credentials are installed by the Pollo IA server.",
  "Conectada con ": "Connected with ",
  "ambiente de PRUEBAS": "TEST environment",
  "producción": "production",
  "Comprobantes emitidos este mes: ": "Receipts issued this month: ",
  "TOPE MENSUAL (VACÍO = SIN TOPE)": "MONTHLY CAP (EMPTY = NO CAP)",
  "sin tope": "no cap",
  "Al llegar al tope se bloquean las emisiones nuevas. Nunca el acceso a lo ya emitido.": "Reaching the cap blocks new issues. Never access to what was already issued.",
  "Desconectar del proveedor": "Disconnect from provider",
  "Hubo un problema con el proveedor.": "There was a problem with the provider.",
  "Tus facturas internas siguen funcionando. Lo ya emitido no se perdió.": "Your internal invoices keep working. What was already issued is not lost.",
  "Factura interna": "Internal invoice",
  ": la que Pollo IA hace siempre, con tu nombre, tu logo y tu RNC. Vale como recibo y como control tuyo.": ": the one Pollo IA always makes, with your name, your logo and your tax ID. It works as a receipt and as your own record.",
  "Comprobante fiscal electrónico": "Electronic tax receipt",
  ": además lleva e-NCF, código de seguridad y QR, y vale ante la DGII solo cuando ella lo acepta. Eso necesita el grupo 3 activado y conectado.": ": it also carries an e-NCF, security code and QR, and counts with the DGII only once they accept it. That needs group 3 turned on and connected.",
  "¿Activar la facturación electrónica?": "Turn on electronic invoicing?",
  "¿Desactivar la facturación electrónica?": "Turn off electronic invoicing?",
  "Se guarda que la quieres usar y te digo qué datos faltan. NO se emite ninguna factura, NO se crea ninguna cuenta de pago y NO se contrata nada. Cada comprobante se manda después, uno por uno, tocando su botón.": "It records that you want to use it and tells you what data is missing. NO invoice is issued, NO payment account is created and nothing is signed up for. Each receipt is sent afterwards, one by one, by tapping its button.",
  "Dejas de poder emitir comprobantes nuevos. Lo que ya emitiste se queda guardado y lo puedes seguir viendo. Tus facturas internas siguen igual.": "You stop being able to issue new receipts. What you already issued stays saved and you can still see it. Your internal invoices stay the same.",
  "Un momento…": "One moment…",
  "Sí, activar": "Yes, turn it on",
  "Sí, desactivar": "Yes, turn it off",
  "🏠 MI GRANJA Y FACTURACIÓN": "🏠 MY FARM & INVOICING",
  "Nombre, logo, país, moneda, RNC y facturación electrónica": "Name, logo, country, currency, tax ID and electronic invoicing",
  "Pollo vivo en pie": "Live chicken on the hoof",
  "interno": "internal",
  "⚡ Emitir e-CF": "⚡ Issue e-invoice",
  "Emitiendo…": "Issuing…",
  "No se pudo emitir.": "Could not issue it.",
};

const CALENDARIO_OFICIAL = [
  { dia: 0,  producto: "Azúcar",               funcion: "Recepción",          producto_nombre: "Azúcar blanca",  dosis: "Media cucha. / litro",    via: "Agua",  tipo: "recepcion" },
  { dia: 1,  producto: "Vitam.+ Electrolitos",  funcion: "Estrés traslado",    producto_nombre: "DYNAVIT-200",    dosis: "0.2 g por litro",            via: "Agua",  tipo: "vitamina" },
  { dia: 2,  producto: "Agua Limpia",           funcion: "",                   producto_nombre: "",               dosis: "",                         via: "",      tipo: "agua" },
  { dia: 3,  producto: "Vitaminas AD3E",        funcion: "Refuerzo",           producto_nombre: "AD3E",           dosis: "1ML / Litro",              via: "Agua",  tipo: "vitamina" },
  { dia: 4,  producto: "PROBIÓTICO",            funcion: "Intestino",          producto_nombre: "Simbyos",        dosis: "1g / 1 litro de agua",     via: "Agua",  tipo: "probiotico" },
  { dia: 5,  producto: "Agua Limpia",           funcion: "",                   producto_nombre: "",               dosis: "",                         via: "",      tipo: "agua" },
  { dia: 6,  producto: "Agua Limpia",           funcion: "",                   producto_nombre: "",               dosis: "",                         via: "",      tipo: "agua" },
  { dia: 7,  producto: "Gumboro",              funcion: "Gumboro",            producto_nombre: "Hipragumboro",   dosis: "1000 pollos por frasco",   via: "Agua",  tipo: "vacuna" },
  { dia: 8,  producto: "Agua Limpia",           funcion: "",                   producto_nombre: "",               dosis: "",                         via: "",      tipo: "agua" },
  { dia: 9,  producto: "Agua Limpia",           funcion: "",                   producto_nombre: "",               dosis: "",                         via: "",      tipo: "agua" },
  { dia: 10, producto: "PROBIÓTICO",            funcion: "Intestino",          producto_nombre: "Simbyos",        dosis: "1g / 1 litro de agua",     via: "Agua",  tipo: "probiotico" },
  { dia: 11, producto: "Agua Limpia",           funcion: "",                   producto_nombre: "",               dosis: "",                         via: "",      tipo: "agua" },
  { dia: 12, producto: "Vitam.+ Electrolitos",  funcion: "Refuerzo",           producto_nombre: "DYNAVIT-200",    dosis: "0.2 g por litro",            via: "Agua",  tipo: "vitamina" },
  { dia: 13, producto: "Agua Limpia",           funcion: "",                   producto_nombre: "",               dosis: "",                         via: "",      tipo: "agua" },
  { dia: 14, producto: "Refuerzo Gumboro",      funcion: "Gumboro",            producto_nombre: "Hipragumboro",   dosis: "1000 pollos por frasco",   via: "Agua",  tipo: "vacuna" },
  { dia: 15, producto: "Enzimas",               funcion: "Mejora FCR",         producto_nombre: "Enzimas",        dosis: "",                         via: "Agua",  tipo: "enzima" },
  { dia: 16, producto: "Enzimas",               funcion: "Mejora FCR",         producto_nombre: "Enzimas",        dosis: "",                         via: "Agua",  tipo: "enzima" },
  { dia: 17, producto: "Enzimas",               funcion: "Mejora FCR",         producto_nombre: "Enzimas",        dosis: "",                         via: "Agua",  tipo: "enzima" },
  { dia: 18, producto: "Enzimas",               funcion: "Mejora FCR",         producto_nombre: "Enzimas",        dosis: "",                         via: "Agua",  tipo: "enzima" },
  { dia: 19, producto: "Enzimas",               funcion: "Mejora FCR",         producto_nombre: "Enzimas",        dosis: "",                         via: "Agua",  tipo: "enzima" },
  { dia: 20, producto: "Desparasitante",         funcion: "Parásitos",          producto_nombre: "Levamisol",      dosis: "1ML por litro",            via: "Agua",  tipo: "desparasitante" },
  { dia: 21, producto: "Agua Limpia",           funcion: "",                   producto_nombre: "",               dosis: "",                         via: "",      tipo: "agua" },
  { dia: 22, producto: "PROBIÓTICO",            funcion: "Intestino",          producto_nombre: "Simbyos",        dosis: "1g / 1 litro de agua",     via: "Agua",  tipo: "probiotico" },
  { dia: 23, producto: "PROBIÓTICO",            funcion: "Intestino",          producto_nombre: "Simbyos",        dosis: "1g / 1 litro de agua",     via: "Agua",  tipo: "probiotico" },
  { dia: 24, producto: "Agua Limpia",           funcion: "",                   producto_nombre: "Agua limpia",    dosis: "",                         via: "Agua",  tipo: "agua" },
  { dia: 25, producto: "Agua Limpia",           funcion: "",                   producto_nombre: "Agua limpia",    dosis: "",                         via: "Agua",  tipo: "agua" },
  { dia: 26, producto: "Agua Limpia",           funcion: "",                   producto_nombre: "Agua limpia",    dosis: "",                         via: "Agua",  tipo: "agua" },
  { dia: 27, producto: "Agua Limpia",           funcion: "",                   producto_nombre: "Agua limpia",    dosis: "",                         via: "Agua",  tipo: "agua" },
  { dia: 28, producto: "Vitam.+ Electrolitos",  funcion: "Refuerzo",           producto_nombre: "DYNAVIT-200",    dosis: "0.2 g por litro",       via: "Agua",  tipo: "vitamina" },
  { dia: 29, producto: "Agua Limpia",           funcion: "",                   producto_nombre: "",               dosis: "",                         via: "",      tipo: "agua" },
  { dia: 30, producto: "Agua Limpia",           funcion: "",                   producto_nombre: "",               dosis: "",                         via: "",      tipo: "agua" },
  { dia: 31, producto: "Agua Limpia",           funcion: "",                   producto_nombre: "Agua limpia",    dosis: "",                         via: "Agua",  tipo: "agua" },
  { dia: 32, producto: "Agua Limpia",           funcion: "",                   producto_nombre: "Agua limpia",    dosis: "",                         via: "Agua",  tipo: "agua" },
  { dia: 33, producto: "Agua Limpia",           funcion: "",                   producto_nombre: "",               dosis: "",                         via: "",      tipo: "agua" },
  { dia: 34, producto: "Agua Limpia",           funcion: "",                   producto_nombre: "",               dosis: "",                         via: "",      tipo: "agua" },
  { dia: 35, producto: "Agua Limpia",           funcion: "",                   producto_nombre: "",               dosis: "",                         via: "",      tipo: "agua" },
];

const TIPO_COLORES = {
  recepcion: "#9c27b0", vacuna: "#ff6b1a", vitamina: "#4caf50",
  probiotico: "#2196f3", enzima: "#00bcd4", desparasitante: "#f44336",
  betaina: "#ff9800", agua: "#607d8b",
};

// --- FASES DE ALIMENTO + CÁLCULO DE SACOS ---
// El reparto entre fases NO son porcentajes a mano: sale de las TABLAS OFICIALES de consumo
// acumulado de cada raza. La app repartía 12/43/45 y el PDF 20/35/45; con las tablas lo real
// a 35 días es ~9/38/53, así que el engorde (donde más come el pollo) quedaba corto.
//   Ross 308 — Aviagen, Objetivos de Rendimiento (mixto, métrico).
//   Cobb 500 — Cobb500 Broiler Performance & Nutrition Supplement 2022 (as hatched, métrico).
// Índice = día de vida (0 a 42) · valor = gramos de alimento acumulados por ave.
const CONSUMO_ACUM = {
  ross: [0,13,30,50,73,100,131,165,204,247,295,348,406,469,537,611,691,777,869,966,1070,1180,1296,1418,1546,1679,1819,1965,2116,2272,2434,2601,2773,2951,3132,3319,3510,3705,3904,4107,4314,4525,4739],
  // Cobb no publica el consumo de los días 1-6: se estiman con la curva de Ross escalada
  // al día 7 de Cobb (180 g). Del día 7 en adelante son los números de la tabla.
  cobb: [0,14,33,55,80,109,143,180,220,264,314,371,435,508,588,672,763,861,966,1077,1195,1320,1451,1588,1731,1880,2034,2194,2359,2528,2702,2880,3063,3250,3441,3635,3833,4035,4241,4450,4663,4880,5100],
};
function tablaConsumo(raza) { return /cobb/i.test(String(raza || "")) ? CONSUMO_ACUM.cobb : CONSUMO_ACUM.ross; }
function consumoAcum(raza, dia) {
  var t = tablaConsumo(raza);
  var d = Math.max(0, Math.min(t.length - 1, Math.round(Number(dia) || 0)));
  return t[d];
}

// LAS FASES SON SIEMPRE LAS DE LA GUÍA OFICIAL (3: iniciador, crecimiento, engorde).
// Aviagen recomienda justo estos días para el Ross 308 y Cobb usa los mismos hasta el día 22.
// `inv` = renglón del inventario de esa fase.
// Razas que se pueden elegir (las mismas al crear una camada y al editarla).
const RAZAS = ["Ross 308", "Ross 708", "Cobb 500", "Cobb 700", "Hubbard", "Arbor Acres", "Hubbard Flex", "Otra"];

const FASES_BASE = [
  { key: "iniciador",   inv: "iniciador",   nombre: "Iniciador",   emoji: "🐣", color: "#F5C200", bg: "#2a2000", d1: 1,  d2: 10 },
  { key: "crecimiento", inv: "crecimiento", nombre: "Crecimiento", emoji: "🐔", color: "#4caf50", bg: "#0a2000", d1: 11, d2: 24 },
  { key: "engorde",     inv: "engorde",     nombre: "Engorde",     emoji: "🍗", color: "#2196f3", bg: "#00101a", d1: 25, d2: 35 },
];
// El modo de 4 fases NO mueve ningún día ni cambia el total: lo único que hace es apartar los
// PRIMEROS 2 SACOS del iniciador y comprarlos como preinicio (alimento más fino para los
// primeros días). El día en que se acaban esos 2 sacos depende de cuántos pollos hay, y ese
// es el día en que se pasa de preinicio a iniciador.
const FASE_PREINICIO = { key: "preinicio", inv: "preinicio", nombre: "Preinicio", emoji: "🐥", color: "#FF8A3D", bg: "#2a1200" };
// El preinicio es SOLO para recibir los pollitos: los 3 primeros días. La cantidad no es fija,
// sale de la tabla de la raza, así que crece con el tamaño del lote (700 pollos ≈ 1 saco,
// 2000 ≈ 3 sacos). Esos sacos se le restan al iniciador: el total del lote no cambia.
const DIAS_PREINICIO = 3;
const DIAS_CICLO_ALIM = 35;
// AJUSTE PROPIO DE LA GRANJA (Andrés): en sus lotes el pollo pide algo más de crecimiento.
// Mueve 3 puntos del iniciador y 1 del engorde al crecimiento, en los dos modos. La suma sigue
// siendo 100%: NO cambia el total de sacos. Para volver a las tablas puras, poner todo en 0.
const AJUSTE_FASES = { iniciador: -0.03, crecimiento: 0.04, engorde: -0.01 };
function modoFases(m) { return Number(m) === 4 ? 4 : 3; }

// Fases para dibujar. En 4 fases se parte el iniciador en preinicio + iniciador; los días de
// crecimiento y engorde quedan EXACTAMENTE iguales que en 3 fases.
function fasesDe(m, opciones) {
  var personal = opciones && opciones.personal;
  var base = FASES_BASE.map(function (f) { return Object.assign({}, f); });
  if (modoFases(m) !== 4) return aplicarFasesPersonal(base, personal);
  return aplicarFasesPersonal([
    Object.assign({}, FASE_PREINICIO, { d1: base[0].d1, d2: DIAS_PREINICIO }),
    Object.assign({}, base[0], { d1: DIAS_PREINICIO + 1 }),
    base[1], base[2],
  ], personal);
}
// Nombre, emoji y color de la fase de alimento a partir de lo que se guardó en el registro.
function faseAlimentoInfo(inv) {
  if (!inv) return null;
  var todas = FASES_BASE.concat([FASE_PREINICIO]);
  for (var i = 0; i < todas.length; i++) if (todas[i].inv === inv) return todas[i];
  return { inv: inv, nombre: String(inv), emoji: "🌽", color: "#F5C200" };
}
function faseDelDia(m, dia, opciones) {
  var fs = fasesDe(m, opciones), d = Number(dia) || 0;
  for (var i = 0; i < fs.length; i++) if (d <= fs[i].d2) return Object.assign({ idx: i }, fs[i]);
  return Object.assign({ idx: fs.length - 1 }, fs[fs.length - 1]);
}
// Pesaje en grupo: la lista va en LIBRAS TOTALES del grupo (que es lo que marca la balanza)
// y guarda el peso POR AVE en kg, que es lo que usa el resto de la app.
function opcionesPesoGrupo(aves) {
  var n = Number(aves) || 1;
  var paso = n >= 100 ? 5 : 0.5;
  var maxLb = 4.085 * 2.20462 * n;      // mismo tope que la lista de un solo pollo
  var arr = [{ label: tr("No pesé hoy"), val: "" }];
  for (var lb = paso; lb <= maxLb + 0.001; lb += paso) {
    var lbAve = lb / n;
    var kgAve = lbAve / 2.20462;
    if (kgAve < 0.03) continue;
    arr.push({
      label: (Math.round(lb * 10) / 10) + " lb " + tr("en total") + "  ·  " + lbAve.toFixed(2) + " lb/" + tr("ave"),
      val: kgAve.toFixed(3),
    });
  }
  return arr;
}
// Cambios que hizo el granjero a mano en una fase: desde qué día, hasta qué día y cuántos
// sacos. Lo que no tocó se queda con el cálculo de la tabla oficial de la raza.
function aplicarFasesPersonal(fases, personal) {
  if (!personal) return fases;
  return (fases || []).map(function (f) {
    var p = personal[f.key];
    if (!p) return f;
    var o = Object.assign({}, f);
    if (p.d1 != null && p.d1 !== "") o.d1 = Math.max(0, Number(p.d1) || 0);
    if (p.d2 != null && p.d2 !== "") o.d2 = Math.max(o.d1, Number(p.d2) || 0);
    if (p.sacos != null && p.sacos !== "") { o.sacos = Math.max(0, Math.round(Number(p.sacos) || 0)); o.lb = o.sacos * 100; o.editado = true; }
    if ((p.d1 != null && p.d1 !== "") || (p.d2 != null && p.d2 !== "")) o.editado = true;
    return o;
  });
}
function diasFaseTxt(fase) { return tr("Días {a}-{b}", { a: fase.d1, b: fase.d2 }); }

// Plan FIJO del lote: sacos por fase según la tabla de consumo de la raza, sobre los pollos
// RECIBIDOS (nunca sobre los vivos de hoy). Con 4 fases, los 2 primeros sacos del iniciador
// pasan a preinicio: el total y las demás fases no cambian ni un saco.
function planAlimento(totalPollos, lbPorPollo, raza, m, personal) {
  var lbTotal = (Number(totalPollos) || 0) * (Number(lbPorPollo) || 7.5);
  var totalCurva = consumoAcum(raza, DIAS_CICLO_ALIM) || 1;
  var fases = FASES_BASE.map(function (f, i) {
    var frac = (consumoAcum(raza, Math.min(f.d2, DIAS_CICLO_ALIM)) - consumoAcum(raza, f.d1 - 1)) / totalCurva;
    frac = Math.max(0.005, frac + (AJUSTE_FASES[f.key] || 0));    // ajuste propio de la granja
    var lb = lbTotal * frac;
    return Object.assign({}, f, { idx: i, frac: frac, lb: lb, sacos: Math.ceil(lb / 100) });
  });
  if (modoFases(m) === 4) {
    var ini = fases[0];
    // Lo que se comen de verdad en los 3 primeros días, según la tabla de la raza.
    var lbPre = lbTotal * (consumoAcum(raza, DIAS_PREINICIO) / totalCurva);
    var pre = Math.max(1, Math.min(Math.ceil(lbPre / 100), ini.sacos - 1));   // al iniciador le queda 1 saco mínimo
    fases = [
      Object.assign({}, FASE_PREINICIO, { idx: 0, d1: ini.d1, d2: DIAS_PREINICIO, sacos: pre, lb: pre * 100, frac: (pre * 100) / (lbTotal || 1) }),
      Object.assign({}, ini, { idx: 1, d1: DIAS_PREINICIO + 1, sacos: ini.sacos - pre, lb: ini.lb - pre * 100, frac: (ini.lb - pre * 100) / (lbTotal || 1) }),
      Object.assign({}, fases[1], { idx: 2 }),
      Object.assign({}, fases[2], { idx: 3 }),
    ];
  }
  fases = aplicarFasesPersonal(fases, personal);
  var lbBase = fases.reduce(function (a, x) { return a + (Number(x.lb) || 0); }, 0) || 1;
  fases = fases.map(function (f) {
    return Object.assign({}, f, {
      pct: Math.round(Math.max(0, f.editado ? (Number(f.lb) || 0) / lbBase : f.frac) * 100),
      ancho: (f.d2 - f.d1 + 1) / DIAS_CICLO_ALIM,
      diasStr: diasFaseTxt(f),
    });
  });
  var sacos = fases.reduce(function (a, x) { return a + x.sacos; }, 0);
  return { fases: fases, sacos: sacos, lb: sacos * 100, lbTeorico: lbTotal };
}

// --- CATEGORÍAS DE GASTOS (basado en Camada #2) ---
const CATEGORIAS_GASTOS = [
  { id: "pollitos",       label: "Costo de Pollitos",          icono: "🐣", color: "#f5c200" },
  { id: "transporte",     label: "Transporte",                 icono: "🚛", color: "#ff6b1a" },
  { id: "alimento",       label: "Alimento",                   icono: "🌽", color: "#4caf50" },
  { id: "paja",           label: "Paja / Cama",                icono: "🌾", color: "#8bc34a" },
  { id: "vacunas",        label: "Vacunas",                    icono: "💉", color: "#2196f3" },
  { id: "vitaminas",      label: "Vitaminas / Suplementos",    icono: "💊", color: "#9c27b0" },
  { id: "probiotico",     label: "Probiótico",                 icono: "🧫", color: "#00bcd4" },
  { id: "desparasitante", label: "Desparasitante",             icono: "🔬", color: "#f44336" },
  { id: "agua",           label: "Agua",                       icono: "💧", color: "#03a9f4" },
  { id: "cal",            label: "Cal",                        icono: "🪣", color: "#9e9e9e" },
  { id: "vinagre",        label: "Vinagre",                    icono: "🍶", color: "#ff9800" },
  { id: "otros",          label: "Otros gastos",               icono: "📦", color: "#607d8b" },
];

// --- DB ---
const DB = {
  getUsers: () => { try { return JSON.parse(localStorage.getItem("pe_users") || "{}"); } catch { return {}; } },
  saveUsers: (u) => { try { localStorage.setItem("pe_users", JSON.stringify(u)); } catch {} },
  // Multi-camada: lista de camadas por propietario
  getCamadasList: (email) => { try { return JSON.parse(localStorage.getItem("pe_camlist_" + email) || "[]"); } catch { return []; } },
  saveCamadasList: (email, list) => { try { localStorage.setItem("pe_camlist_" + email, JSON.stringify(list)); } catch {} },
  getCamadaById: (email, id) => { try { const list = DB.getCamadasList(email); return list.find(c => c.id === id) || null; } catch { return null; } },
  saveCamadaById: (email, camada) => { try { const list = DB.getCamadasList(email); const idx = list.findIndex(c => c.id === camada.id); if (idx >= 0) list[idx] = camada; else list.push(camada); DB.saveCamadasList(email, list); } catch {} },
  getActivaCamadaId: (email) => { try { return localStorage.getItem("pe_activa_" + email) || null; } catch { return null; } },
  setActivaCamadaId: (email, id) => { try { localStorage.setItem("pe_activa_" + email, id); } catch {} },
  // Legacy single-camada (migration)
  getCamadas: () => { try { return JSON.parse(localStorage.getItem("pe_camadas") || "{}"); } catch { return {}; } },
  saveCamadas: (c) => { try { localStorage.setItem("pe_camadas", JSON.stringify(c)); } catch {} },
  getHistorial: (email) => { try { return JSON.parse(localStorage.getItem("pe_historial_" + email) || "[]"); } catch { return []; } },
  saveHistorial: (email, h) => { try { localStorage.setItem("pe_historial_" + email, JSON.stringify(h)); } catch {} },
  // User settings
  getSettings: (email) => { try { return JSON.parse(localStorage.getItem("pe_settings_" + email) || "{}"); } catch { return {}; } },
  saveSettings: (email, s) => { try { localStorage.setItem("pe_settings_" + email, JSON.stringify(s)); } catch {} },
  /* Datos fiscales del EMISOR (RNC, razón social). El original vive en el
     servidor, en una tabla que la llave anónima no puede ni leer. Esto es solo
     una copia para imprimirlos en la factura cuando no hay señal. */
  getFiscal: (email) => { try { return JSON.parse(localStorage.getItem("pe_fiscal_" + email) || "{}"); } catch { return {}; } },
  saveFiscal: (email, d) => { try { localStorage.setItem("pe_fiscal_" + email, JSON.stringify(d || {})); } catch {} },
  /* Comprobantes electrónicos YA aceptados, por id de venta. Es una copia para poder
     IMPRIMIR el sello sin señal: el original manda siempre desde el servidor. Aquí no
     se guarda nada que no venga ya aceptado por la DGII. */
  getEdocs: (email) => { try { return JSON.parse(localStorage.getItem("pe_edocs_" + email) || "{}"); } catch { return {}; } },
  saveEdocs: (email, d) => { try { localStorage.setItem("pe_edocs_" + email, JSON.stringify(d || {})); } catch {} },
  getSession: () => { try { return JSON.parse(localStorage.getItem("pe_session") || "null"); } catch { return null; } },
  saveSession: (s) => { try { localStorage.setItem("pe_session", JSON.stringify(s)); } catch {} },
  clearSession: () => { try { localStorage.removeItem("pe_session"); } catch {} },
};

function genCode() { return Math.random().toString(36).substring(2, 8).toUpperCase(); }
// UUID v4 válido (para id único de cada registro; compatible con la columna uuid de la tabla 'registros').
function genUUID() {
  try { if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID(); } catch (e) {}
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    var r = Math.random() * 16 | 0, v = c === "x" ? r : (r & 0x3 | 0x8); return v.toString(16);
  });
}
function hashPass(p) { return btoa(p + "pe_salt_2026"); }

// Correos que ven el "Panel del dueño de la app" (config global: prueba gratis, etc.)
const ADMIN_EMAILS = ["andresmmatos@gmail.com", "polloexpressrd@gmail.com"];

const DEFAULT_PERMISOS = {
  verDashboard: true, verInfoCamada: true, verRegistro: false,
  verCalendario: true, verCostos: false, editarCamada: false,
  verVentas: false, editarVentas: false,
  verHistorialCamadas: false, verHistorialRegistros: false,
  verEmpleados: false, verMisCamadas: false, verHecesIA: false,
  verInventario: false,
};


// --- ILUSTRACION SVG DE POLLO BROILER (misma para todas las razas) ---
// --- ÍCONO DEL APARATO POLLO BOX ---
// Dibujado como vector para que se vea nítido en cualquier tamaño (24 px en menús,
// 48 px en encabezados) y pese casi nada. `apagado` lo atenúa cuando no hay señal.
function PolloBoxSVG({ size, apagado }) {
  var s = size || 28;
  var h = Math.round(s * 0.76);
  return (
    <svg width={s} height={h} viewBox="0 0 400 300" xmlns="http://www.w3.org/2000/svg"
      style={{ display: "block", flexShrink: 0, opacity: apagado ? 0.4 : 1, filter: apagado ? "grayscale(1)" : "none" }}>
      {/* carcasa */}
      <rect x="8" y="8" width="384" height="284" rx="34" fill="#E3C93E" stroke="#1A1A1A" strokeWidth="9" />
      {/* pantalla */}
      <rect x="128" y="60" width="148" height="112" fill="#40608C" stroke="#1A1A1A" strokeWidth="9" />
      {/* placa del nombre */}
      <rect x="118" y="196" width="168" height="44" fill="#E3C93E" stroke="#1A1A1A" strokeWidth="7" />
      <text x="202" y="230" textAnchor="middle" fontFamily="'Syne', sans-serif" fontSize="32" fontWeight="900"
        stroke="#1A1A1A" strokeWidth="5" paintOrder="stroke">
        <tspan fill="#D9536F">POLLO</tspan><tspan fill="#8FA3B8"> IA</tspan>
      </text>
    </svg>
  );
}

function BroilerSVG({ size }) {
  var s = size || 90;
  return (
    React.createElement("svg", { width: s, height: s, viewBox: "0 0 120 120", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
      // Cuerpo
      React.createElement("ellipse", { cx: "58", cy: "72", rx: "38", ry: "32", fill: "#FDFDFB" }),
      React.createElement("ellipse", { cx: "58", cy: "72", rx: "38", ry: "32", fill: "#F0EDE6", opacity: "0.5" }),
      // Pechuga
      React.createElement("ellipse", { cx: "44", cy: "78", rx: "22", ry: "24", fill: "#FFFFFF" }),
      // Ala
      React.createElement("path", { d: "M68 62 Q88 66 84 86 Q74 92 64 84 Q62 72 68 62 Z", fill: "#E8E4DA" }),
      React.createElement("path", { d: "M70 68 Q82 71 80 82", stroke: "#D4CFC2", strokeWidth: "1.5", fill: "none" }),
      // Cabeza
      React.createElement("circle", { cx: "40", cy: "44", r: "20", fill: "#FDFDFB" }),
      // Cresta
      React.createElement("path", { d: "M32 28 Q34 20 38 26 Q40 18 44 26 Q47 20 48 28 Q44 32 40 31 Q35 32 32 28 Z", fill: "#E23B2E" }),
      // Pico
      React.createElement("path", { d: "M22 44 L10 47 L22 51 Z", fill: "#F5A623" }),
      // Barbilla
      React.createElement("path", { d: "M26 54 Q24 60 28 60 Q30 56 30 53 Z", fill: "#E23B2E" }),
      // Ojo
      React.createElement("circle", { cx: "36", cy: "42", r: "4.5", fill: "#1A1A1A" }),
      React.createElement("circle", { cx: "37.5", cy: "40.5", r: "1.5", fill: "#FFFFFF" }),
      // Patas
      React.createElement("path", { d: "M48 102 L48 112 M44 112 L52 112", stroke: "#F5A623", strokeWidth: "3", strokeLinecap: "round" }),
      React.createElement("path", { d: "M68 102 L68 112 M64 112 L72 112", stroke: "#F5A623", strokeWidth: "3", strokeLinecap: "round" }),
      // Cola
      React.createElement("path", { d: "M92 64 Q108 56 104 74 Q100 80 92 76 Z", fill: "#E8E4DA" })
    )
  );
}

// --- ILUSTRACION SVG DE GALPON MODERNO DE POLLOS ---
function GalponSVG({ size }) {
  var s = size || 48;
  return (
    React.createElement("svg", { width: s, height: s, viewBox: "0 0 64 64", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
      // Techo curvo moderno (galpón avícola tipo túnel)
      React.createElement("path", { d: "M4 30 Q32 8 60 30 L60 34 Q32 14 4 34 Z", fill: "#D4500A" }),
      React.createElement("path", { d: "M4 30 Q32 8 60 30", stroke: "#B33D00", strokeWidth: "1.5", fill: "none" }),
      // Cuerpo del galpón
      React.createElement("rect", { x: "8", y: "32", width: "48", height: "24", rx: "2", fill: "#E8E4DA" }),
      React.createElement("rect", { x: "8", y: "32", width: "48", height: "24", rx: "2", fill: "#F5F2EC", opacity: "0.4" }),
      // Cortinas laterales (ventilación típica de galpón)
      React.createElement("rect", { x: "11", y: "38", width: "42", height: "7", fill: "#C8C2B4", opacity: "0.7" }),
      React.createElement("line", { x1: "18", y1: "38", x2: "18", y2: "45", stroke: "#A89E8C", strokeWidth: "1" }),
      React.createElement("line", { x1: "25", y1: "38", x2: "25", y2: "45", stroke: "#A89E8C", strokeWidth: "1" }),
      React.createElement("line", { x1: "32", y1: "38", x2: "32", y2: "45", stroke: "#A89E8C", strokeWidth: "1" }),
      React.createElement("line", { x1: "39", y1: "38", x2: "39", y2: "45", stroke: "#A89E8C", strokeWidth: "1" }),
      React.createElement("line", { x1: "46", y1: "38", x2: "46", y2: "45", stroke: "#A89E8C", strokeWidth: "1" }),
      // Puerta
      React.createElement("rect", { x: "28", y: "47", width: "8", height: "9", rx: "1", fill: "#8A6D3B" }),
      // Ventilador extractor (círculo en el frente)
      React.createElement("circle", { cx: "48", cy: "50", r: "3.5", fill: "#FFFFFF", stroke: "#A89E8C", strokeWidth: "1" }),
      React.createElement("path", { d: "M48 47 L48 53 M45 50 L51 50", stroke: "#A89E8C", strokeWidth: "1" })
    )
  );
}

// --- ILUSTRACION SVG DE POLLITO BEBE RECIEN RECIBIDO ---
function PollitoSVG({ size }) {
  var s = size || 48;
  return (
    React.createElement("svg", { width: s, height: s, viewBox: "0 0 64 64", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
      // Cuerpo amarillo esponjoso
      React.createElement("ellipse", { cx: "32", cy: "40", rx: "18", ry: "16", fill: "#FFD23F" }),
      React.createElement("ellipse", { cx: "32", cy: "40", rx: "18", ry: "16", fill: "#FFDE6B", opacity: "0.5" }),
      // Cabeza
      React.createElement("circle", { cx: "32", cy: "24", r: "13", fill: "#FFD23F" }),
      React.createElement("circle", { cx: "32", cy: "24", r: "13", fill: "#FFE285", opacity: "0.4" }),
      // Mejilla
      React.createElement("circle", { cx: "24", cy: "27", r: "2.5", fill: "#FFB3B3", opacity: "0.6" }),
      // Pico
      React.createElement("path", { d: "M20 24 L13 26 L20 28 Z", fill: "#F5A623" }),
      // Ojo
      React.createElement("circle", { cx: "27", cy: "22", r: "3", fill: "#1A1A1A" }),
      React.createElement("circle", { cx: "28", cy: "21", r: "1", fill: "#FFFFFF" }),
      // Copete (plumita arriba)
      React.createElement("path", { d: "M32 11 Q30 5 33 8 Q34 4 36 9 Q38 6 37 12 Z", fill: "#FFC107" }),
      // Ala
      React.createElement("path", { d: "M42 38 Q50 40 46 50 Q40 50 40 42 Z", fill: "#FFC107" }),
      // Patas
      React.createElement("path", { d: "M27 55 L27 60 M24 60 L30 60", stroke: "#F5A623", strokeWidth: "2", strokeLinecap: "round" }),
      React.createElement("path", { d: "M37 55 L37 60 M34 60 L40 60", stroke: "#F5A623", strokeWidth: "2", strokeLinecap: "round" })
    )
  );
}

// --- TOMBSTONES: items eliminados (ventas/gastos/registros) para que no resuciten en el merge ---
// ── REGISTROS DIARIOS: sincronización entre teléfonos ──
// Varios teléfonos (dueño y empleados) escriben en la MISMA camada. Reglas:
//  · Cada registro lleva _ts = hora de su último cambio. Entre dos versiones del mismo
//    registro gana la de _ts más nuevo (por eso editar DEBE poner _ts nuevo).
//  · Borrar NO quita el registro: lo cambia por una marca {id, borrado:true, _ts}. Si se
//    quitara, el teléfono de otro empleado que aún lo tiene lo volvería a subir.
//  · Guardar junta lo del teléfono con lo que hay en la nube: nadie pisa lo de otro.
function claveRegistro(r) { return r && r.id != null ? String(r.id) : (r.dia + "_" + (r.fecha || "")); }
function fusionarRegistros(a, b) {
  var mapa = {}, orden = [];
  (a || []).concat(b || []).forEach(function (r) {
    if (!r) return;
    var k = claveRegistro(r);
    if (!(k in mapa)) { mapa[k] = r; orden.push(k); return; }
    if (Number(r._ts || 0) > Number(mapa[k]._ts || 0)) mapa[k] = r;   // gana el cambio más nuevo
  });
  return orden.map(function (k) { return mapa[k]; });
}
// Un registro diario pertenece a UNA camada. Desde 2026-10-11 cada uno lleva "camadaId";
// si llegara uno marcado con otra camada (datos viejos mezclados, o un guardado raro), se
// descarta aquí. Los registros viejos, sin la marca, se respetan tal cual.
function soloDeEstaCamada(regs, camadaId) {
  if (!camadaId) return regs || [];
  return (regs || []).filter(function (r) { return !r || !r.camadaId || r.camadaId === camadaId; });
}
function separarBorrados(regs) {
  var visibles = [], borrados = [];
  (regs || []).forEach(function (r) { if (r) (r.borrado ? borrados : visibles).push(r); });
  return { visibles: visibles, borrados: borrados };
}
// "vivos" de cada registro = recibidos − muertes acumuladas hasta ese registro (en orden).
function recalcularVivosRegistros(regs, totalPollos) {
  var ordenados = (regs || []).slice().sort(function (a, b) {
    if ((a.dia || 0) !== (b.dia || 0)) return (a.dia || 0) - (b.dia || 0);
    return (a._ts || 0) - (b._ts || 0);
  });
  var acum = 0, vivosPorId = {};
  ordenados.forEach(function (r) { acum += Number(r.mortalidad || 0); vivosPorId[claveRegistro(r)] = Math.max(0, (Number(totalPollos) || 0) - acum); });
  return (regs || []).map(function (r) { return Object.assign({}, r, { vivos: vivosPorId[claveRegistro(r)] }); });
}

function getDeletedItems(ownerEmail) {
  try { return JSON.parse(localStorage.getItem("pe_del_items_" + ownerEmail) || '{"ventas":[],"gastos":[],"registros":[]}'); }
  catch { return { ventas: [], gastos: [], registros: [] }; }
}
function addDeletedItem(ownerEmail, tipo, key) {
  var d = getDeletedItems(ownerEmail);
  if (!d[tipo]) d[tipo] = [];
  if (d[tipo].indexOf(String(key)) === -1) d[tipo].push(String(key));
  try { localStorage.setItem("pe_del_items_" + ownerEmail, JSON.stringify(d)); } catch {}
}

const initCamada = (ownerEmail, num = 1) => ({
  id: "cam_" + Date.now() + "_" + Math.random().toString(36).slice(2,6),
  nombre: "Camada #" + num,
  galpon: "Galpón 1",
  numeroCamada: num,
  raza: "Ross 308",
  fechaInicio: new Date().toISOString().split("T")[0],
  totalPollos: 800,
  registros: [], gastos: [], ventas: [], precioVenta: 0, owner: ownerEmail,
});


/* =====================================================================
   PAÍS FISCAL
   El país NO se deduce de la moneda. Son dos ajustes separados a propósito:
   el país decide la legislación y la integración tributaria, la moneda decide
   en qué se escriben los importes.
   "fiscal" dice si ya existe integración de facturación electrónica. Hoy solo
   República Dominicana; en los demás la app funciona igual con su facturación
   interna y se enseña "próximamente".
   ===================================================================== */
const PAISES_FISCALES = [
  // ── Hispanoamérica ──────────────────────────────────────────────────────
  { iso:"DO", nombre:"República Dominicana", bandera:"🇩🇴", moneda:"DOP", locale:"es-DO", fiscal:"si",     autoridad:"DGII",   grupo:"Hispanoamérica" },
  { iso:"MX", nombre:"México",               bandera:"🇲🇽", moneda:"MXN", locale:"es-MX", fiscal:"pronto", autoridad:"SAT",    grupo:"Hispanoamérica" },
  { iso:"CO", nombre:"Colombia",             bandera:"🇨🇴", moneda:"COP", locale:"es-CO", fiscal:"pronto", autoridad:"DIAN",   grupo:"Hispanoamérica" },
  { iso:"AR", nombre:"Argentina",            bandera:"🇦🇷", moneda:"ARS", locale:"es-AR", fiscal:"pronto", autoridad:"",       grupo:"Hispanoamérica" },
  { iso:"PE", nombre:"Perú",                 bandera:"🇵🇪", moneda:"PEN", locale:"es-PE", fiscal:"pronto", autoridad:"SUNAT",  grupo:"Hispanoamérica" },
  { iso:"CL", nombre:"Chile",                bandera:"🇨🇱", moneda:"CLP", locale:"es-CL", fiscal:"pronto", autoridad:"SII",    grupo:"Hispanoamérica" },
  { iso:"EC", nombre:"Ecuador",              bandera:"🇪🇨", moneda:"USD", locale:"es-EC", fiscal:"pronto", autoridad:"SRI",    grupo:"Hispanoamérica" },
  { iso:"GT", nombre:"Guatemala",            bandera:"🇬🇹", moneda:"GTQ", locale:"es-GT", fiscal:"pronto", autoridad:"SAT",    grupo:"Hispanoamérica" },
  { iso:"CR", nombre:"Costa Rica",           bandera:"🇨🇷", moneda:"CRC", locale:"es-CR", fiscal:"pronto", autoridad:"Hacienda", grupo:"Hispanoamérica" },
  { iso:"PA", nombre:"Panamá",               bandera:"🇵🇦", moneda:"PAB", locale:"es-PA", fiscal:"pronto", autoridad:"DGI",    grupo:"Hispanoamérica" },
  { iso:"SV", nombre:"El Salvador",          bandera:"🇸🇻", moneda:"USD", locale:"es-SV", fiscal:"pronto", autoridad:"DGII",   grupo:"Hispanoamérica" },
  { iso:"HN", nombre:"Honduras",             bandera:"🇭🇳", moneda:"HNL", locale:"es-HN", fiscal:"pronto", autoridad:"SAR",    grupo:"Hispanoamérica" },
  { iso:"NI", nombre:"Nicaragua",            bandera:"🇳🇮", moneda:"NIO", locale:"es-NI", fiscal:"pronto", autoridad:"DGI",    grupo:"Hispanoamérica" },
  { iso:"BO", nombre:"Bolivia",              bandera:"🇧🇴", moneda:"BOB", locale:"es-BO", fiscal:"pronto", autoridad:"SIN",    grupo:"Hispanoamérica" },
  { iso:"PY", nombre:"Paraguay",             bandera:"🇵🇾", moneda:"PYG", locale:"es-PY", fiscal:"pronto", autoridad:"",       grupo:"Hispanoamérica" },
  { iso:"UY", nombre:"Uruguay",              bandera:"🇺🇾", moneda:"UYU", locale:"es-UY", fiscal:"pronto", autoridad:"DGI",    grupo:"Hispanoamérica" },
  { iso:"VE", nombre:"Venezuela",            bandera:"🇻🇪", moneda:"VES", locale:"es-VE", fiscal:"pronto", autoridad:"SENIAT", grupo:"Hispanoamérica" },
  { iso:"CU", nombre:"Cuba",                 bandera:"🇨🇺", moneda:"CUP", locale:"es-CU", fiscal:"no",     autoridad:"",       grupo:"Hispanoamérica" },
  { iso:"PR", nombre:"Puerto Rico",          bandera:"🇵🇷", moneda:"USD", locale:"es-PR", fiscal:"no",     autoridad:"",       grupo:"Hispanoamérica" },

  // ── Brasil ──────────────────────────────────────────────────────────────
  { iso:"BR", nombre:"Brasil",               bandera:"🇧🇷", moneda:"BRL", locale:"pt-BR", fiscal:"pronto", autoridad:"SEFAZ",  grupo:"Brasil" },

  // ── España ──────────────────────────────────────────────────────────────
  { iso:"ES", nombre:"España",               bandera:"🇪🇸", moneda:"EUR", locale:"es-ES", fiscal:"pronto", autoridad:"AEAT",   grupo:"España" },
  { iso:"GQ", nombre:"Guinea Ecuatorial",    bandera:"🇬🇶", moneda:"XAF", locale:"es-GQ", fiscal:"no",     autoridad:"",       grupo:"España" },

  // ── Caribe anglófono (los vecinos: mismo clima, misma avicultura) ───────
  { iso:"JM", nombre:"Jamaica",              bandera:"🇯🇲", moneda:"JMD", locale:"en-JM", fiscal:"no", autoridad:"", grupo:"Caribe anglófono" },
  { iso:"TT", nombre:"Trinidad y Tobago",    bandera:"🇹🇹", moneda:"TTD", locale:"en-TT", fiscal:"no", autoridad:"", grupo:"Caribe anglófono" },
  { iso:"BB", nombre:"Barbados",             bandera:"🇧🇧", moneda:"BBD", locale:"en-BB", fiscal:"no", autoridad:"", grupo:"Caribe anglófono" },
  { iso:"BS", nombre:"Bahamas",              bandera:"🇧🇸", moneda:"BSD", locale:"en-BS", fiscal:"no", autoridad:"", grupo:"Caribe anglófono" },
  { iso:"GY", nombre:"Guyana",               bandera:"🇬🇾", moneda:"GYD", locale:"en-GY", fiscal:"no", autoridad:"", grupo:"Caribe anglófono" },
  { iso:"BZ", nombre:"Belice",               bandera:"🇧🇿", moneda:"BZD", locale:"en-BZ", fiscal:"no", autoridad:"", grupo:"Caribe anglófono" },
  { iso:"AG", nombre:"Antigua y Barbuda",    bandera:"🇦🇬", moneda:"XCD", locale:"en-AG", fiscal:"no", autoridad:"", grupo:"Caribe anglófono" },
  { iso:"LC", nombre:"Santa Lucía",          bandera:"🇱🇨", moneda:"XCD", locale:"en-LC", fiscal:"no", autoridad:"", grupo:"Caribe anglófono" },
  { iso:"VC", nombre:"San Vicente y las Granadinas", bandera:"🇻🇨", moneda:"XCD", locale:"en-VC", fiscal:"no", autoridad:"", grupo:"Caribe anglófono" },
  { iso:"GD", nombre:"Granada",              bandera:"🇬🇩", moneda:"XCD", locale:"en-GD", fiscal:"no", autoridad:"", grupo:"Caribe anglófono" },
  { iso:"KN", nombre:"San Cristóbal y Nieves", bandera:"🇰🇳", moneda:"XCD", locale:"en-KN", fiscal:"no", autoridad:"", grupo:"Caribe anglófono" },
  { iso:"DM", nombre:"Dominica",             bandera:"🇩🇲", moneda:"XCD", locale:"en-DM", fiscal:"no", autoridad:"", grupo:"Caribe anglófono" },

  // ── Norteamérica, Europa y Oceanía ──────────────────────────────────────
  { iso:"US", nombre:"Estados Unidos",       bandera:"🇺🇸", moneda:"USD", locale:"en-US", fiscal:"no", autoridad:"", grupo:"Norteamérica, Europa y Oceanía" },
  { iso:"CA", nombre:"Canadá",               bandera:"🇨🇦", moneda:"CAD", locale:"en-CA", fiscal:"no", autoridad:"", grupo:"Norteamérica, Europa y Oceanía" },
  { iso:"GB", nombre:"Reino Unido",          bandera:"🇬🇧", moneda:"GBP", locale:"en-GB", fiscal:"no", autoridad:"", grupo:"Norteamérica, Europa y Oceanía" },
  { iso:"IE", nombre:"Irlanda",              bandera:"🇮🇪", moneda:"EUR", locale:"en-IE", fiscal:"no", autoridad:"", grupo:"Norteamérica, Europa y Oceanía" },
  { iso:"AU", nombre:"Australia",            bandera:"🇦🇺", moneda:"AUD", locale:"en-AU", fiscal:"no", autoridad:"", grupo:"Norteamérica, Europa y Oceanía" },
  { iso:"NZ", nombre:"Nueva Zelanda",        bandera:"🇳🇿", moneda:"NZD", locale:"en-NZ", fiscal:"no", autoridad:"", grupo:"Norteamérica, Europa y Oceanía" },

  // ── África anglófona ────────────────────────────────────────────────────
  { iso:"NG", nombre:"Nigeria",              bandera:"🇳🇬", moneda:"NGN", locale:"en-NG", fiscal:"pronto", autoridad:"", grupo:"África" },
  { iso:"GH", nombre:"Ghana",                bandera:"🇬🇭", moneda:"GHS", locale:"en-GH", fiscal:"pronto", autoridad:"GRA", grupo:"África" },
  { iso:"KE", nombre:"Kenia",                bandera:"🇰🇪", moneda:"KES", locale:"en-KE", fiscal:"pronto", autoridad:"KRA", grupo:"África" },
  { iso:"ZA", nombre:"Sudáfrica",            bandera:"🇿🇦", moneda:"ZAR", locale:"en-ZA", fiscal:"no", autoridad:"", grupo:"África" },
  { iso:"TZ", nombre:"Tanzania",             bandera:"🇹🇿", moneda:"TZS", locale:"en-TZ", fiscal:"no", autoridad:"", grupo:"África" },
  { iso:"UG", nombre:"Uganda",               bandera:"🇺🇬", moneda:"UGX", locale:"en-UG", fiscal:"no", autoridad:"", grupo:"África" },

  // ── Asia anglófona ──────────────────────────────────────────────────────
  { iso:"IN", nombre:"India",                bandera:"🇮🇳", moneda:"INR", locale:"en-IN", fiscal:"pronto", autoridad:"GST", grupo:"Asia" },
  { iso:"PH", nombre:"Filipinas",            bandera:"🇵🇭", moneda:"PHP", locale:"en-PH", fiscal:"pronto", autoridad:"BIR", grupo:"Asia" },
  { iso:"MY", nombre:"Malasia",              bandera:"🇲🇾", moneda:"MYR", locale:"en-MY", fiscal:"pronto", autoridad:"LHDN", grupo:"Asia" },
  { iso:"SG", nombre:"Singapur",             bandera:"🇸🇬", moneda:"SGD", locale:"en-SG", fiscal:"no", autoridad:"", grupo:"Asia" },
  { iso:"PK", nombre:"Pakistán",             bandera:"🇵🇰", moneda:"PKR", locale:"en-PK", fiscal:"no", autoridad:"", grupo:"Asia" },
  { iso:"LK", nombre:"Sri Lanka",            bandera:"🇱🇰", moneda:"LKR", locale:"en-LK", fiscal:"no", autoridad:"", grupo:"Asia" },
  { iso:"BD", nombre:"Bangladés",            bandera:"🇧🇩", moneda:"BDT", locale:"en-BD", fiscal:"no", autoridad:"", grupo:"Asia" },
];
/* El orden de los grupos en el menú. */
const GRUPOS_PAIS = ["Hispanoamérica","Brasil","España","Caribe anglófono","Norteamérica, Europa y Oceanía","África","Asia"];
/* Las monedas que se le ofrecen al productor. Es una LISTA DE DATOS y no
   marcado escrito a mano: la pantalla de Mi Granja y Facturación la recorre, y
   si mañana hace falta en otro sitio se recorre igual. El código es ISO 4217.
   Escoger moneda NO cambia el país fiscal ni reconvierte lo ya guardado. */
const MONEDAS_FACTURA = [
  { grupo: "🌎 El Caribe y Centroamérica", items: [
    ["DOP", "🇩🇴 Peso Dominicano — RD$"], ["JMD", "🇯🇲 Dólar Jamaiquino — J$"],
    ["TTD", "🇹🇹 Dólar Trinidad — TT$"],  ["GTQ", "🇬🇹 Quetzal — Q"],
    ["HNL", "🇭🇳 Lempira — L"],           ["NIO", "🇳🇮 Córdoba — C$"],
    ["USD", "🇸🇻 🇪🇨 🇵🇦 Dólar estadounidense — $"], ["HTG", "🇭🇹 Gourde Haitiano — G"],
    ["CRC", "🇨🇷 Colón Costarricense — ₡"], ["PAB", "🇵🇦 Balboa Panameño — B/."],
    ["CUP", "🇨🇺 Peso Cubano — ₱"],
  ] },
  { grupo: "🌎 Sudamérica", items: [
    ["COP", "🇨🇴 Peso Colombiano — $"], ["VES", "🇻🇪 Bolívar — Bs."],
    ["PEN", "🇵🇪 Sol Peruano — S/"],    ["BRL", "🇧🇷 Real Brasileño — R$"],
    ["ARS", "🇦🇷 Peso Argentino — $"],  ["CLP", "🇨🇱 Peso Chileno — $"],
    ["BOB", "🇧🇴 Boliviano — Bs."],     ["PYG", "🇵🇾 Guaraní — ₲"],
    ["MXN", "🇲🇽 Peso Mexicano — $"],
  ] },
  { grupo: "🌍 África", items: [
    ["NGN", "🇳🇬 Naira Nigeriana — ₦"], ["GHS", "🇬🇭 Cedi Ghanés — ₵"],
    ["KES", "🇰🇪 Chelín Keniano — KSh"], ["TZS", "🇹🇿 Chelín Tanzano — TSh"],
    ["UGX", "🇺🇬 Chelín Ugandés — USh"], ["XOF", "🇨🇮 Franco CFA — CFA"],
    ["XAF", "🇨🇲 Franco CFA Central — FCFA"], ["ETB", "🇪🇹 Birr Etíope — Br"],
    ["EGP", "🇪🇬 Libra Egipcia — E£"], ["MAD", "🇲🇦 Dírham Marroquí — MAD"],
  ] },
  { grupo: "🌏 Asia", items: [
    ["INR", "🇮🇳 Rupia India — ₹"], ["PHP", "🇵🇭 Peso Filipino — ₱"],
    ["IDR", "🇮🇩 Rupia Indonesia — Rp"], ["BDT", "🇧🇩 Taka Bangladesí — ৳"],
    ["PKR", "🇵🇰 Rupia Pakistaní — ₨"], ["VND", "🇻🇳 Dong Vietnamita — ₫"],
    ["LKR", "🇱🇰 Rupia Sri Lanka — Rs"],
  ] },
  { grupo: "🌐 Otras", items: [
    ["EUR", "🇪🇺 Euro — €"], ["GBP", "🇬🇧 Libra Esterlina — £"],
    ["USD", "🇺🇸 Dólar USA — $"],
  ] },
];

function paisFiscal(iso) {
  return PAISES_FISCALES.find(p => p.iso === iso) || null;
}
/* Los códigos viejos USD_SV y USD_EC no son ISO 4217: los dos son dólares. Se
   traducen al vuelo para no tener que tocar ni un dato guardado. */
const MONEDA_HEREDADA = { USD_SV: "USD", USD_EC: "USD" };
function monedaISO(m) {
  return MONEDA_HEREDADA[m] || m || "DOP";
}
/* El idioma con el que se escriben los números. Sale del país fiscal si lo hay;
   si no, del idioma de la app. Antes estaba clavado en "es-DO" para todo el
   mundo, así que un colombiano leía "1,234.56" donde él escribe "1.234,56". */
let LOCALE_NUM = "es-DO";
function fijarLocaleNumeros(isoPais, idioma) {
  const p = paisFiscal(isoPais);
  LOCALE_NUM = (p && p.locale) || (idioma === "en" ? "en-US" : idioma === "pt" ? "pt-BR" : "es-DO");
}
/* Importes de dinero. Usa el formato de verdad del país (separadores y posición
   del símbolo), con el símbolo de la app como respaldo si la moneda no la
   conoce el navegador. La cantidad de decimales la manda la moneda: el peso
   colombiano no lleva centavos, el dominicano sí. */
function fmtDinero(n, moneda, conSimbolo) {
  const cod = monedaISO(moneda);
  const v = Number(n || 0);
  try {
    return new Intl.NumberFormat(LOCALE_NUM, {
      style: conSimbolo === false ? "decimal" : "currency",
      currency: cod, currencyDisplay: "narrowSymbol",
    }).format(v);
  } catch (e) {
    const dec = cod === "COP" || cod === "CRC" || cod === "PYG" || cod === "CLP" ? 0 : 2;
    return (conSimbolo === false ? "" : monedaSimbolo(cod) + " ") +
      v.toLocaleString(LOCALE_NUM, { minimumFractionDigits: dec, maximumFractionDigits: dec });
  }
}

// --- MONEDAS: código → símbolo (cubre TODAS las opciones del menú de Ajustes) ---
const MONEDA_SIMBOLOS = {
  DOP: "RD$", USD: "$", EUR: "€", GBP: "£", CAD: "CA$", AUD: "A$",
  MXN: "MX$", COP: "COP$", VES: "Bs.", PEN: "S/.", CLP: "CLP$",
  ARS: "AR$", BRL: "R$", GTQ: "Q", HNL: "L", NIO: "C$", CRC: "₡",
  PAB: "B/.", JMD: "J$", TTD: "TT$", BBD: "Bds$", BSD: "B$",
  GYD: "G$", SRD: "Sr$", BOB: "Bs.", PYG: "₲", UYU: "$U",
  HTG: "G", XCD: "EC$", CUP: "₱", USD_SV: "$", USD_EC: "$",
  NGN: "₦", GHS: "GH₵", KES: "KSh", TZS: "TSh", UGX: "USh",
  XOF: "CFA", XAF: "FCFA", ETB: "Br", EGP: "E£", MAD: "MAD", ZAR: "R",
  INR: "₹", PHP: "₱", IDR: "Rp", THB: "฿", VND: "₫", NZD: "NZ$", BZD: "BZ$", SGD: "S$", MYR: "RM",
  BDT: "৳", PKR: "₨", LKR: "Rs",
};
// Símbolo para cualquier moneda: si el código no está en el mapa, muestra el código mismo
// (nunca cae a RD$ por error — ese era el bug de las facturas con monedas extranjeras).
function monedaSimbolo(moneda) {
  const m = MONEDA_HEREDADA[moneda] || moneda;
  return MONEDA_SIMBOLOS[m] || m || "RD$";
}

/* =====================================================================
   QUIÉN VENDE (el emisor de la factura).

   El vendedor es LA GRANJA. Pollo IA solo es el programa que imprime el papel,
   así que su nombre va chiquito debajo y nunca en lugar del de la granja.

   De dónde sale cada cosa:
     · nombre y logo → ajustes de la granja (se guardan al instante, sin señal)
     · razón social y RNC → copia local de lo que hay en el servidor, que es
       donde viven de verdad (tabla con RLS, la llave anónima no la ve)
   Si no hay RNC configurado, simplemente no se imprime: una factura sin RNC es
   una factura interna normal, no una factura mal hecha.
   ===================================================================== */
function emisorFactura(session, userSettings) {
  const u = userSettings || {};
  const f = (session && session.ownerEmail) ? (DB.getFiscal(session.ownerEmail) || {}) : {};
  const nombre = String(u.nombreGranja || "").trim() ||
    (session && session.ownerEmail ? session.ownerEmail.split("@")[0].toUpperCase() : "MI GRANJA");
  /* El RNC sale de la copia local del servidor; si no la hay —el teléfono del
     empleado, que no puede leer fiscal_config— se cae a la copia que viaja en
     los ajustes de la granja. Así la factura lleva el RNC la saque quien la saque. */
  const rnc = String(f.rnc || u.rncGranja || "").replace(/[^0-9]/g, "");
  return {
    nombre: nombre,
    logo: u.logoGranja || null,
    razonSocial: String(f.razonSocial || u.razonSocialGranja || "").trim(),
    rnc: rnc,
    // 9 dígitos = RNC, 11 = cédula. Se etiqueta bien en vez de poner "RNC/Céd".
    docEtiqueta: rnc.length === 11 ? "Cédula" : "RNC",
    direccion: String(u.direccionGranja || "").trim(),
    telefono: String(u.telefonoGranja || "").trim(),
    correo: String(u.correoGranja || "").trim(),
    pais: u.paisFiscal || "",
    // ¿Tiene la facturación electrónica encendida y lista? Lo dice la copia local.
    fiscalActivo: !!f.activo && f.estado === "conectado",
  };
}

// --- GENERAR FACTURA PDF + WHATSAPP ---
/* ¿Esta factura ES un comprobante fiscal electrónico, de verdad?

   Solo si la DGII lo ACEPTÓ y devolvió un e-NCF. Un documento "interno", "enviado",
   "rechazado" o de la simulación NO cuenta, por mucho que el interruptor de la granja
   esté encendido: el interruptor dice que la granja puede emitir, no que ESTE papel
   sea un comprobante. Pintar el sello verde sin esto sería decirle al comprador que
   tiene un comprobante fiscal que no tiene, y eso lo paga el productor, no la app. */
function eCFValido(doc) {
  if (!doc || doc.estado !== "aceptado" || !doc.encf) return null;
  return doc;
}

/* El QR de verificación de la DGII solo se pinta si el proveedor lo mandó ya como
   imagen. Ni se inventa ni se dibuja uno "parecido": un QR falso en una factura es
   peor que no tener QR, porque el comprador lo escanea y no llega a ninguna parte. */
function qrDelECF(doc) {
  const u = doc && doc.qrUrl;
  return (typeof u === "string" && /^data:image\//.test(u)) ? u : null;
}

function generarFactura(venta, camada, session, userSettings, docFiscal) {
  var subtotal = Number(venta.libras) * Number(venta.precioLb);
  var lbAve = venta.pollos > 0 ? (Number(venta.libras) / Number(venta.pollos)).toFixed(2) : "0";
  var fecha = venta.fecha || new Date().toLocaleDateString("es-DO");
  var numFactura = "F-" + Date.now().toString().slice(-6);
  var em = emisorFactura(session, userSettings);
  var granja = em.nombre;
  var logoGranja = em.logo;
  var comprador = venta.comprador || "Cliente";
  var moneda = (userSettings && userSettings.moneda) ? userSettings.moneda : "DOP";
  var simbolo = monedaSimbolo(moneda);
  var isEn = userSettings && userSettings.idioma === "en";

  // Textos según idioma
  var txt = isEn ? {
    titulo: "INVOICE", comprador: "Buyer", camada: "Flock", galpon: "Barn",
    cantidad: "Number of chickens", libras: "Total pounds", lbAve: "Lb per bird",
    precio: "Price per pound", total: "TOTAL TO PAY", guardarPDF: "🖨️ Save PDF",
    whatsapp: "📲 WhatsApp", footer: "Generated by Pollo IA · polloia.net",
    aves: "birds", waMsg: "🐔 *INVOICE*",
    interna: "Internal document - not an electronic tax receipt",
    rncEmisor: "Tax ID", direccion: "Address", tel: "Phone",
    ecfT1: "ELECTRONIC", ecfT2: "INVOICE",
    ecfNota: "Electronic Tax Receipt authorized by the DGII of the Dominican Republic.",
    ecfCod: "Security code", ecfPie: "Electronic Tax Receipt - e-NCF "
  } : {
    titulo: "FACTURA", comprador: "Comprador", camada: "Camada", galpon: "Galpón",
    cantidad: "Cantidad de pollos", libras: "Libras totales", lbAve: "Lb por ave",
    precio: "Precio por libra", total: "TOTAL A PAGAR", guardarPDF: "🖨️ Guardar PDF",
    whatsapp: "📲 WhatsApp", footer: "Generado por Pollo IA · polloia.net",
    aves: "aves", waMsg: "🐔 *FACTURA*",
    /* Que nadie confunda esto con un comprobante fiscal. Mientras no haya e-NCF
       aceptado por la DGII, esta factura es interna y así se dice. */
    interna: "Documento interno · no es un comprobante fiscal electrónico",
    rncEmisor: "RNC", direccion: "Dirección", tel: "Tel",
    ecfT1: "FACTURA", ecfT2: "ELECTRÓNICA",
    ecfNota: "Comprobante Fiscal Electrónico autorizado por la DGII de la República Dominicana.",
    ecfCod: "Código de seguridad", ecfPie: "Comprobante Fiscal Electrónico · e-NCF "
  };

  var ecf = eCFValido(docFiscal);
  var ecfQR = qrDelECF(ecf);

  var html = '<!DOCTYPE html><html><head><meta charset="UTF-8"><title>' + txt.titulo + ' ' + numFactura + '</title>' +
    '<style>body{font-family:Arial,sans-serif;max-width:400px;margin:0 auto;padding:20px;color:#111}' +
    '.header{text-align:center;border-bottom:3px solid #f5c200;padding-bottom:16px;margin-bottom:20px}' +
    '.logo{font-size:28px;font-weight:900;color:#111}.logo span{color:#cc1a00}' +
    '.emisor-legal{font-size:12px;color:#444;font-weight:700;margin-top:3px}' +
    '.emisor-datos{font-size:11px;color:#777;margin-top:3px;line-height:1.5}' +
    '.interna{font-size:10px;color:#999;margin-top:6px;font-style:italic}' +
    '.subtitle{font-size:12px;color:#666;margin-top:4px}' +
    '.factura-num{font-size:13px;color:#888;margin-top:8px}' +
    '.section{margin-bottom:16px}' +
    '.label{font-size:11px;color:#888;text-transform:uppercase;letter-spacing:1px;margin-bottom:4px}' +
    '.value{font-size:15px;font-weight:700;color:#111}' +
    '.row{display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid #eee}' +
    '.row-label{color:#555;font-size:14px}.row-value{font-weight:700;font-size:14px}' +
    '.total{background:#f5c200;padding:14px;border-radius:8px;text-align:center;margin-top:20px}' +
    '.total-label{font-size:12px;color:#333;text-transform:uppercase}' +
    '.total-value{font-size:28px;font-weight:900;color:#111}' +
    '.footer{text-align:center;margin-top:24px;font-size:11px;color:#aaa;border-top:1px solid #eee;padding-top:12px}' +
    '@media print{body{max-width:100%}button{display:none!important}}' +
    '.btn-row{display:flex;gap:10px;margin-top:20px}' +
    '.btn{flex:1;padding:12px;border:none;border-radius:8px;font-size:15px;font-weight:700;cursor:pointer}' +
    '.btn-pdf{background:#111;color:#fff}.btn-wa{background:#25D366;color:#fff}' +
    /* El sello de factura electrónica: el cuadrado verde que se ve de un golpe.
       Sale ARRIBA, pegado al encabezado, porque es lo primero que tiene que ver
       quien recibe la factura por WhatsApp. */
    '.ecf{display:flex;align-items:center;gap:12px;border:2px solid #0f8a3c;' +
      'border-radius:10px;padding:11px;margin-bottom:18px;background:#f1fbf4}' +
    '.ecf-sello{width:84px;height:84px;flex-shrink:0;background:#0f8a3c;border-radius:9px;' +
      'color:#fff;display:flex;flex-direction:column;align-items:center;justify-content:center;' +
      'text-align:center;line-height:1.15}' +
    '.ecf-check{font-size:21px;font-weight:900;line-height:1}' +
    '.ecf-t1{font-size:8.5px;font-weight:700;letter-spacing:.6px;margin-top:3px}' +
    '.ecf-t2{font-size:9px;font-weight:900;letter-spacing:.2px}' +
    '.ecf-t3{font-size:13px;font-weight:900;letter-spacing:2px;margin-top:4px;padding-top:4px;' +
      'border-top:1px solid rgba(255,255,255,.55);width:56px}' +
    '.ecf-d{flex:1;min-width:0}' +
    '.ecf-lbl{font-size:8.5px;color:#0f8a3c;text-transform:uppercase;letter-spacing:1.2px;font-weight:700}' +
    '.ecf-encf{font-family:"Courier New",monospace;font-size:15px;font-weight:900;color:#111;' +
      'word-break:break-all;line-height:1.2;margin-top:1px}' +
    '.ecf-nota{font-size:9.5px;color:#4a4a4a;margin-top:5px;line-height:1.4}' +
    '.ecf-cod{font-size:9px;color:#888;margin-top:3px;font-family:"Courier New",monospace}' +
    '.ecf-qr{width:74px;height:74px;flex-shrink:0}' +
    '</style></head><body>' +
    '<div class="header">' +
    (logoGranja ? '<img src="' + logoGranja + '" style="max-height:70px;max-width:180px;object-fit:contain;margin-bottom:8px" /><br>' : '') +
    '<div class="logo">' + granja + '</div>' +
    // La razón social solo si es distinta del nombre comercial: si no, se repite tonto.
    (em.razonSocial && em.razonSocial.toUpperCase() !== granja.toUpperCase()
      ? '<div class="emisor-legal">' + em.razonSocial + '</div>' : '') +
    // Datos fiscales del EMISOR. No se imprimen los que no tiene.
    (function () {
      var p = [];
      if (em.rnc) p.push(em.docEtiqueta + ': ' + em.rnc);
      if (em.direccion) p.push(em.direccion);
      if (em.telefono) p.push(txt.tel + ': ' + em.telefono);
      if (em.correo) p.push(em.correo);
      return p.length ? '<div class="emisor-datos">' + p.join(' &middot; ') + '</div>' : '';
    })() +
    '<div class="subtitle">polloia.net</div>' +
    '<div class="factura-num">' + txt.titulo + ' #' + numFactura + ' · ' + fecha + '</div>' +
    '</div>' +
    (ecf
      ? '<div class="ecf">' +
          '<div class="ecf-sello">' +
            '<div class="ecf-check">&#10003;</div>' +
            '<div class="ecf-t1">' + txt.ecfT1 + '</div>' +
            '<div class="ecf-t2">' + txt.ecfT2 + '</div>' +
            '<div class="ecf-t3">DGII</div>' +
          '</div>' +
          '<div class="ecf-d">' +
            '<div class="ecf-lbl">e-NCF</div>' +
            '<div class="ecf-encf">' + ecf.encf + '</div>' +
            '<div class="ecf-nota">' + txt.ecfNota + '</div>' +
            (ecf.codigoSeguridad
              ? '<div class="ecf-cod">' + txt.ecfCod + ': ' + ecf.codigoSeguridad + '</div>' : '') +
          '</div>' +
          (ecfQR ? '<img class="ecf-qr" src="' + ecfQR + '" alt="QR DGII" />' : '') +
        '</div>'
      : '') +
    '<div class="section"><div class="label">' + txt.comprador + '</div><div class="value">' + comprador +
      // El RNC y el teléfono del cliente, cuando se tienen: hacen falta para que la
      // factura sirva de algo más que de recibo de mano.
      ((venta.compradorDoc || venta.compradorTel)
        ? '<div style="font-size:12px;color:#777;font-weight:400;margin-top:3px">' +
          [venta.compradorDoc ? 'RNC/Céd: ' + venta.compradorDoc : '', venta.compradorTel ? 'Tel: ' + venta.compradorTel : '']
            .filter(Boolean).join(' &middot; ') + '</div>'
        : '') +
      '</div></div>' +
    '<div class="section"><div class="label">' + txt.camada + '</div><div class="value">' + (camada ? camada.nombre : "—") + ' · ' + txt.galpon + ' ' + (camada ? camada.galpon : "—") + '</div></div>' +
    '<div class="section">' +
    '<div class="row"><span class="row-label">' + txt.cantidad + '</span><span class="row-value">' + Number(venta.pollos).toLocaleString() + ' ' + txt.aves + '</span></div>' +
    '<div class="row"><span class="row-label">' + txt.libras + '</span><span class="row-value">' + Number(venta.libras).toLocaleString() + ' lb</span></div>' +
    '<div class="row"><span class="row-label">' + txt.lbAve + '</span><span class="row-value">' + lbAve + ' lb/' + txt.aves + '</span></div>' +
    '<div class="row"><span class="row-label">' + txt.precio + '</span><span class="row-value">' + simbolo + ' ' + venta.precioLb + '/lb</span></div>' +
    '</div>' +
    '<div class="total"><div class="total-label">' + txt.total + '</div><div class="total-value">' + simbolo + ' ' + Number(subtotal).toLocaleString() + '</div></div>' +
    '<div class="btn-row">' +
    '<button class="btn btn-wa" onclick="window.print()" style="width:100%">💾📲 ' + (isEn ? "Save / Share (PDF & WhatsApp)" : "Guardar / Compartir (PDF y WhatsApp)") + '</button>' +
    '</div>' +
    /* Si es un e-CF aceptado, el pie NO puede seguir diciendo "no es un comprobante
       fiscal electrónico": se contradiría con el sello de arriba. */
    '<div class="footer">' +
      (ecf ? '<span style="color:#0f8a3c;font-weight:700">' + txt.ecfPie + ecf.encf + '</span>' : txt.interna) +
      '<br>' + txt.footer + '</div>' +
    '</body></html>';

  var win = window.open("", "_blank");
  if (win && win.document) {
    win.document.write(html);
    win.document.close();
  } else {
    // Popup blocked - open in same tab
    var blob = new Blob([html], { type: "text/html" });
    var url = URL.createObjectURL(blob);
    window.location.href = url;
  }
}

// --- COMPARTIR FACTURA COMO PDF ---
// Genera un PDF real y abre el menú de COMPARTIR del teléfono (ahí aparece WhatsApp con el
// PDF ya adjunto). En PC (sin menú de compartir) descarga el PDF. Si algo falla, cae a la
// factura imprimible de siempre (generarFactura) — nunca deja al usuario sin factura.
function compartirFacturaPDF(venta, camada, session, userSettings, docFiscal) {
  try {
    var subtotal = Number(venta.libras) * Number(venta.precioLb);
    var lbAve = venta.pollos > 0 ? (Number(venta.libras) / Number(venta.pollos)).toFixed(2) : "0";
    var fecha = venta.fecha || new Date().toLocaleDateString("es-DO");
    var numFactura = "F-" + Date.now().toString().slice(-6);
    var em = emisorFactura(session, userSettings);
    var granja = em.nombre;
    var logoGranja = em.logo;
    var comprador = venta.comprador || "Cliente";
    var moneda = (userSettings && userSettings.moneda) ? userSettings.moneda : "DOP";
    var simbolo = monedaSimbolo(moneda);
    var isEn = userSettings && userSettings.idioma === "en";
    var txt = isEn
      ? { titulo: "INVOICE", comprador: "Buyer", camada: "Flock", galpon: "Barn", cantidad: "Number of chickens", libras: "Total pounds", lbAve: "Lb per bird", precio: "Price per pound", total: "TOTAL TO PAY", footer: "Generated by Pollo IA · polloia.net", aves: "birds",
          interna: "Internal document · not an electronic tax receipt", tel: "Phone",
          ecfT1: "ELECTRONIC", ecfT2: "INVOICE", ecfCod: "Security code",
          ecfNota: "Electronic Tax Receipt authorized by the DGII of the Dominican Republic.",
          ecfPie: "Electronic Tax Receipt  -  e-NCF " }
      : { titulo: "FACTURA", comprador: "Comprador", camada: "Camada", galpon: "Galpón", cantidad: "Cantidad de pollos", libras: "Libras totales", lbAve: "Lb por ave", precio: "Precio por libra", total: "TOTAL A PAGAR", footer: "Generado por Pollo IA · polloia.net", aves: "aves",
          interna: "Documento interno · no es un comprobante fiscal electrónico", tel: "Tel",
          ecfT1: "FACTURA", ecfT2: "ELECTRÓNICA", ecfCod: "Código de seguridad",
          ecfNota: "Comprobante Fiscal Electrónico autorizado por la DGII de la República Dominicana.",
          ecfPie: "Comprobante Fiscal Electrónico  ·  e-NCF " };

    var ecf = eCFValido(docFiscal);
    var ecfQR = qrDelECF(ecf);

    var doc = new jsPDF({ unit: "mm", format: "a4" });
    var W = 210, x = 25, x2 = 185, y = 20;

    // Logo (opcional, centrado, conservando proporción)
    if (logoGranja) {
      try {
        var props = doc.getImageProperties(logoGranja);
        var esc = Math.min(72.5 / props.width, 29 / props.height); // logo 45% más grande
        var iw = props.width * esc, ih = props.height * esc;
        var fmt = logoGranja.indexOf("image/png") > -1 ? "PNG" : "JPEG";
        doc.addImage(logoGranja, fmt, (W - iw) / 2, y, iw, ih);
        y += ih + 6;
      } catch (e) {}
    }
    doc.setFont("helvetica", "bold"); doc.setFontSize(20); doc.setTextColor(17, 17, 17);
    doc.text(String(granja), W / 2, y + 6, { align: "center" }); y += 12;
    /* La razón social, solo si no repite el nombre comercial: si la granja se
       llama igual legalmente, imprimirlo dos veces queda tonto. */
    if (em.razonSocial && em.razonSocial.toUpperCase() !== String(granja).toUpperCase()) {
      doc.setFont("helvetica", "bold"); doc.setFontSize(9.5); doc.setTextColor(68);
      doc.text(em.razonSocial, W / 2, y, { align: "center" }); y += 5;
    }
    /* Los datos fiscales del EMISOR, que antes no salían en el PDF. Lo que la
       granja no tenga configurado, simplemente no se imprime. */
    var lineasEm = [];
    var primera = [];
    if (em.rnc) primera.push(em.docEtiqueta + ": " + em.rnc);
    if (em.telefono) primera.push(txt.tel + ": " + em.telefono);
    if (primera.length) lineasEm.push(primera.join("   ·   "));
    if (em.direccion) lineasEm.push(em.direccion);
    if (em.correo) lineasEm.push(em.correo);
    if (lineasEm.length) {
      doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(120);
      lineasEm.forEach(function (linea) {
        doc.text(String(linea), W / 2, y, { align: "center" }); y += 4.2;
      });
      y += 1.5;
    }
    doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(120);
    doc.text("polloia.net", W / 2, y, { align: "center" }); y += 6;
    doc.text(txt.titulo + " #" + numFactura + "  -  " + fecha, W / 2, y, { align: "center" }); y += 6;
    doc.setDrawColor(245, 194, 0); doc.setLineWidth(1.2);
    doc.line(x, y, x2, y); y += 10;

    /* ── SELLO DE FACTURA ELECTRÓNICA ──
       Va arriba del todo, debajo del encabezado, porque es lo primero que mira quien
       recibe el PDF por WhatsApp. Solo sale si la DGII aceptó el comprobante. */
    if (ecf) {
      var lado = 28, sy = y, alto = lado + 8;
      doc.setDrawColor(15, 138, 60); doc.setLineWidth(0.6);
      doc.roundedRect(x, sy, x2 - x, alto, 2.5, 2.5, "S");

      var sx = x + 4, cx = sx + lado / 2;
      doc.setFillColor(15, 138, 60);
      doc.roundedRect(sx, sy + 4, lado, lado, 2.5, 2.5, "F");

      /* El visto bueno se DIBUJA con dos rayas: el carácter ✓ no existe en la
         codificación WinAnsi de las fuentes base del PDF y saldría como un símbolo
         raro justo en el sello que tiene que dar confianza. */
      doc.setDrawColor(255, 255, 255); doc.setLineWidth(1.5); doc.setLineCap("round");
      doc.line(cx - 3.4, sy + 11.2, cx - 1.1, sy + 13.6);
      doc.line(cx - 1.1, sy + 13.6, cx + 3.6, sy + 8.4);
      doc.setLineCap("butt");

      doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold");
      doc.setFontSize(6.2); doc.text(txt.ecfT1, cx, sy + 19, { align: "center" });
      doc.setFontSize(6.2); doc.text(txt.ecfT2, cx, sy + 22, { align: "center" });
      doc.setDrawColor(255, 255, 255); doc.setLineWidth(0.3);
      doc.line(sx + 7, sy + 23.6, sx + lado - 7, sy + 23.6);
      doc.setFontSize(10); doc.text("DGII", cx, sy + 28.4, { align: "center" });

      // El QR oficial, solo si el proveedor ya lo mandó como imagen.
      var hayQR = false;
      if (ecfQR) {
        try { doc.addImage(ecfQR, "PNG", x2 - 28, sy + 5, 24, 24); hayQR = true; } catch (e) {}
      }

      // Al lado: el e-NCF, que es el número que el comprador puede verificar.
      var dx = sx + lado + 6, anchoTxt = (hayQR ? x2 - 32 : x2 - 4) - dx;
      doc.setFont("helvetica", "bold"); doc.setFontSize(6.8); doc.setTextColor(15, 138, 60);
      doc.text("e-NCF", dx, sy + 9);
      doc.setFont("courier", "bold"); doc.setFontSize(13); doc.setTextColor(17);
      doc.text(String(ecf.encf), dx, sy + 16);
      doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(95);
      doc.text(doc.splitTextToSize(txt.ecfNota, anchoTxt), dx, sy + 21.5);
      if (ecf.codigoSeguridad) {
        doc.setFont("courier", "normal"); doc.setFontSize(6.8); doc.setTextColor(140);
        doc.text(txt.ecfCod + ": " + ecf.codigoSeguridad, dx, sy + 30);
      }
      y = sy + alto + 9;
    }

    doc.setFontSize(8); doc.setTextColor(150); doc.text(txt.comprador.toUpperCase(), x, y); y += 5;
    doc.setFontSize(13); doc.setTextColor(17); doc.setFont("helvetica", "bold"); doc.text(String(comprador), x, y); y += 5;
    // RNC y teléfono del cliente debajo del nombre, en chiquito.
    var datosCli = [venta.compradorDoc ? "RNC/Ced: " + venta.compradorDoc : "", venta.compradorTel ? "Tel: " + venta.compradorTel : ""].filter(Boolean).join("   ");
    if (datosCli) { doc.setFontSize(9); doc.setTextColor(120); doc.setFont("helvetica", "normal"); doc.text(datosCli, x, y); y += 5; }
    y += 4;
    doc.setFontSize(8); doc.setTextColor(150); doc.setFont("helvetica", "normal"); doc.text(txt.camada.toUpperCase(), x, y); y += 5;
    doc.setFontSize(12); doc.setTextColor(17); doc.setFont("helvetica", "bold");
    doc.text((camada ? String(camada.nombre) : "-") + "  -  " + txt.galpon + " " + (camada ? String(camada.galpon) : "-"), x, y); y += 10;

    [
      [txt.cantidad, Number(venta.pollos).toLocaleString() + " " + txt.aves],
      [txt.libras, Number(venta.libras).toLocaleString() + " lb"],
      [txt.lbAve, lbAve + " lb/" + txt.aves],
      [txt.precio, simbolo + " " + venta.precioLb + "/lb"],
    ].forEach(function (f) {
      doc.setFont("helvetica", "normal"); doc.setFontSize(11); doc.setTextColor(85);
      doc.text(f[0], x, y);
      doc.setFont("helvetica", "bold"); doc.setTextColor(17);
      doc.text(f[1], x2, y, { align: "right" });
      doc.setDrawColor(230); doc.setLineWidth(0.2);
      doc.line(x, y + 2.5, x2, y + 2.5);
      y += 9;
    });
    y += 4;
    doc.setFillColor(245, 194, 0);
    doc.roundedRect(x, y, x2 - x, 22, 2, 2, "F");
    doc.setFontSize(9); doc.setTextColor(51); doc.setFont("helvetica", "normal");
    doc.text(txt.total, W / 2, y + 7, { align: "center" });
    doc.setFontSize(20); doc.setTextColor(17); doc.setFont("helvetica", "bold");
    doc.text(simbolo + " " + Number(subtotal).toLocaleString(), W / 2, y + 17, { align: "center" });
    y += 32;
    /* Primero QUÉ ES este papel y después quién lo hizo. Mientras no haya un
       e-NCF aceptado por la DGII, esto es una factura interna y lo dice. */
    if (ecf) {
      doc.setFontSize(8); doc.setTextColor(15, 138, 60); doc.setFont("helvetica", "bold");
      doc.text(txt.ecfPie + ecf.encf, W / 2, y, { align: "center" }); y += 4.5;
    } else {
      doc.setFontSize(8); doc.setTextColor(150); doc.setFont("helvetica", "italic");
      doc.text(txt.interna, W / 2, y, { align: "center" }); y += 4.5;
    }
    doc.setFontSize(8); doc.setTextColor(170); doc.setFont("helvetica", "normal");
    doc.text(txt.footer, W / 2, y, { align: "center" });

    var nombreArchivo = txt.titulo + "-" + numFactura + ".pdf";
    var blob = doc.output("blob");
    try {
      var file = new File([blob], nombreArchivo, { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        navigator.share({ files: [file], title: txt.titulo + " " + numFactura, text: txt.titulo + " - " + granja })
          .catch(function () {}); // el usuario canceló el menú → no es error
        return;
      }
    } catch (e) {}
    // PC / navegador sin menú de compartir → descargar el PDF.
    doc.save(nombreArchivo);
  } catch (e) {
    generarFactura(venta, camada, session, userSettings, docFiscal);
  }
}

// --- COMPARTIR CUALQUIER REPORTE COMO PDF ---
// Recibe el HTML de un reporte, lo convierte a un PDF real (multipágina) y abre el menú de
// COMPARTIR del teléfono (WhatsApp con el PDF adjunto). En PC descarga el PDF. Si algo falla,
// cae a la ventana imprimible de siempre — nunca deja al usuario sin su reporte.
function compartirReportePDF(html, nombreBase) {
  var nombreArchivo = String(nombreBase || "Reporte").replace(/[^\w\-]+/g, "-").replace(/-+/g, "-") + ".pdf";
  var abrirVentana = function () {
    var w = window.open("", "_blank");
    if (w) { w.document.write(html); w.document.close(); }
  };
  try {
    // Render invisible del reporte para "fotografiarlo"
    var iframe = document.createElement("iframe");
    iframe.style.cssText = "position:fixed;left:-10000px;top:0;width:780px;height:900px;border:0;";
    document.body.appendChild(iframe);
    var idoc = iframe.contentDocument;
    idoc.open(); idoc.write(html); idoc.close();
    var st = idoc.createElement("style");
    st.textContent = "button,.noprint{display:none!important} body{background:#ffffff!important}";
    idoc.head.appendChild(st);
    var limpiar = function () { try { document.body.removeChild(iframe); } catch (e) {} };
    setTimeout(function () {
      try {
        html2canvas(idoc.body, { scale: 2, backgroundColor: "#ffffff", useCORS: true, logging: false }).then(function (canvas) {
          try {
            var doc = new jsPDF({ unit: "mm", format: "a4" });
            var pw = 210, ph = 297, margen = 8;
            var iw = pw - margen * 2;
            var pagPx = Math.floor((ph - margen * 2) * canvas.width / iw); // alto de una página, en píxeles del canvas
            var y = 0, primera = true;
            while (y < canvas.height) {
              var slice = document.createElement("canvas");
              slice.width = canvas.width;
              slice.height = Math.min(pagPx, canvas.height - y);
              slice.getContext("2d").drawImage(canvas, 0, y, canvas.width, slice.height, 0, 0, canvas.width, slice.height);
              if (!primera) doc.addPage();
              doc.addImage(slice.toDataURL("image/jpeg", 0.92), "JPEG", margen, margen, iw, slice.height * iw / canvas.width);
              primera = false;
              y += pagPx;
            }
            limpiar();
            var blob = doc.output("blob");
            try {
              var file = new File([blob], nombreArchivo, { type: "application/pdf" });
              if (navigator.canShare && navigator.canShare({ files: [file] })) {
                navigator.share({ files: [file], title: nombreArchivo.replace(".pdf", "") }).catch(function () {});
                return;
              }
            } catch (e) {}
            doc.save(nombreArchivo); // PC: descarga directa
          } catch (e) { limpiar(); abrirVentana(); }
        }).catch(function () { limpiar(); abrirVentana(); });
      } catch (e) { limpiar(); abrirVentana(); }
    }, 450); // pequeña espera para que carguen logo/fotos del reporte
  } catch (e) { abrirVentana(); }
}

function getLuzPrograma(dia) {
  if (dia <= 1)  return { horas: 24, oscuridad: 0,  fase: "ARRANQUE",    color: "#FFD700", emoji: "☀️", tip: "Luz continua — máximo consumo de alimento" };
  if (dia <= 7)  return { horas: 23, oscuridad: 1,  faseKey: "iniciador", fase: "INICIADOR",   color: "#FFB300", emoji: "🌟", tip: "23h luz — estimula ingesta y crecimiento inicial" };
  if (dia <= 14) return { horas: 20, oscuridad: 4,  fase: "TRANSICIÓN",  color: "#FF8C00", emoji: "🌤️", tip: "20h luz — permite descanso muscular y huesos" };
  if (dia <= 21) return { horas: 18, oscuridad: 6,  faseKey: "crecimiento", fase: "CRECIMIENTO", color: "#00C896", emoji: "🌿", tip: "18h luz — balance óptimo crecimiento/bienestar" };
  if (dia <= 28) return { horas: 17, oscuridad: 7,  fase: "DESARROLLO",  color: "#00BFFF", emoji: "💪", tip: "17h luz — desarrollo muscular y esquelético" };
  return            { horas: 16, oscuridad: 8,  faseKey: "engorde", fase: "ENGORDE",     color: "#9B59B6", emoji: "🥩", tip: "16h luz — fase final, eficiencia máxima" };
}

function migrarCamadaLegacy(email) {
  // Migra datos antiguos al nuevo sistema multi-camada
  const legacy = DB.getCamadas();
  if (legacy[email] && DB.getCamadasList(email).length === 0) {
    const old = { ...legacy[email], id: "cam_legacy_1", galpon: "Galpón 1", numeroCamada: 1, ventas: legacy[email].ventas || [] };
    DB.saveCamadasList(email, [old]);
    DB.setActivaCamadaId(email, old.id);
    return old;
  }
  return null;
}

function diasDesde(fechaStr) {
  var hoy = new Date();
  var hoyFecha = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
  // Parsear partes explícitamente para evitar bug de timezone con ISO strings
  var partes = String(fechaStr).split("-");
  var inicioFecha = new Date(Number(partes[0]), Number(partes[1]) - 1, Number(partes[2]));
  return Math.max(0, Math.round((hoyFecha - inicioFecha) / 86400000));
}
function fmtNum(n, dec = 0) {
  // LOCALE_NUM lo pone el país fiscal del productor. Antes estaba clavado en
  // "es-DO", así que a un colombiano los separadores le salían al revés.
  return Number(n || 0).toLocaleString(LOCALE_NUM, { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

/* =====================================================================
   TOKEN SIEMPRE FRESCO
   El access_token de Supabase dura una hora. Aquí se renueva solo, usando el
   refresh_token que se guarda al entrar. Si dos cosas piden token a la vez,
   solo se hace UNA petición de renovación (la promesa se comparte).
   ===================================================================== */
let _renovando = null;

function tokenVencido(t) {
  try {
    /* El payload viene en base64url: "-" y "_" donde base64 pone "+" y "/", y sin
       relleno. atob() los rechaza con error, así que hay que traducirlo antes; si no,
       un token recién hecho podría darse por vencido. */
    let b = String(t).split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    while (b.length % 4) b += "=";
    const carga = JSON.parse(atob(b));
    // Un minuto de margen: más vale renovar de más que fallar a mitad de una venta.
    return !carga.exp || (carga.exp * 1000) < Date.now() + 60000;
  } catch (e) { return true; }
}

async function tokenFresco() {
  const s = DB.getSession();
  if (!s) return null;
  if (s.token && !tokenVencido(s.token)) return s.token;
  if (!s.refreshToken) return null;      // sesión vieja, de antes de guardar el refresh
  if (!_renovando) {
    _renovando = fetch(SUPABASE_URL + "/auth/v1/token?grant_type=refresh_token", {
      method: "POST", headers: SB.headers,
      body: JSON.stringify({ refresh_token: s.refreshToken }),
    })
      .then(r => r.json())
      .then(d => {
        if (!d || !d.access_token) return null;
        const actual = DB.getSession() || {};
        DB.saveSession(Object.assign({}, actual, {
          token: d.access_token,
          refreshToken: d.refresh_token || actual.refreshToken,
        }));
        return d.access_token;
      })
      .catch(() => null)
      .finally(() => { _renovando = null; });
  }
  return _renovando;
}

/* Vuelve a autenticar SIN cerrar la sesión ni perder la pantalla. Hace falta para las
   cuentas que entraron antes de que la app guardara el refresh_token: su token caducó y
   no hay con qué renovarlo. Pedir la contraseña una vez es mucho menos brusco que mandar
   al granjero al login con media factura escrita. */
async function reautenticar(clave) {
  const s = DB.getSession();
  if (!s || !s.email) throw new Error(tr("No hay sesión abierta."));
  if (s.rol === "empleado") throw new Error(tr("La lista de clientes la maneja el dueño de la granja."));
  const d = await SB.signIn(s.email, clave);
  if (!d || !d.access_token) {
    const codigo = String((d && (d.error_code || d.error)) || "") + " " +
                   String((d && (d.msg || d.error_description || d.message)) || "");
    throw new Error(/invalid|credential/i.test(codigo)
      ? tr("Contraseña incorrecta.")
      : tr("No pudimos confirmar tu contraseña ahora mismo. Revisa tu internet."));
  }
  DB.saveSession(Object.assign({}, DB.getSession() || s, {
    token: d.access_token,
    refreshToken: d.refresh_token || "",
  }));
  return d.access_token;
}

/* Única puerta de la app hacia los endpoints de facturación. Siempre manda el
   token en la cabecera; nunca manda el correo, porque el servidor lo saca del
   token (si no, cualquiera pediría los datos de otra granja). */
/* La credencial del EMPLEADO para la libreta de clientes.

   El empleado no tiene cuenta en Supabase —entra con el código de 6 letras de la
   granja—, así que no hay token. El código se busca en tres sitios, de más fiable
   a menos, para que esto funcione también con las sesiones ya abiertas y nadie
   tenga que volver a entrar:
     1. La sesión, si se guardó ahí (las nuevas sí).
     2. La libreta local de usuarios del teléfono.
     3. El propio correo interno, que se fabricó como CODIGO_nombre@empleado.polloia
        al entrar: el código es literalmente lo que va antes del guion bajo. */
function credencialEmpleado() {
  const s = DB.getSession();
  if (!s || s.rol !== "empleado" || !s.email) return null;
  let code = s.empCode;
  if (!code) { try { const u = (DB.getUsers() || {})[s.email]; code = u && u.empCode; } catch (e) {} }
  if (!code) { const m = /^([A-Za-z0-9]{4,12})_/.exec(s.email); if (m) code = m[1]; }
  return code ? { email: s.email, code: String(code).toUpperCase() } : null;
}

async function apiFiscal(ruta, opciones) {
  const o = opciones || {};
  const t = await tokenFresco();
  /* Sin token puede ser un empleado. Para la LIBRETA DE CLIENTES su código vale
     —el servidor lo comprueba contra la base antes de dejarle escribir nada—, y
     con eso deja de perderse toda ficha que escriba un empleado. Lo demás
     (configuración fiscal, emitir comprobantes) sigue siendo cosa del dueño. */
  const cred = (!t && ruta === "clientes") ? credencialEmpleado() : null;
  if (!t && !cred) {
    const s = DB.getSession();
    const esEmpleado = !!(s && s.rol === "empleado");
    const e = new Error(esEmpleado
      ? tr("Esto lo maneja el dueño de la granja. Escribe el nombre y queda en la factura igual.")
      : tr("Vuelve a entrar para usar la facturación."));
    e.sesion = !esEmpleado;
    throw e;
  }
  const cabeceras = { "Content-Type": "application/json" };
  if (t) cabeceras.Authorization = "Bearer " + t;
  else { cabeceras["X-Emp-Email"] = cred.email; cabeceras["X-Emp-Code"] = cred.code; }
  const r = await fetch("/api/" + ruta, {
    method: o.method || "GET",
    headers: cabeceras,
    body: o.body ? JSON.stringify(o.body) : undefined,
  });
  let d = null;
  try { d = await r.json(); } catch (e) {}
  if (!r.ok) {
    const e = new Error((d && d.error) || tr("No se pudo completar la operación."));
    e.estado = r.status; e.sesion = r.status === 401;
    throw e;
  }
  return d;
}

// --- NOTIFICACIONES PUSH ---
// Llave pública VAPID (la privada vive solo en Vercel como variable de entorno)
const VAPID_PUBLIC_KEY = "BJ_xnnbSO3lT-cQOOY79g05KsZyK1qHxZLkOmIISRSAMGRACBmdfuNVIRdkLwjDlb6ZZfnmCSu5RpgvIADc66RQ";
const PUSH_PREFS_DEFAULT = { vacunas: true, pesaje: true, mortalidad: true, pesoBajo: true, registro: true };
const PUSH_PREF_LABELS = [
  { key: "vacunas",    emoji: "💉", label: "Vacunas del calendario",         desc: "Aviso el día que toca vacunar" },
  { key: "pesaje",     emoji: "⚖️", label: "Pesaje semanal",                 desc: "Cada 7 días de la camada" },
  { key: "mortalidad", emoji: "⚠️", label: "Mortalidad anormal",             desc: "Si ayer hubo más bajas de lo normal" },
  { key: "pesoBajo",   emoji: "📉", label: "Peso bajo el estándar",          desc: "Si el pesaje va >10% bajo la curva" },
  { key: "registro",   emoji: "📝", label: "Recordatorio de registro diario", desc: "7:00 PM si no has anotado nada hoy" },
];
function urlBase64ToUint8Array(base64String) {
  var padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  var base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  var raw = window.atob(base64);
  var arr = new Uint8Array(raw.length);
  for (var i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

// --- UI Components ---
function Badge({ color, children, small }) {
  return <span style={{ background: color + "22", color, border: `1px solid ${color}44`, borderRadius: 4, padding: small ? "1px 6px" : "2px 8px", fontSize: small ? 10 : 11, fontFamily: "'Space Mono', monospace", fontWeight: 700, letterSpacing: 1, whiteSpace: "nowrap" }}>{children}</span>;
}
/* Pide la contraseña una sola vez y arregla la sesión por dentro: no cierra sesión, no
   recarga la página y no se pierde lo que el granjero ya escribió en la factura. */
function ModalClave({ C, correo, onListo, onCerrar }) {
  const [clave, setClave] = useState("");
  const [error, setError] = useState("");
  const [yendo, setYendo] = useState(false);
  async function confirmar() {
    if (!clave || yendo) return;
    setYendo(true); setError("");
    try { await reautenticar(clave); onListo(); }
    catch (e) { setError(e.message || ""); setYendo(false); }
  }
  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 68,
      background: "#000c", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={function () { if (!yendo) onCerrar(); }}>
      <div onClick={function (e) { e.stopPropagation(); }}
        style={{ width: "100%", maxWidth: 400, background: C.surface, borderRadius: 16,
          padding: "22px 20px", border: "1.5px solid " + C.accent + "88" }}>
        <div style={{ fontSize: 19, fontWeight: 900, color: C.accent, fontFamily: "'Syne', sans-serif", marginBottom: 9 }}>
          {tr("Confirma tu contraseña")}
        </div>
        <div style={{ fontSize: 14.5, color: C.textMuted, lineHeight: 1.55, marginBottom: 14 }}>
          {tr("Es una sola vez. Tu sesión es vieja y hay que renovarla para poder guardar clientes y facturas. No pierdes nada: ni tus camadas, ni lo que ya escribiste aquí.")}
        </div>
        <div style={{ fontSize: 13, color: C.textFaint, fontFamily: "'Space Mono', monospace",
          marginBottom: 8, overflowWrap: "break-word" }}>{correo}</div>
        <input type="password" value={clave} autoFocus autoComplete="current-password"
          onChange={function (e) { setClave(e.target.value); }}
          onKeyDown={function (e) { if (e.key === "Enter") confirmar(); }}
          placeholder={tr("Tu contraseña")}
          style={{ width: "100%", boxSizing: "border-box", background: C.card,
            border: "1.5px solid " + C.border, borderRadius: 10, color: C.text,
            padding: "14px 13px", fontSize: 17, outline: "none", fontFamily: "'Space Mono', monospace" }} />
        {error && <div style={{ fontSize: 13.5, color: C.danger, marginTop: 10, lineHeight: 1.45 }}>{error}</div>}
        <div style={{ display: "flex", gap: 10, marginTop: 15 }}>
          <button onClick={function () { if (!yendo) onCerrar(); }}
            style={{ flex: 1, padding: "13px", borderRadius: 11, border: "1px solid " + C.border,
              cursor: "pointer", background: C.card, color: C.text, fontSize: 15, fontWeight: 700,
              fontFamily: "'Syne', sans-serif" }}>{tr("Ahora no")}</button>
          <button onClick={confirmar} disabled={yendo || !clave}
            style={{ flex: 2, padding: "13px", borderRadius: 11, border: "none",
              cursor: (yendo || !clave) ? "default" : "pointer", background: C.accent, color: "#1a0a00",
              fontSize: 15, fontWeight: 900, fontFamily: "'Syne', sans-serif", opacity: (yendo || !clave) ? 0.5 : 1 }}>
            {yendo ? tr("Confirmando…") : tr("Confirmar")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* =====================================================================
   MI GRANJA Y FACTURACIÓN
   Todo lo que identifica a la granja y sale impreso en sus facturas, en un
   solo sitio y en tres grupos: quién eres, tus datos fiscales, y el
   interruptor de la facturación electrónica.

   DÓNDE VIVE CADA COSA, Y POR QUÉ:

   · Nombre, logo, dirección, teléfono, correo, país y moneda → en los ajustes
     de la granja (user_settings). Se guardan al instante y funcionan sin
     internet, porque son los que el granjero cambia a cada rato.

   · RNC, razón social y régimen de ITBIS → en el SERVIDOR (fiscal_config, con
     RLS encendido). No pasan por la llave anónima del navegador. Aquí solo se
     guarda una copia local para poder imprimirlos en la factura sin señal.

   · Credenciales del proveedor → NUNCA aquí. Las instala el servidor. Esta
     pantalla no tiene ni una casilla donde escribir una clave de API, y el
     backend rechaza la petición si le llega una.

   Solo el dueño puede cambiar algo. El empleado lo ve y no lo toca: la
   facturación compromete fiscalmente a la granja, no a quien la teclea.
   ===================================================================== */
const ITBIS_REGIMENES = [
  { v: "exento", etiqueta: "Exento / no aplica", pista: "El pollo fresco está exento en R.D." },
  { v: "tasa",   etiqueta: "Cobro ITBIS",        pista: "Escribe la tasa que te toca" },
];

/* La tarjeta de cada grupo. Vive FUERA del componente a propósito: si se
   definiera dentro, React la recrearía en cada dibujado y los campos de texto
   perderían el foco a cada letra que se escribe. */
function GrupoFis({ C, n, color, titulo, pista, children }) {
  return (
    <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 16,
      padding: "16px 15px", marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 4 }}>
        <div style={{ width: 25, height: 25, borderRadius: 8, flexShrink: 0, background: color + "22",
          border: "1.5px solid " + color + "66", color: color, fontSize: 13, fontWeight: 900,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontFamily: "'Space Mono', monospace" }}>{n}</div>
        <div style={{ fontSize: 16.5, fontWeight: 900, color: color,
          fontFamily: "'Syne', sans-serif", lineHeight: 1.2 }}>{titulo}</div>
      </div>
      <div style={{ fontSize: 12.5, color: C.textFaint, lineHeight: 1.5, marginBottom: 14,
        marginLeft: 34 }}>{pista}</div>
      {children}
    </div>
  );
}

function MiGranjaPage({ C, session, userSettings, saveSettings, onClose }) {
  const esDueno = !session || session.rol === "propietario";
  const [cargando, setCargando] = useState(true);
  const [srv, setSrv] = useState(null);          // lo que contestó el servidor
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState("");
  const [pedirClave, setPedirClave] = useState(false);
  const [trasClave, setTrasClave] = useState(null);   // qué se estaba haciendo
  const [confirmar, setConfirmar] = useState("");     // "activar" | "desactivar"
  // Los datos fiscales se editan en un formulario propio: tocan el servidor, así
  // que no se mandan con cada letra que se escribe.
  const [fis, setFis] = useState(function () {
    const local = DB.getFiscal(session.ownerEmail) || {};
    return {
      rnc: local.rnc || "", razonSocial: local.razonSocial || "",
      itbisRegimen: local.itbisRegimen || "exento",
      itbisTasa: local.itbisTasa != null ? String(local.itbisTasa) : "",
      topeMensual: local.topeMensual != null ? String(local.topeMensual) : "",
    };
  });
  const [sucio, setSucio] = useState(false);     // hay cambios sin guardar

  const pais = paisFiscal(userSettings.paisFiscal);
  const paisDO = !!pais && pais.fiscal === "si";

  /* Trae la configuración del servidor. Si la sesión está vieja, se ofrece el
     cuadro de la contraseña en vez de dejar la pantalla muerta. */
  function cargar(callback) {
    return apiFiscal("fiscal-config")
      .then(function (d) {
        setSrv(d); setError("");
        const c = d.config || {};
        setFis({
          rnc: c.rnc || "", razonSocial: c.razonSocial || "",
          itbisRegimen: c.itbisRegimen || "exento",
          itbisTasa: c.itbisTasa != null ? String(c.itbisTasa) : "",
          topeMensual: c.topeMensual != null ? String(c.topeMensual) : "",
        });
        setSucio(false);
        // Copia local, para imprimir el RNC en la factura aunque no haya señal.
        DB.saveFiscal(session.ownerEmail, {
          rnc: c.rnc || "", docTipo: c.docTipo || "", razonSocial: c.razonSocial || "",
          itbisRegimen: c.itbisRegimen || "exento", itbisTasa: c.itbisTasa,
          activo: !!c.activo, estado: c.estado || "desactivado",
        });
        if (callback) callback(d);
      })
      .catch(function (e) {
        setError(e.message || "");
        if (e.sesion && esDueno) { setTrasClave("cargar"); setPedirClave(true); }
      })
      .then(function () { setCargando(false); });
  }
  useEffect(function () { cargar(); }, [session.ownerEmail]);

  /* Guardar los datos fiscales. Manda TAMBIÉN el país, el nombre comercial y la
     dirección que están en los ajustes, para que el servidor tenga su propia
     copia completa del emisor y pueda emitir sin preguntarle nada al teléfono. */
  function guardarFiscal() {
    if (guardando) return Promise.resolve();
    if (!userSettings.paisFiscal) {
      setError(tr("Primero escoge el país de tu granja, aquí arriba."));
      return Promise.resolve();
    }
    if (fis.itbisRegimen === "tasa" && !(Number(fis.itbisTasa) > 0)) {
      setError(tr("Escribe la tasa de ITBIS que te toca (por ejemplo 18)."));
      return Promise.resolve();
    }
    setGuardando(true); setError(""); setAviso("");
    return apiFiscal("fiscal-config", { method: "PUT", body: {
      pais: userSettings.paisFiscal,
      rnc: fis.rnc,
      razonSocial: fis.razonSocial,
      nombreComercial: userSettings.nombreGranja || "",
      direccionFiscal: userSettings.direccionGranja || "",
      telefono: userSettings.telefonoGranja || "",
      correo: userSettings.correoGranja || "",
      itbisRegimen: fis.itbisRegimen,
      itbisTasa: fis.itbisRegimen === "tasa" ? Number(fis.itbisTasa) : null,
      topeMensual: fis.topeMensual === "" ? null : Number(fis.topeMensual),
    } })
      .then(function () { return cargar(); })
      .then(function () {
        /* Copia de lo imprimible en los ajustes de la granja, para que el
           teléfono del empleado también pueda poner el RNC en la factura. */
        saveSettings(Object.assign({}, userSettings, {
          rncGranja: String(fis.rnc || "").replace(/[^0-9]/g, ""),
          razonSocialGranja: String(fis.razonSocial || "").trim(),
        }));
        setAviso(tr("Datos fiscales guardados."));
      })
      .catch(function (e) {
        setError(e.message || "");
        if (e.sesion && esDueno) { setTrasClave("guardar"); setPedirClave(true); }
      })
      .then(function () { setGuardando(false); });
  }

  /* El interruptor y la conexión. Todo pasa por el servidor, que es el que
     decide: aquí no se "pone" un estado, se pide una acción y se vuelve a leer. */
  function accion(cual, proveedorElegido) {
    if (guardando) return;
    setGuardando(true); setError(""); setAviso("");
    apiFiscal("fiscal-config", { method: "POST", body: { accion: cual, proveedor: proveedorElegido } })
      .then(function (d) {
        setSrv(function (p) { return Object.assign({}, p, d); });
        setAviso(d.aviso || "");
        return cargar();
      })
      .catch(function (e) {
        setError(e.message || "");
        // Se guarda también CUÁL proveedor, para retomar lo mismo que él tocó.
        if (e.sesion && esDueno) { setTrasClave({ cual: cual, proveedor: proveedorElegido }); setPedirClave(true); }
        cargar();
      })
      .then(function () { setGuardando(false); setConfirmar(""); });
  }

  // ── Piezas visuales, para que los tres grupos se vean igual ───────────────
  const etiqueta = { fontSize: 12.5, color: C.textMuted, fontFamily: "'Space Mono', monospace",
    letterSpacing: 0.5, marginBottom: 6 };
  const campoS = { width: "100%", boxSizing: "border-box", background: C.card,
    border: "1.5px solid " + C.border, borderRadius: 10, color: C.text,
    padding: "12px 13px", fontSize: 15.5, fontFamily: "'Syne', sans-serif", outline: "none" };
  const selS = Object.assign({}, campoS, { appearance: "none", cursor: "pointer",
    border: "2px solid " + C.accent + "55", paddingRight: 34 });

  /* Rejilla que se parte sola: dos columnas en tableta y PC, una en el teléfono.
     Sin medir la ventana ni escuchar eventos. */
  const rejilla = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 12 };

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 52,
      background: C.bg, overflowY: "auto" }}>
      <div style={{ width: "100%", maxWidth: 560, margin: "0 auto", minHeight: "100vh",
        padding: "18px 14px 60px", boxSizing: "border-box" }}>

        {/* Cabecera */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 20, fontWeight: 900, color: C.accent, fontFamily: "'Syne', sans-serif" }}>
              🏠 {tr("Mi Granja y Facturación")}
            </div>
            <div style={{ fontSize: 13, color: C.textMuted, marginTop: 3, lineHeight: 1.45 }}>
              {tr("Lo que sale impreso en tus facturas")}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "1px solid " + C.border,
            borderRadius: 9, color: C.textMuted, cursor: "pointer", padding: "7px 13px", fontSize: 15,
            flexShrink: 0, fontFamily: "'Syne', sans-serif" }}>✕</button>
        </div>
        <div style={{ height: 1, background: C.border, margin: "14px 0 16px" }} />

        {!esDueno && (
          <div style={{ background: C.warn + "18", border: "1px solid " + C.warn + "55", borderRadius: 12,
            padding: "11px 13px", fontSize: 13, color: C.warn, lineHeight: 1.5, marginBottom: 14 }}>
            {tr("Esto lo maneja el dueño de la granja. Lo puedes ver, pero no cambiarlo.")}
          </div>
        )}

        {/* ── GRUPO 1 · IDENTIDAD ─────────────────────────────────────────── */}
        <GrupoFis C={C} n="1" color="#6AA6F5" titulo={tr("Identidad de mi granja")}
          pista={tr("El nombre y el logo que el comprador ve arriba en la factura.")}>

          <div style={etiqueta}>{tr("LOGO DE LA GRANJA")}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            {userSettings.logoGranja
              ? <img src={userSettings.logoGranja} alt={tr("logo")}
                  style={{ height: 54, width: 54, objectFit: "contain", borderRadius: 10,
                    background: "#fff", border: "1px solid " + C.border, flexShrink: 0 }} />
              : <div style={{ height: 54, width: 54, borderRadius: 10, flexShrink: 0, background: C.card,
                  border: "1.5px dashed " + C.border, display: "flex", alignItems: "center",
                  justifyContent: "center", fontSize: 22, color: C.textFaint }}>🏠</div>}
            <label style={{ flex: 1, background: C.card, border: "1.5px dashed " + C.border,
              borderRadius: 10, padding: "12px 10px", textAlign: "center", color: C.textMuted,
              fontSize: 13.5, cursor: esDueno ? "pointer" : "default", opacity: esDueno ? 1 : 0.5 }}>
              {userSettings.logoGranja ? tr("✏️ Cambiar logo") : tr("📷 Subir logo")}
              <input type="file" accept="image/*" disabled={!esDueno} style={{ display: "none" }}
                onChange={function (e) {
                  const file = e.target.files[0];
                  if (!file) return;
                  // Se achica solo: así vale cualquier foto del teléfono.
                  comprimirImagen(file, 512, 0.85).then(function (dataUrl) {
                    saveSettings(Object.assign({}, userSettings, { logoGranja: dataUrl }));
                  }).catch(function () {});
                }} />
            </label>
            {userSettings.logoGranja && esDueno && (
              <button onClick={function () { saveSettings(Object.assign({}, userSettings, { logoGranja: null })); }}
                style={{ background: "none", border: "1px solid " + C.border, borderRadius: 9,
                  color: C.danger, cursor: "pointer", padding: "9px 12px", fontSize: 13,
                  flexShrink: 0, fontFamily: "'Syne', sans-serif" }}>{tr("Quitar")}</button>
            )}
          </div>

          <div style={etiqueta}>{tr("NOMBRE COMERCIAL")}</div>
          <input value={userSettings.nombreGranja || ""} disabled={!esDueno}
            onChange={function (e) { saveSettings(Object.assign({}, userSettings, { nombreGranja: e.target.value })); }}
            placeholder={tr("Granja Los Matos")} style={campoS} />
          <div style={{ fontSize: 11.5, color: C.textFaint, margin: "6px 0 14px", lineHeight: 1.45 }}>
            {tr("Sale en grande arriba en la factura. Debajo va polloia.net, que es quien la genera.")}
          </div>

          <div style={rejilla}>
            <div>
              <div style={etiqueta}>{tr("TELÉFONO")}</div>
              <input value={userSettings.telefonoGranja || ""} disabled={!esDueno} inputMode="tel"
                onChange={function (e) { saveSettings(Object.assign({}, userSettings, { telefonoGranja: e.target.value })); }}
                placeholder="809-555-1234" style={campoS} />
            </div>
            <div>
              <div style={etiqueta}>{tr("CORREO")}</div>
              <input value={userSettings.correoGranja || ""} disabled={!esDueno} inputMode="email"
                onChange={function (e) { saveSettings(Object.assign({}, userSettings, { correoGranja: e.target.value })); }}
                placeholder="granja@correo.com" style={campoS} />
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <div style={etiqueta}>{tr("DIRECCIÓN DE LA GRANJA")}</div>
            <input value={userSettings.direccionGranja || ""} disabled={!esDueno}
              onChange={function (e) { saveSettings(Object.assign({}, userSettings, { direccionGranja: e.target.value })); }}
              placeholder={tr("Calle, sector, provincia")} style={campoS} />
            <div style={{ fontSize: 11.5, color: C.textFaint, marginTop: 6, lineHeight: 1.45 }}>
              {tr("Es también la dirección fiscal que la DGII pide del emisor.")}
            </div>
          </div>

          <div style={{ fontSize: 12, color: C.green, marginTop: 13 }}>
            {esDueno ? tr("✓ Esto se guarda solo, al momento de escribirlo.") : ""}
          </div>
        </GrupoFis>

        {/* ── GRUPO 2 · DATOS FISCALES Y MONEDA ───────────────────────────── */}
        <GrupoFis C={C} n="2" color="#4ADE80" titulo={tr("Datos fiscales y moneda")}
          pista={tr("Tu RNC y tu nombre legal. El país decide la ley que aplica; la moneda, en qué se escriben los importes. Son dos cosas distintas.")}>

          <div style={rejilla}>
            <div>
              <div style={etiqueta}>🌎 {tr("PAÍS DE OPERACIÓN FISCAL")}</div>
              <div style={{ position: "relative" }}>
                <select value={userSettings.paisFiscal || ""} disabled={!esDueno} style={selS}
                  onChange={function (e) {
                    const iso = e.target.value;
                    const p = paisFiscal(iso);
                    /* Al escoger país se SUGIERE su moneda, pero solo si él no había
                       elegido una a mano. Nunca se le cambia por detrás, y los importes
                       que ya están guardados no se reconvierten jamás. */
                    const s2 = Object.assign({}, userSettings, { paisFiscal: iso });
                    if (p && !userSettings.monedaElegida) s2.moneda = p.moneda;
                    saveSettings(s2);
                  }}>
                  <option value="">{tr("Sin especificar")}</option>
                  {GRUPOS_PAIS.map(function (g) {
                    return (
                      <optgroup key={g} label={tr(g)}>
                        {PAISES_FISCALES.filter(function (p) { return p.grupo === g; }).map(function (p) {
                          return <option key={p.iso} value={p.iso}>{p.bandera + " " + tr(p.nombre)}</option>;
                        })}
                      </optgroup>
                    );
                  })}
                </select>
                <span style={{ position: "absolute", right: 13, top: "50%", transform: "translateY(-50%)",
                  color: C.accent, pointerEvents: "none", fontSize: 13 }}>▼</span>
              </div>
            </div>
            <div>
              <div style={etiqueta}>💵 {tr("MONEDA PARA FACTURAR")}</div>
              <div style={{ position: "relative" }}>
                <select value={userSettings.moneda || "DOP"} disabled={!esDueno} style={selS}
                  onChange={function (e) { saveSettings(Object.assign({}, userSettings, { moneda: e.target.value, monedaElegida: true })); }}>
                  {MONEDAS_FACTURA.map(function (grupo) {
                    return (
                      <optgroup key={grupo.grupo} label={tr(grupo.grupo)}>
                        {grupo.items.map(function (m) {
                          return <option key={grupo.grupo + m[0]} value={m[0]}>{tr(m[1])}</option>;
                        })}
                      </optgroup>
                    );
                  })}
                </select>
                <span style={{ position: "absolute", right: 13, top: "50%", transform: "translateY(-50%)",
                  color: C.accent, pointerEvents: "none", fontSize: 13 }}>▼</span>
              </div>
            </div>
          </div>
          <div style={{ fontSize: 11.5, color: C.textFaint, margin: "7px 0 15px", lineHeight: 1.45 }}>
            {tr("Cambiar la moneda no reconvierte lo que ya tienes guardado: cada factura se queda en la moneda con la que se hizo.")}
          </div>

          <div style={etiqueta}>{tr("RAZÓN SOCIAL (NOMBRE LEGAL)")}</div>
          <input value={fis.razonSocial} disabled={!esDueno}
            onChange={function (e) { setFis(Object.assign({}, fis, { razonSocial: e.target.value })); setSucio(true); }}
            placeholder={tr("Granja Los Matos SRL")} style={campoS} />
          <div style={{ fontSize: 11.5, color: C.textFaint, margin: "6px 0 14px", lineHeight: 1.45 }}>
            {tr("El nombre con el que estás registrado. Puede ser distinto del comercial.")}
          </div>

          <div style={etiqueta}>{tr("RNC O CÉDULA DEL PROPIETARIO O EMPRESA")}</div>
          <input value={fis.rnc} disabled={!esDueno} inputMode="numeric"
            onChange={function (e) { setFis(Object.assign({}, fis, { rnc: e.target.value })); setSucio(true); }}
            placeholder="101023457" style={Object.assign({}, campoS, { fontFamily: "'Space Mono', monospace" })} />
          <div style={{ fontSize: 11.5, color: C.textFaint, marginTop: 6, lineHeight: 1.45 }}>
            {tr("El RNC tiene 9 dígitos y la cédula 11. Este es TU número, el del que vende — no el del comprador, que se pide aparte en cada venta.")}
          </div>
          {(function () {
            const n = String(fis.rnc || "").replace(/\D/g, "");
            if (!n) return null;
            const ok = n.length === 9 || n.length === 11;
            return (
              <div style={{ fontSize: 12, marginTop: 7, color: ok ? C.green : C.warn }}>
                {ok ? (n.length === 9 ? tr("✓ Parece un RNC") : tr("✓ Parece una cédula"))
                    : tr("⚠ Lleva 9 dígitos (RNC) u 11 (cédula). Tienes ") + n.length + "."}
              </div>
            );
          })()}

          {/* ITBIS. Nunca una tasa automática: el pollo fresco está exento y
              suponer un 18% para todos le falsearía la factura al productor. */}
          <div style={Object.assign({}, etiqueta, { marginTop: 16 })}>{tr("ITBIS EN TUS VENTAS")}</div>
          <div style={{ display: "flex", gap: 8 }}>
            {ITBIS_REGIMENES.map(function (r) {
              const on = fis.itbisRegimen === r.v;
              return (
                <button key={r.v} disabled={!esDueno}
                  onClick={function () { setFis(Object.assign({}, fis, { itbisRegimen: r.v })); setSucio(true); }}
                  style={{ flex: 1, padding: "11px 8px", borderRadius: 11, textAlign: "left",
                    cursor: esDueno ? "pointer" : "default", background: on ? C.green + "22" : "transparent",
                    border: "2px solid " + (on ? C.green : C.border), color: on ? C.green : C.textMuted,
                    fontFamily: "'Syne', sans-serif" }}>
                  <div style={{ fontSize: 13.5, fontWeight: 800 }}>{tr(r.etiqueta)}</div>
                  <div style={{ fontSize: 11, opacity: 0.8, marginTop: 2, lineHeight: 1.35 }}>{tr(r.pista)}</div>
                </button>
              );
            })}
          </div>
          {fis.itbisRegimen === "tasa" && (
            <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 9 }}>
              <input value={fis.itbisTasa} disabled={!esDueno} inputMode="decimal"
                onChange={function (e) { setFis(Object.assign({}, fis, { itbisTasa: e.target.value })); setSucio(true); }}
                placeholder="18" style={Object.assign({}, campoS, { width: 100, flex: "none",
                  fontFamily: "'Space Mono', monospace", textAlign: "center" })} />
              <span style={{ fontSize: 15, color: C.textMuted }}>%</span>
            </div>
          )}
          <div style={{ fontSize: 11.5, color: C.textFaint, marginTop: 8, lineHeight: 1.5 }}>
            {tr("Esto no es asesoría fiscal. Lo que escojas aquí es lo que se imprime; confírmalo con tu contador.")}
          </div>

          {esDueno && (
            <button onClick={guardarFiscal} disabled={guardando}
              style={{ width: "100%", marginTop: 15, padding: "14px", borderRadius: 12, border: "none",
                cursor: guardando ? "default" : "pointer", background: sucio ? C.green : C.card,
                color: sucio ? "#06230f" : C.textMuted, fontSize: 15.5, fontWeight: 900,
                fontFamily: "'Syne', sans-serif", opacity: guardando ? 0.6 : 1 }}>
              {guardando ? tr("Guardando…")
                : (!sucio && (fis.rnc || fis.razonSocial)) ? tr("✓ Datos fiscales guardados")
                : tr("Guardar datos fiscales")}
            </button>
          )}
          <div style={{ fontSize: 11.5, color: C.textFaint, marginTop: 9, lineHeight: 1.5 }}>
            {tr("Tu RNC y tu razón social se guardan en el servidor, no en el teléfono, y solo tu granja los ve.")}
          </div>
        </GrupoFis>

        {/* ── GRUPO 3 · FACTURACIÓN ELECTRÓNICA ───────────────────────────── */}
        <GrupoFis C={C} n="3" color="#C084FC"
          titulo={paisDO ? tr("Facturación electrónica — DGII") : tr("Facturación electrónica")}
          pista={tr("Opcional. Tu facturación interna funciona igual sin esto.")}>

          {/* País sin integración: se dice la verdad y no se promete fecha. */}
          {!paisDO ? (
            <div style={{ background: C.card, border: "1px solid " + C.border, borderRadius: 12,
              padding: "14px 13px" }}>
              <div style={{ fontSize: 14.5, fontWeight: 800, color: C.accent, marginBottom: 6 }}>
                {pais ? "🕓 " + tr("Facturación electrónica próximamente") : "🌎 " + tr("Escoge tu país arriba")}
              </div>
              <div style={{ fontSize: 13, color: C.textMuted, lineHeight: 1.55 }}>
                {pais
                  ? tr("Hoy solo está lista la de República Dominicana (DGII)") +
                    (pais.autoridad ? ". " + tr("La de tu país sería con ") + pais.autoridad + "." : ".")
                  : tr("La facturación electrónica depende del país, así que primero hay que saber cuál es.")}
              </div>
              <div style={{ fontSize: 12.5, color: C.green, marginTop: 10, lineHeight: 1.5 }}>
                {tr("Mientras tanto sigues facturando con Pollo IA, con tu nombre, tu logo y tu RNC.")}
              </div>
            </div>
          ) : cargando ? (
            <div style={{ fontSize: 13.5, color: C.textMuted, padding: "6px 0" }}>{tr("Cargando…")}</div>
          ) : (function () {
            const cfg = (srv && srv.config) || { activo: false, estado: "desactivado" };
            const faltan = (srv && srv.requisitos) || [];
            const COLOR_ESTADO = {
              desactivado: C.textMuted, config_pendiente: C.accent,
              pendiente_autorizacion: "#C084FC", conectado: C.green, suspendido: C.danger,
            };
            const TEXTO_ESTADO = {
              desactivado: tr("Desactivada"),
              config_pendiente: tr("Pendiente de configuración"),
              pendiente_autorizacion: tr("Pendiente de autorización"),
              conectado: tr("Lista para emitir"),
              suspendido: tr("Suspendida o con error"),
            };
            const col = COLOR_ESTADO[cfg.estado] || C.textMuted;
            return (
              <>
                {/* Estado, bien visible: el productor tiene que saber en qué punto va. */}
                <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 13 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 5, background: col, flexShrink: 0 }} />
                  <span style={{ fontSize: 14.5, fontWeight: 800, color: col, fontFamily: "'Syne', sans-serif" }}>
                    {TEXTO_ESTADO[cfg.estado] || cfg.estado}
                  </span>
                </div>

                {/* El interruptor */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, background: C.card,
                  border: "1.5px solid " + (cfg.activo ? C.green + "66" : C.border), borderRadius: 12,
                  padding: "13px 13px" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14.5, fontWeight: 800, color: C.text }}>
                      {tr("Activar facturación electrónica")}
                    </div>
                    <div style={{ fontSize: 12, color: C.textMuted, marginTop: 3, lineHeight: 1.45 }}>
                      {cfg.activo ? tr("SÍ — activada") : tr("NO — desactivada (como viene de fábrica)")}
                    </div>
                  </div>
                  <div onClick={function () {
                      if (!esDueno || guardando) return;
                      setConfirmar(cfg.activo ? "desactivar" : "activar");
                    }}
                    style={{ width: 50, height: 28, borderRadius: 15, flexShrink: 0, position: "relative",
                      cursor: esDueno ? "pointer" : "default", transition: "background 0.2s",
                      background: cfg.activo ? C.green : C.surface,
                      border: "1.5px solid " + (cfg.activo ? C.green : C.border), opacity: esDueno ? 1 : 0.5 }}>
                    <div style={{ width: 21, height: 21, borderRadius: "50%", background: "#fff",
                      position: "absolute", top: 2, left: cfg.activo ? 25 : 2, transition: "left 0.2s" }} />
                  </div>
                </div>

                {/* Lo que pasa y lo que NO pasa al activar. Dicho antes de tocarlo. */}
                <div style={{ fontSize: 12.5, color: C.textFaint, lineHeight: 1.55, marginTop: 10 }}>
                  {cfg.activo
                    ? tr("Activarla no emitió nada. Cada comprobante hay que mandarlo tocando «Emitir factura electrónica» en su factura.")
                    : tr("Mientras está apagada: Pollo IA sigue haciendo tus facturas, no se manda nada a la DGII ni a ningún proveedor, y no se te cobra nada por esto.")}
                </div>

                {/* Requisitos que faltan */}
                {cfg.activo && faltan.length > 0 && (
                  <div style={{ marginTop: 13, background: C.accent + "14",
                    border: "1px solid " + C.accent + "55", borderRadius: 12, padding: "12px 13px" }}>
                    <div style={{ fontSize: 13.5, fontWeight: 800, color: C.accent, marginBottom: 7 }}>
                      {tr("Falta esto para poder emitir")}
                    </div>
                    {faltan.map(function (f, i) {
                      return (
                        <div key={i} style={{ fontSize: 13, color: C.text, lineHeight: 1.5,
                          display: "flex", gap: 7, marginTop: 4 }}>
                          <span style={{ color: C.accent, flexShrink: 0 }}>•</span><span>{tr(f)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Conectar con el proveedor autorizado */}
                {cfg.activo && faltan.length === 0 && cfg.estado !== "conectado" && (
                  <div style={{ marginTop: 13 }}>
                    <div style={etiqueta}>{tr("PROVEEDOR AUTORIZADO")}</div>
                    {((srv && srv.proveedores) || []).map(function (p) {
                      return (
                        <div key={p.nombre} style={{ background: C.card, border: "1px solid " + C.border,
                          borderRadius: 12, padding: "12px 13px", marginBottom: 8 }}>
                          <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>{p.etiqueta}</div>
                          <div style={{ fontSize: 12, marginTop: 3, lineHeight: 1.45,
                            color: p.disponible ? C.green : C.textMuted }}>
                            {p.disponible ? tr("Disponible") : (p.porQueNo || tr("No disponible todavía"))}
                          </div>
                          {esDueno && p.disponible && (
                            <button onClick={function () { accion("conectar", p.nombre); }} disabled={guardando}
                              style={{ marginTop: 10, width: "100%", padding: "11px", borderRadius: 10,
                                border: "none", cursor: "pointer", background: "#C084FC", color: "#1a0b2e",
                                fontSize: 14, fontWeight: 900, fontFamily: "'Syne', sans-serif" }}>
                              {tr("Conectar mi granja")}
                            </button>
                          )}
                        </div>
                      );
                    })}
                    <div style={{ fontSize: 11.5, color: C.textFaint, lineHeight: 1.5 }}>
                      {tr("Tú no escribes ninguna clave: las credenciales del proveedor las instala el servidor de Pollo IA.")}
                    </div>
                  </div>
                )}

                {/* Ya conectada: proveedor, ambiente, consumo y tope */}
                {cfg.estado === "conectado" && (
                  <div style={{ marginTop: 13, background: C.green + "14",
                    border: "1px solid " + C.green + "55", borderRadius: 12, padding: "12px 13px" }}>
                    <div style={{ fontSize: 13.5, fontWeight: 800, color: C.green, marginBottom: 6 }}>
                      {tr("Conectada con ") + cfg.proveedor +
                        (cfg.ambiente === "pruebas" ? " · " + tr("ambiente de PRUEBAS") : " · " + tr("producción"))}
                    </div>
                    {cfg.estadoDetalle && (
                      <div style={{ fontSize: 12.5, color: C.textMuted, lineHeight: 1.5, marginBottom: 8 }}>
                        {cfg.estadoDetalle}
                      </div>
                    )}
                    <div style={{ fontSize: 12.5, color: C.text, lineHeight: 1.5 }}>
                      {tr("Comprobantes emitidos este mes: ")}
                      <b>{srv && srv.consumoMes != null ? srv.consumoMes : "—"}</b>
                      {cfg.topeMensual != null ? " / " + cfg.topeMensual : ""}
                    </div>
                    {esDueno && (
                      <div style={{ marginTop: 10 }}>
                        <div style={etiqueta}>{tr("TOPE MENSUAL (VACÍO = SIN TOPE)")}</div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <input value={fis.topeMensual} inputMode="numeric" placeholder={tr("sin tope")}
                            onChange={function (e) { setFis(Object.assign({}, fis, { topeMensual: e.target.value })); setSucio(true); }}
                            style={Object.assign({}, campoS, { flex: 1, fontFamily: "'Space Mono', monospace" })} />
                          <button onClick={guardarFiscal} disabled={guardando}
                            style={{ padding: "0 16px", borderRadius: 10, border: "1px solid " + C.border,
                              cursor: "pointer", background: C.card, color: C.text, fontSize: 14,
                              fontWeight: 800, fontFamily: "'Syne', sans-serif" }}>{tr("Guardar")}</button>
                        </div>
                        <div style={{ fontSize: 11.5, color: C.textFaint, marginTop: 7, lineHeight: 1.5 }}>
                          {tr("Al llegar al tope se bloquean las emisiones nuevas. Nunca el acceso a lo ya emitido.")}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Desconectar: borra el vínculo, nunca los comprobantes */}
                {cfg.estado === "conectado" && esDueno && (
                  <button onClick={function () { accion("desconectar"); }} disabled={guardando}
                    style={{ marginTop: 10, width: "100%", padding: "11px", borderRadius: 10,
                      border: "1px solid " + C.border, cursor: "pointer", background: "transparent",
                      color: C.textMuted, fontSize: 13.5, fontWeight: 700,
                      fontFamily: "'Syne', sans-serif" }}>{tr("Desconectar del proveedor")}</button>
                )}

                {/* Suspendida o con error */}
                {cfg.estado === "suspendido" && (
                  <div style={{ marginTop: 13, background: C.danger + "18",
                    border: "1px solid " + C.danger + "66", borderRadius: 12, padding: "12px 13px" }}>
                    <div style={{ fontSize: 13.5, fontWeight: 800, color: C.danger, marginBottom: 6 }}>
                      {tr("Suspendida")}
                    </div>
                    <div style={{ fontSize: 13, color: C.text, lineHeight: 1.5 }}>
                      {cfg.estadoDetalle || tr("Hubo un problema con el proveedor.")}
                    </div>
                    <div style={{ fontSize: 12.5, color: C.textMuted, marginTop: 8, lineHeight: 1.5 }}>
                      {tr("Tus facturas internas siguen funcionando. Lo ya emitido no se perdió.")}
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </GrupoFis>

        {/* Avisos y errores, al final y en claro */}
        {error && (
          <div style={{ background: C.danger + "18", border: "1px solid " + C.danger + "55",
            borderRadius: 12, padding: "12px 13px", fontSize: 13.5, color: C.danger, lineHeight: 1.5 }}>
            {error}
          </div>
        )}
        {aviso && !error && (
          <div style={{ background: C.green + "14", border: "1px solid " + C.green + "55",
            borderRadius: 12, padding: "12px 13px", fontSize: 13.5, color: C.green, lineHeight: 1.5 }}>
            {aviso}
          </div>
        )}

        {/* La diferencia entre una factura interna y un comprobante fiscal, dicha
            donde se lee: si esto no queda claro, todo lo demás da igual. */}
        <div style={{ marginTop: 16, background: C.card, border: "1px solid " + C.border,
          borderRadius: 12, padding: "13px 13px" }}>
          <div style={{ fontSize: 12.5, color: C.textMuted, lineHeight: 1.6 }}>
            <b style={{ color: C.text }}>{tr("Factura interna")}</b>{tr(": la que Pollo IA hace siempre, con tu nombre, tu logo y tu RNC. Vale como recibo y como control tuyo.")}
          </div>
          <div style={{ fontSize: 12.5, color: C.textMuted, lineHeight: 1.6, marginTop: 8 }}>
            <b style={{ color: C.text }}>{tr("Comprobante fiscal electrónico")}</b>{tr(": además lleva e-NCF, código de seguridad y QR, y vale ante la DGII solo cuando ella lo acepta. Eso necesita el grupo 3 activado y conectado.")}
          </div>
        </div>

        <button onClick={onClose} style={{ width: "100%", marginTop: 18, background: C.accent,
          color: "#1a0a00", border: "none", borderRadius: 11, padding: "14px", fontSize: 15.5,
          fontWeight: 900, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>{tr("Listo")}</button>
      </div>

      {/* Confirmación del interruptor. Se dice QUÉ PASA y QUÉ NO PASA antes de
          tocar nada: encender no emite, no abre cuentas y no cobra. */}
      {confirmar && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 66,
          background: "#000c", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
          onClick={function () { if (!guardando) setConfirmar(""); }}>
          <div onClick={function (e) { e.stopPropagation(); }}
            style={{ width: "100%", maxWidth: 410, background: C.surface, borderRadius: 16,
              padding: "20px 18px", border: "1.5px solid " + C.border }}>
            <div style={{ fontSize: 18, fontWeight: 900, fontFamily: "'Syne', sans-serif",
              marginBottom: 10, color: confirmar === "activar" ? C.green : C.warn }}>
              {confirmar === "activar"
                ? tr("¿Activar la facturación electrónica?")
                : tr("¿Desactivar la facturación electrónica?")}
            </div>
            <div style={{ fontSize: 13.5, color: C.textMuted, lineHeight: 1.6 }}>
              {confirmar === "activar"
                ? tr("Se guarda que la quieres usar y te digo qué datos faltan. NO se emite ninguna factura, NO se crea ninguna cuenta de pago y NO se contrata nada. Cada comprobante se manda después, uno por uno, tocando su botón.")
                : tr("Dejas de poder emitir comprobantes nuevos. Lo que ya emitiste se queda guardado y lo puedes seguir viendo. Tus facturas internas siguen igual.")}
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 17 }}>
              <button onClick={function () { if (!guardando) setConfirmar(""); }}
                style={{ flex: 1, padding: "13px", borderRadius: 11, border: "1px solid " + C.border,
                  cursor: "pointer", background: C.card, color: C.text, fontSize: 15, fontWeight: 700,
                  fontFamily: "'Syne', sans-serif" }}>{tr("Cancelar")}</button>
              <button onClick={function () { accion(confirmar); }} disabled={guardando}
                style={{ flex: 1, padding: "13px", borderRadius: 11, border: "none", cursor: "pointer",
                  background: confirmar === "activar" ? C.green : C.warn, color: "#fff", fontSize: 15,
                  fontWeight: 900, fontFamily: "'Syne', sans-serif", opacity: guardando ? 0.6 : 1 }}>
                {guardando ? tr("Un momento…") : confirmar === "activar" ? tr("Sí, activar") : tr("Sí, desactivar")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sesión vieja: se pide la contraseña y se termina sola la acción que
          se había quedado a medias. Nunca se cierra la sesión. */}
      {pedirClave && (
        <ModalClave C={C} correo={(session && session.email) || ""}
          onCerrar={function () { setPedirClave(false); setTrasClave(null); }}
          onListo={function () {
            setPedirClave(false); setError("");
            const queda = trasClave; setTrasClave(null);
            if (queda === "guardar") guardarFiscal();
            else if (queda && queda.cual) accion(queda.cual, queda.proveedor);
            else cargar();
          }} />
      )}
    </div>
  );
}

function Card({ children, style = {} }) {
  return <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 18, ...style }}>{children}</div>;
}
function Stat({ label, value, unit = "", color = C.accent, sub }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 24, fontWeight: 800, color, fontFamily: "'Space Mono', monospace", lineHeight: 1 }}>
        {value}<span style={{ fontSize: 14, marginLeft: 2, color: C.textMuted }}>{unit}</span>
      </div>
      <div style={{ fontSize: 14, color: C.textMuted, marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: 15, color: C.textFaint, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
function Input({ label, type = "text", value, onChange, placeholder, min, step, style = {} }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      {label && <span style={{ fontSize: 14, color: C.textMuted, fontFamily: "'Space Mono', monospace", letterSpacing: 1, textTransform: "uppercase" }}>{label}</span>}
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} min={min} step={step}
        style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, padding: "11px 13px", fontSize: 14, fontFamily: "'Syne', sans-serif", outline: "none", width: "100%", boxSizing: "border-box", ...style }} />
    </label>
  );
}
/* ---------------------------------------------------------------------------
   BARRA CON CONTROL FINO.

   El problema de una <input type="range"> en un teléfono, con el dedo:

   1. TOCAR = SALTAR. El pulgar se va al punto donde posas el dedo. Rozarla ya
      cambiaba la venta.
   2. UN PÍXEL VALE MUCHO. Con 750 pollos la barra de libras abarca unas 3,400 lb
      repartidas en 330 píxeles: cada píxel son 10 libras. Poner una cifra exacta
      con el dedo era imposible.

   Esta barra cambia las dos cosas:

   · ARRASTRE RELATIVO. Posar el dedo no mueve nada. El valor cambia según lo que
     el dedo RECORRE desde donde lo posaste, así que un roce ya no es un salto.

   · SENSIBILIDAD SEGÚN LA VELOCIDAD. Despacio, cada paso pide varios píxeles y se
     va de libra en libra; de un barrido rápido se cruza el rango entero. Es lo
     mismo que hace el puntero del ordenador y el dedo lo entiende sin que nadie
     se lo explique.

   Se ve igual que la barra de siempre: mismo alto, misma pista, misma bola.
   --------------------------------------------------------------------------- */
function BarraFina({ min, max, paso, valor, onChange, color }) {
  const refPista = useRef(null);
  const arrastre = useRef(null);

  const rango = Math.max(1e-9, max - min);
  const frac = Math.min(1, Math.max(0, (Number(valor) - min) / rango));

  /* Redondear al paso SIN arrastrar la basura del binario: 0.1+0.2 no puede
     acabar escribiendo 47.300000000000004 en una factura. */
  function clavar(v) {
    const r = Math.round(v / paso) * paso;
    return Math.min(max, Math.max(min, Number(r.toFixed(4))));
  }

  function alBajar(e) {
    const pista = refPista.current;
    if (!pista) return;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) {}
    arrastre.current = {
      x: e.clientX,
      t: e.timeStamp,
      v: Number(valor),
      ancho: pista.getBoundingClientRect().width || 1,
    };
  }

  function alMover(e) {
    const a = arrastre.current;
    if (!a) return;
    const dx = e.clientX - a.x;
    if (dx === 0) return;
    const dt = Math.max(1, e.timeStamp - a.t);
    a.x = e.clientX; a.t = e.timeStamp;

    const porPixel = rango / a.ancho;          // lo que valdría un píxel "a pelo"
    const vel = Math.abs(dx) / dt;             // píxeles por milisegundo

    /* Ganancia. Abajo: un paso cada 7 píxeles (control de a una libra). Arriba: algo
       más que el rango completo de lado a lado. La curva al cuadrado hace que el
       tramo lento ocupe casi todo el recorrido del dedo, que es donde hace falta. */
    const gLenta = Math.min(1, paso / (7 * porPixel));
    const gRapida = 1.35;
    const k = Math.min(1, Math.max(0, (vel - 0.06) / 0.84));
    const g = gLenta + Math.max(0, gRapida - gLenta) * (k * k);

    a.v = Math.min(max, Math.max(min, a.v + dx * porPixel * g));
    const nuevo = clavar(a.v);
    if (nuevo !== Number(valor)) onChange(nuevo);
  }

  function alSoltar(e) {
    arrastre.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (err) {}
  }

  // Las flechas mueven de a un paso: hace falta para quien use la app en el PC.
  function alTeclear(e) {
    let d = 0;
    if (e.key === "ArrowRight" || e.key === "ArrowUp") d = paso;
    else if (e.key === "ArrowLeft" || e.key === "ArrowDown") d = -paso;
    else return;
    e.preventDefault();
    onChange(clavar(Number(valor) + d));
  }

  return (
    <div className="pe-barra" ref={refPista} tabIndex={0}
      role="slider" aria-valuemin={min} aria-valuemax={max} aria-valuenow={Number(valor)}
      onPointerDown={alBajar} onPointerMove={alMover}
      onPointerUp={alSoltar} onPointerCancel={alSoltar} onKeyDown={alTeclear}>
      <div className="pe-barra-pista">
        <div className="pe-barra-lleno" style={{ width: (frac * 100).toFixed(3) + "%", background: color + "55" }} />
      </div>
      <div className="pe-barra-bola"
        style={{ left: "calc((100% - 46px) * " + frac.toFixed(4) + ")", background: color }} />
    </div>
  );
}

function Btn({ children, onClick, color = C.accent, textColor = "#1a0a00", outline = false, small = false, style = {} }) {
  return (
    <button onClick={onClick} style={{ background: outline ? "transparent" : color, color: outline ? color : textColor, border: `1px solid ${color}`, borderRadius: 8, padding: small ? "7px 14px" : "12px 18px", fontSize: small ? 13 : 14, fontWeight: 700, fontFamily: "'Syne', sans-serif", cursor: "pointer", width: "100%", ...style }}>
      {children}
    </button>
  );
}

// --- App ---
// --- PAYWALL SCREEN ---
function PaywallScreen({ session, onLogout, subStatus, onTrialActivated }) {
  const [loading, setLoading] = useState(false);
  const [trialLoading, setTrialLoading] = useState(false);
  const [plan, setPlan] = useState("monthly");
  // --- Código promocional (el que viene pegado a cada Pollo Box) ---
  const [promoAbierto, setPromoAbierto] = useState(false);
  const [promoCodigo, setPromoCodigo] = useState("");
  const [promoCargando, setPromoCargando] = useState(false);
  const [promoError, setPromoError] = useState("");
  const [promoOk, setPromoOk] = useState(null);
  function canjearCodigo() {
    var cod = promoCodigo.trim();
    if (!cod) { setPromoError(tr("Escribe el código que viene con tu caja.")); return; }
    setPromoCargando(true); setPromoError(""); setPromoOk(null);
    // El canje vive dentro de check-subscription (el plan gratuito de Vercel solo
    // admite 12 funciones y ya estaban todas ocupadas).
    fetch("/api/check-subscription", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: session.email, redeemCode: cod }),
    }).then(function (r) { return r.json(); })
      .then(function (d) {
        setPromoCargando(false);
        if (d && d.ok) {
          setPromoOk(d.mensaje || tr("¡Listo!"));
          // Se recarga para que la app vuelva a preguntar el acceso y entre de una vez.
          setTimeout(function () { window.location.reload(); }, 1800);
        } else {
          setPromoError((d && d.mensaje) || tr("No pudimos canjear ese código."));
        }
      })
      .catch(function () {
        setPromoCargando(false);
        setPromoError(tr("Error de conexión. Verifica tu internet."));
      });
  }
  // Config global de la prueba (la controla el dueño de la app desde su panel)
  const [trialCfg, setTrialCfg] = useState({ enabled: true, days: 7 });
  useEffect(function () {
    SB.select("app_config", "key=in.(trial_enabled,trial_days)").then(function (rows) {
      var en = true, d = 7;
      (Array.isArray(rows) ? rows : []).forEach(function (r) {
        if (r.key === "trial_enabled") en = r.value !== "false";
        if (r.key === "trial_days" && Number(r.value) > 0) d = Number(r.value);
      });
      setTrialCfg({ enabled: en, days: d });
    }).catch(function () {});
  }, []);
  var trialUsed = subStatus && subStatus.trialExpired;
  var subtitle = trialUsed ? tr("Tu período de prueba ha terminado") : tr("Elige un plan para continuar");
  return (
    <div style={{ minHeight: "100vh", background: "#1e1e24", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "'Syne', sans-serif" }}>
      <div style={{ maxWidth: 380, width: "100%", textAlign: "center" }}>
        <div style={{ fontSize: 56, marginBottom: 16 }}>🐔</div>
        <div style={{ fontSize: 24, fontWeight: 900, color: "#f5c200", marginBottom: 8 }}>{tr("POLLO")} <span style={{ color: "#cc1a00" }}>{tr("IA")}</span></div>
        <div style={{ fontSize: 14, color: "#b0b0c0", marginBottom: 8 }}>{subtitle}</div>
        <div style={{ fontSize: 12.5, color: "#888898", marginBottom: 28 }}>{tr("Sesión iniciada como")} <b style={{ color: "#e0e0e0" }}>{session.email}</b>
        </div>

        <div style={{ background: "#26262e", borderRadius: 20, padding: 24, marginBottom: 24, border: "1px solid #44444f" }}>
          <div style={{ fontSize: 13, color: "#b0b0c0", marginBottom: 16 }}>{trialCfg.enabled ? tr("Elige tu plan — ") + trialCfg.days + tr(" días gratis incluidos") : tr("Elige tu plan")}</div>

          <div style={{ textAlign: "left", marginBottom: 20 }}>
            {["✅ Hasta 5 camadas activas", "✅ Registro diario ilimitado", "✅ Análisis de heces con IA", "✅ Historial completo", "✅ Empleados gratis con código", "✅ Cámara con IA"].map(function(f) {
              return <div key={f} style={{ fontSize: 13, color: "#e0e0e0", padding: "5px 0", borderBottom: "1px solid #44444f" }}>{tr(f)}</div>;
            })}
          </div>

          {/* Plan selector */}
          <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
            <button onClick={function() { setPlan("monthly"); }}
              style={{ flex: 1, padding: "12px 8px", borderRadius: 12, border: "2px solid " + (plan === "monthly" ? "#f5c200" : "#44444f"), background: plan === "monthly" ? "#f5c20022" : "#26262e", cursor: "pointer" }}>
              <div style={{ fontSize: 18, fontWeight: 900, color: plan === "monthly" ? "#f5c200" : "#888" }}>$9.99</div>
              <div style={{ fontSize: 11, color: "#888" }}>{tr("por mes")}</div>
            </button>
            <button onClick={function() { setPlan("yearly"); }}
              style={{ flex: 1, padding: "12px 8px", borderRadius: 12, border: "2px solid " + (plan === "yearly" ? "#f5c200" : "#44444f"), background: plan === "yearly" ? "#f5c20022" : "#26262e", cursor: "pointer", position: "relative" }}>
              <div style={{ position: "absolute", top: -10, right: 8, background: "#cc1a00", color: "#fff", fontSize: 9, fontWeight: 800, padding: "2px 6px", borderRadius: 4 }}>{tr("AHORRA 17%")}</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: plan === "yearly" ? "#f5c200" : "#888" }}>$99.99</div>
              <div style={{ fontSize: 11, color: "#888" }}>{tr("por año")}</div>
            </button>
          </div>

          <button onClick={function() {
            setLoading(true);
            // Dentro de la app de Android → cobra Google Play. En la web → Stripe.
            if (isPlayBillingAvailable()) {
              comprarConGooglePlay(plan).then(function(r) {
                if (!r.ok) {
                  if (r.reason !== "cancelado") alert(tr("No se pudo iniciar el pago de Google Play. Intenta de nuevo."));
                  setLoading(false);
                  return;
                }
                // Verificar la compra en el servidor antes de dar acceso.
                fetch("/api/verify-play-purchase", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ email: session.email, purchaseToken: r.purchaseToken, productId: r.productId }),
                }).then(function(x) { return x.json(); })
                  .then(function(resp) {
                    if (resp && resp.active) { window.location.reload(); }
                    else { alert(tr("No se pudo confirmar la compra. Si se te cobró, escríbenos y lo resolvemos.")); setLoading(false); }
                  })
                  .catch(function() { alert(tr("Error de conexión al confirmar la compra.")); setLoading(false); });
              });
            } else {
              startCheckout(session.email, session.nombre, plan);
            }
          }} disabled={loading}
            style={{ width: "100%", background: loading ? "#44444f" : "linear-gradient(135deg, #f5c200, #cc8800)", color: "#1a0a00", border: "none", borderRadius: 14, padding: "16px", fontSize: 16, fontWeight: 900, cursor: loading ? "not-allowed" : "pointer", marginBottom: 10 }}>
            {loading ? tr("Procesando...") : tr("Suscribirme — ") + (plan === "yearly" ? tr("$99.99/año") : tr("$9.99/mes")) + " →"}
          </button>

          {!trialUsed && trialCfg.enabled && (
          <button onClick={function() {
            setTrialLoading(true);
            var trialStart = new Date().toISOString();
            fetch("/api/check-subscription", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ email: session.email, setTrial: true, trialStart: trialStart })
            }).then(function(r) { return r.json(); })
            .then(function(data) {
              setTrialLoading(false);
              if (data && data.trialDisabled) {
                alert(data.message || tr("Prueba gratis no disponible por el momento."));
              } else if (data && data.active) {
                if (onTrialActivated) { onTrialActivated(data.daysLeft); }
                else { window.location.reload(); }
              } else {
                alert(tr("Error activando el trial. Intenta de nuevo."));
              }
            })
            .catch(function() {
              setTrialLoading(false);
              alert(tr("Error de conexión. Verifica tu internet."));
            });
          }} disabled={trialLoading}
            style={{ width: "100%", background: trialLoading ? "#44444f" : "transparent", color: trialLoading ? "#888" : "#f5c200", border: "2px solid #f5c200", borderRadius: 14, padding: "15px", fontSize: 16, fontWeight: 900, cursor: trialLoading ? "not-allowed" : "pointer", marginBottom: 12 }}>
            {trialLoading ? tr("Activando...") : tr("🎁 Comenzar prueba gratis de ") + trialCfg.days + tr(" días")}
          </button>
          )}

          {/* ── Código promocional ── */}
          {!promoAbierto && !promoOk && (
            <button onClick={function () { setPromoAbierto(true); }}
              style={{ width: "100%", background: "linear-gradient(135deg, #34C759, #1f9440)", color: "#06220f", border: "none", borderRadius: 12, padding: "13px", fontSize: 15, fontWeight: 900, cursor: "pointer", marginBottom: 12, whiteSpace: "nowrap" }}>{tr("🎟️ Tengo un código")}</button>
          )}

          {promoAbierto && !promoOk && (
            <div style={{ background: "#1e1e24", border: "1.5px solid #44444f", borderRadius: 12, padding: 14, marginBottom: 12, textAlign: "left" }}>
              <div style={{ fontSize: 12, color: "#b0b0c0", marginBottom: 8 }}>{tr("Escribe el código que viene con tu")} <b style={{ color: "#f5c200" }}>{tr("Pollo Box")}</b>:
              </div>
              <input
                value={promoCodigo}
                onChange={function (e) { setPromoCodigo(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, "")); setPromoError(""); }}
                onKeyDown={function (e) { if (e.key === "Enter") canjearCodigo(); }}
                placeholder={tr("Ej: KT47")}
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                style={{ width: "100%", background: "#26262e", border: "2px solid " + (promoError ? "#cc1a00" : "#44444f"), borderRadius: 10, color: "#f5f5f5", padding: "13px 14px", fontSize: 17, fontWeight: 800, fontFamily: "'Space Mono', monospace", letterSpacing: 1.5, textAlign: "center", outline: "none", marginBottom: 10 }} />
              {promoError && (
                <div style={{ fontSize: 12.5, color: "#ff6b5a", marginBottom: 10, lineHeight: 1.5 }}>{promoError}</div>
              )}
              <button onClick={canjearCodigo} disabled={promoCargando}
                style={{ width: "100%", background: promoCargando ? "#44444f" : "#f5c200", color: "#1a0a00", border: "none", borderRadius: 10, padding: "13px", fontSize: 15, fontWeight: 900, cursor: promoCargando ? "not-allowed" : "pointer", fontFamily: "'Syne', sans-serif" }}>
                {promoCargando ? tr("Comprobando...") : tr("Canjear código")}
              </button>
            </div>
          )}

          {promoOk && (
            <div style={{ background: "#0a2000", border: "1.5px solid #4caf5088", borderRadius: 12, padding: "16px 14px", marginBottom: 12, textAlign: "center" }}>
              <div style={{ fontSize: 30, marginBottom: 6 }}>🎉</div>
              <div style={{ fontSize: 14.5, color: "#4caf50", fontWeight: 800, lineHeight: 1.5 }}>{promoOk}</div>
              <div style={{ fontSize: 12, color: "#b0b0c0", marginTop: 8 }}>{tr("Entrando…")}</div>
            </div>
          )}

          <div style={{ fontSize: 11, color: "#888898", textAlign: "center" }}>{tr("Pago seguro con Stripe · Cancela cuando quieras")}</div>
        </div>

        <button onClick={onLogout} style={{ background: "none", border: "none", color: "#888898", fontSize: 13, cursor: "pointer", textDecoration: "underline" }}>{tr("Cerrar sesión")}</button>
      </div>
    </div>
  );
}

export default function PolloExpressApp() {
  const [session, setSession] = useState(DB.getSession());
  const [subStatus, setSubStatus] = useState(null);
  const [screen, setScreen] = useState(session ? "app" : "login");
  // Botón de idioma antes de entrar. Dentro de la app manda Ajustes.
  const [, setIdiomaTick] = useState(0);
  function conIdioma(pantalla) {
    var en = IDIOMA_APP === "en";
    return (
      <>
        <button id="pe-idioma-toggle" onClick={function () { fijarIdioma(en ? "es" : "en"); setIdiomaTick(function (x) { return x + 1; }); }}
          style={{ position: "fixed", top: 12, right: 12, zIndex: 50, background: "#26262e", border: "1px solid #44444f", borderRadius: 20, padding: "6px 12px", cursor: "pointer", fontSize: 13, fontWeight: 800, fontFamily: "'Space Mono', monospace", color: "#888898" }}>
          <span style={{ color: en ? "#888898" : "#f5c200" }}>ES</span> · <span style={{ color: en ? "#f5c200" : "#888898" }}>EN</span>
        </button>
        {pantalla}
      </>
    );
  }

  // Detectar link de reset password en la URL
  useEffect(function() {
    var hash = window.location.hash;
    if (hash && hash.indexOf("type=recovery") !== -1) {
      var params = {};
      hash.replace("#", "").split("&").forEach(function(p) {
        var kv = p.split("="); params[kv[0]] = kv[1];
      });
      if (params.access_token) {
        window.location.hash = "";
        setScreen("resetpassword_" + params.access_token);
      }
    }
  }, []);

  // Verificar suscripción al restaurar sesión (ej. después de reload)
  useEffect(function() {
    if (session && session.rol === "propietario" && subStatus === null) {
      verificarAcceso(session.email, function(err, data) {
        if (err || !data) {
          setSubStatus({ active: false, error: true });
        } else {
          setSubStatus(data);
        }
      });
    } else if (session && session.rol !== "propietario" && subStatus === null) {
      setSubStatus({ active: true, trial: false });
    }
  }, [session]);

  // SEGURIDAD: si la sesión es de un EMPLEADO, verificar en cada carga que sigue existiendo en
  // Supabase. Si el dueño lo eliminó, su `usuarios` ya no está → se cierra su sesión. Así un
  // empleado eliminado NO puede seguir accediendo (su teléfono lo recordaba localmente).
  // Falla en modo seguro: si hay error de red, NO cierra sesión (la app funciona offline).
  useEffect(function() {
    if (!session || session.rol !== "empleado" || !session.email) return;
    SB.select("usuarios", "email=eq." + encodeURIComponent(session.email) + "&select=email")
      .then(function(rows) {
        if (Array.isArray(rows) && rows.length === 0) {
          // Confirmado eliminado → limpiar y cerrar sesión.
          try { var us = DB.getUsers(); delete us[session.email]; DB.saveUsers(us); } catch (e) {}
          handleLogout();
        }
      })
      .catch(function() {}); // sin internet → no cerrar (offline)
  }, [session]);

  function handleLogin(sess) {
    DB.saveSession(sess);
    setSession(sess);
    setScreen("app");
    // Check subscription for propietarios
    if (sess && sess.rol === "propietario") {
      verificarAcceso(sess.email, function(err, data) {
        if (err || !data) {
          setSubStatus({ active: false, error: true });
        } else {
          setSubStatus(data);
        }
      });
    } else {
      setSubStatus({ active: true, trial: false });
    }
  }

  function handleLogout() { DB.clearSession(); setSession(null); setSubStatus(null); setScreen("login"); }

  if (screen === "login") return conIdioma(<AuthScreen onLogin={handleLogin} onGoRegister={() => setScreen("register")} onGoCode={() => setScreen("joincode")} />);
  if (screen === "register") return conIdioma(<RegisterScreen onLogin={handleLogin} onBack={() => setScreen("login")} />);
  if (screen === "joincode") return conIdioma(<JoinCodeScreen onLogin={handleLogin} onBack={() => setScreen("login")} />);
  if (screen && screen.startsWith("resetpassword_")) {
    var resetToken = screen.replace("resetpassword_", "");
    return <ResetPasswordScreen token={resetToken} onDone={() => setScreen("login")} />;
  }

  // Mientras verifica suscripción — mostrar loading
  if (screen === "app" && session && session.rol === "propietario" && subStatus === null) {
    return (
      <div style={{ minHeight: "100vh", background: "#1e1e24", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16 }}>
        <div style={{ fontSize: 48 }}>🐔</div>
        <div style={{ color: "#f5c200", fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 18 }}>Verificando acceso...</div>
      </div>
    );
  }

  // Show paywall if no active subscription
  if (screen === "app" && session && session.rol === "propietario" && subStatus && !subStatus.active) {
    return conIdioma(<PaywallScreen session={session} onLogout={handleLogout} subStatus={subStatus} onTrialActivated={function(d) { setSubStatus({ active: true, trial: true, daysLeft: d || 7 }); }} />);
  }

  if (screen === "app" && session) return <MainApp session={session} subStatus={subStatus} onLogout={handleLogout} />;
  return conIdioma(<AuthScreen onLogin={handleLogin} onGoRegister={() => setScreen("register")} onGoCode={() => setScreen("joincode")} />);
}

function ResetPasswordScreen({ token, onDone }) {
  const [pass, setPass] = useState("");
  const [pass2, setPass2] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  var C = C_BASE;

  function doReset() {
    setError("");
    if (!pass || pass.length < 6) { setError(tr("La contraseña debe tener al menos 6 caracteres")); return; }
    if (pass !== pass2) { setError(tr("Las contraseñas no coinciden")); return; }
    setLoading(true);
    fetch(SUPABASE_URL + "/auth/v1/user", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "apikey": SUPABASE_KEY,
        "Authorization": "Bearer " + token
      },
      body: JSON.stringify({ password: pass })
    }).then(function(r) { return r.json(); })
    .then(function(data) {
      setLoading(false);
      if (data.error) { setError(data.error.message || tr("Error al cambiar contraseña")); return; }
      setDone(true);
    }).catch(function() {
      setLoading(false);
      setError(tr("Error de conexión. Intenta de nuevo."));
    });
  }

  return (
    <AuthShell title={tr("Nueva Contraseña")}>
      {done ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 16, textAlign: "center" }}>
          <div style={{ fontSize: 48 }}>✅</div>
          <div style={{ color: C.accent, fontWeight: 700, fontSize: 16 }}>{tr("¡Contraseña actualizada!")}</div>
          <div style={{ color: C.textMuted, fontSize: 14 }}>{tr("Ya puedes iniciar sesión con tu nueva contraseña.")}</div>
          <Btn onClick={onDone}>{tr("Ir al login")}</Btn>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
          <div style={{ color: C.textMuted, fontSize: 14, marginBottom: 4 }}>{tr("Escribe tu nueva contraseña.")}</div>
          <Input label={tr("Nueva contraseña")} type="password" value={pass} onChange={setPass} placeholder={tr("Mínimo 6 caracteres")} />
          <Input label={tr("Confirmar contraseña")} type="password" value={pass2} onChange={setPass2} placeholder={tr("Repite la contraseña")} />
          {error && <div style={{ color: C.danger, fontSize: 14, textAlign: "center" }}>{error}</div>}
          <Btn onClick={doReset} disabled={loading}>{loading ? tr("Guardando...") : tr("Guardar nueva contraseña")}</Btn>
        </div>
      )}
    </AuthShell>
  );
}

function AuthShell({ title, children, onBack }) {
  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", flexDirection: "column", maxWidth: 480, margin: "0 auto", paddingBottom: 40 }}>
      <style>{`${fonts} * { box-sizing: border-box; margin: 0; padding: 0; } body { background: ${C.bg}; color: ${C.text}; font-family: 'Syne', sans-serif; } input:focus { border-color: ${C.accent} !important; outline: none; }`}</style>
      <div style={{ padding: "28px 24px 16px", display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
        <div style={{ width: 80, height: 80, borderRadius: "50%", overflow: "hidden", border: `3px solid ${C.accent}`, boxShadow: `0 0 20px ${C.accent}44` }}>
          <img src={"data:image/jpeg;base64,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"} alt={tr("Pollo IA")} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        </div>
        <div style={{ textAlign: "center" }}>
          <div>
          <div style={{ fontSize: 28, fontWeight: 800 }}><span style={{ color: C.accent }}>{tr("POLLO")}</span> <span style={{ color: C.danger }}>{tr("IA")}</span></div>
          
        </div>
          <div style={{ fontSize: 15, color: C.textFaint, fontFamily: "'Space Mono', monospace" }}>{tr("Manejo y Crianza de Pollos de Engorde")}</div>
        </div>
      </div>
      <div style={{ margin: "0 16px" }}>
        <Card>
          {onBack && <button onClick={onBack} style={{ background: "none", border: "none", color: C.textMuted, cursor: "pointer", fontSize: 14, fontFamily: "'Space Mono', monospace", marginBottom: 14, padding: 0 }}>{tr("← Volver")}</button>}
          <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 18, color: C.accent }}>{title}</div>
          {children}
        </Card>
      </div>
    </div>
  );
}

// Un correo siempre se guarda y se busca igual: sin espacios y en minúsculas. El teclado del
// teléfono suele dejar un espacio al final al autocompletar, y mucha gente escribe la primera
// letra en mayúscula; las dos cosas dejaban al usuario fuera de su propia cuenta.
function normalizarCorreo(x) { return String(x || "").trim().toLowerCase(); }

function AuthScreen({ onLogin, onGoRegister, onGoCode }) {
  const [email, setEmail] = useState(""); const [pass, setPass] = useState(""); const [error, setError] = useState(""); const [loading, setLoading] = useState(false);
  // true cuando Supabase dice "correo sin confirmar": se ofrece reenviar el enlace.
  const [sinConfirmar, setSinConfirmar] = useState(false);
  function doLogin() {
    setError(""); setLoading(true); setSinConfirmar(false);
    var correo = normalizarCorreo(email);
    if (!correo || !pass) { setError(tr("Completa todos los campos")); setLoading(false); return; }
    // Try Supabase auth first
    SB.signIn(correo, pass)
    .then(function(data) {
      if (data.error || !data.access_token) {
        // Fallback 1: localStorage
        const users = DB.getUsers(); const u = users[correo];
        if (u && u.pass === btoa(pass)) {
          const sess = { email: u.email, nombre: u.nombre, rol: u.rol, ownerEmail: u.ownerEmail || u.email, permisos: u.permisos };
          DB.saveSession(sess); onLogin(sess); return;
        }
        // SEGURIDAD: aquí había un "Fallback 2" que, si Supabase rechazaba la
        // contraseña, buscaba el correo en la tabla `usuarios` y dejaba entrar SIN
        // comprobar la contraseña. Cualquiera que supiera el correo de un usuario
        // entraba en su cuenta desde otro teléfono. Ahora manda lo que diga Supabase.
        var codigoError = String((data && (data.error_code || data.error)) || "");
        var textoError = String((data && (data.msg || data.error_description || data.message)) || "");
        if (codigoError === "email_not_confirmed" || /not confirmed/i.test(textoError)) {
          // Se registró pero nunca tocó el enlace de confirmación.
          setSinConfirmar(true);
          setError(tr("Tu correo todavía no está confirmado. Busca en tu bandeja de entrada (y en spam) el correo que te enviamos y toca el enlace."));
        } else if (codigoError === "invalid_credentials" || /invalid login/i.test(textoError)) {
          setError(tr("Correo o contraseña incorrectos. Si eres empleado, entra con \"Tengo un código de empleado\"."));
        } else {
          // Límite de intentos, servidor caído, etc.: no es culpa de la contraseña.
          setError(tr("No pudimos iniciar sesión ahora mismo. Intenta de nuevo en un momento."));
        }
      } else {
        // Supabase auth success
        const sbUser = data.user || {};
        const meta = sbUser.user_metadata || {};
        SB.withToken(data.access_token).select("usuarios", "email=eq." + encodeURIComponent(correo))
        .then(function(rows) {
          const u = (rows && rows[0]) || {};
          const sess = {
            email: correo,
            nombre: meta.nombre || u.nombre || correo,
            rol: u.rol || "propietario",
            ownerEmail: u.owner_email || correo,
            permisos: u.permisos || {},
            token: data.access_token,
            // Sin esto el token muere en una hora y no hay cómo renovarlo.
            refreshToken: data.refresh_token || "",
          };
          DB.saveSession(sess); onLogin(sess);
        });
      }
      setLoading(false);
    })
    .catch(function() {
      setError(tr("Error de conexión. Verifica tu internet.")); setLoading(false);
    });
  }

  return (
    <AuthShell title={tr("Iniciar Sesión")}>
      <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
        <Input label={tr("Correo electrónico")} type="email" value={email} onChange={setEmail} placeholder={tr("tu@correo.com")} />
        <Input label={tr("Contraseña")} type="password" value={pass} onChange={setPass} placeholder="••••••••" />
        {error && <div style={{ color: C.danger, fontSize: 14, textAlign: "center" }}>{error}</div>}
        {sinConfirmar && (
          <button onClick={function () {
            var correo = email.trim().toLowerCase();
            if (!correo) return;
            fetch(SUPABASE_URL + "/auth/v1/resend", {
              method: "POST", headers: SB.headers,
              body: JSON.stringify({ type: "signup", email: correo }),
            }).then(function (r) {
              setError(r.ok
                ? tr("✅ Te reenviamos el correo de confirmación. Toca el enlace y vuelve a entrar.")
                : tr("No pudimos reenviarlo ahora mismo (se puede pedir una vez por minuto). Intenta en un rato."));
            }).catch(function () { setError(tr("Error de conexión. Verifica tu internet.")); });
          }} style={{ background: "none", border: "none", color: C.accent, cursor: "pointer", fontSize: 14, textDecoration: "underline", padding: "4px 0" }}>{tr("📧 Reenviar correo de confirmación")}</button>
        )}
        <Btn onClick={doLogin}>{tr("Entrar")}</Btn>
        <button onClick={function() {
          var correo = normalizarCorreo(email);
          if (!correo) { setError(tr("Escribe tu correo primero")); return; }
          SB.resetPassword(correo)
          .then(function(r) {
            // resetPassword devuelve el JSON aunque Supabase conteste con error, así que hay
            // que mirarlo: antes siempre decía "te enviamos el link", incluso cuando no salió.
            if (r && (r.error || r.msg || r.error_code)) { setError(tr("No pudimos enviar el correo. Verifica tu dirección.")); return; }
            setError(tr("✅ Te enviamos un link de recuperación a tu correo."));
          })
          .catch(function() { setError(tr("No pudimos enviar el correo. Verifica tu dirección.")); });
        }} style={{ background: "none", border: "none", color: C.accent, cursor: "pointer", fontSize: 14, textDecoration: "underline", padding: "4px 0" }}>{tr("¿Olvidaste tu contraseña?")}</button>
        <div style={{ textAlign: "center", color: C.textFaint, fontSize: 15 }}>─── o ───</div>
        <Btn onClick={onGoRegister} outline color={C.accent}>{tr("Crear cuenta nueva")}</Btn>
        <Btn onClick={onGoCode} outline color={C.warn}>{tr("Tengo un código de empleado")}</Btn>
      </div>
    </AuthShell>
  );
}

function RegisterScreen({ onLogin, onBack }) {
  const [nombre, setNombre] = useState(""); const [email, setEmail] = useState("");
  const [pass, setPass] = useState(""); const [pass2, setPass2] = useState(""); 
  const [error, setError] = useState(""); const [loading, setLoading] = useState(false);
  
  function doRegister() {
    setError(""); setLoading(true);
    var correo = normalizarCorreo(email);
    if (!nombre || !correo || !pass) { setError(tr("Completa todos los campos")); setLoading(false); return; }
    if (pass !== pass2) { setError(tr("Las contraseñas no coinciden")); setLoading(false); return; }
    if (pass.length < 6) { setError(tr("Contraseña mínimo 6 caracteres")); setLoading(false); return; }
    
    var empCode = genCode();
    
    // Register in Supabase Auth
    SB.signUp(correo, pass, { nombre: nombre })
    .then(function(data) {
      if (data.error) {
        // Fallback to localStorage registration
        var users = DB.getUsers();
        if (users[correo]) { setError(tr("Este correo ya está registrado")); setLoading(false); return; }
        users[correo] = { email: correo, nombre, pass: btoa(pass), rol: "propietario", ownerEmail: correo, empCode };
        DB.saveUsers(users);
        var sess = { email: correo, nombre, rol: "propietario", ownerEmail: correo };
        DB.saveSession(sess); onLogin(sess);
        setLoading(false);
        return;
      }
      
      // Save user profile to Supabase
      SB.upsert("usuarios", {
        email: correo,
        nombre: nombre,
        rol: "propietario",
        owner_email: correo,
        emp_code: empCode,
        empleado_permisos: {},
        permisos: {}
      }).then(function() {
        // Also save locally as backup
        var users = DB.getUsers();
        users[correo] = { email: correo, nombre, pass: btoa(pass), rol: "propietario", ownerEmail: correo, empCode };
        DB.saveUsers(users);
        var token = data.session && data.session.access_token || "";
        var refresh = data.session && data.session.refresh_token || "";
        var sess = { email: correo, nombre, rol: "propietario", ownerEmail: correo, token, refreshToken: refresh };
        DB.saveSession(sess); onLogin(sess);
        setLoading(false);
      });
    })
    .catch(function() {
      setError(tr("Error de conexión. Verifica tu internet.")); setLoading(false);
    });
  }

  return (
    <AuthShell title={tr("Crear Cuenta")} onBack={onBack}>
      <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
        <Input label={tr("Tu nombre")} value={nombre} onChange={setNombre} placeholder={tr("Andrés Méndez")} />
        <Input label={tr("Correo electrónico")} type="email" value={email} onChange={setEmail} placeholder={tr("tu@correo.com")} />
        <Input label={tr("Contraseña")} type="password" value={pass} onChange={setPass} placeholder={tr("Mínimo 6 caracteres")} />
        <Input label={tr("Confirmar contraseña")} type="password" value={pass2} onChange={setPass2} placeholder={tr("Repite la contraseña")} />
        {error && <div style={{ color: C.danger, fontSize: 14, textAlign: "center" }}>{error}</div>}
        <Btn onClick={doRegister}>{tr("Registrarme como Propietario")}</Btn>
      </div>
    </AuthShell>
  );
}

function JoinCodeScreen({ onLogin, onBack }) {
  const [codigo, setCodigo] = useState(""); const [nombre, setNombre] = useState("");
  const [pass, setPass] = useState(""); const [error, setError] = useState(""); 
  const [ownerFound, setOwnerFound] = useState(null); const [loading, setLoading] = useState(false);
  function buscar() {
    setError(""); setOwnerFound(null); setLoading(true);
    var code = codigo.toUpperCase();
    // Si ya existe un empleado con este código en localStorage → login directo
    var users = DB.getUsers();
    var existingEmp = Object.values(users).find(function(u) {
      return u.rol === "empleado" && (u.empCode === code || u.emp_code === code);
    });
    if (existingEmp) {
      // SEGURIDAD: antes de entrar con el atajo local, verificar en Supabase que el empleado
      // NO fue eliminado por el dueño. Si ya no existe en `usuarios`, se bloquea el acceso.
      var entrar = function() {
        var sess = { email: existingEmp.email, nombre: existingEmp.nombre, rol: "empleado", ownerEmail: existingEmp.ownerEmail, empCode: existingEmp.empCode || existingEmp.emp_code || code, permisos: existingEmp.permisos || {} };
        DB.saveSession(sess); onLogin(sess); setLoading(false);
      };
      SB.select("usuarios", "email=eq." + encodeURIComponent(existingEmp.email) + "&select=email")
        .then(function(rows) {
          if (Array.isArray(rows) && rows.length === 0) {
            // Eliminado por el dueño → quitar de local y bloquear.
            try { var us = DB.getUsers(); delete us[existingEmp.email]; DB.saveUsers(us); } catch (e) {}
            setError(tr("Tu acceso fue removido por el dueño. Pídele un código nuevo.")); setLoading(false);
          } else { entrar(); } // existe (o no se pudo confirmar borrado) → permitir
        })
        .catch(function() { entrar(); }); // sin internet → permitir entrar (offline); se valida al reconectar
      return;
    }
    // Primera vez — buscar propietario
    SB.select("usuarios", "emp_code=eq." + code + "&rol=eq.propietario")
    .then(function(rows) {
      if (rows && rows.length > 0) {
        setOwnerFound({ email: rows[0].email, nombre: rows[0].nombre, empCode: code, empleadoPermisos: rows[0].permisos, fromSupabase: true });
        setLoading(false);
      } else {
        SB.select("usuarios", "emp_code=eq." + code)
        .then(function(rows2) {
          if (rows2 && rows2.length > 0) {
            var fila = rows2[0];
            // Si el código es de un EMPLEADO que ya existe (creado por el dueño) → entrar
            // DIRECTO como ese empleado. Sin pedir nombre ni re-crearlo (evita el choque 409
            // y las identidades duplicadas por escribir el nombre distinto).
            if (fila.rol === "empleado") {
              var ownerEm = fila.owner_email || fila.ownerEmail;
              var us2 = DB.getUsers();
              us2[fila.email] = { email: fila.email, nombre: fila.nombre, rol: "empleado", ownerEmail: ownerEm, empCode: code, permisos: fila.permisos || {} };
              DB.saveUsers(us2);
              var sessEmp = { email: fila.email, nombre: fila.nombre, rol: "empleado", ownerEmail: ownerEm, permisos: fila.permisos || {} };
              DB.saveSession(sessEmp);
              onLogin(sessEmp);
              setLoading(false);
              return;
            }
            var ownerEmail = fila.owner_email || fila.ownerEmail;
            SB.select("usuarios", "email=eq." + ownerEmail)
            .then(function(ownerRows) {
              if (ownerRows && ownerRows.length > 0) {
                setOwnerFound({ email: ownerRows[0].email, nombre: ownerRows[0].nombre, empCode: code, empleadoPermisos: fila.permisos, fromSupabase: true });
              } else { setError(tr("Código no válido. Verifica con tu jefe.")); }
              setLoading(false);
            }).catch(function() { setError(tr("Error de conexión.")); setLoading(false); });
          } else { setError(tr("Código no válido. Verifica con tu jefe.")); setLoading(false); }
        }).catch(function() { setError(tr("Código no válido.")); setLoading(false); });
      }
    }).catch(function() { setError(tr("Error de conexión. Verifica tu internet.")); setLoading(false); });
  }

  function doJoin() {
    if (!nombre) { setError(tr("Escribe tu nombre")); return; }
    var empCode = ownerFound.empCode || ownerFound.emp_code;
    var ownerEmail = ownerFound.email;
    var empEmail = empCode + "_" + nombre.toLowerCase().replace(/\s+/g, "") + "@empleado.polloia";
    SB.upsert("usuarios", { email: empEmail, nombre, rol: "empleado", owner_email: ownerEmail, emp_code: empCode, permisos: ownerFound.empleadoPermisos || {} }).catch(function() {});
    var users = DB.getUsers();
    users[empEmail] = { email: empEmail, nombre, rol: "empleado", ownerEmail, empCode, permisos: ownerFound.empleadoPermisos || {} };
    DB.saveUsers(users);
    /* El código queda GUARDADO en la sesión: es la credencial con la que el empleado
       escribe en la libreta de clientes de la granja (no tiene token de Supabase). */
    var sess = { email: empEmail, nombre, rol: "empleado", ownerEmail, empCode, permisos: ownerFound.empleadoPermisos || {} };
    DB.saveSession(sess);
    onLogin(sess);
  }
  return (
    <AuthShell title={tr("Código de Empleado")} onBack={onBack}>
      <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
        <div style={{ fontSize: 15, color: C.textMuted, textAlign: "center" }}>{tr("Tu empleador te comparte un código de 6 letras.")}</div>
        <div style={{ display: "flex", gap: 8 }}>
          <input value={codigo} onChange={e => setCodigo(e.target.value.toUpperCase())} placeholder={tr("ABC123")} maxLength={6}
            style={{ flex: 1, background: C.surface, border: `2px solid ${C.accent}55`, borderRadius: 8, color: C.accent, padding: "12px", fontSize: 22, fontFamily: "'Space Mono', monospace", fontWeight: 700, outline: "none", textAlign: "center", letterSpacing: 4, boxSizing: "border-box" }} />
          <button onClick={buscar} style={{ background: C.accent, color: "#1a0a00", border: "none", borderRadius: 8, padding: "12px 14px", fontWeight: 700, cursor: "pointer", fontSize: 14, fontFamily: "'Syne', sans-serif" }}>{tr("Verificar")}</button>
        </div>
        {error && <div style={{ color: C.danger, fontSize: 14, textAlign: "center" }}>{error}</div>}
        {ownerFound && (
          <div style={{ background: "#1a2a00", border: `1px solid ${C.accent}44`, borderRadius: 10, padding: 14 }}>
            <div style={{ fontSize: 15, color: C.textFaint, marginBottom: 4 }}>{tr("GRANJA ENCONTRADA")}</div>
            <div style={{ fontWeight: 700, color: C.accent, marginBottom: 12 }}>{ownerFound.nombre}</div>
            <div style={{ fontSize: 13, color: C.textMuted, marginBottom: 8 }}>{tr("Es tu primera vez. Escribe tu nombre para registrarte:")}</div>
            <Input label={tr("Tu nombre")} value={nombre} onChange={setNombre} placeholder={tr("Tu nombre")} />
            <div style={{ marginTop: 12 }}><Btn onClick={doJoin} color={C.warn}>{tr("Unirme como Empleado")}</Btn></div>
          </div>
        )}
      </div>
    </AuthShell>
  );
}

// --- PDF / PRINT FUNCTION ---
// --- GRÁFICA DE DONA (círculo) para los reportes ---
// Se dibuja en un <canvas> y se mete como imagen PNG. Es la única forma de que salga nítida
// en el PDF: html2canvas (el que arma el PDF) no dibuja bien ni los SVG ni los conic-gradient.
function tortaDataURL(items, tam, centroTxt, centroSub) {
  try {
    var size = tam || 440;
    var cv = document.createElement("canvas");
    cv.width = size; cv.height = size;
    var g = cv.getContext("2d");
    var total = items.reduce(function (a, i) { return a + Math.max(0, Number(i.valor) || 0); }, 0);
    if (total <= 0) return null;
    g.fillStyle = "#ffffff"; g.fillRect(0, 0, size, size);
    var cx = size / 2, cy = size / 2, R = size * 0.43, r = size * 0.255;
    var ang = -Math.PI / 2;
    items.forEach(function (it) {
      var v = Math.max(0, Number(it.valor) || 0);
      if (v <= 0) return;
      var paso = v / total * Math.PI * 2;
      g.beginPath(); g.moveTo(cx, cy); g.arc(cx, cy, R, ang, ang + paso); g.closePath();
      g.fillStyle = it.color; g.fill();
      g.lineWidth = size * 0.012; g.strokeStyle = "#ffffff"; g.stroke();
      ang += paso;
    });
    g.beginPath(); g.arc(cx, cy, r, 0, Math.PI * 2); g.fillStyle = "#ffffff"; g.fill();
    if (centroTxt) {
      g.fillStyle = "#16213e"; g.textAlign = "center"; g.textBaseline = "middle";
      g.font = "bold " + Math.round(size * 0.1) + "px Arial, Helvetica, sans-serif";
      g.fillText(String(centroTxt), cx, cy - (centroSub ? size * 0.035 : 0));
      if (centroSub) {
        g.fillStyle = "#7a8699"; g.font = Math.round(size * 0.055) + "px Arial, Helvetica, sans-serif";
        g.fillText(String(centroSub), cx, cy + size * 0.06);
      }
    }
    return cv.toDataURL("image/png");
  } catch (e) { return null; }
}

function imprimirCamada(c, ownerName, simbolo) {
  var S = simbolo || "RD$";
  var esc = function (t) { return String(t == null ? "" : t).replace(/[&<>]/g, function (x) { return { "&": "&amp;", "<": "&lt;", ">": "&gt;" }[x]; }); };
  var dinero = function (v) { return S + " " + Math.round(Number(v) || 0).toLocaleString(); };

  var ganancia = Number(c.ganancia) || 0;
  var inversion = Number(c.inversion) || 0;
  var ingreso = Number(c.ingreso) || 0;
  var totalLibras = Number(c.totalLibras) || 0;
  var totalPollos = Number(c.totalPollos) || 0;
  var pollosVendidos = Number(c.pollosVendidos) || 0;
  var totalMuertos = Math.max(0, totalPollos - pollosVendidos);
  var pctMort = totalPollos > 0 ? (totalMuertos / totalPollos * 100) : 0;
  var lbPorAve = pollosVendidos > 0 ? (totalLibras / pollosVendidos) : 0;
  var margen = ingreso > 0 ? (ganancia / ingreso * 100) : 0;
  var roi = inversion > 0 ? (ganancia / inversion * 100) : 0;
  var costoPorAve = totalPollos > 0 ? (inversion / totalPollos) : 0;
  var costoPorLb = totalLibras > 0 ? (inversion / totalLibras) : 0;
  var precioPromedio = totalLibras > 0 ? (ingreso / totalLibras) : 0;
  var gastos = c.gastos || [], ventas = c.ventas || [], registros = (c.registros || []).filter(function (r) { return r && !r.borrado; });
  var gastoAlimento = gastos.filter(function (g) { return g.categoria === "alimento"; }).reduce(function (a, g) { return a + Number(g.monto || 0); }, 0);
  var lbAlimento = gastoAlimento > 0 ? (gastoAlimento / 1395 * 100) : 0;
  var fcr = lbAlimento > 0 && totalLibras > 0 ? (lbAlimento / totalLibras) : null;
  var bueno = ganancia >= 0;

  // Gastos agrupados por categoría (para la dona)
  var porCat = {};
  gastos.forEach(function (g) {
    var id = g.categoria || "otros";
    porCat[id] = (porCat[id] || 0) + Number(g.monto || 0);
  });
  var cats = Object.keys(porCat).map(function (id) {
    var def = CATEGORIAS_GASTOS.find(function (x) { return x.id === id; }) || { label: id, icono: "📦", color: "#607d8b" };
    return { id: id, label: tr(def.label), icono: def.icono, color: def.color, valor: porCat[id] };
  }).sort(function (a, b) { return b.valor - a.valor; });

  var donaGastos = tortaDataURL(cats, 440, dinero(inversion), tr("inversión"));
  var donaReparto = tortaDataURL(
    bueno ? [{ valor: inversion, color: "#64748B" }, { valor: ganancia, color: "#16A34A" }]
          : [{ valor: ingreso, color: "#64748B" }, { valor: -ganancia, color: "#DC2626" }],
    440, (bueno ? "+" : "−") + Math.abs(Math.round(margen)) + "%", bueno ? tr("de margen") : tr("de pérdida"));

  var html = "<!DOCTYPE html><html lang='es'><head><meta charset='utf-8'><title>" + esc(c.nombre || "") + " · " + esc(tr("Reporte de camada")) + "</title><style>";
  html += "*{box-sizing:border-box}body{font-family:Arial,Helvetica,sans-serif;margin:0;padding:0;color:#16213e;background:#fff}";
  html += ".pg{max-width:740px;margin:0 auto;padding:0 22px 22px}";
  html += ".cab{background:#16213e;color:#fff;padding:22px 22px 18px;margin:0 -22px 18px}";
  html += ".cab .et{font-size:9.5px;letter-spacing:2.4px;text-transform:uppercase;color:#8fa0c0}";
  html += ".cab h1{font-size:25px;margin:5px 0 3px;font-weight:bold}";
  html += ".cab .meta{font-size:11px;color:#b9c6de}";
  html += ".cinta{display:flex;gap:0;margin-top:16px;border-top:1px solid #2b3a63;padding-top:13px}";
  html += ".cinta div{flex:1;border-left:1px solid #2b3a63;padding-left:11px}.cinta div:first-child{border-left:0;padding-left:0}";
  html += ".cinta .l{font-size:8.5px;color:#8fa0c0;text-transform:uppercase;letter-spacing:1.1px}";
  html += ".cinta .v{font-size:16px;font-weight:bold;margin-top:3px}";
  html += ".sec{font-size:11px;font-weight:bold;letter-spacing:1.6px;text-transform:uppercase;color:#D4500A;margin:22px 0 10px;padding-bottom:5px;border-bottom:2px solid #f0e2d4}";
  html += ".kpis{display:flex;flex-wrap:wrap;gap:9px}";
  html += ".kpi{flex:1 1 150px;border:1px solid #e4e8f0;border-left:4px solid #D4500A;border-radius:7px;padding:11px 13px;background:#fbfcfe}";
  html += ".kpi .l{font-size:8.5px;color:#7a8699;text-transform:uppercase;letter-spacing:1.1px}";
  html += ".kpi .v{font-size:20px;font-weight:bold;margin-top:3px}";
  html += ".kpi .n{font-size:9.5px;color:#9aa5b6;margin-top:2px}";
  html += ".verde{color:#16A34A}.rojo{color:#DC2626}.gris{color:#64748B}";
  html += ".kpi.g{border-left-color:#16A34A}.kpi.r{border-left-color:#DC2626}.kpi.b{border-left-color:#2563EB}";
  html += ".dos{display:flex;gap:16px;align-items:flex-start}";
  html += ".dona{width:188px;flex:0 0 188px;text-align:center}.dona img{width:188px;height:188px}";
  html += ".dona .cap{font-size:9px;color:#9aa5b6;margin-top:4px}";
  html += ".ley{flex:1;min-width:0}";
  html += ".leyf{display:flex;align-items:center;gap:7px;padding:5px 0;border-bottom:1px solid #f1f4f9;font-size:11px}";
  html += ".leyf .pt{width:9px;height:9px;border-radius:2px;flex:0 0 9px}";
  html += ".leyf .nm{flex:1;min-width:0}.leyf .mt{font-weight:bold;font-family:monospace}.leyf .pc{width:44px;text-align:right;color:#9aa5b6;font-size:10px}";
  html += ".barf{margin:9px 0}.barf .t{display:flex;justify-content:space-between;font-size:11px;margin-bottom:3px}";
  html += ".barf .t b{font-family:monospace}.barf .p{height:15px;background:#eef1f6;border-radius:4px;overflow:hidden}";
  html += ".barf .p i{display:block;height:100%;border-radius:4px}";
  html += ".graf{display:flex;align-items:flex-end;gap:1px;border-left:1px solid #dfe4ee;border-bottom:1px solid #dfe4ee;padding:0 2px;margin-top:6px}";
  html += ".graf .col{flex:1 1 0;min-width:0;text-align:center}.graf .hueco{height:92px;display:flex;align-items:flex-end;justify-content:center}";
  html += ".graf .barra{width:78%;border-radius:2px 2px 0 0;background:#DC2626}.graf .dia{font-size:7px;color:#9aa5b6;padding-top:2px}";
  html += "table{width:100%;border-collapse:collapse;font-size:10px;margin-top:4px}";
  html += "th{background:#16213e;color:#fff;padding:6px 7px;text-align:left;font-size:9px;letter-spacing:.4px;text-transform:uppercase}";
  html += "td{padding:5px 7px;border-bottom:1px solid #eef1f6}tr:nth-child(even) td{background:#fafbfd}";
  html += ".num{font-family:monospace;text-align:right}";
  html += ".tot{background:#16213e!important;color:#fff;font-weight:bold}.tot td{background:#16213e!important;color:#fff;border:0}";
  html += ".nota{font-size:10.5px;color:#64748B;background:#f7f9fc;border-left:3px solid #D4500A;padding:9px 12px;border-radius:0 6px 6px 0;margin-top:9px;line-height:1.5}";
  html += ".pie{margin-top:26px;border-top:1px solid #e4e8f0;padding-top:11px;font-size:9px;color:#9aa5b6;display:flex;justify-content:space-between}";
  html += "</style></head><body><div class='pg'>";

  // ── Cabecera ──
  html += "<div class='cab'><div class='et'>" + esc(tr("Reporte de camada")) + "</div>";
  html += "<h1>" + esc(c.nombre || "") + "</h1>";
  html += "<div class='meta'>" + esc(ownerName || "") + " &nbsp;·&nbsp; " + esc(c.fechaInicio || "") + " → " + esc(c.fechaCierre || "") +
    (c.diasCiclo ? " &nbsp;·&nbsp; " + c.diasCiclo + " " + esc(tr("días")) : "") + "</div>";
  html += "<div class='cinta'>";
  html += "<div><div class='l'>" + esc(tr("Ingreso")) + "</div><div class='v'>" + dinero(ingreso) + "</div></div>";
  html += "<div><div class='l'>" + esc(tr("Inversión")) + "</div><div class='v'>" + dinero(inversion) + "</div></div>";
  html += "<div><div class='l'>" + esc(bueno ? tr("Ganancia neta") : tr("Pérdida neta")) + "</div><div class='v' style='color:" + (bueno ? "#4ADE80" : "#F87171") + "'>" + dinero(Math.abs(ganancia)) + "</div></div>";
  html += "</div></div>";

  // ── Indicadores ──
  html += "<div class='sec'>" + esc(tr("Indicadores clave")) + "</div><div class='kpis'>";
  html += "<div class='kpi " + (roi >= 0 ? "g" : "r") + "'><div class='l'>" + esc(tr("Retorno (ROI)")) + "</div><div class='v " + (roi >= 0 ? "verde" : "rojo") + "'>" + (roi >= 0 ? "+" : "") + roi.toFixed(1) + "%</div><div class='n'>" + esc(tr("sobre la inversión")) + "</div></div>";
  html += "<div class='kpi " + (margen >= 0 ? "g" : "r") + "'><div class='l'>" + esc(tr("Margen")) + "</div><div class='v " + (margen >= 0 ? "verde" : "rojo") + "'>" + (margen >= 0 ? "+" : "") + margen.toFixed(1) + "%</div><div class='n'>" + esc(tr("de cada peso vendido")) + "</div></div>";
  html += "<div class='kpi b'><div class='l'>" + esc(tr("Costo por libra")) + "</div><div class='v'>" + S + " " + costoPorLb.toFixed(2) + "</div><div class='n'>" + esc(tr("se vendió a")) + " " + S + " " + precioPromedio.toFixed(2) + "</div></div>";
  html += "<div class='kpi'><div class='l'>FCR</div><div class='v'>" + (fcr ? fcr.toFixed(2) : "—") + "</div><div class='n'>" + esc(tr("lb alimento por lb carne")) + "</div></div>";
  html += "<div class='kpi " + (pctMort <= 5 ? "g" : "r") + "'><div class='l'>" + esc(tr("Mortalidad")) + "</div><div class='v " + (pctMort <= 5 ? "verde" : "rojo") + "'>" + pctMort.toFixed(1) + "%</div><div class='n'>" + totalMuertos.toLocaleString() + " " + esc(tr("de")) + " " + totalPollos.toLocaleString() + "</div></div>";
  html += "<div class='kpi'><div class='l'>" + esc(tr("Libras por ave")) + "</div><div class='v'>" + lbPorAve.toFixed(2) + " lb</div><div class='n'>" + totalLibras.toFixed(0) + " lb " + esc(tr("en total")) + "</div></div>";
  html += "</div>";

  // ── Resultado financiero: barras + dona del reparto ──
  html += "<div class='sec'>" + esc(tr("Resultado financiero")) + "</div><div class='dos'>";
  html += "<div class='ley'>";
  var tope = Math.max(ingreso, inversion, 1);
  function barra(nombre, valor, color) {
    return "<div class='barf'><div class='t'><span>" + esc(nombre) + "</span><b>" + dinero(valor) + "</b></div>" +
      "<div class='p'><i style='width:" + Math.max(1, Math.round(Math.abs(valor) / tope * 100)) + "%;background:" + color + "'></i></div></div>";
  }
  html += barra(tr("Ingreso por ventas"), ingreso, "#2563EB");
  html += barra(tr("Inversión (gastos)"), inversion, "#64748B");
  html += barra(bueno ? tr("Ganancia neta") : tr("Pérdida neta"), Math.abs(ganancia), bueno ? "#16A34A" : "#DC2626");
  html += "<div class='nota'>" + esc(tr("Por cada")) + " " + S + " 100 " + esc(tr("vendidos, quedaron")) + " <b>" + S + " " + Math.abs(margen).toFixed(0) + "</b> " +
    esc(bueno ? tr("de ganancia.") : tr("de pérdida.")) + " " + esc(tr("Cada ave costó")) + " " + S + " " + costoPorAve.toFixed(2) + ".</div>";
  html += "</div>";
  if (donaReparto) html += "<div class='dona'><img src='" + donaReparto + "' alt=''><div class='cap'>" + esc(bueno ? tr("Inversión vs ganancia") : tr("Ingreso vs pérdida")) + "</div></div>";
  html += "</div>";

  // ── Gastos por categoría: dona + leyenda ──
  if (cats.length) {
    html += "<div class='sec'>" + esc(tr("En qué se fue el dinero")) + "</div><div class='dos'>";
    if (donaGastos) html += "<div class='dona'><img src='" + donaGastos + "' alt=''><div class='cap'>" + esc(tr("Gastos por categoría")) + "</div></div>";
    html += "<div class='ley'>";
    cats.forEach(function (x) {
      var pc = inversion > 0 ? (x.valor / inversion * 100) : 0;
      html += "<div class='leyf'><span class='pt' style='background:" + x.color + "'></span>" +
        "<span class='nm'>" + x.icono + " " + esc(x.label) + "</span>" +
        "<span class='mt'>" + dinero(x.valor) + "</span><span class='pc'>" + pc.toFixed(0) + "%</span></div>";
    });
    html += "</div></div>";
  }

  // ── Bajas por día ──
  if (registros.length) {
    var diaMax = registros.reduce(function (m, r) { return Math.max(m, Number(r.dia) || 0); }, 0);
    var porDia = [];
    for (var d = 0; d <= diaMax; d++) porDia.push(0);
    registros.forEach(function (r) { var d = Math.max(0, Math.min(diaMax, Number(r.dia) || 0)); porDia[d] += Number(r.mortalidad || 0); });
    var maxDia = Math.max.apply(null, porDia.concat([1]));
    var peor = porDia.indexOf(Math.max.apply(null, porDia));
    html += "<div class='sec'>" + esc(tr("Bajas por día")) + "</div><div class='graf'>";
    porDia.forEach(function (v, idx) {
      html += "<div class='col'><div class='hueco'><div class='barra' style='height:" + (v > 0 ? Math.max(2, Math.round(v / maxDia * 92)) : 0) + "px'></div></div>" +
        "<div class='dia'>" + (idx % 5 === 0 || idx === porDia.length - 1 ? idx : "") + "</div></div>";
    });
    html += "</div>";
    if (maxDia > 0) html += "<div class='nota'>" + esc(tr("El día de más bajas fue el día")) + " <b>" + peor + "</b> " + esc(tr("con")) + " <b>" + maxDia + "</b> " + esc(tr("pollos")) + ".</div>";
  }

  // ── Ventas ──
  if (ventas.length) {
    html += "<div class='sec'>" + esc(tr("Ventas")) + " — " + ventas.length + " " + esc(tr("lotes")) + "</div>";
    html += "<table><thead><tr><th>#</th><th>" + esc(tr("Fecha")) + "</th><th>" + esc(tr("Comprador")) + "</th><th class='num'>" + esc(tr("Pollos")) + "</th><th class='num'>" + esc(tr("Libras")) + "</th><th class='num'>" + esc(tr("lb/ave")) + "</th><th class='num'>" + S + "/lb</th><th class='num'>" + esc(tr("Total")) + "</th></tr></thead><tbody>";
    ventas.forEach(function (v, i) {
      var sub = Number(v.libras || 0) * Number(v.precioLb || 0);
      var lba = Number(v.pollos) > 0 ? (Number(v.libras) / Number(v.pollos)).toFixed(2) : "—";
      html += "<tr><td>" + (i + 1) + "</td><td>" + esc(v.fecha || "") + "</td><td>" + esc(v.comprador || "—") + "</td>" +
        "<td class='num'>" + Number(v.pollos || 0).toLocaleString() + "</td><td class='num'>" + Number(v.libras || 0).toLocaleString() + "</td>" +
        "<td class='num'>" + lba + "</td><td class='num'>" + Number(v.precioLb || 0).toFixed(2) + "</td><td class='num'>" + dinero(sub) + "</td></tr>";
    });
    html += "<tr class='tot'><td colspan='3'>" + esc(tr("Total")) + "</td><td class='num'>" + pollosVendidos.toLocaleString() + "</td><td class='num'>" + totalLibras.toLocaleString() + "</td><td class='num'>" + lbPorAve.toFixed(2) + "</td><td class='num'>" + precioPromedio.toFixed(2) + "</td><td class='num'>" + dinero(ingreso) + "</td></tr>";
    html += "</tbody></table>";
  }

  // ── Gastos detallados ──
  if (gastos.length) {
    html += "<div class='sec'>" + esc(tr("Detalle de gastos")) + "</div>";
    html += "<table><thead><tr><th>" + esc(tr("Fecha")) + "</th><th>" + esc(tr("Categoría")) + "</th><th>" + esc(tr("Descripción")) + "</th><th class='num'>" + esc(tr("Monto")) + "</th></tr></thead><tbody>";
    gastos.slice().sort(function (a, b) { return String(a.fecha || "").localeCompare(String(b.fecha || "")); }).forEach(function (g) {
      var def = CATEGORIAS_GASTOS.find(function (x) { return x.id === g.categoria; }) || { label: g.categoria || "—", icono: "📦" };
      html += "<tr><td>" + esc(g.fecha || "") + "</td><td>" + def.icono + " " + esc(tr(def.label)) + "</td><td>" + esc(g.descripcion || "") + "</td><td class='num'>" + dinero(g.monto) + "</td></tr>";
    });
    html += "<tr class='tot'><td colspan='3'>" + esc(tr("Inversión total")) + "</td><td class='num'>" + dinero(inversion) + "</td></tr>";
    html += "</tbody></table>";
  }

  // ── Registros diarios ──
  if (registros.length) {
    html += "<div class='sec'>" + esc(tr("Registros diarios")) + " — " + registros.length + "</div>";
    html += "<table><thead><tr><th>" + esc(tr("Día")) + "</th><th class='num'>" + esc(tr("Vivos")) + "</th><th class='num'>" + esc(tr("Bajas")) + "</th><th class='num'>" + esc(tr("Peso kg")) + "</th><th>" + esc(tr("Alimento")) + "</th><th>" + esc(tr("Suplemento")) + "</th><th>" + esc(tr("Cama")) + "</th><th>" + esc(tr("Heces")) + "</th></tr></thead><tbody>";
    registros.forEach(function (r) {
      var faT = faseAlimentoInfo(r.alimentoFase);
      var aliT = Number(r.alimentoSacos) > 0 ? Number(r.alimentoSacos) + (faT ? " " + tr(faT.nombre) : "") : "—";
      html += "<tr><td>" + (r.dia != null ? r.dia : "") + "</td><td class='num'>" + Number(r.vivos || 0).toLocaleString() + "</td>" +
        "<td class='num'" + (Number(r.mortalidad) > 0 ? " style='color:#DC2626;font-weight:bold'" : "") + ">" + (Number(r.mortalidad) || "") + "</td>" +
        "<td class='num'>" + (r.pesoMuestra || "—") + "</td><td>" + esc(aliT) + "</td><td>" + esc(r.suplemento || "—") + "</td>" +
        "<td>" + esc(r.estadoCama || "—") + "</td><td>" + esc(r.estadoHeces || "—") + "</td></tr>";
    });
    html += "</tbody></table>";
  }

  html += "<div class='pie'><span>Pollo IA · " + esc(c.nombre || "") + "</span><span>" + esc(tr("Generado")) + " " + new Date().toLocaleDateString("es-DO") + "</span></div>";
  html += "</div></body></html>";

  compartirReportePDF(html, "Reporte-" + (c.nombre || "camada"));
}

// --- IMPRIMIR / GUARDAR PDF: inspección de supervisor ---
function imprimirInspeccionSupervisor(insp, camada) {
  var OP = { A: { l: "Correcto", c: "#16A34A" }, B: { l: "Atención", c: "#EAB308" }, C: { l: "Crítico", c: "#DC2626" } };
  var estColor = insp.pct >= 90 ? "#16A34A" : insp.pct >= 70 ? "#EAB308" : "#DC2626";
  var fecha = insp.fecha ? new Date(insp.fecha).toLocaleDateString("es-DO") : "";
  var resp = insp.respuestas || {};
  var html = "<!DOCTYPE html><html lang='es'><head><meta charset='utf-8'><title>Inspección Supervisor - " + (camada.nombre || "") + "</title>";
  html += "<style>body{font-family:Arial,sans-serif;padding:24px;max-width:700px;margin:0 auto;color:#1A0A00}";
  html += "h1{color:#D4500A;font-size:22px;margin-bottom:4px}.sub{color:#6B4A28;font-size:12px;margin-bottom:16px}";
  html += ".score{text-align:center;border:2px solid " + estColor + ";border-radius:12px;padding:14px;margin-bottom:20px}";
  html += ".pct{font-size:40px;font-weight:800;color:" + estColor + "}.est{font-size:15px;font-weight:700;color:" + estColor + "}";
  html += ".sec{margin-bottom:16px}.sec-t{font-size:13px;font-weight:700;color:#D4500A;border-bottom:2px solid #D4500A33;padding-bottom:4px;margin-bottom:8px}";
  html += ".q{display:flex;justify-content:space-between;align-items:center;padding:5px 0;border-bottom:1px solid #F0E8E0;font-size:12px}.badge{font-weight:700;padding:2px 10px;border-radius:6px;color:#fff;font-size:11px;white-space:nowrap}";
  html += ".obs{background:#F9F5F0;border:1px solid #E0D5C5;border-radius:8px;padding:12px;font-size:12px;margin-top:6px}";
  html += ".foot{margin-top:24px;font-size:10px;color:#A08060;text-align:center;border-top:1px solid #E0D5C5;padding-top:12px}</style></head><body>";
  html += "<h1>&#128269; Inspeccion de Supervisor</h1>";
  html += "<div class='sub'>" + (camada.nombre || "") + " &middot; Dia " + (insp.edadLote != null ? insp.edadLote : "") + " &middot; " + fecha + "<br>Supervisor: " + (insp.supervisor || "") + (insp.granja ? " &middot; Granja: " + insp.granja : "") + "</div>";
  html += "<div class='score'><div class='pct'>" + insp.pct + "%</div><div class='est'>" + (insp.estado || "") + "</div><div style='font-size:11px;color:#6B4A28'>Puntaje: " + insp.puntaje + (insp.rojas ? " &middot; " + insp.rojas + " criticas" : "") + "</div></div>";
  if ((insp.conteos && insp.conteos.length) || (insp.conteoReferencia != null && insp.conteoReferencia !== "")) {
    html += "<div class='sec'><div class='q'><span>&#128290; Conteo de pollos (referencia)</span><span style='font-weight:800;font-size:16px'>" + (insp.conteoReferencia != null && insp.conteoReferencia !== "" ? Number(insp.conteoReferencia).toLocaleString() : "") + "</span></div>";
    (insp.conteos || []).forEach(function(cc) {
      html += "<div class='q'><span style='padding-left:14px'>&#128020; " + (cc.nombre || "") + "</span><span style='font-weight:700'>" + Number(cc.conteo || 0).toLocaleString() + "</span></div>";
    });
    html += "</div>";
  }
  SUPERVISOR_SECCIONES.forEach(function (sec) {
    html += "<div class='sec'><div class='sec-t'>" + sec.titulo + "</div>";
    sec.preguntas.forEach(function (preg, i) {
      var v = resp[sec.id + "_" + i];
      var op = OP[v];
      html += "<div class='q'><span>" + preg + "</span>" + (op ? "<span class='badge' style='background:" + op.c + "'>" + v + " &middot; " + op.l + "</span>" : "<span style='color:#bbb'>&mdash;</span>") + "</div>";
    });
    html += "</div>";
  });
  if (insp.observaciones) html += "<div class='sec'><div class='sec-t'>Observaciones</div><div class='obs'>" + String(insp.observaciones).replace(/</g, "&lt;") + "</div></div>";
  html += "<div class='foot'>Pollo Express IA &middot; Generado " + new Date().toLocaleDateString("es-DO") + "</div></body></html>";
  compartirReportePDF(html, "Inspeccion-" + (camada.nombre || "") + "-Dia-" + (insp.edadLote != null ? insp.edadLote : ""));
}

// --- IMPRIMIR / GUARDAR PDF: un registro diario individual ---
function imprimirRegistroDiario(reg, camada) {
  var fecha = reg.fecha || "";
  var plb = reg.pesoMuestra ? (Number(reg.pesoMuestra) * 2.205).toFixed(3) : null;
  var refTxt = (reg.conteoReferencia != null && reg.conteoReferencia !== "") ? Number(reg.conteoReferencia).toLocaleString() : null;
  var html = "<!DOCTYPE html><html lang='es'><head><meta charset='utf-8'><title>Registro Diario - " + (camada.nombre || "") + " - Dia " + (reg.dia != null ? reg.dia : "") + "</title>";
  html += "<style>body{font-family:Arial,sans-serif;padding:24px;max-width:600px;margin:0 auto;color:#1A0A00}";
  html += "h1{color:#D4500A;font-size:22px;margin-bottom:4px}.sub{color:#6B4A28;font-size:12px;margin-bottom:18px}";
  html += ".grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:16px}";
  html += ".card{border:1px solid #E0D5C5;border-radius:10px;padding:12px}";
  html += ".lbl{font-size:10px;color:#A08060;text-transform:uppercase;letter-spacing:1px}.val{font-size:18px;font-weight:800;color:#1A0A00;margin-top:2px}";
  html += ".obs{background:#F9F5F0;border:1px solid #E0D5C5;border-radius:8px;padding:12px;font-size:13px;margin-top:8px}";
  html += ".foot{margin-top:24px;font-size:10px;color:#A08060;text-align:center;border-top:1px solid #E0D5C5;padding-top:12px}";
  html += "img.foto{max-width:100%;border-radius:8px;margin-top:8px}</style></head><body>";
  html += "<h1>&#128203; Registro Diario</h1>";
  html += "<div class='sub'>" + (camada.nombre || "") + " &middot; Dia " + (reg.dia != null ? reg.dia : "") + " &middot; " + fecha + (reg.registradoPor ? "<br>Registrado por: " + reg.registradoPor : "") + "</div>";
  html += "<div class='grid'>";
  html += "<div class='card'><div class='lbl'>Pollos vivos</div><div class='val'>" + (reg.vivos != null ? Number(reg.vivos).toLocaleString() : "—") + "</div></div>";
  html += "<div class='card'><div class='lbl'>Bajas (mortalidad)</div><div class='val'>" + (reg.mortalidad != null ? reg.mortalidad : "—") + "</div></div>";
  html += "<div class='card'><div class='lbl'>Peso muestra</div><div class='val'>" + (reg.pesoMuestra ? reg.pesoMuestra + " kg" : "—") + (plb ? " <span style='font-size:11px;color:#6B4A28'>(" + plb + " lb)</span>" : "") + "</div></div>";
  html += "<div class='card'><div class='lbl'>Suplemento</div><div class='val' style='font-size:14px'>" + (reg.suplemento || "—") + "</div></div>";
  var faImp = faseAlimentoInfo(reg.alimentoFase);
  var sacosImp = Number(reg.alimentoSacos) > 0
    ? Number(reg.alimentoSacos) + (Number(reg.alimentoSacos) === 1 ? " saco" : " sacos") + (faImp ? " de " + faImp.nombre : "")
    : "—";
  html += "<div class='card'><div class='lbl'>Alimento gastado</div><div class='val' style='font-size:14px'>" + sacosImp + "</div></div>";
  html += "<div class='card'><div class='lbl'>Estado de cama</div><div class='val' style='font-size:14px'>" + (reg.estadoCama || "—") + "</div></div>";
  html += "<div class='card'><div class='lbl'>Heces</div><div class='val' style='font-size:14px'>" + (reg.estadoHeces || "—") + "</div></div>";
  html += "</div>";
  if (reg.notas) html += "<div class='obs'><b>Notas:</b> " + String(reg.notas).replace(/</g, "&lt;") + "</div>";
  if (reg.fotoHeces) html += "<img class='foto' src='" + reg.fotoHeces + "' alt='foto' />";
  html += "<div class='foot'>Pollo Express IA &middot; Generado " + new Date().toLocaleDateString("es-DO") + "</div></body></html>";
  compartirReportePDF(html, "Registro-" + (camada.nombre || "") + "-Dia-" + (reg.dia != null ? reg.dia : ""));
}

// --- GRÁFICAS EN CANVAS para los reportes ---
// Todo se dibuja en un <canvas> y se mete como imagen PNG: es la única forma de que salga
// nítido en el PDF, porque html2canvas (el que arma el PDF) no dibuja bien SVG ni gradientes.
// Se dibuja al doble de tamaño para que la letra se vea limpia al imprimir.
function _lienzo(ancho, alto) {
  var cv = document.createElement("canvas");
  cv.width = ancho * 2; cv.height = alto * 2;
  var g = cv.getContext("2d");
  g.scale(2, 2);
  g.fillStyle = "#ffffff"; g.fillRect(0, 0, ancho, alto);
  return { cv: cv, g: g };
}
function _caja(g, x, y, w, h, r) {
  if (g.roundRect) { g.beginPath(); g.roundRect(x, y, w, h, r); return; }
  g.beginPath(); g.rect(x, y, w, h);
}
// Rejilla + eje Y, común a barras y línea. Devuelve el área de dibujo.
function _maxLindo(max, entero) { if (!entero) return max; return Math.max(1, Math.ceil(max / 3)) * 3; }
function _rejilla(g, W, H, max, fmt) {
  var mi = 42, md = 12, mt = 14, mb = 26;
  var aw = W - mi - md, ah = H - mt - mb;
  g.font = "11px Arial, Helvetica, sans-serif"; g.textAlign = "right"; g.textBaseline = "middle";
  for (var i = 0; i <= 3; i++) {
    var y = mt + ah - (ah * i / 3);
    g.strokeStyle = i === 0 ? "#cfd6e4" : "#eef1f6"; g.lineWidth = 1;
    g.beginPath(); g.moveTo(mi, y + 0.5); g.lineTo(mi + aw, y + 0.5); g.stroke();
    g.fillStyle = "#8b96a8";
    g.fillText(fmt ? fmt(max * i / 3) : Math.round(max * i / 3), mi - 7, y);
  }
  return { mi: mi, mt: mt, aw: aw, ah: ah };
}
function _ejeX(g, a, etiquetas, cada) {
  var paso = a.aw / etiquetas.length;
  g.font = "11px Arial, Helvetica, sans-serif"; g.fillStyle = "#8b96a8"; g.textAlign = "center"; g.textBaseline = "top";
  var ultimo = etiquetas.length - 1;
  etiquetas.forEach(function (t, i) {
    var dibujar = (i % cada === 0 && i !== ultimo) || (i === ultimo && ultimo % cada >= cada * 0.6);
    if (dibujar) g.fillText(t, a.mi + paso * i + paso / 2, a.mt + a.ah + 7);
  });
}
// Barras verticales. datos: [{x, y, color}]
function barrasDataURL(datos, opc) {
  try {
    opc = opc || {};
    var W = opc.ancho || 710, H = opc.alto || 170;
    var L = _lienzo(W, H), g = L.g;
    var max = _maxLindo(Math.max.apply(null, datos.map(function (d) { return Number(d.y) || 0; }).concat([opc.minMax || 1])), !opc.fmt);
    var a = _rejilla(g, W, H, max, opc.fmt);
    var paso = a.aw / datos.length, bw = Math.max(2.5, paso * 0.66);
    datos.forEach(function (d, i) {
      var v = Number(d.y) || 0; if (v <= 0) return;
      var h = Math.max(1.5, v / max * a.ah), x = a.mi + paso * i + (paso - bw) / 2, y = a.mt + a.ah - h;
      g.fillStyle = d.color || opc.color || "#DC2626";
      _caja(g, x, y, bw, h, [3, 3, 0, 0]); g.fill();
    });
    _ejeX(g, a, datos.map(function (d) { return d.x; }), opc.cada || 5);
    return L.cv.toDataURL("image/png");
  } catch (e) { return null; }
}
// Línea con área debajo. datos: [{x, y}]
function lineaDataURL(datos, opc) {
  try {
    opc = opc || {};
    var W = opc.ancho || 430, H = opc.alto || 170;
    var L = _lienzo(W, H), g = L.g;
    var max = _maxLindo(Math.max.apply(null, datos.map(function (d) { return Number(d.y) || 0; }).concat([opc.minMax || 1])), !opc.fmt);
    var a = _rejilla(g, W, H, max, opc.fmt);
    var color = opc.color || "#2563EB";
    var px = function (i) { return a.mi + (datos.length > 1 ? a.aw * i / (datos.length - 1) : a.aw / 2); };
    var py = function (v) { return a.mt + a.ah - (Number(v) || 0) / max * a.ah; };
    // área
    g.beginPath(); g.moveTo(px(0), a.mt + a.ah);
    datos.forEach(function (d, i) { g.lineTo(px(i), py(d.y)); });
    g.lineTo(px(datos.length - 1), a.mt + a.ah); g.closePath();
    g.fillStyle = (opc.relleno || color) + "26"; g.fill();
    // línea
    g.beginPath();
    datos.forEach(function (d, i) { if (i === 0) g.moveTo(px(i), py(d.y)); else g.lineTo(px(i), py(d.y)); });
    g.strokeStyle = color; g.lineWidth = 2.4; g.lineJoin = "round"; g.stroke();
    // punto final destacado
    if (datos.length) {
      var ult = datos[datos.length - 1];
      g.beginPath(); g.arc(px(datos.length - 1), py(ult.y), 4, 0, Math.PI * 2);
      g.fillStyle = color; g.fill();
      g.strokeStyle = "#fff"; g.lineWidth = 2; g.stroke();
    }
    _ejeX(g, a, datos.map(function (d) { return d.x; }), opc.cada || 5);
    return L.cv.toDataURL("image/png");
  } catch (e) { return null; }
}

// --- REPORTE DEL CICLO: todo en UNA hoja, en forma de tablero ---
// Pensado para que el granjero vea de un golpe cómo se portó la camada: cuándo se le
// murieron más pollos, cuándo empezaron a comer fuerte, cómo subió el peso y qué vitamina
// se dio cada día. El detalle día por día está en el reporte de Historial de Camadas.
function imprimirReporteCiclo(camada, registros) {
  var esc = function (t) { return String(t == null ? "" : t).replace(/[&<>]/g, function (c) { return { "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]; }); };
  var regs = (registros || []).filter(function (r) { return r && !r.borrado; });
  if (!regs.length) { alert(tr("Todavía no hay registros para el reporte.")); return; }

  var total = Number(camada.totalPollos) || 0;
  var diaMax = regs.reduce(function (m, r) { return Math.max(m, Number(r.dia) || 0); }, 0);

  var dias = [];
  for (var d = 0; d <= diaMax; d++) dias.push({ dia: d, bajas: 0, sacos: 0, fases: {}, peso: null, sup: [], fecha: "" });
  regs.forEach(function (r) {
    var d = Math.max(0, Math.min(diaMax, Number(r.dia) || 0)), x = dias[d];
    if (!x.fecha && r.fecha) x.fecha = r.fecha;
    x.bajas += Number(r.mortalidad || 0);
    if (Number(r.alimentoSacos) > 0) {
      x.sacos += Number(r.alimentoSacos);
      var fa = faseAlimentoInfo(r.alimentoFase);
      if (fa) x.fases[fa.nombre] = (x.fases[fa.nombre] || 0) + Number(r.alimentoSacos);
    }
    if (Number(r.pesoMuestra) > 0) x.peso = Number(r.pesoMuestra);
    if (r.suplemento) x.sup.push(String(r.suplemento));
  });
  var acum = 0, sacosAcum = 0;
  dias.forEach(function (x) { acum += x.bajas; sacosAcum += x.sacos; x.acum = acum; x.vivos = Math.max(0, total - acum); });

  var muertes = acum, sacosTotal = sacosAcum;
  var pctMort = total > 0 ? muertes / total * 100 : 0;
  var vivos = total - muertes;
  var pesoFinal = null, diaPesoFinal = 0;
  dias.forEach(function (x) { if (x.peso) { pesoFinal = x.peso; diaPesoFinal = x.dia; } });
  var peorIdx = 0;
  dias.forEach(function (x, i) { if (x.bajas > dias[peorIdx].bajas) peorIdx = i; });
  var colorFase = { "Preinicio": "#FF8A3D", "Iniciador": "#F5C200", "Crecimiento": "#4caf50", "Engorde": "#2196f3" };

  // Sacos por fase (para la dona)
  var porFase = {};
  dias.forEach(function (x) { Object.keys(x.fases).forEach(function (k) { porFase[k] = (porFase[k] || 0) + x.fases[k]; }); });
  var fasesLista = Object.keys(porFase).map(function (k) { return { label: k, valor: porFase[k], color: colorFase[k] || "#F5C200" }; });

  // ── Las cinco gráficas ──
  var gMort = lineaDataURL(dias.map(function (x) { return { x: x.dia, y: total > 0 ? Number((x.acum / total * 100).toFixed(2)) : 0 }; }),
    { ancho: 420, alto: 114, color: "#DC2626", fmt: function (v) { return v.toFixed(1) + "%"; }, cada: 7 });
  var gLote = tortaDataURL([{ valor: vivos, color: "#16A34A" }, { valor: muertes, color: "#DC2626" }], 300,
    pctMort.toFixed(1) + "%", tr("mortalidad"));
  var gBajas = barrasDataURL(dias.map(function (x) { return { x: x.dia, y: x.bajas }; }),
    { ancho: 710, alto: 108, color: "#DC2626", cada: 5 });
  var gAlim = barrasDataURL(dias.map(function (x) {
    var k = Object.keys(x.fases)[0];
    return { x: x.dia, y: x.sacos, color: k ? (colorFase[k] || "#F5C200") : "#F5C200" };
  }), { ancho: 420, alto: 110, cada: 7 });
  var gFases = fasesLista.length ? tortaDataURL(fasesLista, 300, String(sacosTotal), tr("sacos")) : null;
  var conPeso = dias.filter(function (x) { return x.peso; });
  var gPeso = conPeso.length > 1 ? lineaDataURL(conPeso.map(function (x) { return { x: x.dia, y: x.peso }; }),
    { ancho: 710, alto: 92, color: "#2563EB", fmt: function (v) { return v.toFixed(2); }, cada: Math.max(1, Math.round(conPeso.length / 8)) }) : null;

  var conSup = dias.filter(function (x) { return x.sup.length; });

  var html = "<!DOCTYPE html><html lang='es'><head><meta charset='utf-8'><title>" + esc(tr("Reporte del ciclo")) + " · " + esc(camada.nombre || "") + "</title><style>";
  html += "*{box-sizing:border-box}body{font-family:Arial,Helvetica,sans-serif;margin:0;color:#16213e;background:#fff}";
  html += ".pg{width:780px;margin:0 auto;padding:0 22px 10px}";
  html += ".cab{background:#16213e;color:#fff;padding:12px 22px 11px;margin:0 -22px 11px}";
  html += ".cab .et{font-size:10px;letter-spacing:2.6px;text-transform:uppercase;color:#8fa0c0}";
  html += ".cab h1{font-size:23px;margin:4px 0 2px;font-weight:bold}";
  html += ".cab .meta{font-size:12px;color:#b9c6de}";
  html += ".kpis{display:flex;gap:8px;margin-top:10px}";
  html += ".kpi{flex:1;background:#1e2d4f;border:1px solid #2b3a63;border-radius:8px;padding:6px 9px}";
  html += ".kpi .l{font-size:9px;color:#8fa0c0;text-transform:uppercase;letter-spacing:1px}";
  html += ".kpi .v{font-size:19px;font-weight:bold;margin-top:2px}";
  html += ".fila{display:flex;gap:12px;margin-bottom:8px}";
  html += ".tar{border:1px solid #e4e8f0;border-radius:10px;padding:8px 11px 6px;background:#fff}";
  html += ".tar .tt{font-size:12.5px;font-weight:bold;color:#16213e;margin-bottom:2px}";
  html += ".tar .ts{font-size:10.5px;color:#8b96a8;margin-bottom:4px}";
  html += ".tar img{display:block;width:100%}";
  html += ".dona{width:238px;flex:0 0 238px;text-align:center}.dona img{width:122px;height:122px;margin:2px auto 0}";
  html += ".leyf{display:flex;align-items:center;gap:6px;font-size:11.5px;padding:2px 0}";
  html += ".leyf .pt{width:10px;height:10px;border-radius:3px;flex:0 0 10px}.leyf .nm{flex:1;text-align:left}.leyf b{font-family:monospace}";
  html += ".chips{display:flex;flex-wrap:wrap;gap:4px;margin-top:3px}";
  html += ".chip{border:1px solid #e4e8f0;border-left:3px solid #9c27b0;border-radius:5px;padding:3px 7px;font-size:10.5px;background:#fcfbfe}";
  html += ".chip b{color:#6A1B9A}";
  html += ".res{display:flex;gap:8px;margin-bottom:8px}";
  html += ".res div{flex:1;font-size:11.5px;line-height:1.4;background:#f7f9fc;border-left:3px solid #D4500A;border-radius:0 7px 7px 0;padding:6px 9px}";
  html += ".pie{border-top:1px solid #e4e8f0;padding-top:8px;font-size:10px;color:#9aa5b6;display:flex;justify-content:space-between}";
  html += "</style></head><body><div class='pg'>";

  // Cabecera con los números grandes
  html += "<div class='cab'><div class='et'>" + esc(tr("Reporte del ciclo")) + "</div>";
  html += "<h1>" + esc(camada.nombre || "") + "</h1>";
  html += "<div class='meta'>" + esc(camada.raza || "Ross 308") + " &nbsp;·&nbsp; " + esc(tr("Día")) + " 0 – " + diaMax +
    (camada.fechaInicio ? " &nbsp;·&nbsp; " + esc(camada.fechaInicio) : "") + "</div>";
  html += "<div class='kpis'>";
  html += "<div class='kpi'><div class='l'>" + esc(tr("Recibidos")) + "</div><div class='v'>" + total.toLocaleString() + "</div></div>";
  html += "<div class='kpi'><div class='l'>" + esc(tr("Vivos")) + "</div><div class='v' style='color:#4ADE80'>" + vivos.toLocaleString() + "</div></div>";
  html += "<div class='kpi'><div class='l'>" + esc(tr("Bajas")) + "</div><div class='v' style='color:#F87171'>" + muertes.toLocaleString() + "</div></div>";
  html += "<div class='kpi'><div class='l'>" + esc(tr("Mortalidad")) + "</div><div class='v'>" + pctMort.toFixed(1) + "%</div></div>";
  html += "<div class='kpi'><div class='l'>" + esc(tr("Sacos")) + "</div><div class='v'>" + sacosTotal + "</div></div>";
  html += "<div class='kpi'><div class='l'>" + esc(tr("Último peso")) + "</div><div class='v'>" + (pesoFinal ? pesoFinal + " kg" : "—") + "</div></div>";
  html += "</div></div>";

  // Fila 1: mortalidad acumulada + dona del lote
  html += "<div class='fila'>";
  html += "<div class='tar' style='flex:1'><div class='tt'>" + esc(tr("Cómo subió la mortalidad")) + "</div><div class='ts'>" + esc(tr("% acumulado del lote, día por día")) + "</div>";
  if (gMort) html += "<img src='" + gMort + "' alt=''>";
  html += "</div>";
  html += "<div class='tar dona'><div class='tt'>" + esc(tr("Cómo terminó el lote")) + "</div>";
  if (gLote) html += "<img src='" + gLote + "' alt=''>";
  html += "<div class='leyf'><span class='pt' style='background:#16A34A'></span><span class='nm'>" + esc(tr("Vivos")) + "</span><b>" + vivos.toLocaleString() + "</b></div>";
  html += "<div class='leyf'><span class='pt' style='background:#DC2626'></span><span class='nm'>" + esc(tr("Bajas")) + "</span><b>" + muertes.toLocaleString() + "</b></div>";
  html += "</div></div>";

  // Fila 2: bajas por día
  html += "<div class='fila'><div class='tar' style='flex:1'><div class='tt'>" + esc(tr("Bajas por día")) + "</div><div class='ts'>" +
    esc(tr("Cada barra es un día del ciclo")) + "</div>";
  if (gBajas) html += "<img src='" + gBajas + "' alt=''>";
  html += "</div></div>";

  // Fila 3: alimento por día + dona por fase
  html += "<div class='fila'>";
  html += "<div class='tar' style='flex:1'><div class='tt'>" + esc(tr("Sacos de alimento por día")) + "</div><div class='ts'>" + esc(tr("El color es la fase del alimento")) + "</div>";
  if (gAlim) html += "<img src='" + gAlim + "' alt=''>";
  html += "</div>";
  html += "<div class='tar dona'><div class='tt'>" + esc(tr("Alimento por fase")) + "</div>";
  if (gFases) {
    html += "<img src='" + gFases + "' alt=''>";
    fasesLista.forEach(function (x) {
      html += "<div class='leyf'><span class='pt' style='background:" + x.color + "'></span><span class='nm'>" + esc(tr(x.label)) + "</span><b>" + x.valor + "</b></div>";
    });
  } else {
    html += "<div class='ts' style='padding:40px 0'>" + esc(tr("Sin alimento registrado")) + "</div>";
  }
  html += "</div></div>";

  // Fila 4: curva de peso
  if (gPeso) {
    html += "<div class='fila'><div class='tar' style='flex:1'><div class='tt'>" + esc(tr("Cómo fue subiendo el peso")) + "</div><div class='ts'>" +
      esc(tr("Kilos por ave en cada pesaje")) + "</div><img src='" + gPeso + "' alt=''></div></div>";
  }

  // Vitaminas
  if (conSup.length) {
    var porProducto = {};
    conSup.forEach(function (x) {
      x.sup.forEach(function (p) { (porProducto[p] = porProducto[p] || []).push(x.dia); });
    });
    var productos = Object.keys(porProducto).sort(function (a, b) { return porProducto[a][0] - porProducto[b][0]; });
    html += "<div class='fila'><div class='tar' style='flex:1'><div class='tt'>&#128138; " + esc(tr("Vitaminas y suplementos")) + "</div><div class='ts'>" +
      esc(tr("Qué se dio y en qué día")) + "</div><div class='chips'>";
    productos.forEach(function (p) {
      html += "<span class='chip'><b>" + esc(p) + "</b> · " + esc(porProducto[p].length === 1 ? tr("día") : tr("días")) + " " + porProducto[p].join(", ") + "</span>";
    });
    html += "</div></div></div>";
  }

  // Conclusiones automáticas
  var primerSaco = null;
  dias.forEach(function (x) { if (primerSaco === null && x.sacos > 0) primerSaco = x.dia; });
  var semanaPeor = Math.floor(peorIdx / 7) + 1;
  html += "<div class='res'>";
  html += "<div><b>" + esc(tr("Mortalidad")) + ":</b> " + esc(tr("el peor día fue el")) + " <b>" + esc(tr("día")) + " " + peorIdx + "</b> (" + dias[peorIdx].bajas + " " +
    esc(tr("pollos")) + ", " + esc(tr("semana")) + " " + semanaPeor + "). " + esc(tr("En total se perdió el")) + " " + pctMort.toFixed(1) + "%.</div>";
  html += "<div><b>" + esc(tr("Alimento")) + ":</b> " + (sacosTotal > 0
    ? esc(tr("se usaron")) + " <b>" + sacosTotal + "</b> " + esc(tr("sacos")) + (primerSaco !== null ? ", " + esc(tr("desde el día")) + " " + primerSaco : "") + "."
    : esc(tr("no se registró alimento en este ciclo."))) + "</div>";
  if (pesoFinal) html += "<div><b>" + esc(tr("Peso")) + ":</b> " + esc(tr("el último pesaje fue de")) + " <b>" + pesoFinal + " kg</b> " + esc(tr("el día")) + " " + diaPesoFinal + ".</div>";
  html += "</div>";

  html += "<div class='pie'><span>Pollo IA · " + esc(camada.nombre || "") + "</span><span>" + esc(tr("Generado")) + " " + new Date().toLocaleDateString("es-DO") + "</span></div>";
  html += "</div></body></html>";

  compartirReportePDF(html, "Reporte-Ciclo-" + (camada.nombre || "camada"));
}

function imprimirReporteVentas(camada, simbolo) {
  var S = simbolo || "RD$";
  var ventas = camada.ventas || [];
  var totalPollos = ventas.reduce(function(a,v){return a+Number(v.pollos||0);},0);
  var totalLibras = ventas.reduce(function(a,v){return a+Number(v.libras||0);},0);
  var totalIngreso = ventas.reduce(function(a,v){return a+Number(v.libras||0)*Number(v.precioLb||0);},0);
  var html = "<!DOCTYPE html><html lang='es'><head><meta charset='utf-8'><title>Ventas - "+camada.nombre+"</title>";
  html += "<style>body{font-family:Arial,sans-serif;padding:24px;max-width:700px;margin:0 auto;color:#1A0A00}";
  html += "h1{color:#D4500A;font-size:20px;margin-bottom:4px}.sub{color:#6B4A28;font-size:12px;margin-bottom:20px}";
  html += "table{width:100%;border-collapse:collapse;font-size:12px;margin-bottom:20px}";
  html += "th{background:#D4500A;color:#fff;padding:7px 8px;text-align:left;font-size:11px}";
  html += "td{padding:6px 8px;border-bottom:1px solid #F0E8E0}tr:nth-child(even) td{background:#FFF8F5}";
  html += ".total{background:#FFF3E8;border:1px solid #D4500A44;border-radius:8px;padding:14px;margin-bottom:20px}";
  html += ".trow{display:flex;justify-content:space-between;font-size:13px;padding:4px 0}";
  html += ".noprint{margin-bottom:16px}.foot{margin-top:24px;font-size:10px;color:#A08060;text-align:center;border-top:1px solid #E0D5C5;padding-top:12px}";
  html += "@media print{.noprint{display:none}}</style></head><body>";
  html += "<div class='noprint'><button onclick='window.print()' style='background:#D4500A;color:#fff;border:none;border-radius:6px;padding:9px 18px;font-size:13px;cursor:pointer;font-weight:700'>\uD83D\uDDA8\uFE0F Imprimir / Guardar PDF</button></div>";
  html += "<h1>\uD83D\uDC14 Reporte de Ventas</h1>";
  html += "<div class='sub'>"+camada.nombre+" \u00B7 "+camada.galpon+" \u00B7 Inicio: "+camada.fechaInicio+"</div>";
  html += "<div class='total'>";
  html += "<div class='trow'><span>Total pollos vendidos</span><b>"+totalPollos.toLocaleString()+" aves</b></div>";
  html += "<div class='trow'><span>Total libras</span><b>"+totalLibras.toFixed(1)+" lb</b></div>";
  html += "<div class='trow'><span>Lb por ave promedio</span><b>"+(totalPollos>0?(totalLibras/totalPollos).toFixed(2):"\u2014")+" lb</b></div>";
  html += "<div class='trow' style='font-size:15px;font-weight:700;margin-top:8px;color:#D4500A'><span>INGRESO TOTAL</span><span>"+S+" "+totalIngreso.toLocaleString()+"</span></div>";
  html += "</div>";
  if (ventas.length === 0) { html += "<p style='color:#A08060'>No hay ventas registradas.</p>"; }
  else {
    html += "<table><thead><tr><th>Fecha</th><th>Pollos</th><th>Lb Netas</th><th>Lb Brutas</th><th>Canastas lb</th><th>$/lb</th><th>Total</th><th>Comprador</th></tr></thead><tbody>";
    ventas.forEach(function(v) {
      var total = Number(v.libras||0)*Number(v.precioLb||0);
      html += "<tr><td>"+(v.fecha||"\u2014")+"</td><td>"+Number(v.pollos||0).toLocaleString()+"</td><td>"+Number(v.libras||0).toFixed(1)+"</td><td>"+(v.librasBrutas?Number(v.librasBrutas).toFixed(1):"\u2014")+"</td><td>"+(v.pesoCanastas?Number(v.pesoCanastas).toFixed(1):"\u2014")+"</td><td>"+S+" "+v.precioLb+"</td><td>"+S+" "+total.toLocaleString()+"</td><td>"+(v.comprador||"\u2014")+"</td></tr>";
    });
    html += "</tbody></table>";
  }
  html += "<div class='foot'>Pollo Express IA \u00B7 Generado "+new Date().toLocaleDateString("es-DO")+"</div>";
  html += "</body></html>";
  compartirReportePDF(html, "Ventas-" + (camada.nombre || ""));
}

function imprimirReporteCostos(camada, simbolo) {
  var S = simbolo || "RD$";
  var gastos = camada.gastos || [];
  var totalGastos = gastos.reduce(function(a,g){return a+Number(g.monto||0);},0);
  var categorias = {};
  gastos.forEach(function(g){ var cat = g.categoria||"otro"; categorias[cat] = (categorias[cat]||0)+Number(g.monto||0); });
  var html = "<!DOCTYPE html><html lang='es'><head><meta charset='utf-8'><title>Costos - "+camada.nombre+"</title>";
  html += "<style>body{font-family:Arial,sans-serif;padding:24px;max-width:700px;margin:0 auto;color:#1A0A00}";
  html += "h1{color:#D4500A;font-size:20px;margin-bottom:4px}.sub{color:#6B4A28;font-size:12px;margin-bottom:20px}";
  html += "table{width:100%;border-collapse:collapse;font-size:12px;margin-bottom:20px}";
  html += "th{background:#D4500A;color:#fff;padding:7px 8px;text-align:left;font-size:11px}";
  html += "td{padding:6px 8px;border-bottom:1px solid #F0E8E0}tr:nth-child(even) td{background:#FFF8F5}";
  html += ".total{background:#FFF3E8;border:1px solid #D4500A44;border-radius:8px;padding:14px;margin-bottom:20px}";
  html += ".trow{display:flex;justify-content:space-between;font-size:13px;padding:4px 0}";
  html += ".noprint{margin-bottom:16px}.foot{margin-top:24px;font-size:10px;color:#A08060;text-align:center;border-top:1px solid #E0D5C5;padding-top:12px}";
  html += "@media print{.noprint{display:none}}</style></head><body>";
  html += "<div class='noprint'><button onclick='window.print()' style='background:#D4500A;color:#fff;border:none;border-radius:6px;padding:9px 18px;font-size:13px;cursor:pointer;font-weight:700'>\uD83D\uDDA8\uFE0F Imprimir / Guardar PDF</button></div>";
  html += "<h1>\uD83D\uDCE6 Reporte de Costos</h1>";
  html += "<div class='sub'>"+camada.nombre+" \u00B7 "+camada.galpon+" \u00B7 Inicio: "+camada.fechaInicio+"</div>";
  html += "<div class='total'>";
  Object.keys(categorias).forEach(function(cat){
    html += "<div class='trow'><span style='text-transform:capitalize'>"+cat+"</span><b>"+S+" "+Number(categorias[cat]).toLocaleString()+"</b></div>";
  });
  html += "<div class='trow' style='font-size:15px;font-weight:700;margin-top:8px;color:#D4500A'><span>COSTO TOTAL</span><span>"+S+" "+totalGastos.toLocaleString()+"</span></div>";
  html += "</div>";
  if (gastos.length === 0) { html += "<p style='color:#A08060'>No hay gastos registrados.</p>"; }
  else {
    html += "<table><thead><tr><th>Fecha</th><th>Categor\u00EDa</th><th>Descripci\u00F3n</th><th>Monto</th></tr></thead><tbody>";
    gastos.forEach(function(g) {
      html += "<tr><td>"+(g.fecha||"\u2014")+"</td><td style='text-transform:capitalize'>"+(g.categoria||"\u2014")+"</td><td>"+(g.descripcion||"\u2014")+"</td><td>"+S+" "+Number(g.monto||0).toLocaleString()+"</td></tr>";
    });
    html += "</tbody></table>";
  }
  html += "<div class='foot'>Pollo Express IA \u00B7 Generado "+new Date().toLocaleDateString("es-DO")+"</div>";
  html += "</body></html>";
  compartirReportePDF(html, "Costos-" + (camada.nombre || ""));
}

function imprimirReporteGeneral(camada, simbolo, modoAlim) {
  var S = simbolo || "RD$";
  var gastos = camada.gastos || [];
  var ventas = camada.ventas || [];
  var registros = camada.registros || [];
  var totalGastos = gastos.reduce(function(a,g){return a+Number(g.monto||0);},0);
  var totalLibras = ventas.reduce(function(a,v){return a+Number(v.libras||0);},0);
  var totalPollosVendidos = ventas.reduce(function(a,v){return a+Number(v.pollos||0);},0);
  var totalIngreso = ventas.reduce(function(a,v){return a+Number(v.libras||0)*Number(v.precioLb||0);},0);
  var ganancia = totalIngreso - totalGastos;
  var lbStd = camada.lbPorPolloEstandar || 7.5;
  var totalPollos = camada.totalPollos || 0;
  var alimTotal = totalPollos * lbStd;
  var mortalidad = registros.reduce(function(a,r){return a+Number(r.muertos||0);},0);
  var categorias = {};
  gastos.forEach(function(g){ var cat=g.categoria||"otro"; categorias[cat]=(categorias[cat]||0)+Number(g.monto||0); });
  var html = "<!DOCTYPE html><html lang=\'es\'><head><meta charset=\'utf-8\'><title>Reporte General - "+camada.nombre+"</title>";
  html += "<style>body{font-family:Arial,sans-serif;padding:24px;max-width:750px;margin:0 auto;color:#1A0A00}h1{color:#D4500A;font-size:22px;margin-bottom:2px}h2{color:#D4500A;font-size:15px;margin:20px 0 8px;border-bottom:2px solid #D4500A22;padding-bottom:4px}.sub{color:#6B4A28;font-size:12px;margin-bottom:18px}table{width:100%;border-collapse:collapse;font-size:12px;margin-bottom:16px}th{background:#D4500A;color:#fff;padding:7px 8px;text-align:left;font-size:11px}td{padding:6px 8px;border-bottom:1px solid #F0E8E0}tr:nth-child(even) td{background:#FFF8F5}.card{background:#FFF3E8;border:1px solid #D4500A33;border-radius:8px;padding:14px;margin-bottom:14px}.row{display:flex;justify-content:space-between;font-size:13px;padding:4px 0;border-bottom:1px solid #F0E8E0}.total{font-size:16px;font-weight:800;color:#D4500A;margin-top:8px}.green{color:#2d7a00}.red{color:#c0392b}.noprint{margin-bottom:16px}.foot{margin-top:24px;font-size:10px;color:#A08060;text-align:center;border-top:1px solid #E0D5C5;padding-top:12px}@media print{.noprint{display:none}}</style></head><body>";
  html += "<div class=\'noprint\'><button onclick=\'window.print()\' style=\'background:#D4500A;color:#fff;border:none;border-radius:6px;padding:9px 18px;font-size:13px;cursor:pointer;font-weight:700\'>Imprimir / Guardar PDF</button></div>";
  html += "<h1>Reporte General Financiero</h1><div class=\'sub\'>"+camada.nombre+" - "+camada.galpon+" - Inicio: "+camada.fechaInicio+" - "+totalPollos.toLocaleString()+" pollos</div>";
  html += "<h2>Resumen Ejecutivo</h2><div class=\'card\'>";
  html += "<div class=\'row\'><span>Total pollos</span><b>"+totalPollos.toLocaleString()+" aves</b></div>";
  html += "<div class=\'row\'><span>Mortalidad</span><b>"+mortalidad+" aves ("+(totalPollos>0?(mortalidad/totalPollos*100).toFixed(1):"0")+" %)</b></div>";
  html += "<div class=\'row\'><span>Pollos vendidos</span><b>"+totalPollosVendidos.toLocaleString()+" aves</b></div>";
  html += "<div class=\'row\'><span>Total libras vendidas</span><b>"+totalLibras.toFixed(1)+" lb</b></div>";
  html += "<div class=\'row\'><span>Lb por ave promedio</span><b>"+(totalPollosVendidos>0?(totalLibras/totalPollosVendidos).toFixed(2):"--")+" lb</b></div>";
  html += "<div class=\'row\'><span>Inversion total</span><b>"+S+" "+totalGastos.toLocaleString()+"</b></div>";
  html += "<div class=\'row\'><span>Ingreso total</span><b>"+S+" "+totalIngreso.toLocaleString()+"</b></div>";
  html += "<div class=\'row total\'><span>GANANCIA / PERDIDA</span><span class=\'"+(ganancia>=0?"green":"red")+"\'>"+S+" "+ganancia.toLocaleString()+"</span></div></div>";
  html += "<h2>Alimentacion (Estandar: "+lbStd+" lb/pollo)</h2><div class=\'card\'>";
  html += "<div class=\'row\'><span>Total estimado</span><b>"+alimTotal.toFixed(0)+" lb / "+(alimTotal/100).toFixed(1)+" sacos 100lb</b></div>";
  planAlimento(totalPollos, lbStd, camada.raza, modoAlim).fases.forEach(function(fa) {
    html += "<div class='row'><span>"+fa.nombre+" D"+fa.d1+"-"+fa.d2+" ~"+fa.pct+"%</span><b>"+fa.lb.toFixed(0)+" lb / "+fa.sacos+" sacos</b></div>";
  });
  html += "</div>";
  html += "<h2>Desglose de Costos</h2>";
  if (gastos.length===0) { html += "<p style=\'color:#A08060\'>No hay gastos registrados.</p>"; }
  else {
    html += "<div class=\'card\'>";
    Object.keys(categorias).forEach(function(cat){ html += "<div class=\'row\'><span style=\'text-transform:capitalize\'>"+cat+"</span><b>"+S+" "+Number(categorias[cat]).toLocaleString()+"</b></div>"; });
    html += "<div class=\'row total\'><span>TOTAL COSTOS</span><span>"+S+" "+totalGastos.toLocaleString()+"</span></div></div>";
    html += "<table><thead><tr><th>Fecha</th><th>Categoria</th><th>Descripcion</th><th>Monto</th></tr></thead><tbody>";
    gastos.forEach(function(g){ html += "<tr><td>"+(g.fecha||"--")+"</td><td style=\'text-transform:capitalize\'>"+(g.categoria||"--")+"</td><td>"+(g.descripcion||"--")+"</td><td>"+S+" "+Number(g.monto||0).toLocaleString()+"</td></tr>"; });
    html += "</tbody></table>";
  }
  html += "<h2>Detalle de Ventas</h2>";
  if (ventas.length===0) { html += "<p style=\'color:#A08060\'>No hay ventas registradas.</p>"; }
  else {
    html += "<table><thead><tr><th>Fecha</th><th>Pollos</th><th>Lb Netas</th><th>$/lb</th><th>Total</th><th>Comprador</th></tr></thead><tbody>";
    ventas.forEach(function(v){ var tot=Number(v.libras||0)*Number(v.precioLb||0); html += "<tr><td>"+(v.fecha||"--")+"</td><td>"+Number(v.pollos||0).toLocaleString()+"</td><td>"+Number(v.libras||0).toFixed(1)+"</td><td>"+S+" "+v.precioLb+"</td><td>"+S+" "+tot.toLocaleString()+"</td><td>"+(v.comprador||"--")+"</td></tr>"; });
    html += "</tbody></table>";
  }
  html += "<div class=\'foot\'>Pollo Express IA - Reporte generado el "+new Date().toLocaleDateString("es-DO")+"</div></body></html>";
  compartirReportePDF(html, "Reporte-" + (camada.nombre || ""));
}



function MisCamadasModal({
  camadasList, activaCamadaId, onSelect, onClose, onCrear, onEditar, onEliminar, onCerrar, onGuardar, C, diasDesde, limit, idioma, sensores, abrirNueva
}) {
  var isEn = idioma === "en";
  var t = function(k) {
    var en = { vivosActuales: "live", nombGalpon: "Barn / House", limiteFlocks: "Flock limit reached. Close one to create a new one.", nuevaCamada: "New Flock", nombreCamada: "Flock name", cantidadPollos: "Number of chicks", fechaInicio: "Start date", crear: "Create", cancelar: "Cancel", galpon: "Barn" };
    var es = { vivosActuales: "vivos", nombGalpon: "Nombre del Galpón", limiteFlocks: "Límite de camadas activas. Cierra una para crear una nueva.", nuevaCamada: "Nueva Camada", nombreCamada: "Nombre de la camada", cantidadPollos: "Cantidad de pollitos", fechaInicio: "Fecha de inicio", crear: "Crear", cancelar: "Cancelar", galpon: "Galpón" };
    return (isEn ? en[k] : es[k]) || k;
  };
  // Sensores: qué sensor ocupa cada camada ACTIVA (un sensor = una camada activa) y cuáles quedan libres.
  var sensoresLista = Array.isArray(sensores) ? sensores : [];
  var sensorOcupadoPor = {};
  camadasList.filter(function (c) { return c.activa !== false && c.sensorId; }).forEach(function (c) { sensorOcupadoPor[c.sensorId] = c.nombre; });
  var sensoresLibres = sensoresLista.filter(function (s) { return !sensorOcupadoPor[s.id]; });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [form, setForm] = useState({
    nombre: (isEn ? "Flock #" : "Camada #") + (camadasList.length + 1),
    galpon: isEn ? "Barn 1" : "Galpón 1",
    totalPollos: "800",
    raza: "Ross 308",
    fechaInicio: new Date().toISOString().split("T")[0],
    // Con exactamente 1 sensor libre se vincula solo (chip removible); con 0 o 2+ arranca sin elegir.
    sensorId: (sensoresLista.length === 1 && sensoresLibres.length === 1) ? sensoresLibres[0].id : "",
  });
  const [showForm, setShowForm] = useState(!!abrirNueva);
  // Entrando por el botón "Nueva camada" de Inicio, la ventana baja sola al formulario:
  // si no, el formulario queda debajo de la lista y parece que el botón no hizo nada.
  useEffect(function () {
    if (!abrirNueva) return;
    var t = setTimeout(function () {
      var el = document.getElementById("pe-form-nueva");
      if (el && el.scrollIntoView) el.scrollIntoView({ block: "start" });
    }, 260);
    return function () { clearTimeout(t); };
  }, []);
  const inputStyle = { display: "block", width: "100%", marginTop: 5, background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, padding: "10px 13px", fontSize: 14, fontFamily: "'Syne', sans-serif", outline: "none", boxSizing: "border-box" };
  const labelStyle = { fontSize: 14, color: C.textMuted, fontFamily: "'Space Mono', monospace", letterSpacing: 1, textTransform: "uppercase" };

  function startEdit(c) {
    setEditingId(c.id);
    setEditForm({ nombre: c.nombre, galpon: c.galpon || "", totalPollos: String(c.totalPollos), fechaInicio: c.fechaInicio, raza: c.raza || "Ross 308", sensorId: c.sensorId || "" });
  }
  // Aviso al elegir un sensor que ya está midiendo otra camada activa.
  function avisoSensorOcupado(sensorId, camadaActualId) {
    if (!sensorId) return "";
    var otra = camadasList.find(function (c) { return c.activa !== false && c.sensorId === sensorId && c.id !== camadaActualId; });
    return otra ? "Este sensor ya está midiendo la camada " + otra.nombre + ". Si lo eliges, se moverá a esta." : "";
  }
  function saveEdit() {
    if (!editForm.nombre) return;
    onEditar(editingId, editForm);
    setEditingId(null);
  }

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 50, background: C.bg, overflowY: "auto" }}>
      <div style={{ width: "100%", maxWidth: 480, margin: "0 auto", minHeight: "100vh", background: C.bg, padding: "20px 16px 60px" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.accent }}>🐔 Mis Camadas</div>
            <div style={{ fontSize: 15, color: C.textFaint, marginTop: 2 }}>{camadasList.length} / {limit} activas</div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={onClose} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMuted, cursor: "pointer", padding: "7px 12px", fontSize: 15 }}>✕</button>
            <button onClick={function() { if (onGuardar) onGuardar(camadasList); onClose(); }} style={{ background: C.accent, border: "none", borderRadius: 8, color: "#1a0a00", cursor: "pointer", padding: "7px 16px", fontSize: 15, fontWeight: 800, fontFamily: "'Syne', sans-serif" }}>💾 Guardar</button>
          </div>
        </div>

        {/* Lista */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
          {camadasList.filter(c => c.activa !== false).map(c => {
            const dias = c.fechaInicio ? diasDesde(c.fechaInicio) : 0;
            const isActiva = c.id === activaCamadaId;
            const vivos = Math.max(0, (c.totalPollos || 0) - (c.registros || []).reduce((a, r) => a + Number(r.mortalidad || 0), 0) - (c.ventas || []).reduce((a, v) => a + Number(v.pollos || 0), 0));
            return (
              <div key={c.id} style={{ background: isActiva ? C.accent + "18" : C.card, border: `2px solid ${isActiva ? C.accent : C.border}`, borderRadius: 16, padding: "16px" }}>
                {editingId === c.id ? (
                  /* ── MODO EDICIÓN ── */
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: C.accent, marginBottom: 12 }}>✏️ Editando {c.nombre}</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      <div><span style={{ fontSize: 14, color: C.textMuted, fontFamily: "'Space Mono', monospace" }}>NOMBRE</span>
                        <input value={editForm.nombre} onChange={e => setEditForm(f => ({...f, nombre: e.target.value}))}
                          style={{ display: "block", width: "100%", marginTop: 4, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, padding: "9px 12px", fontSize: 14, fontFamily: "'Syne', sans-serif", outline: "none", boxSizing: "border-box" }} /></div>
                      <div><span style={{ fontSize: 14, color: C.textMuted, fontFamily: "'Space Mono', monospace" }}>GALPÓN</span>
                        <input value={editForm.galpon} onChange={e => setEditForm(f => ({...f, galpon: e.target.value}))}
                          style={{ display: "block", width: "100%", marginTop: 4, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, padding: "9px 12px", fontSize: 14, fontFamily: "'Syne', sans-serif", outline: "none", boxSizing: "border-box" }} /></div>
                      <div><span style={{ fontSize: 14, color: C.textMuted, fontFamily: "'Space Mono', monospace" }}>TOTAL POLLOS</span>
                        <input type="number" value={editForm.totalPollos} onChange={e => setEditForm(f => ({...f, totalPollos: e.target.value}))}
                          style={{ display: "block", width: "100%", marginTop: 4, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, padding: "9px 12px", fontSize: 14, fontFamily: "'Space Mono', monospace", outline: "none", boxSizing: "border-box" }} /></div>
                      <div><span style={{ fontSize: 14, color: C.textMuted, fontFamily: "'Space Mono', monospace" }}>RAZA DEL POLLO</span>
                        <select value={editForm.raza || "Ross 308"} onChange={e => setEditForm(f => ({...f, raza: e.target.value}))}
                          style={{ display: "block", width: "100%", marginTop: 4, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, padding: "9px 12px", fontSize: 14, fontFamily: "'Syne', sans-serif", outline: "none", boxSizing: "border-box", cursor: "pointer" }}>
                          {RAZAS.map(function (r) { return <option key={r} value={r}>{r}</option>; })}
                        </select>
                        <div style={{ fontSize: 12, color: C.textFaint, marginTop: 5, lineHeight: 1.4 }}>{tr("Cambia el plan de alimento al de esa raza.")}</div>
                      </div>
                      <div><span style={{ fontSize: 14, color: C.textMuted, fontFamily: "'Space Mono', monospace" }}>FECHA INICIO</span>
                        <input type="date" value={editForm.fechaInicio} onChange={e => setEditForm(f => ({...f, fechaInicio: e.target.value}))}
                          style={{ display: "block", width: "100%", marginTop: 4, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, padding: "9px 12px", fontSize: 14, fontFamily: "'Syne', sans-serif", outline: "none", boxSizing: "border-box" }} /></div>
                      {/* ¿Qué sensor mide este galpón? — aquí se hace la vinculación */}
                      {sensoresLista.length > 0 && (
                        <div>
                          <span style={{ fontSize: 14, color: C.textMuted, fontFamily: "'Space Mono', monospace" }}>📡 ¿QUÉ SENSOR MIDE ESTE GALPÓN?</span>
                          <select value={editForm.sensorId || ""} onChange={e => setEditForm(f => ({ ...f, sensorId: e.target.value }))}
                            style={{ display: "block", width: "100%", marginTop: 4, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, padding: "9px 12px", fontSize: 14, fontFamily: "'Syne', sans-serif", outline: "none", boxSizing: "border-box", cursor: "pointer" }}>
                            <option value="">Ninguno</option>
                            {sensoresLista.map(function (s) {
                              return <option key={s.id} value={s.id}>{s.nombre || "Aparato"}{s.serial ? " · " + s.serial : ""}</option>;
                            })}
                          </select>
                          {avisoSensorOcupado(editForm.sensorId, c.id) && (
                            <div style={{ fontSize: 12, color: C.warn, marginTop: 6, lineHeight: 1.4 }}>⚠️ {avisoSensorOcupado(editForm.sensorId, c.id)}</div>
                          )}
                        </div>
                      )}
                      <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                        <button onClick={saveEdit} style={{ flex: 2, background: C.green, color: "#fff", border: "none", borderRadius: 10, padding: "11px", fontWeight: 700, fontSize: 15, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>✅ Guardar</button>
                        <button onClick={() => setEditingId(null)} style={{ flex: 1, background: "none", color: C.textMuted, border: `1px solid ${C.border}`, borderRadius: 10, padding: "11px", fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>Cancelar</button>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* ── MODO NORMAL ── */
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div style={{ flex: 1, cursor: "pointer" }} onClick={() => onSelect(c.id)}>
                        {isActiva && <div style={{ fontSize: 15, color: C.accent, fontFamily: "'Space Mono', monospace", fontWeight: 700, marginBottom: 4, letterSpacing: 1 }}>● ACTIVA AHORA</div>}
                        <div style={{ fontSize: 17, fontWeight: 800, color: isActiva ? C.accent : C.text }}>{c.nombre}</div>
                        <div style={{ fontSize: 14, color: C.textMuted, marginTop: 3 }}>{c.galpon || "Sin galpón"} · {(c.totalPollos || 0).toLocaleString()} pollos</div>
                        <div style={{ fontSize: 15, color: C.textFaint, fontFamily: "'Space Mono', monospace", marginTop: 3 }}>desde {c.fechaInicio}</div>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8, marginLeft: 12 }}>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontSize: 36, fontWeight: 800, fontFamily: "'Space Mono', monospace", color: isActiva ? C.accent : C.textMuted, lineHeight: 1 }}>{dias}</div>
                          <div style={{ fontSize: 15, color: C.textFaint, fontFamily: "'Space Mono', monospace" }}>días</div>
                          <div style={{ fontSize: 14, color: C.green, marginTop: 4, fontWeight: 700 }}>{(vivos || 0).toLocaleString()} {t("vivosActuales") || "vivos"}</div>
                        </div>
                        <button onClick={e => { e.stopPropagation(); startEdit(c); }}
                          style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMuted, cursor: "pointer", padding: "5px 10px", fontSize: 14, fontFamily: "'Syne', sans-serif" }}>
                          ✏️ Editar
                        </button>
                        {onEliminar && (
                          <button onClick={e => { e.stopPropagation(); if (window.confirm("¿Eliminar " + c.nombre + "? Esta acción no se puede deshacer.")) { onEliminar(c.id); } }}
                            style={{ background: "none", border: "1px solid #ff4444", borderRadius: 8, color: "#ff4444", cursor: "pointer", padding: "5px 10px", fontSize: 14, fontFamily: "'Syne', sans-serif" }}>
                            🗑️ Eliminar
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Cerrar camada: abre el resumen de cierre de ESTA camada.
                        Ahí es donde se confirma; aquí solo se elige cuál. */}
                    {onCerrar && c.activa !== false && (
                      <button onClick={e => { e.stopPropagation(); onCerrar(c.id); }}
                        style={{ width: "100%", marginTop: 14, background: "transparent", color: C.warn, border: "1px solid " + C.warn + "77", borderRadius: 10, padding: "11px", fontSize: 14.5, fontWeight: 700, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>
                        🏁 Cerrar camada
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Botón nueva camada */}
        {camadasList.length < limit && !showForm && (
          <button onClick={() => setShowForm(true)}
            style={{ width: "100%", background: C.warn, color: "#1a0a00", border: "none", borderRadius: 12, padding: "14px", fontSize: 15, fontWeight: 800, fontFamily: "'Syne', sans-serif", cursor: "pointer", marginBottom: 10 }}>
            + Nueva Camada
          </button>
        )}
        {camadasList.length >= limit && (
          <div style={{ textAlign: "center", color: C.textFaint, fontSize: 14, padding: "12px", background: C.card, borderRadius: 10 }}>
            {t("limiteFlocks") ? t("limiteFlocks").replace("{n}", limit) : ("Límite de " + limit + " camadas activas alcanzado. Cierra una camada para crear una nueva.")}
          </div>
        )}

        {/* Formulario nueva camada */}
        {showForm && (
          <div id="pe-form-nueva" style={{ background: C.card, borderRadius: 16, border: `1px solid ${C.warn}55`, padding: 18 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: C.warn, marginBottom: 18 }}>🐣 Nueva Camada</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
              <div><span style={labelStyle}>Nombre de la Camada</span><input value={form.nombre} onChange={e => setForm(f => ({...f, nombre: e.target.value}))} style={inputStyle} placeholder="Ej: Camada #3" /></div>
              <div><span style={labelStyle}>{t("nombGalpon") || "Nombre del Galpón"}</span><input value={form.galpon} onChange={e => setForm(f => ({...f, galpon: e.target.value}))} style={inputStyle} placeholder="Ej: Galpón Este, Galpón 2..." /></div>
              <div><span style={labelStyle}>Total de Pollitos</span><input type="number" min="1" value={form.totalPollos} onChange={e => setForm(f => ({...f, totalPollos: e.target.value}))} style={inputStyle} /></div>
              <div>
                <span style={labelStyle}>Raza del Pollo</span>
                <select value={form.raza} onChange={e => setForm(f => ({...f, raza: e.target.value}))} style={{ ...inputStyle, cursor: "pointer" }}>
                  {RAZAS.map(function (r) { return <option key={r} value={r}>{r}</option>; })}
                </select>
              </div>
              <div><span style={labelStyle}>Fecha de Inicio</span><input type="date" value={form.fechaInicio} onChange={e => setForm(f => ({...f, fechaInicio: e.target.value}))} style={inputStyle} /></div>

              {/* Sensor ambiental (solo si el usuario tiene aparatos): 1 → chip removible · 2+ → elegir galpón */}
              {sensoresLista.length === 1 && form.sensorId && (
                <div>
                  <span style={labelStyle}>📡 Sensor</span>
                  <div style={{ marginTop: 5, display: "flex", alignItems: "center", gap: 8, background: "#0F1B2D", border: "1px solid #22D3EE55", borderRadius: 8, padding: "9px 12px" }}>
                    <span style={{ fontSize: 14, color: "#7DD3FC", fontWeight: 700, flex: 1 }}>📡 {(sensoresLibres[0] && sensoresLibres[0].nombre) || "Galpón"}</span>
                    <button onClick={() => setForm(f => ({ ...f, sensorId: "" }))} title="Quitar sensor de este lote"
                      style={{ background: "none", border: "1px solid " + C.border, borderRadius: 6, color: C.textMuted, cursor: "pointer", fontSize: 13, padding: "3px 9px" }}>quitar ✕</button>
                  </div>
                </div>
              )}
              {sensoresLista.length === 1 && !form.sensorId && sensoresLibres.length === 1 && (
                <button onClick={() => setForm(f => ({ ...f, sensorId: sensoresLibres[0].id }))}
                  style={{ width: "100%", background: "none", border: "1px dashed #22D3EE66", borderRadius: 8, color: "#7DD3FC", padding: "9px", fontSize: 13, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>
                  📡 Vincular {sensoresLibres[0].nombre || "el sensor"} a este lote
                </button>
              )}
              {sensoresLista.length >= 2 && (
                <div>
                  <span style={labelStyle}>📡 ¿En cuál galpón está este lote?</span>
                  <select value={form.sensorId} onChange={e => setForm(f => ({ ...f, sensorId: e.target.value }))} style={{ ...inputStyle, cursor: "pointer" }}>
                    <option value="">Ninguno</option>
                    {sensoresLista.map(function (s) {
                      var ocupado = sensorOcupadoPor[s.id];
                      return <option key={s.id} value={s.id} disabled={!!ocupado && form.sensorId !== s.id}>{s.nombre || "Galpón"}{ocupado ? " (ocupado por " + ocupado + ")" : ""}</option>;
                    })}
                  </select>
                </div>
              )}

              <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                <button onClick={() => { if (form.nombre && form.galpon && form.totalPollos) onCrear(form.nombre, form.galpon, form.totalPollos, form.fechaInicio, form.raza, form.sensorId || null); }}
                  style={{ flex: 2, background: C.green, color: "#fff", border: "none", borderRadius: 10, padding: "13px", fontWeight: 800, fontSize: 14, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>
                  ✅ Crear Camada
                </button>
                <button onClick={() => setShowForm(false)}
                  style={{ flex: 1, background: "none", color: C.textMuted, border: `1px solid ${C.border}`, borderRadius: 10, padding: "13px", fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// --- MÓDULO SUPERVISOR: secciones de inspección ---
const SUPERVISOR_SECCIONES = [
  { id: "bioseguridad", titulo: "Bioseguridad", icono: "🛡️", color: "#3B82F6", preguntas: [
    "Pediluvio funcionando", "Control de visitantes", "Cercas y accesos",
    "Uso de botas y ropa adecuada", "Control de roedores", "Control de insectos" ] },
  { id: "pollos", titulo: "Estado de los pollos", icono: "🐔", color: "#F5C200", preguntas: [
    "Uniformidad del lote", "Actividad normal", "Mortalidad dentro del rango esperado",
    "Pollos enfermos", "Plumaje y apariencia", "Problemas de patas o locomoción" ] },
  { id: "alimentacion", titulo: "Alimentación", icono: "🌽", color: "#F97316", preguntas: [
    "Disponibilidad de alimento", "Calidad del alimento", "Comederos suficientes",
    "Comederos limpios", "Consumo normal" ] },
  { id: "agua", titulo: "Agua", icono: "💧", color: "#06B6D4", preguntas: [
    "Agua disponible", "Bebederos funcionando", "Bebederos limpios",
    "Presión adecuada", "Sin fugas" ] },
  { id: "ambiente", titulo: "Ambiente del galpón", icono: "🌡️", color: "#8B5CF6", preguntas: [
    "Temperatura", "Ventilación", "Humedad", "Estado de la cama",
    "Nivel de amoníaco", "Iluminación" ] },
  { id: "equipos", titulo: "Equipos e infraestructura", icono: "🔧", color: "#94A3B8", preguntas: [
    "Ventiladores", "Cortinas", "Sistema eléctrico", "Tanques de agua",
    "Sistema solar o generador", "Estado del techo" ] },
  // NOTA: la sección "Inventario" ya NO es semáforo A/B/C. Ahora el supervisor escribe las
  // CANTIDADES reales en un bloque numérico (ver SupervisorInspeccion) que, al guardar el
  // reporte, actualiza el inventario real de la granja (inventarioGranja).
  { id: "personal", titulo: "Personal", icono: "👷", color: "#14B8A6", preguntas: [
    "Personal completo", "Limpieza general", "Cumplimiento de protocolos",
    "Uso de equipos de protección" ] },
];

const SUP_OPCIONES = [
  { v: "A", label: "Correcto", color: "#16A34A", emoji: "🟢" },
  { v: "B", label: "Atención", color: "#EAB308", emoji: "🟡" },
  { v: "C", label: "Crítico", color: "#DC2626", emoji: "🔴" },
];
const SUP_PUNTOS = { A: 2, B: 1, C: 0 };

function supTotalPreguntas() {
  return SUPERVISOR_SECCIONES.reduce(function (a, s) { return a + s.preguntas.length; }, 0);
}
function supCalcular(answers) {
  var total = supTotalPreguntas();
  var respondidas = 0, puntaje = 0, rojas = 0;
  SUPERVISOR_SECCIONES.forEach(function (s) {
    s.preguntas.forEach(function (_, i) {
      var v = answers[s.id + "_" + i];
      if (v) { respondidas++; puntaje += SUP_PUNTOS[v]; if (v === "C") rojas++; }
    });
  });
  var pct = respondidas > 0 ? Math.round((puntaje / (respondidas * 2)) * 100) : 0;
  var estado = pct >= 90 ? { label: "Operación Excelente", color: "#16A34A", emoji: "🟢" }
    : pct >= 70 ? { label: "Requiere Correcciones", color: "#EAB308", emoji: "🟡" }
    : { label: "Requiere Intervención Inmediata", color: "#DC2626", emoji: "🔴" };
  return { total: total, respondidas: respondidas, puntaje: puntaje, rojas: rojas, pct: pct, estado: estado };
}

// Anillo de progreso (SVG) con animación
function SupRing({ pct, color, C }) {
  var R = 42, CIRC = 2 * Math.PI * R;
  var off = CIRC - (Math.max(0, Math.min(100, pct)) / 100) * CIRC;
  return (
    <svg width="110" height="110" viewBox="0 0 110 110">
      <circle cx="55" cy="55" r={R} fill="none" stroke={C.border} strokeWidth="9" />
      <circle cx="55" cy="55" r={R} fill="none" stroke={color} strokeWidth="9" strokeLinecap="round"
        strokeDasharray={CIRC} strokeDashoffset={off}
        transform="rotate(-90 55 55)" style={{ transition: "stroke-dashoffset 0.6s ease, stroke 0.3s ease" }} />
      <text x="55" y="52" textAnchor="middle" fontSize="26" fontWeight="900" fill={color} fontFamily="'Space Mono', monospace">{pct}%</text>
      <text x="55" y="70" textAnchor="middle" fontSize="11" fill={C.textMuted} fontFamily="'Space Mono', monospace">PUNTAJE</text>
    </svg>
  );
}

// Campos de inventario que el supervisor puede actualizar desde su reporte.
// Son los MISMOS insumos del inventario de la granja (incluidos los "otros" que agregó el dueño),
// para que lo que escriba el supervisor caiga en el renglón correcto.
function supInvCampos(inv, modo) {
  inv = inv || {};
  var uni = inv.unidades || {};
  var base = (modoFases(modo) === 4 || Number(inv.preinicio) > 0
    ? [{ key: "preinicio", emoji: "🐥", nombre: "Alimento Preinicio", unidad: "sacos", ph: "Ej. 10" }]
    : []).concat([
    { key: "iniciador",       emoji: "🐣", nombre: "Alimento Iniciador",   unidad: "sacos", ph: "Ej. 20" },
    { key: "crecimiento",     emoji: "🐔", nombre: "Alimento Crecimiento", unidad: "sacos", ph: "Ej. 20" },
    { key: "engorde",         emoji: "🍗", nombre: "Alimento Engorde",     unidad: "sacos", ph: "Ej. 20" },
    { key: "agua",            emoji: "💧", nombre: "Agua (tanque)",        unidad: "%",     ph: "Ej. 80" },
    { key: "paja",            emoji: "🌾", nombre: "Paja de arroz",        unidad: "sacos", ph: "Ej. 15" },
    { key: "vitaminas",       emoji: "💊", nombre: "Vitaminas",            unidad: uni.vitaminas || "unidades",       ph: "Ej. 4" },
    { key: "desparasitantes", emoji: "🪱", nombre: "Desparasitantes",      unidad: uni.desparasitantes || "unidades", ph: "Ej. 3" },
    { key: "probioticos",     emoji: "🦠", nombre: "Probióticos",          unidad: uni.probioticos || "unidades",     ph: "Ej. 3" },
  ]).map(function (c) {
    return Object.assign({}, c, { actual: inv[c.key] != null ? inv[c.key] : 0 });
  });
  // "Otros" insumos del dueño: se identifican por posición (otro_0, otro_1…).
  var otros = (Array.isArray(inv.otros) ? inv.otros : []).map(function (o, i) {
    return {
      key: "otro_" + i, emoji: "🧪", nombre: o.nombre || "Insumo",
      unidad: o.unidad || "unidades", ph: "Ej. 5", actual: o.cantidad != null ? o.cantidad : 0,
    };
  });
  return base.concat(otros);
}

// --- FORMULARIO DE INSPECCIÓN DEL SUPERVISOR (controlado por props) ---
function SupervisorInspeccion({ C, camada, camadas, session, diaActual, granja, form, setForm, onGuardar, guardado, inspecciones, inventarioActual, modoAlim }) {
  var answers = form.answers || {};
  function setAnswer(qid, val) {
    var next = Object.assign({}, answers);
    next[qid] = next[qid] === val ? undefined : val; // re-tocar = deseleccionar
    setForm(Object.assign({}, form, { answers: next }));
  }
  var calc = supCalcular(answers);
  var completo = calc.respondidas === calc.total;

  return (
    <div className="fadeIn" style={{ display: "flex", flexDirection: "column", gap: 14 }}>

      {/* Header */}
      <div style={{ background: C.surface, borderRadius: 16, border: `1px solid ${C.border}`, padding: "16px 18px" }}>
        <div style={{ fontSize: 20, fontWeight: 900, color: C.accent }}>🔍 Inspección de Supervisor</div>
        <div style={{ fontSize: 13, color: C.textMuted, marginTop: 3 }}>
          {camada.nombre} · Día {diaActual} · {session.nombre}
        </div>
        <div style={{ marginTop: 10, fontSize: 12, color: C.textMuted, display: "flex", justifyContent: "space-between" }}>
          <span>{calc.respondidas} de {calc.total} respondidas</span>
          {calc.rojas > 0 && <span style={{ color: "#DC2626", fontWeight: 700 }}>🔴 {calc.rojas} crítica{calc.rojas > 1 ? "s" : ""}</span>}
        </div>
        <div style={{ height: 8, background: C.bg, borderRadius: 5, overflow: "hidden", marginTop: 6 }}>
          <div style={{ height: "100%", width: (calc.total ? (calc.respondidas / calc.total) * 100 : 0) + "%", background: C.accent, borderRadius: 5, transition: "width 0.4s ease" }} />
        </div>
      </div>

      {/* Secciones */}
      {SUPERVISOR_SECCIONES.map(function (sec) {
        return (
          <div key={sec.id} style={{ background: C.surface, borderRadius: 18, border: `1.5px solid ${sec.color}44`, padding: "16px 16px 8px", boxShadow: "0 1px 3px #00000022" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <div style={{ width: 40, height: 40, borderRadius: 12, background: sec.color + "22", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>{sec.icono}</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>{sec.titulo}</div>
            </div>
            {sec.preguntas.map(function (preg, i) {
              var qid = sec.id + "_" + i;
              var sel = answers[qid];
              return (
                <div key={qid} style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 14, color: C.text, marginBottom: 7 }}>{preg}</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {SUP_OPCIONES.map(function (op) {
                      var on = sel === op.v;
                      return (
                        <button key={op.v} onClick={function () { setAnswer(qid, op.v); }}
                          style={{
                            flex: 1, padding: "11px 4px", borderRadius: 12, cursor: "pointer",
                            border: `2px solid ${on ? op.color : C.border}`,
                            background: on ? op.color : "transparent",
                            color: on ? "#fff" : C.textMuted,
                            fontWeight: on ? 800 : 600, fontSize: 13, fontFamily: "'Syne', sans-serif",
                            transform: on ? "scale(1.04)" : "scale(1)",
                            boxShadow: on ? `0 4px 12px ${op.color}55` : "none",
                            transition: "all 0.18s ease",
                          }}>
                          <div style={{ fontSize: 16 }}>{op.v}</div>
                          <div style={{ fontSize: 10, opacity: on ? 1 : 0.8 }}>{op.label}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}

      {/* 📦 INVENTARIO — el supervisor escribe las CANTIDADES REALES que ve en la granja.
          Al guardar el reporte, estas cantidades ACTUALIZAN el inventario real de la granja.
          Los campos vacíos NO tocan nada (se conserva el valor que ya tenía el inventario). */}
      <div style={{ background: C.surface, borderRadius: 18, border: `1.5px solid #22C55E44`, padding: "16px 16px 8px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: "#22C55E22", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>📦</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>Inventario</div>
        </div>
        <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12 }}>
          Escriba lo que hay AHORA en la granja. Al guardar, el inventario se actualiza. Lo que deje vacío no se cambia.
        </div>
        {supInvCampos(inventarioActual, modoAlim).map(function (campo) {
          var inv = form.inventario || {};
          var val = inv[campo.key] != null ? inv[campo.key] : "";
          var actual = campo.actual;
          return (
            <div key={campo.key} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {campo.emoji} {campo.nombre}
                </div>
                <div style={{ fontSize: 11, color: C.textFaint }}>
                  Ahora: {actual != null ? actual : 0} {campo.unidad}
                </div>
              </div>
              <input type="number" inputMode="decimal" value={val}
                onChange={function (e) {
                  var next = Object.assign({}, form.inventario || {});
                  next[campo.key] = e.target.value;
                  setForm(Object.assign({}, form, { inventario: next }));
                }}
                placeholder={campo.ph}
                style={{ width: 120, background: C.bg, border: `1.5px solid ${C.border}`, borderRadius: 12, color: C.text, padding: "11px 12px", fontSize: 16, fontFamily: "'Space Mono', monospace", outline: "none", boxSizing: "border-box" }} />
            </div>
          );
        })}
      </div>

      {/* Conteo de pollos (referencia) — UNO POR CAMADA; NO afecta el cálculo de vivos */}
      <div style={{ background: C.surface, borderRadius: 16, border: `1px solid ${C.border}`, padding: "16px 18px" }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: C.text, marginBottom: 4 }}>🔢 Conteo de pollos (referencia)</div>
        <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 10 }}>Cuántos pollos cuenta el supervisor en cada camada. Es solo un dato de referencia — no cambia el conteo de vivos.</div>
        {(camadas && camadas.length ? camadas : (camada ? [camada] : [])).map(function (c) {
          var val = (form.conteos && form.conteos[c.id] != null) ? form.conteos[c.id] : "";
          return (
            <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>🐔 {c.nombre}</div>
                {c.galpon && <div style={{ fontSize: 11, color: C.textFaint }}>{c.galpon}</div>}
              </div>
              <input type="number" inputMode="numeric" value={val}
                onChange={function (e) {
                  var conteos = Object.assign({}, form.conteos || {});
                  conteos[c.id] = e.target.value;
                  setForm(Object.assign({}, form, { conteos: conteos }));
                }}
                placeholder="Ej. 780"
                style={{ width: 130, background: C.bg, border: `1.5px solid ${C.border}`, borderRadius: 12, color: C.text, padding: "11px 12px", fontSize: 16, fontFamily: "'Space Mono', monospace", outline: "none", boxSizing: "border-box" }} />
            </div>
          );
        })}
      </div>

      {/* Observaciones */}
      <div style={{ background: C.surface, borderRadius: 16, border: `1px solid ${C.border}`, padding: "16px 18px" }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: C.text, marginBottom: 8 }}>📝 Observaciones</div>
        <textarea value={form.observaciones || ""} onChange={function (e) { setForm(Object.assign({}, form, { observaciones: e.target.value })); }}
          placeholder="Escriba observaciones, recomendaciones o problemas detectados…"
          rows={4}
          style={{ width: "100%", background: C.bg, border: `1.5px solid ${C.border}`, borderRadius: 12, color: C.text, padding: "12px 14px", fontSize: 14, fontFamily: "'Syne', sans-serif", outline: "none", resize: "vertical", boxSizing: "border-box" }} />
      </div>

      {/* Resumen visual */}
      <div style={{ background: C.surface, borderRadius: 18, border: `2px solid ${calc.estado.color}66`, padding: "18px", textAlign: "center" }}>
        <div style={{ fontSize: 13, color: C.textMuted, fontFamily: "'Space Mono', monospace", letterSpacing: 1, marginBottom: 8 }}>ESTADO GENERAL</div>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}>
          <SupRing pct={calc.pct} color={calc.estado.color} C={C} />
        </div>
        <div style={{ fontSize: 18, fontWeight: 900, color: calc.estado.color }}>{calc.estado.emoji} {calc.estado.label}</div>
        <div style={{ fontSize: 13, color: C.textMuted, marginTop: 4 }}>Puntaje: {calc.puntaje} / {calc.respondidas * 2 || 0}</div>
        <div style={{ height: 10, background: C.bg, borderRadius: 6, overflow: "hidden", marginTop: 12 }}>
          <div style={{ height: "100%", width: calc.pct + "%", background: `linear-gradient(90deg, ${calc.estado.color}, ${calc.estado.color}cc)`, borderRadius: 6, transition: "width 0.6s ease" }} />
        </div>
      </div>

      {/* Guardar */}
      <button onClick={function () { onGuardar(calc); }} disabled={calc.respondidas === 0}
        style={{ width: "100%", background: calc.respondidas === 0 ? C.border : `linear-gradient(135deg, ${C.accent}, ${C.accentDim})`, color: "#1a0a00", border: "none", borderRadius: 14, padding: "16px", fontSize: 16, fontWeight: 900, fontFamily: "'Syne', sans-serif", cursor: calc.respondidas === 0 ? "default" : "pointer" }}>
        {guardado ? "✅ ¡Inspección guardada!" : (completo ? "Guardar inspección" : `Guardar (${calc.respondidas}/${calc.total})`)}
      </button>

      {/* Historial de inspecciones */}
      {inspecciones && inspecciones.length > 0 && (
        <div style={{ background: C.surface, borderRadius: 16, border: `1px solid ${C.border}`, padding: "16px 18px" }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: C.text, marginBottom: 12 }}>📚 Historial de inspecciones</div>
          {inspecciones.slice().reverse().slice(0, 10).map(function (insp) {
            var est = insp.pct >= 90 ? "#16A34A" : insp.pct >= 70 ? "#EAB308" : "#DC2626";
            var f = insp.fecha ? new Date(insp.fecha) : null;
            return (
              <div key={insp.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "10px 0", borderTop: `1px solid ${C.border}` }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, color: C.text, fontWeight: 700 }}>{f ? f.toLocaleDateString() : "—"}</div>
                  <div style={{ fontSize: 12, color: C.textMuted }}>Día {insp.edadLote} · {insp.supervisor}{insp.rojas ? " · 🔴 " + insp.rojas : ""}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                  <div style={{ background: est + "22", color: est, fontWeight: 900, fontSize: 15, padding: "6px 12px", borderRadius: 10, fontFamily: "'Space Mono', monospace" }}>{insp.pct}%</div>
                  <button onClick={function () { imprimirInspeccionSupervisor(insp, camada); }}
                    title="Imprimir / Guardar PDF"
                    style={{ background: C.accent, color: "#1a0a00", border: "none", borderRadius: 10, padding: "8px 10px", fontSize: 15, fontWeight: 800, cursor: "pointer" }}>🖨️</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// --- DIBUJOS DEL REGISTRO SENCILLO ---
// Figuras simples que se entienden sin leer: una cama con goticas si está mojada, heces
// firmes o aguadas, un saco de alimento, un frasco... Todas toman el color que se les pase.
function IcoSimple({ t, c, s }) {
  var z = s || 44;
  var col = c || "#F0F0F5";
  var pico = "#F2994A";
  // Una gota de agua, para enseñar cuánta humedad tiene la cama.
  function gota(x, y, k) {
    return <path key={"g" + x} d="M0 0c0 0 5 6.5 5 9.5a5 5 0 0 1-10 0C-5 6.5 0 0 0 0z" fill={col} transform={"translate(" + x + "," + y + ") scale(" + (k || 1) + ")"} />;
  }
  // La cama de viruta vista de lado.
  var camaBase = (
    <g stroke={col} strokeLinecap="round" fill="none">
      <g strokeWidth="3.6">
        <path d="M14 28.5h8M26.5 28.5h8" />
        <path d="M7 35h9.5M21 35h8M33.5 35h7.5" />
        <path d="M4 41.5h10.5M19 41.5h9.5M33 41.5h11" />
      </g>
    </g>
  );
  var gallina = (
    <g>
      <ellipse cx="21" cy="31" rx="13" ry="11" fill={col} />
      <circle cx="32" cy="17" r="7.5" fill={col} />
      <path d="M29 9.6c.3-2.6 2.3-3 3-1 1.2-2 3-1.2 3.2.9" stroke={col} strokeWidth="2.6" fill="none" strokeLinecap="round" />
      <path d="M39.5 17.5l5.5 2-5.5 2.2z" fill={pico} />
      <circle cx="34.5" cy="16" r="1.5" fill="#11131A" />
      <path d="M17 41.5v3.5M25 41.5v3.5" stroke={col} strokeWidth="2.6" strokeLinecap="round" />
    </g>
  );
  var figuras = {
    // Pollos vivos / fase de crecimiento
    gallina: gallina,
    // Pollo muerto: acostado, con la patita tiesa y una equis en el ojo.
    muerto: (
      <g>
        <ellipse cx="27" cy="35" rx="15" ry="8.5" fill={col} />
        <circle cx="10" cy="30" r="6.8" fill={col} />
        <path d="M3.6 29l-3.2 1.4 3.2 1.4z" fill={pico} />
        <path d="M7.4 27.6l3.6 3.6M11 27.6l-3.6 3.6" stroke="#11131A" strokeWidth="2.3" strokeLinecap="round" />
        <g stroke={col} strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" fill="none">
          <path d="M23.5 27l-2.5-9.5" /><path d="M17.5 15l3.5 2.5 3.4-2.8" />
          <path d="M33 28l3.5-8.5" /><path d="M32.8 17l3.7 2.5 3.5-1.9" />
        </g>
      </g>
    ),
    // Pollito (alimento iniciador)
    pollito: (
      <g transform="translate(24,26) scale(0.76) translate(-24,-26)">
        <circle cx="24" cy="29" r="11" fill={col} />
        <circle cx="24" cy="16" r="7" fill={col} />
        <path d="M30.5 16.2l5 1.8-5 2z" fill={pico} />
        <circle cx="26.5" cy="15" r="1.4" fill="#11131A" />
        <path d="M21.5 8.6c.6-2.4 2.5-2.4 3 0" stroke={col} strokeWidth="2.4" fill="none" strokeLinecap="round" />
        <path d="M21 39.5V43M27 39.5V43" stroke={col} strokeWidth="2.4" strokeLinecap="round" />
      </g>
    ),
    // Pollo grande (alimento de engorde)
    polloGrande: (
      <g>
        <ellipse cx="22" cy="30" rx="16" ry="13" fill={col} />
        <circle cx="35" cy="15" r="7" fill={col} />
        <path d="M32 7.6c.3-2.5 2.2-2.9 2.9-1 1.1-1.9 2.9-1.1 3.1.9" stroke={col} strokeWidth="2.5" fill="none" strokeLinecap="round" />
        <path d="M42 15.5l5 2-5 2z" fill={pico} />
        <circle cx="37.5" cy="14" r="1.4" fill="#11131A" />
        <path d="M16 42.5V46M26 42.5V46" stroke={col} strokeWidth="2.6" strokeLinecap="round" />
      </g>
    ),
    // Cama SECA: viruta con sol encima
    cama1: (
      <g>
        <circle cx="24" cy="12" r="5" fill={col} />
        <path d="M24 3v2.6M24 18.6V21M14.5 12H17M31 12h2.5M17.4 5.4l1.8 1.8M28.8 16.8l1.8 1.8M30.6 5.4l-1.8 1.8M19.2 16.8l-1.8 1.8" stroke={col} strokeWidth="2.4" strokeLinecap="round" />
        {camaBase}
      </g>
    ),
    // Cama CASI SECA: una gotica
    cama2: (<g>{gota(24, 7)}{camaBase}</g>),
    // Cama HÚMEDA: dos goticas
    cama3: (<g>{gota(15, 8, 0.9)}{gota(33, 8, 0.9)}{camaBase}</g>),
    // Cama MOJADA: tres goticas y un charco debajo
    cama4: (
      <g>
        {gota(11, 7, 0.85)}{gota(24, 4, 0.95)}{gota(37, 7, 0.85)}
        {camaBase}
        <ellipse cx="24" cy="46.2" rx="18" ry="2.4" fill={col} opacity="0.7" />
      </g>
    ),
    // Heces FIRMES: bien formadas, en torre
    heces1: (
      <g fill={col}>
        <ellipse cx="24" cy="38.5" rx="15" ry="5.8" />
        <ellipse cx="25.8" cy="29.8" rx="11.6" ry="5.2" />
        <ellipse cx="22.2" cy="22.2" rx="8.2" ry="4.6" />
        <circle cx="24.6" cy="16" r="3.6" />
      </g>
    ),
    // Heces BLANDAS: aplastadas
    heces2: (
      <g fill={col}>
        <ellipse cx="24" cy="36" rx="17" ry="6.5" />
        <ellipse cx="24" cy="27.5" rx="11" ry="5" />
      </g>
    ),
    // DIARREA: charco con salpicadura
    heces3: (
      <g fill={col}>
        <ellipse cx="24" cy="34" rx="18" ry="7" />
        <circle cx="7.5" cy="23.5" r="2.6" />
        <circle cx="40" cy="24.5" r="3" />
        <circle cx="31" cy="19.5" r="2.2" />
        <circle cx="15.5" cy="19" r="2" />
      </g>
    ),
    // DIARREA CON SANGRE: el charco más las gotas de sangre
    heces4: (
      <g>
        <ellipse cx="24" cy="35" rx="18" ry="7" fill={col} />
        <circle cx="8" cy="25" r="2.6" fill={col} />
        <circle cx="40" cy="26" r="3" fill={col} />
        <path d="M17 8c0 0 4.6 6.2 4.6 9A4.6 4.6 0 0 1 12.4 17c0-2.8 4.6-9 4.6-9z" fill="#F23B2F" />
        <path d="M31 13c0 0 3.6 4.8 3.6 7a3.6 3.6 0 0 1-7.2 0c0-2.2 3.6-7 3.6-7z" fill="#F23B2F" />
      </g>
    ),
    // Saco de alimento
    saco: (
      <g>
        <path d="M11.5 18.5h25l1.6 19.5a6 6 0 0 1-6 6.5H15.9a6 6 0 0 1-6-6.5z" fill={col} />
        <path d="M11.5 18.5l3.4-7h18.2l3.4 7z" fill={col} opacity="0.5" />
        <path d="M18 11.5v7M30 11.5v7" stroke="#11131A" strokeWidth="1.8" opacity="0.35" />
        <rect x="16.5" y="27" width="15" height="9.5" rx="2.5" fill="#11131A" opacity="0.45" />
      </g>
    ),
    // Balanza, para el peso del pollo
    balanza: (
      <g>
        <path d="M9 20h30l4.5 22.5a2.5 2.5 0 0 1-2.5 3H7a2.5 2.5 0 0 1-2.5-3z" fill={col} opacity="0.3" stroke={col} strokeWidth="2.6" strokeLinejoin="round" />
        <path d="M4.5 20h39" stroke={col} strokeWidth="3.4" strokeLinecap="round" />
        <path d="M17.5 20v-7h13v7" fill="none" stroke={col} strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="24" cy="34" r="7.5" fill="none" stroke={col} strokeWidth="2.8" />
        <path d="M24 34l5-3.5" stroke={col} strokeWidth="2.8" strokeLinecap="round" />
      </g>
    ),
    // Cámara, para la foto de las heces
    camara: (
      <g stroke={col} strokeWidth="3.2" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 16.5h7l3-5h16l3 5h7a3 3 0 0 1 3 3V38a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V19.5a3 3 0 0 1 3-3z" />
        <circle cx="24" cy="28" r="8" />
      </g>
    ),
    // Gota grande (agua)
    gotaGrande: (<path d="M24 5s11 14 11 20.5a11 11 0 0 1-22 0C13 19 24 5 24 5z" fill={col} />),
    // Paca de paja
    paja: (
      <g>
        <rect x="5" y="15" width="38" height="22" rx="6" fill={col} opacity="0.35" />
        <path d="M10 21h28M10 26h28M10 31h28" stroke={col} strokeWidth="2.6" strokeLinecap="round" />
        <rect x="14" y="13" width="4" height="26" rx="2" fill={col} opacity="0.85" />
        <rect x="30" y="13" width="4" height="26" rx="2" fill={col} opacity="0.85" />
      </g>
    ),
    // Cápsula (vitaminas)
    pastilla: (
      <g transform="rotate(-40 24 24)">
        <rect x="5" y="17" width="38" height="14" rx="7" fill={col} opacity="0.4" />
        <path d="M12 17h12v14H12a7 7 0 0 1 0-14z" fill={col} />
        <path d="M24 17.8v12.4" stroke="#11131A" strokeWidth="2" opacity="0.55" />
      </g>
    ),
    // Jeringa (desparasitantes)
    jeringa: (
      <g>
        <rect x="18" y="12.5" width="12" height="21.5" rx="2" fill={col} opacity="0.3" stroke={col} strokeWidth="2.6" />
        <rect x="19.8" y="22" width="8.4" height="11" fill={col} />
        <path d="M14.5 12.5h19M20 7.5h8M24 7.5v5M24 34v8.5" stroke={col} strokeWidth="3" strokeLinecap="round" />
      </g>
    ),
    // Frasco (probióticos y cualquier otro insumo)
    frasco: (
      <g>
        <rect x="18" y="5.5" width="12" height="5.5" rx="1.5" fill={col} />
        <path d="M19.5 11h9v4.5c0 2.2 6 5.5 6 10.8V37a5 5 0 0 1-5 5H18.5a5 5 0 0 1-5-5V26.3c0-5.3 6-8.6 6-10.8z" fill={col} opacity="0.35" stroke={col} strokeWidth="2.6" strokeLinejoin="round" />
        <rect x="16.5" y="28" width="15" height="9" rx="2" fill={col} />
      </g>
    ),
    // Guardar / listo
    guardar: (
      <g stroke={col} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none">
        <path d="M24 6v22" /><path d="M14 20l10 10 10-10" /><path d="M8 38v3.5h32V38" />
      </g>
    ),
    listo: (
      <g stroke={col} strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" fill="none">
        <path d="M9 25.5l9.5 9.5L39 14" />
      </g>
    ),
  };
  var fig = figuras[t] || figuras.frasco;
  return (
    <svg width={z} height={z} viewBox="0 0 48 48" style={{ display: "block", flexShrink: 0 }} aria-hidden="true">{fig}</svg>
  );
}
// Vitaminas, probióticos y vacunas que el granjero tiene puestos en SU calendario. No hay
// una lista aparte que mantener: lo que él agregue o edite en el calendario sale aquí solo.
// El agua limpia no entra, porque no es algo que se "dé". Lo que toca HOY se pone de primero.
function suplementosDelCalendario(cal, dia) {
  var vistos = {}, out = [];
  (cal || []).forEach(function (ev) {
    if (!ev || ev.tipo === "agua") return;
    var nombre = (ev.producto_nombre && String(ev.producto_nombre).trim()) ||
                 (ev.producto && String(ev.producto).trim()) || "";
    if (!nombre || /^agua/i.test(nombre)) return;
    var clave = nombre.toLowerCase();
    var esHoy = Number(ev.dia) === Number(dia);
    if (vistos[clave]) { if (esHoy) vistos[clave].hoy = true; return; }
    var esVacuna = ev.tipo === "vacuna";
    var deFrasco = ev.tipo === "probiotico" || ev.tipo === "enzima" || ev.tipo === "desparasitante";
    var ficha = {
      val: nombre, nombre: nombre, vacuna: esVacuna, hoy: esHoy,
      ico: esVacuna ? "jeringa" : deFrasco ? "frasco" : "pastilla",
      color: esVacuna ? "#F0883E" : "#6AA6F5",
    };
    vistos[clave] = ficha;
    out.push(ficha);
  });
  return out.sort(function (a, b) {
    if (a.hoy !== b.hoy) return a.hoy ? -1 : 1;
    if (a.vacuna !== b.vacuna) return a.vacuna ? 1 : -1;
    return 0;
  });
}
// --- SENSOR AMBIENTAL (aparato ESP32: temperatura, humedad, amoniaco) ---
// La caja se vincula escribiendo en la app el código de 6 dígitos que ella muestra
// en su pantalla (el WiFi se configura en la propia caja, ya no por Bluetooth).
// Colores del semáforo (verde / amarillo / rojo / gris "sin dato-calibrar").
const SENS_VERDE = "#22C55E", SENS_AMAR = "#FBBF24", SENS_ROJO = "#EF4444", SENS_GRIS = "#64748B";
// Violeta = "no me fío de esta lectura" (mismo color que el LED de la caja en ese estado).
const SENS_REVISAR = "#A78BFA";
// Techo del convertidor del ESP32: por encima de esto el sensor ya no distingue nada, así
// que su número no significa amoniaco bajo, significa que NO está midiendo. Mismos valores
// que el firmware (NH3_TECHO / NH3_VUELVE) para que caja y app digan siempre lo mismo.
const NH3_TECHO = 4080, NH3_VUELVE = 4050;
// Curva de temperatura ideal por día — MISMA "TEMP_IDEAL" que usa el dashboard.
// (Copia a nivel de módulo para que los helpers del sensor la usen sin depender de MainApp.)
const TEMP_IDEAL_SENSOR = {
  0:[33,34], 1:[32,34], 2:[32,34], 3:[32,34], 4:[30,32], 5:[30,32], 6:[30,32], 7:[30,32],
  8:[28,30], 9:[28,30], 10:[28,30], 11:[26,28], 12:[26,28], 13:[26,28], 14:[26,28],
  15:[24,26], 16:[24,26], 17:[24,26], 18:[24,26], 19:[23,25], 20:[23,25], 21:[23,25],
  22:[21,24], 23:[21,24], 24:[21,24], 25:[21,24], 26:[21,24], 27:[21,24], 28:[21,24],
  29:[20,23], 30:[20,23], 31:[20,23], 32:[20,23], 33:[20,23], 34:[20,23], 35:[20,23],
};
function tempObjetivoSensor(dia) {
  var r = TEMP_IDEAL_SENSOR[Math.max(0, Math.min(Number(dia) || 0, 35))] || [20, 23];
  return (r[0] + r[1]) / 2;
}
// Minutos desde el último reporte (o null si nunca reportó).
function sensHaceMin(ts) { return ts ? Math.floor((Date.now() - new Date(ts).getTime()) / 60000) : null; }
// Semáforo de temperatura vs el objetivo del día (rojo ±2.5 · amarillo ±1.5).
function sensTempEstado(temp, dia) {
  if (temp == null) return { color: SENS_GRIS, nivel: "gris", obj: tempObjetivoSensor(dia) };
  var obj = tempObjetivoSensor(dia), d = Math.abs(Number(temp) - obj);
  if (d >= 2.5) return { color: SENS_ROJO, nivel: "rojo", obj: obj };
  if (d >= 1.5) return { color: SENS_AMAR, nivel: "amarillo", obj: obj };
  return { color: SENS_VERDE, nivel: "verde", obj: obj };
}
// Semáforo de humedad (amarillo fuera de 50–75%).
function sensHumEstado(hum) {
  if (hum == null) return { color: SENS_GRIS, nivel: "gris" };
  if (Number(hum) < 50 || Number(hum) > 75) return { color: SENS_AMAR, nivel: "amarillo" };
  return { color: SENS_VERDE, nivel: "verde" };
}
// ¿El sensor está pegado al techo (o sea, no está midiendo)? Se aplica la MISMA histéresis
// que la caja: se entra en "revisar" al pasar de NH3_TECHO y solo se sale al bajar de
// NH3_VUELVE. Se recorren las lecturas de la más VIEJA a la más nueva.
// `lecturas` viene ordenado de la más nueva a la más vieja.
// Sin mq_raw (lecturas viejas, modo demostración) devuelve false y todo sigue como antes.
function sensNh3EnTecho(lecturas) {
  if (!Array.isArray(lecturas) || !lecturas.length) return false;
  var enTecho = false, vistoAlguno = false;
  for (var i = lecturas.length - 1; i >= 0; i--) {
    var raw = lecturas[i] && lecturas[i].mq_raw;
    if (raw == null) continue;
    vistoAlguno = true;
    if (Number(raw) >= NH3_TECHO) enTecho = true;
    else if (Number(raw) < NH3_VUELVE) enTecho = false;
  }
  return vistoAlguno && enTecho;
}
// Semáforo de amoniaco desde nh3_ratio (sin r0 → gris "sin calibrar").
// Con el sensor en el techo NO se pinta verde: decir "bajo" con un sensor que no mide es
// peor que no decir nada, porque el amoniaco es justo lo que este producto vigila.
function sensNh3Estado(ratio, enTecho) {
  if (enTecho) return { color: SENS_REVISAR, nivel: "revisar", label: "Revisar sensor" };
  if (ratio == null) return { color: SENS_GRIS, nivel: "gris", label: "Sin calibrar" };
  var r = Number(ratio);
  if (r >= 1.8) return { color: SENS_ROJO, nivel: "rojo", label: "Alto" };
  if (r >= 1.35) return { color: SENS_AMAR, nivel: "amarillo", label: "Subiendo" };
  return { color: SENS_VERDE, nivel: "verde", label: "Bajo" };
}
// --- TABLA DE APARATOS: `sensor_devices` (definitiva) o `sensores` (anterior) ---
// La migración se aplica corriendo `supabase-r6.sql`. Hasta entonces la app sigue
// funcionando sobre `sensores`; en cuanto se corre el SQL pasa sola a la nueva.
// Ambas tienen los MISMOS nombres de columna, así que solo cambia el nombre de la tabla.
var _tablaAparatos = null;
function tablaAparatos() {
  if (_tablaAparatos) return Promise.resolve(_tablaAparatos);
  return SB.select("sensor_devices", "select=device_token&limit=1")
    .then(function (r) { _tablaAparatos = Array.isArray(r) ? "sensor_devices" : "sensores"; return _tablaAparatos; })
    .catch(function () { _tablaAparatos = "sensores"; return _tablaAparatos; });
}
// Columna con la que una camada apunta a su aparato.
function columnaCamadaSensor() {
  return _tablaAparatos === "sensor_devices" ? "sensor_device_id" : "sensor_id";
}
// Columna con la que una lectura apunta a su aparato.
function columnaLectura() {
  return _tablaAparatos === "sensor_devices" ? "sensor_device_id" : "sensor_id";
}
// Objeto para guardar (o soltar, con null) el vínculo de una camada con su aparato.
function vinculoCamada(idAparato) {
  var o = {};
  o[columnaCamadaSensor()] = idAparato;
  return o;
}

// Segundos desde una marca de tiempo (para "hace 40 segundos").
function sensHaceSeg(ts) { return ts ? Math.max(0, Math.floor((Date.now() - new Date(ts).getTime()) / 1000)) : null; }
// Texto humano del tiempo transcurrido: "hace 40 segundos" · "hace 3 min" · "hace 2 h".
function sensHaceTxt(ts) {
  var s = sensHaceSeg(ts);
  if (s == null) return "sin datos";
  if (s < 60) return "hace " + s + " segundos";
  var m = Math.floor(s / 60);
  if (m < 60) return "hace " + m + " min";
  var h = Math.floor(m / 60);
  if (h < 24) return "hace " + h + " h";
  return "hace " + Math.floor(h / 24) + " d";
}

// --- MODO DEMOSTRACIÓN ---
// Lecturas SIMULADAS que se generan en el teléfono y NUNCA se guardan en Supabase.
// Sirven para ver/probar el diseño y grabar videos sin tener el aparato físico.
// SIEMPRE se muestran con el sello "DEMO" — jamás se enseña un dato falso sin sello.
function demoRuido(semilla) {
  var x = Math.sin(semilla * 12.9898) * 43758.5453;
  return x - Math.floor(x);   // 0..1, determinista (misma semilla → mismo valor)
}
// Devuelve 24 h de lecturas (cada 10 min), ordenadas de la más reciente a la más vieja,
// igual que llegan de Supabase. `tick` hace que la lectura actual cambie en cada refresco.
function demoLecturas(diaActual, tick) {
  var meta = tempObjetivoSensor(diaActual);
  var ahora = Date.now();
  var out = [];
  for (var i = 0; i <= 144; i++) {
    var t = ahora - i * 600000;                       // 10 min por punto
    var k = Math.floor(t / 600000);
    var hora = new Date(t).getHours();
    var ciclo = Math.cos((hora - 15) / 24 * 2 * Math.PI) * 0.8;   // de madrugada baja un poco
    var temp = meta + ciclo + (demoRuido(k) - 0.5) * 0.8;         // ±0.4° alrededor de la meta
    var hum = 58 + demoRuido(k + 7) * 14;                         // 58–72 %
    // El amoniaco alterna entre BAJO y SUBIENDO cada ~14 minutos.
    var subiendo = Math.floor(t / (14 * 60000)) % 2 === 0;
    var ratio = subiendo ? 1.36 + demoRuido(k + 3) * 0.12 : 1.02 + demoRuido(k + 5) * 0.2;
    out.push({
      ts: new Date(t).toISOString(),
      temp_c: Number(temp.toFixed(1)),
      humedad: Number(hum.toFixed(0)),
      mq_raw: Math.round(ratio * 1180),               // coherente con r0=1180 (nombre mq_raw intacto)
      nh3_ratio: Number(ratio.toFixed(3)),
      _demo: true,
    });
  }
  // Lectura "de ahora mismo": oscila con el tick para que se vea viva en cada refresco.
  var subeAhora = (tick % 2) === 0;
  out[0] = {
    ts: new Date(ahora).toISOString(),
    temp_c: Number((meta + (demoRuido(tick + 11) - 0.5) * 0.8).toFixed(1)),
    humedad: Number((58 + demoRuido(tick + 21) * 14).toFixed(0)),
    mq_raw: Math.round((subeAhora ? 1.42 : 1.08) * 1180),
    nh3_ratio: Number((subeAhora ? 1.38 + demoRuido(tick) * 0.08 : 1.05 + demoRuido(tick + 1) * 0.15).toFixed(3)),
    _demo: true,
  };
  return out;
}
// --- PIEZAS VISUALES DE LA TARJETA AMBIENTE (estilo tablero) ---
// Anillo de segmentos que se van encendiendo según el valor, como un tablero de control.
function AnilloSeg({ pct, color, size, contenido }) {
  var S = size || 58;
  var segs = 22, cx = S / 2, cy = S / 2, rExt = S / 2 - 2, rInt = rExt - 6;
  var activos = Math.round((Math.max(0, Math.min(100, Number(pct) || 0)) / 100) * segs);
  var lineas = [];
  for (var i = 0; i < segs; i++) {
    var ang = (-90 + i * (360 / segs)) * Math.PI / 180;
    var on = i < activos;
    lineas.push(
      <line key={i}
        x1={cx + Math.cos(ang) * rInt} y1={cy + Math.sin(ang) * rInt}
        x2={cx + Math.cos(ang) * rExt} y2={cy + Math.sin(ang) * rExt}
        stroke={on ? color : "#ffffff14"} strokeWidth="3.2" strokeLinecap="round" />
    );
  }
  return (
    <div style={{ position: "relative", width: S, height: S, flexShrink: 0 }}>
      <svg width={S} height={S} style={{ display: "block" }}>{lineas}</svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: S * 0.34, fontWeight: 900, color: color, lineHeight: 1 }}>
        {contenido}
      </div>
    </div>
  );
}
// Mini-gráfica de la tendencia reciente (la rayita ondulada del tablero).
function MiniGrafica({ valores, color, ancho, alto }) {
  var W = ancho || 52, H = alto || 22;
  var v = (valores || []).filter(function (x) { return x != null && !isNaN(Number(x)); }).map(Number);
  if (v.length < 2) return <div style={{ width: W, height: H }} />;
  var min = Math.min.apply(null, v), max = Math.max.apply(null, v);
  var rango = (max - min) || 1;
  var pts = v.map(function (x, i) {
    return ((i / (v.length - 1)) * W).toFixed(1) + "," + (H - ((x - min) / rango) * (H - 3) - 1.5).toFixed(1);
  }).join(" ");
  return (
    <svg width={W} height={H} style={{ display: "block", flexShrink: 0 }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" opacity="0.95" />
    </svg>
  );
}
// Sello DEMO (ámbar) — obligatorio en toda superficie con datos simulados.
function BadgeDemo({ style }) {
  return (
    <span style={Object.assign({
      background: "#FBBF24", color: "#3a2a00", fontSize: 9, fontWeight: 900,
      letterSpacing: 1, padding: "2px 7px", borderRadius: 6, fontFamily: "'Space Mono', monospace",
      flexShrink: 0, whiteSpace: "nowrap",
    }, style || {})}>DEMO</span>
  );
}

// Flecha de tendencia del amoniaco: promedio de la última hora vs. las 3 anteriores.
// lecturas = array ordenado por ts DESC con {nh3_ratio, ts}.
function sensNh3Tendencia(lecturas) {
  var ahora = Date.now(), h1 = [], h3 = [];
  (lecturas || []).forEach(function (l) {
    if (l.nh3_ratio == null) return;
    var edadH = (ahora - new Date(l.ts).getTime()) / 3600000;
    if (edadH <= 1) h1.push(Number(l.nh3_ratio));
    else if (edadH <= 4) h3.push(Number(l.nh3_ratio));
  });
  if (!h1.length || !h3.length) return "→";
  var a = h1.reduce(function (x, y) { return x + y; }, 0) / h1.length;
  var b = h3.reduce(function (x, y) { return x + y; }, 0) / h3.length;
  if (a - b > 0.05) return "↗";
  if (a - b < -0.05) return "↘";
  return "→";
}

// --- COMPRESIÓN DE FOTOS ---
// Una foto de un teléfono moderno pesa entre 3 y 12 MB. Mandarla tal cual al análisis de
// IA falla (el límite de subida de Vercel es 4.5 MB) y además llena el almacenamiento del
// navegador. Aquí se reduce a un tamaño que la IA sigue leyendo perfecto: lado largo de
// 1280 px y JPEG de calidad 0.72 → normalmente entre 100 y 300 KB.
// A prueba de fallos: ante cualquier problema devuelve la foto original sin tocar.
function comprimirImagen(file, ladoMax, calidad) {
  var LADO = ladoMax || 1280;
  var Q = calidad || 0.72;
  return new Promise(function (resolve, reject) {
    var lector = new FileReader();
    lector.onerror = function () { reject(new Error("no se pudo leer la foto")); };
    lector.onload = function (ev) {
      var original = ev.target.result;
      var img = new Image();
      img.onerror = function () { resolve(original); };
      img.onload = function () {
        try {
          var w = img.naturalWidth || img.width, h = img.naturalHeight || img.height;
          if (!w || !h) { resolve(original); return; }
          var escala = Math.min(1, LADO / Math.max(w, h));
          var cw = Math.max(1, Math.round(w * escala)), ch = Math.max(1, Math.round(h * escala));
          var lienzo = document.createElement("canvas");
          lienzo.width = cw; lienzo.height = ch;
          var ctx = lienzo.getContext("2d");
          ctx.fillStyle = "#ffffff";        // sin esto un PNG transparente sale en negro
          ctx.fillRect(0, 0, cw, ch);
          ctx.drawImage(img, 0, 0, cw, ch);
          var chica = lienzo.toDataURL("image/jpeg", Q);
          resolve(chica.length < original.length ? chica : original);
        } catch (e) { resolve(original); }
      };
      img.src = original;
    };
    lector.readAsDataURL(file);
  });
}

// --- PANEL DE CÓDIGOS PROMOCIONALES (solo el dueño de la app) ---
// Genera los códigos que van pegados a cada Pollo Box. Un código = una caja.
// Formato corto, fácil de dictar y de escribir en una etiqueta: 2 letras + 2 números (ej. KT47).
// Sin nada que se confunda al leerlo: fuera las letras I, L, O (parecen 1 y 0) y fuera los
// números 0 y 1. Son 23 × 23 × 8 × 8 = 33.856 combinaciones, de sobra para unas decenas de cajas.
const PROMO_LETRAS = "ABCDEFGHJKMNPQRSTUVWXYZ";
const PROMO_NUMEROS = "23456789";
function generarCodigoPromo() {
  var buf = new Uint32Array(4);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) crypto.getRandomValues(buf);
  else for (var j = 0; j < 4; j++) buf[j] = Math.floor(Math.random() * 4294967296);
  return PROMO_LETRAS[buf[0] % PROMO_LETRAS.length] + PROMO_LETRAS[buf[1] % PROMO_LETRAS.length] +
         PROMO_NUMEROS[buf[2] % PROMO_NUMEROS.length] + PROMO_NUMEROS[buf[3] % PROMO_NUMEROS.length];
}

// Guarda una fila de app_config sin duplicar: PATCH y, si no existía, INSERT.
function guardarAppConfig(key, value) {
  return SB.update("app_config", { key: key }, { value: String(value) }).then(function (r) {
    if (!r || r.error || (Array.isArray(r) && r.length === 0)) {
      return SB.insert("app_config", { key: key, value: String(value) });
    }
    return r;
  });
}

// ── PANEL DE FIRMWARE (solo el dueño de la app) ──
// Publica una versión nueva del programa de las cajas. Cada caja pregunta sola al servidor
// (al encender y cada pocas horas) y, si hay una versión distinta CON enlace, se actualiza y
// se reinicia. "Actualizar ahora" le da la orden en su próximo reporte (máximo 5 minutos).
function FirmwarePanel({ C }) {
  const [cajas, setCajas] = useState(null);
  const [version, setVersion] = useState("");
  const [url, setUrl] = useState("");
  const [solo, setSolo] = useState("");
  const [aviso, setAviso] = useState("");
  const [ocupado, setOcupado] = useState(false);

  function cargar() {
    tablaAparatos().then(function (tabla) {
      return SB.select(tabla, "select=id,nombre,serial,id_caja,fw_version,ultimo_reporte&order=id.asc");
    }).then(function (rows) { setCajas(Array.isArray(rows) ? rows : []); })
      .catch(function () { setCajas([]); });
    SB.select("app_config", "key=in.(firmware_version,firmware_url,firmware_solo)&select=key,value")
      .then(function (rows) {
        (Array.isArray(rows) ? rows : []).forEach(function (r) {
          if (r.key === "firmware_version") setVersion(r.value || "");
          if (r.key === "firmware_url") setUrl(r.value || "");
          if (r.key === "firmware_solo") setSolo(r.value || "");
        });
      }).catch(function () {});
  }
  useEffect(function () { cargar(); }, []);

  function guardarConfig(cambios, textoOk) {
    setOcupado(true); setAviso("");
    Promise.all(cambios.map(function (c) { return guardarAppConfig(c.key, c.value); })).then(function () {
      setOcupado(false);
      setAviso(textoOk);
      setTimeout(function () { setAviso(""); }, 4000);
    }).catch(function () { setOcupado(false); setAviso("No se pudo guardar. Revisa la conexión."); });
  }

  function publicar() {
    var v = version.trim(), u = url.trim();
    if (!v) { setAviso("Escribe el número de versión (ej. 4.1.0)."); return; }
    if (u && !/^https:\/\/.+\.bin$/i.test(u)) { setAviso("El enlace debe empezar con https:// y terminar en .bin"); return; }
    guardarConfig([
      { key: "firmware_version", value: v },
      { key: "firmware_url", value: u },
      { key: "firmware_solo", value: solo.trim() },
    ], u ? "✅ Publicado. Las cajas se actualizarán solas." : "⏸️ Publicación detenida (sin enlace, nadie se actualiza).");
  }

  function detener() {
    setUrl("");
    guardarConfig([{ key: "firmware_url", value: "" }], "⏸️ Detenido: ninguna caja se va a actualizar.");
  }

  function actualizarAhora(idCaja) {
    setOcupado(true); setAviso("");
    SB.select("app_config", "key=eq.firmware_ota_ahora&select=value&limit=1").then(function (rows) {
      var actual = (Array.isArray(rows) && rows.length && rows[0].value) ? String(rows[0].value) : "";
      var ids = actual.split(",").map(function (x) { return x.trim(); }).filter(Boolean);
      if (ids.indexOf(String(idCaja)) === -1) ids.push(String(idCaja));
      return guardarAppConfig("firmware_ota_ahora", ids.join(","));
    }).then(function () {
      setOcupado(false);
      setAviso("📡 Orden enviada. La caja se actualiza en su próximo reporte (máx. 5 min).");
      setTimeout(function () { setAviso(""); }, 6000);
    }).catch(function () { setOcupado(false); setAviso("No se pudo enviar la orden."); });
  }

  var inputS = { width: "100%", background: C.surface, border: "1.5px solid " + C.border, borderRadius: 8, color: C.text, padding: "10px 12px", fontSize: 14, fontFamily: "'Syne', sans-serif", outline: "none", marginTop: 4 };
  var lbl = { fontSize: 11, color: C.textFaint, fontFamily: "'Space Mono', monospace", letterSpacing: 1 };

  return (
    <div style={{ marginBottom: 20, background: C.card, borderRadius: 12, padding: "14px 16px", border: "1.5px solid " + C.accent + "44" }}>
      <div style={{ fontSize: 12, color: C.accent, fontFamily: "'Space Mono', monospace", letterSpacing: 1, marginBottom: 4 }}>📡 FIRMWARE DE LAS CAJAS</div>
      <div style={{ fontSize: 11, color: C.textFaint, marginBottom: 12, lineHeight: 1.5 }}>
        Publica una versión nueva y todas las cajas se actualizan solas por WiFi. Solo tú ves esto.
      </div>

      {/* Cajas y la versión que tiene cada una */}
      <div style={{ marginBottom: 14 }}>
        <div style={lbl}>CAJAS REGISTRADAS</div>
        {cajas == null ? (
          <div style={{ fontSize: 12, color: C.textMuted, marginTop: 6 }}>Cargando…</div>
        ) : cajas.length === 0 ? (
          <div style={{ fontSize: 12, color: C.textMuted, marginTop: 6 }}>Todavía no hay cajas registradas.</div>
        ) : cajas.map(function (c) {
          var min = c.ultimo_reporte ? Math.round((Date.now() - new Date(c.ultimo_reporte).getTime()) / 60000) : null;
          var viva = min != null && min <= 15;
          return (
            <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 8, background: C.surface, borderRadius: 10, padding: "9px 11px", marginTop: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: viva ? C.green : C.textFaint, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {c.nombre || "Caja"} {c.serial ? "· " + c.serial : ""}
                </div>
                <div style={{ fontSize: 11, color: C.textMuted, fontFamily: "'Space Mono', monospace" }}>
                  v{c.fw_version || "?"} · {min == null ? "sin reportar" : min <= 60 ? "hace " + min + " min" : "hace " + Math.round(min / 60) + " h"}
                </div>
              </div>
              <button onClick={function () { actualizarAhora(c.id); }} disabled={ocupado || !url.trim()}
                title={!url.trim() ? "Primero publica una versión con enlace" : ""}
                style={{ background: url.trim() ? C.accent : C.surface, color: url.trim() ? "#1a0a00" : C.textFaint, border: "1px solid " + C.border, borderRadius: 8, padding: "7px 10px", fontSize: 11.5, fontWeight: 800, cursor: url.trim() ? "pointer" : "not-allowed", fontFamily: "'Syne', sans-serif", flexShrink: 0 }}>
                Actualizar ahora
              </button>
            </div>
          );
        })}
      </div>

      {/* Publicación */}
      <div style={lbl}>VERSIÓN DISPONIBLE</div>
      <input value={version} onChange={function (e) { setVersion(e.target.value); }} placeholder="4.1.0" style={inputS} />
      <div style={Object.assign({}, lbl, { marginTop: 10 })}>ENLACE DEL ARCHIVO .bin</div>
      <input value={url} onChange={function (e) { setUrl(e.target.value); }} placeholder="https://app.polloia.net/firmware/polloia-4.1.0.bin" style={inputS} />
      <div style={Object.assign({}, lbl, { marginTop: 10 })}>¿A CUÁLES CAJAS?</div>
      <select value={solo} onChange={function (e) { setSolo(e.target.value); }} style={Object.assign({}, inputS, { cursor: "pointer" })}>
        <option value="">Todas las cajas</option>
        {(cajas || []).map(function (c) {
          return <option key={c.id} value={c.id_caja || c.serial || ""}>Solo {c.nombre || "Caja"} {c.serial ? "· " + c.serial : ""}</option>;
        })}
      </select>
      <div style={{ fontSize: 11, color: C.textFaint, marginTop: 6, lineHeight: 1.5 }}>
        Consejo: publica primero a UNA caja, comprueba que encendió bien, y después ponlo en “todas”.
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <button onClick={publicar} disabled={ocupado}
          style={{ flex: 2, background: C.accent, color: "#1a0a00", border: "none", borderRadius: 8, padding: "11px", fontSize: 13.5, fontWeight: 800, cursor: "pointer", fontFamily: "'Syne', sans-serif" }}>
          {ocupado ? "Guardando…" : "Publicar actualización"}
        </button>
        <button onClick={detener} disabled={ocupado}
          style={{ flex: 1, background: "none", color: C.danger, border: "1px solid " + C.danger + "66", borderRadius: 8, padding: "11px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "'Syne', sans-serif" }}>
          Detener
        </button>
      </div>
      {aviso && <div style={{ fontSize: 12, color: C.text, marginTop: 10, lineHeight: 1.5 }}>{aviso}</div>}
    </div>
  );
}

function PromoCodesPanel({ C }) {
  const [meses, setMeses] = useState(3);
  const [cantidad, setCantidad] = useState(10);
  const [lote, setLote] = useState("");
  const [generando, setGenerando] = useState(false);
  const [aviso, setAviso] = useState("");
  const [lista, setLista] = useState(null);
  const [filtro, setFiltro] = useState("libres");
  const [faltaSql, setFaltaSql] = useState(false);

  function cargar() {
    SB.select("promo_codes", "select=*&order=creado_en.desc&limit=500").then(function (rows) {
      if (!Array.isArray(rows)) { setFaltaSql(true); setLista([]); return; }
      setFaltaSql(false); setLista(rows);
    }).catch(function () { setFaltaSql(true); setLista([]); });
  }
  useEffect(function () { cargar(); }, []);

  function generar() {
    var n = Math.max(1, Math.min(200, Number(cantidad) || 1));
    setGenerando(true); setAviso("");
    var filas = [];
    // Ni repetidos dentro del lote ni códigos que ya existan: con el formato corto hay
    // pocas combinaciones y de vez en cuando saldría uno igual a otro ya impreso.
    var vistos = {};
    (lista || []).forEach(function (x) { vistos[x.codigo] = true; });
    var intentos = 0;
    while (filas.length < n && intentos < n * 50) {
      intentos++;
      var c = generarCodigoPromo();
      if (vistos[c]) continue;
      vistos[c] = true;
      filas.push({ codigo: c, meses: meses, lote: lote.trim() || null, usos_max: 1 });
    }
    SB.insert("promo_codes", filas).then(function (r) {
      setGenerando(false);
      if (!r || r.error) {
        setAviso("No se pudieron guardar. ¿Corriste supabase-promos.sql en Supabase?");
        return;
      }
      setAviso("Listo: " + n + " códigos de " + meses + (meses === 1 ? " mes" : " meses") + " creados.");
      cargar();
    }).catch(function () {
      setGenerando(false);
      setAviso("No se pudieron guardar. ¿Corriste supabase-promos.sql en Supabase?");
    });
  }

  var todos = lista || [];
  var libres = todos.filter(function (c) { return c.activo !== false && (c.usos || 0) < (c.usos_max || 1); });
  var usados = todos.filter(function (c) { return (c.usos || 0) >= (c.usos_max || 1); });
  var mostrar = filtro === "libres" ? libres : filtro === "usados" ? usados : todos;

  function copiar() {
    var txt = mostrar.map(function (c) {
      return c.codigo + "\t" + c.meses + " meses" + (c.lote ? "\t" + c.lote : "");
    }).join("\n");
    try {
      navigator.clipboard.writeText(txt);
      setAviso("Copiados " + mostrar.length + " códigos. Pégalos en Excel o Word para imprimirlos.");
    } catch (e) { setAviso("No se pudo copiar en este navegador."); }
  }

  var etiqueta = { fontSize: 12, color: C.textMuted, fontWeight: 700, marginBottom: 6 };
  var campo = { width: "100%", background: C.surface, border: "1.5px solid " + C.border, borderRadius: 8, color: C.text, padding: "10px 12px", fontSize: 14, fontFamily: "'Syne', sans-serif", outline: "none" };

  return (
    <div style={{ marginBottom: 20, background: C.card, borderRadius: 12, padding: "14px 16px", border: "1.5px solid " + C.accent + "66" }}>
      <div style={{ fontSize: 12, color: C.accent, fontFamily: "'Space Mono', monospace", letterSpacing: 1, marginBottom: 4 }}>🎟️ CÓDIGOS PROMOCIONALES</div>
      <div style={{ fontSize: 11, color: C.textFaint, marginBottom: 12, lineHeight: 1.5 }}>
        Un código = una caja. Cada uno sirve UNA sola vez y regala los meses que elijas.
      </div>

      {faltaSql && (
        <div style={{ background: C.danger + "18", border: "1.5px solid " + C.danger + "66", borderRadius: 10, padding: "11px 12px", marginBottom: 12, fontSize: 12.5, color: C.text, lineHeight: 1.6 }}>
          Falta preparar la base de datos. Corre el archivo <b>supabase-promos.sql</b> en Supabase → SQL Editor, y vuelve a abrir esta pantalla.
        </div>
      )}

      <div style={etiqueta}>Meses de regalo</div>
      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        {[1, 3, 6, 12].map(function (m) {
          var on = meses === m;
          return (
            <button key={m} onClick={function () { setMeses(m); }}
              style={{ flex: 1, padding: "10px 4px", borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: on ? 900 : 600, fontFamily: "'Space Mono', monospace", border: "2px solid " + (on ? C.accent : C.border), background: on ? C.accent + "22" : "transparent", color: on ? C.accent : C.textMuted }}>
              {m === 12 ? "1 año" : m + (m === 1 ? " mes" : " meses")}
            </button>
          );
        })}
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
        <div style={{ width: 110 }}>
          <div style={etiqueta}>Cuántos</div>
          <input type="number" min="1" max="200" value={cantidad}
            onChange={function (e) { setCantidad(e.target.value); }} style={campo} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={etiqueta}>Lote (para acordarte)</div>
          <input value={lote} placeholder="veterinaria Santiago" maxLength={60}
            onChange={function (e) { setLote(e.target.value); }} style={campo} />
        </div>
      </div>

      <button onClick={generar} disabled={generando || faltaSql}
        style={{ width: "100%", background: (generando || faltaSql) ? C.border : C.accent, color: "#1a0a00", border: "none", borderRadius: 10, padding: "13px", fontSize: 15, fontWeight: 900, cursor: (generando || faltaSql) ? "not-allowed" : "pointer", fontFamily: "'Syne', sans-serif", marginBottom: 10 }}>
        {generando ? "Generando…" : "Generar códigos"}
      </button>

      {aviso && <div style={{ fontSize: 12.5, color: C.text, marginBottom: 10, lineHeight: 1.5 }}>{aviso}</div>}

      {lista == null ? (
        <div style={{ fontSize: 13, color: C.textMuted }}>Cargando…</div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 6, marginBottom: 10, alignItems: "center" }}>
            {[["libres", "Sin usar (" + libres.length + ")"], ["usados", "Usados (" + usados.length + ")"], ["todos", "Todos (" + todos.length + ")"]].map(function (x) {
              var on = filtro === x[0];
              return (
                <button key={x[0]} onClick={function () { setFiltro(x[0]); }}
                  style={{ flex: 1, padding: "8px 4px", borderRadius: 8, cursor: "pointer", fontSize: 11.5, fontWeight: on ? 800 : 600, fontFamily: "'Syne', sans-serif", border: "1.5px solid " + (on ? C.accent : C.border), background: on ? C.accent + "18" : "transparent", color: on ? C.accent : C.textMuted }}>
                  {x[1]}
                </button>
              );
            })}
          </div>

          {mostrar.length > 0 && (
            <button onClick={copiar}
              style={{ width: "100%", background: "transparent", border: "1.5px solid " + C.border, borderRadius: 8, color: C.textMuted, padding: "9px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "'Syne', sans-serif", marginBottom: 10 }}>
              Copiar los {mostrar.length} de la lista
            </button>
          )}

          <div style={{ maxHeight: 260, overflowY: "auto", border: "1px solid " + C.border, borderRadius: 8 }}>
            {mostrar.length === 0 ? (
              <div style={{ fontSize: 12.5, color: C.textFaint, padding: 14, textAlign: "center" }}>Nada por aquí todavía.</div>
            ) : mostrar.map(function (c) {
              var gastado = (c.usos || 0) >= (c.usos_max || 1);
              return (
                <div key={c.codigo} style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 11px", borderBottom: "1px solid " + C.border }}>
                  <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 13.5, fontWeight: 800, color: gastado ? C.textFaint : C.text, textDecoration: gastado ? "line-through" : "none", letterSpacing: 0.5 }}>{c.codigo}</span>
                  <span style={{ fontSize: 11, color: C.textMuted, marginLeft: "auto", whiteSpace: "nowrap" }}>
                    {c.meses === 12 ? "1 año" : c.meses + (c.meses === 1 ? " mes" : " meses")}
                  </span>
                  <span style={{ fontSize: 15 }}>{gastado ? "✅" : "⚪"}</span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// --- PANTALLA ESTABLE ---
// Las pantallas (Inicio, Ventas, Costos, Registro…) se definen DENTRO de MainApp, así que
// React las veía como un componente distinto en cada re-dibujo y las destruía y volvía a
// crear: se perdía lo que el usuario tenía escrito y se repetía la animación de entrada.
// Este envoltorio vive fuera y NUNCA cambia de identidad, así que React conserva la
// pantalla viva. La `key` es la pestaña: solo se reinicia al cambiar de pestaña, que es
// justo lo que se espera.
function Pantalla({ dibujar }) {
  return dibujar();
}

// --- FRENO DE REFRESCOS DE FONDO ---
// Regla: mientras el usuario tiene algo abierto (un formulario de venta, una ventana,
// el teclado en un campo) NINGÚN refresco automático puede correr. Antes, un refresco
// en mal momento le borraba lo que estaba escribiendo.
// Los componentes avisan que están "ocupados" con usarOcupado(true/false).
var PE_OCUPADO = { n: 0 };
function marcarOcupado(activo) {
  PE_OCUPADO.n = Math.max(0, PE_OCUPADO.n + (activo ? 1 : -1));
}
function estaOcupado() {
  if (PE_OCUPADO.n > 0) return true;
  var el = typeof document !== "undefined" ? document.activeElement : null;
  return !!(el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT"));
}
// Hook: mientras "activo" sea true, la app se considera ocupada (no se refresca sola).
function usarOcupado(activo) {
  React.useEffect(function () {
    if (!activo) return;
    marcarOcupado(true);
    return function () { marcarOcupado(false); };
  }, [activo]);
}

// --- MAIN APP ---
// ── CIERRES DE CAMADA PENDIENTES ──
// Cerrar una camada es un PATCH activa=false. Si falla (sin internet, error del servidor)
// la nube la sigue teniendo activa y en la siguiente descarga completa "revivía". Mientras
// el cierre no esté confirmado, el id queda anotado aquí: la carga la oculta y reintenta.
function cierresPendientes(email) {
  try { return JSON.parse(localStorage.getItem("pe_cierres_pend_" + email) || "[]"); } catch (e) { return []; }
}
function fijarCierresPendientes(email, ids) {
  try { localStorage.setItem("pe_cierres_pend_" + email, JSON.stringify(ids)); } catch (e) {}
}
function quitarCierrePendiente(email, id) {
  var p = cierresPendientes(email);
  if (p.indexOf(id) !== -1) fijarCierresPendientes(email, p.filter(function (x) { return x !== id; }));
}
// Lo mismo al CREAR: una camada hecha sin internet solo existe en este teléfono. Mientras su
// creación no esté confirmada, la carga la conserva y la vuelve a subir. Sin esta lista, toda
// camada que faltara en la nube se conservaba para siempre (así revivían las cerradas/borradas).
function creacionesPendientes(email) {
  try { return JSON.parse(localStorage.getItem("pe_creaciones_pend_" + email) || "[]"); } catch (e) { return []; }
}
function marcarCreacionPendiente(email, id) {
  var p = creacionesPendientes(email);
  if (p.indexOf(id) === -1) { p.push(id); try { localStorage.setItem("pe_creaciones_pend_" + email, JSON.stringify(p)); } catch (e) {} }
}
function quitarCreacionPendiente(email, id) {
  var p = creacionesPendientes(email);
  if (p.indexOf(id) !== -1) { try { localStorage.setItem("pe_creaciones_pend_" + email, JSON.stringify(p.filter(function (x) { return x !== id; }))); } catch (e) {} }
}
function cerrarCamadaEnNube(email, id) {
  var p = cierresPendientes(email);
  if (p.indexOf(id) === -1) { p.push(id); fijarCierresPendientes(email, p); }
  return SB.update("camadas", { id: id }, { activa: false }).then(function (res) {
    if (res && !res.error) quitarCierrePendiente(email, id);   // confirmado (o ya no existe)
  }).catch(function () {});
}

function MainApp({ session, onLogout, subStatus }) {
  const [tab, setTab] = useState("dashboard");

  // --- Detección de PC/escritorio (≥1024px) para usar un layout diferente con menú lateral ---
  // En teléfono/Android (ancho < 1024) NO cambia nada: se mantiene el diseño móvil de siempre.
  const [isDesktop, setIsDesktop] = useState(function () {
    try { return typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches; } catch (e) { return false; }
  });
  useEffect(function () {
    if (typeof window === "undefined") return;
    var mq = window.matchMedia ? window.matchMedia("(min-width: 1024px)") : null;
    var recompute = function () { setIsDesktop(window.innerWidth >= 1024); };
    var mqHandler = function (e) { setIsDesktop(e.matches); };
    if (mq) { if (mq.addEventListener) mq.addEventListener("change", mqHandler); else if (mq.addListener) mq.addListener(mqHandler); }
    window.addEventListener("resize", recompute);
    return function () {
      if (mq) { if (mq.removeEventListener) mq.removeEventListener("change", mqHandler); else if (mq.removeListener) mq.removeListener(mqHandler); }
      window.removeEventListener("resize", recompute);
    };
  }, []);

  // El "día" se calcula con la fecha de HOY. Si la app queda abierta y cambia la fecha (medianoche),
  // hay que re-renderizar para que el día aumente solo. Programamos un refresco justo después de las 12am.
  const [, setDiaTick] = useState(0);
  useEffect(function () {
    var timer;
    function programarMedianoche() {
      var ahora = new Date();
      var prox = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate() + 1, 0, 0, 10); // 12:00:10 am
      timer = setTimeout(function () { setDiaTick(function (t) { return t + 1; }); programarMedianoche(); }, prox - ahora);
    }
    programarMedianoche();
    return function () { clearTimeout(timer); };
  }, []);

  // --- Multi-camada init ---
  const [camadasList, setCamadasList] = useState(() => {
    // Solo cargar del localStorage — Supabase las reemplazará al conectar
    return DB.getCamadasList(session.ownerEmail);
  });
  const [activaCamadaId, setActivaCamadaIdState] = useState(() => {
    return DB.getActivaCamadaId(session.ownerEmail) || (DB.getCamadasList(session.ownerEmail)[0]?.id || "");
  });
  const camada = camadasList.find(c => c.id === activaCamadaId) || camadasList[0] || null;

  // Refresco DISPARADO POR EL USUARIO: se llama después de guardar/eliminar/crear/cerrar.
  // Esta es la vía principal de actualización; el refresco de fondo pasa a ser solo
  // una red de seguridad lenta para ver los cambios que hacen los empleados.
  const loadCamadasRef = React.useRef(null);
  const refrescoTimerRef = React.useRef(null);
  function refrescarDatos() {
    // Anti-rebote: si el usuario hace varias acciones seguidas (guardar y borrar, por
    // ejemplo) se agrupan en un solo refresco en vez de disparar uno por cada una.
    if (refrescoTimerRef.current) clearTimeout(refrescoTimerRef.current);
    refrescoTimerRef.current = setTimeout(function () {
      refrescoTimerRef.current = null;
      if (loadCamadasRef.current && session.ownerEmail) loadCamadasRef.current(session.ownerEmail);
    }, 700);
  }

  function saveCamada(updated) {
    const newList = camadasList.map(c => c.id === updated.id ? updated : c);
    setCamadasList(newList);
    DB.saveCamadaById(session.ownerEmail, updated);
    var payload = {
      id: updated.id,
      owner_email: session.ownerEmail,
      nombre: updated.nombre,
      galpon: updated.galpon || "Galpón 1",
      fecha_inicio: updated.fechaInicio,
      total_pollos: updated.totalPollos,
      lb_por_pollo_estandar: updated.lbPorPolloEstandar || 7.5,
      precio_venta: updated.precioVenta || null,
      raza: updated.raza || "Ross 308",
      registros: (updated.registros || []).concat(updated.registrosBorrados || []),
      gastos: updated.gastos || [],
      ventas: updated.ventas || [],
      heces_analisis: updated.hecesAnalisis || [],
    };
    // Se guarda con PATCH y SOLO si la camada sigue activa en la nube. Antes era un upsert con
    // "activa" sacado de la copia de este teléfono: si la camada se había cerrado en otro
    // dispositivo (o el cierre no llegó), cualquier guardado la volvía a marcar ACTIVA.
    function reintentar(intento) {
      if (intento < 3) setTimeout(function() { intentarGuardar(intento + 1); }, 1500 * intento);
    }
    // Lo que sale de este teléfono: registros visibles + marcas de borrado.
    var regsSalientes = (updated.registros || []).concat(updated.registrosBorrados || []);
    function intentarGuardar(intento) {
      var datos = Object.assign({}, payload);
      delete datos.id;
      // Antes de escribir se lee lo que hay en la nube y se JUNTA (gana el cambio más nuevo
      // de cada registro). Así un teléfono con datos viejos no borra ni revive nada ajeno.
      SB.select("camadas", "id=eq." + encodeURIComponent(updated.id) + "&select=registros").then(function(rowsNube) {
        var nube = (Array.isArray(rowsNube) && rowsNube[0] && Array.isArray(rowsNube[0].registros)) ? rowsNube[0].registros : [];
        datos.registros = fusionarRegistros(regsSalientes, nube);
      }).catch(function() {
        datos.registros = regsSalientes;
      }).then(function() {
      return SB.update("camadas", { id: updated.id, activa: true }, datos);
      }).then(function(res) {
        if (!res || res.error) { reintentar(intento); return; }
        if (Array.isArray(res) && res.length === 0) {
          // No cambió ninguna fila: o la camada está cerrada (no se toca) o nunca llegó a la nube.
          SB.select("camadas", "id=eq." + encodeURIComponent(updated.id) + "&select=id").then(function(rows) {
            if (!Array.isArray(rows)) { reintentar(intento); return; }
            if (rows.length > 0) { refrescarDatos(); return; }        // existe pero cerrada → no revivirla
            // No existe: solo se crea si es una camada recién hecha en este teléfono (su creación
            // no llegó). Una vieja que falta es que la borraron en otro dispositivo.
            var m = String(updated.id).match(/cam_(\d+)/);
            var reciente = m && (Date.now() - Number(m[1])) < 24 * 3600000;
            var pendCrear = creacionesPendientes(session.ownerEmail).indexOf(updated.id) !== -1;
            if (!(reciente || pendCrear) || cierresPendientes(session.ownerEmail).indexOf(updated.id) !== -1) return;
            SB.upsert("camadas", Object.assign({}, payload, { activa: true })).then(function(r2) {
              if (r2 === null) { reintentar(intento); return; }
              quitarCreacionPendiente(session.ownerEmail, updated.id);
              refrescarDatos();
            }).catch(function() { reintentar(intento); });
          }).catch(function() { reintentar(intento); });
          return;
        }
        refrescarDatos();   // guardado OK → traer la versión final de la nube
      }).catch(function() { reintentar(intento); });
    }
    intentarGuardar(1);
  }
  function setActivaCamada(id) {
    setActivaCamadaIdState(id);
    DB.setActivaCamadaId(session.ownerEmail, id);
  }
  function crearNuevaCamada(nombre, galpon, totalPollos, fechaInicio, raza, sensorId) {
    const num = camadasList.length + 1;
    const nueva = { ...initCamada(session.ownerEmail, num), nombre, galpon, totalPollos: Number(totalPollos), fechaInicio, raza: raza || "Ross 308" };
    if (sensorId) nueva.sensorId = sensorId;   // vínculo con el aparato (aparece de una en la tarjeta Ambiente)
    const newList = [...camadasList, nueva];
    setCamadasList(newList);
    DB.saveCamadasList(session.ownerEmail, newList);
    setActivaCamada(nueva.id);
    marcarCreacionPendiente(session.ownerEmail, nueva.id);
    // Guardar en Supabase inmediatamente
    SB.upsert("camadas", {
      id: nueva.id,
      owner_email: session.ownerEmail,
      nombre: nueva.nombre,
      galpon: nueva.galpon,
      fecha_inicio: nueva.fechaInicio,
      total_pollos: nueva.totalPollos,
      lb_por_pollo_estandar: nueva.lbPorPolloEstandar || 7.5,
      precio_venta: nueva.precioVenta || 0,
      raza: nueva.raza,
      activa: true,
      registros: [],
      gastos: [],
      ventas: [],
    }).then(function(res) {
      if (res !== null) quitarCreacionPendiente(session.ownerEmail, nueva.id);
      // Vincular el sensor por PATCH (NUNCA en el upsert parcial: rompería por columnas NOT NULL).
      if (sensorId) SB.update("camadas", { id: nueva.id }, vinculoCamada(sensorId)).catch(function() {});
      refrescarDatos();
    }).catch(function(e) {
      console.error("Error guardando camada en Supabase:", e);
      // Reintento automático a los 3 segundos
      setTimeout(function() {
        SB.upsert("camadas", {
          id: nueva.id, owner_email: session.ownerEmail, nombre: nueva.nombre, galpon: nueva.galpon,
          fecha_inicio: nueva.fechaInicio, total_pollos: nueva.totalPollos,
          lb_por_pollo_estandar: nueva.lbPorPolloEstandar || 7.5, precio_venta: nueva.precioVenta || 0,
          raza: nueva.raza, activa: true, registros: [], gastos: [], ventas: [],
        }).then(function(res) {
          if (res !== null) quitarCreacionPendiente(session.ownerEmail, nueva.id);
          if (sensorId) SB.update("camadas", { id: nueva.id }, vinculoCamada(sensorId)).catch(function() {});
        }).catch(function() {});
      }, 3000);
    });
    // Con sensor vinculado: ofrecer calibrar el amoniaco (el galpón suele estar limpio antes del lote).
    if (sensorId) pedirCalibrarSiSensor(sensorId);
    return nueva;
  }

  // --- Sync from Supabase on load ---
  useEffect(function() {
    if (!session.ownerEmail) return;
    // Para empleados, primero verificar ownerEmail desde Supabase por si acaso
    // Descarga COMPLETA de camadas + merge. Solo se llama cuando algo cambió
    // (lo decide el pre-chequeo liviano de loadCamadas) o como red de seguridad.
    function doFullLoad(ownerEmail) {
      try { localStorage.setItem("pe_lastfull_" + ownerEmail, String(Date.now())); } catch (e) {}
      SB.select("camadas", "owner_email=eq." + ownerEmail + "&activa=eq.true&order=created_at.asc")
      .then(function(rows) {
        if (!rows || rows.error) return;
        // IDs que el usuario eliminó explícitamente — guardados en localStorage
        var deletedIds = [];
        try { deletedIds = JSON.parse(localStorage.getItem("pe_deleted_" + ownerEmail) || "[]"); } catch {}
        // Filtrar solo las camadas que NO fueron borradas explícitamente
        var pendCierreFull = cierresPendientes(ownerEmail);
        var filteredRows = rows.filter(function(r) { return deletedIds.indexOf(r.id) === -1 && pendCierreFull.indexOf(r.id) === -1; });
        var localList = DB.getCamadasList(ownerEmail);
        // Merge por UNIÓN: combina local + remoto sin perder nada (dedupe por id o dia+fecha)
        var delItems = getDeletedItems(ownerEmail);
        // Arrastre de la versión vieja: marcas de borrado que solo existen en ESTE teléfono.
        // Si el registro sigue en la nube y NADIE lo borró allá (ni este teléfono tiene una
        // marca propia pendiente de subir), la marca local es basura vieja: se quita y el
        // registro vuelve a contarse. Así todos los teléfonos dan el mismo número de vivos.
        (function limpiarMarcasViejas() {
          var lista = delItems.registros || [];
          if (!lista.length) return;
          var enNube = {}, borradoEnNube = {};
          rows.forEach(function(r) {
            (r.registros || []).forEach(function(rg) {
              if (!rg || rg.id == null) return;
              if (rg.borrado) borradoEnNube[String(rg.id)] = true; else enNube[String(rg.id)] = true;
            });
          });
          var marcaPropia = {};   // borrado hecho aquí que aún no ha subido
          localList.forEach(function(c) {
            ((c && c.registrosBorrados) || []).forEach(function(rg) { if (rg && rg.id != null) marcaPropia[String(rg.id)] = true; });
          });
          var quedan = lista.filter(function(k) { return !(enNube[k] && !borradoEnNube[k] && !marcaPropia[k]); });
          if (quedan.length === lista.length) return;
          delItems.registros = quedan;
          try { localStorage.setItem("pe_del_items_" + ownerEmail, JSON.stringify(delItems)); } catch (e) {}
        })();
        function unirPorId(localArr, remotoArr, tipoDel) {
          var map = {};
          var borrados = (delItems[tipoDel] || []);
          function poner(item) {
            if (!item) return;
            var key = item.id !== undefined ? String(item.id) : (item.dia + "_" + (item.fecha || ""));
            if (borrados.indexOf(key) !== -1) return; // eliminado por el usuario — no resucitar
            if (!map[key]) { map[key] = item; return; }
            // Conflicto (mismo item local y remoto): gana el de timestamp más reciente.
            // Sin timestamps, gana el LOCAL (entró primero) — protege las ediciones del usuario.
            var tsActual = Number(map[key]._ts || 0), tsNuevo = Number(item._ts || 0);
            if (tsNuevo > tsActual) map[key] = item;
          }
          (localArr || []).forEach(poner);   // LOCAL primero — sus ediciones no se pisan
          (remotoArr || []).forEach(poner);  // remoto solo gana si su _ts es mayor
          return Object.keys(map).map(function(k) { return map[k]; }).sort(function(a, b) {
            return (a.dia !== undefined && b.dia !== undefined) ? a.dia - b.dia : (Number(a.id) || 0) - (Number(b.id) || 0);
          });
        }
        var merged = filteredRows.map(function(r) {
          var local = localList.find(function(c) { return c.id === r.id; });
          return {
            id: r.id,
            nombre: r.nombre,
            galpon: r.galpon,
            fechaInicio: r.fecha_inicio,
            totalPollos: r.total_pollos,
            lbPorPolloEstandar: r.lb_por_pollo_estandar,
            precioVenta: r.precio_venta || 0,
            raza: r.raza || (local && local.raza) || "Ross 308",
            owner: r.owner_email,
            activa: r.activa !== false,
            registros: separarBorrados(soloDeEstaCamada(unirPorId([].concat((local && local.registros) || [], (local && local.registrosBorrados) || []), r.registros, "registros"), r.id)).visibles,
            registrosBorrados: separarBorrados(soloDeEstaCamada(unirPorId([].concat((local && local.registros) || [], (local && local.registrosBorrados) || []), r.registros, "registros"), r.id)).borrados,
            gastos: unirPorId((local && local.gastos), r.gastos, "gastos"),
            ventas: unirPorId((local && local.ventas), r.ventas, "ventas"),
            hecesAnalisis: r.heces_analisis || [],
            supervisorInspecciones: r.supervisor_inspecciones || (local && local.supervisorInspecciones) || [],
            inventarioAlimento: r.inventario_alimento || (local && local.inventarioAlimento) || null,
            sensorId: r.sensor_device_id || r.sensor_id || (local && local.sensorId) || null,
          };
        });
        // Preservar SOLO camadas locales muy recientes que aún no llegaron a Supabase (no borradas)
        var remotosIds = merged.map(function(c) { return c.id; });
        function firmaCamada(c) { return (c.nombre || "") + "|" + (c.galpon || "") + "|" + (c.fechaInicio || ""); }
        var firmasRemotas = merged.map(firmaCamada);
        var ahora = Date.now();
        localList.forEach(function(lc) {
          if (lc.activa === false) return;
          if (remotosIds.indexOf(lc.id) !== -1) return;        // ya está por id
          if (deletedIds.indexOf(lc.id) !== -1) return;        // fue borrada
          if (firmasRemotas.indexOf(firmaCamada(lc)) !== -1) return; // misma camada con otro id -> evitar duplicado
          // Solo preservar si el id contiene timestamp reciente (creada hace < 2 min, aún sincronizando)
          var idTs = 0;
          var m = String(lc.id).match(/cam_(\d+)/);
          if (m) idTs = Number(m[1]);
          var creacionPend = creacionesPendientes(ownerEmail).indexOf(lc.id) !== -1;
          // Vieja y sin creación pendiente → la cerraron o borraron en otro dispositivo: no re-agregar.
          if (!creacionPend && (!idTs || (ahora - idTs) > 120000)) return;
          merged.push(lc);
          if (creacionPend) {
            // Se creó sin internet: subirla completa ahora que hay conexión.
            SB.upsert("camadas", {
              id: lc.id, owner_email: ownerEmail, nombre: lc.nombre, galpon: lc.galpon || "Galpón 1",
              fecha_inicio: lc.fechaInicio, total_pollos: lc.totalPollos,
              lb_por_pollo_estandar: lc.lbPorPolloEstandar || 7.5, precio_venta: lc.precioVenta || 0,
              raza: lc.raza || "Ross 308", activa: true,
              registros: (lc.registros || []).concat(lc.registrosBorrados || []), gastos: lc.gastos || [], ventas: lc.ventas || [],
            }).then(function(res) { if (res !== null) quitarCreacionPendiente(ownerEmail, lc.id); }).catch(function() {});
          }
        });
        // Guarda el resultado final en estado/localStorage (solo si cambió algo).
        function aplicarYGuardar() {
          var prevJson = "";
          try { prevJson = JSON.stringify(DB.getCamadasList(ownerEmail)); } catch {}
          var newJson = JSON.stringify(merged);
          if (newJson === prevJson) return;
          DB.saveCamadasList(ownerEmail, merged);
          setCamadasList(merged);
          if (merged.length > 0) {
            // CRÍTICO: leer la camada activa REAL desde localStorage, NO del closure
            // (el setInterval congela el valor del primer render y causaba el salto a la camada #1)
            var activaReal = DB.getActivaCamadaId(ownerEmail);
            var existe = merged.some(function(c) { return c.id === activaReal; });
            if (!activaReal || !existe) {
              setActivaCamadaIdState(merged[0].id);
              DB.setActivaCamadaId(ownerEmail, merged[0].id);
            } else {
              // Sincronizar el estado de React con el valor persistido (React ignora si es igual)
              setActivaCamadaIdState(activaReal);
            }
          }
        }
        // Igual que con registros: las inspecciones de supervisor también viven en su propia tabla
        // 'supervisor_inspecciones'. La leemos y rellenamos en cada camada las que falten en su JSONB.
        function mergeSupervisor() {
          SB.select("supervisor_inspecciones", "owner_email=eq." + ownerEmail + "&select=*")
          .then(function(insRows) {
            if (Array.isArray(insRows) && insRows.length) {
              var porCam = {};
              insRows.forEach(function(ir) { if (ir && ir.camada_id) (porCam[ir.camada_id] = porCam[ir.camada_id] || []).push(ir); });
              var borradosSup = (delItems.supervisor || []);
              merged.forEach(function(cam) {
                var tabla = porCam[cam.id];
                if (!tabla) return;
                if (!cam.supervisorInspecciones) cam.supervisorInspecciones = [];
                var ids = {};
                cam.supervisorInspecciones.forEach(function(s) { ids[s.id] = true; });
                tabla.forEach(function(ir) {
                  if (ids[ir.id] || borradosSup.indexOf(ir.id) !== -1) return; // ya está o fue borrada
                  ids[ir.id] = true;
                  cam.supervisorInspecciones.push({
                    id: ir.id, fecha: ir.fecha, supervisor: ir.supervisor, granja: ir.granja,
                    lote: ir.lote, edadLote: ir.edad_lote, respuestas: ir.respuestas || {},
                    observaciones: ir.observaciones, conteoReferencia: ir.conteo_referencia,
                    conteos: (ir.respuestas && ir.respuestas._conteos) || null,
                    puntaje: ir.puntaje, pct: ir.pct, rojas: ir.rojas, estado: ir.estado,
                  });
                });
                cam.supervisorInspecciones.sort(function(a, b) { return new Date(a.fecha || 0) - new Date(b.fecha || 0); });
              });
            }
            aplicarYGuardar();
          })
          .catch(function() { aplicarYGuardar(); }); // si la tabla no existe/no se puede leer, guarda lo demás
        }
        // Red de seguridad: los registros también viven en la tabla 'registros' (guardado
        // por-fila, más confiable que el bloque JSONB completo de la camada). Traemos esa tabla
        // y rellenamos en cada camada los días que falten en su JSONB. Así el admin SIEMPRE ve
        // los registros del empleado aunque el upsert del bloque grande haya fallado.
        SB.select("registros", "owner_email=eq." + ownerEmail + "&select=id,camada_id,dia,fecha,vivos,mortalidad,peso_muestra,estado_cama,estado_heces,suplemento,notas,registrado_por,created_at")
        .then(function(regRows) {
          if (Array.isArray(regRows) && regRows.length) {
            var porCamada = {};
            regRows.forEach(function(rr) {
              if (!rr || !rr.camada_id) return;
              (porCamada[rr.camada_id] = porCamada[rr.camada_id] || []).push(rr);
            });
            var borr = (delItems.registros || []);
            merged.forEach(function(cam) {
              var tabla = porCamada[cam.id];
              if (!tabla) return;
              if (!cam.registros) cam.registros = [];
              // ids ya presentes en el JSONB (registros nuevos) y CONTEO de viejos (sin id) por día.
              var existIds = {}, countDF = {};
              (cam.registrosBorrados || []).forEach(function(rg) { if (rg && rg.id) existIds[rg.id] = true; });
              cam.registros.forEach(function(rg) {
                if (rg.id) existIds[rg.id] = true;
                else { var k = rg.dia + "_" + (rg.fecha || ""); countDF[k] = (countDF[k] || 0) + 1; }
              });
              tabla.forEach(function(rr) {
                if (rr.id && existIds[rr.id]) return;            // ya está (mismo id)
                if (rr.id && borr.indexOf(rr.id) !== -1) return; // borrado por id
                var df = rr.dia + "_" + (rr.fecha || "");
                if (borr.indexOf(df) !== -1) return;             // borrado (formato viejo dia_fecha)
                // Si hay registros VIEJOS sin id para este día, asumimos que esta fila YA está
                // representada por uno de ellos (conserva su foto): la "consumimos" y no duplicamos.
                if ((countDF[df] || 0) > 0) { countDF[df]--; if (rr.id) existIds[rr.id] = true; return; }
                // Fila EXTRA (varios registros el mismo día) → agregar.
                if (rr.id) existIds[rr.id] = true;
                cam.registros.push({
                  id: rr.id, dia: rr.dia, fecha: rr.fecha, vivos: rr.vivos, mortalidad: rr.mortalidad,
                  pesoMuestra: rr.peso_muestra, estadoCama: rr.estado_cama, estadoHeces: rr.estado_heces,
                  suplemento: rr.suplemento, notas: rr.notas, registradoPor: rr.registrado_por || null, fotoHeces: null, camadaId: cam.id,
                  _ts: rr.created_at ? new Date(rr.created_at).getTime() : undefined,
                });
              });
              cam.registros.sort(function(a, b) {
                if ((a.dia || 0) !== (b.dia || 0)) return (a.dia || 0) - (b.dia || 0);
                return (a._ts || 0) - (b._ts || 0); // mismo día → por hora
              });
            });
          }
          mergeSupervisor();
        })
        .catch(function() { mergeSupervisor(); }); // si la tabla de registros falla, sigue con supervisor
      }).catch(function() {});
    }

    // Pre-chequeo LIVIANO: descarga solo id + updated_at (unos bytes) y compara con la última
    // "firma" guardada. Si nada cambió, NO descarga las camadas completas → ahorra muchísimo
    // ancho de banda a escala. Si la columna updated_at no existe todavía, cae al comportamiento
    // normal (descarga completa), así que es 100% compatible aunque no se haya corrido el SQL.
    function loadCamadas(ownerEmail, esDeFondo) {
      // Un refresco DE FONDO nunca puede correr mientras el usuario está en medio de algo
      // (formulario abierto, escribiendo, ventana abierta). Los refrescos disparados por
      // el usuario (guardar/eliminar/crear/cerrar) sí corren siempre.
      if (esDeFondo && estaOcupado()) return;
      SB.select("camadas", "owner_email=eq." + ownerEmail + "&activa=eq.true&select=id,updated_at")
      .then(function(rows) {
        if (!rows || rows.error || !Array.isArray(rows)) { doFullLoad(ownerEmail); return; }
        var pendCierre = cierresPendientes(ownerEmail);
        if (pendCierre.length) {
          var idsActivos = rows.map(function(r) { return r.id; });
          pendCierre.forEach(function(id) {
            if (idsActivos.indexOf(id) !== -1) cerrarCamadaEnNube(ownerEmail, id);   // la nube aún la tiene activa
            else quitarCierrePendiente(ownerEmail, id);                              // ya está cerrada allí
          });
        }
        var allHaveTs = rows.length === 0 || rows.every(function(r) { return r.updated_at; });
        if (!allHaveTs) { doFullLoad(ownerEmail); return; } // sin updated_at → comportamiento normal
        var sig = rows.map(function(r) { return r.id + ":" + r.updated_at; }).sort().join("|");
        var lastSig = null, lastFull = 0;
        try { lastSig = localStorage.getItem("pe_camsig_" + ownerEmail); } catch (e) {}
        try { lastFull = Number(localStorage.getItem("pe_lastfull_" + ownerEmail) || 0); } catch (e) {}
        var stale = (Date.now() - lastFull) > 480000; // red de seguridad: refresco completo cada 8 min
        if (sig === lastSig && !stale) return;         // nada cambió → no descargar todo
        try { localStorage.setItem("pe_camsig_" + ownerEmail, sig); } catch (e) {}
        doFullLoad(ownerEmail);
      })
      .catch(function() { doFullLoad(ownerEmail); }); // ante cualquier duda, descarga normal
    }
    loadCamadasRef.current = loadCamadas;   // para poder refrescar desde los botones de la app

    if (session.rol === "empleado") {
      // Para empleados: obtener ownerEmail desde Supabase si no está en sesión
      if (session.ownerEmail) {
        loadCamadas(session.ownerEmail);
      } else {
        SB.select("usuarios", "email=eq." + session.email)
        .then(function(rows) {
          if (rows && rows[0] && rows[0].owner_email) {
            loadCamadas(rows[0].owner_email);
          }
        }).catch(function() {});
      }
    } else {
      loadCamadas(session.ownerEmail);
    }

    // Recargar de Supabase cuando la app vuelve al primer plano (cambios de empleados/otros dispositivos)
    function onVisible() {
      if (document.visibilityState === "visible" && session.ownerEmail) {
        loadCamadas(session.ownerEmail, true);
      }
    }
    document.addEventListener("visibilitychange", onVisible);

    // Red de seguridad: refresco de fondo LENTO (3 min) solo para traer lo que hicieron
    // los empleados desde otro dispositivo. Antes corría cada 30 s e interrumpía al usuario.
    var refreshInterval = setInterval(function() {
      if (document.visibilityState === "visible" && session.ownerEmail) {
        loadCamadas(session.ownerEmail, true);
      }
    }, 180000);

    return function() {
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(refreshInterval);
    };
  }, [session.ownerEmail]);

  // --- User settings ---
  const [userSettings, setUserSettingsState] = useState(() => {
    const s = DB.getSettings(session.ownerEmail);
    const st = { idioma: IDIOMA_APP, unidadPeso: "lb", moneda: "DOP", tema: "gris", fasesAlimento: 3, ...s };
    // Valor viejo guardado como símbolo ("RD$") → código ("DOP"): sin esto el selector de
    // moneda en Ajustes quedaba sin opción marcada y parecía que "no funcionaba".
    if (st.moneda === "RD$") st.moneda = "DOP";
    return st;
  });
  // Cambios que el granjero hizo a mano en las fases de alimento de ESTA camada
  // (desde qué día, hasta qué día y cuántos sacos). Viven en los ajustes de la granja.
  const fasesPersonal = (camada && userSettings.fasesPorCamada && userSettings.fasesPorCamada[camada.id]) || null;

  function saveSettings(s) {
    setUserSettingsState(s);
    DB.saveSettings(session.ownerEmail, s);
    // Persistir en Supabase para que no se pierda entre dispositivos/sesiones
    SB.upsert("user_settings", {
      email: session.ownerEmail,
      ajustes: s,
    }).catch(function() {});
  }

  // --- Notificaciones push (por dispositivo) ---
  const pushSoportado = typeof window !== "undefined" && "Notification" in window && "PushManager" in window && "serviceWorker" in navigator;
  const [pushPrefs, setPushPrefs] = useState(function() {
    try { var raw = localStorage.getItem("pe_push_" + session.email); return raw ? JSON.parse(raw) : null; } catch (e) { return null; }
  });
  const [pushBusy, setPushBusy] = useState(false);
  function savePushLocal(prefs) {
    setPushPrefs(prefs);
    try {
      if (prefs) localStorage.setItem("pe_push_" + session.email, JSON.stringify(prefs));
      else localStorage.removeItem("pe_push_" + session.email);
    } catch (e) {}
  }
  async function activarPush(prefs) {
    if (!pushSoportado) { alert(tr("Este dispositivo no soporta notificaciones push.")); return; }
    setPushBusy(true);
    try {
      var perm = await Notification.requestPermission();
      if (perm !== "granted") { alert(tr("Debes permitir las notificaciones en tu teléfono para recibir avisos.")); return; }
      var reg = await navigator.serviceWorker.ready;
      var sub = await reg.pushManager.getSubscription();
      if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) });
      var r = await fetch("/api/save-push", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: session.email, ownerEmail: session.ownerEmail || session.email, subscription: sub.toJSON(), prefs: prefs }),
      });
      if (!r.ok) throw new Error("no se pudo guardar en el servidor");
      savePushLocal(prefs);
    } catch (e) {
      alert(tr("No se pudieron activar las notificaciones: ") + e.message);
    } finally { setPushBusy(false); }
  }
  async function desactivarPush() {
    setPushBusy(true);
    try {
      var reg = await navigator.serviceWorker.ready;
      var sub = await reg.pushManager.getSubscription();
      if (sub) {
        fetch("/api/save-push", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ remove: true, subscription: sub.toJSON() }),
        }).catch(function() {});
        await sub.unsubscribe();
      }
    } catch (e) {}
    savePushLocal(null);
    setPushBusy(false);
  }
  function togglePushPref(key) {
    var cur = pushPrefs || PUSH_PREFS_DEFAULT;
    var next = { ...cur, [key]: cur[key] === false };
    activarPush(next);
  }

  // Cargar ajustes (logo, nombre granja, etc) desde Supabase al iniciar
  useEffect(function() {
    SB.select("user_settings", "email=eq." + encodeURIComponent(session.ownerEmail)).then(function(rows) {
      if (rows && rows[0] && rows[0].ajustes) {
        var remoto = rows[0].ajustes;
        // Normalizar moneda legada guardada como símbolo en la nube
        if (remoto.moneda === "RD$") remoto.moneda = "DOP";
        setUserSettingsState(function(prev) { return { ...prev, ...remoto }; });
        DB.saveSettings(session.ownerEmail, remoto);
      }
      // Inventario de la GRANJA (compartido por todas las camadas)
      if (rows && rows[0] && rows[0].inventario) {
        setInventarioGranja(rows[0].inventario);
        try { localStorage.setItem("pe_inv_" + session.ownerEmail, JSON.stringify(rows[0].inventario)); } catch (e) {}
      }
    }).catch(function() {});
  }, [session.ownerEmail]);

  // --- Calendario del usuario (cargado en MainApp para que el dashboard lo use) ---
  const calStorageKey = "pe_calendario_" + session.ownerEmail;
  const [calendarioUsuario, setCalendarioUsuario] = useState(() => {
    try { const saved = localStorage.getItem(calStorageKey); return saved ? JSON.parse(saved) : CALENDARIO_OFICIAL; }
    catch { return CALENDARIO_OFICIAL; }
  });
  useEffect(function() {
    SB.select("user_settings", "email=eq." + encodeURIComponent(session.ownerEmail)).then(function(rows) {
      if (rows && rows[0] && rows[0].calendario) {
        var remoto = rows[0].calendario;
        setCalendarioUsuario(remoto);
        try { localStorage.setItem(calStorageKey, JSON.stringify(remoto)); } catch {}
      }
    }).catch(function() {});
  }, [session.ownerEmail]);

  // Reactive color theme based on user settings
  const C = { ...C_BASE, ...(TEMAS[userSettings.tema] || TEMAS.gris) };
  const t = useT(userSettings.idioma || "es");
  fijarIdioma(userSettings.idioma || "es");   // todo lo que dibuje debajo sale en este idioma
  fijarLocaleNumeros(userSettings.paisFiscal, userSettings.idioma || "es");   // y los números con el formato de su país

  // Currency symbol — usa el mapa GLOBAL (una sola fuente para app, facturas y PDFs)
  const $ = monedaSimbolo(userSettings.moneda);

  const [registroHoy, setRegistroHoy] = useState({ mortalidad: "", pesoMuestra: "", estadoCama: "", estadoHeces: "", suplemento: "", suplementos: [], notas: "", fotoHeces: null, alimentoFase: "", alimentoSacos: "" });
  // Aviso cuando se intenta guardar sin haber puesto el peso del día.
  const [pedirPeso, setPedirPeso] = useState(false);
  // ¿Cuántos pollos se subieron a la balanza? 1, 10 o 100. Cambia SOLO la lista de pesos.
  const [pesoModo, setPesoModo] = useState(1);
  const registroHoyNotasRef = React.useRef("");
  const [savedOk, setSavedOk] = useState(false);
  // --- Módulo Supervisor (pestañas dentro de Registro Diario) ---
  const [registroSubTab, setRegistroSubTab] = useState("diario");
  const [supervisorForm, setSupervisorForm] = useState({ answers: {}, observaciones: "", conteoReferencia: "", conteos: {}, inventario: {} });
  const [supervisorGuardado, setSupervisorGuardado] = useState(false);
  // 🔍 Inspecciones de supervisor a nivel GRANJA: son un registro general (no de una camada),
  // así que el historial se lee de la tabla por dueño y se ve igual desde cualquier camada.
  const [supervisorTabla, setSupervisorTabla] = useState([]);
  useEffect(function() {
    SB.select("supervisor_inspecciones", "owner_email=eq." + encodeURIComponent(session.ownerEmail) + "&select=*&order=fecha.asc&limit=200")
      .then(function(rows) {
        if (!Array.isArray(rows)) return;
        setSupervisorTabla(rows.map(function(ir) {
          return {
            id: ir.id, fecha: ir.fecha, supervisor: ir.supervisor, granja: ir.granja,
            lote: ir.lote, edadLote: ir.edad_lote, respuestas: ir.respuestas || {},
            observaciones: ir.observaciones, conteoReferencia: ir.conteo_referencia,
                    conteos: (ir.respuestas && ir.respuestas._conteos) || null,
            puntaje: ir.puntaje, pct: ir.pct, rojas: ir.rojas, estado: ir.estado,
          };
        }));
      }).catch(function() {});
  }, [session.ownerEmail]);
  // Lista combinada: tabla (fuente confiable) + lo que viva en el JSONB de CUALQUIER camada
  // (inspecciones viejas de antes de la tabla), sin duplicados y sin las borradas.
  const supervisorGranja = (function() {
    var borrados = (getDeletedItems(session.ownerEmail).supervisor || []);
    var vistos = {};
    var lista = [];
    supervisorTabla.forEach(function(s) {
      if (!s || vistos[s.id] || borrados.indexOf(String(s.id)) !== -1) return;
      vistos[s.id] = true; lista.push(s);
    });
    camadasList.forEach(function(c) {
      (c.supervisorInspecciones || []).forEach(function(s) {
        if (!s || vistos[s.id] || borrados.indexOf(String(s.id)) !== -1) return;
        vistos[s.id] = true; lista.push(s);
      });
    });
    lista.sort(function(a, b) { return new Date(a.fecha || 0) - new Date(b.fecha || 0); });
    return lista;
  })();
  function guardarInspeccionSupervisor(calc) {
    if (!camada || calc.respondidas === 0) return;
    var insp = {
      id: "insp_" + Date.now(),
      fecha: new Date().toISOString(),
      supervisor: session.nombre || session.email,
      granja: (userSettings && userSettings.nombreGranja) || "",
      lote: camada.nombre,
      edadLote: diaActual,
      respuestas: Object.assign({}, supervisorForm.answers),
      observaciones: supervisorForm.observaciones || "",
      // Conteo de referencia POR CAMADA (solo informativo — nunca toca el cálculo de vivos)
      conteos: (function() {
        var lista = [];
        camadasList.filter(function(c) { return c.activa !== false; }).forEach(function(c) {
          var v = supervisorForm.conteos && supervisorForm.conteos[c.id];
          if (v !== "" && v != null) lista.push({ camadaId: c.id, nombre: c.nombre, conteo: Number(v) || 0 });
        });
        return lista.length ? lista : null;
      })(),
      conteoReferencia: (supervisorForm.conteoReferencia !== "" && supervisorForm.conteoReferencia != null) ? Number(supervisorForm.conteoReferencia) : null,
      puntaje: calc.puntaje,
      pct: calc.pct,
      rojas: calc.rojas,
      estado: calc.estado.label,
    };
    // Compatibilidad: la columna int conteo_referencia (y vistas viejas) guarda el TOTAL
    if (insp.conteos && insp.conteos.length) {
      insp.conteoReferencia = insp.conteos.reduce(function(a, x) { return a + (Number(x.conteo) || 0); }, 0);
    }
    // Mostrarla al instante en el historial de la GRANJA (la tabla es la fuente de verdad)
    setSupervisorTabla(function(prev) { return prev.concat([insp]); });
    var lista = (camada.supervisorInspecciones || []).concat([insp]).slice(-50);
    var updated = Object.assign({}, camada, { supervisorInspecciones: lista });
    setCamadasList(function (prev) { return prev.map(function (c) { return c.id === updated.id ? updated : c; }); });
    DB.saveCamadaById(session.ownerEmail, updated);
    // Supabase (compatibilidad): sigue guardando el bloque JSONB en la camada.
    SB.update("camadas", { id: updated.id }, { supervisor_inspecciones: lista }).catch(function () {});
    // GUARDADO DEFINITIVO: cada inspección se inserta como su PROPIA fila en la tabla
    // 'supervisor_inspecciones' (confiable, no depende del bloque grande). Si la tabla no existe
    // aún, el .catch lo ignora sin romper nada.
    var sbCli = session.token ? SB.withToken(session.token) : SB;
    sbCli.insert("supervisor_inspecciones", {
      id: insp.id,
      camada_id: camada.id,
      owner_email: session.ownerEmail,
      fecha: insp.fecha,
      supervisor: insp.supervisor,
      granja: insp.granja || null,
      lote: insp.lote || null,
      edad_lote: insp.edadLote,
      // Los conteos por camada viajan DENTRO del jsonb respuestas (clave _conteos) para
      // no requerir una columna nueva; la lectura los extrae de ahí.
      respuestas: Object.assign({}, insp.respuestas || {}, insp.conteos ? { _conteos: insp.conteos } : {}),
      observaciones: insp.observaciones || null,
      conteo_referencia: insp.conteoReferencia,
      puntaje: insp.puntaje,
      pct: insp.pct,
      rojas: insp.rojas,
      estado: insp.estado || null,
    }).catch(function () {});
    // Tocar updated_at de la camada (PATCH liviano) para que el admin lo detecte en ~30s.
    SB.update("camadas", { id: camada.id }, { updated_at: new Date().toISOString() }).catch(function () {});
    // 📦 ACTUALIZAR EL INVENTARIO REAL con las cantidades que escribió el supervisor.
    // Solo se tocan los renglones que llenó; los vacíos conservan su valor anterior.
    aplicarInventarioSupervisor(supervisorForm.inventario);
    // Reiniciar el formulario y mostrar confirmación.
    setSupervisorForm({ answers: {}, observaciones: "", conteoReferencia: "", conteos: {}, inventario: {} });
    setSupervisorGuardado(true);
    setTimeout(function () { setSupervisorGuardado(false); }, 2500);
  }
  const [menuOpen, setMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // --- Panel del dueño de la app (config GLOBAL: prueba gratis on/off + 7/14 días) ---
  const esAdminApp = ADMIN_EMAILS.indexOf(session.email) !== -1;
  const [adminCfg, setAdminCfg] = useState(null);
  useEffect(function () {
    if (!settingsOpen || !esAdminApp) return;
    SB.select("app_config", "key=in.(trial_enabled,trial_days)").then(function (rows) {
      var en = true, d = 7;
      (Array.isArray(rows) ? rows : []).forEach(function (r) {
        if (r.key === "trial_enabled") en = r.value !== "false";
        if (r.key === "trial_days" && Number(r.value) > 0) d = Number(r.value);
      });
      setAdminCfg({ enabled: en, days: d });
    }).catch(function () { setAdminCfg({ enabled: true, days: 7 }); });
  }, [settingsOpen]);
  // Guarda una llave de app_config (update si existe; insert si es nueva — no asume llave primaria).
  function saveAppConfig(key, value) {
    SB.update("app_config", { key: key }, { value: String(value) }).then(function (r) {
      if (!r || r.error || (Array.isArray(r) && r.length === 0)) {
        SB.insert("app_config", { key: key, value: String(value) }).catch(function () {});
      }
    }).catch(function () {
      SB.insert("app_config", { key: key, value: String(value) }).catch(function () {});
    });
  }
  const [historialOpen, setHistorialOpen] = useState(false);
  const [cierreOpen, setCierreOpen] = useState(false);
  const [inventarioOpen, setInventarioOpen] = useState(false);
  // --- Inventario de la GRANJA (uno solo, compartido por todas las camadas) ---
  // Vive en user_settings.inventario (por dueño), con copia local para offline.
  const [inventarioGranja, setInventarioGranja] = useState(function () {
    try { return JSON.parse(localStorage.getItem("pe_inv_" + session.ownerEmail) || "null"); } catch (e) { return null; }
  });
  function saveInventarioGranja(inv) {
    setInventarioGranja(inv);
    try { localStorage.setItem("pe_inv_" + session.ownerEmail, JSON.stringify(inv)); } catch (e) {}
    // Guardado AISLADO: si la columna aún no existe, solo falla esto (queda local) sin romper nada.
    SB.upsert("user_settings", { email: session.ownerEmail, inventario: inv }).catch(function () {});
  }
  // 📦 Resta del inventario de la granja TODO lo que se gastó hoy: los sacos de alimento y
  // los demás insumos (vitaminas, paja, vinagre...). Se hace en una sola pasada y se guarda
  // una sola vez; si se guardara por separado, el segundo guardado pisaría al primero.
  function descontarGastosInventario(faseKey, sacos) {
    var usados = Number(sacos) || 0;
    if (!faseKey || usados <= 0) return;
    var base = inventarioGranja || (camada && camada.inventarioAlimento) || {};
    var next = Object.assign({}, base);
    next[faseKey] = Math.max(0, (Number(next[faseKey]) || 0) - usados);
    next.actualizado = new Date().toISOString();
    saveInventarioGranja(next);
  }
  // 📦 Aplica al inventario REAL las cantidades que el supervisor escribió en su reporte.
  // Regla: SOLO se actualizan los renglones que llenó. Un campo vacío conserva su valor anterior
  // (nunca pone 0 por descuido). Devuelve true si algo cambió.
  function aplicarInventarioSupervisor(invForm) {
    if (!invForm) return false;
    var base = inventarioGranja || (camada && camada.inventarioAlimento) || {};
    var next = Object.assign({}, base);
    var otros = (Array.isArray(base.otros) ? base.otros : []).map(function (o) { return Object.assign({}, o); });
    var cambio = false;
    Object.keys(invForm).forEach(function (k) {
      var raw = invForm[k];
      if (raw === "" || raw == null) return;          // vacío = no tocar
      var n = Number(raw);
      if (!isFinite(n) || n < 0) return;              // valor inválido = ignorar
      if (k.indexOf("otro_") === 0) {
        var i = Number(k.slice(5));
        if (otros[i]) { otros[i].cantidad = n; cambio = true; }
      } else if (k === "agua") {
        next.agua = Math.max(0, Math.min(100, n));    // el agua es porcentaje (0–100)
        cambio = true;
      } else {
        next[k] = n;
        cambio = true;
      }
    });
    if (!cambio) return false;
    next.otros = otros;
    next.actualizado = new Date().toISOString();
    next.actualizadoPor = session.nombre || session.email;
    saveInventarioGranja(next);
    return true;
  }
  // --- SENSOR AMBIENTAL de la granja (aparatos ESP32 vinculados por owner_email) ---
  // Los usuarios SIN sensor no ven nada nuevo: toda la UI se gatea por sensoresGranja.length>0.
  // La Pollo Box arranca con lo ÚLTIMO que se vio (guardado en el teléfono) y se actualiza
  // por detrás. Antes empezaba vacía: al abrir la app la tarjeta salía en blanco varios
  // segundos hasta que llegaba la primera lectura. La propia tarjeta ya dice "hace X min",
  // así que mostrar datos viejos no engaña a nadie.
  const [sensoresGranja, setSensoresGranja] = useState(function () {
    try { var v = JSON.parse(localStorage.getItem("pe_cajas_" + session.ownerEmail) || "[]"); return Array.isArray(v) ? v : []; } catch (e) { return []; }
  });
  const [lecturasSensor, setLecturasSensor] = useState(function () {   // { [sensor_id]: [lecturas...] }
    try { var v = JSON.parse(localStorage.getItem("pe_lecturas_" + session.ownerEmail) || "{}"); return (v && typeof v === "object") ? v : {}; } catch (e) { return {}; }
  });
  // Cada vez que llega algo nuevo, se guarda para la próxima vez que se abra la app.
  useEffect(function () {
    try { localStorage.setItem("pe_cajas_" + session.ownerEmail, JSON.stringify(sensoresGranja || [])); } catch (e) {}
  }, [sensoresGranja]);
  useEffect(function () {
    try { localStorage.setItem("pe_lecturas_" + session.ownerEmail, JSON.stringify(lecturasSensor || {})); } catch (e) {}
  }, [lecturasSensor]);
  const [miSensorOpen, setMiSensorOpen] = useState(false);
  const [miGranjaOpen, setMiGranjaOpen] = useState(false);   // Mi Granja y Facturación
  const [ambientePageOpen, setAmbientePageOpen] = useState(false);
  const [historialAmbOpen, setHistorialAmbOpen] = useState(false);
  const [controlesOpen, setControlesOpen] = useState(false);
  const [controlAbierto, setControlAbierto] = useState(null);   // control que se abre al tocarlo desde su Pollo Box
  // Mensaje "¿el galpón está limpio? → calibrar" que aparece al crear/cerrar una camada CON sensor.
  const [calibrarPrompt, setCalibrarPrompt] = useState(null);   // { sensorId, nombre } | null
  // Latido para que la tarjeta/página Ambiente se refresquen solas cada 30 s:
  // en modo demostración regenera los datos simulados; con sensor real solo re-dibuja
  // el "hace X segundos" (los datos los trae el effect de lecturas).
  const [ambTick, setAmbTick] = useState(0);
  const ambVisibleRef = React.useRef(false);
  useEffect(function () {
    var iv = setInterval(function () {
      // Solo late si hay tarjeta Ambiente en pantalla. Antes corría para TODOS los usuarios
      // cada 30 s y ese latido era lo que re-dibujaba la app y cerraba lo que estabas haciendo.
      if (!ambVisibleRef.current) return;
      if (estaOcupado()) return;
      if (document.visibilityState === "visible") setAmbTick(function (t) { return t + 1; });
    }, 60000);
    return function () { clearInterval(iv); };
  }, []);
  function cargarSensores() {
    if (!session.ownerEmail) return;
    tablaAparatos().then(function (tabla) {
      var orden = tabla === "sensor_devices" ? "id.asc" : "creado.asc";
      return SB.select(tabla, "owner_email=eq." + encodeURIComponent(session.ownerEmail) + "&select=*&order=" + orden)
        .then(function (rows) {
          if (!Array.isArray(rows) || rows.error) return;
          setSensoresGranja(function (prev) {
            // Misma lista que ya teníamos → devolvemos la anterior y React no re-dibuja.
            try { if (JSON.stringify(prev) === JSON.stringify(rows)) return prev; } catch (e) {}
            return rows;
          });
        });
    }).catch(function () {});
  }
  // Después de ELIMINAR un aparato: se suelta el vínculo en las camadas que lo usaban
  // (si no, quedaría apuntando a un sensor que ya no existe) y se refresca la lista.
  function limpiarSensorEliminado(sensorId) {
    setCamadasList(function (prev) {
      return prev.map(function (c) {
        if (c.sensorId !== sensorId) return c;
        var act = Object.assign({}, c, { sensorId: null });
        DB.saveCamadaById(session.ownerEmail, act);
        return act;
      });
    });
    setLecturasSensor(function (prev) { var n = Object.assign({}, prev); delete n[sensorId]; return n; });
    cargarSensores();
  }
  // Muestra el mensaje de calibración SOLO si ese vínculo tiene una caja sensor conectada.
  function pedirCalibrarSiSensor(sensorId) {
    if (!sensorId) return;
    var s = sensoresGranja.find(function (x) { return x.id === sensorId; });
    setCalibrarPrompt({ sensorId: sensorId, nombre: (s && s.nombre) || "Galpón" });
  }
  function confirmarCalibracion() {
    if (!calibrarPrompt) return;
    // Se escribe en la MISMA tabla de la que se leyeron los aparatos (sensor_devices o
    // sensores). Antes estaba fija en "sensores" y la orden nunca llegaba a la caja.
    tablaAparatos()
      .then(function (tabla) { return SB.update(tabla, { id: calibrarPrompt.sensorId }, { solicitud_calibracion: true }); })
      .then(function () { cargarSensores(); }).catch(function () {});
    setCalibrarPrompt(null);
  }
  function cargarLecturasSensor(sensorId) {
    if (!sensorId) return;
    SB.select("lecturas_sensor", columnaLectura() + "=eq." + sensorId + "&select=temp_c,humedad,nh3_ratio,mq_raw,ts&order=ts.desc&limit=60")
      .then(function (rows) {
        if (!Array.isArray(rows) || rows.error) return;
        setLecturasSensor(function (prev) {
          // Si no llegó ninguna lectura nueva, devolvemos el mismo objeto: React no
          // re-dibuja nada y la pantalla del usuario queda intacta.
          var ant = prev[sensorId];
          if (ant && ant.length === rows.length && (!rows[0] || (ant[0] && ant[0].ts === rows[0].ts))) return prev;
          // La caja apagada (o lecturas viejas ya limpiadas del servidor) devuelve una lista
          // VACÍA. En ese caso NO se borra lo que ya teníamos: así la tarjeta sigue mostrando
          // la última lectura conocida con el cartel de OFFLINE, en vez de quedarse en blanco.
          if (rows.length === 0 && ant && ant.length) return prev;
          var n = Object.assign({}, prev); n[sensorId] = rows; return n;
        });
      }).catch(function () {});
  }
  useEffect(function () { cargarSensores(); }, [session.ownerEmail]);
  // Lecturas del sensor de la camada activa (tarjeta Ambiente).
  // AL ABRIR LA APP (y al volver a ella) NO se espera al siguiente ciclo: se dispara una
  // RÁFAGA de intentos durante el primer medio minuto. Así, si la caja acaba de encenderse
  // y sube su primer reporte en esos segundos, la app lo agarra enseguida en vez de
  // mostrar "sin señal" hasta el próximo refresco.
  useEffect(function () {
    var sid = camada && camada.sensorId ? camada.sensorId : null;
    if (!sid) return;
    var vivo = true;
    var timers = [];
    function rafaga() {
      timers.forEach(clearTimeout);
      timers = [];
      // 4 intentos: al instante, a los 4 s, a los 12 s y a los 30 s.
      [0, 4000, 12000, 30000].forEach(function (ms, i) {
        timers.push(setTimeout(function () {
          if (!vivo || document.visibilityState !== "visible") return;
          if (i === 0) cargarSensores();   // la lista de aparatos solo hace falta una vez
          cargarLecturasSensor(sid);
        }, ms));
      });
    }
    rafaga();
    function alVolver() { if (document.visibilityState === "visible") rafaga(); }
    document.addEventListener("visibilitychange", alVolver);
    var iv = setInterval(function () {
      if (estaOcupado()) return;   // el usuario está en medio de algo → no tocarle la pantalla
      if (document.visibilityState === "visible") { cargarSensores(); cargarLecturasSensor(sid); }
    }, 60000);
    return function () {
      vivo = false;
      timers.forEach(clearTimeout);
      document.removeEventListener("visibilitychange", alVolver);
      clearInterval(iv);
    };
  }, [camada && camada.sensorId]);
  // (Se quitó el vínculo automático de la caja con la camada — 2026-10-05, pedido de Andrés.)
  // Antes, si la granja tenía una Pollo Box suelta, la app la vinculaba SOLA a la camada
  // activa en cada carga. Eso hacía que la tarjeta de Ambiente apareciera en el Inicio
  // aunque el granjero no hubiera vinculado nada, y volvía a aparecer aunque él la quitara.
  // Ahora la caja se vincula SOLO a mano: al crear la camada o en Mis Camadas → ✏️ Editar.
  // Regla de la tarjeta: se ve si la camada tiene caja vinculada, o si el modo demostración
  // está encendido. Un usuario nuevo, sin caja y sin demo, no ve nada de esto.
  const [registrosHistOpen, setRegistrosHistOpen] = useState(false);
  const [alimentacionOpen, setAlimentacionOpen] = useState(false);
  const [hecesIAOpen, setHecesIAOpen] = useState(false);
  const [hecesPageOpen, setHecesPageOpen] = useState(false);
  const [analisisCamadaOpen, setAnalisisCamadaOpen] = useState(false);
  const [centroSaludOpen, setCentroSaludOpen] = useState(false);
  const [camaraOpen, setCamaraOpen] = useState(false);
  const [misCamadasOpen, setMisCamadasOpen] = useState(false);
  // true = la ventana de Mis Camadas se abre ya con el formulario de camada nueva
  const [misCamadasNueva, setMisCamadasNueva] = useState(false);
  // Eliminar cuenta (requisito Apple App Store)
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  // Con CUALQUIERA de estas ventanas abiertas la app se marca como "ocupada": los
  // refrescos de fondo se quedan esperando y no le mueven la pantalla al usuario.
  usarOcupado(
    settingsOpen || historialOpen || cierreOpen || inventarioOpen || misCamadasOpen ||
    miSensorOpen || controlesOpen || registrosHistOpen || alimentacionOpen ||
    hecesIAOpen || hecesPageOpen || analisisCamadaOpen || centroSaludOpen ||
    camaraOpen || deleteAccountOpen
  );
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deletingAccount, setDeletingAccount] = useState(false);

  function limpiarDatosLocales() {
    try {
      var emails = [session.email, session.ownerEmail].filter(Boolean).map(function(e) { return String(e).toLowerCase(); });
      // Borrar todas las claves del localStorage que pertenezcan a este usuario
      var aBorrar = [];
      for (var i = 0; i < localStorage.length; i++) {
        var k = localStorage.key(i);
        if (!k) continue;
        if (k === "pe_session") { aBorrar.push(k); continue; }
        for (var j = 0; j < emails.length; j++) {
          if (k.toLowerCase().indexOf(emails[j]) !== -1) { aBorrar.push(k); break; }
        }
      }
      aBorrar.forEach(function(k) { try { localStorage.removeItem(k); } catch (e) {} });
      // Quitar al usuario del mapa de usuarios locales
      var users = DB.getUsers();
      emails.forEach(function(e) { if (users[e]) delete users[e]; });
      DB.saveUsers(users);
    } catch (e) {}
  }

  function borrarCuenta() {
    if (deletingAccount) return;
    if (deleteConfirmText.trim().toUpperCase() !== "ELIMINAR") {
      alert(userSettings.idioma === "en" ? 'Type "ELIMINAR" to confirm.' : 'Escribe "ELIMINAR" para confirmar.');
      return;
    }
    setDeletingAccount(true);
    fetch("/api/delete-account", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: session.email, token: session.token }),
    })
      .then(function(r) { return r.json().catch(function() { return {}; }); })
      .then(function(data) {
        if (data && data.error) {
          setDeletingAccount(false);
          alert((userSettings.idioma === "en" ? "Could not delete account: " : "No se pudo eliminar la cuenta: ") + data.error);
          return;
        }
        // Éxito: limpiar todo lo local y cerrar sesión
        limpiarDatosLocales();
        alert(userSettings.idioma === "en"
          ? "Your account has been deleted. Your data was anonymized."
          : "Tu cuenta fue eliminada. Tus datos quedaron anonimizados.");
        onLogout();
      })
      .catch(function() {
        setDeletingAccount(false);
        alert(userSettings.idioma === "en" ? "Connection error. Try again." : "Error de conexión. Intenta de nuevo.");
      });
  }

  const isProp = session.rol === "propietario";
  const permisos = isProp
    ? { verDashboard: true, verInfoCamada: true, verRegistro: true, verCalendario: true, verCostos: true, verVentas: true, editarCamada: true, verHistorialCamadas: true, verHistorialRegistros: true, verEmpleados: true, verMisCamadas: true, verHecesIA: true }
    : Object.assign({}, DEFAULT_PERMISOS, session.permisos || {});

  // Si no hay camadas — mostrar pantalla de bienvenida
  if (!camada) {
    return (
      <div style={{ minHeight: "100vh", background: "#1e1e24", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "'Syne', sans-serif" }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>🐔</div>
        <div style={{ fontSize: 22, fontWeight: 900, color: "#f5c200", marginBottom: 8 }}>{t("bienvenido")}</div>
        <div style={{ fontSize: 14, color: "#888", marginBottom: 32, textAlign: "center" }}>
          {camadasList.length === 0 ? t("cargando") + tr(" camadas...") : t("sinCamadas")}
        </div>
        {isProp && (
          <button onClick={function() {
            var nombre = "Camada #1";
            var galpon = "Galpón 1";
            var totalPollos = 800;
            var fechaInicio = new Date().toISOString().split("T")[0];
            crearNuevaCamada(nombre, galpon, totalPollos, fechaInicio);
          }} style={{ background: "#f5c200", color: "#111", border: "none", borderRadius: 14, padding: "16px 32px", fontSize: 18, fontWeight: 900, cursor: "pointer" }}>
            + {t("crearPrimeraCamada")}
          </button>
        )}
        <button onClick={onLogout} style={{ marginTop: 24, background: "none", border: "none", color: "#666", cursor: "pointer", fontSize: 14 }}>{t("cerrarSesion")}</button>
      </div>
    );
  }

  // saveCamada defined above in multi-camada block

  const diaActual = camada.fechaInicio ? diasDesde(camada.fechaInicio) : 0;

  // --- AMBIENTE: de dónde salen los datos (sensor REAL o modo demostración) ---
  // Regla: si la camada tiene un sensor de verdad, manda el sensor (nunca demo).
  // Si NO hay sensor y el modo demostración está encendido → datos simulados CON sello DEMO.
  // Si no hay sensor y el modo está apagado → no se muestra nada (app idéntica a antes).
  const ambSensorReal = (camada && camada.sensorId)
    ? (sensoresGranja.find(function (s) { return s.id === camada.sensorId; }) || null)
    : null;
  const ambEsDemo = !ambSensorReal && !!userSettings.modoDemo;
  const ambLecturas = ambEsDemo
    ? demoLecturas(diaActual, ambTick)
    : (ambSensorReal ? (lecturasSensor[camada.sensorId] || []) : []);
  const ambSensor = ambSensorReal || (ambEsDemo
    ? { id: "demo", nombre: "Galpon Demo", ultimo_reporte: new Date().toISOString(), r0: 1180, fw_version: "demo" }
    : null);
  const ambUltima = ambLecturas[0] || null;
  // "Sin señal": la última lectura tiene más de 15 minutos (en demo nunca pasa).
  const ambSinSenal = !ambUltima || (sensHaceSeg(ambUltima.ts) == null) || (sensHaceSeg(ambUltima.ts) > 15 * 60);
  const ambVisible = !!ambSensor;
  ambVisibleRef.current = ambVisible;   // el latido de 30 s solo corre si esta tarjeta existe
  const ultimoReg = camada.registros[camada.registros.length - 1];
  // Vivos = total recibido − TODA la mortalidad sumada (no la "foto" del último registro).
  // Así, al borrar/editar cualquier registro, el conteo se recalcula correctamente.
  const totalMortalidad = (camada.registros || []).reduce((a, r) => a + Number(r.mortalidad || 0), 0);
  const vivosPorMortalidad = Math.max(0, camada.totalPollos - totalMortalidad);
  const pollosVendidos = (camada.ventas || []).reduce((a, v) => a + Number(v.pollos || 0), 0);
  const vivosActuales = Math.max(0, vivosPorMortalidad - pollosVendidos);
  const mortalidadAcum = camada.totalPollos - vivosPorMortalidad;
  const pctMort = ((mortalidadAcum / camada.totalPollos) * 100).toFixed(1);
  const costoTotal = (camada.gastos || []).reduce((a, g) => a + Number(g.monto || 0), 0);
  // Peso promedio del lote: SOLO los pesajes de los últimos 3 días. Promediar "los últimos 20
  // pesajes" abarcaba semanas y mezclaba el peso de pollito con el de engorde (en la granja
  // marcaba 1.34 lb cuando los pollos ya pesaban 2.18 lb). En 3 días el pollo sube poco, así
  // que el promedio sigue siendo fiel y de paso suaviza el azar de cuál pollo se pesó.
  // La ventana se cuenta desde el ÚLTIMO DÍA QUE SE PESÓ, no desde hoy: si pasan varios días
  // sin pesar, se sigue enseñando el último dato bueno en vez de dejar el cuadro en blanco.
  const DIAS_PROM_PESO = 3;
  const registrosConPeso = (camada.registros || []).filter(r => r.pesoMuestra && Number(r.pesoMuestra) > 0);
  const diaUltPesaje = registrosConPeso.reduce((a, r) => Math.max(a, Number(r.dia) || 0), -1);
  const pesosRecientes = diaUltPesaje < 0 ? []
    : registrosConPeso.filter(r => (Number(r.dia) || 0) > diaUltPesaje - DIAS_PROM_PESO);
  const pesoPromedioKg = pesosRecientes.length > 0
    ? (pesosRecientes.reduce((a, r) => a + Number(r.pesoMuestra), 0) / pesosRecientes.length).toFixed(3)
    : null;
  const pesoPromedioLb = pesoPromedioKg ? (Number(pesoPromedioKg) * 2.205).toFixed(3) : null;
  // Último peso reportado
  const ultimoPesoReg = registrosConPeso.length > 0 ? registrosConPeso[registrosConPeso.length - 1] : null;
  const ultimoPesoKg = ultimoPesoReg ? Number(ultimoPesoReg.pesoMuestra).toFixed(3) : null;
  const ultimoPesoLb = ultimoPesoKg ? (Number(ultimoPesoKg) * 2.205).toFixed(3) : null;
  const costoPorAve = vivosActuales > 0 ? (costoTotal / vivosActuales).toFixed(2) : 0;
  const pesoEst = ultimoReg?.pesoMuestra ? (ultimoReg.pesoMuestra * vivosActuales * 2.205).toFixed(0) : null;
  const ingresoEst = pesoEst && camada.precioVenta ? (Number(pesoEst) * Number(camada.precioVenta)).toFixed(0) : null;
  const utilidadEst = ingresoEst ? (ingresoEst - costoTotal).toFixed(0) : null;
  const eventoHoy = calendarioUsuario.find(e => e.dia === diaActual);
  const proximosEventos = calendarioUsuario.filter(e => e.dia > diaActual && e.dia <= diaActual + 3 && e.tipo !== "agua");

  // --- FCR y estándares Andrés ---
  // Estándar: 5 lb vivo en 35 días, consumo 8-9 lb alimento/pollo
  const ESTANDAR_PESO_LB = 5.0;        // lb objetivo a día 35
  const ESTANDAR_FCR_MIN = 8.0 / 5.0; // 1.60
  const ESTANDAR_FCR_MAX = 9.0 / 5.0; // 1.80

  // Peso actual en libras por pollo
  // Carne producida, FCR y proyección salen del MISMO promedio reciente. Antes salían del
  // peso del último registro, y desde que el peso es opcional ese registro casi siempre viene
  // sin peso: el panel de eficiencia se quedaba en blanco sin que nadie supiera por qué.
  const pesoActualLb = pesoPromedioLb ? Number(pesoPromedioLb) : null;

  // Curva de peso realista día por día (gramos → libras)
  // Fuente: tabla de crecimiento estándar broiler, meta 5 lb (2270g) en día 35
  const CURVA_PESO_LB = {
     0: 0.088,
     1: 0.09,  2: 0.11,  3: 0.12,  4: 0.14,  5: 0.17,
     6: 0.20,  7: 0.24,  8: 0.30,  9: 0.36, 10: 0.44,
    11: 0.53, 12: 0.63, 13: 0.74, 14: 0.87, 15: 0.99,
    16: 1.15, 17: 1.29, 18: 1.46, 19: 1.63, 20: 1.82,
    21: 2.01, 22: 2.20, 23: 2.41, 24: 2.63, 25: 2.87,
    26: 3.10, 27: 3.34, 28: 3.59, 29: 3.85, 30: 4.10,
    31: 4.35, 32: 4.59, 33: 4.76, 34: 4.89, 35: 5.00,
    36: 5.18, 37: 5.35, 38: 5.52, 39: 5.68, 40: 5.84,
    41: 5.99, 42: 6.14, 43: 6.28, 44: 6.42, 45: 6.55,
    46: 6.67, 47: 6.79, 48: 6.90, 49: 7.01, 50: 7.11,
    51: 7.20, 52: 7.29, 53: 7.37, 54: 7.45, 55: 7.52, 56: 7.59,
  };
  const pesoEsperadoHoy = diaActual >= 0 ? (CURVA_PESO_LB[Math.min(diaActual, 56)] || 7.59) : 0;

  // Alimento consumido total (suma de gastos de alimento convertido a lbs, o estimación)
  const totalAlimentoLb = (camada.gastos || [])
    .filter(g => g.categoria === "alimento")
    .reduce((a, g) => a + Number(g.monto || 0), 0) / 1395 * 100; // precio aprox saco 100lb = RD$1395

  // Carne producida = la que sigue en pie + la que YA SE VENDIÓ.
  // Antes solo contaba la que quedaba viva, así que al vender toda la camada el FCR
  // se quedaba en "sin datos" justo cuando por fin era el número definitivo del lote.
  const lbCarneTotal = pesoActualLb ? pesoActualLb * vivosActuales : 0;
  const lbVendidasFcr = (camada.ventas || []).reduce((a, v) => a + Number(v.libras || 0), 0);
  const lbCarneProducida = lbCarneTotal + lbVendidasFcr;

  // FCR real: lb alimento / lb carne producida
  const fcrReal = totalAlimentoLb > 0 && lbCarneProducida > 0 ? (totalAlimentoLb / lbCarneProducida).toFixed(2) : null;

  // Proyección a día 35
  const pctAvancePeso = pesoActualLb ? ((pesoActualLb / ESTANDAR_PESO_LB) * 100).toFixed(1) : null;
  const proyeccionDia35 = pesoActualLb && diaActual > 0
    ? ((pesoActualLb / diaActual) * 35).toFixed(2) : null;

  // Estado del FCR
  const fcrEstado = fcrReal
    ? fcrReal <= ESTANDAR_FCR_MIN ? "excelente"
      : fcrReal <= ESTANDAR_FCR_MAX ? "bueno"
      : "alto"
    : null;
  const fcrColores = { excelente: C.green, bueno: C.accent, alto: C.danger };

  // 💰 Costo por libra producida: gastos totales ÷ (lb vendidas + lb de carne en pie)
  const lbVendidas = lbVendidasFcr;
  const lbProducidasTotal = lbCarneProducida;
  const costoPorLb = costoTotal > 0 && lbProducidasTotal > 0 ? costoTotal / lbProducidasTotal : null;
  // Precio de venta promedio por lb (de las ventas reales; si no hay, el precio configurado)
  const precioLbProm = lbVendidas > 0
    ? (camada.ventas || []).reduce((a, v) => a + Number(v.libras || 0) * Number(v.precioLb || 0), 0) / lbVendidas
    : (Number(camada.precioVenta) || null);
  const margenLb = costoPorLb !== null && precioLbProm ? precioLbProm - costoPorLb : null;

  // 🐔 Comparación vs estándar de raza — al día del ÚLTIMO pesaje (no al día de hoy,
  // porque el último peso puede ser de hace varios días)
  const compRaza = (() => {
    if (!ultimoPesoReg) return null;
    const diaPesaje = Number(ultimoPesoReg.dia) || 0;
    const estandarLb = CURVA_PESO_LB[Math.min(diaPesaje, 56)] || 7.59;
    const actualLb = Number(ultimoPesoReg.pesoMuestra) * 2.205;
    if (!estandarLb || !actualLb) return null;
    const pct = ((actualLb / estandarLb) - 1) * 100;
    const color = pct >= -5 ? C.green : pct >= -15 ? C.warn : C.danger;
    const emoji = pct >= -5 ? "✅" : pct >= -15 ? "⚠️" : "🔻";
    return { dia: diaPesaje, actual: actualLb.toFixed(2), estandar: estandarLb.toFixed(2), pct: (pct >= 0 ? "+" : "") + pct.toFixed(0), color, emoji };
  })();

  const COND_GRAVES_HECES = ["diarrea_sangre", "diarrea_amarilla", "verdes"];
  const COND_GRAVES_CAMA  = ["muy_humeda", "critica", "reemplazar"];
  const COND_BUENAS_HECES = ["normal"];
  const COND_BUENAS_CAMA  = ["excelente", "buena"];
  function clasificarRegistro(reg) {
    var h = reg.estadoHeces || "", c = reg.estadoCama || "";
    if (COND_GRAVES_HECES.includes(h) || COND_GRAVES_CAMA.includes(c)) return "grave";
    if (COND_BUENAS_HECES.includes(h) && COND_BUENAS_CAMA.includes(c)) return "bueno";
    return "normal";
  }
  // "extra" permite guardar con un dato que se acaba de confirmar en pantalla (el peso),
  // sin esperar a que React refresque el estado.
  function guardarRegistro(extra) {
      if (registroHoyNotasRef.current) { registroHoy.notas = registroHoyNotasRef.current; }
    var datos = Object.assign({}, registroHoy, (extra && !extra.nativeEvent) ? extra : {});
    const vivos = vivosPorMortalidad - Number(datos.mortalidad || 0);
    // id único por registro (UUID) → permite VARIOS registros el mismo día sin que se colapsen.
    const regId = genUUID();
    const nuevoReg = { id: regId, dia: diaActual, fecha: new Date().toISOString().split("T")[0], vivos, mortalidad: Number(datos.mortalidad || 0), pesoMuestra: Number(datos.pesoMuestra || 0), estadoCama: datos.estadoCama, estadoHeces: datos.estadoHeces, suplemento: datos.suplemento, notas: datos.notas, fotoHeces: datos.fotoHeces || null, registradoPor: session.nombre || session.email, registradoPorEmail: session.email, camadaId: camada.id, _ts: Date.now() };
    // Alimento gastado hoy: queda anotado en el registro y se descuenta del inventario.
    var sacosUsados = Number(datos.alimentoSacos || 0);
    if (datos.alimentoFase && sacosUsados > 0) {
      nuevoReg.alimentoFase = datos.alimentoFase;
      nuevoReg.alimentoSacos = sacosUsados;
    }
    // Del inventario solo se descuenta el alimento. Los demás insumos se cuadran a mano una
    // vez por semana desde la pantalla de Inventario, no en el registro de cada día.
    descontarGastosInventario(nuevoReg.alimentoFase, nuevoReg.alimentoSacos);
    saveCamada({ ...camada, registros: [...camada.registros, nuevoReg] });
    // Also save registro to Supabase (tabla 'registros' = guardado por-fila CONFIABLE,
    // sobrevive aunque el upsert del bloque JSONB completo de la camada falle).
    var sbClient = session.token ? SB.withToken(session.token) : SB;
    sbClient.insert("registros", {
      id: regId,
      camada_id: camada.id,
      owner_email: session.ownerEmail,
      dia: nuevoReg.dia,
      fecha: nuevoReg.fecha,
      vivos: nuevoReg.vivos,
      mortalidad: nuevoReg.mortalidad,
      peso_muestra: nuevoReg.pesoMuestra || null,
      estado_cama: nuevoReg.estadoCama || null,
      estado_heces: nuevoReg.estadoHeces || null,
      suplemento: nuevoReg.suplemento || null,
      notas: nuevoReg.notas || null,
      registrado_por: nuevoReg.registradoPor || null,
    }).catch(function() {});
    // Tocar updated_at de la camada (PATCH liviano y confiable). Garantiza que el pre-chequeo
    // del admin detecte el cambio en ~30s aunque el guardado del bloque grande haya fallado.
    SB.update("camadas", { id: camada.id }, { updated_at: new Date().toISOString() }).catch(function() {});
    if (clasificarRegistro(nuevoReg) === "grave") {
      try {
        const alertas = [];
        if (COND_GRAVES_HECES.includes(datos.estadoHeces)) alertas.push("Heces: " + datos.estadoHeces.replace(/_/g, " "));
        if (COND_GRAVES_CAMA.includes(datos.estadoCama)) alertas.push("Cama: " + datos.estadoCama.replace(/_/g, " "));
        const notifs = JSON.parse(localStorage.getItem("pe_notifs_" + session.ownerEmail) || "[]");
        notifs.unshift({ id: Date.now(), mensaje: "⚠️ " + session.nombre + " reportó problema en " + camada.nombre + " (Día " + diaActual + "): " + alertas.join(", "), fecha: new Date().toISOString(), leida: false });
        localStorage.setItem("pe_notifs_" + session.ownerEmail, JSON.stringify(notifs.slice(0, 50)));
      } catch(e) {}
    }
    setRegistroHoy({ mortalidad: "", pesoMuestra: "", estadoCama: "", estadoHeces: "", suplemento: "", suplementos: [], notas: "", fotoHeces: null, alimentoFase: "", alimentoSacos: "" });
    setPedirPeso(false);
    setSavedOk(true); setTimeout(() => setSavedOk(false), 2000);
  }

  const TABS = [
    permisos.verDashboard && { id: "dashboard", label: t("inicio"), icon: "📊" },
    permisos.verRegistro && { id: "diario", label: t("registro"), icon: "📝" },
    permisos.verCalendario && { id: "calendario", label: t("calendarioTab"), icon: "💉" },
    permisos.verCostos && { id: "costos", label: t("costos"), icon: "💰" },
    permisos.verVentas && { id: "ventas", label: t("ventas"), icon: "🐔" },
  ].filter(Boolean);

  const appStyle = `${fonts} * { box-sizing: border-box; margin: 0; padding: 0; } body { background: ${C.bg}; color: ${C.text}; font-family: 'Syne', sans-serif; } input:focus, textarea:focus, select:focus { border-color: ${C.accent} !important; outline: none; } ::-webkit-scrollbar { width: 4px; } ::-webkit-scrollbar-track { background: ${C.bg}; } ::-webkit-scrollbar-thumb { background: ${C.border}; border-radius: 2px; } @keyframes fadeIn { from { opacity:0; transform: translateY(6px); } to { opacity:1; transform: translateY(0); } } .fadeIn { animation: fadeIn 0.3s ease; } @keyframes peVivo { 0%,100% { opacity: 1; box-shadow: 0 0 0 0 #22C55E88; } 50% { opacity: 0.45; box-shadow: 0 0 0 4px #22C55E00; } } .pe-vivo { animation: peVivo 2s ease-in-out infinite; } @media (min-width: 760px) and (max-width: 1023px) { body { background: linear-gradient(135deg, ${C.surface} 0%, ${C.bg} 55%) fixed; } .pe-app { box-shadow: 0 0 0 1px ${C.border}, -16px 0 55px rgba(0,0,0,0.30), 16px 0 55px rgba(0,0,0,0.30); } } @media (min-width: 1024px) { .pe-sidebar-btn:hover { background: ${C.accent}18 !important; } } .pe-masonry { column-count: 2; column-gap: 16px; } .pe-masonry > * { display: block; width: 100%; margin: 0 0 16px; break-inside: avoid; -webkit-column-break-inside: avoid; page-break-inside: avoid; } .pe-masonry > .pe-ancho-total { column-span: all; } .pe-contenido { max-width: 1100px; margin: 0 auto; } .pe-contenido-estrecho { max-width: 780px; margin: 0 auto; } @media (min-width: 1500px) { .pe-masonry { column-count: 3; } .pe-contenido { max-width: 1460px; } } @media (min-width: 1900px) { .pe-contenido { max-width: 1760px; } } @media (min-width: 2400px) { .pe-masonry { column-count: 4; } .pe-contenido { max-width: 2200px; } } @keyframes peFalta { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.09); } } .pe-falta { animation: peFalta 1.4s ease-in-out infinite; } .pe-slider { -webkit-appearance: none; appearance: none; width: 100%; height: 46px; background: transparent; outline: none; margin: 0; display: block; } .pe-slider::-webkit-slider-runnable-track { height: 16px; border-radius: 8px; background: ${C.surface}; border: 1.5px solid ${C.border}; } .pe-slider::-webkit-slider-thumb { -webkit-appearance: none; width: 46px; height: 46px; border-radius: 23px; background: #E8B44C; border: 4px solid ${C.card}; margin-top: -16px; box-shadow: 0 3px 10px rgba(0,0,0,0.55); cursor: pointer; } .pe-slider::-moz-range-track { height: 16px; border-radius: 8px; background: ${C.surface}; border: 1.5px solid ${C.border}; } .pe-slider::-moz-range-thumb { width: 38px; height: 38px; border-radius: 19px; background: #E8B44C; border: 4px solid ${C.card}; box-shadow: 0 3px 10px rgba(0,0,0,0.55); cursor: pointer; } .pe-barra { position: relative; display: block; width: 100%; height: 46px; touch-action: none; -webkit-user-select: none; user-select: none; -webkit-tap-highlight-color: transparent; cursor: grab; } .pe-barra:active { cursor: grabbing; } .pe-barra-pista { position: absolute; left: 0; right: 0; top: 15px; height: 16px; border-radius: 8px; background: ${C.surface}; border: 1.5px solid ${C.border}; overflow: hidden; } .pe-barra-lleno { height: 100%; } .pe-barra-bola { position: absolute; top: 0; width: 46px; height: 46px; border-radius: 23px; border: 4px solid ${C.card}; box-shadow: 0 3px 10px rgba(0,0,0,0.55); } .pe-barra-bola::after { content: ""; position: absolute; left: 50%; top: 50%; width: 12px; height: 2px; margin: -1px 0 0 -6px; border-radius: 1px; background: rgba(0,0,0,.33); box-shadow: 0 -4px 0 rgba(0,0,0,.33), 0 4px 0 rgba(0,0,0,.33); }`;

  // --- DASHBOARD ---
  // --- TEMPERATURA IDEAL POR DÍA (engorde tropical) ---
  const TEMP_IDEAL = {
    0:  [33,34], 1:  [32,34], 2:  [32,34], 3:  [32,34],
    4:  [30,32], 5:  [30,32], 6:  [30,32], 7:  [30,32],
    8:  [28,30], 9:  [28,30], 10: [28,30],
    11: [26,28], 12: [26,28], 13: [26,28], 14: [26,28],
    15: [24,26], 16: [24,26], 17: [24,26], 18: [24,26],
    19: [23,25], 20: [23,25], 21: [23,25],
    22: [21,24], 23: [21,24], 24: [21,24], 25: [21,24],
    26: [21,24], 27: [21,24], 28: [21,24],
    29: [20,23], 30: [20,23], 31: [20,23], 32: [20,23],
    33: [20,23], 34: [20,23], 35: [20,23],
  };

  const Dashboard = () => {
    // Fases de alimento de la granja (3 o 4, según el ajuste). Los anchos de la barra son
    // los días que dura cada fase, así que la barra cuadra siempre con el calendario real.
    const ctxFases = { totalPollos: camada.totalPollos, raza: camada.raza, personal: fasesPersonal };
    const fasesHoy = fasesDe(userSettings.fasesAlimento, ctxFases).map(function (fa) {
      return Object.assign({}, fa, { ancho: (fa.d2 - fa.d1 + 1) / DIAS_CICLO_ALIM });
    });
    const faseDia = faseDelDia(userSettings.fasesAlimento, diaActual, ctxFases);
    const faseInfo = {
      label: tr(faseDia.nombre), color: faseDia.color, bg: faseDia.bg, emoji: faseDia.emoji,
      dias: diasFaseTxt(faseDia),
      pct: Math.min(100, Math.max(0, ((diaActual - faseDia.d1 + 1) / (faseDia.d2 - faseDia.d1 + 1)) * 100)),
    };
    const eventoHoyDash = calendarioUsuario.find(e => e.dia === diaActual);
    const TIPO_C = { vacuna:"#a855f7", vitamina:"#22c55e", probiotico:"#ec4899", desparasitante:"#ef4444", betaina:"#3b82f6", agua:"#60a5fa", recepcion:"#f5c200" };
    const colHoy = eventoHoyDash ? (TIPO_C[eventoHoyDash.tipo] || C.accent) : "#60a5fa";
    const iconoHoy = eventoHoyDash?.tipo === "vacuna" ? "💉" : eventoHoyDash?.tipo === "vitamina" ? "💊" : eventoHoyDash?.tipo === "probiotico" ? "🦠" : eventoHoyDash?.tipo === "desparasitante" ? "🪱" : "💧";
    const temp = TEMP_IDEAL[Math.min(diaActual, 35)] || TEMP_IDEAL[35];
    const tempMin = temp[0], tempMax = temp[1];

    return (
      <div className={isDesktop ? "fadeIn pe-masonry" : "fadeIn"}
        style={isDesktop ? undefined : { display: "flex", flexDirection: "column", gap: 14 }}>

        {/* Barra de camada: mitad izquierda para crear una nueva, mitad derecha para elegir */}
        <div className={isDesktop ? "pe-ancho-total" : undefined} style={{ display: "flex", gap: 8 }}>
          {(isProp || permisos.editarCamada) && (
            <button onClick={function () { setMisCamadasNueva(true); setMisCamadasOpen(true); }}
              style={{
                // boxSizing border-box: sin esto el relleno se SUMA al ancho y la mitad
                // izquierda queda más ancha que el selector.
                flex: "0 0 auto", width: "calc(50% - 4px)", minWidth: 0, boxSizing: "border-box",
                background: "linear-gradient(135deg, #FF9A4D, #F26B0F)", border: "none",
                borderRadius: 12, color: "#2a1000", padding: isDesktop ? "11px 14px" : "11px 4px", fontSize: isDesktop ? 14 : 11, fontWeight: 800,
                fontFamily: "'Syne', sans-serif", cursor: "pointer", display: "flex", alignItems: "center",
                justifyContent: "center", gap: isDesktop ? 7 : 4, boxShadow: "0 2px 10px #00000055", letterSpacing: 0,
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}>
              <span style={{ fontSize: isDesktop ? 18 : 15, lineHeight: 1, fontWeight: 900, flexShrink: 0 }}>+</span>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{tr("Nueva camada")}</span>
            </button>
          )}
          <div style={{ flex: "0 0 auto", width: "calc(50% - 4px)", minWidth: 0, position: "relative" }}>
            <select value={activaCamadaId} onChange={e => setActivaCamada(e.target.value)}
              style={{ width: "100%", background: C.surface, border: `1.5px solid ${C.accent}66`, borderRadius: 12, color: C.accent, padding: "11px 36px 11px 14px", fontSize: 14, fontWeight: 700, fontFamily: "'Syne', sans-serif", outline: "none", appearance: "none", cursor: "pointer" }}>
              {camadasList.filter(c => c.activa !== false).map(c => <option key={c.id} value={c.id}>{c.nombre} — {c.galpon}</option>)}
            </select>
            <span style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", color: C.accent, pointerEvents: "none", fontSize: 14 }}>▼</span>
          </div>
        </div>

        {/* Card camada — compacto y visual · tap → página Mis Camadas */}
        <div onClick={(isProp || permisos.verMisCamadas) ? () => setMisCamadasOpen(true) : undefined}
          style={{ cursor: (isProp || permisos.verMisCamadas) ? "pointer" : "default" }}>
        <Card>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {/* Imagen del pollo + raza */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, flexShrink: 0 }}>
              <div style={{ background: "linear-gradient(135deg, " + C.accent + "22, transparent)", borderRadius: "50%", padding: 4 }}>
                <BroilerSVG size={56} />
              </div>
              <div style={{ fontSize: 10, fontWeight: 800, color: C.accent, fontFamily: "'Space Mono', monospace", textAlign: "center", lineHeight: 1, marginTop: 2 }}>{camada.raza || tr("Ross 308")}</div>
            </div>
            {/* Info */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 6 }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: C.accent, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{camada.nombre}</div>
                <div style={{ fontSize: 22, fontWeight: 900, fontFamily: "'Space Mono', monospace", color: C.accent, lineHeight: 1, flexShrink: 0 }}>
                  {diaActual}<span style={{ fontSize: 12, color: C.textFaint, fontWeight: 400 }}> {t("diaActual")}</span>
                </div>
              </div>
              <div style={{ fontSize: 12, color: C.textMuted, fontFamily: "'Space Mono', monospace", marginTop: 4 }}>📅 {camada.fechaInicio}</div>
            </div>
          </div>
          {/* Dos tarjetas gráficas: Galpón + Pollitos recibidos */}
          <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
            <div style={{ flex: 1, background: C.surface, borderRadius: 12, padding: "10px 12px", border: "1px solid " + C.border, display: "flex", alignItems: "center", gap: 10 }}>
              <GalponSVG size={42} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 10, color: C.textFaint, fontFamily: "'Space Mono', monospace", letterSpacing: 1, textTransform: "uppercase" }}>{t("galpon")}</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{camada.galpon}</div>
              </div>
            </div>
            <div style={{ flex: 1, background: C.surface, borderRadius: 12, padding: "10px 12px", border: "1px solid " + C.border, display: "flex", alignItems: "center", gap: 10 }}>
              <PollitoSVG size={42} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 10, color: C.textFaint, fontFamily: "'Space Mono', monospace", letterSpacing: 1, textTransform: "uppercase" }}>{t("recibidos")}</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: C.text, fontFamily: "'Space Mono', monospace" }}>{Number(camada.totalPollos).toLocaleString()}</div>
              </div>
            </div>
          </div>
        </Card>
        </div>

        {/* TARJETA AMBIENTE — con sensor vinculado, o SIMULADA si el modo demostración está encendido.
            Sin sensor y sin demo → no se renderiza nada (la app se ve igual que antes). */}
        {(function () {
          if (!ambVisible) return null;
          var sensor = ambSensor;
          var lecturas = ambLecturas;
          var ult = ambUltima;
          var sinSenal = ambSinSenal;
          var esDemo = ambEsDemo;

          // Esquinas cortadas: el marco "de tablero" del diseño elegido.
          var corte = "polygon(0 9px, 9px 0, calc(100% - 9px) 0, 100% 9px, 100% calc(100% - 9px), calc(100% - 9px) 100%, 9px 100%, 0 calc(100% - 9px))";
          var cl = function (v) { return Math.max(0, Math.min(100, v)); };
          var rangoDia = TEMP_IDEAL_SENSOR[Math.max(0, Math.min(diaActual, 35))] || [20, 23];

          var tE = sensTempEstado(ult && ult.temp_c, diaActual);
          var hE = sensHumEstado(ult && ult.humedad);
          var nh3Techo = sensNh3EnTecho(lecturas);
          var nE = sensNh3Estado(ult && ult.nh3_ratio, nh3Techo);
          var trend = sensNh3Tendencia(lecturas);
          var hayRojo = !sinSenal && (tE.nivel === "rojo" || nE.nivel === "rojo");
          var acento = sinSenal ? SENS_GRIS : hayRojo ? SENS_ROJO : "#22D3EE";

          // Últimas lecturas (de más vieja a más nueva) para las mini-gráficas.
          var ultimas = lecturas.slice(0, 14).reverse();

          // Una fila del tablero: anillo segmentado + etiqueta + valor grande + rango + mini-gráfica.
          var fila = function (k, icono, etiqueta, valor, unidad, sub, pct, color, serie) {
            return (
              <div key={k} style={{ background: "linear-gradient(135deg, " + color + "1f, #0b1526)", border: "1px solid " + color + "44", clipPath: corte, WebkitClipPath: corte, padding: "10px 12px", marginBottom: k === "a" ? 0 : 8, display: "flex", alignItems: "center", gap: 12 }}>
                <AnilloSeg pct={sinSenal ? 0 : pct} color={sinSenal ? SENS_GRIS : color} size={54} contenido={icono} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 9.5, fontWeight: 800, color: sinSenal ? "#94A3B8" : color, fontFamily: "'Space Mono', monospace", letterSpacing: 1.4, textTransform: "uppercase", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{etiqueta}</div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 4, lineHeight: 1.05, marginTop: 2 }}>
                    <span style={{ fontSize: valor.length > 6 ? 21 : 27, fontWeight: 900, color: sinSenal ? "#64748B" : "#F1F5F9", whiteSpace: "nowrap" }}>{valor}</span>
                    {unidad && <span style={{ fontSize: 12, fontWeight: 800, color: sinSenal ? "#64748B" : color }}>{unidad}</span>}
                  </div>
                  <div style={{ display: "inline-block", marginTop: 5, background: "#00000055", border: "1px solid " + (sinSenal ? "#33415555" : color + "33"), borderRadius: 4, padding: "2px 7px", fontSize: 8.5, color: sinSenal ? "#64748B" : "#94A3B8", fontFamily: "'Space Mono', monospace", letterSpacing: 0.6, whiteSpace: "nowrap" }}>{sub}</div>
                </div>
                <MiniGrafica valores={serie} color={sinSenal ? "#3a4655" : color} ancho={50} alto={26} />
              </div>
            );
          };

          // Estado general del galpón (la barra de abajo del diseño).

          // Tornillo de la carcasa (decorativo): las 4 esquinas del aparato real.
          var tornillo = function (pos) {
            return <div style={Object.assign({ position: "absolute", width: 7, height: 7, borderRadius: "50%", background: "radial-gradient(circle at 30% 30%, #FFE884, #B08B00)", border: "1px solid #1A1A1A66", pointerEvents: "none" }, pos)} />;
          };
          // Amarillo de la carcasa: se apaga un poco cuando el aparato no reporta.
          var carcasa = sinSenal
            ? "linear-gradient(165deg, #D9C25C, #C2AC49 55%, #A68F35)"
            : "linear-gradient(165deg, #FFD836, #F5C200 55%, #D9AC00)";

          return (
            <div onClick={() => setAmbientePageOpen(true)}
              style={{ position: "relative", cursor: "pointer", background: carcasa, borderRadius: 22, padding: "11px 10px 9px", border: "3px solid #1A1A1A", boxShadow: "0 6px 22px #00000066, inset 0 2px 0 #FFE27A, inset 0 -3px 6px #00000022" }}>

              {/* Tornillos de la carcasa */}
              {tornillo({ left: 5, top: 5 })}
              {tornillo({ right: 5, top: 5 })}
              {tornillo({ left: 5, bottom: 5 })}
              {tornillo({ right: 5, bottom: 5 })}

              {/* PANTALLA del aparato: aquí adentro va todo el tablero */}
              <div style={{ position: "relative", background: "linear-gradient(160deg, #0F1B2D, #17293F)", borderRadius: 9, padding: "11px 12px 10px", border: "3px solid #1A1A1A", overflow: "hidden", boxShadow: "inset 0 0 26px #000000AA, 0 1px 0 #FFE27A55" }}>
              <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: acento }} />
              {/* Reflejo del vidrio de la pantalla */}
              <div style={{ position: "absolute", left: 0, right: 0, top: 0, height: "42%", background: "linear-gradient(180deg, #FFFFFF12, #FFFFFF00)", pointerEvents: "none" }} />

              {/* Encabezado */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 8, paddingLeft: 4 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: sinSenal ? "#94A3B8" : "#7DD3FC", fontFamily: "'Space Mono', monospace", letterSpacing: 1.5, textTransform: "uppercase", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{tr("📡 Ambiente ·")} {sinSenal ? tr("OFFLINE") : tr("EN VIVO")}
                  </div>
                  {esDemo && <BadgeDemo />}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}>
                  <span className={sinSenal ? "" : "pe-vivo"} style={{ width: 7, height: 7, borderRadius: "50%", background: sinSenal ? SENS_GRIS : SENS_VERDE }} />
                  <span style={{ fontSize: 10, color: "#94A3B8", fontFamily: "'Space Mono', monospace" }}>{ult ? sensHaceTxt(ult.ts) : tr("sin datos")}</span>
                </div>
              </div>

              {/* Filas del tablero */}
              {fila("t", "🌡", "Temperatura",
                (ult && ult.temp_c != null) ? Number(ult.temp_c).toFixed(1) : "—",
                (ult && ult.temp_c != null) ? "°C" : "",
                "RANGO IDEAL " + rangoDia[0] + "-" + rangoDia[1] + "°C",
                (ult && ult.temp_c != null) ? cl(((Number(ult.temp_c) - (tE.obj - 6)) / 12) * 100) : 0,
                sinSenal ? SENS_GRIS : tE.color,
                ultimas.map(function (l) { return l.temp_c; }))}

              {fila("h", "💧", "Humedad",
                (ult && ult.humedad != null) ? Number(ult.humedad).toFixed(0) : "—",
                (ult && ult.humedad != null) ? "%" : "",
                "RANGO IDEAL 50-70%",
                (ult && ult.humedad != null) ? cl(Number(ult.humedad)) : 0,
                sinSenal ? SENS_GRIS : hE.color,
                ultimas.map(function (l) { return l.humedad; }))}

              {/* AMONIACO — palabra y semáforo, nunca un número de ppm */}
              {fila("a", <span style={{ fontSize: 13 }}>{tr("NH₃")}</span>,
                <>{"Amoniaco " + (sinSenal ? "" : trend)}{!esDemo && ult && ult.mq_raw != null && <span style={{ fontWeight: 400, color: "#94A3B8", letterSpacing: 0.5, marginLeft: 6 }}>ADC {ult.mq_raw}</span>}</>,
                (ult && ult.nh3_ratio != null) ? nE.label.toUpperCase() : "—", "",
                nE.nivel === "revisar" ? "EL SENSOR NO ESTÁ MIDIENDO" : nE.nivel === "gris" ? "SIN CALIBRAR" : "LO IDEAL ES BAJO",
                (!nh3Techo && ult && ult.nh3_ratio != null) ? cl((Number(ult.nh3_ratio) / 2.5) * 100) : 0,
                sinSenal ? SENS_GRIS : nE.color,
                ultimas.map(function (l) { return l.nh3_ratio; }))}

              {sinSenal && (
                <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 8, paddingLeft: 4, lineHeight: 1.5 }}>
                  {sensor.nombre || tr("El aparato")} {tr("no reporta.")} {ult
                    ? <>{tr("Estos son los últimos datos, de")} {sensHaceTxt(ult.ts)}.</>
                    : tr("Todavía no ha enviado ninguna lectura.")} {tr("Toca para ver el detalle.")}</div>
              )}
              </div>
              {/* Fin de la pantalla */}

              {/* Placa del nombre + rejillas de ventilación (parte baja del aparato) */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 8, paddingBottom: 1 }}>
                <div style={{ flex: 1, height: 8, borderRadius: 2, background: "repeating-linear-gradient(90deg, #1A1A1A40 0 3px, transparent 3px 8px)" }} />
                <div style={{ background: sinSenal ? "#DCC97F" : "#FFD22B", border: "2px solid #1A1A1A", borderRadius: 5, padding: "0 9px", fontFamily: "'Syne', sans-serif", fontSize: 12, fontWeight: 800, letterSpacing: 0.4, lineHeight: 1.7, whiteSpace: "nowrap", flexShrink: 0 }}>
                  <span style={{ color: "#C6304F" }}>{tr("POLLO")}</span><span style={{ color: "#55708C" }}> {tr("IA")}</span>
                </div>
                <div style={{ flex: 1, height: 8, borderRadius: 2, background: "repeating-linear-gradient(90deg, #1A1A1A40 0 3px, transparent 3px 8px)" }} />
              </div>
            </div>
          );
        })()}

        {/* HOY — card prominente */}
        <div onClick={() => setTab("calendario")} style={{ cursor: "pointer" }}>
        <Card style={{ border: `2px solid ${colHoy}55`, background: colHoy + "11" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, color: colHoy, fontFamily: "'Space Mono', monospace", letterSpacing: 1, marginBottom: 6, fontWeight: 700 }}>
                <span style={{ fontSize: 22, marginRight: 6 }}>🔔</span>{t("hoySugerencia")} — {t("diaActual").toUpperCase()} {diaActual}
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, color: C.text }}>
                {eventoHoyDash ? tr(eventoHoyDash.producto) : t("aguaLimpia")}
              </div>
              {eventoHoyDash?.funcion && <div style={{ fontSize: 14, color: C.textMuted, marginTop: 3 }}>{tr(eventoHoyDash.funcion)}</div>}
              {eventoHoyDash?.producto_nombre && <div style={{ fontSize: 15, color: colHoy, marginTop: 3, fontFamily: "'Space Mono', monospace" }}>{tr(eventoHoyDash.producto_nombre)} · {tr(eventoHoyDash.dosis)}</div>}
            </div>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: colHoy + "22", border: `1px solid ${colHoy}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0, marginLeft: 12 }}>{iconoHoy}</div>
          </div>
          <div style={{ height: 5, background: C.surface, borderRadius: 3, marginTop: 12, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${Math.min(100,(diaActual/35)*100)}%`, background: colHoy, borderRadius: 3 }} />
          </div>
          <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center", background: colHoy + "15", borderRadius: 8, padding: "7px 12px" }}>
            <div>
              <div style={{ fontSize: 11, color: colHoy, fontFamily: "'Space Mono', monospace", fontWeight: 700 }}>⚖️ {t("pesoIdealHoy")}</div>
              <div style={{ fontSize: 18, fontWeight: 900, fontFamily: "'Space Mono', monospace", color: colHoy }}>
                {pesoEsperadoHoy.toFixed(2)} {tr("lb")}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 13, color: colHoy, fontFamily: "'Space Mono', monospace" }}>
                {(pesoEsperadoHoy / 2.205).toFixed(3)} {tr("kg")}</div>
              {pesoActualLb && (
                <div style={{ fontSize: 12, color: pesoActualLb >= pesoEsperadoHoy ? C.green : C.warn, fontWeight: 700, marginTop: 2 }}>
                  {pesoActualLb >= pesoEsperadoHoy ? "✅ " + t("enMeta") : "⚠ " + pesoActualLb.toFixed(2) + tr(" lb")}
                </div>
              )}
            </div>
          </div>
          {/* Badge: comparación vs estándar de la raza (al día del último pesaje) */}
          {compRaza && (
            <div style={{ marginTop: 8, background: compRaza.color + "18", border: `1.5px solid ${compRaza.color}66`, borderRadius: 10, padding: "8px 12px", display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 18 }}>{compRaza.emoji}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 800, color: compRaza.color }}>{tr("Día")} {compRaza.dia}: {compRaza.actual} {tr("lb vs")} {compRaza.estandar} {tr("lb estándar")} {camada.raza || tr("Ross 308")}
                </div>
                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 1 }}>{tr("al día del último pesaje")}</div>
              </div>
              <span style={{ fontSize: 15, fontWeight: 900, fontFamily: "'Space Mono', monospace", color: compRaza.color, flexShrink: 0 }}>{compRaza.pct}%</span>
            </div>
          )}
        </Card>
        </div>

        {/* Temperatura ideal */}
        <Card style={{ border: `1px solid #ff6b1a44`, background: "#1e1e24" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 36 }}>🌡</span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: "#ff6b1a", letterSpacing: 0.5 }}>{t("inicio") === "Home" ? tr("Ideal Temperature") : t("inicio") === "Início" ? tr("Temperatura Ideal") : t("inicio") === "होम" ? "आदर्श तापमान" : tr("Temperatura Ideal")}</div>
                <div style={{ fontSize: 14, color: C.textMuted, marginTop: 2 }}>{tr("DÍA")} {diaActual}</div>
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 18, fontWeight: 900, fontFamily: "'Space Mono', monospace", color: "#ff6b1a", lineHeight: 1 }}>{tempMin}–{tempMax}°C</div>
              <div style={{ fontSize: 13, color: C.textFaint, marginTop: 2 }}>{tr("rango ideal")}</div>
            </div>
          </div>

        {/* Programa de Luz */}
        {(function() {
          var luz = getLuzPrograma(diaActual);
          return (
            <div style={{ background: luz.color + "18", border: "2px solid " + luz.color, borderRadius: 16, padding: "14px 18px", marginTop: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: luz.color, fontFamily: "'Space Mono', monospace", letterSpacing: 1.5, textTransform: "uppercase" }}>
                    {luz.emoji} {t("programaLuz")} · {t("diaActual").toUpperCase()} {diaActual}
                  </div>
                  <div style={{ fontSize: 26, fontWeight: 900, color: luz.color, lineHeight: 1.1, marginTop: 4 }}>
                    {luz.horas}{t("horasLuz")}
                  </div>
                  <div style={{ fontSize: 13, color: C.textMuted, marginTop: 4 }}>
                    {luz.oscuridad}{t("oscuridad")} · {t("fase")} {t(luz.faseKey) || luz.fase}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 36 }}>{luz.emoji}</div>
                  <div style={{ fontSize: 11, color: luz.color, fontWeight: 700, fontFamily: "'Space Mono', monospace" }}>{luz.horas}L:{luz.oscuridad}D</div>
                </div>
              </div>
              {/* Barra visual LUZ - gruesa */}
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 11, color: luz.color, fontFamily: "'Space Mono', monospace", letterSpacing: 1, marginBottom: 6 }}>💡 {t("horasLuz") ? luz.horas + " " + t("horasLuz").toUpperCase() : luz.horas + tr("H OF LIGHT")}</div>
                <div style={{ height: 22, borderRadius: 10, background: "#2a2a2a", overflow: "hidden" }}>
                  <div style={{ height: "100%", width: (luz.horas / 24 * 100) + "%", background: `linear-gradient(90deg, ${luz.color}, ${luz.color}cc)`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "flex-end", paddingRight: 8, transition: "width 0.5s" }}>
                    <span style={{ fontSize: 11, fontWeight: 900, color: "#fff", fontFamily: "'Space Mono', monospace" }}>{luz.horas}h</span>
                  </div>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5, fontSize: 11, color: C.textFaint, fontFamily: "'Space Mono', monospace" }}>
                  <span>0h</span><span>12h</span><span>24h</span>
                </div>
              </div>
            </div>
          );
        })()}
          {/* Barra TEMPERATURA - gruesa */}
          <div style={{ marginTop: 4 }}>
            <div style={{ fontSize: 11, color: "#ff6b1a", fontFamily: "'Space Mono', monospace", letterSpacing: 1, marginBottom: 6 }}>🌡️ {t("tempIdeal") || tr("IDEAL TEMPERATURE")}</div>
            <div style={{ height: 22, background: "#2a2a2a", borderRadius: 10, overflow: "hidden", position: "relative" }}>
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to right, #2196f3, #4caf50, #ff9800, #f44336)", opacity: 0.25, borderRadius: 10 }} />
              <div style={{ position: "absolute", left: `${Math.round(((tempMin-20)/14)*100)}%`, width: `${Math.round(((tempMax-tempMin)/14)*100)}%`, top: 3, bottom: 3, background: "linear-gradient(90deg, #ff6b1a, #ffaa00)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ fontSize: 10, fontWeight: 900, color: "#fff", fontFamily: "'Space Mono', monospace", whiteSpace: "nowrap" }}>{tempMin}-{tempMax}°C</span>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5, fontSize: 11, color: C.textFaint, fontFamily: "'Space Mono', monospace" }}>
              <span>20°C</span><span>25°C</span><span>30°C</span><span>34°C</span>
            </div>
          </div>
        </Card>

        {/* Fase de alimento — tappable → alimentación */}
        <div onClick={() => setAlimentacionOpen(true)} style={{ cursor: "pointer" }}>
        <Card style={{ border: `2px solid ${faseInfo.color}88`, background: faseInfo.color + "11" }}>
          {/* Header de fase */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 28 }}>{faseInfo.emoji}</span>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: faseInfo.color, letterSpacing: 0.5 }}>
                  {faseInfo.label.toUpperCase()} · {t("diaActual").toUpperCase()} {diaActual}
                </div>
                <div style={{ fontSize: 14, color: C.textMuted }}>{faseInfo.dias} · {faseInfo.pct.toFixed(0)}{t("diasCompletado")}</div>
              </div>
            </div>
            <span style={{ fontSize: 14, color: faseInfo.color, opacity: 0.7 }}>{t("verFlecha")}</span>
          </div>

          {/* Barra de fases — 3 o 4 según el ajuste de la granja */}
          <div style={{
            height: 36, borderRadius: 18, overflow: "hidden", border: "2px solid #ffffff33",
            boxShadow: "0 2px 12px #00000066, inset 0 1px 0 #ffffff22", display: "flex", marginBottom: 10,
          }}>
            {fasesHoy.map(function (fa, i) {
              var act = fa.key === faseDia.key;
              return (
                <div key={fa.key} style={{
                  width: (fa.ancho * 100) + "%",
                  background: act
                    ? "linear-gradient(to bottom, " + fa.color + "ee, " + fa.color + " 40%, " + fa.color + "88)"
                    : "linear-gradient(to bottom, " + fa.color + "55, " + fa.color + "33)",
                  borderRight: i < fasesHoy.length - 1 ? "2px solid #00000033" : "none",
                  position: "relative", transition: "all 0.3s",
                  boxShadow: act ? "inset 0 1px 0 #ffffff55" : "none",
                }}>
                  {act && (
                    <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "linear-gradient(to bottom, rgba(255,255,255,0.28) 0%, transparent 50%)" }} />
                  )}
                </div>
              );
            })}
          </div>

          {/* Etiquetas */}
          <div style={{ display: "flex" }}>
            {fasesHoy.map(function (fa) {
              var act = fa.key === faseDia.key;
              return (
                <div key={fa.key} style={{ width: (fa.ancho * 100) + "%", textAlign: "center" }}>
                  <div style={{ fontSize: 18, marginBottom: 2 }}>{fa.emoji}</div>
                  <div style={{ fontSize: 14, fontWeight: act ? 800 : 500, color: act ? fa.color : fa.color + "66" }}>{tr(fa.nombre)}</div>
                  <div style={{ fontSize: 15, color: C.textFaint }}>D{fa.d1}–{fa.d2}</div>
                </div>
              );
            })}
          </div>
        </Card>
        </div>

        {/* Vivos y Muertos */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Card style={{ padding: 13 }}>
            <Stat label={t("vivosActuales") || "Vivos"} value={fmtNum(vivosActuales)} color={C.accent} />
            {pesoPromedioLb && (
              <div style={{ marginTop: 8, borderTop: "1px solid " + C.border, paddingTop: 6 }}>
                <div style={{ fontSize: 11, color: C.textFaint, fontFamily: "'Space Mono', monospace", marginBottom: 2 }}>{tr("PESO PROMEDIO")}</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: C.green, fontFamily: "'Space Mono', monospace" }}>{pesoPromedioLb} {tr("lb")}</div>
                <div style={{ fontSize: 10.5, color: C.textFaint, fontFamily: "'Space Mono', monospace", marginTop: 2 }}>
                  {pesosRecientes.length > 1
                    ? tr("días") + " " + Math.max(0, diaUltPesaje - DIAS_PROM_PESO + 1) + "-" + diaUltPesaje
                    : tr("día") + " " + diaUltPesaje}
                </div>
              </div>
            )}
          </Card>
          <Card style={{ padding: 13 }}><Stat label={t("muertos") || "Muertos"} value={fmtNum(mortalidadAcum)} color={C.danger} sub={pctMort + "%"} /></Card>
        </div>

        {/* 📈 Eficiencia: FCR + costo por libra */}
        {(fcrReal || costoPorLb !== null) && (
          <Card>
            <div style={{ fontSize: 15, color: C.textMuted, fontFamily: "'Space Mono', monospace", letterSpacing: 1, marginBottom: 12 }}>{tr("📈 EFICIENCIA")}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div style={{ background: C.surface, borderRadius: 12, border: "1px solid " + C.border, padding: "10px 12px" }}>
                <div style={{ fontSize: 10, color: C.textFaint, fontFamily: "'Space Mono', monospace", letterSpacing: 1 }}>{tr("FCR (CONVERSIÓN)")}</div>
                <div style={{ fontSize: 22, fontWeight: 900, fontFamily: "'Space Mono', monospace", color: fcrEstado ? fcrColores[fcrEstado] : C.textMuted, lineHeight: 1.2 }}>
                  {fcrReal || "—"}
                </div>
                <div style={{ fontSize: 11, color: fcrEstado ? fcrColores[fcrEstado] : C.textFaint, fontWeight: 700 }}>
                  {fcrEstado === "excelente" ? tr("✅ Excelente") : fcrEstado === "bueno" ? tr("👍 Bueno") : fcrEstado === "alto" ? tr("⚠️ Alto") : tr("Sin datos de alimento")}
                </div>
                <div style={{ fontSize: 10, color: C.textFaint, marginTop: 3 }}>{tr("lb alimento / lb carne · meta")} {ESTANDAR_FCR_MIN.toFixed(2)}–{ESTANDAR_FCR_MAX.toFixed(2)}</div>
              </div>
              <div style={{ background: C.surface, borderRadius: 12, border: "1px solid " + C.border, padding: "10px 12px" }}>
                <div style={{ fontSize: 10, color: C.textFaint, fontFamily: "'Space Mono', monospace", letterSpacing: 1 }}>{tr("COSTO POR LIBRA")}</div>
                <div style={{ fontSize: 22, fontWeight: 900, fontFamily: "'Space Mono', monospace", color: margenLb === null ? C.accent : margenLb > 0 ? C.green : C.danger, lineHeight: 1.2 }}>
                  {costoPorLb !== null ? $ + " " + costoPorLb.toFixed(2) : "—"}
                </div>
                {precioLbProm ? (
                  <div style={{ fontSize: 11, color: C.textMuted, fontWeight: 700 }}>{tr("venta")} {$} {Number(precioLbProm).toFixed(2)}/lb
                    {margenLb !== null && <span style={{ color: margenLb > 0 ? C.green : C.danger }}> · {margenLb > 0 ? "+" : ""}{$} {margenLb.toFixed(2)}/lb</span>}
                  </div>
                ) : (
                  <div style={{ fontSize: 11, color: C.textFaint, fontWeight: 700 }}>{costoPorLb !== null ? tr("gastos ÷ lb producidas") : tr("Sin gastos aún")}</div>
                )}
                <div style={{ fontSize: 10, color: C.textFaint, marginTop: 3 }}>{fmtNum(lbProducidasTotal)} {tr("lb producidas · gastos")} {$} {fmtNum(costoTotal)}</div>
              </div>
            </div>
            <div style={{ fontSize: 10, color: C.textFaint, marginTop: 8 }}>{tr("* Alimento estimado desde los gastos de la categoría \"Alimento\" (saco 100 lb ≈ RD$1,395). Lb producidas = vendidas + carne en pie (último peso × vivos).")}</div>
          </Card>
        )}


        {/* Próximos eventos */}
        {proximosEventos.length > 0 && (
          <Card>
            <div style={{ fontSize: 15, color: C.warn, fontFamily: "'Space Mono', monospace", letterSpacing: 1, marginBottom: 10 }}><span style={{ fontSize: 20, marginRight: 6 }}>⏰</span>{t("proximosEventos") || tr("PRÓXIMOS")}</div>
            {proximosEventos.map(ev => (
              <div key={ev.dia} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{tr(ev.producto)}</div>
                  <div style={{ fontSize: 15, color: C.textMuted }}>{tr("Día")} {ev.dia}</div>
                </div>
                <span style={{ background: C.warn+"22", color: C.warn, border: `1px solid ${C.warn}44`, borderRadius: 6, padding: "3px 10px", fontSize: 15, fontFamily: "'Space Mono', monospace" }}>{tr("en")} {ev.dia - diaActual}d</span>
              </div>
            ))}
          </Card>
        )}

        {/* Últimos registros */}
        {camada.registros.length > 0 && (
          <Card>
            <div style={{ fontSize: 15, color: C.textMuted, fontFamily: "'Space Mono', monospace", marginBottom: 10 }}>{t("ultimosRegistros") || tr("ÚLTIMOS REGISTROS")}</div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 15 }}>
                <thead>
                  <tr>{[t("diaNum")||"Día", t("vivosActuales")||"Vivos", "Mort.", tr("Peso kg"), t("estadoCama")||"Cama", t("estadoHeces")||"Heces"].map(h => <th key={h} style={{ textAlign: "left", padding: "4px 6px", color: C.textFaint, fontFamily: "'Space Mono', monospace", fontSize: 14, borderBottom: `1px solid ${C.border}` }}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {camada.registros.slice(-4).reverse().map(r => {
                    const est = clasificarRegistro(r);
                    const rowBg = est === "grave" ? "#cc1a0022" : est === "bueno" ? "#4caf5022" : "transparent";
                    return (
                      <tr key={r.id || (r.dia + "_" + r.fecha + "_" + (r._ts || 0))} style={{ background: rowBg }}>
                        {[r.dia, r.vivos, r.mortalidad||0, r.pesoMuestra||"—", r.estadoCama||"—", r.estadoHeces||"—"].map((v,i) => (
                          <td key={i} style={{ padding: "6px 6px", color: est === "grave" ? "#ff8888" : est === "bueno" ? "#88cc88" : C.text, borderBottom: `1px solid ${C.border}`, fontWeight: est !== "normal" ? 700 : 400 }}>{v}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* 📊 Estado del inventario de la GRANJA — todos lo VEN; tocarlo solo abre la página si tiene permiso */}
        {(inventarioGranja || camada.inventarioAlimento) && (
          <TableroInventario items={invTablero(inventarioGranja || camada.inventarioAlimento, userSettings.fasesAlimento)} C={C}
            onClick={(isProp || permisos.verInventario) ? function() { setInventarioOpen(true); } : undefined} />
        )}

      </div>
    );
  };

  // --- REGISTRO DIARIO ---
  const OPCIONES_MORTALIDAD = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "13", "14", "15", "16", "17", "18", "19", "20", "21", "22", "23", "24", "25", "26", "27", "28", "29", "30", "31", "32", "33", "34", "35", "36", "37", "38", "39", "40", "41", "42", "43", "44", "45", "46", "47", "48", "49", "50", "51", "52", "53", "54", "55", "56", "57", "58", "59", "60", "61", "62", "63", "64", "65", "66", "67", "68", "69", "70", "71", "72", "73", "74", "75", "76", "77", "78", "79", "80", "81", "82", "83", "84", "85", "86", "87", "88", "89", "90", "91", "92", "93", "94", "95", "96", "97", "98", "99", "100", "101", "102", "103", "104", "105", "106", "107", "108", "109", "110", "111", "112", "113", "114", "115", "116", "117", "118", "119", "120", "121", "122", "123", "124", "125", "126", "127", "128", "129", "130", "131", "132", "133", "134", "135", "136", "137", "138", "139", "140", "141", "142", "143", "144", "145", "146", "147", "148", "149", "150", "151", "152", "153", "154", "155", "156", "157", "158", "159", "160", "161", "162", "163", "164", "165", "166", "167", "168", "169", "170", "171", "172", "173", "174", "175", "176", "177", "178", "179", "180", "181", "182", "183", "184", "185", "186", "187", "188", "189", "190", "191", "192", "193", "194", "195", "196", "197", "198", "199", "200"];
  const OPCIONES_PESO = [{ label: tr("No pesé hoy"), val: "" }].concat((function() {
    var arr = [];
    for (var i = 30; i <= 4085; i += 5) {
      var kg = i / 1000;
      var lb = (kg * 2.20462).toFixed(3);
      arr.push({ label: kg.toFixed(3) + " kg (" + lb + " lb)", val: kg.toFixed(3) });
    }
    return arr;
  })());
  const OPCIONES_CAMA = [
    { label: tr("Seleccionar estado de la cama..."), val: "" },
    { label: tr("🟢 Excelente — seca y suelta"), val: "excelente" },
    { label: tr("🟡 Buena — algo húmeda"), val: "buena" },
    { label: tr("🟠 Regular — húmeda en zonas"), val: "regular" },
    { label: tr("🔴 Mala — muy húmeda/apelmazada"), val: "mala" },
    { label: tr("⚫ Crítica — requiere cambio urgente"), val: "critica" },
  ];
  const OPCIONES_HECES = [
    { label: tr("Seleccionar estado de las heces..."), val: "" },
    { label: tr("🟢 Normal — firme y marrón"), val: "normal" },
    { label: tr("🟡 Blandas — algo líquidas"), val: "blandas" },
    { label: tr("🟠 Diarrea amarilla — líquidas"), val: "diarrea_amarilla" },
    { label: tr("🔴 Diarrea con sangre — rojizas"), val: "diarrea_sangre" },
    { label: tr("⚫ Cecales — marrón oscuro (normal)"), val: "cecales" },
    { label: tr("🔵 Verdes — posible estrés/infección"), val: "verdes" },
  ];
  const CAMA_COLOR = { excelente: "#4caf50", buena: "#f5c200", regular: "#ff6b1a", mala: "#cc1a00", critica: "#880000" };
  const HECES_COLOR = { normal: "#4caf50", blandas: "#f5c200", diarrea_amarilla: "#ff6b1a", diarrea_sangre: "#cc1a00", cecales: "#c49a60", verdes: "#2196f3" };

  const OPCIONES_SUPLEMENTO = [
    { label: tr("Sin suplemento hoy"), val: "" },
    // Vitaminas
    { label: tr("💊 DYNAVIT-200 — Vitaminas + Electrolitos"), val: "DYNAVIT-200" },
    { label: tr("💊 AD3E — Vitaminas A, D3, E"), val: "AD3E" },
    { label: tr("🍋 Limón / Vitamina C"), val: "Vitamina C (Limón)" },
    { label: tr("🧬 Betaína"), val: "Betaína" },
    { label: tr("🔬 Enzimas digestivas"), val: "Enzimas digestivas" },
    // Vacunas
    { label: tr("💉 Hipragumboro — Vacuna Gumboro"), val: "Vacuna Gumboro" },
    { label: tr("💉 Newcastle La Sota — Vacuna Newcastle"), val: "Vacuna Newcastle" },
    { label: tr("💉 Bronquitis infecciosa"), val: "Vacuna Bronquitis" },
    { label: tr("💉 Marek"), val: "Vacuna Marek" },
    // Probióticos
    { label: tr("🦠 Simbyos — Probiótico"), val: "Probiótico Simbyos" },
    // Desparasitante
    { label: tr("🪱 Levamisol — Desparasitante"), val: "Levamisol" },
    // Otros
    { label: tr("🍬 Azúcar (recepción)"), val: "Azúcar" },
    { label: tr("💧 Clorox / Cloro en agua"), val: "Cloración agua" },
    { label: tr("🧪 Otro (ver notas)"), val: "Otro" },
  ];

  const RegistroDiario = () => {
    const vivosProyectados = vivosActuales - Number(registroHoy.mortalidad || 0);
    const pesoLb = registroHoy.pesoMuestra ? (Number(registroHoy.pesoMuestra) * 2.205).toFixed(3) : null;

    const DropField = ({ label, value, onChange, options, emptyLabel }) => (
      <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <span style={{ fontSize: 14, color: C.textMuted, fontFamily: "'Space Mono', monospace", letterSpacing: 1, textTransform: "uppercase" }}>{label}</span>
        <div style={{ position: "relative" }}>
          <select value={value} onChange={e => onChange(e.target.value)} style={{
            width: "100%", background: C.surface, border: `1.5px solid ${value ? C.accent : C.border}`,
            borderRadius: 8, color: value ? C.text : C.textFaint, padding: "12px 36px 12px 13px",
            fontSize: 14, fontFamily: "'Syne', sans-serif", outline: "none",
            appearance: "none", WebkitAppearance: "none", cursor: "pointer",
          }}>
            {options.map(opt => typeof opt === "string"
              ? <option key={opt} value={opt}>{opt === "0" ? tr("0 aves (ninguna)") : opt + " " + tr("aves muertas")}</option>
              : <option key={opt.val} value={opt.val}>{opt.label}</option>
            )}
          </select>
          <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: C.textMuted, pointerEvents: "none", fontSize: 14 }}>▼</span>
        </div>
      </label>
    );

    // Colores más suaves y del MISMO tono de saturación, sobre el fondo normal de las tarjetas
    // (antes cada campo tenía un fondo muy oscuro y saturado, y en los temas claros se veía mal).
    // Cada tarjeta se distingue por una franja de color a la izquierda y el ícono en un círculo.
    const CAMPO_COLORS = {
      mortalidad: { color: "#E5736B", icon: "💀" },
      peso:       { color: "#E8B44C", icon: "⚖️" },
      alimento:   { color: "#F2994A", icon: "🌽" },
      cama:       { color: "#A78BFA", icon: "🌾" },
      heces:      { color: "#4CC38A", icon: "🔬" },
      suplemento: { color: "#6AA6F5", icon: "💊" },
      notas:      { color: "#93A3B8", icon: "📝" },
    };
    // Tarjeta de un campo: mismo aspecto para todos.
    const campoCard = function (col) {
      return {
        background: C.card, borderRadius: 14, border: "1px solid " + col + "2e",
        borderLeft: "4px solid " + col, padding: "15px 16px",
        boxShadow: "0 1px 6px #00000022",
      };
    };
    const campoIcono = function (col) {
      return {
        fontSize: 21, background: col + "1f", borderRadius: 11, width: 40, height: 40,
        display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      };
    };

    return (
      <div className="fadeIn" style={{ display: "flex", flexDirection: "column", gap: 14 }}>

        {/* Pestañas: Registro Diario / Supervisor */}
        <div style={{ display: "flex", gap: 8, background: C.surface, borderRadius: 14, border: `1px solid ${C.border}`, padding: 5 }}>
          {[{ id: "diario", label: tr("📋 Registro Diario") }, { id: "supervisor", label: tr("🔍 Supervisor") }].map(function (st) {
            var on = registroSubTab === st.id;
            return (
              <button key={st.id} onClick={function () { setRegistroSubTab(st.id); }}
                style={{ flex: 1, padding: "11px 6px", borderRadius: 10, border: "none", cursor: "pointer", fontSize: 14, fontWeight: on ? 800 : 600, fontFamily: "'Syne', sans-serif", background: on ? C.accent : "transparent", color: on ? "#1a0a00" : C.textMuted, transition: "all 0.18s ease" }}>
                {st.label}
              </button>
            );
          })}
        </div>

        {registroSubTab === "supervisor" ? (
          <SupervisorInspeccion C={C} camada={camada} camadas={camadasList.filter(function(c) { return c.activa !== false; })}
            session={session} diaActual={diaActual}
            granja={(userSettings && userSettings.nombreGranja) || ""}
            form={supervisorForm} setForm={setSupervisorForm}
            onGuardar={guardarInspeccionSupervisor} guardado={supervisorGuardado} modoAlim={userSettings.fasesAlimento}
            inspecciones={supervisorGranja}
            inventarioActual={inventarioGranja || (camada && camada.inventarioAlimento) || null} />
        ) : (
          /* ── REGISTRO SENCILLO (para quien lee poco): dibujos, números grandes y + / − ── */
          (function () {
            var OPC_CAMA_S = [
              { val: "excelente", nombre: tr("Seca"),      ico: "cama1", color: "#4caf50" },
              { val: "buena",     nombre: tr("Casi seca"), ico: "cama2", color: "#f5c200" },
              { val: "regular",   nombre: tr("Húmeda"),    ico: "cama3", color: "#ff6b1a" },
              { val: "mala",      nombre: tr("Mojada"),    ico: "cama4", color: "#cc1a00" },
            ];
            var OPC_HECES_S = [
              { val: "normal",           nombre: tr("Bien"),       ico: "heces1", color: "#4caf50" },
              { val: "blandas",          nombre: tr("Blandas"),    ico: "heces2", color: "#f5c200" },
              { val: "diarrea_amarilla", nombre: tr("Diarrea"),    ico: "heces3", color: "#ff6b1a" },
              { val: "diarrea_sangre",   nombre: tr("Con sangre"), ico: "heces4", color: "#cc1a00" },
            ];
            var ICO_FASE = { preinicio: "pollito", iniciador: "pollito", crecimiento: "gallina", engorde: "polloGrande" };

            // Botón redondo grande de + o −
            var btnMas = function (txt, alTocar, color, apagado) {
              return (
                <button onClick={function () { if (!apagado) alTocar(); }} disabled={apagado}
                  style={{ width: 66, height: 66, flexShrink: 0, borderRadius: 18, border: "2px solid " + (apagado ? C.border : color),
                    background: apagado ? "transparent" : color + "22", color: apagado ? C.textFaint : color,
                    fontSize: 34, fontWeight: 900, lineHeight: 1, cursor: apagado ? "default" : "pointer", fontFamily: "'Syne', sans-serif" }}>{txt}</button>
              );
            };
            var contador = function (valor, alCambiar, color) {
              var v = Number(valor) || 0;
              return (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                  {btnMas("−", function () { alCambiar(String(Math.max(0, v - 1))); }, color, v <= 0)}
                  <div style={{ flex: 1, textAlign: "center", fontSize: 56, fontWeight: 900, lineHeight: 1,
                    color: v > 0 ? color : C.textFaint, fontFamily: "'Space Mono', monospace" }}>{v}</div>
                  {btnMas("+", function () { alCambiar(String(v + 1)); }, color, false)}
                </div>
              );
            };
            // Fila de dibujos para escoger. Debajo se lee en grande lo que quedó escogido.
            var escoger = function (valor, alCambiar, lista, colorCaja) {
              var puesto = lista.filter(function (o) { return o.val === valor; })[0];
              return (
                <div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {lista.map(function (o) {
                      var on = valor === o.val;
                      return (
                        <button key={o.val} onClick={function () { alCambiar(on ? "" : o.val); }}
                          style={{ flex: 1, padding: "12px 2px 10px", borderRadius: 16, cursor: "pointer",
                            display: "flex", flexDirection: "column", alignItems: "center",
                            border: on ? "3px solid " + o.color : "2px solid " + C.border,
                            background: on ? o.color + "2e" : C.surface }}>
                          <IcoSimple t={o.ico} c={on ? o.color : C.textMuted} s={40} />
                          <div style={{ width: 26, height: 7, borderRadius: 4, background: o.color, marginTop: 7, opacity: on ? 1 : 0.45 }} />
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ marginTop: 11, textAlign: "center", fontSize: 23, fontWeight: 900, lineHeight: 1.15,
                    fontFamily: "'Syne', sans-serif", color: puesto ? puesto.color : C.textFaint }}>
                    {puesto ? puesto.nombre : tr("Toca un dibujo")}
                  </div>
                </div>
              );
            };
            var titulo = function (ico, texto, color) {
              return (
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                  <IcoSimple t={ico} c={color} s={32} />
                  <div style={{ fontSize: 19, fontWeight: 900, color: color, fontFamily: "'Syne', sans-serif" }}>{texto}</div>
                </div>
              );
            };
            var caja = function (color) {
              return { background: C.card, borderRadius: 18, border: "2px solid " + color + "44", padding: "16px 16px 18px" };
            };

            // Peso del día: la barra nace en la meta, pero hasta que el empleado no la mueve
            // o confirma, NO se guarda nada (si no, todos los días saldrían clavados en la meta).
            // Cuántos pollos se suben a la balanza (1, 10, 50 o 100). Queda guardado en los
            // ajustes de la granja para que no haya que escogerlo todos los días.
            var avesPeso = Math.max(1, Number(userSettings.pesoAves) || 1);
            var idealG = Math.max(30, Math.round(pesoEsperadoHoy * 453.592));   // meta POR AVE
            var pesoPuesto = Number(registroHoy.pesoMuestra) > 0;
            // Por dentro siempre se trabaja con los gramos de UN pollo; la barra solo los
            // multiplica para enseñar lo que marcaría la balanza con el grupo encima.
            var gramosPeso = registroHoy.pesoSliderG != null ? Number(registroHoy.pesoSliderG)
              : (pesoPuesto ? Math.round(Number(registroHoy.pesoMuestra) * 1000) : idealG);
            var anotarPeso = function (g) { setPedirPeso(false); setRegistroHoy(function (r) { return Object.assign({}, r, { pesoSliderG: g, pesoMuestra: (g / 1000).toFixed(3) }); }); };

            var ctxSimple = { totalPollos: camada.totalPollos, raza: camada.raza, personal: fasesPersonal };
            var fasesSimple = fasesDe(userSettings.fasesAlimento, ctxSimple);
            var faseHoySimple = faseDelDia(userSettings.fasesAlimento, diaActual, ctxSimple);
            var fasePuesta = fasesSimple.filter(function (fa) { return fa.inv === registroHoy.alimentoFase; })[0];

            return (
              <div className="fadeIn" style={{ display: "flex", flexDirection: "column", gap: 14 }}>

                {/* Día y pollos vivos, solo números */}
                <div style={{ background: C.surface, borderRadius: 18, border: "2px solid " + C.border, padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 13, color: C.textMuted, fontFamily: "'Space Mono', monospace", letterSpacing: 1 }}>{tr("DÍA")}</div>
                    <div style={{ fontSize: 38, fontWeight: 900, color: C.accent, fontFamily: "'Space Mono', monospace", lineHeight: 1 }}>{diaActual}</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <IcoSimple t="gallina" c={C.text} s={38} />
                    <div style={{ fontSize: 28, fontWeight: 900, color: C.text, fontFamily: "'Space Mono', monospace", lineHeight: 1 }}>{fmtNum(vivosPorMortalidad)}</div>
                  </div>
                </div>

                {/* Pollos muertos */}
                <div style={caja("#E5736B")}>
                  {titulo("muerto", tr("Pollos muertos"), "#E5736B")}
                  {contador(registroHoy.mortalidad, function (v) { setRegistroHoy(function (r) { return Object.assign({}, r, { mortalidad: v }); }); }, "#E5736B")}
                </div>

                {/* Peso de un pollo. La barra nace en el peso que DEBERÍA tener hoy, para que
                    el empleado solo la corra un poquito. Mientras no la toque no se guarda peso. */}
                {(function () {
                  // Todo se multiplica por la cantidad de pollos: la meta, los topes y el paso.
                  // Así la barra siempre tiene la misma cantidad de pasos y la marca de "meta"
                  // cae en el mismo punto, se pese 1 pollo o 100.
                  var minG = Math.max(20, Math.round(idealG * 0.45));
                  var maxG = Math.round(idealG * 1.6);
                  var pasoG = idealG < 300 ? 2 : idealG < 1200 ? 10 : 20;
                  var minTot = minG * avesPeso, maxTot = maxG * avesPeso, pasoTot = pasoG * avesPeso;
                  var gramos = gramosPeso;                    // de un pollo
                  var gramosTot = gramos * avesPeso;          // lo que marca la balanza
                  var pesado = pesoPuesto;
                  var dif = Math.round((gramos / idealG - 1) * 100);
                  var colDif = dif >= -5 ? "#4caf50" : dif >= -15 ? "#f5c200" : "#cc1a00";
                  var pctMeta = (idealG - minG) / (maxG - minG);
                  var anotar = anotarPeso;
                  var ponerAves = function (cuantos) {
                    if (cuantos === avesPeso) return;
                    saveSettings(Object.assign({}, userSettings, { pesoAves: cuantos }));
                  };
                  return (
                    <div style={Object.assign(caja("#E8B44C"), pesado ? {} : { border: "2px solid #E8B44C" })}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                        <IcoSimple t="balanza" c="#E8B44C" s={32} />
                        <div style={{ flex: 1, fontSize: 19, fontWeight: 900, color: "#E8B44C", fontFamily: "'Syne', sans-serif" }}>{tr("Peso")}</div>
                        {!pesado && (
                          <div className="pe-falta" style={{ background: "#E8B44C", color: "#1a0a00", fontSize: 11.5, fontWeight: 900, borderRadius: 8, padding: "4px 9px", fontFamily: "'Space Mono', monospace" }}>{tr("FALTA")}</div>
                        )}
                      </div>
                      {/* Cuántos pollos hay en la balanza */}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 7, marginBottom: 13 }}>
                        {[1, 10, 50, 100].map(function (cuantos) {
                          var on = avesPeso === cuantos;
                          return (
                            <button key={cuantos} onClick={function () { ponerAves(cuantos); }}
                              style={{ padding: "9px 2px 8px", borderRadius: 13, cursor: "pointer", minWidth: 0,
                                display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
                                border: on ? "3px solid #E8B44C" : "2px solid " + C.border,
                                background: on ? "#E8B44C2e" : C.surface }}>
                              <IcoSimple t="gallina" c={on ? "#E8B44C" : C.textMuted} s={22} />
                              <div style={{ fontSize: 18, fontWeight: 900, lineHeight: 1, fontFamily: "'Space Mono', monospace", color: on ? "#E8B44C" : C.textFaint }}>{cuantos}</div>
                            </button>
                          );
                        })}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 46, fontWeight: 900, lineHeight: 1, fontFamily: "'Space Mono', monospace", color: pesado ? colDif : C.textFaint }}>
                            {(gramosTot / 453.592).toFixed(2)}<span style={{ fontSize: 22 }}> {tr("lb")}</span>
                          </div>
                          <div style={{ fontSize: 15, color: C.textMuted, fontFamily: "'Space Mono', monospace", marginTop: 4 }}>
                            {(gramosTot / 1000).toFixed(3)} {tr("kg")}
                            {pesado && <span style={{ color: colDif, fontWeight: 800 }}>{"  ·  " + (dif >= 0 ? "+" : "") + dif + "%"}</span>}
                          </div>
                          {avesPeso > 1 && (
                            <div style={{ fontSize: 14, color: "#E8B44C", fontFamily: "'Space Mono', monospace", marginTop: 3 }}>
                              {tr("cada pollo")}: {(gramos / 453.592).toFixed(2)} {tr("lb")}
                            </div>
                          )}
                        </div>
                        <button onClick={function () {
                          if (pesado) setRegistroHoy(function (r) { return Object.assign({}, r, { pesoMuestra: "" }); });
                          else anotar(gramos);
                        }}
                          className={pesado ? "" : "pe-falta"}
                          style={{ width: 62, height: 62, borderRadius: 18, flexShrink: 0, cursor: "pointer",
                            border: pesado ? "none" : "3px solid #E8B44C", background: pesado ? "#4caf50" : "#E8B44C22",
                            display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <IcoSimple t="listo" c={pesado ? "#ffffff" : "#E8B44C"} s={34} />
                        </button>
                      </div>
                      <input type="range" className="pe-slider" min={minTot} max={maxTot} step={pasoTot} value={gramosTot}
                        onChange={function (e) { anotar(Math.max(1, Math.round(Number(e.target.value) / avesPeso))); }} style={{ marginTop: 10 }} />
                      <div style={{ position: "relative", height: 24 }}>
                        <div style={{ position: "absolute", top: 0, left: "calc(23px + (100% - 46px) * " + pctMeta.toFixed(4) + ")", transform: "translateX(-50%)", textAlign: "center" }}>
                          <div style={{ width: 2, height: 8, background: "#E8B44C", margin: "0 auto 2px" }} />
                          <div style={{ fontSize: 12, color: "#E8B44C", fontFamily: "'Space Mono', monospace", whiteSpace: "nowrap" }}>{tr("meta")}</div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Cama */}
                <div style={caja("#A78BFA")}>
                  {titulo("cama3", tr("Cama"), "#A78BFA")}
                  {escoger(registroHoy.estadoCama, function (v) { setRegistroHoy(function (r) { return Object.assign({}, r, { estadoCama: v }); }); }, OPC_CAMA_S)}
                </div>

                {/* Heces + foto */}
                <div style={caja("#4CC38A")}>
                  {titulo("heces1", tr("Heces"), "#4CC38A")}
                  {escoger(registroHoy.estadoHeces, function (v) { setRegistroHoy(function (r) { return Object.assign({}, r, { estadoHeces: v }); }); }, OPC_HECES_S)}
                  <label style={{ display: "block", marginTop: 12, cursor: "pointer" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, background: "#4CC38A1f", border: "2px dashed #4CC38A77", borderRadius: 14, padding: "12px" }}>
                      <IcoSimple t="camara" c="#4CC38A" s={32} />
                      <span style={{ fontSize: 17, fontWeight: 900, color: "#4CC38A", fontFamily: "'Syne', sans-serif" }}>{tr("Foto")}</span>
                    </div>
                    <input type="file" accept="image/*" capture="environment" style={{ display: "none" }}
                      onChange={function (e) { var fo = e.target.files[0]; if (!fo) return; comprimirImagen(fo).then(function (dataUrl) { setRegistroHoy(function (prev) { return Object.assign({}, prev, { fotoHeces: dataUrl }); }); }).catch(function () {}); }} />
                  </label>
                  {registroHoy.fotoHeces && (
                    <div style={{ marginTop: 10, borderRadius: 14, overflow: "hidden", border: "2px solid #4CC38A55", position: "relative" }}>
                      <img src={registroHoy.fotoHeces} alt="" style={{ width: "100%", maxHeight: 150, objectFit: "cover", display: "block" }} />
                      <button onClick={function () { setRegistroHoy(function (prev) { return Object.assign({}, prev, { fotoHeces: null }); }); }}
                        style={{ position: "absolute", top: 8, right: 8, width: 40, height: 40, borderRadius: 20, border: "none", background: "#000000aa", color: "#fff", fontSize: 20, cursor: "pointer" }}>✕</button>
                    </div>
                  )}
                </div>

                {/* Suplemento y vacuna: los botones salen del calendario del granjero */}
                {(function () {
                  var sups = suplementosDelCalendario(calendarioUsuario, diaActual);
                  var puestos = registroHoy.suplementos || [];
                  var alternar = function (val) {
                    setRegistroHoy(function (r) {
                      var lista = (r.suplementos || []).slice();
                      var i = lista.indexOf(val);
                      if (i >= 0) lista.splice(i, 1); else lista.push(val);
                      return Object.assign({}, r, { suplementos: lista, suplemento: lista.join(" · ") });
                    });
                  };
                  return (
                    <div style={caja("#6AA6F5")}>
                      {titulo("pastilla", tr("Suplemento"), "#6AA6F5")}
                      {sups.length === 0 ? (
                        <div style={{ fontSize: 15, color: C.textFaint, textAlign: "center", padding: "4px 0" }}>{tr("No hay vitaminas en el calendario")}</div>
                      ) : (
                        <div>
                          {/* una fila por producto: el dibujo al lado del nombre */}
                          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
                            {sups.map(function (sp) {
                              var on = puestos.indexOf(sp.val) >= 0;
                              return (
                                <button key={sp.val} onClick={function () { alternar(sp.val); }}
                                  style={{ padding: "12px 12px", borderRadius: 14, cursor: "pointer", minWidth: 0, position: "relative", textAlign: "left",
                                    display: "flex", alignItems: "center", gap: 8,
                                    border: on ? "3px solid " + sp.color : "2px solid " + (sp.hoy ? sp.color + "99" : C.border),
                                    background: on ? sp.color + "2e" : C.surface }}>
                                  {sp.hoy && (
                                    <div style={{ position: "absolute", top: -8, right: -3, background: sp.color, color: "#11131A", fontSize: 10, fontWeight: 900, borderRadius: 7, padding: "2px 6px", fontFamily: "'Space Mono', monospace" }}>{tr("HOY")}</div>
                                  )}
                                  <IcoSimple t={sp.ico} c={on ? sp.color : C.textMuted} s={30} />
                                  <div style={{ flex: 1, minWidth: 0, fontSize: 14.5, fontWeight: 800, lineHeight: 1.2,
                                    color: on ? sp.color : C.textFaint, fontFamily: "'Syne', sans-serif", overflowWrap: "break-word" }}>{sp.nombre}</div>
                                </button>
                              );
                            })}
                          </div>
                          <div style={{ marginTop: 11, textAlign: "center", fontSize: 20, fontWeight: 900, lineHeight: 1.2,
                            fontFamily: "'Syne', sans-serif", color: puestos.length ? "#6AA6F5" : C.textFaint }}>
                            {puestos.length ? puestos.join(" · ") : tr("Nada hoy")}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Sacos de alimento */}
                <div style={caja("#F2994A")}>
                  {titulo("saco", tr("Sacos"), "#F2994A")}
                  {contador(registroHoy.alimentoSacos, function (v) { setRegistroHoy(function (r) { return Object.assign({}, r, { alimentoSacos: v, alimentoFase: (Number(v) > 0 && !r.alimentoFase) ? faseHoySimple.inv : r.alimentoFase }); }); }, "#F2994A")}
                  {Number(registroHoy.alimentoSacos) > 0 && (
                    <div style={{ marginTop: 14 }}>
                      <div style={{ display: "flex", gap: 8 }}>
                        {fasesSimple.map(function (fa) {
                          var on = registroHoy.alimentoFase === fa.inv;
                          return (
                            <button key={fa.key} onClick={function () { setRegistroHoy(function (r) { return Object.assign({}, r, { alimentoFase: on ? "" : fa.inv }); }); }}
                              style={{ flex: 1, padding: "12px 2px 10px", borderRadius: 16, cursor: "pointer",
                                display: "flex", flexDirection: "column", alignItems: "center",
                                border: on ? "3px solid " + fa.color : "2px solid " + C.border,
                                background: on ? fa.color + "2e" : C.surface }}>
                              <IcoSimple t={ICO_FASE[fa.inv] || "saco"} c={on ? fa.color : C.textMuted} s={38} />
                              <div style={{ fontSize: 11.5, fontWeight: 800, color: on ? fa.color : C.textFaint, marginTop: 5, fontFamily: "'Space Mono', monospace" }}>D{fa.d1}-{fa.d2}</div>
                            </button>
                          );
                        })}
                      </div>
                      <div style={{ marginTop: 11, textAlign: "center", fontSize: 23, fontWeight: 900, lineHeight: 1.15,
                        fontFamily: "'Syne', sans-serif", color: fasePuesta ? fasePuesta.color : C.textFaint }}>
                        {fasePuesta ? tr(fasePuesta.nombre) : tr("Toca un dibujo")}
                      </div>
                    </div>
                  )}
                </div>

                {/* Guardar. Si falta el peso no se guarda callado: se pregunta con dos botones. */}
                {(pedirPeso && !pesoPuesto) ? (
                  <div style={{ background: C.card, border: "2px solid #E8B44C", borderRadius: 20, padding: 16 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                      <IcoSimple t="balanza" c="#E8B44C" s={34} />
                      <div style={{ fontSize: 21, fontWeight: 900, color: "#E8B44C", fontFamily: "'Syne', sans-serif" }}>{tr("¿Pesaste hoy?")}</div>
                    </div>
                    <button onClick={function () { setPedirPeso(false); guardarRegistro({ pesoSliderG: gramosPeso, pesoMuestra: (gramosPeso / 1000).toFixed(3) }); }}
                      style={{ width: "100%", padding: "18px", borderRadius: 16, border: "none", cursor: "pointer", background: "#4caf50", color: "#fff",
                        fontSize: 24, fontWeight: 900, fontFamily: "'Syne', sans-serif", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
                      <IcoSimple t="listo" c="#ffffff" s={30} />
                      {(gramosPeso * avesPeso / 453.592).toFixed(2)} {tr("lb")}
                    </button>
                    <button onClick={function () { setPedirPeso(false); guardarRegistro(); }}
                      style={{ width: "100%", marginTop: 10, padding: "16px", borderRadius: 16, border: "2px solid " + C.border, cursor: "pointer",
                        background: "transparent", color: C.textMuted, fontSize: 18, fontWeight: 900, fontFamily: "'Syne', sans-serif" }}>
                      {tr("NO PESÉ")}
                    </button>
                  </div>
                ) : (
                  <button onClick={function () { if (!pesoPuesto) { setPedirPeso(true); return; } guardarRegistro(); }}
                    style={{ width: "100%", padding: "22px", borderRadius: 20, border: "none", cursor: "pointer",
                      background: savedOk ? "#4caf50" : C.accent, color: savedOk ? "#fff" : "#1a0a00",
                      fontSize: 26, fontWeight: 900, fontFamily: "'Syne', sans-serif", display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }}>
                    <IcoSimple t={savedOk ? "listo" : "guardar"} c={savedOk ? "#ffffff" : "#1a0a00"} s={34} />
                    {savedOk ? tr("LISTO") : tr("GUARDAR")}
                  </button>
                )}
              </div>
            );
          })()
        )}
      </div>
    );
  };

  // --- CALENDARIO EDITABLE ---
  const Calendario = ({ onCalendarioChange }) => {
    const storageKey = "pe_calendario_" + session.ownerEmail;
    const [eventos, setEventos] = useState(() => {
      try { const saved = localStorage.getItem(storageKey); return saved ? JSON.parse(saved) : CALENDARIO_OFICIAL; }
      catch { return CALENDARIO_OFICIAL; }
    });
    const [filtro, setFiltro] = useState("todos");
    const [editando, setEditando] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [agregando, setAgregando] = useState(false);
    const [nuevoEvento, setNuevoEvento] = useState({ dia: "", producto: "", funcion: "", producto_nombre: "", dosis: "", via: "Agua", tipo: "vitamina" });

    // Cargar calendario desde Supabase al iniciar
    useEffect(function() {
      SB.select("user_settings", "email=eq." + encodeURIComponent(session.ownerEmail)).then(function(rows) {
        if (rows && rows[0] && rows[0].calendario) {
          var remoto = rows[0].calendario;
          setEventos(remoto);
          try { localStorage.setItem(storageKey, JSON.stringify(remoto)); } catch {}
        }
      }).catch(function() {});
    }, []);

    function saveEventos(list) {
      if (onCalendarioChange) onCalendarioChange(list);
      setEventos(list);
      try { localStorage.setItem(storageKey, JSON.stringify(list)); } catch {}
      // Sincronizar con Supabase
      SB.upsert("user_settings", { email: session.ownerEmail, calendario: list }).catch(function() {});
    }
    function startEdit(ev) { setEditando(ev.dia + "_" + ev.producto); setEditForm({...ev}); }
    function saveEdit() {
      const updated = eventos.map(e => (e.dia + "_" + e.producto === editando) ? editForm : e);
      saveEventos(updated); setEditando(null);
    }
    function deleteEvento(ev) { saveEventos(eventos.filter(e => !(e.dia === ev.dia && e.producto === ev.producto))); }
    function addEvento() {
      if (!nuevoEvento.dia || !nuevoEvento.producto) return;
      saveEventos([...eventos, { ...nuevoEvento, dia: Number(nuevoEvento.dia) }].sort((a,b) => a.dia - b.dia));
      setNuevoEvento({ dia: "", producto: "", funcion: "", producto_nombre: "", dosis: "", via: "Agua", tipo: "vitamina" });
      setAgregando(false);
    }
    function resetear() { if (window.confirm && window.confirm(tr("¿Resetear al calendario original?"))) { saveEventos(CALENDARIO_OFICIAL); } else { saveEventos(CALENDARIO_OFICIAL); } }
    const [calGuardadoOk, setCalGuardadoOk] = useState(false);
    usarOcupado(agregando || editando != null);
    function forceGuardarCalendario() {
      try { localStorage.setItem(storageKey, JSON.stringify(eventos)); } catch {}
      if (onCalendarioChange) onCalendarioChange(eventos);
      SB.upsert("user_settings", { email: session.ownerEmail, calendario: eventos }).then(function() {
        setCalGuardadoOk(true); setTimeout(function() { setCalGuardadoOk(false); }, 2500);
      }).catch(function() {
        setCalGuardadoOk(true); setTimeout(function() { setCalGuardadoOk(false); }, 2500);
      });
    }

    const filtrados = filtro === "todos" ? eventos : filtro === "activos" ? eventos.filter(e => e.tipo !== "agua") : eventos.filter(e => e.tipo === filtro);
    const inputS = { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, padding: "7px 10px", fontSize: 14, fontFamily: "'Syne', sans-serif", outline: "none", width: "100%" };

    return (
      <div className="fadeIn" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {/* Filtros */}
        <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 2 }}>
          {[["todos","Todos"],["activos","Activos"],["vacuna","Vacunas"],["vitamina","Vitaminas"],["probiotico","Probiótico"],["desparasitante","Desparasit."],["betaina","Betaína"]].map(([val,lbl]) => (
            <button key={val} onClick={() => setFiltro(val)} style={{ flexShrink: 0, padding: "6px 12px", borderRadius: 20, border: `1px solid ${filtro===val ? C.accent : C.border}`, background: filtro===val ? C.accent+"22" : C.surface, color: filtro===val ? C.accent : C.textMuted, fontSize: 15, fontFamily: "'Syne', sans-serif", cursor: "pointer", fontWeight: filtro===val ? 700 : 400 }}>{tr(lbl)}</button>
          ))}
        </div>

        {/* Botones acción */}
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setAgregando(!agregando)} style={{ flex: 1, background: agregando ? C.surface : C.warn, color: agregando ? C.textMuted : "#1a0a00", border: agregando ? `1px solid ${C.border}` : "none", borderRadius: 10, padding: "10px", fontSize: 14, fontWeight: 700, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>
            {agregando ? tr("✕ Cancelar") : tr("+ Agregar evento")}
          </button>
          <button onClick={resetear} style={{ background: C.surface, color: C.textFaint, border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 14px", fontSize: 15, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>{tr("↺ Resetear")}</button>
        </div>

        {/* Botón Guardar calendario en la nube */}
        <button onClick={forceGuardarCalendario} style={{ width: "100%", background: calGuardadoOk ? C.green : C.accent, color: "#1a0a00", border: "none", borderRadius: 10, padding: "12px", fontSize: 15, fontWeight: 800, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>
          {calGuardadoOk ? tr("✅ Calendario guardado") : tr("💾 Guardar calendario")}
        </button>

        {/* Formulario nuevo evento */}
        {agregando && (
          <Card style={{ border: `1px solid ${C.warn}44` }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.warn, marginBottom: 12 }}>{tr("Nuevo evento")}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
              <div><div style={{ fontSize: 15, color: C.textFaint, marginBottom: 3 }}>{tr("DÍA")}</div><input type="number" min="0" max="120" value={nuevoEvento.dia} onChange={e => setNuevoEvento(n => ({...n, dia: e.target.value}))} style={inputS} placeholder={tr("Ej: 42")} /></div>
              <div><div style={{ fontSize: 15, color: C.textFaint, marginBottom: 3 }}>{tr("TIPO")}</div>
                <select value={nuevoEvento.tipo} onChange={e => setNuevoEvento(n => ({...n, tipo: e.target.value}))} style={inputS}>
                  {["vacuna","vitamina","probiotico","desparasitante","betaina","agua","recepcion"].map(t => <option key={t} value={t}>{tr(t)}</option>)}
                </select>
              </div>
            </div>
            <div style={{ fontSize: 12, color: C.textFaint, marginBottom: 8 }}>{tr("💡 Puedes usar cualquier número de día (40, 45, 50…) para adaptar el calendario a la duración de tu ciclo.")}</div>
            {[["NOMBRE DEL EVENTO","producto","Ej: Newcastle"],["FUNCIÓN","funcion","Ej: Prevención"],["PRODUCTO","producto_nombre","Ej: Hipragumboro"],["DOSIS","dosis","Ej: 1 frasco / 1000 pollos"],["VÍA","via","Agua / Inyectable"]].map(([lbl,key,ph]) => (
              <div key={key} style={{ marginBottom: 7 }}><div style={{ fontSize: 15, color: C.textFaint, marginBottom: 3 }}>{tr(lbl)}</div><input value={nuevoEvento[key]} onChange={e => setNuevoEvento(n => ({...n, [key]: e.target.value}))} style={inputS} placeholder={tr(ph)} /></div>
            ))}
            <button onClick={addEvento} style={{ width: "100%", background: C.green, color: "#fff", border: "none", borderRadius: 8, padding: "11px", fontWeight: 700, fontSize: 15, fontFamily: "'Syne', sans-serif", cursor: "pointer", marginTop: 4 }}>{tr("Guardar evento")}</button>
          </Card>
        )}

        {/* Lista eventos */}
        {filtrados.map(ev => {
          const evKey = ev.dia + "_" + ev.producto;
          const isEdit = editando === evKey;
          const col = TIPO_COLORES[ev.tipo] || C.accent;
          return (
            <Card key={evKey} style={{ border: `1px solid ${col}44` }}>
              {isEdit ? (
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.accent, marginBottom: 10 }}>{tr("Editando Día")} {editForm.dia}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                    <div><div style={{ fontSize: 15, color: C.textFaint, marginBottom: 3 }}>{tr("DÍA")}</div><input type="number" value={editForm.dia} onChange={e => setEditForm(f => ({...f, dia: Number(e.target.value)}))} style={inputS} /></div>
                    <div><div style={{ fontSize: 15, color: C.textFaint, marginBottom: 3 }}>{tr("TIPO")}</div>
                      <select value={editForm.tipo} onChange={e => setEditForm(f => ({...f, tipo: e.target.value}))} style={inputS}>
                        {["vacuna","vitamina","probiotico","desparasitante","betaina","agua","recepcion"].map(t => <option key={t} value={t}>{tr(t)}</option>)}
                      </select>
                    </div>
                  </div>
                  {[["NOMBRE","producto"],["FUNCIÓN","funcion"],["PRODUCTO","producto_nombre"],["DOSIS","dosis"],["VÍA","via"]].map(([lbl,key]) => (
                    <div key={key} style={{ marginBottom: 7 }}><div style={{ fontSize: 15, color: C.textFaint, marginBottom: 3 }}>{tr(lbl)}</div><input value={editForm[key]||""} onChange={e => setEditForm(f => ({...f, [key]: e.target.value}))} style={inputS} /></div>
                  ))}
                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <button onClick={saveEdit} style={{ flex: 1, background: C.green, color: "#fff", border: "none", borderRadius: 8, padding: "10px", fontWeight: 700, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>{tr("✅ Guardar")}</button>
                    <button onClick={() => setEditando(null)} style={{ flex: 1, background: "none", color: C.textMuted, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px", fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>{tr("Cancelar")}</button>
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <span style={{ background: col+"22", color: col, border: `1px solid ${col}44`, borderRadius: 4, padding: "2px 7px", fontSize: 14, fontFamily: "'Space Mono', monospace", fontWeight: 700 }}>{tr("DÍA")} {ev.dia}</span>
                        <span style={{ fontSize: 14, color: col, fontFamily: "'Space Mono', monospace" }}>{tr(ev.tipo)}</span>
                      </div>
                      <div style={{ fontSize: 16, fontWeight: 700 }}>{tr(ev.producto)}</div>
                      {ev.funcion && <div style={{ fontSize: 14, color: C.textMuted, marginTop: 2 }}>{tr(ev.funcion)}</div>}
                      {ev.producto_nombre && <div style={{ fontSize: 15, color: C.textFaint, marginTop: 2 }}>{tr(ev.producto_nombre)}{ev.dosis ? " · " + tr(ev.dosis) : ""}</div>}
                    </div>
                    <div style={{ display: "flex", gap: 6, marginLeft: 8 }}>
                      <button onClick={() => startEdit(ev)} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMuted, cursor: "pointer", padding: "5px 8px", fontSize: 14 }}>✏️</button>
                      {!["agua"].includes(ev.tipo) && <button onClick={() => deleteEvento(ev)} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", fontSize: 16, padding: "2px 4px" }}>×</button>}
                    </div>
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    );
  };

  // --- COSTOS DETALLADOS ---
  const Costos = () => {
    const [showGastoForm, setShowGastoForm] = useState(false);
    const [newGasto, setNewGasto] = useState({ categoria: "pollitos", descripcion: "", monto: "" });
    // Escaneo del recibo con la cámara: la IA lee el total y propone la categoría.
    const [reciboEstado, setReciboEstado] = useState(null);   // null · "leyendo" · {ok, texto}
    function escanearRecibo(file) {
      if (!file) return;
      setReciboEstado({ leyendo: true });
      comprimirImagen(file, 1400, 0.8).then(function (dataUrl) {
        var base64 = dataUrl.split(",")[1];
        var mediaType = dataUrl.split(";")[0].split(":")[1] || "image/jpeg";
        return fetch("/api/analyze-heces", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            tipo: "recibo", imageBase64: base64, mediaType: mediaType,
            idioma: (userSettings && userSettings.idioma) || "es",
            email: session && session.email, token: session && session.token,
          }),
        }).then(function (r) { return r.json(); });
      }).then(function (d) {
        if (!d || d.error) { setReciboEstado({ texto: d && d.error ? d.error : tr("No se pudo leer el recibo.") }); return; }
        if (d.es_recibo === false) {
          setReciboEstado({ texto: tr("Esa foto no parece un recibo.") + " " + (d.descripcion || "") });
          return;
        }
        // Se rellena el formulario, pero NO se guarda: el granjero revisa y confirma.
        setNewGasto(function (g) {
          return {
            categoria: d.categoria || g.categoria,
            descripcion: [d.proveedor, d.descripcion].filter(Boolean).join(" · ").slice(0, 80) || g.descripcion,
            monto: d.total > 0 ? String(d.total) : g.monto,
          };
        });
        setReciboEstado({
          ok: true,
          texto: d.total > 0
            ? tr("Leído del recibo: {t}. Revísalo y guarda.", { t: $ + " " + d.total })
            : tr("No se distinguió el total. Escríbelo a mano."),
          dudoso: d.confianza === "baja" || !(d.total > 0),
        });
      }).catch(function () {
        setReciboEstado({ texto: tr("Error de conexión al leer el recibo.") });
      });
    }
    const [showVentaForm, setShowVentaForm] = useState(false);
    const [newVenta, setNewVenta] = useState({ pollos: "", libras: "", precioLb: "", comprador: "", pesoCanastas: "" });
    usarOcupado(showGastoForm || showVentaForm);
    const gastos = camada.gastos || [];
    const ventas = camada.ventas || [];

    // Totales de ventas
    const totalPollosVendidos = ventas.reduce((a, v) => a + Number(v.pollos || 0), 0);
    const totalLibrasVendidas = ventas.reduce((a, v) => a + Number(v.libras || 0), 0);
    const ingresoTotal = ventas.reduce((a, v) => a + Number(v.libras || 0) * Number(v.precioLb || 0), 0);
    const gananciaReal = ingresoTotal - costoTotal;
    const margenPct = ingresoTotal > 0 ? ((gananciaReal / ingresoTotal) * 100).toFixed(1) : null;
    const roi = costoTotal > 0 ? ((gananciaReal / costoTotal) * 100).toFixed(1) : null;
    const lbPorAveVendida = totalPollosVendidos > 0 ? (totalLibrasVendidas / totalPollosVendidos).toFixed(2) : null;
    const gananciaColor = gananciaReal > 0 ? C.green : gananciaReal < 0 ? C.danger : C.textFaint;

    // Opciones dropdown pollos (0-10000, incrementos variables)
    const opcionesPollos = [];
    for (let i = 0; i <= 50; i++) opcionesPollos.push(i);
    for (let i = 55; i <= 100; i += 5) opcionesPollos.push(i);
    for (let i = 110; i <= 300; i += 10) opcionesPollos.push(i);
    for (let i = 325; i <= 500; i += 25) opcionesPollos.push(i);
    for (let i = 550; i <= 1000; i += 50) opcionesPollos.push(i);
    for (let i = 1100; i <= 5000; i += 100) opcionesPollos.push(i);
    for (let i = 5500; i <= 10000; i += 500) opcionesPollos.push(i);

    function agregarVenta() {
      if (!newVenta.pollos || !newVenta.libras || !newVenta.precioLb || Number(newVenta.precioLb) <= 0) return null;
      const librasNetas = Math.max(0, Number(newVenta.libras) - Number(newVenta.pesoCanastas || 0));
      const v = { id: Date.now(), ...newVenta, pollos: Number(newVenta.pollos), libras: librasNetas, librasBrutas: Number(newVenta.libras), pesoCanastas: Number(newVenta.pesoCanastas || 0), precioLb: Number(newVenta.precioLb), fecha: new Date().toISOString().split("T")[0], _ts: Date.now() };
      saveCamada({ ...camada, ventas: [...ventas, v] });
      setNewVenta({ pollos: "", libras: "", precioLb: "", comprador: "", pesoCanastas: "" });
      setShowVentaForm(false);
      return v;
    }

    function eliminarVenta(id) {
      addDeletedItem(session.ownerEmail, "ventas", id);
      const nuevasVentas = ventas.filter(v => v.id !== id);
      saveCamada({ ...camada, ventas: nuevasVentas });
      // PATCH directo a Supabase de SOLO la columna ventas: el borrado queda en la nube
      // aunque el upsert grande de la camada falle (payload pesado, dispositivo desactualizado, etc.)
      SB.update("camadas", { id: camada.id }, { ventas: nuevasVentas, updated_at: new Date().toISOString() }).catch(() => {});
    }
    function agregarGasto() {
      if (!newGasto.monto || Number(newGasto.monto) <= 0) return;
      const g = { id: Date.now(), ...newGasto, monto: Number(newGasto.monto), fecha: new Date().toISOString().split("T")[0], _ts: Date.now() };
      saveCamada({ ...camada, gastos: [...gastos, g] });
      setNewGasto({ categoria: "pollitos", descripcion: "", monto: "" });
      setShowGastoForm(false);
    }
    function eliminarGasto(id) {
      addDeletedItem(session.ownerEmail, "gastos", id);
      const nuevosGastos = gastos.filter(g => g.id !== id);
      saveCamada({ ...camada, gastos: nuevosGastos });
      // PATCH directo a Supabase de SOLO la columna gastos (misma razón que eliminarVenta)
      SB.update("camadas", { id: camada.id }, { gastos: nuevosGastos, updated_at: new Date().toISOString() }).catch(() => {});
    }

    return (
      <div className="fadeIn" style={{ display: "flex", flexDirection: "column", gap: 14 }}>

        {/* ─── ESTADO FINANCIERO ─── */}
        <Card style={{ background: "#162000", border: `2px solid ${ingresoTotal > 0 ? (gananciaReal >= 0 ? C.green : C.danger) : C.border}55` }}>
          <div style={{ fontSize: 15, color: C.green, fontFamily: "'Space Mono', monospace", letterSpacing: 1, marginBottom: 14 }}>{tr("💰 ESTADO FINANCIERO")}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
            <div style={{ background: C.card, borderRadius: 10, padding: "12px 14px", border: `1px solid ${C.danger}44` }}>
              <div style={{ fontSize: 15, color: C.danger, fontFamily: "'Space Mono', monospace", marginBottom: 3 }}>{t("inversionTotal") || tr("INVERSIÓN TOTAL")}</div>
              <div style={{ fontSize: 20, fontWeight: 800, fontFamily: "'Space Mono', monospace", color: C.danger }}>{$} {fmtNum(costoTotal)}</div>
              <div style={{ fontSize: 14, color: C.textMuted, marginTop: 4 }}>{$} {costoPorAve} / ave</div>
            </div>
            <div style={{ background: C.card, borderRadius: 10, padding: "12px 14px", border: `1px solid ${ingresoTotal > 0 ? C.accent : C.border}44` }}>
              <div style={{ fontSize: 15, color: C.accent, fontFamily: "'Space Mono', monospace", marginBottom: 3 }}>{tr("INGRESO TOTAL")}</div>
              <div style={{ fontSize: 20, fontWeight: 800, fontFamily: "'Space Mono', monospace", color: ingresoTotal > 0 ? C.accent : C.textFaint }}>
                {ingresoTotal > 0 ? `${$} ${fmtNum(ingresoTotal)}` : "—"}
              </div>
              {lbPorAveVendida && <div style={{ fontSize: 14, color: C.textMuted, marginTop: 4 }}>{lbPorAveVendida} {tr("lb / ave vendida")}</div>}
            </div>
          </div>
          <div style={{ background: C.surface, borderRadius: 12, padding: "16px", border: `2px solid ${gananciaColor}44`, marginBottom: ingresoTotal > 0 ? 14 : 0 }}>
            <div style={{ fontSize: 14, color: C.textFaint, fontFamily: "'Space Mono', monospace", marginBottom: 6 }}>
              {gananciaReal > 0 ? tr("✅ GANANCIA NETA") : ingresoTotal > 0 ? tr("❌ PÉRDIDA NETA") : tr("GANANCIA NETA")}
            </div>
            <div style={{ fontSize: 34, fontWeight: 800, fontFamily: "'Space Mono', monospace", color: gananciaColor }}>
              {ingresoTotal > 0 ? `${$} ${fmtNum(Math.abs(gananciaReal))}` : "—"}
            </div>
            {margenPct && (
              <div style={{ display: "flex", gap: 20, marginTop: 10 }}>
                <div style={{ fontSize: 14, color: C.textMuted }}>{tr("Margen:")} <span style={{ color: Number(margenPct) > 0 ? C.green : C.danger, fontWeight: 700 }}>{margenPct}%</span></div>
                <div style={{ fontSize: 14, color: C.textMuted }}>{tr("ROI:")} <span style={{ color: Number(roi) > 0 ? C.green : C.danger, fontWeight: 700 }}>{roi}%</span></div>
              </div>
            )}
          </div>
          {ingresoTotal > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {[
                [t("pollosVendidos") || "Pollos vendidos", fmtNum(totalPollosVendidos) + tr(" aves"), C.text],
                [t("librasTotales") || "Libras totales vendidas", `${fmtNum(totalLibrasVendidas)} lb`, C.text],
                [t("ingresoBruto") || "Ingreso bruto", `${$} ${fmtNum(ingresoTotal)}`, C.accent],
                [t("totalInvertido") || "Total invertido", `${$} ${fmtNum(costoTotal)}`, C.danger],
                [t("gananciaNeta") || "Ganancia neta", `${$} ${fmtNum(Math.abs(gananciaReal))}`, gananciaColor],
              ].map(([l, v, col]) => (
                <div key={l} style={{ display: "flex", justifyContent: "space-between", fontSize: 14, padding: "5px 0", borderBottom: `1px solid ${C.border}` }}>
                  <span style={{ color: C.textMuted }}>{l}</span>
                  <span style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, color: col }}>{v}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* ─── REGISTRO DE VENTAS ─── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.accent }}>🐔 {t("registrarVenta") || tr("Ventas del día")}</div>
            <div style={{ fontSize: 15, color: C.textFaint }}>{ventas.length} {tr("lote")}{ventas.length !== 1 ? "s" : ""} · {fmtNum(totalPollosVendidos)} {tr("aves ·")} {fmtNum(totalLibrasVendidas)} {tr("lb")}</div>
          </div>
          <Btn onClick={function () {
            /* Cancelar cancela de verdad: se suelta también el cliente y lo tecleado. Si no,
               la venta siguiente arrancaba con el comprador de la que se acababa de anular. */
            if (showVentaForm) {
              setClienteSel(null); setNuevoCli(null); setModoCliente(""); setVentaPaso(0);
              setNewVenta({ pollos: "", libras: "", precioLb: "", comprador: "", pesoCanastas: "" });
            }
            setShowVentaForm(function (v) { return !v; });
          }} color={showVentaForm ? C.surface : C.accent} textColor={showVentaForm ? C.textMuted : "#1a0a00"} outline={showVentaForm} style={{ width: "auto", padding: "8px 14px", fontSize: 15 }}>
            {showVentaForm ? tr("✕ Cancelar") : "+ " + (t("nuevaVenta") || tr("Nueva venta"))}
          </Btn>
        </div>

        {/* Formulario nueva venta */}
        {showVentaForm && (
          <Card style={{ border: `1px solid ${C.accent}55` }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.accent, marginBottom: 14 }}>{tr("Nueva Venta")}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Input label={tr("Cantidad de pollos vendidos")} type="number" min="0" value={newVenta.pollos} onChange={v => setNewVenta(n => ({ ...n, pollos: v }))} placeholder={tr("Ej: 200")} />
              <Input label={tr("Libras totales vendidas en este lote")} type="number" min="0" step="0.5" value={newVenta.libras} onChange={v => setNewVenta(n => ({ ...n, libras: v }))} placeholder={tr("Ej: 450")} />
              <Input label={tr("Restar peso de canastas (lb) — opcional")} type="number" min="0" step="0.5"
                value={newVenta.pesoCanastas} onChange={v => setNewVenta(n => ({ ...n, pesoCanastas: v }))}
                placeholder={tr("Ej: 12.5 (peso total canastas vacías)")} />
              {newVenta.libras && Number(newVenta.pesoCanastas) > 0 && (
                <div style={{ background: "#1a1a00", borderRadius: 6, padding: "6px 12px", fontSize: 13, color: C.textMuted }}>{tr("🧺 Lb brutas:")} <b style={{color:C.text}}>{newVenta.libras} {tr("lb")}</b> {tr("— canastas:")} <b style={{color:C.warn}}>{newVenta.pesoCanastas} {tr("lb")}</b> {tr("= neto:")} <b style={{color:C.accent}}>{Math.max(0, Number(newVenta.libras) - Number(newVenta.pesoCanastas)).toFixed(1)} {tr("lb")}</b>
                </div>
              )}

              <Input label={`${t("precioPorLibra")} (${$})`} type="number" min="0" step="0.01"
                value={newVenta.precioLb} onChange={v => setNewVenta(n => ({ ...n, precioLb: v }))}
                placeholder={tr("Ej: 49, 60, 75.5...")} />
              <Input label={tr("Comprador / referencia (opcional)")}
                value={newVenta.comprador} onChange={v => setNewVenta(n => ({ ...n, comprador: v }))} placeholder={tr("Ej: Lucas, Papolo, Amigo...")} />
              {newVenta.pollos && newVenta.libras && newVenta.precioLb && Number(newVenta.precioLb) > 0 && (
                <div style={{ background: "#1a2800", borderRadius: 8, padding: "10px 14px", fontSize: 14, color: C.textMuted }}>
                  {(() => { const neto = Math.max(0, Number(newVenta.libras) - Number(newVenta.pesoCanastas || 0)); return (<>
                    <div>{tr("Lb netas:")} <span style={{ color: C.text, fontFamily: "'Space Mono', monospace" }}>{neto.toFixed(1)} {tr("lb")}</span></div>
                    <div>{tr("Lb / ave:")} <span style={{ color: C.text, fontFamily: "'Space Mono', monospace" }}>{(neto / Number(newVenta.pollos)).toFixed(2)} {tr("lb")}</span></div>
                    <div>{tr("Total lote:")} <span style={{ color: C.accent, fontWeight: 700, fontFamily: "'Space Mono', monospace" }}>{$} {fmtNum(neto * Number(newVenta.precioLb))}</span></div>
                  </>); })()}
                </div>
              )}
              <Btn onClick={function() { var v = agregarVenta(); if (v) compartirFacturaPDF(v, camada, session, userSettings); }} color="#25D366" textColor="#fff">{tr("📲 Guardar y generar factura WhatsApp")}</Btn>
              <Btn onClick={agregarVenta} color={C.green} textColor="#fff">{tr("Guardar venta")}</Btn>
            </div>
          </Card>
        )}

        {/* ─── GASTOS ─── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.warn }}>{tr("📦 Gastos")}</div>
          <Btn onClick={() => setShowGastoForm(s => !s)} color={showGastoForm ? C.surface : C.warn} textColor={showGastoForm ? C.textMuted : "#1a0a00"} outline={showGastoForm} style={{ width: "auto", padding: "8px 14px", fontSize: 15 }}>
            {showGastoForm ? tr("✕ Cancelar") : "+ " + (t("agregarGasto") || tr("Agregar gasto"))}
          </Btn>
        </div>

        {showGastoForm && (
          <Card style={{ border: `1px solid ${C.warn}44` }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.warn, marginBottom: 14 }}>{tr("Nuevo Gasto")}</div>

            {/* Escanear el recibo: la cámara lee el total y llena el formulario */}
            <label style={{ display: "block", cursor: reciboEstado && reciboEstado.leyendo ? "wait" : "pointer", marginBottom: 12 }}>
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                background: C.warn + "18", border: "1.5px dashed " + C.warn + "88", borderRadius: 12,
                padding: "13px 12px", color: C.warn, fontWeight: 800, fontSize: 14, fontFamily: "'Syne', sans-serif",
              }}>
                <span style={{ fontSize: 20 }}>📸</span>
                {(reciboEstado && reciboEstado.leyendo) ? tr("Leyendo el recibo…") : tr("Escanear recibo con la cámara")}
              </div>
              <input type="file" accept="image/*" capture="environment" style={{ display: "none" }}
                disabled={!!(reciboEstado && reciboEstado.leyendo)}
                onChange={function (e) { var f = e.target.files && e.target.files[0]; e.target.value = ""; escanearRecibo(f); }} />
            </label>

            {/* Subir el recibo desde la galería: sirve cuando la foto la tomó otra persona
                y se la mandaron por WhatsApp (sin "capture", el teléfono abre la galería). */}
            <label id="subirReciboGaleria" style={{ display: "block", cursor: reciboEstado && reciboEstado.leyendo ? "wait" : "pointer", marginBottom: 12 }}>
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                background: C.surface, border: "1.5px solid " + C.border, borderRadius: 12,
                padding: "13px 12px", color: C.text, fontWeight: 800, fontSize: 14, fontFamily: "'Syne', sans-serif",
              }}>
                <span style={{ fontSize: 20 }}>🖼️</span>
                {tr("Subir recibo desde la galería")}
              </div>
              <input type="file" accept="image/*" style={{ display: "none" }}
                disabled={!!(reciboEstado && reciboEstado.leyendo)}
                onChange={function (e) { var f = e.target.files && e.target.files[0]; e.target.value = ""; escanearRecibo(f); }} />
            </label>
            {reciboEstado && !reciboEstado.leyendo && reciboEstado.texto && (
              <div style={{
                marginBottom: 12, padding: "9px 12px", borderRadius: 10, fontSize: 13, lineHeight: 1.5,
                background: (reciboEstado.ok && !reciboEstado.dudoso ? C.green : C.warn) + "1f",
                border: "1px solid " + (reciboEstado.ok && !reciboEstado.dudoso ? C.green : C.warn) + "55",
                color: C.text,
              }}>
                {reciboEstado.ok && !reciboEstado.dudoso ? "✅ " : "⚠️ "}{reciboEstado.texto}
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
              <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <span style={{ fontSize: 14, color: C.textMuted, fontFamily: "'Space Mono', monospace", letterSpacing: 1, textTransform: "uppercase" }}>{tr("Categoría")}</span>
                <select value={newGasto.categoria} onChange={e => setNewGasto(g => ({ ...g, categoria: e.target.value }))}
                  style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, padding: "11px 13px", fontSize: 14, fontFamily: "'Syne', sans-serif", outline: "none" }}>
                  {CATEGORIAS_GASTOS.map(cat => <option key={cat.id} value={cat.id}>{cat.icono} {tr(cat.label)}</option>)}
                </select>
              </label>
              <Input label={tr("Descripción (opcional)")} value={newGasto.descripcion}
                onChange={v => setNewGasto(g => ({ ...g, descripcion: v }))} placeholder={tr("Ej: 4 sacos inicio a 1395")} />
              <Input label={`${t("monto")} (${$})`} type="number" min="0" value={newGasto.monto}
                onChange={v => setNewGasto(g => ({ ...g, monto: v }))} placeholder="0" />
              <Btn onClick={agregarGasto} color={C.green} textColor="#fff">{tr("Guardar gasto")}</Btn>
            </div>
          </Card>
        )}

        {CATEGORIAS_GASTOS.map(cat => {
          const items = gastos.filter(g => g.categoria === cat.id);
          if (items.length === 0) return null;
          const totalCat = items.reduce((a, g) => a + Number(g.monto || 0), 0);
          return (
            <Card key={cat.id}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: cat.color }}>{cat.icono} {tr(cat.label)}</div>
                <div style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 15, color: cat.color }}>{$} {fmtNum(totalCat)}</div>
              </div>
              {items.map(g => (
                <div key={g.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderTop: `1px solid ${C.border}` }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14 }}>{g.descripcion || tr(cat.label)}</div>
                    <div style={{ fontSize: 14, color: C.textFaint, fontFamily: "'Space Mono', monospace" }}>{g.fecha}</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 14 }}>{$} {fmtNum(g.monto)}</span>
                    <button onClick={() => eliminarGasto(g.id)} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", fontSize: 18 }}>×</button>
                  </div>
                </div>
              ))}
            </Card>
          );
        })}

        {gastos.length === 0 && !showGastoForm && ventas.length === 0 && !showVentaForm && (
          <div style={{ textAlign: "center", color: C.textFaint, fontSize: 15, padding: "20px 0" }}>{tr("Sin movimientos registrados aún.")}</div>
        )}

        {/* ─── REPORTES PDF ─── */}
        {(ventas.length > 0 || gastos.length > 0) && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
            {gastos.length > 0 && <Btn onClick={() => imprimirReporteCostos(camada, $)} color={C.warn} textColor="#1a0a00">{tr("🖨️ Reporte PDF de Costos")}</Btn>}
            <Btn onClick={() => imprimirReporteGeneral(camada, $, userSettings.fasesAlimento)} color="#1a3300" textColor={C.accent}>{tr("📊 Reporte General Financiero")}</Btn>
          </div>
        )}
      </div>
    );
  };

  // --- VENTAS ---
  const Ventas = () => {
    const [showVentaForm, setShowVentaForm] = useState(false);
    /* Clientes de la granja. Viven en el backend (las tablas fiscales están cerradas
       al navegador), así que se piden una vez al abrir la pantalla. Si falla —sin
       señal, sesión vencida— la venta se puede hacer igual con comprador escrito. */
    const [clientes, setClientes] = useState([]);
    const [clientesError, setClientesError] = useState("");
    const [clienteSel, setClienteSel] = useState(null);     // objeto cliente o null
    /* La tarjeta del cliente tiene tres momentos: sin escoger nada, escogiendo de la
       lista, o escribiendo uno nuevo. Antes la lista y el botón de nuevo salían a la
       vez y no quedaba claro qué hacer. */
    const [modoCliente, setModoCliente] = useState("");     // "" | "lista" | "nuevo"
    const [nuevoCli, setNuevoCli] = useState(null);         // {nombre, doc, tel, guardar}
    const [guardandoCli, setGuardandoCli] = useState(false);
    /* Lo que hay que contarle al granjero después de guardar: si el cliente no se pudo
       guardar, tiene que enterarse. Antes el error se tragaba en silencio. */
    const [avisoVenta, setAvisoVenta] = useState(null);     // {texto, sesion}
    const [sesionVencida, setSesionVencida] = useState(false);
    /* Cuando la sesión está vieja se pide la contraseña y, al volver, se termina sola
       la acción que se había quedado a medias (cargar la lista o guardar el cliente). */
    const [pedirClave, setPedirClave] = useState(false);
    const [trasClave, setTrasClave] = useState("");          // "" | "guardar"
    const [ventaPaso, setVentaPaso] = useState(0);          // 0 = nada tocado todavía
    /* Facturación electrónica. null = todavía no se sabe; false = apagada (el
       caso de casi todo el mundo) y entonces nada de esto se dibuja. */
    const [puedeEmitir, setPuedeEmitir] = useState(false);
    /* Arranca con los comprobantes guardados en el teléfono: así la factura sale con
       su sello desde el primer segundo, antes de que conteste el servidor y aunque no
       haya señal en el galpón. */
    const [docsFiscales, setDocsFiscales] = useState(function () {
      return (session && session.ownerEmail) ? (DB.getEdocs(session.ownerEmail) || {}) : {};
    });
    const [emitiendo, setEmitiendo] = useState("");         // ventaId en curso
    useEffect(function () {
      // Solo el dueño: al empleado no le toca y no se le molesta con la petición.
      if (!session || session.rol === "empleado") return;
      /* Con la facturación electrónica apagada NO se pregunta nada. La copia local
         se escribe cada vez que se abre Mi Granja y Facturación, así que si dice
         que esto está apagado, está apagado y la pantalla de ventas no gasta ni
         una petición. Solo se pregunta al servidor cuando nunca se ha mirado en
         este teléfono, o cuando la última vez estaba encendida. */
      const copia = DB.getFiscal(session.ownerEmail) || {};
      const seHaMirado = !!copia.estado;
      if (seHaMirado && copia.estado !== "conectado") { setPuedeEmitir(false); return; }
      let vivo = true;
      apiFiscal("fiscal-config")
        .then(function (d) { if (vivo) setPuedeEmitir(!!(d && d.puedeEmitir)); })
        .catch(function () { if (vivo) setPuedeEmitir(false); });   // si falla, no se ofrece
      return function () { vivo = false; };
    }, [session && session.ownerEmail]);
    useEffect(function () {
      if (!showVentaForm) return;
      // El precio arranca puesto con el de la camada; las libras NO, que hay que pesarlas.
      setNewVenta(function (nv) {
        if (nv.precioLb !== "") return nv;
        var base = Number(camada.precioVenta) || (ventas.length ? Number(ventas[ventas.length - 1].precioLb) : 0);
        return base > 0 ? Object.assign({}, nv, { precioLb: String(base) }) : nv;
      });
      let vivo = true;
      apiFiscal("clientes")
        .then(function (d) { if (vivo) { setClientes(d.clientes || []); setClientesError(""); } })
        .catch(function (e) { if (vivo) { setClientesError(e.message || ""); setSesionVencida(!!e.sesion); } });
      return function () { vivo = false; };
    }, [showVentaForm]);
    const [newVenta, setNewVenta] = useState({ pollos: "", libras: "", precioLb: "", comprador: "", pesoCanastas: "" });
    usarOcupado(showVentaForm);   // con la venta a medio llenar, nada la interrumpe
    const ventas = camada.ventas || [];

    const totalPollosVendidos = ventas.reduce((a, v) => a + Number(v.pollos || 0), 0);
    const totalLibrasVendidas = ventas.reduce((a, v) => a + Number(v.libras || 0), 0);
    const ingresoTotal = ventas.reduce((a, v) => a + Number(v.libras || 0) * Number(v.precioLb || 0), 0);
    const lbPorAve = totalPollosVendidos > 0 ? (totalLibrasVendidas / totalPollosVendidos).toFixed(2) : null;
    // Pollos disponibles para vender (vivos por mortalidad, sin restar ventas)
    const ultimoRegV = camada.registros[camada.registros.length - 1];
    const vivosBaseV = ultimoRegV ? ultimoRegV.vivos : camada.totalPollos;
    const pollosRestantes = Math.max(0, vivosBaseV - totalPollosVendidos);
    const pctVendido = vivosBaseV > 0 ? Math.min(100, Math.round((totalPollosVendidos / vivosBaseV) * 100)) : 0;

    /* Guardar la venta. Es asíncrona porque, si el granjero escribió un cliente nuevo y
       pidió guardarlo, el cliente se crea JUSTO AQUÍ — al guardar la venta, no antes.
       Si la creación del cliente falla (sin señal, sesión vencida), la VENTA SE GUARDA
       IGUAL con el nombre escrito: no se pierde una venta por un problema de la libreta. */
    async function agregarVenta() {
      if (!newVenta.pollos || !newVenta.libras || !newVenta.precioLb || Number(newVenta.precioLb) <= 0) return null;
      const librasNetas = Math.max(0, Number(newVenta.libras) - Number(newVenta.pesoCanastas || 0));
      const v = { id: Date.now(), ...newVenta, pollos: Number(newVenta.pollos), libras: librasNetas, librasBrutas: Number(newVenta.libras), pesoCanastas: Number(newVenta.pesoCanastas || 0), precioLb: Number(newVenta.precioLb), fecha: new Date().toISOString().split("T")[0], _ts: Date.now() };

      let cli = clienteSel;
      let falloCliente = null;
      // Cliente escrito a mano que hay que guardar en la libreta.
      if (!cli && nuevoCli && String(nuevoCli.nombre || "").trim() && nuevoCli.guardar !== false) {
        try { cli = await crearCliente(nuevoCli); }
        catch (e) { cli = null; falloCliente = e; }    // la venta sigue, pero se avisa
      }
      if (cli) {
        // Los datos del cliente quedan CONGELADOS en la venta: si mañana se corrige la
        // ficha, la factura ya emitida sigue diciendo lo que decía.
        v.clienteId = cli.id; v.comprador = cli.nombre;
        v.compradorDoc = cli.doc_numero || ""; v.compradorTel = cli.telefono || "";
      } else if (nuevoCli && String(nuevoCli.nombre || "").trim()) {
        // Provisional o fallo al guardar: los datos van en la venta aunque no haya ficha.
        v.comprador = String(nuevoCli.nombre).trim();
        v.compradorDoc = String(nuevoCli.doc || "").trim();
        v.compradorTel = String(nuevoCli.tel || "").trim();
      }

      saveCamada({ ...camada, ventas: [...ventas, v] });
      setNewVenta({ pollos: "", libras: "", precioLb: "", comprador: "", pesoCanastas: "" });
      setClienteSel(null); setNuevoCli(null); setModoCliente(""); setVentaPaso(0);
      setShowVentaForm(false);
      /* Si el cliente no se pudo guardar, se dice. La venta quedó bien y con todos los
         datos del comprador; lo único que falta es la ficha en la libreta. */
      setAvisoVenta(falloCliente
        ? { texto: tr("La venta se guardó bien, pero el cliente no se pudo guardar en tu lista.") +
                   " " + (falloCliente.message || ""), sesion: !!falloCliente.sesion }
        : null);
      return v;
    }
    function eliminarVenta(id) {
      addDeletedItem(session.ownerEmail, "ventas", id);
      const nuevasVentas = ventas.filter(v => v.id !== id);
      saveCamada({ ...camada, ventas: nuevasVentas });
      // PATCH directo a Supabase de SOLO la columna ventas: el borrado queda en la nube
      // aunque el upsert grande de la camada falle (payload pesado, dispositivo desactualizado, etc.)
      SB.update("camadas", { id: camada.id }, { ventas: nuevasVentas, updated_at: new Date().toISOString() }).catch(() => {});
    }

    /* Crea el cliente en la libreta de la granja y lo devuelve.
       Una cédula dominicana son 11 dígitos y un RNC 9: se deduce del largo. */
    async function crearCliente(datos) {
      const nom = String((datos && datos.nombre) || "").trim();
      if (!nom) return null;
      setGuardandoCli(true);
      try {
        const doc = String(datos.doc || "").replace(/\D/g, "");
        const d = await apiFiscal("clientes", { method: "POST", body: {
          nombre: nom,
          docTipo: doc ? (doc.length === 11 ? "CEDULA" : "RNC") : null,
          docNumero: doc,
          telefono: String(datos.tel || "").trim(),
        } });
        setClientes(function (l) { return [d.cliente].concat(l); });
        setClientesError("");
        return d.cliente;
      } catch (e) {
        setClientesError(e.message || "");
        throw e;
      } finally {
        setGuardandoCli(false);
      }
    }

    /* Guarda los cambios de una ficha que YA existe (el lápiz de la lista).
       El servidor filtra por owner_email, así que con el id de un cliente de otra
       granja no se cambia nada: devuelve 404 y no toca ninguna fila. */
    async function cambiarCliente(datos) {
      const nom = String((datos && datos.nombre) || "").trim();
      if (!nom || !datos.editId) return null;
      setGuardandoCli(true);
      try {
        const doc = String(datos.doc || "").replace(/\D/g, "");
        const d = await apiFiscal("clientes", { method: "PATCH", body: {
          id: datos.editId,
          nombre: nom,
          docTipo: doc ? (doc.length === 11 ? "CEDULA" : "RNC") : null,
          docNumero: doc,
          telefono: String(datos.tel || "").trim(),
        } });
        setClientes(function (l) {
          return l.map(function (c) { return c.id === d.cliente.id ? d.cliente : c; });
        });
        /* Si ese cliente estaba ya escogido para esta factura, se refresca también:
           si no, la factura se imprimiría con el RNC viejo. */
        setClienteSel(function (s) { return s && s.id === d.cliente.id ? d.cliente : s; });
        setClientesError("");
        return d.cliente;
      } catch (e) {
        setClientesError(e.message || "");
        throw e;
      } finally {
        setGuardandoCli(false);
      }
    }

    /* El lápiz de la lista: carga la ficha en el MISMO formulario de "cliente nuevo"
       marcándola con editId. Así no hay dos formularios que mantener en paralelo. */
    function abrirEditarCliente(c) {
      setClientesError("");
      setNuevoCli({ nombre: c.nombre || "", doc: c.doc_numero || "", tel: c.telefono || "", editId: c.id });
      setModoCliente("nuevo");
    }

    /* Vuelve a pedir la libreta al servidor (después de renovar la sesión). */
    function recargarClientes() {
      return apiFiscal("clientes")
        .then(function (d) { setClientes(d.clientes || []); setClientesError(""); setSesionVencida(false); })
        .catch(function (e) { setClientesError(e.message || ""); setSesionVencida(!!e.sesion); });
    }

    /* El botón "Guardar cliente": lo crea en la libreta AHORA y lo deja ya escogido para
       esta factura, que es lo que el granjero espera. Si la sesión está vieja, pide la
       contraseña y vuelve solo a este mismo punto. */
    async function guardarClienteAhora() {
      if (guardandoCli) return;
      if (!String((nuevoCli && nuevoCli.nombre) || "").trim()) return;
      try {
        if (nuevoCli.editId) {
          /* Editando: al terminar vuelve a la LISTA, no se escoge solo. El lápiz se
             toca para corregir un dato, no necesariamente para facturarle a ese. */
          const cli = await cambiarCliente(nuevoCli);
          if (cli) { setNuevoCli(null); setModoCliente("lista"); }
        } else {
          const cli = await crearCliente(nuevoCli);
          if (cli) { setClienteSel(cli); setNuevoCli(null); setModoCliente(""); }
        }
      } catch (e) {
        setSesionVencida(!!e.sesion);
        if (e.sesion && session && session.rol !== "empleado") { setTrasClave("guardar"); setPedirClave(true); }
      }
    }

    /* EMITIR UN COMPROBANTE FISCAL ELECTRÓNICO de una venta.

       Pasa en dos pasos, y los dos los pide el productor tocando el botón:
         1. La factura interna se guarda en el servidor y recibe su número
            correlativo de verdad (hasta ahora el número se inventaba al
            imprimir, así que la misma venta daba dos números distintos).
         2. Ese documento, y solo ese, se manda al proveedor.

       El servidor no deja emitir dos veces la misma factura, así que tocar el
       botón otra vez devuelve el comprobante que ya había en vez de crear otro.
       Y si algo falla, la VENTA no se toca: sigue guardada igual. */
    async function emitirElectronica(v) {
      if (emitiendo) return;
      setEmitiendo(String(v.id));
      setAvisoVenta(null);
      try {
        const libras = Number(v.libras) || 0;
        const precio = Number(v.precioLb) || 0;
        const f = await apiFiscal("facturas", { method: "POST", body: {
          ventaId: String(v.id),
          camadaId: camada.id,
          clienteId: v.clienteId || null,
          clienteNombre: v.comprador || "",
          fecha: v.fecha || null,
          moneda: monedaISO(userSettings.moneda),
          lineas: [{
            descripcion: tr("Pollo vivo en pie") + (camada.nombre ? " · " + camada.nombre : ""),
            cantidad: libras, unidad: "lb", precioUnit: precio,
          }],
        } });
        const d = await apiFiscal("fiscal-emitir", { method: "POST", body: {
          facturaId: f.factura.id,
        } });
        setDocsFiscales(function (p) {
          const x = Object.assign({}, p); x[String(v.id)] = d.documento; return x;
        });
        setAvisoVenta({ texto: d.aviso || "", sesion: false });
      } catch (e) {
        setAvisoVenta({ texto: e.message || tr("No se pudo emitir."), sesion: !!e.sesion });
        if (e.sesion && session && session.rol !== "empleado") { setTrasClave(""); setPedirClave(true); }
      } finally {
        setEmitiendo("");
      }
    }

    /* Deja el cliente escrito como provisional: no entra en la libreta, pero su nombre,
       RNC y teléfono sí quedan en la factura. */
    function usarSinGuardar() {
      setNuevoCli(function (c) { return Object.assign({}, c, { guardar: false }); });
      setModoCliente("");
    }

    return (
      <div className="fadeIn" style={{ display: "flex", flexDirection: "column", gap: 14 }}>

        {/* Resumen ventas */}
        <Card style={{ background: "#162000", border: `1px solid ${C.accent}44` }}>
          <div style={{ fontSize: 15, color: C.accent, fontFamily: "'Space Mono', monospace", letterSpacing: 1, marginBottom: 14 }}>{tr("🐔 RESUMEN DE VENTAS")}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
            {[
              [tr("POLLOS VENDIDOS"), fmtNum(totalPollosVendidos) + tr(" aves"), C.accent],
              [tr("LIBRAS TOTALES"), fmtNum(totalLibrasVendidas) + " lb", C.text],
              [t("ingresoTotal") || "INGRESO TOTAL", $ + " " + fmtNum(ingresoTotal), C.green],
              [tr("LB / AVE PROM."), lbPorAve ? lbPorAve + " lb" : "—", C.textMuted],
            ].map(([lbl, val, col]) => (
              <div key={lbl} style={{ background: C.card, borderRadius: 10, padding: "12px 14px" }}>
                <div style={{ fontSize: 15, color: C.textFaint, fontFamily: "'Space Mono', monospace", marginBottom: 4 }}>{lbl}</div>
                <div style={{ fontSize: 18, fontWeight: 800, fontFamily: "'Space Mono', monospace", color: col }}>{val}</div>
              </div>
            ))}
          </div>
          {/* Barra de progreso de venta de pollos */}
          <div style={{ background: C.surface, borderRadius: 12, padding: "14px 16px", border: `1px solid ${C.accent}33` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
              <span style={{ fontSize: 13, color: C.textFaint, fontFamily: "'Space Mono', monospace", letterSpacing: 1 }}>{tr("🐔 AVANCE DE VENTA")}</span>
              <span style={{ fontSize: 18, fontWeight: 900, color: C.accent, fontFamily: "'Space Mono', monospace" }}>{pctVendido}%</span>
            </div>
            <div style={{ height: 16, borderRadius: 9, background: "#2a2a2a", overflow: "hidden", position: "relative", marginBottom: 10 }}>
              <div style={{ position: "absolute", top: 0, bottom: 0, left: 0, width: pctVendido + "%", background: "linear-gradient(90deg, " + C.accent + ", #4caf50)", borderRadius: 9, transition: "width 0.5s", boxShadow: "0 0 8px " + C.accent + "66" }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontSize: 11, color: C.textFaint, fontFamily: "'Space Mono', monospace" }}>{tr("VENDIDOS")}</div>
                <div style={{ fontSize: 17, fontWeight: 800, color: C.accent, fontFamily: "'Space Mono', monospace" }}>{fmtNum(totalPollosVendidos)}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 11, color: C.textFaint, fontFamily: "'Space Mono', monospace" }}>{tr("QUEDAN POR VENDER")}</div>
                <div style={{ fontSize: 17, fontWeight: 800, color: C.text, fontFamily: "'Space Mono', monospace" }}>{fmtNum(pollosRestantes)} {tr("aves")}</div>
              </div>
            </div>
          </div>
        </Card>

        {/* Botón agregar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{ventas.length} {tr("lote")}{ventas.length !== 1 ? "s" : ""} {tr("registrados")}</div>
            {ventas.length > 0 && <div style={{ fontSize: 15, color: C.textFaint }}>{fmtNum(totalPollosVendidos)} {tr("aves ·")} {fmtNum(totalLibrasVendidas)} {tr("lb")}</div>}
          </div>
          <Btn onClick={() => setShowVentaForm(s => !s)} color={showVentaForm ? C.surface : C.accent} textColor={showVentaForm ? C.textMuted : "#1a0a00"} outline={showVentaForm} style={{ width: "auto", padding: "8px 16px", fontSize: 15 }}>
            {showVentaForm ? "✕ " + (t("cancelar") || tr("Cancelar")) : "+ " + (t("nuevaVenta") || tr("Nueva venta"))}
          </Btn>
        </div>

        {pedirClave && (
          <ModalClave C={C} correo={(session && session.email) || ""}
            onCerrar={function () { setPedirClave(false); setTrasClave(""); }}
            onListo={function () {
              setPedirClave(false); setClientesError("");
              const queda = trasClave; setTrasClave("");
              recargarClientes().then(function () { if (queda === "guardar") guardarClienteAhora(); });
            }} />
        )}

        {/* Aviso de lo que pasó con el último guardado */}
        {avisoVenta && (
          <Card style={{ border: `2px solid ${C.warn}77`, background: C.warn + "14" }}>
            <div style={{ fontSize: 15, color: C.text, lineHeight: 1.5 }}>⚠️ {avisoVenta.texto}</div>
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              {avisoVenta.sesion && (
                /* Las sesiones de antes no guardaban con qué renovarse. Se arregla pidiendo
                   la contraseña aquí mismo, sin cerrar sesión ni perder la pantalla. */
                <button onClick={function () { setTrasClave(""); setPedirClave(true); setAvisoVenta(null); }}
                  style={{ flex: 2, padding: "12px", borderRadius: 12, border: "none", cursor: "pointer",
                    background: C.accent, color: "#1a0a00", fontSize: 15, fontWeight: 900, fontFamily: "'Syne', sans-serif" }}>
                  {tr("Arreglar ahora")}
                </button>
              )}
              <button onClick={function () { setAvisoVenta(null); }}
                style={{ flex: 1, padding: "12px", borderRadius: 12, border: "2px solid " + C.border,
                  cursor: "pointer", background: "transparent", color: C.textMuted, fontSize: 14,
                  fontWeight: 800, fontFamily: "'Syne', sans-serif" }}>{tr("Entendido")}</button>
            </div>
          </Card>
        )}

        {/* Formulario — pensado para llenarse con el dedo, sin apuntar a letritas */}
        {showVentaForm && (function () {
          const pollosN = Number(newVenta.pollos) || 0;
          /* Lo que esos pollos DEBERÍAN pesar hoy: la cantidad por el peso promedio de los
             últimos 3 días. Es de donde arranca la barra, para no arrastrar de más. */
          const lbPorAveEsp = Number(pesoPromedioLb) || 0;
          const lbEsperadas = pollosN * lbPorAveEsp;
          const hayEsperado = pollosN > 0 && lbEsperadas > 0;
          const lbMin = hayEsperado ? Math.max(1, Math.round(lbEsperadas * 0.55)) : 1;
          const lbMax = hayEsperado ? Math.round(lbEsperadas * 1.45) : 100;
          /* De libra en libra SIEMPRE que la camada sea grande. Antes, por encima de
             400 lb esperadas el paso era de 5 y no había forma de escribir un peso
             exacto; ahora el control fino lo da la barra, no el paso. */
          const lbPaso = lbEsperadas > 100 ? 1 : 0.5;
          const lbAhora = newVenta.libras !== "" ? Number(newVenta.libras) : Math.round(lbEsperadas);
          const difLb = hayEsperado && newVenta.libras !== "" ? Math.round((lbAhora / lbEsperadas - 1) * 100) : 0;

          /* El precio arranca en el que ya tiene puesto la camada, o en el de la última venta. */
          const precioBase = Number(camada.precioVenta) || (ventas.length ? Number(ventas[ventas.length - 1].precioLb) : 0) || 50;
          const prMin = Math.max(1, Math.round(precioBase * 0.5));
          const prMax = Math.round(precioBase * 1.8);
          const prAhora = newVenta.precioLb !== "" ? Number(newVenta.precioLb) : precioBase;

          /* CANASTAS: una jaula de pollo vivo vacía pesa unas 4 o 5 lb y lleva de 10 a 15
             aves, así que el tope de la barra crece con la cantidad en vez de ser un número
             fijo (con 500 pollos un tope de 100 lb no alcanzaría). Arranca en 0: quien no
             descuenta las canastas no ve ningún cambio y el neto sigue siendo el bruto. */
          const canMax = Math.max(40, Math.ceil(pollosN * 0.8 / 5) * 5);
          const canPaso = canMax > 120 ? 1 : 0.5;
          const canAhora = newVenta.pesoCanastas !== "" ? Number(newVenta.pesoCanastas) : 0;

          const neto = Math.max(0, lbAhora - Number(newVenta.pesoCanastas || 0));
          const totalVenta = neto * prAhora;
          const listo = pollosN > 0 && newVenta.libras !== "" && newVenta.precioLb !== "";
          const nombreComprador = clienteSel ? clienteSel.nombre : (newVenta.comprador || "");

          const caja = function (color) {
            return { background: C.card, borderRadius: 18, border: "2px solid " + color + "44", padding: "16px 16px 18px" };
          };
          const titulo = function (ico, texto, color) {
            return (
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <IcoSimple t={ico} c={color} s={30} />
                <div style={{ flex: 1, fontSize: 19, fontWeight: 900, color: color, fontFamily: "'Syne', sans-serif" }}>{texto}</div>
              </div>
            );
          };
          /* Cambiar la cantidad invalida el pesaje: se borran las libras a propósito.
             Si no, la barra se redibuja con el rango nuevo pero el número viejo se queda
             guardado, y el total saldría con una cifra que ya no se ve en pantalla. */
          const ponerPollos = function (v) {
            setNewVenta(function (nv) { return Object.assign({}, nv, { pollos: String(v), libras: "" }); });
          };
          const btnRedondo = function (txt, alTocar, color, apagado) {
            return (
              <button onClick={function () { if (!apagado) alTocar(); }} disabled={apagado}
                style={{ width: 62, height: 62, flexShrink: 0, borderRadius: 18, cursor: apagado ? "default" : "pointer",
                  border: "2px solid " + (apagado ? C.border : color), background: apagado ? "transparent" : color + "22",
                  color: apagado ? C.textFaint : color, fontSize: 30, fontWeight: 900, lineHeight: 1,
                  fontFamily: "'Syne', sans-serif" }}>{txt}</button>
            );
          };

          return (
          <Card style={{ border: `2px solid ${C.accent}55` }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

              {/* ── 1. A quién se le vende ── */}
              <div style={caja("#6AA6F5")}>
                {titulo("frasco", tr("Cliente"), "#6AA6F5")}

                {/* Dos caminos, bien claros. Antes salía todo a la vez y no se sabía qué tocar. */}
                {!modoCliente && !clienteSel && !nuevoCli && (
                  <>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9 }}>
                    <button onClick={function () { setModoCliente("lista"); }}
                      style={{ padding: "16px 8px", borderRadius: 14, cursor: "pointer", border: "2px solid " + C.border,
                        background: C.surface, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                      <IcoSimple t="gallina" c="#6AA6F5" s={30} />
                      <div style={{ fontSize: 14.5, fontWeight: 900, color: "#6AA6F5", fontFamily: "'Syne', sans-serif", textAlign: "center", lineHeight: 1.2 }}>
                        {tr("Mis clientes")}
                      </div>
                      <div style={{ fontSize: 11.5, color: sesionVencida ? C.warn : C.textFaint, fontFamily: "'Space Mono', monospace" }}>
                        {sesionVencida ? tr("vuelve a entrar") : clientes.length}
                      </div>
                    </button>
                    <button onClick={function () { setModoCliente("nuevo"); setNuevoCli({ nombre: "", doc: "", tel: "", guardar: true }); }}
                      style={{ padding: "16px 8px", borderRadius: 14, cursor: "pointer", border: "2px dashed #6AA6F577",
                        background: "#6AA6F51a", display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                      <div style={{ fontSize: 28, lineHeight: 1, color: "#6AA6F5" }}>＋</div>
                      <div style={{ fontSize: 14.5, fontWeight: 900, color: "#6AA6F5", fontFamily: "'Syne', sans-serif", textAlign: "center", lineHeight: 1.2 }}>
                        {tr("Cliente nuevo")}
                      </div>
                    </button>
                  </div>
                  {/* Si la sesión está vieja, la solución se ofrece aquí mismo y no después
                      de haber llenado toda la factura. */}
                  {sesionVencida && session && session.rol !== "empleado" && (
                    <button onClick={function () { setTrasClave(""); setPedirClave(true); }}
                      style={{ width: "100%", marginTop: 9, padding: "11px", borderRadius: 12, cursor: "pointer",
                        border: "2px solid " + C.warn + "88", background: C.warn + "14", color: C.warn,
                        fontSize: 13.5, fontWeight: 800, fontFamily: "'Syne', sans-serif", lineHeight: 1.35 }}>
                      {tr("Confirma tu contraseña para usar tu lista de clientes")}
                    </button>
                  )}
                  </>
                )}

                {/* Escoger de la lista */}
                {modoCliente === "lista" && !clienteSel && (
                  <div>
                    {clientes.length === 0 ? (
                      <div style={{ fontSize: 15, color: C.textFaint, textAlign: "center", padding: "10px 0", lineHeight: 1.5 }}>
                        {clientesError ? clientesError : tr("Todavía no tienes clientes guardados.")}
                      </div>
                    ) : (
                      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
                        {clientes.map(function (c) {
                          return (
                            <div key={c.id} style={{ display: "flex", alignItems: "stretch", gap: 7, minWidth: 0 }}>
                              <button onClick={function () { setClienteSel(c); setNuevoCli(null); }}
                                style={{ flex: 1, padding: "13px 12px", borderRadius: 14, cursor: "pointer", textAlign: "left", minWidth: 0,
                                  display: "flex", alignItems: "center", gap: 10,
                                  border: "2px solid " + C.border, background: C.surface }}>
                                <IcoSimple t="gallina" c={C.textMuted} s={26} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 15.5, fontWeight: 800, color: C.text, overflowWrap: "break-word" }}>{c.nombre}</div>
                                  <div style={{ fontSize: 12, color: C.textFaint, fontFamily: "'Space Mono', monospace" }}>
                                    {[c.doc_numero, c.telefono].filter(Boolean).join("  ·  ")}
                                  </div>
                                </div>
                              </button>
                              {/* El lápiz va FUERA del botón de escoger, no dentro: un botón
                                  dentro de otro botón no es HTML válido y el toque se lo come
                                  el de afuera. Así el nombre escoge y el lápiz edita. */}
                              <button onClick={function () { abrirEditarCliente(c); }}
                                aria-label={tr("Editar cliente")} title={tr("Editar cliente")}
                                style={{ width: 54, flexShrink: 0, borderRadius: 14, cursor: "pointer",
                                  border: "2px solid " + C.border, background: C.surface,
                                  fontSize: 20, lineHeight: 1 }}>✏️</button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <button onClick={function () { setModoCliente(""); }}
                      style={{ width: "100%", marginTop: 10, padding: "11px", borderRadius: 12, cursor: "pointer",
                        border: "2px solid " + C.border, background: "transparent", color: C.textMuted,
                        fontSize: 14, fontWeight: 800, fontFamily: "'Syne', sans-serif" }}>{tr("Atrás")}</button>
                  </div>
                )}

                {/* Nombre, RNC y teléfono. El MISMO formulario sirve para crear y para
                    editar: lo que cambia es si nuevoCli trae editId. */}
                {modoCliente === "nuevo" && nuevoCli && !clienteSel && (
                  <div style={{ background: "rgba(0,0,0,0.28)", border: "1.5px solid #6AA6F555", borderRadius: 14, padding: 12 }}>
                    <div style={{ fontSize: 14.5, fontWeight: 900, color: "#6AA6F5", marginBottom: 10,
                      fontFamily: "'Syne', sans-serif" }}>
                      {nuevoCli.editId ? "✏️ " + tr("Editar cliente") : tr("Cliente nuevo")}
                    </div>
                    {[
                      { k: "nombre", ph: tr("Nombre del cliente"), modo: "text",    fuente: "'Syne', sans-serif",       tam: 17 },
                      { k: "doc",    ph: tr("RNC o cédula"),       modo: "numeric", fuente: "'Space Mono', monospace", tam: 16 },
                      { k: "tel",    ph: tr("Teléfono"),           modo: "tel",     fuente: "'Space Mono', monospace", tam: 16 },
                    ].map(function (campo, i) {
                      return (
                        <input key={campo.k} value={nuevoCli[campo.k] || ""} placeholder={campo.ph} inputMode={campo.modo}
                          onChange={function (e) { const v = e.target.value; setNuevoCli(function (c) { const o = Object.assign({}, c); o[campo.k] = v; return o; }); }}
                          style={{ width: "100%", boxSizing: "border-box", marginTop: i ? 8 : 0, background: C.surface,
                            border: "1.5px solid " + C.border, borderRadius: 10, color: C.text, padding: "13px 12px",
                            fontSize: campo.tam, fontWeight: campo.k === "nombre" ? 700 : 400, outline: "none",
                            fontFamily: campo.fuente }} />
                      );
                    })}

                    {clientesError && (
                      <div style={{ fontSize: 12.5, color: C.warn, marginTop: 10, lineHeight: 1.4 }}>
                        {clientesError}
                        {sesionVencida && " " + tr("Puedes seguir con la venta: el nombre queda en la factura igual.")}
                      </div>
                    )}

                    {/* Atrás a un lado, GUARDAR al otro. Antes solo había "Atrás" y no se
                        veía por ningún lado cómo guardar al cliente. */}
                    <div style={{ display: "flex", gap: 9, marginTop: 12 }}>
                      <button onClick={function () { setModoCliente(nuevoCli.editId ? "lista" : ""); setNuevoCli(null); }}
                        style={{ flex: 1, padding: "14px 8px", borderRadius: 12, cursor: "pointer",
                          border: "2px solid " + C.border, background: "transparent", color: C.textMuted,
                          fontSize: 14.5, fontWeight: 800, fontFamily: "'Syne', sans-serif" }}>{tr("← Atrás")}</button>
                      <button onClick={guardarClienteAhora}
                        disabled={guardandoCli || !String(nuevoCli.nombre || "").trim()}
                        style={{ flex: 2, padding: "14px 8px", borderRadius: 12, border: "none",
                          cursor: (guardandoCli || !String(nuevoCli.nombre || "").trim()) ? "default" : "pointer",
                          background: "#6AA6F5", color: "#061523", fontSize: 15.5, fontWeight: 900,
                          fontFamily: "'Syne', sans-serif",
                          opacity: (guardandoCli || !String(nuevoCli.nombre || "").trim()) ? 0.45 : 1 }}>
                        {guardandoCli ? tr("Guardando…")
                          : (nuevoCli.editId ? tr("Guardar cambios") : tr("Guardar cliente"))}
                      </button>
                    </div>

                    {/* Salida para quien no quiere la ficha: el nombre va en la factura igual.
                        Editando no tiene sentido: la ficha ya existe en la libreta. */}
                    {!nuevoCli.editId && (<button onClick={usarSinGuardar}
                      disabled={!String(nuevoCli.nombre || "").trim()}
                      style={{ width: "100%", marginTop: 9, padding: "8px", borderRadius: 10, border: "none",
                        cursor: String(nuevoCli.nombre || "").trim() ? "pointer" : "default", background: "transparent",
                        color: C.textFaint, fontSize: 13, fontWeight: 700, fontFamily: "'Syne', sans-serif",
                        textDecoration: "underline", opacity: String(nuevoCli.nombre || "").trim() ? 1 : 0.45 }}>
                      {tr("Usar solo en esta factura, sin guardarlo")}
                    </button>)}
                  </div>
                )}

                {/* Cliente provisional: escrito pero sin ficha en la libreta */}
                {!clienteSel && nuevoCli && modoCliente !== "nuevo" && String(nuevoCli.nombre || "").trim() && (
                  <div style={{ background: "rgba(0,0,0,0.28)", border: "2px dashed #6AA6F577",
                    borderRadius: 14, padding: "13px 12px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <IcoSimple t="gallina" c={C.textMuted} s={26} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 17, fontWeight: 900, color: C.text, fontFamily: "'Syne', sans-serif", lineHeight: 1.25, overflowWrap: "break-word" }}>{nuevoCli.nombre}</div>
                        <div style={{ fontSize: 12, color: C.textFaint, fontFamily: "'Space Mono', monospace", marginTop: 2 }}>
                          {[nuevoCli.doc, nuevoCli.tel].filter(Boolean).join("  ·  ") || tr("solo en esta factura")}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, marginTop: 11 }}>
                      <button onClick={function () { setModoCliente("nuevo"); }}
                        style={{ flex: 1, padding: "10px", borderRadius: 11, cursor: "pointer",
                          border: "1.5px solid " + C.border, background: "transparent", color: C.textMuted,
                          fontSize: 13.5, fontWeight: 800, fontFamily: "'Syne', sans-serif" }}>{tr("Cambiar")}</button>
                      <button onClick={function () { setNuevoCli(null); setModoCliente(""); }}
                        style={{ flex: 1, padding: "10px", borderRadius: 11, cursor: "pointer",
                          border: "1.5px solid " + C.border, background: "transparent", color: C.textMuted,
                          fontSize: 13.5, fontWeight: 800, fontFamily: "'Syne', sans-serif" }}>{tr("Quitar")}</button>
                    </div>
                  </div>
                )}

                {/* Ya hay cliente escogido */}
                {clienteSel && (
                  <div style={{ display: "flex", alignItems: "center", gap: 11, background: "#6AA6F52e",
                    border: "3px solid #6AA6F5", borderRadius: 14, padding: "13px 12px" }}>
                    <IcoSimple t="gallina" c="#6AA6F5" s={28} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: String(clienteSel.nombre || "").length > 16 ? 15 : 18, fontWeight: 900, color: "#6AA6F5", fontFamily: "'Syne', sans-serif", lineHeight: 1.2, overflowWrap: "break-word" }}>{clienteSel.nombre}</div>
                      {(clienteSel.doc_numero || clienteSel.telefono) && (
                        <div style={{ fontSize: 12.5, color: C.textMuted, fontFamily: "'Space Mono', monospace", marginTop: 2 }}>
                          {[clienteSel.doc_numero, clienteSel.telefono].filter(Boolean).join("  ·  ")}
                        </div>
                      )}
                    </div>
                    <button onClick={function () { setClienteSel(null); setModoCliente(""); }}
                      style={{ width: 40, height: 40, borderRadius: 12, flexShrink: 0, cursor: "pointer",
                        border: "none", background: "rgba(0,0,0,.3)", color: C.textMuted, fontSize: 18 }}>✕</button>
                  </div>
                )}
              </div>

              {/* ── 2. Cuántos pollos ── */}
              <div style={caja("#F2994A")}>
                {titulo("gallina", tr("Cuántos pollos"), "#F2994A")}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                  {btnRedondo("−", function () { ponerPollos(Math.max(0, pollosN - 1)); }, "#F2994A", pollosN <= 0)}
                  <input value={newVenta.pollos} inputMode="numeric" placeholder="0"
                    onChange={function (e) { ponerPollos(e.target.value.replace(/\D/g, "")); }}
                    style={{ flex: 1, minWidth: 0, textAlign: "center", background: "transparent", border: "none", outline: "none",
                      color: pollosN > 0 ? "#F2994A" : C.textFaint, fontSize: 52, fontWeight: 900, lineHeight: 1,
                      fontFamily: "'Space Mono', monospace" }} />
                  {btnRedondo("+", function () { ponerPollos(pollosN + 1); }, "#F2994A", false)}
                </div>
                {/* Los atajos SUMAN, no reemplazan: tocar 100 dos veces deja 200. Por eso el
                    botón dice "+100" y no "100" — así se lee lo que va a pasar, y no hace
                    falta pintar ninguno como "escogido" (sumar no es escoger). */}
                <div style={{ display: "flex", gap: 7, marginTop: 12 }}>
                  {[100, 500, 1000, 2000].map(function (q) {
                    return (
                      <button key={q} onClick={function () { ponerPollos(pollosN + q); }}
                        style={{ flex: 1, padding: "10px 2px", borderRadius: 12, cursor: "pointer",
                          border: "2px solid #F2994A66", background: "#F2994A1a", color: "#F2994A",
                          fontSize: 16, fontWeight: 900, fontFamily: "'Space Mono', monospace" }}>{"+" + q}</button>
                    );
                  })}
                </div>
                {/* Si solo se suma, un toque de más obligaría a 500 toques en "−". */}
                {pollosN > 0 && (
                  <button onClick={function () { ponerPollos(0); }}
                    style={{ display: "block", margin: "9px auto 0", padding: "4px 10px", border: "none",
                      background: "transparent", color: C.textFaint, cursor: "pointer",
                      fontSize: 13, fontWeight: 700, fontFamily: "'Syne', sans-serif", textDecoration: "underline" }}>
                    {tr("empezar de cero")}
                  </button>
                )}
                {pollosRestantes > 0 && (
                  <div style={{ marginTop: 10, textAlign: "center", fontSize: 13, color: C.textMuted, fontFamily: "'Space Mono', monospace" }}>
                    {tr("quedan")} {fmtNum(pollosRestantes)}
                  </div>
                )}
              </div>

              {/* ── 3. Libras: la barra nace en lo que deberían pesar ── */}
              <div style={caja("#E8B44C")}>
                {titulo("balanza", tr("Libras en la balanza"), "#E8B44C")}
                {!hayEsperado ? (
                  <div style={{ fontSize: 15, color: C.textFaint, textAlign: "center", padding: "6px 0", lineHeight: 1.5 }}>
                    {tr("Pon primero cuántos pollos.")}
                  </div>
                ) : (<>
                  <div style={{ fontSize: 46, fontWeight: 900, lineHeight: 1, fontFamily: "'Space Mono', monospace",
                    color: newVenta.libras !== "" ? "#E8B44C" : C.textFaint }}>
                    {fmtNum(lbAhora, lbPaso < 1 ? 1 : 0)}<span style={{ fontSize: 22 }}> {tr("lb")}</span>
                  </div>
                  <div style={{ fontSize: 14.5, color: C.textMuted, fontFamily: "'Space Mono', monospace", marginTop: 4 }}>
                    {(lbAhora / Math.max(1, pollosN)).toFixed(2)} {tr("lb")} {tr("cada pollo")}
                    {newVenta.libras !== "" && (
                      <span style={{ color: Math.abs(difLb) <= 8 ? C.green : Math.abs(difLb) <= 20 ? C.warn : C.danger, fontWeight: 800 }}>
                        {"  ·  " + (difLb >= 0 ? "+" : "") + difLb + "%"}
                      </span>
                    )}
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <BarraFina min={lbMin} max={lbMax} paso={lbPaso} valor={lbAhora} color="#E8B44C"
                      onChange={function (v) { setNewVenta(function (n) { return Object.assign({}, n, { libras: String(v) }); }); }} />
                  </div>
                  <div style={{ position: "relative", height: 24 }}>
                    <div style={{ position: "absolute", top: 0,
                      left: "calc(23px + (100% - 46px) * " + ((lbEsperadas - lbMin) / Math.max(1, lbMax - lbMin)).toFixed(4) + ")",
                      transform: "translateX(-50%)", textAlign: "center" }}>
                      <div style={{ width: 2, height: 8, background: "#E8B44C", margin: "0 auto 2px" }} />
                      <div style={{ fontSize: 12, color: "#E8B44C", fontFamily: "'Space Mono', monospace", whiteSpace: "nowrap" }}>{tr("esperado")}</div>
                    </div>
                  </div>
                </>)}
              </div>

              {/* ── 4. Peso de las canastas, con barra como las demás ──
                  Antes esto estaba escondido en un desplegable DESPUÉS del precio y había
                  que teclear el número. Va aquí, pegado a las libras, porque la balanza
                  marca pollos + canastas juntos: lo que se descuenta se decide en ese
                  momento, no al final. */}
              <div style={caja("#B48EAD")}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                  <span style={{ fontSize: 28, lineHeight: 1 }}>🧺</span>
                  <div style={{ flex: 1, fontSize: 19, fontWeight: 900, color: "#B48EAD", fontFamily: "'Syne', sans-serif" }}>
                    {tr("Peso de las canastas")}
                  </div>
                </div>
                <div style={{ fontSize: 46, fontWeight: 900, lineHeight: 1, fontFamily: "'Space Mono', monospace",
                  color: canAhora > 0 ? "#B48EAD" : C.textFaint }}>
                  {fmtNum(canAhora, canPaso < 1 ? 1 : 0)}<span style={{ fontSize: 22 }}> {tr("lb")}</span>
                </div>
                <div style={{ marginTop: 10 }}>
                  <BarraFina min={0} max={canMax} paso={canPaso} valor={canAhora} color="#B48EAD"
                    onChange={function (v) { setNewVenta(function (n) { return Object.assign({}, n, { pesoCanastas: String(v) }); }); }} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, color: C.textFaint,
                  fontFamily: "'Space Mono', monospace" }}>
                  <span>0 {tr("lb")}</span><span>{canMax} {tr("lb")}</span>
                </div>
                {canAhora <= 0 ? (
                  <div style={{ marginTop: 8, fontSize: 13.5, color: C.textMuted, lineHeight: 1.45 }}>
                    {/* "cero" con letras a propósito: la fuente Syne dibuja el 0 casi igual
                        que una "o" a este tamaño, y "déjala en o" no se entiende. */}
                    {tr("Déjala en cero si pesaste los pollos sin canastas.")}
                  </div>
                ) : newVenta.libras !== "" && (
                  <div style={{ marginTop: 10, background: "rgba(0,0,0,.28)", borderRadius: 12, padding: "10px 12px",
                    fontSize: 14, color: C.textMuted, fontFamily: "'Space Mono', monospace", lineHeight: 1.6 }}>
                    {fmtNum(lbAhora, 1)} {tr("lb")} − {fmtNum(canAhora, 1)} {tr("lb")} ={" "}
                    <b style={{ color: C.green, fontSize: 16 }}>{fmtNum(neto, 1)} {tr("lb")}</b>{" "}
                    <span style={{ color: C.textFaint }}>{tr("netas")}</span>
                  </div>
                )}
              </div>

              {/* ── 5. Precio por libra ── */}
              <div style={caja("#4CC38A")}>
                {titulo("pastilla", tr("Precio por libra"), "#4CC38A")}
                <div style={{ fontSize: 44, fontWeight: 900, lineHeight: 1, fontFamily: "'Space Mono', monospace",
                  color: newVenta.precioLb !== "" ? "#4CC38A" : C.textFaint }}>
                  {$} {fmtNum(prAhora, 2)}
                </div>
                <div style={{ marginTop: 10 }}>
                  <BarraFina min={prMin} max={prMax} paso={0.5} valor={prAhora} color="#4CC38A"
                    onChange={function (v) { setNewVenta(function (n) { return Object.assign({}, n, { precioLb: String(v) }); }); }} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, color: C.textFaint,
                  fontFamily: "'Space Mono', monospace" }}>
                  <span>{$} {prMin}</span><span>{$} {prMax}</span>
                </div>
              </div>

              {/* ── 6. El total, que es lo que importa ── */}
              {listo && (
                <div style={{ background: "#132400", border: `2px solid ${C.green}66`, borderRadius: 18, padding: "16px 18px", textAlign: "center" }}>
                  <div style={{ fontSize: 13, color: C.textMuted, fontFamily: "'Space Mono', monospace", letterSpacing: 1 }}>{tr("TOTAL")}</div>
                  <div style={{ fontSize: 44, fontWeight: 900, color: C.green, fontFamily: "'Space Mono', monospace", lineHeight: 1.1 }}>
                    {$} {fmtNum(totalVenta, 2)}
                  </div>
                  <div style={{ fontSize: 13.5, color: C.textMuted, marginTop: 4, fontFamily: "'Space Mono', monospace" }}>
                    {fmtNum(neto, 1)} {tr("lb")} × {$} {fmtNum(prAhora, 2)}
                  </div>
                </div>
              )}

              {!listo && (
                <div style={{ textAlign: "center", fontSize: 14.5, color: C.warn, lineHeight: 1.5, padding: "4px 0" }}>
                  {pollosN <= 0 ? tr("Falta poner cuántos pollos.") : tr("Falta mover la barra de las libras.")}
                </div>
              )}
              <Btn onClick={function () { if (!listo) return; agregarVenta().then(function (v) { if (v) compartirFacturaPDF(v, camada, session, userSettings); }); }}
                color={listo ? "#25D366" : C.surface} textColor={listo ? "#fff" : C.textFaint}>{tr("📲 Guardar y generar factura WhatsApp")}</Btn>
              <Btn onClick={function () { if (listo) agregarVenta(); }} color={listo ? C.green : C.surface} textColor={listo ? "#fff" : C.textFaint}>{tr("Guardar venta")}</Btn>
            </div>
          </Card>
          );
        })()}

        {/* Lista */}
        {ventas.map((v, i) => {
          const subtotal = Number(v.libras) * Number(v.precioLb);
          const lbAve = Number(v.pollos) > 0 ? (Number(v.libras) / Number(v.pollos)).toFixed(2) : "—";
          return (
            <Card key={v.id}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: C.accent }}>
                    {fmtNum(v.pollos)} {tr("pollos ·")} {fmtNum(v.libras)} {tr("lb")}</div>
                  <div style={{ fontSize: 14, color: C.textMuted, marginTop: 3 }}>
                    {$} {v.precioLb}/lb · {lbAve} {tr("lb/ave")}{v.comprador ? <span style={{ color: C.textFaint }}> · {v.comprador}</span> : ""}
                  </div>
                  <div style={{ fontSize: 14, color: C.textFaint, fontFamily: "'Space Mono', monospace", marginTop: 3 }}>{v.fecha}</div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
                  <div style={{ fontWeight: 800, fontSize: 16, fontFamily: "'Space Mono', monospace", color: C.accent }}>
                    {$} {fmtNum(subtotal)}
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={function() { compartirFacturaPDF(v, camada, session, userSettings); }}
                      style={{ background: "#1a3a00", border: "1px solid #4caf50", borderRadius: 6, color: "#4caf50", cursor: "pointer", fontSize: 12, padding: "4px 8px", fontWeight: 700 }}>{tr("🧾 Factura")}</button>
                    {puedeEmitir && (function () {
                      const doc = docsFiscales[String(v.id)];
                      // Ya emitido: se enseña el estado REAL, no "listo" a secas.
                      if (doc) {
                        const ok = doc.estado === "aceptado";
                        return (
                          <span title={doc.estadoDetalle || ""}
                            style={{ fontSize: 11, padding: "4px 8px", borderRadius: 6, fontWeight: 700,
                              border: "1px solid " + (ok ? "#4caf50" : C.border),
                              color: ok ? "#4caf50" : C.textMuted,
                              background: ok ? "#1a3a00" : "transparent" }}>
                            {doc.encf ? "e-NCF " + doc.encf : tr("interno")}
                          </span>
                        );
                      }
                      return (
                        <button onClick={function () { emitirElectronica(v); }}
                          disabled={!!emitiendo}
                          style={{ background: "#2a1340", border: "1px solid #C084FC", borderRadius: 6,
                            color: "#C084FC", cursor: emitiendo ? "default" : "pointer", fontSize: 12,
                            padding: "4px 8px", fontWeight: 700, opacity: emitiendo ? 0.6 : 1 }}>
                          {emitiendo === String(v.id) ? tr("Emitiendo…") : tr("⚡ Emitir e-CF")}
                        </button>
                      );
                    })()}
                    <button onClick={() => eliminarVenta(v.id)} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", fontSize: 18 }}>×</button>
                  </div>
                </div>
              </div>
            </Card>
          );
        })}

        {ventas.length === 0 && !showVentaForm && (
          <div style={{ textAlign: "center", color: C.textFaint, fontSize: 15, padding: "30px 0" }}>
            {t("sinVentas") || tr("Sin ventas registradas.")}
          </div>
        )}
        {ventas.length > 0 && (
          <Btn onClick={() => imprimirReporteVentas(camada, $)} color={C.accent} textColor="#1a0a00">{tr("🖨️ Reporte PDF de Ventas")}</Btn>
        )}
      </div>
    );
  };

  // --- PANEL EMPLEADOS ---
  const PanelEmpleados = () => {
    const MAX_EMPLEADOS = 5;
    const [vista, setVista] = useState("lista"); // lista | nuevo | permisos
    const [empSeleccionado, setEmpSeleccionado] = useState(null);
    const [nuevoNombre, setNuevoNombre] = useState("");
    const [nuevoPermisos, setNuevoPermisos] = useState({
      verDashboard: true, verRegistro: false, verCalendario: true,
      verCostos: false, verVentas: false, editarVentas: false,
      verHistorialCamadas: false, verHistorialRegistros: false,
      verEmpleados: false, editarCamada: false,
    });
    const [empleadosSB, setEmpleadosSB] = useState([]);
    const [, forceUpdate] = useState(0);
    usarOcupado(vista !== "lista");
    useEffect(function() {
      SB.select("usuarios", "owner_email=eq." + session.ownerEmail + "&rol=eq.empleado")
      .then(function(rows) {
        if (rows && !rows.error) {
          var delEmp = [];
          try { delEmp = JSON.parse(localStorage.getItem("pe_deleted_emp_" + session.ownerEmail) || "[]"); } catch {}
          setEmpleadosSB(rows.filter(function(e) { return delEmp.indexOf(e.email) === -1; }));
        }
      }).catch(function() {});
    }, []);

    const users = DB.getUsers();
    // Combinar localStorage + Supabase
    const empleadosLocal = Object.entries(users).filter(function(e) { return e[1].ownerEmail === session.ownerEmail && e[1].rol === "empleado"; });
    const empleadosSBEmails = empleadosSB.map(function(e) { return e.email; });
    const empleadosExtra = empleadosSB.filter(function(e) { return !users[e.email]; }).map(function(e) { return [e.email, { email: e.email, nombre: e.nombre, rol: "empleado", ownerEmail: e.owner_email, empCode: e.emp_code, permisos: e.permisos || {} }]; });
    const empleados = [...empleadosLocal, ...empleadosExtra];

    const PERM_LABELS = [
      { key: "verDashboard",          label: "📊 Ver Inicio",              grupo: "basico" },
      { key: "verRegistro",           label: "📝 Hacer Registros",         grupo: "basico" },
      { key: "verCalendario",         label: "💉 Ver Vacunas",             grupo: "basico" },
      { key: "verMisCamadas",         label: "🐔 Ver Mis Camadas",         grupo: "basico" },
      { key: "verHecesIA",            label: "💩 Análisis de Heces IA",    grupo: "basico" },
      { key: "verInventario",         label: "📦 Ver Inventario",          grupo: "basico" },
      { key: "verCostos",             label: "💰 Ver Costos",              grupo: "avanzado" },
      { key: "verVentas",             label: "🐔 Ver Ventas",              grupo: "avanzado" },
      { key: "editarVentas",          label: "🐔 Editar Ventas",           grupo: "avanzado" },
      { key: "verHistorialCamadas",   label: "📋 Ver Historial Camadas",   grupo: "historial" },
      { key: "verHistorialRegistros", label: "📓 Ver Historial Registros", grupo: "historial" },
      { key: "verEmpleados",          label: "👥 Ver Empleados",           grupo: "admin" },
      { key: "editarCamada",          label: "✏️ Editar Camada",           grupo: "admin" },
    ];

    const grupos = [
      { id: "basico",    label: "Acceso Básico" },
      { id: "avanzado",  label: "Financiero" },
      { id: "historial", label: "Historial" },
      { id: "admin",     label: "Administración" },
    ];

    function crearEmpleado() {
      if (!nuevoNombre.trim()) return;
      if (empleados.length >= MAX_EMPLEADOS) return;
      var empCode = genCode();
      var empEmail = empCode + "_" + nuevoNombre.toLowerCase().replace(/\s+/g, "") + "@empleado.polloia";
      var newEmp = { email: empEmail, nombre: nuevoNombre.trim(), pass: btoa(empCode), rol: "empleado", ownerEmail: session.email, empCode, permisos: nuevoPermisos };
      var updated = { ...users, [empEmail]: newEmp };
      DB.saveUsers(updated);
      // Save to Supabase
      SB.upsert("usuarios", { email: empEmail, nombre: nuevoNombre.trim(), rol: "empleado", owner_email: session.email, emp_code: empCode, permisos: nuevoPermisos }).catch(function() {});
      setNuevoNombre("");
      setNuevoPermisos({ verDashboard: true, verRegistro: false, verCalendario: true, verCostos: false, verVentas: false, editarVentas: false, verHistorialCamadas: false, verHistorialRegistros: false, verEmpleados: false, editarCamada: false });
      setVista("lista");
      forceUpdate(function(n) { return n + 1; });
    }

    function eliminarEmpleado(empEmail) {
      if (!window.confirm("¿Eliminar este empleado? Ya no podrá acceder a la granja.")) return;
      var empData = users[empEmail] || empleadosSB.find(function(e) { return e.email === empEmail; }) || {};
      var empCode = empData.empCode || empData.emp_code || null;
      var updated = { ...users };
      delete updated[empEmail];
      DB.saveUsers(updated);
      setEmpleadosSB(function(prev) { return prev.filter(function(e) { return e.email !== empEmail; }); });
      // Lista de empleados eliminados — evita que vuelvan si el DELETE remoto falla
      var delKey = "pe_deleted_emp_" + session.ownerEmail;
      var delList = [];
      try { delList = JSON.parse(localStorage.getItem(delKey) || "[]"); } catch {}
      if (delList.indexOf(empEmail) === -1) delList.push(empEmail);
      try { localStorage.setItem(delKey, JSON.stringify(delList)); } catch {}
      forceUpdate(function(n) { return n + 1; });
      // Borrar en Supabase con verificación y reintento por emp_code
      SB.delete("usuarios", { email: empEmail }).then(function(ok) {
        if (!ok && empCode) {
          return SB.delete("usuarios", { emp_code: empCode, owner_email: session.ownerEmail });
        }
        return ok;
      }).then(function(ok2) {
        if (!ok2) {
          // Reintento final por email tras 2 segundos (puede ser fallo de red transitorio)
          setTimeout(function() {
            SB.delete("usuarios", { email: empEmail }).then(function(ok3) {
              if (!ok3) alert("No se pudo eliminar el empleado del servidor. Revisa tu conexión e inténtalo de nuevo.");
            }).catch(function() {});
          }, 2000);
        }
      }).catch(function() {});
    }

    function guardarPermisos(empEmail, perms) {
      var updated = { ...users };
      if (updated[empEmail]) { updated[empEmail].permisos = perms; DB.saveUsers(updated); }
      SB.update("usuarios", { email: empEmail }, { permisos: perms }).catch(function() {});
      setEmpSeleccionado(null);
      setVista("lista");
      forceUpdate(function(n) { return n + 1; });
    }

    var inputStyle = { width: "100%", background: C.surface, border: "1.5px solid " + C.border, borderRadius: 10, color: C.text, padding: "12px 14px", fontSize: 15, fontFamily: "'Syne', sans-serif", outline: "none", boxSizing: "border-box" };

  return (
      <div className="fadeIn" style={{ display: "flex", flexDirection: "column", gap: 14 }}>

        {/* Header */}
        <Card style={{ background: "#1a2a00", border: "1px solid " + C.accent + "44" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 15, color: C.accent, fontFamily: "'Space Mono', monospace", marginBottom: 2 }}>👥 MIS EMPLEADOS</div>
              <div style={{ fontSize: 13, color: C.textMuted }}>{empleados.length} / {MAX_EMPLEADOS} empleados activos</div>
            </div>
            {vista === "lista" && empleados.length < MAX_EMPLEADOS && (
              <button onClick={function() { setVista("nuevo"); }}
                style={{ background: C.accent, color: "#1a0a00", border: "none", borderRadius: 10, padding: "10px 16px", fontSize: 13, fontWeight: 800, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>
                + Nuevo
              </button>
            )}
            {vista !== "lista" && (
              <button onClick={function() { setVista("lista"); setEmpSeleccionado(null); }}
                style={{ background: "none", border: "1px solid " + C.border, borderRadius: 10, padding: "10px 16px", fontSize: 13, color: C.textMuted, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>
                ← Volver
              </button>
            )}
          </div>
        </Card>

        {/* VISTA: Lista de empleados */}
        {vista === "lista" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {empleados.length === 0 ? (
              <Card>
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                  <div style={{ fontSize: 36, marginBottom: 10 }}>👷</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 6 }}>No hay empleados</div>
                  <div style={{ fontSize: 13, color: C.textMuted }}>Toca "+ Nuevo" para agregar tu primer empleado.</div>
                </div>
              </Card>
            ) : empleados.map(function(entry) {
              var empEmail = entry[0], emp = entry[1];
              return (
                <Card key={empEmail}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>{emp.nombre}</div>
                      <div style={{ fontSize: 11, color: C.textFaint, marginTop: 2 }}>Empleado · acceso limitado</div>
                    </div>
                    <button onClick={function() { eliminarEmpleado(empEmail); }}
                      style={{ background: "none", border: "none", color: C.danger, fontSize: 20, cursor: "pointer", padding: 4 }}>🗑</button>
                  </div>
                  <div style={{ background: C.surface, borderRadius: 10, padding: "10px 14px", marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: 11, color: C.textFaint, marginBottom: 2 }}>{t("codigoAcceso") || "CÓDIGO DE ACCESO"}</div>
                      <div style={{ fontSize: 18, fontWeight: 900, color: C.accent, fontFamily: "'Space Mono', monospace", letterSpacing: 3 }}>{emp.empCode}</div>
                    </div>
                    <div style={{ fontSize: 12, color: C.textMuted }}>{t("comparteEsteCodigo") || "Comparte este código"}</div>
                  </div>
                  <button onClick={function() { setEmpSeleccionado({ email: empEmail, ...emp, permisos: emp.permisos || {} }); setVista("permisos"); }}
                    style={{ width: "100%", background: C.surface, border: "1px solid " + C.border, borderRadius: 10, padding: "10px", fontSize: 13, color: C.text, fontFamily: "'Syne', sans-serif", cursor: "pointer", fontWeight: 600 }}>
                    ⚙️ Editar permisos
                  </button>
                </Card>
              );
            })}
          </div>
        )}

        {/* VISTA: Nuevo empleado */}
        {vista === "nuevo" && (
          <Card>
            <div style={{ fontSize: 15, fontWeight: 800, color: C.accent, marginBottom: 14 }}>➕ Nuevo Empleado</div>
            <div style={{ fontSize: 12, color: C.textMuted, fontFamily: "'Space Mono', monospace", marginBottom: 6 }}>NOMBRE DEL EMPLEADO</div>
            <input value={nuevoNombre} onChange={function(e) { setNuevoNombre(e.target.value); }}
              style={{ ...inputStyle, marginBottom: 16 }} placeholder="Ej: Juan, María..." />
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 10 }}>Permisos iniciales:</div>
            {grupos.map(function(grupo) {
              return (
                <div key={grupo.id} style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, color: C.accent, fontFamily: "'Space Mono', monospace", fontWeight: 700, marginBottom: 6, paddingBottom: 4, borderBottom: "1px solid " + C.border }}>{grupo.label.toUpperCase()}</div>
                  {PERM_LABELS.filter(function(p) { return p.grupo === grupo.id; }).map(function(p) {
                    var active = nuevoPermisos[p.key] === true;
                    return (
                      <div key={p.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid " + C.border + "44" }}>
                        <span style={{ fontSize: 13, color: active ? C.text : C.textMuted }}>{p.label}</span>
                        <button onClick={function() { setNuevoPermisos(function(prev) { var n = { ...prev }; n[p.key] = !n[p.key]; return n; }); }}
                          style={{ background: active ? C.accent : C.card, color: active ? "#1a0a00" : C.textFaint, border: "2px solid " + (active ? C.accent : C.border), borderRadius: 20, padding: "4px 14px", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "'Syne', sans-serif" }}>
                          {active ? "ON" : "OFF"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              );
            })}
            <button onClick={crearEmpleado} disabled={!nuevoNombre.trim()}
              style={{ width: "100%", background: nuevoNombre.trim() ? C.accent : C.surface, color: nuevoNombre.trim() ? "#1a0a00" : C.textFaint, border: "none", borderRadius: 12, padding: "14px", fontSize: 15, fontWeight: 900, cursor: nuevoNombre.trim() ? "pointer" : "not-allowed", fontFamily: "'Syne', sans-serif", marginTop: 10 }}>
              ✅ Crear Empleado
            </button>
          </Card>
        )}

        {/* VISTA: Editar permisos */}
        {vista === "permisos" && empSeleccionado && (
          <Card>
            <div style={{ fontSize: 15, fontWeight: 800, color: C.accent, marginBottom: 4 }}>⚙️ Permisos — {empSeleccionado.nombre}</div>
            <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 14 }}>Código: <span style={{ color: C.accent, fontFamily: "'Space Mono', monospace", fontWeight: 700 }}>{empSeleccionado.empCode}</span></div>
            {grupos.map(function(grupo) {
              return (
                <div key={grupo.id} style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, color: C.accent, fontFamily: "'Space Mono', monospace", fontWeight: 700, marginBottom: 6, paddingBottom: 4, borderBottom: "1px solid " + C.border }}>{grupo.label.toUpperCase()}</div>
                  {PERM_LABELS.filter(function(p) { return p.grupo === grupo.id; }).map(function(p) {
                    var active = (empSeleccionado.permisos || {})[p.key] === true;
                    return (
                      <div key={p.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid " + C.border + "44" }}>
                        <span style={{ fontSize: 13, color: active ? C.text : C.textMuted }}>{p.label}</span>
                        <button onClick={function() {
                          setEmpSeleccionado(function(prev) {
                            var newPerms = { ...(prev.permisos || {}), [p.key]: !active };
                            return { ...prev, permisos: newPerms };
                          });
                        }}
                          style={{ background: active ? C.accent : C.card, color: active ? "#1a0a00" : C.textFaint, border: "2px solid " + (active ? C.accent : C.border), borderRadius: 20, padding: "4px 14px", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "'Syne', sans-serif" }}>
                          {active ? "ON" : "OFF"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              );
            })}
            <button onClick={function() { guardarPermisos(empSeleccionado.email, empSeleccionado.permisos || {}); }}
              style={{ width: "100%", background: C.accent, color: "#1a0a00", border: "none", borderRadius: 12, padding: "14px", fontSize: 15, fontWeight: 900, cursor: "pointer", fontFamily: "'Syne', sans-serif", marginTop: 6 }}>
              💾 Guardar Permisos
            </button>
          </Card>
        )}

      </div>
  );
  };

  const InfoCamada = () => {
    return (
      <div className="fadeIn" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <Card>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.accent, marginBottom: 12 }}>{tr("📋 Info de la Camada")}</div>
          {[
            [tr("Nombre"), camada.nombre],
            [tr("Galpón"), camada.galpon || "—"],
            [tr("Fecha inicio"), camada.fechaInicio],
            [tr("Total pollitos"), camada.totalPollos + tr(" aves")],
            [tr("Día actual"), diaActual + tr(" días")],
            [tr("Vivos actuales"), vivosActuales + tr(" aves")],
            [tr("Mortalidad"), mortalidadAcum + tr(" aves") + " (" + pctMort + "%)"],
          ].map(function(r) {
            return (
              <div key={r[0]} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid " + C.border, fontSize: 14 }}>
                <span style={{ color: C.textMuted }}>{r[0]}</span>
                <span style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700 }}>{r[1]}</span>
              </div>
            );
          })}
        </Card>
      </div>
    );
  };

  // Devuelve la FUNCIÓN que dibuja la pestaña actual (no un elemento nuevo): <Pantalla>
  // la ejecuta manteniendo vivo el estado de la pantalla entre re-dibujos.
  const dibujarTab = () => {
    switch (tab) {
      case "infocamada": return InfoCamada;
      case "diario": return RegistroDiario;
      case "calendario": return function () { return Calendario({ onCalendarioChange: setCalendarioUsuario }); };
      case "costos": return Costos;
      case "ventas": return Ventas;
      case "empleados": return PanelEmpleados;
      default: return Dashboard;
    }
  };
  // Ítems del menú (reutilizados en el dropdown móvil y en el menú lateral de PC)
  const menuItems = [
    { icon: "⚙️", label: t("configuracion"), action: () => { setSettingsOpen(true); setMenuOpen(false); }, show: true },
    { icon: "🐔", label: t("misCamadas"), action: () => { setMisCamadasOpen(true); setMenuOpen(false); }, show: isProp || permisos.verMisCamadas },
    { icon: "📋", label: t("historialCamadas") || "Historial de Camadas", action: () => { setHistorialOpen(true); setMenuOpen(false); }, show: isProp || permisos.verHistorialCamadas },
    { icon: "📓", label: t("historialRegistros") || "Historial de Registros", action: () => { setRegistrosHistOpen(true); setMenuOpen(false); }, show: isProp || permisos.verHistorialRegistros },
    { icon: "🌽", label: t("alimentacionMenu"), action: () => { setAlimentacionOpen(true); setMenuOpen(false); }, show: isProp || permisos.verRegistro },
    { icon: "📦", label: tr("Inventario"), action: () => { setInventarioOpen(true); setMenuOpen(false); }, show: isProp || permisos.verInventario },
    { icon: "box", label: tr("Mis Pollo Box"), action: () => { setMiSensorOpen(true); setMenuOpen(false); }, show: sensoresGranja.length > 0 },
    { icon: "💩", label: t("analisisHeces"), action: () => { setHecesPageOpen(true); setMenuOpen(false); }, show: isProp || permisos.verHecesIA },
    { icon: "🤖", label: t("analisisCamada"), action: () => { setAnalisisCamadaOpen(true); setMenuOpen(false); }, show: isProp },
    { icon: "🏥", label: tr("Centro de Salud Avicola"), action: () => { setCentroSaludOpen(true); setMenuOpen(false); }, show: true },
    { icon: "🏁", label: t("cerrarCamada"), action: () => { setCierreOpen(true); setMenuOpen(false); }, show: isProp || permisos.editarCamada },
    { icon: "👥", label: isProp ? (t("equipo") || "Equipo / Empleados") : "Mi Cuenta", action: () => { setTab("empleados"); setMenuOpen(false); }, show: isProp || permisos.verEmpleados },
  ].filter(item => item.show);
  return (
    <>
      <style>{appStyle}</style>
      {/* ── MENÚ LATERAL (solo PC ≥1024px) ── */}
      {isDesktop && (
        <div className="pe-sidebar" style={{ position: "fixed", top: 0, left: 0, bottom: 0, width: 250, background: C.surface, borderRight: `1px solid ${C.border}`, display: "flex", flexDirection: "column", zIndex: 25, overflowY: "auto" }}>
          {/* Logo */}
          <div style={{ padding: "18px 18px", display: "flex", alignItems: "center", gap: 12, borderBottom: `1px solid ${C.border}` }}>
            <div style={{ width: 40, height: 40, borderRadius: "50%", overflow: "hidden", border: `2px solid ${C.accent}`, boxShadow: `0 0 10px ${C.accent}44`, flexShrink: 0 }}>
              <img src="/icon-512x512.png" alt={tr("Pollo IA")} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
            <div style={{ fontSize: 19, fontWeight: 900, letterSpacing: 0.5 }}>
              <span style={{ color: C.accent }}>{tr("POLLO")}</span> <span style={{ color: C.danger }}>{tr("IA")}</span>
            </div>
          </div>
          {/* Pestañas principales */}
          <div style={{ padding: "12px 10px 6px", display: "flex", flexDirection: "column", gap: 4 }}>
            {TABS.map(tb => {
              var on = tab === tb.id;
              return (
                <button key={tb.id} className="pe-sidebar-btn" onClick={() => setTab(tb.id)}
                  style={{ display: "flex", alignItems: "center", gap: 11, width: "100%", textAlign: "left", padding: "11px 14px", borderRadius: 10, border: "none", cursor: "pointer", fontSize: 15, fontFamily: "'Syne', sans-serif", fontWeight: on ? 800 : 600, background: on ? C.accent : "transparent", color: on ? "#1a0a00" : C.text, transition: "all 0.15s" }}>
                  <span style={{ fontSize: 19 }}>{tb.icon}</span> {tb.label}
                </button>
              );
            })}
          </div>
          <div style={{ height: 1, background: C.border, margin: "8px 14px" }} />
          {/* Opciones del menú */}
          <div style={{ padding: "0 10px 6px", display: "flex", flexDirection: "column", gap: 2 }}>
            {menuItems.map(item => (
              <button key={item.label} className="pe-sidebar-btn" onClick={item.action}
                style={{ display: "flex", alignItems: "center", gap: 11, width: "100%", textAlign: "left", padding: "10px 14px", borderRadius: 10, border: "none", cursor: "pointer", fontSize: 14, fontFamily: "'Syne', sans-serif", color: C.text, background: "transparent", transition: "all 0.15s" }}>
                {item.icon === "box" ? <PolloBoxSVG size={24} /> : <span style={{ fontSize: 17 }}>{item.icon}</span>} {item.label}
              </button>
            ))}
          </div>
          {/* Cerrar sesión */}
          <div style={{ marginTop: "auto", padding: 10, borderTop: `1px solid ${C.border}` }}>
            <button onClick={onLogout} style={{ display: "flex", alignItems: "center", gap: 11, width: "100%", textAlign: "left", padding: "11px 14px", borderRadius: 10, border: "none", cursor: "pointer", fontSize: 14, fontFamily: "'Syne', sans-serif", color: C.danger, background: "transparent" }}>
              <span style={{ fontSize: 17 }}>🚪</span> {tr("Cerrar sesión")}</button>
          </div>
        </div>
      )}
      <div className="pe-app" style={isDesktop
        ? { minHeight: "100vh", background: C.bg, display: "flex", flexDirection: "column", maxWidth: "none", marginLeft: 250, marginRight: 0 }
        : { minHeight: "100vh", background: C.bg, display: "flex", flexDirection: "column", maxWidth: 480, margin: "0 auto" }}>
        {/* ── HEADER ── */}
        <div style={{ padding: isDesktop ? "11px 14px" : "8px 14px", borderBottom: `1px solid ${C.border}`, background: C.surface, position: "sticky", top: 0, zIndex: 20, display: "flex", alignItems: "center", gap: 11 }}>
          {/* En PC el logo redondo no va aquí: el titular manda solo. En el teléfono sí se queda. */}
          {!isDesktop && (
            <div style={{ width: 42, height: 42, borderRadius: "50%", overflow: "hidden", border: `2px solid ${C.accent}`, boxShadow: `0 0 10px ${C.accent}44`, flexShrink: 0 }}>
              <img src="/icon-512x512.png" alt={tr("Pollo IA")} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
          )}
          {/* Hueco del mismo ancho que el escudo de la derecha, para que el titular quede
              centrado de verdad y no corrido hacia la izquierda. */}
          {isDesktop && <div style={{ width: 50, flexShrink: 0 }} />}
          <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", justifyContent: isDesktop ? "center" : "flex-start" }}>
            {isDesktop ? (
              /* Cabecera: el nombre de la granja de titular y, debajo, qué hace el programa */
              (function cabeceraPC() {
                var titular = ((userSettings && userSettings.nombreGranja) || "").trim() || tr("POLLO IA");
                return (
                  <div style={{ textAlign: "center", minWidth: 0, maxWidth: "100%" }}>
                    <div style={{
                      fontSize: "clamp(20px, 2.1vw, 30px)", fontWeight: 900, letterSpacing: 1, lineHeight: 1.1,
                      // Degradado amarillo→rojo: usa los dos colores de la marca sin tener que
                      // pintar palabra por palabra, que es lo que se veía mal.
                      background: `linear-gradient(100deg, ${C.accent} 0%, ${C.accent} 38%, ${C.danger} 100%)`,
                      WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent",
                      WebkitTextFillColor: "transparent",
                      textTransform: "uppercase", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>{titular}</div>
                    <div style={{
                      fontSize: "clamp(9.5px, 0.85vw, 12px)", letterSpacing: 3, lineHeight: 1.1, marginTop: 5,
                      color: C.textMuted, fontFamily: "'Space Mono', monospace", textTransform: "uppercase", whiteSpace: "nowrap",
                    }}>{tr("Manejo y Crianza de Pollos de Engorde")}</div>
                  </div>
                );
              })()
            ) : (
              <div style={{ fontSize: 17, fontWeight: 900, letterSpacing: 0.5 }}>
                <span style={{ color: C.accent }}>{tr("POLLO")}</span>{" "}
                <span style={{ color: C.danger, fontSize: 17 }}>{tr("IA")}</span>
              </div>
            )}
          </div>
          {/* M Shield logo — centro del header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "0 8px" }}>
            <img src="data:image/png;base64,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" alt="M" style={{ width: 34, height: 34, objectFit: "contain" }} />
          </div>
          {/* Hamburger menu button (oculto en PC: el menú lateral lo reemplaza) */}
          {!isDesktop && (
          <button onClick={() => setMenuOpen(m => !m)} style={{ background: "none", border: "none", cursor: "pointer", padding: "6px 8px", display: "flex", flexDirection: "column", gap: 5, alignItems: "center" }}>
            {[0,1,2].map(i => (
              <div key={i} style={{ width: 22, height: 2, background: menuOpen ? C.accent : C.textMuted, borderRadius: 2, transition: "all 0.2s",
                transform: menuOpen ? (i===0 ? "rotate(45deg) translateY(7px)" : i===2 ? "rotate(-45deg) translateY(-7px)" : "scaleX(0)") : "none"
              }} />
            ))}
          </button>
          )}
        </div>

        {/* ── DROPDOWN MENU ── */}
        {menuOpen && (
          <div style={{ position: "fixed", top: 58, right: "calc(50% - 240px)", zIndex: 30, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, minWidth: 200, boxShadow: "0 8px 32px #0008", overflow: "hidden" }}
            onClick={() => setMenuOpen(false)}>
            <div style={{ padding: "10px 16px 6px", fontSize: 14, color: C.textFaint, fontFamily: "'Space Mono', monospace", letterSpacing: 1 }}>{t("menu") || tr("MENÚ")}</div>
            {menuItems.map(item => (
              <button key={item.label} onClick={item.action} style={{ width: "100%", background: "none", border: "none", padding: "12px 16px", textAlign: "left", color: C.text, cursor: "pointer", fontSize: 14, fontFamily: "'Syne', sans-serif", display: "flex", alignItems: "center", gap: 10, borderTop: `1px solid ${C.border}` }}>
                {item.icon === "box" ? <PolloBoxSVG size={26} /> : <span style={{ fontSize: 18 }}>{item.icon}</span>} {item.label}
              </button>
            ))}
            <div style={{ borderTop: `1px solid ${C.border}` }}>
              <button onClick={onLogout} style={{ width: "100%", background: "none", border: "none", padding: "12px 16px", textAlign: "left", color: C.danger, cursor: "pointer", fontSize: 14, fontFamily: "'Syne', sans-serif", display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 18 }}>🚪</span> {tr("Cerrar sesión")}</button>
            </div>
          </div>
        )}

        {/* ── OVERLAY to close menu ── */}
        {menuOpen && <div onClick={() => setMenuOpen(false)} style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 19 }} />}

        {/* ── SETTINGS MODAL ── */}
        {settingsOpen && (
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 40, background: "#000a", display: "flex", alignItems: "flex-end" }} onClick={() => setSettingsOpen(false)}>
            <div onClick={e => e.stopPropagation()} style={{ width: "100%", maxWidth: 480, margin: "0 auto", background: C.surface, borderRadius: "16px 16px 0 0", padding: "24px 20px 44px", border: `1px solid ${C.border}`, maxHeight: "90vh", overflowY: "auto" }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.accent, marginBottom: 6 }}>⚙️ {t("ajustes")}</div>
              <div style={{ fontSize: 14, color: C.textMuted, marginBottom: 22 }}>{t("personalizaApp") || tr("Personaliza cómo funciona Pollo Express IA")}</div>

              {/* Idioma */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 14, color: C.textMuted, fontFamily: "'Space Mono', monospace", letterSpacing: 1, marginBottom: 8 }}>🌐 {t("idioma")}</div>
                <div style={{ position: "relative" }}>
                  <select value={userSettings.idioma || "es"}
                    onChange={e => {
                      var lang = e.target.value;
                      var monedaDefault = { es: "DOP", en: "USD", pt: "BRL", hi: "INR", it: "EUR", jam: "JMD" };
                      saveSettings({ ...userSettings, idioma: lang, moneda: monedaDefault[lang] || userSettings.moneda });
                    }}
                    style={{ width: "100%", background: C.card, border: `2px solid ${C.accent}55`, borderRadius: 10, color: C.text, padding: "12px 36px 12px 14px", fontSize: 15, fontFamily: "'Syne', sans-serif", outline: "none", appearance: "none", cursor: "pointer" }}>
                    <option value="es">{tr("🇪🇸 Español")}</option>
                    <option value="en">{tr("🇺🇸 English")}</option>
                  </select>
                  <span style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", color: C.accent, pointerEvents: "none", fontSize: 15 }}>▼</span>
                </div>
              </div>

              {/* 🔔 Notificaciones push */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 14, color: C.textMuted, fontFamily: "'Space Mono', monospace", letterSpacing: 1, marginBottom: 8 }}>{tr("🔔 NOTIFICACIONES")}</div>
                {!pushSoportado ? (
                  <div style={{ background: C.card, border: "1px solid " + C.border, borderRadius: 10, padding: 12, fontSize: 13, color: C.textFaint }}>{tr("Este dispositivo o navegador no soporta notificaciones push.")}</div>
                ) : (
                  <div style={{ background: C.card, border: `1.5px solid ${pushPrefs ? C.green + "66" : C.border}`, borderRadius: 12, padding: 14 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>{tr("Avisos de la granja")}</div>
                        <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>{tr("Vacunas, pesajes y alertas en este dispositivo")}</div>
                      </div>
                      <button disabled={pushBusy}
                        onClick={function() { pushPrefs ? desactivarPush() : activarPush(PUSH_PREFS_DEFAULT); }}
                        style={{ background: pushPrefs ? C.green : C.surface, color: pushPrefs ? "#fff" : C.textMuted, border: `1.5px solid ${pushPrefs ? C.green : C.border}`, borderRadius: 20, padding: "8px 16px", fontSize: 13, fontWeight: 800, cursor: "pointer", opacity: pushBusy ? 0.6 : 1, flexShrink: 0 }}>
                        {pushBusy ? "..." : pushPrefs ? tr("✓ Activadas") : tr("Activar")}
                      </button>
                    </div>
                    {pushPrefs && (
                      <div style={{ marginTop: 12, borderTop: "1px solid " + C.border, paddingTop: 10 }}>
                        {PUSH_PREF_LABELS.map(function(p) {
                          var on = pushPrefs[p.key] !== false;
                          return (
                            <div key={p.key} onClick={function() { if (!pushBusy) togglePushPref(p.key); }}
                              style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", cursor: "pointer", opacity: pushBusy ? 0.6 : 1 }}>
                              <span style={{ fontSize: 18, width: 24, textAlign: "center" }}>{p.emoji}</span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13.5, fontWeight: 700, color: on ? C.text : C.textFaint }}>{p.label}</div>
                                <div style={{ fontSize: 11, color: C.textFaint }}>{p.desc}</div>
                              </div>
                              <div style={{ width: 40, height: 22, borderRadius: 12, background: on ? C.green : C.surface, border: `1.5px solid ${on ? C.green : C.border}`, position: "relative", flexShrink: 0, transition: "background 0.2s" }}>
                                <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#fff", position: "absolute", top: 1.5, left: on ? 19 : 2, transition: "left 0.2s" }} />
                              </div>
                            </div>
                          );
                        })}
                        <div style={{ fontSize: 11, color: C.textFaint, marginTop: 6 }}>{tr("⏰ Los avisos llegan a las 7:00 AM y el recordatorio de registro a las 7:00 PM.")}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* 📡 Sensor ambiental — entrada para conectar el PRIMER aparato (solo el dueño) */}
              {isProp && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 14, color: C.textMuted, fontFamily: "'Space Mono', monospace", letterSpacing: 1, marginBottom: 8 }}>{tr("📡 SENSOR AMBIENTAL")}</div>
                  <button onClick={function () { setSettingsOpen(false); setMiSensorOpen(true); }}
                    style={{ width: "100%", background: C.card, border: "1.5px solid #22D3EE55", borderRadius: 12, padding: "13px 14px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer", textAlign: "left" }}>
                    <span style={{ fontSize: 24, flexShrink: 0 }}>📡</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 800, color: "#7DD3FC" }}>{sensoresGranja.length > 0 ? tr("Mis Pollo Box (") + sensoresGranja.length + ")" : tr("Conectar un Pollo Box")}</div>
                      <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>{sensoresGranja.length > 0 ? tr("Ver estado, calibrar o agregar otro aparato") : tr("Vincula tu caja de temperatura, humedad y amoniaco")}</div>
                    </div>
                    <span style={{ fontSize: 18, color: C.textFaint, flexShrink: 0 }}>›</span>
                  </button>

                  {/* Modo demostración: enseña la pantalla de Ambiente con datos SIMULADOS
                      (para probar el diseño o grabar videos sin tener el aparato). Siempre con sello DEMO. */}
                  <div style={{ marginTop: 10, background: C.card, border: `1.5px solid ${userSettings.modoDemo ? "#FBBF24" : C.border}`, borderRadius: 12, padding: "12px 14px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                          <span style={{ fontSize: 14.5, fontWeight: 800, color: C.text }}>{tr("Modo demostración")}</span>
                          {userSettings.modoDemo && <BadgeDemo />}
                        </div>
                        <div style={{ fontSize: 12, color: C.textMuted, marginTop: 3, lineHeight: 1.5 }}>{tr("Muestra la pantalla de Ambiente con datos de ejemplo, sin aparato conectado. Los datos NO son de tu galpón.")}</div>
                      </div>
                      <div onClick={function () { saveSettings({ ...userSettings, modoDemo: !userSettings.modoDemo }); }}
                        style={{ width: 46, height: 26, borderRadius: 14, background: userSettings.modoDemo ? "#FBBF24" : C.surface, border: `1.5px solid ${userSettings.modoDemo ? "#FBBF24" : C.border}`, position: "relative", flexShrink: 0, cursor: "pointer", transition: "background 0.2s" }}>
                        <div style={{ width: 19, height: 19, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: userSettings.modoDemo ? 23 : 2, transition: "left 0.2s" }} />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ── MI GRANJA Y FACTURACIÓN ──────────────────────────────────
                  Antes el nombre, el logo, el país y la moneda estaban sueltos
                  aquí en medio, entre las notificaciones y la zona horaria. Son
                  los datos que SALEN IMPRESOS en la factura, así que ahora van
                  juntos en su propia pantalla, con los datos fiscales y el
                  interruptor de la facturación electrónica. */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 14, color: C.textMuted, fontFamily: "'Space Mono', monospace", letterSpacing: 1, marginBottom: 8 }}>{tr("🏠 MI GRANJA Y FACTURACIÓN")}</div>
                <button onClick={function () { setSettingsOpen(false); setMiGranjaOpen(true); }}
                  style={{ width: "100%", background: C.card, border: "1.5px solid " + C.accent + "55", borderRadius: 12, padding: "13px 14px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer", textAlign: "left" }}>
                  <span style={{ fontSize: 24, flexShrink: 0 }}>🧾</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: C.accent }}>{tr("Mi Granja y Facturación")}</div>
                    <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2, lineHeight: 1.45 }}>{tr("Nombre, logo, país, moneda, RNC y facturación electrónica")}</div>
                  </div>
                  <span style={{ fontSize: 18, color: C.textFaint, flexShrink: 0 }}>›</span>
                </button>
              </div>

              {/* Zona horaria */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 14, color: C.textMuted, fontFamily: "'Space Mono', monospace", letterSpacing: 1, marginBottom: 8 }}>🕐 {t("zonaHoraria")}</div>
                <div style={{ position: "relative" }}>
                  <select value={userSettings.timezone || "America/Santo_Domingo"}
                    onChange={e => saveSettings({ ...userSettings, timezone: e.target.value })}
                    style={{ width: "100%", background: C.card, border: `2px solid ${C.accent}55`, borderRadius: 10, color: C.text, padding: "12px 36px 12px 14px", fontSize: 15, fontFamily: "'Syne', sans-serif", outline: "none", appearance: "none", cursor: "pointer" }}>
                    <option value="America/Santo_Domingo">{tr("🇩🇴 Rep. Dominicana (GMT-4)")}</option>
                    <option value="America/Panama">{tr("🇵🇦 Panamá (GMT-5)")}</option>
                    <option value="America/Costa_Rica">{tr("🇨🇷 Costa Rica (GMT-6)")}</option>
                    <option value="America/El_Salvador">{tr("🇸🇻 El Salvador (GMT-6)")}</option>
                    <option value="America/Guatemala">{tr("🇬🇹 Guatemala (GMT-6)")}</option>
                    <option value="America/Honduras">{tr("🇭🇳 Honduras (GMT-6)")}</option>
                    <option value="America/Managua">{tr("🇳🇮 Nicaragua (GMT-6)")}</option>
                    <option value="America/Mexico_City">{tr("🇲🇽 México (GMT-6)")}</option>
                    <option value="America/Bogota">{tr("🇨🇴 Colombia (GMT-5)")}</option>
                    <option value="America/Guayaquil">{tr("🇪🇨 Ecuador (GMT-5)")}</option>
                    <option value="America/Lima">{tr("🇵🇪 Perú (GMT-5)")}</option>
                    <option value="America/Caracas">{tr("🇻🇪 Venezuela (GMT-4)")}</option>
                    <option value="America/Santiago">{tr("🇨🇱 Chile (GMT-3/-4)")}</option>
                    <option value="America/Argentina/Buenos_Aires">{tr("🇦🇷 Argentina (GMT-3)")}</option>
                    <option value="America/Sao_Paulo">{tr("🇧🇷 Brasil (GMT-3)")}</option>
                    <option value="America/New_York">{tr("🇺🇸 Este USA (GMT-4/-5)")}</option>
                    <option value="America/Chicago">{tr("🇺🇸 Centro USA (GMT-5/-6)")}</option>
                    <option value="America/Los_Angeles">{tr("🇺🇸 Pacífico USA (GMT-7/-8)")}</option>
                    <option value="Europe/Madrid">{tr("🇪🇸 España (GMT+1/+2)")}</option>
                    <option value="Europe/London">{tr("🇬🇧 Reino Unido (GMT+0/+1)")}</option>
                  </select>
                  <span style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", color: C.accent, pointerEvents: "none", fontSize: 15 }}>▼</span>
                </div>
              </div>

              {/* Tema de color */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 12, color: C.textMuted, fontFamily: "'Space Mono', monospace", letterSpacing: 1, marginBottom: 8 }}>🎨 {t("tema")}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {[
                    ["gris",      "#1e1e24", "#26262e", "Gris Oscuro"],
                    ["marino",    "#0d1117", "#161b22", "Negro Azulado"],
                    ["verde",     "#0d1a0f", "#122014", "Verde Bosque"],
                    ["azul",      "#0f1923", "#16232f", "Azul Petróleo"],
                    ["marron",    "#1a0a00", "#231100", "Marrón Original"],
                    ["negro",     "#0a0a0a", "#111111", "Negro Puro"],
                    ["blanco",    "#f0f0f0", "#ffffff", "Blanco Claro"],
                    ["amarillo",  "#fdf8e1", "#fffde7", "Amarillo Claro"],
                    ["naranja",   "#fff3e0", "#ffe0b2", "Naranja Claro"],
                    ["cafetinto", "#2c1810", "#3d2318", "Café/Marrón"],
                  ].map(([key, bgCol, surfCol, lbl]) => {
                    const isActive = (userSettings.tema || "gris") === key;
                    return (
                      <button key={key} onClick={() => saveSettings({ ...userSettings, tema: key })}
                        style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderRadius: 10, border: `2px solid ${isActive ? C.accent : C.border}`, background: bgCol, cursor: "pointer", textAlign: "left" }}>
                        <div style={{ width: 20, height: 20, borderRadius: 6, background: surfCol, border: `1px solid ${isActive ? C.accent : "#ffffff22"}`, flexShrink: 0 }} />
                        <span style={{ fontSize: 12, color: isActive ? C.accent : "#ffffff99", fontWeight: isActive ? 800 : 400, fontFamily: "'Syne', sans-serif" }}>{tr(lbl)}</span>
                        {isActive && <span style={{ marginLeft: "auto", color: C.accent, fontSize: 14 }}>✓</span>}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ── Códigos promocionales (solo el dueño de la app) ── */}
              {esAdminApp && <PromoCodesPanel C={C} />}

              {/* ── Firmware de las cajas (solo el dueño de la app) ── */}
              {esAdminApp && <FirmwarePanel C={C} />}

              {/* ── PANEL DEL DUEÑO DE LA APP (solo cuentas admin): prueba gratis global ── */}
              {esAdminApp && (
                <div style={{ marginBottom: 20, background: C.card, borderRadius: 12, padding: "14px 16px", border: `1.5px solid ${C.warn}66` }}>
                  <div style={{ fontSize: 12, color: C.warn, fontFamily: "'Space Mono', monospace", letterSpacing: 1, marginBottom: 4 }}>{tr("👑 PANEL DEL DUEÑO DE LA APP")}</div>
                  <div style={{ fontSize: 11, color: C.textFaint, marginBottom: 12 }}>{tr("Esto aplica a TODOS los usuarios nuevos de la app (solo tú ves esta sección).")}</div>
                  {adminCfg == null ? (
                    <div style={{ fontSize: 13, color: C.textMuted }}>{tr("Cargando…")}</div>
                  ) : (
                    <>
                      <div style={{ fontSize: 13, color: C.text, fontWeight: 700, marginBottom: 6 }}>{tr("🎁 Prueba gratis")}</div>
                      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                        {[{ v: true, l: "✅ Activada" }, { v: false, l: "🚫 Desactivada" }].map(function (op) {
                          var on = adminCfg.enabled === op.v;
                          return (
                            <button key={String(op.v)} onClick={function () {
                              setAdminCfg(Object.assign({}, adminCfg, { enabled: op.v }));
                              saveAppConfig("trial_enabled", op.v ? "true" : "false");
                            }}
                              style={{ flex: 1, padding: "10px 6px", borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: on ? 800 : 600, fontFamily: "'Syne', sans-serif", border: `2px solid ${on ? C.warn : C.border}`, background: on ? C.warn + "22" : "transparent", color: on ? C.warn : C.textMuted }}>
                              {op.l}
                            </button>
                          );
                        })}
                      </div>
                      <div style={{ fontSize: 13, color: adminCfg.enabled ? C.text : C.textFaint, fontWeight: 700, marginBottom: 6 }}>{tr("📅 Duración de la prueba")}</div>
                      <div style={{ display: "flex", gap: 8, opacity: adminCfg.enabled ? 1 : 0.45 }}>
                        {[7, 14].map(function (d) {
                          var on = adminCfg.days === d;
                          return (
                            <button key={d} disabled={!adminCfg.enabled} onClick={function () {
                              setAdminCfg(Object.assign({}, adminCfg, { days: d }));
                              saveAppConfig("trial_days", String(d));
                            }}
                              style={{ flex: 1, padding: "10px 6px", borderRadius: 10, cursor: adminCfg.enabled ? "pointer" : "default", fontSize: 14, fontWeight: on ? 800 : 600, fontFamily: "'Space Mono', monospace", border: `2px solid ${on ? C.accent : C.border}`, background: on ? C.accent + "22" : "transparent", color: on ? C.accent : C.textMuted }}>
                              {d} {tr("días")}</button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Info sesión */}
              <div style={{ background: C.card, borderRadius: 10, padding: "12px 14px", marginBottom: 18 }}>
                <div style={{ fontSize: 14, color: C.textFaint, fontFamily: "'Space Mono', monospace", marginBottom: 5 }}>{t("sesionActiva") || tr("SESIÓN ACTIVA")}</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{session.nombre}</div>
                <div style={{ fontSize: 15, color: C.textMuted }}>{session.email} · <span style={{ color: isProp ? C.accent : C.warn }}>{session.rol === "propietario" ? (t("propietario") || tr("propietario")) : (t("empleado") || tr("empleado"))}</span></div>
              </div>

              <button onClick={() => setSettingsOpen(false)} style={{ width: "100%", background: C.accent, color: "#1a0a00", border: "none", borderRadius: 10, padding: "13px", fontSize: 15, fontWeight: 800, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>{tr("Listo")}</button>

              {/* ── ZONA DE PELIGRO: eliminar cuenta (requisito Apple) ── */}
              <div style={{ marginTop: 28, paddingTop: 18, borderTop: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 12, color: C.textFaint, fontFamily: "'Space Mono', monospace", letterSpacing: 1, marginBottom: 10 }}>
                  ⚠️ {userSettings.idioma === "en" ? "DANGER ZONE" : "ZONA DE PELIGRO"}
                </div>
                <button onClick={() => { setDeleteConfirmText(""); setDeleteAccountOpen(true); }}
                  style={{ width: "100%", background: "transparent", color: C.danger, border: `1.5px solid ${C.danger}`, borderRadius: 10, padding: "12px", fontSize: 14, fontWeight: 700, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>
                  🗑️ {userSettings.idioma === "en" ? "Delete my account" : "Eliminar mi cuenta"}
                </button>
                <div style={{ fontSize: 11, color: C.textFaint, marginTop: 8, lineHeight: 1.5 }}>
                  {userSettings.idioma === "en"
                    ? "This permanently deletes your login and anonymizes your data. It cannot be undone."
                    : "Esto elimina tu acceso de forma permanente y anonimiza tus datos. No se puede deshacer."}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── MODAL CONFIRMAR ELIMINACIÓN DE CUENTA ── */}
        {deleteAccountOpen && (
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 60, background: "#000c", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
            onClick={() => { if (!deletingAccount) setDeleteAccountOpen(false); }}>
            <div onClick={e => e.stopPropagation()} style={{ width: "100%", maxWidth: 400, background: C.surface, borderRadius: 16, padding: "24px 20px", border: `1.5px solid ${C.danger}` }}>
              <div style={{ fontSize: 38, textAlign: "center", marginBottom: 8 }}>⚠️</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.danger, textAlign: "center", marginBottom: 10, fontFamily: "'Syne', sans-serif" }}>
                {userSettings.idioma === "en" ? "Delete account?" : "¿Eliminar tu cuenta?"}
              </div>
              <div style={{ fontSize: 14, color: C.textMuted, lineHeight: 1.6, marginBottom: 16 }}>
                {userSettings.idioma === "en"
                  ? "This is permanent and cannot be undone. Your login will be removed, your active subscription cancelled, and your data anonymized."
                  : "Esto es permanente y no se puede deshacer. Se eliminará tu acceso, se cancelará tu suscripción activa y tus datos quedarán anonimizados."}
              </div>
              <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 6 }}>
                {userSettings.idioma === "en" ? 'Type ' : 'Escribe '}<b style={{ color: C.danger }}>{tr("ELIMINAR")}</b>{userSettings.idioma === "en" ? ' to confirm:' : ' para confirmar:'}
              </div>
              <input value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)}
                disabled={deletingAccount} placeholder={tr("ELIMINAR")}
                style={{ width: "100%", background: C.card, border: `1.5px solid ${C.border}`, borderRadius: 10, color: C.text, padding: "12px 14px", fontSize: 15, fontFamily: "'Space Mono', monospace", outline: "none", boxSizing: "border-box", marginBottom: 16, textAlign: "center", letterSpacing: 2 }} />
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => { if (!deletingAccount) setDeleteAccountOpen(false); }} disabled={deletingAccount}
                  style={{ flex: 1, background: C.card, color: C.text, border: `1px solid ${C.border}`, borderRadius: 10, padding: "13px", fontSize: 15, fontWeight: 700, fontFamily: "'Syne', sans-serif", cursor: deletingAccount ? "default" : "pointer", opacity: deletingAccount ? 0.5 : 1 }}>
                  {userSettings.idioma === "en" ? "Cancel" : "Cancelar"}
                </button>
                <button onClick={borrarCuenta} disabled={deletingAccount || deleteConfirmText.trim().toUpperCase() !== "ELIMINAR"}
                  style={{ flex: 1, background: C.danger, color: "#fff", border: "none", borderRadius: 10, padding: "13px", fontSize: 15, fontWeight: 800, fontFamily: "'Syne', sans-serif", cursor: "pointer", opacity: (deletingAccount || deleteConfirmText.trim().toUpperCase() !== "ELIMINAR") ? 0.5 : 1 }}>
                  {deletingAccount ? (userSettings.idioma === "en" ? "Deleting…" : "Eliminando…") : (userSettings.idioma === "en" ? "Delete" : "Eliminar")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── MODAL MIS CAMADAS ── */}
        {misCamadasOpen && (
          <MisCamadasModal
            camadasList={camadasList}
            activaCamadaId={activaCamadaId}
            abrirNueva={misCamadasNueva}
            onSelect={(id) => { setActivaCamada(id); setMisCamadasOpen(false); setMisCamadasNueva(false); setTab("dashboard"); }}
            onCerrar={(isProp || permisos.editarCamada) ? function (id) {
              setActivaCamada(id);
              setMisCamadasOpen(false);
              setCierreOpen(true);
            } : null}
            onClose={() => { setMisCamadasOpen(false); setMisCamadasNueva(false); }}
            onGuardar={function() {
              // Cada cambio de esta ventana (crear, editar, cerrar, eliminar) ya se guarda solo.
              // Antes este botón reescribía TODAS las camadas con la copia de este teléfono y
              // activa:true: revivía camadas cerradas en otro lado y pisaba datos más nuevos.
              refrescarDatos();
            }}
            onEliminar={isProp ? function(id) {
              // 1. Actualizar estado React inmediatamente
              setCamadasList(function(prev) { return prev.filter(function(c) { return c.id !== id; }); });
              // 2. Borrar de localStorage
              var localList = DB.getCamadasList(session.ownerEmail).filter(function(c) { return c.id !== id; });
              DB.saveCamadasList(session.ownerEmail, localList);
              // 3. Guardar ID en lista de borrados para que loadCamadas lo ignore
              var deletedKey = "pe_deleted_" + session.ownerEmail;
              var deletedIds = [];
              try { deletedIds = JSON.parse(localStorage.getItem(deletedKey) || "[]"); } catch {}
              if (deletedIds.indexOf(id) === -1) deletedIds.push(id);
              try { localStorage.setItem(deletedKey, JSON.stringify(deletedIds)); } catch {}
              // 4. Borrar de Supabase
              SB.delete("camadas", { id: id }).then(function() { refrescarDatos(); }).catch(function() {
                SB.update("camadas", { id: id }, { activa: false }).catch(function(){});
                refrescarDatos();
              });
            } : null}
            onCrear={(nombre, galpon, totalPollos, fechaInicio, raza, sensorId) => { crearNuevaCamada(nombre, galpon, totalPollos, fechaInicio, raza, sensorId); setMisCamadasOpen(false); setMisCamadasNueva(false); setTab("dashboard"); }}
            onEditar={(id, form) => {
              var nuevoSensor = form.sensorId || null;
              const updated = camadasList.map(c => {
                if (c.id === id) return { ...c, nombre: form.nombre, galpon: form.galpon, totalPollos: Number(form.totalPollos), fechaInicio: form.fechaInicio, raza: form.raza || c.raza || "Ross 308", sensorId: nuevoSensor };
                // Un aparato mide una sola camada activa: si estaba en otra, se libera.
                if (nuevoSensor && c.activa !== false && c.sensorId === nuevoSensor) return { ...c, sensorId: null };
                return c;
              });
              setCamadasList(updated);
              DB.saveCamadasList(session.ownerEmail, updated);
              SB.update("camadas", { id: id }, { nombre: form.nombre, galpon: form.galpon, total_pollos: Number(form.totalPollos), fecha_inicio: form.fechaInicio, raza: form.raza || "Ross 308" }).catch(function(){});
              // El vínculo va por PATCH aparte (nunca en un upsert parcial de camadas)
              camadasList.forEach(function (c) {
                if (nuevoSensor && c.id !== id && c.activa !== false && c.sensorId === nuevoSensor) {
                  SB.update("camadas", { id: c.id }, vinculoCamada(null)).catch(function(){});
                }
              });
              SB.update("camadas", { id: id }, vinculoCamada(nuevoSensor)).catch(function(){});
            }}
            sensores={sensoresGranja}
            C={C} diasDesde={diasDesde} limit={5} idioma={userSettings.idioma || "es"}
          />
        )}

        {/* ── MODAL ALIMENTACIÓN ── */}
        {alimentacionOpen && (
          <AlimentacionModal
            camada={camada}
            C={C}
            fmtNum={fmtNum}
            t={t}
            modo={userSettings.fasesAlimento}
            onCambiarModo={function (m) { saveSettings(Object.assign({}, userSettings, { fasesAlimento: m })); }}
            onClose={() => setAlimentacionOpen(false)}
            onSaveEstandar={function(val) {
              saveCamada(Object.assign({}, camada, { lbPorPolloEstandar: val }));
              var newSettings = Object.assign({}, userSettings, { lbPorPolloEstandarGlobal: val });
              saveSettings(newSettings);
            }}
            fcrReal={fcrReal}
            fcrMin={ESTANDAR_FCR_MIN}
            fcrMax={ESTANDAR_FCR_MAX}
            fasesPersonal={fasesPersonal}
            onGuardarFase={function (clave, valores) {
              // Se guarda POR CAMADA dentro de los ajustes de la granja: así viaja entre
              // teléfonos sin necesidad de columnas nuevas en la base de datos.
              var todas = Object.assign({}, userSettings.fasesPorCamada || {});
              var deEsta = Object.assign({}, todas[camada.id] || {});
              if (valores) deEsta[clave] = valores; else delete deEsta[clave];
              if (Object.keys(deEsta).length) todas[camada.id] = deEsta; else delete todas[camada.id];
              saveSettings(Object.assign({}, userSettings, { fasesPorCamada: todas }));
            }}
          />
        )}

        {/* ── INVENTARIO DE LA GRANJA (uno solo para todas las camadas) ── */}
        {inventarioOpen && (
          <InventarioModal
            inventario={inventarioGranja || (camada && camada.inventarioAlimento) || null}
            modo={userSettings.fasesAlimento}
            C={C}
            fmtNum={fmtNum}
            onClose={() => setInventarioOpen(false)}
            onSave={function (inv) {
              saveInventarioGranja(inv);
              // Guardar cierra la edición y devuelve a Inicio: es lo que espera el granjero.
              setInventarioOpen(false);
              setTab("dashboard");
            }}
          />
        )}

        {/* ── MI GRANJA Y FACTURACIÓN ── */}
        {miGranjaOpen && (
          <MiGranjaPage
            C={C}
            session={session}
            userSettings={userSettings}
            saveSettings={saveSettings}
            onClose={function () { setMiGranjaOpen(false); }}
          />
        )}

        {/* ── MI SENSOR (aparato ambiental) — solo con ≥1 sensor vinculado ── */}
        {miSensorOpen && (
          <MiSensorPage
            C={C}
            session={session}
            sensores={sensoresGranja}
            camadasList={camadasList}
            onClose={() => setMiSensorOpen(false)}
            onSensoresChange={cargarSensores}
            onSensorEliminado={limpiarSensorEliminado}
            onAbrirHistorial={function () { setMiSensorOpen(false); setHistorialAmbOpen(true); }}
            onAbrirControl={function (ct) { setControlAbierto(ct); setControlesOpen(true); }}
          />
        )}

        {/* ── PÁGINA AMBIENTE (detalle en vivo: temp, humedad, amoniaco y 24 h) ── */}
        {ambientePageOpen && ambVisible && (
          <AmbientePage
            C={C}
            sensor={ambSensor}
            lecturas={ambLecturas}
            esDemo={ambEsDemo}
            sinSenal={ambSinSenal}
            diaActual={diaActual}
            camada={camada}
            onClose={() => setAmbientePageOpen(false)}
              onAbrirSensor={ambEsDemo ? null : function () { setAmbientePageOpen(false); setMiSensorOpen(true); }}
            onAbrirHistorial={function () { setAmbientePageOpen(false); setHistorialAmbOpen(true); }}
          />
        )}

        {/* ── HISTORIAL DE AMBIENTE ── */}
        {historialAmbOpen && ambVisible && (
          <HistorialAmbiente
            C={C}
            sensor={ambSensor}
            camada={camada}
            diaActual={diaActual}
            esDemo={ambEsDemo}
            lecturasDemo={ambLecturas}
            onClose={() => setHistorialAmbOpen(false)}
          />
        )}

        {/* ── MIS CONTROLES (luz · ventilación · regadera) ── */}
        {controlesOpen && (
          <MisControles
            C={C}
            session={session}
            diaActual={diaActual}
            sensores={sensoresGranja}
            abrirId={controlAbierto ? controlAbierto.id : null}
            onClose={() => { setControlesOpen(false); setControlAbierto(null); }}
          />
        )}

        {/* ── MENSAJE "¿GALPÓN LIMPIO? → CALIBRAR" (al crear/cerrar camada, solo si hay caja sensor) ── */}
        {calibrarPrompt && (
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 70, background: "#000c", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
            onClick={() => setCalibrarPrompt(null)}>
            <div onClick={e => e.stopPropagation()} style={{ width: "100%", maxWidth: 400, background: C.surface, borderRadius: 18, padding: "24px 20px", border: "1px solid #22D3EE55" }}>
              <div style={{ fontSize: 40, textAlign: "center", marginBottom: 10 }}>🌬️</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: "#22D3EE", textAlign: "center", marginBottom: 10, fontFamily: "'Syne', sans-serif" }}>{tr("Calibrar sensor de amoniaco")}</div>
              <div style={{ fontSize: 14, color: C.textMuted, lineHeight: 1.6, marginBottom: 8, textAlign: "center" }}>{tr("¿El galpón")} <b style={{ color: C.text }}>{calibrarPrompt.nombre}</b> {tr("está limpio y vacío?")}</div>
              <div style={{ fontSize: 13, color: C.textFaint, lineHeight: 1.6, marginBottom: 20, textAlign: "center" }}>{tr("Si está limpio, este es el mejor momento para calibrar el sensor con aire limpio. Toma unos 30 minutos y no necesitas hacer nada más.")}</div>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => setCalibrarPrompt(null)}
                  style={{ flex: 1, background: C.card, color: C.text, border: "1px solid " + C.border, borderRadius: 12, padding: "14px", fontSize: 15, fontWeight: 700, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>{tr("No, solo continuar")}</button>
                <button onClick={confirmarCalibracion}
                  style={{ flex: 1, background: "#22D3EE", color: "#08222b", border: "none", borderRadius: 12, padding: "14px", fontSize: 15, fontWeight: 900, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>{tr("Sí, calibrar")}</button>
              </div>
            </div>
          </div>
        )}

        {/* ── ANÁLISIS IA CAMADA COMPLETA ── */}
        {analisisCamadaOpen && (
          <AnalisisCamadaIA
            camada={camada}
            C={C}
            diaActual={diaActual}
            vivosActuales={vivosActuales}
            mortalidadAcum={mortalidadAcum}
            costoTotal={costoTotal}
            fcrReal={fcrReal}
            pesoActualLb={pesoActualLb}
            pesoEsperadoHoy={pesoEsperadoHoy}
            idioma={userSettings.idioma || "es"}
            simbolo={$}
            onClose={() => setAnalisisCamadaOpen(false)}
          />
        )}

        {/* ── ANÁLISIS IA DE CAMADA ── */}

        {/* ── PÁGINA ANÁLISIS DE HECES IA ── */}
        {centroSaludOpen && (
          <CentroSaludAvicola C={C} onClose={() => setCentroSaludOpen(false)} />
        )}

        {hecesPageOpen && (
          <HecesIAPage
            camada={camada}
            C={C}
            fmtNum={fmtNum}
            session={session}
            idioma={userSettings.idioma || "es"}
            saveCamada={saveCamada}
            onClose={() => setHecesPageOpen(false)}
          />
        )}

        {/* ── MODAL HISTORIAL DE REGISTROS ── */}
        {registrosHistOpen && (
          <RegistrosHistorialModal
            camada={camada}
            C={C}
            fmtNum={fmtNum}
            session={session}
            saveCamada={saveCamada}
            inspeccionesGranja={supervisorGranja}
            onClose={() => setRegistrosHistOpen(false)}
          />
        )}

        {/* ── MODAL CIERRE DE CAMADA ── */}
        {cierreOpen && (
          <CierreModal
            camada={camada}
            diaActual={diaActual}
            costoTotal={costoTotal}
            costoPorAve={costoPorAve}
            session={session}
            fmtNum={fmtNum}
            C={C}
            simbolo={$}
            onClose={() => setCierreOpen(false)}
            onConfirm={(resumen, nuevaCamada) => {
              const hist = DB.getHistorial(session.ownerEmail);
              // Sobreescribir si ya existe una entrada para esta camada, no duplicar
              const existeIdx = hist.findIndex(function(h) { return h.camadaId === resumen.camadaId; });
              if (existeIdx >= 0) { hist[existeIdx] = resumen; } else { hist.unshift(resumen); }
              DB.saveHistorial(session.ownerEmail, hist);
              // NOTA: el upsert a 'historial_camadas' se eliminó — SIEMPRE falló en silencio
              // (id de la tabla es uuid y le mandábamos "hist_cam_..."; columnas camada_id/resumen
              // no existen). La fuente de verdad del historial es la propia camada con activa=false:
              // el modal de historial la deriva de la nube.
              cerrarCamadaEnNube(session.ownerEmail, camada.id);
              setCamadasList(function(prev) { return prev.filter(function(c) { return c.id !== camada.id; }); });
              DB.saveCamadaById(session.ownerEmail, Object.assign({}, camada, { activa: false }));
              setCierreOpen(false);
              setTab("dashboard");
              // Con sensor vinculado: el galpón quedó vacío → ofrecer calibrar el amoniaco.
              if (camada.sensorId) pedirCalibrarSiSensor(camada.sensorId);
              refrescarDatos();
            }}
          />
        )}

        {/* ── MODAL HISTORIAL ── */}
        {historialOpen && (
          <HistorialModal
            session={session}
            fmtNum={fmtNum}
            C={C}
            simbolo={$}
            onClose={() => setHistorialOpen(false)}
            onReabrirCamada={(camadaId) => {
              SB.select("camadas", "id=eq." + camadaId).then(function(rows) {
                if (!rows || !rows[0]) return;
                var r = rows[0];
                quitarCierrePendiente(session.ownerEmail, camadaId);   // si su cierre estaba pendiente, ya no
                var camadaRec = { id: r.id, nombre: r.nombre, galpon: r.galpon, fechaInicio: r.fecha_inicio, totalPollos: r.total_pollos, lbPorPolloEstandar: r.lb_por_pollo_estandar, precioVenta: r.precio_venta || 0, owner: r.owner_email, activa: true, registros: separarBorrados(r.registros).visibles, registrosBorrados: separarBorrados(r.registros).borrados, gastos: r.gastos || [], ventas: r.ventas || [], hecesAnalisis: r.heces_analisis || [] };
                // Upsert completo — no borrar ventas/gastos/registros
                SB.upsert("camadas", {
                  id: camadaId,
                  owner_email: session.ownerEmail,
                  nombre: r.nombre,
                  galpon: r.galpon,
                  fecha_inicio: r.fecha_inicio,
                  total_pollos: r.total_pollos,
                  lb_por_pollo_estandar: r.lb_por_pollo_estandar,
                  precio_venta: r.precio_venta || 0,
                  activa: true,
                  registros: r.registros || [],
                  gastos: r.gastos || [],
                  ventas: r.ventas || [],
                  heces_analisis: r.heces_analisis || [],
                }).catch(function() {});
                setCamadasList(function(prev) {
                  var existe = prev.find(function(c) { return c.id === camadaId; });
                  if (existe) return prev.map(function(c) { return c.id === camadaId ? camadaRec : c; });
                  return [...prev, camadaRec];
                });
                DB.saveCamadaById(session.ownerEmail, camadaRec);
                setActivaCamada(camadaId);
                setHistorialOpen(false);
              }).catch(function() {});
            }}
          />
        )}

        <div style={{ flex: 1, padding: isDesktop ? "26px 32px" : 14, paddingBottom: isDesktop ? 40 : 88, overflowY: "auto" }}>
          <div className={isDesktop ? (tab === "dashboard" ? "pe-contenido" : "pe-contenido-estrecho") : undefined}><Pantalla key={tab} dibujar={dibujarTab()} /></div>
        </div>
        {/* Navegación inferior (oculta en PC: el menú lateral la reemplaza) */}
        {!isDesktop && (
        <div style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: 480, background: C.surface, borderTop: `1px solid ${C.border}`, display: "flex", zIndex: 10 }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{ flex: 1, padding: "9px 0 7px", background: "transparent", border: "none", borderTop: `2px solid ${tab === t.id ? C.accent : "transparent"}`, color: tab === t.id ? C.accent : C.textFaint, cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 2, transition: "all 0.15s" }}>
              <span style={{ fontSize: 15 }}>{t.icon}</span>
              <span style={{ fontSize: 15, fontFamily: "'Space Mono', monospace" }}>{t.label}</span>
            </button>
          ))}
        </div>
        )}
      </div>
    </>
  );
}
// --- TABLERO DE INVENTARIO (compartido: página Inventario + página de Inicio) ---
// Cada renglón usa su "óptimo" (=100%) configurable por el usuario; si no está definido,
// usa estas referencias. Estado: ≥50% ÓPTIMO (verde) · 20–49% BAJO (amarillo) · <20% CRÍTICO (rojo).
// Nombre e icono de cada renglón del inventario. Por defecto son los de siempre, pero el
// dueño puede cambiarlos (guarda su elección en inventario.nombres / inventario.emojis).
function invNombre(inv, key, porDefecto) {
  var n = inv && inv.nombres && inv.nombres[key];
  return (n && String(n).trim()) ? String(n) : porDefecto;
}
function invEmoji(inv, key, porDefecto) {
  var e = inv && inv.emojis && inv.emojis[key];
  return (e && String(e).trim()) ? String(e) : porDefecto;
}
// Iconos para elegir: los que tienen sentido en una granja.
const INV_EMOJIS = [
  "🐣", "🐔", "🍗", "🌽", "🌾", "💧", "💊", "🪱", "🦠", "🧪",
  "🧴", "🧂", "🍶", "🪣", "🧺", "🥣", "🛢️", "🫗", "🥤", "🧹",
  "🔥", "💡", "🔧", "📦", "🧊", "💉", "🥚", "🩺", "⚡", "🚜", "🧰",
];
// Cuadrícula para escoger icono. Se abre tocando el icono actual.
function SelectorEmoji({ C, valor, onElegir, onCerrar }) {
  return (
    <div style={{ background: C.bg, border: "1px solid " + C.border, borderRadius: 12, padding: 10, marginBottom: 10 }}>
      <div style={{ fontSize: 11, color: C.textFaint, fontFamily: "'Space Mono', monospace", letterSpacing: 1, marginBottom: 8 }}>ELIGE UN ICONO</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(44px, 1fr))", gap: 6 }}>
        {INV_EMOJIS.map(function (e) {
          var puesto = e === valor;
          return (
            <button key={e} onClick={function () { onElegir(e); }}
              style={{
                fontSize: 22, lineHeight: 1, padding: "8px 0", cursor: "pointer", borderRadius: 10,
                background: puesto ? C.accent + "33" : C.surface,
                border: "1.5px solid " + (puesto ? C.accent : C.border),
              }}>{e}</button>
          );
        })}
      </div>
      <button onClick={onCerrar}
        style={{ width: "100%", marginTop: 8, background: "none", border: "1px solid " + C.border, borderRadius: 8, color: C.textMuted, padding: "7px", fontSize: 12, cursor: "pointer", fontFamily: "'Syne', sans-serif" }}>
        Cerrar
      </button>
    </div>
  );
}

const INV_OPT_DEFAULT = { preinicio: 10, iniciador: 25, crecimiento: 25, engorde: 25, paja: 20, vitaminas: 5, desparasitantes: 5, probioticos: 5 };
// Unidades de medida elegibles para los insumos (se guardan en inv.unidades / otro.unidad)
const INV_UNIDADES = ["unidades", "sacos", "galones", "litros", "sobres", "frascos", "libras"];
function invNivel(cantidad, lleno) {
  var full = Number(lleno) > 0 ? Number(lleno) : 1;
  var pct = Math.max(0, Math.min(100, Math.round((Number(cantidad) || 0) / full * 100)));
  var estado = pct >= 50 ? { label: tr("ÓPTIMO"), color: "#22c55e" } : pct >= 20 ? { label: tr("BAJO"), color: "#EAB308" } : { label: tr("CRÍTICO"), color: "#ef4444" };
  return { pct: pct, estado: estado };
}
// Construye las filas del tablero desde el objeto inventario (guardado o en edición).
function invTablero(inv, modo) {
  inv = inv || {};
  // El preinicio se enseña cuando la granja cría con 4 fases (o si quedan sacos de antes).
  var conPre = modoFases(modo) === 4 || Number(inv.preinicio) > 0;
  var opt = Object.assign({}, INV_OPT_DEFAULT, inv.optimos || {});
  var uni = inv.unidades || {};
  var aguaPct = Math.max(0, Math.min(100, Number(inv.agua) || 0));
  // Cada renglón usa el nombre y el icono que haya elegido el dueño (si no, los de siempre).
  return (conPre ? [
    Object.assign({ emoji: invEmoji(inv, "preinicio", "🐥"), nombre: invNombre(inv, "preinicio", tr("Alimento Preinicio")), txt: (Number(inv.preinicio) || 0) + "/" + opt.preinicio + " " + tr("sacos") }, invNivel(inv.preinicio, opt.preinicio)),
  ] : []).concat([
    Object.assign({ emoji: invEmoji(inv, "iniciador", "🐣"), nombre: invNombre(inv, "iniciador", tr("Alimento Iniciador")), txt: (Number(inv.iniciador) || 0) + "/" + opt.iniciador + " " + tr("sacos") }, invNivel(inv.iniciador, opt.iniciador)),
    Object.assign({ emoji: invEmoji(inv, "crecimiento", "🐔"), nombre: invNombre(inv, "crecimiento", tr("Alimento Crecimiento")), txt: (Number(inv.crecimiento) || 0) + "/" + opt.crecimiento + " " + tr("sacos") }, invNivel(inv.crecimiento, opt.crecimiento)),
    Object.assign({ emoji: invEmoji(inv, "engorde", "🍗"), nombre: invNombre(inv, "engorde", tr("Alimento Engorde")), txt: (Number(inv.engorde) || 0) + "/" + opt.engorde + " " + tr("sacos") }, invNivel(inv.engorde, opt.engorde)),
    Object.assign({ emoji: invEmoji(inv, "agua", "💧"), nombre: invNombre(inv, "agua", tr("Agua")), txt: aguaPct + "%" }, invNivel(aguaPct, 100)),
    Object.assign({ emoji: invEmoji(inv, "paja", "🌾"), nombre: invNombre(inv, "paja", tr("Paja de arroz")), txt: (Number(inv.paja) || 0) + "/" + opt.paja + " " + tr("sacos") }, invNivel(inv.paja, opt.paja)),
    Object.assign({ emoji: invEmoji(inv, "vitaminas", "💊"), nombre: invNombre(inv, "vitaminas", tr("Vitaminas")), txt: (Number(inv.vitaminas) || 0) + "/" + opt.vitaminas + " " + tr(uni.vitaminas || "unidades") }, invNivel(inv.vitaminas, opt.vitaminas)),
    Object.assign({ emoji: invEmoji(inv, "desparasitantes", "🪱"), nombre: invNombre(inv, "desparasitantes", tr("Desparasitantes")), txt: (Number(inv.desparasitantes) || 0) + "/" + opt.desparasitantes + " " + tr(uni.desparasitantes || "unidades") }, invNivel(inv.desparasitantes, opt.desparasitantes)),
    Object.assign({ emoji: invEmoji(inv, "probioticos", "🦠"), nombre: invNombre(inv, "probioticos", tr("Probióticos")), txt: (Number(inv.probioticos) || 0) + "/" + opt.probioticos + " " + tr(uni.probioticos || "unidades") }, invNivel(inv.probioticos, opt.probioticos)),
  ]).concat((Array.isArray(inv.otros) ? inv.otros : []).map(function (o) {
    var llenoO = Number(o.optimo) > 0 ? Number(o.optimo) : 5;
    return Object.assign({ emoji: o.emoji || "🧪", nombre: o.nombre || tr("Insumo"), txt: (Number(o.cantidad) || 0) + "/" + llenoO + " " + tr(o.unidad || "unidades") }, invNivel(o.cantidad, llenoO));
  }));
}
function TableroInventario({ items, C, onClick, style }) {
  return (
    <div onClick={onClick} style={Object.assign({ background: C.card, borderRadius: 16, border: "1px solid " + C.border, padding: "16px 16px 8px", boxShadow: "0 2px 14px #00000040", cursor: onClick ? "pointer" : "default" }, style || {})}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>{tr("📊 Estado del inventario")}</div>
        <div style={{ display: "flex", gap: 6 }}>
          {[["#22c55e", items.filter(function(x) { return x.pct >= 50; }).length],
            ["#EAB308", items.filter(function(x) { return x.pct >= 20 && x.pct < 50; }).length],
            ["#ef4444", items.filter(function(x) { return x.pct < 20; }).length]].map(function(ch, i) {
            return <span key={i} style={{ background: ch[0] + "22", color: ch[0], border: "1px solid " + ch[0] + "55", borderRadius: 8, fontSize: 12, fontWeight: 800, padding: "2px 8px", fontFamily: "'Space Mono', monospace" }}>{ch[1]}</span>;
          })}
        </div>
      </div>
      {items.map(function(it, i) {
        return (
          <div key={i} style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4, gap: 8 }}>
              <span style={{ fontSize: 13, color: C.text, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{it.emoji} {it.nombre}</span>
              <span style={{ fontSize: 11, color: it.estado.color, fontWeight: 800, fontFamily: "'Space Mono', monospace", whiteSpace: "nowrap" }}>{it.txt} · {it.pct}% · {it.estado.label}</span>
            </div>
            <div style={{ height: 14, background: C.bg, borderRadius: 8, overflow: "hidden", border: "1px solid " + C.border, position: "relative" }}>
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(90deg,#ef4444,#f59e0b,#eab308,#84cc16,#22c55e)", opacity: 0.13 }} />
              {it.pct > 0 && (
                <div style={{ height: "100%", width: it.pct + "%", background: "linear-gradient(90deg,#ef4444,#f59e0b,#eab308,#84cc16,#22c55e)", backgroundSize: (10000 / it.pct) + "% 100%", borderRadius: 8, boxShadow: "0 0 10px " + it.estado.color + "77", transition: "width 0.5s ease" }} />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// --- INVENTARIO MODAL (de la GRANJA: uno solo para todas las camadas) ---
function InventarioModal({ inventario, C, fmtNum, onClose, onSave, modo }) {
  var inv = inventario || {};
  const [preinicio, setPreinicio] = useState(inv.preinicio != null ? String(inv.preinicio) : "");
  const [iniciador, setIniciador] = useState(inv.iniciador != null ? String(inv.iniciador) : "");
  const [crecimiento, setCrecimiento] = useState(inv.crecimiento != null ? String(inv.crecimiento) : "");
  const [engorde, setEngorde] = useState(inv.engorde != null ? String(inv.engorde) : "");
  const [agua, setAgua] = useState(inv.agua != null ? String(inv.agua) : "");
  const [paja, setPaja] = useState(inv.paja != null ? String(inv.paja) : "");
  const [vitaminas, setVitaminas] = useState(inv.vitaminas != null ? String(inv.vitaminas) : "");
  const [desparasitantes, setDesparasitantes] = useState(inv.desparasitantes != null ? String(inv.desparasitantes) : "");
  const [probioticos, setProbioticos] = useState(inv.probioticos != null ? String(inv.probioticos) : "");
  // "Óptimo" (=100%) configurable por renglón — alimenta las barras del tablero.
  const [opt, setOpt] = useState(Object.assign({}, INV_OPT_DEFAULT, inv.optimos || {}));
  function setOptimo(campo, valor) { var o = Object.assign({}, opt); o[campo] = valor; setOpt(o); setSaved(false); }
  // Unidad de medida por insumo (sacos, galones, litros, sobres…) — se ve en el tablero.
  const [unidades, setUnidades] = useState(Object.assign({}, inv.unidades || {}));
  function setUnidad(campo, valor) { var u = Object.assign({}, unidades); u[campo] = valor; setUnidades(u); setSaved(false); }
  // Otros insumos (cuadros agregados por el usuario con el botón +): {nombre, cantidad, optimo}
  const [otros, setOtros] = useState(Array.isArray(inv.otros) ? inv.otros : []);
  function setOtro(i, campo, valor) {
    var arr = otros.slice(); arr[i] = Object.assign({}, arr[i]); arr[i][campo] = valor;
    setOtros(arr); setSaved(false);
  }
  const [nota, setNota] = useState(inv.nota || "");
  const [saved, setSaved] = useState(false);
  // Nombre e icono propios de cada renglón (el dueño puede cambiarlos) y qué selector está abierto.
  const [nombres, setNombres] = useState(Object.assign({}, inv.nombres || {}));
  const [emojis, setEmojis] = useState(Object.assign({}, inv.emojis || {}));
  const [editando, setEditando] = useState(null);     // key del renglón que se está editando
  const [emojiAbierto, setEmojiAbierto] = useState(null);
  function setNombreDe(key, v) { setNombres(function (p) { var o = Object.assign({}, p); o[key] = v; return o; }); setSaved(false); }
  function setEmojiDe(key, v) { setEmojis(function (p) { var o = Object.assign({}, p); o[key] = v; return o; }); setSaved(false); }
  // Cabecera de un renglón: icono (se toca para cambiarlo), nombre y lápiz para renombrar.
  function Cabecera({ keyRenglon, emojiPorDefecto, nombrePorDefecto, color }) {
    var abierto = emojiAbierto === keyRenglon;
    var enEdicion = editando === keyRenglon;
    return (
      <div style={{ marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={function () { setEmojiAbierto(abierto ? null : keyRenglon); }} title="Cambiar el icono"
            style={{ fontSize: 20, lineHeight: 1, background: C.surface, border: "1px solid " + C.border, borderRadius: 10, padding: "6px 9px", cursor: "pointer" }}>
            {invEmoji({ emojis: emojis }, keyRenglon, emojiPorDefecto)}
          </button>
          {enEdicion ? (
            <input autoFocus value={nombres[keyRenglon] != null ? nombres[keyRenglon] : nombrePorDefecto}
              onChange={function (e) { setNombreDe(keyRenglon, e.target.value); }}
              onBlur={function () { setEditando(null); }}
              style={{ flex: 1, background: C.bg, border: "1.5px solid " + C.accent, borderRadius: 8, color: C.text, padding: "8px 11px", fontSize: 15, fontWeight: 800, fontFamily: "'Syne', sans-serif", outline: "none" }} />
          ) : (
            <div onClick={function () { setEditando(keyRenglon); }} style={{ flex: 1, fontSize: 15, fontWeight: 800, color: color || C.text, cursor: "text" }}>
              {invNombre({ nombres: nombres }, keyRenglon, nombrePorDefecto)}
            </div>
          )}
          <button onClick={function () { setEditando(enEdicion ? null : keyRenglon); }} title="Cambiar el nombre"
            style={{ background: "none", border: "none", color: C.textFaint, cursor: "pointer", fontSize: 15, padding: 4 }}>✏️</button>
        </div>
        {abierto && (
          <div style={{ marginTop: 8 }}>
            <SelectorEmoji C={C} valor={invEmoji({ emojis: emojis }, keyRenglon, emojiPorDefecto)}
              onElegir={function (e) { setEmojiDe(keyRenglon, e); setEmojiAbierto(null); }}
              onCerrar={function () { setEmojiAbierto(null); }} />
          </div>
        )}
      </div>
    );
  }

  // Inventario PURO: no se liga al cálculo de alimento de la camada.
  // Los renglones de alimento son los de las fases con las que cría la granja (3 o 4).
  var valoresFase = {
    preinicio: [preinicio, setPreinicio], iniciador: [iniciador, setIniciador],
    crecimiento: [crecimiento, setCrecimiento], engorde: [engorde, setEngorde],
  };
  var fasesInv = fasesDe(modo).map(function (fa) {
    var v = valoresFase[fa.inv];
    return { key: fa.inv, nombre: fa.nombre, emoji: fa.emoji, color: fa.color, val: v[0], set: v[1] };
  });
  var totalSacos = fasesInv.reduce(function(a, f) { return a + (Number(f.val) || 0); }, 0);
  var aguaPct = Math.max(0, Math.min(100, Number(agua) || 0));
  var aguaColor = aguaPct >= 50 ? C.green : aguaPct >= 20 ? "#EAB308" : C.danger;

  // Objeto "en vivo" para el tablero: se actualiza mientras escribes (y se guarda tal cual).
  var invLive = { preinicio: preinicio, iniciador: iniciador, crecimiento: crecimiento, engorde: engorde, agua: agua, paja: paja, vitaminas: vitaminas, desparasitantes: desparasitantes, probioticos: probioticos, optimos: opt, unidades: unidades, otros: otros };
  var tablero = invTablero(invLive, modo);
  var lblCap = { fontSize: 10, color: C.textFaint, fontFamily: "'Space Mono', monospace", letterSpacing: 1, marginBottom: 4 };
  var inputNum = { background: C.bg, border: "1.5px solid " + C.border, borderRadius: 8, color: C.text, padding: "9px 12px", fontSize: 16, fontWeight: 800, fontFamily: "'Space Mono', monospace", outline: "none", boxSizing: "border-box" };

  function guardar() {
    // Óptimos: guarda números válidos; si un campo quedó vacío/0, cae al valor por defecto.
    var optNum = {};
    Object.keys(INV_OPT_DEFAULT).forEach(function(k) {
      optNum[k] = Number(opt[k]) > 0 ? Number(opt[k]) : INV_OPT_DEFAULT[k];
    });
    if (onSave) onSave({
      preinicio: Number(preinicio) || 0, iniciador: Number(iniciador) || 0, crecimiento: Number(crecimiento) || 0, engorde: Number(engorde) || 0,
      agua: agua === "" ? null : aguaPct, paja: Number(paja) || 0,
      vitaminas: Number(vitaminas) || 0, desparasitantes: Number(desparasitantes) || 0, probioticos: Number(probioticos) || 0,
      optimos: optNum,
      unidades: {
        vitaminas: unidades.vitaminas || "unidades",
        desparasitantes: unidades.desparasitantes || "unidades",
        probioticos: unidades.probioticos || "unidades",
      },
      // Otros insumos: solo los que tienen nombre; cantidades/óptimos como números.
      otros: otros.filter(function(o) { return (o.nombre || "").trim() !== ""; }).map(function(o) {
        return { nombre: o.nombre.trim(), cantidad: Number(o.cantidad) || 0, optimo: Number(o.optimo) > 0 ? Number(o.optimo) : 5, unidad: o.unidad || "unidades", emoji: o.emoji || "🧪" };
      }),
      nombres: nombres, emojis: emojis,
      nota: nota || "", actualizado: new Date().toISOString(),
    });
    setSaved(true); setTimeout(function() { setSaved(false); }, 2500);
  }

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 50, background: C.bg, overflowY: "auto" }}>
      <div style={{ width: "100%", maxWidth: 480, margin: "0 auto", minHeight: "100vh", background: C.bg, padding: "20px 16px 60px" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.accent }}>📦 Inventario</div>
            <div style={{ fontSize: 14, color: C.textMuted, marginTop: 2 }}>De toda la granja · el mismo para todas las camadas</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "1px solid " + C.border, borderRadius: 8, color: C.textMuted, cursor: "pointer", padding: "7px 14px", fontSize: 15, fontFamily: "'Syne', sans-serif" }}>✕</button>
        </div>
        <div style={{ height: 1, background: C.border, margin: "14px 0 18px" }} />

        {/* 📊 TABLERO DE ESTADO — barras rojo→verde de todo el inventario (lo primero que se ve) */}
        <TableroInventario items={tablero} C={C} style={{ marginBottom: 16 }} />

        {/* 🌽 Alimento por fase */}
        <div style={{ background: C.card, borderRadius: 14, border: "1px solid " + C.border, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 4 }}>🌽 Alimento (sacos de 100 lb)</div>
          <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12 }}>Cuántos sacos hay de cada fase.</div>
          {fasesInv.map(function(f) {
            return (
              <div key={f.key} style={{ marginBottom: 10, background: C.surface, borderRadius: 10, padding: "10px 12px", border: "1px solid " + f.color + "44" }}>
                <Cabecera keyRenglon={f.key} emojiPorDefecto={f.emoji} nombrePorDefecto={tr(f.nombre)} color={f.color} />
                <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                  <div style={{ flex: 1 }}>
                    <div style={lblCap}>CANTIDAD (SACOS)</div>
                    <input type="number" inputMode="numeric" value={f.val} onChange={function(e) { f.set(e.target.value); setSaved(false); }} placeholder="0"
                      style={Object.assign({ width: "100%" }, inputNum)} />
                  </div>
                  <div style={{ width: 100 }}>
                    <div style={lblCap}>ÓPTIMO =100%</div>
                    <input type="number" inputMode="numeric" value={opt[f.key]} onChange={function(e) { setOptimo(f.key, e.target.value); }} placeholder={String(INV_OPT_DEFAULT[f.key])}
                      style={Object.assign({ width: "100%" }, inputNum, { color: C.textMuted })} />
                  </div>
                </div>
              </div>
            );
          })}
          <div style={{ fontSize: 13, color: C.textMuted, fontFamily: "'Space Mono', monospace", textAlign: "right" }}>
            Total: <b style={{ color: C.accent }}>{totalSacos} sacos</b> ({fmtNum(totalSacos * 100)} lb)
          </div>
        </div>

        {/* 💧 Agua */}
        <div style={{ background: C.card, borderRadius: 14, border: "1px solid " + C.border, padding: 16, marginBottom: 16 }}>
          <Cabecera keyRenglon="agua" emojiPorDefecto="💧" nombrePorDefecto="Agua disponible" />
          <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12 }}>Qué porcentaje del tanque/reserva está lleno (0–100%).</div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <input type="number" inputMode="numeric" min="0" max="100" value={agua} onChange={function(e) { setAgua(e.target.value); setSaved(false); }} placeholder="0"
              style={Object.assign({ width: 110 }, inputNum)} />
            <div style={{ fontSize: 18, fontWeight: 900, color: aguaColor, fontFamily: "'Space Mono', monospace" }}>%</div>
            <div style={{ flex: 1, height: 18, background: C.surface, borderRadius: 9, overflow: "hidden", border: "1px solid " + C.border }}>
              <div style={{ height: "100%", width: aguaPct + "%", background: aguaColor, borderRadius: 9, transition: "width 0.3s" }} />
            </div>
          </div>
          {agua !== "" && aguaPct < 30 && <div style={{ fontSize: 12, color: C.danger, fontWeight: 700, marginTop: 8 }}>⚠️ Agua baja — considera rellenar pronto</div>}
        </div>

        {/* 🌾 Paja de arroz */}
        <div style={{ background: C.card, borderRadius: 14, border: "1px solid " + C.border, padding: 16, marginBottom: 16 }}>
          <Cabecera keyRenglon="paja" emojiPorDefecto="🌾" nombrePorDefecto="Paja de arroz (cama del galpón)" />
          <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12 }}>Sacos de paja disponibles para la cama.</div>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
            <div style={{ flex: 1 }}>
              <div style={lblCap}>CANTIDAD (SACOS)</div>
              <input type="number" inputMode="numeric" value={paja} onChange={function(e) { setPaja(e.target.value); setSaved(false); }} placeholder="0"
                style={Object.assign({ width: "100%" }, inputNum)} />
            </div>
            <div style={{ width: 100 }}>
              <div style={lblCap}>ÓPTIMO =100%</div>
              <input type="number" inputMode="numeric" value={opt.paja} onChange={function(e) { setOptimo("paja", e.target.value); }} placeholder={String(INV_OPT_DEFAULT.paja)}
                style={Object.assign({ width: "100%" }, inputNum, { color: C.textMuted })} />
            </div>
          </div>
        </div>

        {/* 💊 Vitaminas · 🪱 Desparasitantes · 🦠 Probióticos — cada uno con su cuadro y su óptimo */}
        {[
          { key: "vitaminas", nombre: "Vitaminas", emoji: "💊", color: "#a855f7", val: vitaminas, set: setVitaminas, desc: "Cantidad disponible y su unidad (sobres, frascos, litros…)." },
          { key: "desparasitantes", nombre: "Desparasitantes", emoji: "🪱", color: "#ef4444", val: desparasitantes, set: setDesparasitantes, desc: "Cantidad disponible y su unidad (litros, frascos, sobres…)." },
          { key: "probioticos", nombre: "Probióticos", emoji: "🦠", color: "#ec4899", val: probioticos, set: setProbioticos, desc: "Cantidad disponible y su unidad (sobres, frascos, galones…)." },
        ].map(function(s) {
          return (
            <div key={s.key} style={{ background: C.card, borderRadius: 14, border: "1px solid " + s.color + "44", padding: 16, marginBottom: 16 }}>
              <Cabecera keyRenglon={s.key} emojiPorDefecto={s.emoji} nombrePorDefecto={s.nombre} color={s.color} />
              <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12 }}>{s.desc}</div>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 90 }}>
                  <div style={lblCap}>CANTIDAD</div>
                  <input type="number" inputMode="numeric" value={s.val} onChange={function(e) { s.set(e.target.value); setSaved(false); }} placeholder="0"
                    style={Object.assign({ width: "100%" }, inputNum)} />
                </div>
                <div style={{ width: 105 }}>
                  <div style={lblCap}>UNIDAD</div>
                  <select value={unidades[s.key] || "unidades"} onChange={function(e) { setUnidad(s.key, e.target.value); }}
                    style={Object.assign({ width: "100%" }, inputNum, { fontSize: 13, fontWeight: 700, fontFamily: "'Syne', sans-serif", padding: "11px 6px", cursor: "pointer", appearance: "auto" })}>
                    {INV_UNIDADES.map(function(u) { return <option key={u} value={u}>{tr(u)}</option>; })}
                  </select>
                </div>
                <div style={{ width: 90 }}>
                  <div style={lblCap}>ÓPTIMO =100%</div>
                  <input type="number" inputMode="numeric" value={opt[s.key]} onChange={function(e) { setOptimo(s.key, e.target.value); }} placeholder={String(INV_OPT_DEFAULT[s.key])}
                    style={Object.assign({ width: "100%" }, inputNum, { color: C.textMuted })} />
                </div>
              </div>
            </div>
          );
        })}

        {/* 🧪 Otros insumos (cuadros que el usuario agrega con el botón +) */}
        {otros.map(function(o, i) {
          return (
            <div key={i} style={{ background: C.card, borderRadius: 14, border: "1px solid " + C.accent + "44", padding: 16, marginBottom: 16 }}>
              {emojiAbierto === "otro_" + i && (
                <SelectorEmoji C={C} valor={o.emoji || "🧪"}
                  onElegir={function (e) { setOtro(i, "emoji", e); setEmojiAbierto(null); }}
                  onCerrar={function () { setEmojiAbierto(null); }} />
              )}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <button onClick={function () { setEmojiAbierto(emojiAbierto === "otro_" + i ? null : "otro_" + i); }} title="Cambiar el icono"
                  style={{ fontSize: 20, lineHeight: 1, background: C.surface, border: "1px solid " + C.border, borderRadius: 10, padding: "6px 9px", cursor: "pointer" }}>
                  {o.emoji || "🧪"}
                </button>
                <input value={o.nombre || ""} onChange={function(e) { setOtro(i, "nombre", e.target.value); }}
                  placeholder="Nombre del insumo (Ej. Cal, Desinfectante…)"
                  style={{ flex: 1, background: C.bg, border: "1.5px solid " + C.border, borderRadius: 8, color: C.text, padding: "9px 12px", fontSize: 15, fontWeight: 800, fontFamily: "'Syne', sans-serif", outline: "none", boxSizing: "border-box" }} />
                <button onClick={function() { setOtros(otros.filter(function(_, j) { return j !== i; })); setSaved(false); }}
                  title="Eliminar este insumo"
                  style={{ background: "none", border: "1px solid " + C.danger + "66", borderRadius: 8, color: C.danger, cursor: "pointer", fontSize: 16, padding: "6px 10px" }}>×</button>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 90 }}>
                  <div style={lblCap}>CANTIDAD</div>
                  <input type="number" inputMode="numeric" value={o.cantidad != null ? o.cantidad : ""} onChange={function(e) { setOtro(i, "cantidad", e.target.value); }} placeholder="0"
                    style={Object.assign({ width: "100%" }, inputNum)} />
                </div>
                <div style={{ width: 105 }}>
                  <div style={lblCap}>UNIDAD</div>
                  <select value={o.unidad || "unidades"} onChange={function(e) { setOtro(i, "unidad", e.target.value); }}
                    style={Object.assign({ width: "100%" }, inputNum, { fontSize: 13, fontWeight: 700, fontFamily: "'Syne', sans-serif", padding: "11px 6px", cursor: "pointer", appearance: "auto" })}>
                    {INV_UNIDADES.map(function(u) { return <option key={u} value={u}>{tr(u)}</option>; })}
                  </select>
                </div>
                <div style={{ width: 90 }}>
                  <div style={lblCap}>ÓPTIMO =100%</div>
                  <input type="number" inputMode="numeric" value={o.optimo != null ? o.optimo : ""} onChange={function(e) { setOtro(i, "optimo", e.target.value); }} placeholder="5"
                    style={Object.assign({ width: "100%" }, inputNum, { color: C.textMuted })} />
                </div>
              </div>
            </div>
          );
        })}

        {/* ➕ Agregar otro insumo */}
        <button onClick={function() { setOtros(otros.concat([{ nombre: "", cantidad: "", optimo: "", unidad: "unidades", emoji: "🧪" }])); setSaved(false); }}
          style={{ width: "100%", background: "transparent", border: "2px dashed " + C.accent + "66", borderRadius: 14, color: C.accent, padding: "14px", fontSize: 15, fontWeight: 800, fontFamily: "'Syne', sans-serif", cursor: "pointer", marginBottom: 16 }}>
          ➕ Agregar otro insumo
        </button>

        {/* Nota + Guardar */}
        <textarea value={nota} onChange={function(e) { setNota(e.target.value); setSaved(false); }}
          placeholder="Nota (opcional): proveedor, pendientes de comprar…" rows={2}
          style={{ width: "100%", background: C.surface, border: "1.5px solid " + C.border, borderRadius: 10, color: C.text, padding: "10px 12px", fontSize: 13, fontFamily: "'Syne', sans-serif", outline: "none", resize: "vertical", boxSizing: "border-box" }} />
        {inv.actualizado && (
          <div style={{ fontSize: 11, color: C.textFaint, marginTop: 6, fontFamily: "'Space Mono', monospace" }}>Última actualización: {new Date(inv.actualizado).toLocaleString()}</div>
        )}
        <button onClick={guardar}
          style={{ width: "100%", marginTop: 12, background: saved ? C.green : C.accent, color: "#1a0a00", border: "none", borderRadius: 12, padding: "15px", fontSize: 16, fontWeight: 900, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>
          {saved ? "✅ Inventario guardado" : "Guardar inventario"}
        </button>
      </div>
    </div>
  );
}

// --- PÁGINA AMBIENTE (detalle en vivo del galpón) ---
// Muestra temperatura, humedad y amoniaco con barras de posición y la gráfica de 24 h.
// El amoniaco NUNCA lleva número (el sensor no da un ppm confiable): solo palabra + semáforo.
function AmbientePage({ C, sensor, lecturas, esDemo, sinSenal, diaActual, camada, onClose, onAbrirSensor, onAbrirHistorial }) {
  var ls = Array.isArray(lecturas) ? lecturas : [];
  var ult = ls[0] || null;
  var tE = sensTempEstado(ult && ult.temp_c, diaActual);
  var hE = sensHumEstado(ult && ult.humedad);
  var nh3Techo = sensNh3EnTecho(ls);
  var nE = sensNh3Estado(ult && ult.nh3_ratio, nh3Techo);
  var trend = sensNh3Tendencia(ls);
  var meta = tE.obj;
  var clamp = function (v) { return Math.max(0, Math.min(100, v)); };

  // --- Datos de las últimas 24 h para la gráfica (de más viejo a más nuevo) ---
  var corte = Date.now() - 24 * 3600000;
  var serie = ls.filter(function (l) { return new Date(l.ts).getTime() >= corte; }).slice().reverse();
  var tempsG = serie.map(function (l) { return Number(l.temp_c); }).filter(function (v) { return !isNaN(v); });
  var humsG = serie.map(function (l) { return Number(l.humedad); }).filter(function (v) { return !isNaN(v); });
  var tMin = tempsG.length ? Math.min.apply(null, tempsG.concat([meta])) - 1.5 : meta - 4;
  var tMax = tempsG.length ? Math.max.apply(null, tempsG.concat([meta])) + 1.5 : meta + 4;
  var GW = 320, GH = 110;   // lienzo de la gráfica (viewBox)
  function puntos(vals, lo, hi) {
    if (!vals.length) return "";
    return vals.map(function (v, i) {
      var x = vals.length === 1 ? GW : (i / (vals.length - 1)) * GW;
      var y = GH - ((v - lo) / (hi - lo || 1)) * GH;
      return x.toFixed(1) + "," + Math.max(0, Math.min(GH, y)).toFixed(1);
    }).join(" ");
  }
  var yMeta = GH - ((meta - tMin) / ((tMax - tMin) || 1)) * GH;

  var titulo = { fontSize: 12, fontWeight: 800, color: C.textMuted, fontFamily: "'Space Mono', monospace", letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 8 };
  var caja = { background: C.card, borderRadius: 16, border: "1px solid " + C.border, padding: 16, marginBottom: 14 };

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 50, background: C.bg, overflowY: "auto" }}>
      <div style={{ width: "100%", maxWidth: 480, margin: "0 auto", minHeight: "100vh", background: C.bg, padding: "20px 16px 60px" }}>

        {/* Encabezado: EN VIVO + tiempo desde la última lectura + sello DEMO */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 6 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 20, fontWeight: 800, color: C.accent }}>🌡️ Ambiente</span>
              {esDemo && <BadgeDemo />}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
              <span className={sinSenal ? "" : "pe-vivo"} style={{ width: 8, height: 8, borderRadius: "50%", background: sinSenal ? SENS_GRIS : SENS_VERDE, flexShrink: 0 }} />
              <span style={{ fontSize: 12.5, color: sinSenal ? SENS_GRIS : C.textMuted, fontFamily: "'Space Mono', monospace", fontWeight: 700 }}>
                {sinSenal ? "OFFLINE" : "EN VIVO"} · {ult ? sensHaceTxt(ult.ts) : "sin datos"}
              </span>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "1px solid " + C.border, borderRadius: 8, color: C.textMuted, cursor: "pointer", padding: "7px 14px", fontSize: 15, fontFamily: "'Syne', sans-serif", flexShrink: 0 }}>✕</button>
        </div>
        <div style={{ height: 1, background: C.border, margin: "14px 0 18px" }} />

        {sinSenal && (
          <div style={{ background: SENS_GRIS + "18", border: "1px solid " + SENS_GRIS + "55", borderRadius: 12, padding: "12px 14px", marginBottom: 14, fontSize: 13, color: C.textMuted, lineHeight: 1.5 }}>
            ⚠️ El aparato no está reportando. Los valores de abajo son de la última lectura recibida{ult ? " (" + sensHaceTxt(ult.ts) + ")" : ""}, no del momento actual.
          </div>
        )}

        {/* TEMPERATURA */}
        <div style={caja}>
          <div style={titulo}>🌡️ Temperatura</div>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
            <div style={{ fontSize: 44, fontWeight: 900, color: sinSenal ? C.textMuted : tE.color, lineHeight: 1 }}>
              {ult && ult.temp_c != null ? Number(ult.temp_c).toFixed(1) + "°" : "—"}
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 12, color: C.textFaint, fontFamily: "'Space Mono', monospace" }}>META DÍA {diaActual}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>{Math.round(meta)}°C</div>
            </div>
          </div>
          {/* Barra azul→verde→ámbar→rojo con punto blanco en la posición actual */}
          <div style={{ position: "relative", height: 14, borderRadius: 8, marginTop: 14, background: "linear-gradient(90deg,#3B82F6,#22C55E 40%,#FBBF24 70%,#EF4444)" }}>
            {ult && ult.temp_c != null && (
              <div style={{ position: "absolute", top: -3, left: clamp(((Number(ult.temp_c) - (meta - 6)) / 12) * 100) + "%", width: 6, height: 20, background: "#fff", borderRadius: 3, transform: "translateX(-3px)", boxShadow: "0 0 6px #000a" }} />
            )}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5, fontSize: 10.5, color: C.textFaint, fontFamily: "'Space Mono', monospace" }}>
            <span>{Math.round(meta - 6)}°</span><span>meta {Math.round(meta)}°</span><span>{Math.round(meta + 6)}°</span>
          </div>
        </div>

        {/* HUMEDAD */}
        <div style={caja}>
          <div style={titulo}>💧 Humedad</div>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
            <div style={{ fontSize: 44, fontWeight: 900, color: sinSenal ? C.textMuted : hE.color, lineHeight: 1 }}>
              {ult && ult.humedad != null ? Number(ult.humedad).toFixed(0) + "%" : "—"}
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 12, color: C.textFaint, fontFamily: "'Space Mono', monospace" }}>RANGO ÓPTIMO</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>40 – 70 %</div>
            </div>
          </div>
          {/* Barra azul con el rango óptimo marcado (40–70 sobre una escala 0–100) */}
          <div style={{ position: "relative", height: 14, borderRadius: 8, marginTop: 14, background: "#263445", overflow: "hidden" }}>
            <div style={{ position: "absolute", left: "40%", width: "30%", top: 0, bottom: 0, background: "#22C55E33", borderLeft: "2px solid " + SENS_VERDE, borderRight: "2px solid " + SENS_VERDE }} />
            {ult && ult.humedad != null && (
              <div style={{ position: "absolute", top: 0, bottom: 0, left: 0, width: clamp(Number(ult.humedad)) + "%", background: "linear-gradient(90deg,#1D4ED8,#3B82F6)", opacity: 0.85 }} />
            )}
            {ult && ult.humedad != null && (
              <div style={{ position: "absolute", top: -3, left: clamp(Number(ult.humedad)) + "%", width: 6, height: 20, background: "#fff", borderRadius: 3, transform: "translateX(-3px)", boxShadow: "0 0 6px #000a" }} />
            )}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5, fontSize: 10.5, color: C.textFaint, fontFamily: "'Space Mono', monospace" }}>
            <span>0%</span><span>40%</span><span>70%</span><span>100%</span>
          </div>
        </div>

        {/* AMONIACO — palabra grande, SIN número ni ppm */}
        <div style={caja}>
          <div style={titulo}>🫧 Amoniaco{!esDemo && ult && ult.mq_raw != null && <span style={{ fontSize: 11, fontWeight: 400, color: C.textFaint, fontFamily: "'Space Mono', monospace", marginLeft: 8 }}>ADC {ult.mq_raw}</span>}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ width: 18, height: 18, borderRadius: "50%", background: nE.color, flexShrink: 0, boxShadow: "0 0 12px " + nE.color + "88" }} />
            <div style={{ fontSize: 34, fontWeight: 900, color: nE.color, lineHeight: 1, textTransform: "uppercase" }}>{nE.label}</div>
            <div style={{ fontSize: 26, color: C.textMuted, marginLeft: "auto" }}>{trend}</div>
          </div>
          {/* Barra verde→ámbar→rojo con las dos marcas de umbral y el triángulo de posición */}
          {(function () {
            var pAmar = (1.35 / 2.5) * 100, pRojo = (1.8 / 2.5) * 100;
            var pos = (!nh3Techo && ult && ult.nh3_ratio != null) ? clamp((Number(ult.nh3_ratio) / 2.5) * 100) : null;
            return (
              <div style={{ marginTop: 16 }}>
                <div style={{ position: "relative", height: 14, borderRadius: 8, background: "linear-gradient(90deg,#22C55E,#22C55E " + pAmar + "%,#FBBF24 " + pAmar + "%,#FBBF24 " + pRojo + "%,#EF4444 " + pRojo + "%)" }}>
                  <div style={{ position: "absolute", left: pAmar + "%", top: -2, bottom: -2, width: 2, background: "#0f172a99" }} />
                  <div style={{ position: "absolute", left: pRojo + "%", top: -2, bottom: -2, width: 2, background: "#0f172a99" }} />
                  {pos != null && (
                    <div style={{ position: "absolute", left: pos + "%", top: -9, transform: "translateX(-6px)", width: 0, height: 0, borderLeft: "6px solid transparent", borderRight: "6px solid transparent", borderTop: "9px solid #fff", filter: "drop-shadow(0 1px 2px #000a)" }} />
                  )}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 10.5, color: C.textFaint, fontFamily: "'Space Mono', monospace" }}>
                  <span>Bajo</span><span>Subiendo</span><span>Alto</span>
                </div>
                {!nE || nE.nivel === "gris" ? (
                  <div style={{ fontSize: 11.5, color: C.textFaint, marginTop: 8, lineHeight: 1.5 }}>El sensor todavía no está calibrado, por eso no se puede dar un veredicto del amoniaco.</div>
                ) : null}
              </div>
            );
          })()}
        </div>

        {/* GRÁFICA 24 H */}
        <div style={caja}>
          <div style={titulo}>📈 Últimas 24 horas</div>
          {serie.length < 2 ? (
            <div style={{ fontSize: 13, color: C.textFaint, padding: "16px 0", textAlign: "center" }}>Aún no hay suficientes lecturas para dibujar la gráfica.</div>
          ) : (
            <div>
              <svg viewBox={"0 0 " + GW + " " + GH} width="100%" height="130" preserveAspectRatio="none" style={{ display: "block" }}>
                {/* línea de meta de temperatura */}
                <line x1="0" y1={yMeta} x2={GW} y2={yMeta} stroke={C.textFaint} strokeWidth="1" strokeDasharray="5 4" opacity="0.8" />
                {/* humedad (escala 0–100) */}
                <polyline points={puntos(humsG, 0, 100)} fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" opacity="0.85" />
                {/* temperatura */}
                <polyline points={puntos(tempsG, tMin, tMax)} fill="none" stroke="#F97316" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
              </svg>
              <div style={{ display: "flex", gap: 14, marginTop: 10, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11.5, color: C.textMuted, display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 14, height: 3, background: "#F97316", borderRadius: 2 }} /> Temperatura</span>
                <span style={{ fontSize: 11.5, color: C.textMuted, display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 14, height: 3, background: "#3B82F6", borderRadius: 2 }} /> Humedad</span>
                <span style={{ fontSize: 11.5, color: C.textFaint, display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 14, height: 0, borderTop: "2px dashed " + C.textFaint }} /> Meta {Math.round(meta)}°</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 10.5, color: C.textFaint, fontFamily: "'Space Mono', monospace" }}>
                <span>hace 24 h</span><span>ahora</span>
              </div>
            </div>
          )}
        </div>

        {/* ESTADO DEL APARATO */}
        <div style={caja}>
          <div style={titulo}>📡 Aparato</div>
          {[
            ["Nombre", sensor.nombre || "Galpón"],
            ["Último envío", ult ? sensHaceTxt(ult.ts) : (sensor.ultimo_reporte ? sensHaceTxt(sensor.ultimo_reporte) : "nunca")],
            ["Señal", sinSenal ? "🔴 Sin señal" : "🟢 Buena"],
            ["Camada", camada ? camada.nombre : "—"],
          ].map(function (r) {
            return (
              <div key={r[0]} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid " + C.border, fontSize: 13.5 }}>
                <span style={{ color: C.textMuted }}>{r[0]}</span>
                <span style={{ fontWeight: 700, color: C.text }}>{r[1]}</span>
              </div>
            );
          })}
          {esDemo && (
            <div style={{ fontSize: 11.5, color: "#FBBF24", marginTop: 10, lineHeight: 1.5 }}>
              ⚠️ Estás viendo datos de DEMOSTRACIÓN, no de tu galpón. Apaga el modo demostración en Ajustes cuando conectes tu aparato.
            </div>
          )}
          {onAbrirHistorial && (
            <button onClick={onAbrirHistorial}
              style={{ width: "100%", marginTop: 12, background: C.accent, border: "none", borderRadius: 12, color: "#1a0a00", padding: "13px", fontSize: 14, fontWeight: 800, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>
              📈 Ver historial del ambiente
            </button>
          )}
          {onAbrirSensor && (
            <button onClick={onAbrirSensor}
              style={{ width: "100%", marginTop: 12, background: "transparent", border: "1px solid " + C.border, borderRadius: 12, color: C.text, padding: "12px", fontSize: 14, fontWeight: 700, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>
              ⚙️ Ver / configurar el sensor
            </button>
          )}
        </div>

        <button onClick={onClose}
          style={{ width: "100%", background: "transparent", color: C.textMuted, border: "1px solid " + C.border, borderRadius: 14, padding: "13px", fontSize: 15, fontWeight: 700, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>
          Volver
        </button>
      </div>
    </div>
  );
}

// --- MIS CONTROLES (luz · ventilación · regadera) ---
const CTRL_ROLES = [
  { id: "luz",         label: "LUZ",         emoji: "💡", color: "#F5C200" },
  { id: "ventilacion", label: "VENTILACIÓN", emoji: "🌬️", color: "#22D3EE" },
  { id: "regadera",    label: "REGADERA",    emoji: "💦", color: "#3B82F6" },
];
function rolInfo(r) { return CTRL_ROLES.find(function (x) { return x.id === r; }) || CTRL_ROLES[0]; }
function cerebroDefecto(rol) {
  if (rol === "ventilacion") return { tipo: "ventilacion", enciende_sobre: 1.5, apaga_bajo: 1.0, minimo_encendido_min: 2, modo_seguro_min_on: 5, modo_seguro_ciclo_min: 20 };
  if (rol === "regadera") return { tipo: "regadera", sobre_ideal: 2.0, minutos_on: 3, minutos_off: 12, pausa_humedad: 85, arranca_apagada: true };
  return { tipo: "luz", programa: [
    { hasta_dia: 3, horas_luz: 23, inicio: "05:00" },
    { hasta_dia: 7, horas_luz: 20, inicio: "05:00" },
    { hasta_dia: 21, horas_luz: 18, inicio: "05:00" },
    { hasta_dia: 99, horas_luz: 20, inicio: "05:00" },
  ] };
}
// Suma horas a "HH:MM" y devuelve "HH:MM"
function sumaHoras(hhmm, horas) {
  var p = String(hhmm || "05:00").split(":");
  var m = (Number(p[0]) || 0) * 60 + (Number(p[1]) || 0) + Math.round(horas * 60);
  m = ((m % 1440) + 1440) % 1440;
  return String(Math.floor(m / 60)).padStart(2, "0") + ":" + String(m % 60).padStart(2, "0");
}
function MisControles({ C, session, diaActual, sensores, abrirId, onClose }) {
  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [sinTabla, setSinTabla] = useState(false);
  const [abierto, setAbierto] = useState(null);     // control en edición
  const [borrar, setBorrar] = useState(null);
  const [liberar, setLiberar] = useState(null);
  const [guardado, setGuardado] = useState(false);

  function cargar() {
    setCargando(true);
    SB.select("controles", "owner_email=eq." + encodeURIComponent(session.ownerEmail) + "&select=*&order=id.asc")
      .then(function (rows) {
        if (Array.isArray(rows)) { setLista(rows); setSinTabla(false); }
        else setSinTabla(true);
        setCargando(false);
      })
      .catch(function () { setSinTabla(true); setCargando(false); });
  }
  useEffect(function () { cargar(); }, [session.ownerEmail]);
  // Si se entró tocando un control desde su Pollo Box, se abre ese directamente.
  useEffect(function () {
    if (!abrirId || !lista.length) return;
    var c = lista.find(function (x) { return String(x.id) === String(abrirId); });
    if (c) setAbierto(c);
  }, [abrirId, lista.length]);

  function guardar(ctrl, cambios) {
    var act = Object.assign({}, ctrl, cambios);
    setAbierto(act);
    setLista(function (prev) { return prev.map(function (c) { return c.id === ctrl.id ? act : c; }); });
    SB.update("controles", { id: ctrl.id, owner_email: session.ownerEmail }, cambios)
      .then(function () { setGuardado(true); setTimeout(function () { setGuardado(false); }, 1800); })
      .catch(function () {});
  }
  function eliminar() {
    if (!borrar) return;
    SB.delete("controles", { id: borrar.id, owner_email: session.ownerEmail }).then(function () {
      setLista(function (prev) { return prev.filter(function (c) { return c.id !== borrar.id; }); });
      setBorrar(null); setAbierto(null);
    }).catch(function () { setBorrar(null); });
  }
  // Liberar: lo deja sin dueño y sin Pollo Box, listo para instalarse en otra cuenta.
  function hacerLiberar() {
    if (!liberar) return;
    SB.update("controles", { id: liberar.id, owner_email: session.ownerEmail }, { owner_email: null, owner_id: null, caja_id: null })
      .then(function () {
        setLista(function (prev) { return prev.filter(function (x) { return x.id !== liberar.id; }); });
        setLiberar(null); setAbierto(null);
      })
      .catch(function () { setLiberar(null); });
  }
  function estadoDe(c) {
    var min = c.ultima_conexion ? Math.floor((Date.now() - new Date(c.ultima_conexion).getTime()) / 60000) : null;
    if (min == null || min > 15) return { txt: "Sin señal" + (c.ultima_conexion ? " desde hace " + min + " min" : ""), col: SENS_GRIS };
    if ((c.modo || "auto") !== "auto") return { txt: c.modo === "on" ? "Forzado encendido" : "Forzado apagado", col: SENS_AMAR };
    return c.encendido ? { txt: "Encendido", col: SENS_VERDE } : { txt: "Apagado", col: C.textMuted };
  }

  var caja = { background: C.card, borderRadius: 16, border: "1px solid " + C.border, padding: 16, marginBottom: 14 };
  var lbl = { fontSize: 11.5, color: C.textMuted, fontFamily: "'Space Mono', monospace", letterSpacing: 1, marginBottom: 6, marginTop: 14 };
  var inp = { width: "100%", background: C.bg, border: "1.5px solid " + C.border, borderRadius: 10, color: C.text, padding: "11px 12px", fontSize: 15, fontFamily: "'Syne', sans-serif", outline: "none", boxSizing: "border-box" };

  // ── Editor de un control ──
  function Editor({ c }) {
    var info = rolInfo(c.rol);
    var cerebro = c.cerebro || cerebroDefecto(c.rol);
    function setCerebro(campo, valor) {
      var n = Object.assign({}, cerebro); n[campo] = valor;
      guardar(c, { cerebro: n });
    }
    return (
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 62, background: C.bg, overflowY: "auto" }}>
        <div style={{ width: "100%", maxWidth: 480, margin: "0 auto", minHeight: "100vh", padding: "20px 16px 60px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, gap: 10 }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: info.color, minWidth: 0 }}>{info.emoji} {c.nombre || info.label}</div>
            <button onClick={function () { setAbierto(null); }} style={{ background: "none", border: "1px solid " + C.border, borderRadius: 8, color: C.textMuted, cursor: "pointer", padding: "7px 14px", fontSize: 15, flexShrink: 0 }}>✕</button>
          </div>

          {guardado && <div style={{ background: C.green + "22", border: "1px solid " + C.green + "66", borderRadius: 10, padding: "9px 12px", fontSize: 13, color: C.green, marginBottom: 12 }}>✅ Guardado</div>}

          <div style={caja}>
            <div style={lbl}>NOMBRE</div>
            <input defaultValue={c.nombre || ""} onBlur={function (e) { guardar(c, { nombre: e.target.value.trim() || c.nombre }); }} style={inp} />

            <div style={lbl}>¿QUÉ CONTROLA?</div>
            <div style={{ display: "flex", gap: 8 }}>
              {CTRL_ROLES.map(function (r) {
                var on = c.rol === r.id;
                return (
                  <button key={r.id} onClick={function () {
                    if (on) return;
                    if (!window.confirm("¿Cambiar a " + r.label + "? Se reiniciará la configuración de este control.")) return;
                    guardar(c, { rol: r.id, cerebro: cerebroDefecto(r.id) });
                  }}
                    style={{ flex: 1, padding: "11px 4px", borderRadius: 10, border: "2px solid " + (on ? r.color : C.border), background: on ? r.color + "22" : "transparent", color: on ? r.color : C.textMuted, fontSize: 11.5, fontWeight: on ? 900 : 700, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>
                    <div style={{ fontSize: 17 }}>{r.emoji}</div>{r.label}
                  </button>
                );
              })}
            </div>

            <div style={lbl}>MODO</div>
            <div style={{ display: "flex", gap: 8 }}>
              {[{ id: "auto", t: "Automático" }, { id: "on", t: "Forzar encendido" }, { id: "off", t: "Forzar apagado" }].map(function (m) {
                var on = (c.modo || "auto") === m.id;
                return (
                  <button key={m.id} onClick={function () { guardar(c, { modo: m.id }); }}
                    style={{ flex: 1, padding: "10px 4px", borderRadius: 10, border: "2px solid " + (on ? C.accent : C.border), background: on ? C.accent + "22" : "transparent", color: on ? C.accent : C.textMuted, fontSize: 11.5, fontWeight: on ? 900 : 700, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>
                    {m.t}
                  </button>
                );
              })}
            </div>
            <div style={{ fontSize: 11.5, color: C.textFaint, marginTop: 6, lineHeight: 1.5 }}>En modo forzado el control obedece hasta que lo vuelvas a poner en Automático.</div>
          </div>

          {/* ── Cerebro según el rol ── */}
          {c.rol === "luz" && (
            <div style={caja}>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 4 }}>💡 Programa de luz por edad</div>
              <div style={{ fontSize: 12.5, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 }}>
                Hoy, día {diaActual}: {(function () {
                  var tramo = (cerebro.programa || []).find(function (t) { return diaActual <= Number(t.hasta_dia); }) || (cerebro.programa || [])[(cerebro.programa || []).length - 1];
                  if (!tramo) return "sin programa";
                  return "luz de " + tramo.inicio + " a " + sumaHoras(tramo.inicio, Number(tramo.horas_luz)) + " (" + tramo.horas_luz + " horas)";
                })()}
              </div>
              {(cerebro.programa || []).map(function (t, i) {
                return (
                  <div key={i} style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 8, background: C.surface, borderRadius: 10, padding: "8px 10px" }}>
                    <span style={{ fontSize: 11, color: C.textFaint, whiteSpace: "nowrap" }}>hasta día</span>
                    <input type="number" inputMode="numeric" defaultValue={t.hasta_dia}
                      onBlur={function (e) { var p = (cerebro.programa || []).slice(); p[i] = Object.assign({}, p[i], { hasta_dia: Number(e.target.value) || 0 }); setCerebro("programa", p); }}
                      style={{ width: 52, background: C.bg, border: "1px solid " + C.border, borderRadius: 8, color: C.text, padding: "7px 6px", fontSize: 14, textAlign: "center", fontFamily: "'Space Mono', monospace", outline: "none" }} />
                    <input type="number" inputMode="numeric" defaultValue={t.horas_luz}
                      onBlur={function (e) { var p = (cerebro.programa || []).slice(); p[i] = Object.assign({}, p[i], { horas_luz: Number(e.target.value) || 0 }); setCerebro("programa", p); }}
                      style={{ width: 52, background: C.bg, border: "1px solid " + C.border, borderRadius: 8, color: C.text, padding: "7px 6px", fontSize: 14, textAlign: "center", fontFamily: "'Space Mono', monospace", outline: "none" }} />
                    <span style={{ fontSize: 11, color: C.textFaint }}>h desde</span>
                    <input defaultValue={t.inicio}
                      onBlur={function (e) { var p = (cerebro.programa || []).slice(); p[i] = Object.assign({}, p[i], { inicio: e.target.value || "05:00" }); setCerebro("programa", p); }}
                      style={{ width: 62, background: C.bg, border: "1px solid " + C.border, borderRadius: 8, color: C.text, padding: "7px 6px", fontSize: 14, textAlign: "center", fontFamily: "'Space Mono', monospace", outline: "none" }} />
                    <button onClick={function () { var p = (cerebro.programa || []).filter(function (_, j) { return j !== i; }); setCerebro("programa", p); }}
                      style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", fontSize: 17, marginLeft: "auto" }}>×</button>
                  </div>
                );
              })}
              <button onClick={function () { setCerebro("programa", (cerebro.programa || []).concat([{ hasta_dia: 99, horas_luz: 18, inicio: "05:00" }])); }}
                style={{ width: "100%", background: "transparent", border: "2px dashed " + C.accent + "66", borderRadius: 10, color: C.accent, padding: "10px", fontSize: 13.5, fontWeight: 800, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>➕ Agregar tramo</button>
            </div>
          )}

          {c.rol === "ventilacion" && (
            <div style={caja}>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 4 }}>🌬️ Cuándo ventilar</div>
              <div style={{ fontSize: 12.5, color: C.textMuted, marginBottom: 14, lineHeight: 1.55 }}>
                {(function () {
                  var r = TEMP_IDEAL_SENSOR[Math.max(0, Math.min(diaActual, 35))] || [20, 23];
                  var ideal = (r[0] + r[1]) / 2;
                  return "Hoy la temperatura ideal es " + ideal + "°. La ventilación encenderá a " +
                    (ideal + Number(cerebro.enciende_sobre || 1.5)).toFixed(1) + "° y apagará a " +
                    (ideal - Number(cerebro.apaga_bajo || 1.0)).toFixed(1) + "°.";
                })()}
              </div>
              {[
                { k: "enciende_sobre", t: "Enciende cuando pase la ideal en", u: "°", min: 0.5, max: 5, step: 0.5 },
                { k: "apaga_bajo", t: "Apaga cuando baje de la ideal en", u: "°", min: 0, max: 3, step: 0.5 },
                { k: "minimo_encendido_min", t: "Mínimo encendido", u: " min", min: 1, max: 15, step: 1 },
              ].map(function (s) {
                return (
                  <div key={s.k} style={{ marginBottom: 14 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: C.text, marginBottom: 6 }}>
                      <span>{s.t}</span><b style={{ color: C.accent }}>{cerebro[s.k]}{s.u}</b>
                    </div>
                    <input type="range" min={s.min} max={s.max} step={s.step} value={cerebro[s.k]}
                      onChange={function (e) { setCerebro(s.k, Number(e.target.value)); }}
                      style={{ width: "100%", accentColor: C.accent }} />
                  </div>
                );
              })}
              <div style={{ fontSize: 11.5, color: C.textFaint, lineHeight: 1.55, borderTop: "1px solid " + C.border, paddingTop: 10 }}>
                🛡️ Modo seguro: si pasa 15 minutos sin recibir la temperatura, enciende {cerebro.modo_seguro_min_on || 5} minutos de cada {cerebro.modo_seguro_ciclo_min || 20}. En un galpón, ventilar de más es preferible a no ventilar.
              </div>
            </div>
          )}

          {c.rol === "regadera" && (
            <div style={caja}>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 4 }}>💦 Cuándo regar</div>
              <div style={{ fontSize: 12.5, color: C.textMuted, marginBottom: 14, lineHeight: 1.55 }}>
                {(function () {
                  var r = TEMP_IDEAL_SENSOR[Math.max(0, Math.min(diaActual, 35))] || [20, 23];
                  var ideal = (r[0] + r[1]) / 2;
                  return "Hoy empezará a regar si pasa de " + (ideal + Number(cerebro.sobre_ideal || 2)).toFixed(1) +
                    "°, en ciclos de " + cerebro.minutos_on + " min encendida y " + cerebro.minutos_off + " min apagada.";
                })()}
              </div>
              {[
                { k: "sobre_ideal", t: "Empieza cuando pase la ideal en", u: "°", min: 0.5, max: 6, step: 0.5 },
                { k: "minutos_on", t: "Minutos regando", u: " min", min: 1, max: 15, step: 1 },
                { k: "minutos_off", t: "Minutos de descanso", u: " min", min: 3, max: 60, step: 1 },
                { k: "pausa_humedad", t: "Se detiene si la humedad pasa de", u: "%", min: 60, max: 95, step: 1 },
              ].map(function (s) {
                return (
                  <div key={s.k} style={{ marginBottom: 14 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: C.text, marginBottom: 6 }}>
                      <span>{s.t}</span><b style={{ color: C.accent }}>{cerebro[s.k]}{s.u}</b>
                    </div>
                    <input type="range" min={s.min} max={s.max} step={s.step} value={cerebro[s.k]}
                      onChange={function (e) { setCerebro(s.k, Number(e.target.value)); }}
                      style={{ width: "100%", accentColor: C.accent }} />
                  </div>
                );
              })}
              <div style={{ fontSize: 11.5, color: C.textFaint, lineHeight: 1.55, borderTop: "1px solid " + C.border, paddingTop: 10 }}>
                🔌 Tras un corte de luz siempre arranca apagada, nunca al revés.
              </div>
            </div>
          )}

          {/* R7-7 · Mover a otra Pollo Box */}
          <div style={caja}>
            <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 4 }}>📦 ¿En qué Pollo Box está?</div>
            <div style={{ fontSize: 12.5, color: C.textMuted, marginBottom: 10, lineHeight: 1.5 }}>Cámbialo si lo instalaste con la caja equivocada.</div>
            <select value={c.caja_id != null ? String(c.caja_id) : ""}
              onChange={function (e) { guardar(c, { caja_id: e.target.value ? e.target.value : null }); }}
              style={{ ...inp, cursor: "pointer" }}>
              <option value="">Sin asignar</option>
              {(sensores || []).map(function (sx) {
                return <option key={sx.id} value={String(sx.id)}>{sx.nombre || "Pollo Box"}{sx.serial ? " · " + sx.serial : ""}</option>;
              })}
            </select>
          </div>

          {/* R7-7 · Liberar para instalarlo en otra cuenta */}
          <button onClick={function () { setLiberar(c); }}
            style={{ width: "100%", marginBottom: 10, background: "transparent", color: C.warn, border: "1px solid " + C.warn + "66", borderRadius: 12, padding: "12px", fontSize: 13.5, fontWeight: 700, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>
            🔓 Liberar control (para otra cuenta)
          </button>

          <button onClick={function () { setBorrar(c); }}
            style={{ width: "100%", background: "transparent", color: C.danger, border: "1px solid " + C.danger + "66", borderRadius: 12, padding: "12px", fontSize: 13.5, fontWeight: 700, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>
            🗑 Eliminar este control
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 55, background: C.bg, overflowY: "auto" }}>
      <div style={{ width: "100%", maxWidth: 480, margin: "0 auto", minHeight: "100vh", padding: "20px 16px 60px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.accent }}>🎛️ Controles</div>
            <div style={{ fontSize: 13, color: C.textMuted, marginTop: 2 }}>Luz, ventilación y regadera de tus galpones</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "1px solid " + C.border, borderRadius: 8, color: C.textMuted, cursor: "pointer", padding: "7px 14px", fontSize: 15 }}>✕</button>
        </div>
        <div style={{ height: 1, background: C.border, margin: "14px 0 18px" }} />

        {cargando ? (
          <div style={{ textAlign: "center", color: C.textFaint, padding: "40px 0" }}>Cargando…</div>
        ) : lista.length === 0 ? (
          <div style={caja}>
            <div style={{ fontSize: 36, textAlign: "center", marginBottom: 10 }}>🎛️</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.text, textAlign: "center", marginBottom: 8 }}>Todavía no tienes controles</div>
            <div style={{ fontSize: 13.5, color: C.textMuted, lineHeight: 1.6, textAlign: "center" }}>
              Dale corriente al control cerca de tu <b style={{ color: C.text }}>Pollo Box</b>. Aparecerá dentro de ella solo, sin escribir ningún código.
            </div>
            {sinTabla && (
              <div style={{ fontSize: 11.5, color: C.textFaint, marginTop: 14, lineHeight: 1.5, borderTop: "1px solid " + C.border, paddingTop: 10 }}>
                Nota: esta función se termina de activar cuando se aplique la actualización pendiente de la base de datos.
              </div>
            )}
          </div>
        ) : (
          lista.map(function (c) {
            var info = rolInfo(c.rol);
            var est = estadoDe(c);
            return (
              <button key={c.id} onClick={function () { setAbierto(c); }}
                style={{ width: "100%", textAlign: "left", background: C.card, borderRadius: 16, border: "1px solid " + C.border, padding: "14px 16px", marginBottom: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 26, flexShrink: 0 }}>{info.emoji}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.nombre || info.label}</div>
                  <div style={{ fontSize: 11, color: info.color, fontFamily: "'Space Mono', monospace", letterSpacing: 1 }}>{info.label}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 4 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: est.col, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: est.col, fontWeight: 700 }}>{est.txt}</span>
                  </div>
                </div>
                <span style={{ fontSize: 18, color: C.textFaint, flexShrink: 0 }}>›</span>
              </button>
            );
          })
        )}

        {lista.length > 0 && (
          <div style={{ fontSize: 12.5, color: C.textFaint, lineHeight: 1.6, background: C.surface, borderRadius: 12, padding: "12px 14px", marginTop: 4 }}>
            ➕ Para agregar otro: dale corriente al control cerca de la <b style={{ color: C.textMuted }}>Pollo Box</b> del galpón donde va. Aparecerá dentro de esa caja solo.
          </div>
        )}
      </div>

      {abierto && <Editor c={abierto} />}

      {liberar && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 66, background: "#000c", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={function () { setLiberar(null); }}>
          <div onClick={function (e) { e.stopPropagation(); }} style={{ width: "100%", maxWidth: 380, background: C.surface, borderRadius: 16, padding: "22px 20px", border: "1.5px solid " + C.warn }}>
            <div style={{ fontSize: 34, textAlign: "center", marginBottom: 8 }}>🔓</div>
            <div style={{ fontSize: 16.5, fontWeight: 800, color: C.warn, textAlign: "center", marginBottom: 12 }}>¿Liberar "{liberar.nombre}"?</div>
            <div style={{ fontSize: 13.5, color: C.textMuted, lineHeight: 1.6, marginBottom: 18 }}>
              Saldrá de tu cuenta y quedará listo para instalarse en otra. El aparato no se borra: quien lo instale lo adoptará con su propia Pollo Box.
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={function () { setLiberar(null); }} style={{ flex: 1, background: C.card, color: C.text, border: "1px solid " + C.border, borderRadius: 12, padding: "13px", fontSize: 14.5, fontWeight: 700, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>Cancelar</button>
              <button onClick={hacerLiberar} style={{ flex: 1, background: C.warn, color: "#1a0a00", border: "none", borderRadius: 12, padding: "13px", fontSize: 14.5, fontWeight: 800, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>Sí, liberar</button>
            </div>
          </div>
        </div>
      )}

      {borrar && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 66, background: "#000c", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={function () { setBorrar(null); }}>
          <div onClick={function (e) { e.stopPropagation(); }} style={{ width: "100%", maxWidth: 380, background: C.surface, borderRadius: 16, padding: "22px 20px", border: "1.5px solid " + C.danger }}>
            <div style={{ fontSize: 34, textAlign: "center", marginBottom: 8 }}>🗑</div>
            <div style={{ fontSize: 16.5, fontWeight: 800, color: C.danger, textAlign: "center", marginBottom: 12 }}>¿Eliminar "{borrar.nombre}"?</div>
            <div style={{ fontSize: 13.5, color: C.textMuted, lineHeight: 1.6, marginBottom: 18 }}>Saldrá de la app. Si el aparato sigue conectado, volverá a aparecer solo la próxima vez que reporte.</div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={function () { setBorrar(null); }} style={{ flex: 1, background: C.card, color: C.text, border: "1px solid " + C.border, borderRadius: 12, padding: "13px", fontSize: 14.5, fontWeight: 700, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>Cancelar</button>
              <button onClick={eliminar} style={{ flex: 1, background: C.danger, color: "#fff", border: "none", borderRadius: 12, padding: "13px", fontSize: 14.5, fontWeight: 800, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>Sí, eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- HISTORIAL DE AMBIENTE ---
// Cómo estuvo el galpón a lo largo del tiempo. El amoniaco NUNCA lleva número:
// se dibuja como una franja de color (verde/ámbar/rojo) debajo de las gráficas.
const HIST_RANGOS = [
  { id: "24h",    label: "24 HORAS",      horas: 24,        agrupa: "10min" },
  { id: "7d",     label: "7 DÍAS",        horas: 24 * 7,    agrupa: "hora" },
  { id: "30d",    label: "30 DÍAS",       horas: 24 * 30,   agrupa: "dia" },
  { id: "camada", label: "TODA LA CAMADA", horas: null,     agrupa: "dia" },
];
function HistorialAmbiente({ C, sensor, camada, diaActual, esDemo, lecturasDemo, onClose }) {
  const [rango, setRango] = useState("24h");
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(true);

  useEffect(function () {
    setCargando(true);
    var cfg = HIST_RANGOS.find(function (r) { return r.id === rango; }) || HIST_RANGOS[0];
    // En modo demostración los datos son simulados (no se consulta la nube).
    if (esDemo) {
      setFilas((lecturasDemo || []).slice().reverse());
      setCargando(false);
      return;
    }
    var desde;
    if (cfg.horas) desde = new Date(Date.now() - cfg.horas * 3600000).toISOString();
    else desde = camada && camada.fechaInicio ? new Date(camada.fechaInicio + "T00:00:00").toISOString() : new Date(Date.now() - 24 * 3600000 * 60).toISOString();
    SB.select("lecturas_sensor",
      columnaLectura() + "=eq." + sensor.id + "&ts=gte." + encodeURIComponent(desde) +
      "&select=temp_c,humedad,nh3_ratio,mq_raw,ts&order=ts.asc&limit=5000")
      .then(function (rows) { setFilas(Array.isArray(rows) ? rows : []); setCargando(false); })
      .catch(function () { setFilas([]); setCargando(false); });
  }, [rango, sensor && sensor.id, esDemo]);

  var cfg = HIST_RANGOS.find(function (r) { return r.id === rango; }) || HIST_RANGOS[0];

  // Agrupar para que no se vuelva lento ni ilegible.
  var puntos = (function () {
    if (!filas.length) return [];
    var ms = cfg.agrupa === "10min" ? 600000 : cfg.agrupa === "hora" ? 3600000 : 86400000;
    var cubos = {};
    filas.forEach(function (l) {
      var t = new Date(l.ts).getTime();
      var k = Math.floor(t / ms) * ms;
      if (!cubos[k]) cubos[k] = { t: k, temps: [], hums: [], nh3: [], raws: [] };
      if (l.temp_c != null) cubos[k].temps.push(Number(l.temp_c));
      if (l.humedad != null) cubos[k].hums.push(Number(l.humedad));
      if (l.nh3_ratio != null) cubos[k].nh3.push(Number(l.nh3_ratio));
      if (l.mq_raw != null) cubos[k].raws.push(Number(l.mq_raw));
    });
    var prom = function (a) { return a.length ? a.reduce(function (x, y) { return x + y; }, 0) / a.length : null; };
    return Object.keys(cubos).map(Number).sort(function (a, b) { return a - b; }).map(function (k) {
      var c = cubos[k];
      return {
        t: c.t,
        temp: prom(c.temps), tempMin: c.temps.length ? Math.min.apply(null, c.temps) : null, tempMax: c.temps.length ? Math.max.apply(null, c.temps) : null,
        hum: prom(c.hums),
        nh3: c.nh3.length ? Math.max.apply(null, c.nh3) : null,
        // Tramo con el sensor en el techo → se pinta "revisar", no verde.
        nh3Techo: c.raws.length ? Math.max.apply(null, c.raws) >= NH3_TECHO : false,
      };
    });
  })();

  // Resumen del período
  var resumen = (function () {
    var temps = puntos.map(function (p) { return p.temp; }).filter(function (v) { return v != null; });
    if (!temps.length) return null;
    var fuera = 0;
    puntos.forEach(function (p) {
      if (p.temp == null) return;
      var d = camada && camada.fechaInicio ? Math.max(0, Math.round((p.t - new Date(camada.fechaInicio + "T00:00:00").getTime()) / 86400000)) : diaActual;
      var r = TEMP_IDEAL_SENSOR[Math.max(0, Math.min(d, 35))] || [20, 23];
      if (p.temp < r[0] || p.temp > r[1]) fuera++;
    });
    var horasPorPunto = cfg.agrupa === "10min" ? (1 / 6) : cfg.agrupa === "hora" ? 1 : 24;
    return {
      min: Math.min.apply(null, temps).toFixed(1),
      max: Math.max.apply(null, temps).toFixed(1),
      prom: (temps.reduce(function (a, b) { return a + b; }, 0) / temps.length).toFixed(1),
      horasFuera: Math.round(fuera * horasPorPunto),
    };
  })();

  var GW = 320, GH = 120;
  function serieSVG(clave, lo, hi) {
    var v = puntos.map(function (p) { return p[clave]; });
    var pts = [];
    v.forEach(function (x, i) {
      if (x == null) return;
      var px = puntos.length === 1 ? GW : (i / (puntos.length - 1)) * GW;
      var py = GH - ((x - lo) / ((hi - lo) || 1)) * GH;
      pts.push(px.toFixed(1) + "," + Math.max(0, Math.min(GH, py)).toFixed(1));
    });
    return pts.join(" ");
  }
  var temps = puntos.map(function (p) { return p.temp; }).filter(function (v) { return v != null; });
  var tLo = temps.length ? Math.min.apply(null, temps) - 2 : 18;
  var tHi = temps.length ? Math.max.apply(null, temps) + 2 : 34;
  // Línea de la temperatura ideal según la edad que tenía la camada en cada momento
  var lineaIdeal = (function () {
    if (!puntos.length || !camada || !camada.fechaInicio) return "";
    var ini = new Date(camada.fechaInicio + "T00:00:00").getTime();
    return puntos.map(function (p, i) {
      var d = Math.max(0, Math.round((p.t - ini) / 86400000));
      var r = TEMP_IDEAL_SENSOR[Math.max(0, Math.min(d, 35))] || [20, 23];
      var ideal = (r[0] + r[1]) / 2;
      var px = puntos.length === 1 ? GW : (i / (puntos.length - 1)) * GW;
      var py = GH - ((ideal - tLo) / ((tHi - tLo) || 1)) * GH;
      return px.toFixed(1) + "," + Math.max(0, Math.min(GH, py)).toFixed(1);
    }).join(" ");
  })();

  var caja = { background: C.card, borderRadius: 16, border: "1px solid " + C.border, padding: 16, marginBottom: 14 };
  var titulo = { fontSize: 12, fontWeight: 800, color: C.textMuted, fontFamily: "'Space Mono', monospace", letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 10 };

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 55, background: C.bg, overflowY: "auto" }}>
      <div style={{ width: "100%", maxWidth: 480, margin: "0 auto", minHeight: "100vh", padding: "20px 16px 60px" }}>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, gap: 10 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 20, fontWeight: 800, color: C.accent }}>📈 Historial</span>
              {esDemo && <BadgeDemo />}
            </div>
            <div style={{ fontSize: 13, color: C.textMuted, marginTop: 2 }}>{(sensor && sensor.nombre) || "Galpón"}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "1px solid " + C.border, borderRadius: 8, color: C.textMuted, cursor: "pointer", padding: "7px 14px", fontSize: 15, flexShrink: 0 }}>✕</button>
        </div>

        {/* Selector de período */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, margin: "14px 0 16px" }}>
          {HIST_RANGOS.map(function (r) {
            var on = rango === r.id;
            return (
              <button key={r.id} onClick={function () { setRango(r.id); }}
                style={{ padding: "13px 6px", borderRadius: 12, border: "2px solid " + (on ? C.accent : C.border), background: on ? C.accent + "22" : "transparent", color: on ? C.accent : C.textMuted, fontSize: 13, fontWeight: on ? 900 : 700, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>
                {r.label}
              </button>
            );
          })}
        </div>

        {cargando ? (
          <div style={{ textAlign: "center", color: C.textFaint, padding: "50px 0", fontSize: 14 }}>Cargando…</div>
        ) : !puntos.length ? (
          <div style={{ ...caja, textAlign: "center", padding: "40px 20px" }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>📭</div>
            <div style={{ fontSize: 15, color: C.text, fontWeight: 700 }}>Todavía no hay lecturas para este período</div>
            <div style={{ fontSize: 13, color: C.textMuted, marginTop: 6, lineHeight: 1.5 }}>Cuando tu Pollo IA lleve un rato midiendo, aquí verás cómo estuvo el galpón.</div>
          </div>
        ) : (
          <div>
            {/* Resumen */}
            {resumen && (
              <div style={caja}>
                <div style={titulo}>Resumen del período</div>
                <div style={{ display: "flex", gap: 8 }}>
                  {[["MÍNIMA", resumen.min + "°", "#3B82F6"], ["PROMEDIO", resumen.prom + "°", C.text], ["MÁXIMA", resumen.max + "°", "#EF4444"]].map(function (x) {
                    return (
                      <div key={x[0]} style={{ flex: 1, background: C.surface, borderRadius: 10, padding: "10px 8px", textAlign: "center" }}>
                        <div style={{ fontSize: 9.5, color: C.textFaint, fontFamily: "'Space Mono', monospace" }}>{x[0]}</div>
                        <div style={{ fontSize: 19, fontWeight: 900, color: x[2] }}>{x[1]}</div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ marginTop: 10, fontSize: 13, color: resumen.horasFuera > 0 ? C.warn : C.green, fontWeight: 700 }}>
                  {resumen.horasFuera > 0
                    ? "⚠️ Estuvo " + resumen.horasFuera + " h fuera del rango ideal"
                    : "✅ Se mantuvo dentro del rango ideal"}
                </div>
              </div>
            )}

            {/* Temperatura */}
            <div style={caja}>
              <div style={titulo}>🌡️ Temperatura</div>
              <svg viewBox={"0 0 " + GW + " " + GH} width="100%" height="140" preserveAspectRatio="none" style={{ display: "block" }}>
                {lineaIdeal && <polyline points={lineaIdeal} fill="none" stroke={C.textFaint} strokeWidth="1.5" strokeDasharray="5 4" opacity="0.9" />}
                <polyline points={serieSVG("temp", tLo, tHi)} fill="none" stroke="#F97316" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
              </svg>
              <div style={{ display: "flex", gap: 14, marginTop: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11.5, color: C.textMuted, display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 14, height: 3, background: "#F97316", borderRadius: 2 }} /> Medida</span>
                <span style={{ fontSize: 11.5, color: C.textFaint, display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 14, borderTop: "2px dashed " + C.textFaint }} /> Ideal para su edad</span>
              </div>
            </div>

            {/* Humedad */}
            <div style={caja}>
              <div style={titulo}>💧 Humedad</div>
              <svg viewBox={"0 0 " + GW + " " + GH} width="100%" height="110" preserveAspectRatio="none" style={{ display: "block" }}>
                <polyline points={serieSVG("hum", 0, 100)} fill="none" stroke="#3B82F6" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
              </svg>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 10.5, color: C.textFaint, fontFamily: "'Space Mono', monospace" }}>
                <span>0%</span><span>ideal 50–70%</span><span>100%</span>
              </div>
            </div>

            {/* Amoniaco: franja de color, SIN números */}
            <div style={caja}>
              <div style={titulo}>🫧 Amoniaco</div>
              <div style={{ display: "flex", height: 26, borderRadius: 8, overflow: "hidden", border: "1px solid " + C.border }}>
                {puntos.map(function (p, i) {
                  var e = sensNh3Estado(p.nh3, p.nh3Techo);
                  return <div key={i} style={{ flex: 1, background: p.nh3 == null ? "#33415555" : e.color }} title={e.label} />;
                })}
              </div>
              <div style={{ display: "flex", gap: 12, marginTop: 8, flexWrap: "wrap" }}>
                {[["Bajo", SENS_VERDE], ["Subiendo", SENS_AMAR], ["Alto", SENS_ROJO], ["Sin calibrar", SENS_GRIS], ["Revisar sensor", SENS_REVISAR]].map(function (x) {
                  return <span key={x[0]} style={{ fontSize: 11.5, color: C.textMuted, display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: x[1] }} /> {x[0]}</span>;
                })}
              </div>
            </div>

            {/* Eje: en "toda la camada" se mide en días de edad, no en fechas */}
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10.5, color: C.textFaint, fontFamily: "'Space Mono', monospace", marginTop: -4, marginBottom: 14 }}>
              {rango === "camada" && camada && camada.fechaInicio ? (
                <>
                  <span>día 0</span>
                  <span>día {diaActual}</span>
                </>
              ) : (
                <>
                  <span>{cfg.label.toLowerCase()} atrás</span>
                  <span>ahora</span>
                </>
              )}
            </div>
          </div>
        )}

        <button onClick={onClose} style={{ width: "100%", background: "transparent", color: C.textMuted, border: "1px solid " + C.border, borderRadius: 14, padding: "13px", fontSize: 15, fontWeight: 700, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>Volver</button>
      </div>
    </div>
  );
}

// --- MI SENSOR (página del aparato ambiental) ---
function MiSensorPage({ C, session, sensores, camadasList, onClose, onSensoresChange, onSensorEliminado, onAbrirHistorial, onAbrirControl }) {
  var lista = Array.isArray(sensores) ? sensores : [];
  const [lecturas, setLecturas] = useState({});          // { sensor_id: [lecturas...] }
  const [nombreEdit, setNombreEdit] = useState({});      // { sensor_id: nombre } mientras se edita
  const [calSensor, setCalSensor] = useState(null);      // sensor en proceso de checklist de calibración
  const [checks, setChecks] = useState({ cama: false, ventilado: false, sindesinf: false });
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ codigo: "", nombre: "" });
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState("");
  const [addOk, setAddOk] = useState(false);              // vinculación exitosa → mensaje de confirmación
  const [vozPedida, setVozPedida] = useState({});         // { sensor_id: true } mientras se pide la voz
  const [porBorrar, setPorBorrar] = useState(null);       // sensor esperando confirmación de borrado
  const [borrando, setBorrando] = useState(false);

  var esPropietario = !session || session.rol === "propietario";
  // Controles de ESTA cuenta, agrupados por la Pollo Box que los instaló.
  const [controles, setControles] = useState([]);
  useEffect(function () {
    if (!session || !session.ownerEmail) return;
    SB.select("controles", "owner_email=eq." + encodeURIComponent(session.ownerEmail) + "&select=*&order=id.asc")
      .then(function (rows) { if (Array.isArray(rows)) setControles(rows); })
      .catch(function () {});
  }, [session && session.ownerEmail]);
  var controlesPorCaja = {};
  var idsCajas = {};
  lista.forEach(function (s) { idsCajas[String(s.id)] = true; });
  var huerfanos = [];
  controles.forEach(function (ct) {
    var k = ct.caja_id != null ? String(ct.caja_id) : "";
    if (!k || !idsCajas[k]) { huerfanos.push(ct); return; }   // sin Pollo Box o apunta a una que ya no está
    (controlesPorCaja[k] = controlesPorCaja[k] || []).push(ct);
  });
  // Camada ACTIVA que usa cada sensor (para bloquear la calibración "con pollos dentro").
  var camadaPorSensor = {};
  (camadasList || []).filter(function (c) { return c.activa !== false && c.sensorId; }).forEach(function (c) { camadaPorSensor[c.sensorId] = c; });

  useEffect(function () {
    lista.forEach(function (s) {
      SB.select("lecturas_sensor", columnaLectura() + "=eq." + s.id + "&select=temp_c,humedad,nh3_ratio,mq_raw,ts&order=ts.desc&limit=60")
        .then(function (rows) { if (Array.isArray(rows) && !rows.error) setLecturas(function (prev) { var n = Object.assign({}, prev); n[s.id] = rows; return n; }); })
        .catch(function () {});
    });
  }, [lista.length]);

  function guardarNombre(s) {
    var nuevo = (nombreEdit[s.id] || "").trim();
    if (!nuevo || nuevo === s.nombre) { setNombreEdit(function (p) { var n = Object.assign({}, p); delete n[s.id]; return n; }); return; }
    tablaAparatos()
      .then(function (tabla) { return SB.update(tabla, { id: s.id }, { nombre: nuevo }); })
      .then(function () { if (onSensoresChange) onSensoresChange(); }).catch(function () {});
    setNombreEdit(function (p) { var n = Object.assign({}, p); delete n[s.id]; return n; });
  }

  function pedirCalibracion() {
    if (!calSensor) return;
    tablaAparatos()
      .then(function (tabla) { return SB.update(tabla, { id: calSensor.id }, { solicitud_calibracion: true }); })
      .then(function () { if (onSensoresChange) onSensoresChange(); }).catch(function () {});
    setCalSensor(null);
    setChecks({ cama: false, ventilado: false, sindesinf: false });
  }

  function abrirAgregar() {
    setAddForm({ codigo: "", nombre: "" });
    setAddError(""); setAddOk(false); setAddOpen(true);
  }

  // Vincular por CÓDIGO: el WiFi ya se configuró en la pantallita de la caja, así que
  // aquí solo hace falta el código de 6 dígitos que ella muestra y el nombre del galpón.
  function vincularAparato() {
    setAddError("");
    var codigo = String(addForm.codigo || "").replace(/\D/g, "");
    var nombre = String(addForm.nombre || "").trim();
    if (codigo.length !== 6) { setAddError("Escribe los 6 números que aparecen en la pantalla de tu Pollo IA."); return; }
    if (!nombre) { setAddError("Ponle un nombre a este galpón para reconocerlo."); return; }
    setAddBusy(true);
    fetch("/api/pairing-claim", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ codigo: codigo, owner_email: session.ownerEmail, nombre: nombre }),
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        setAddBusy(false);
        if (data && data.ok) {
          setAddOk(true);
          if (onSensoresChange) onSensoresChange();
          setTimeout(function () {
            setAddOpen(false); setAddOk(false); setAddForm({ codigo: "", nombre: "" });
            if (onClose) onClose();          // vuelve al inicio, con la tarjeta Ambiente ya visible
          }, 1600);
          return;
        }
        // El servidor manda los mensajes ya redactados en español para el productor.
        setAddError((data && data.error) || "No encontramos ninguna caja con ese código. Revisa la pantalla de tu Pollo IA.");
      })
      .catch(function () {
        setAddBusy(false);
        setAddError("No hay conexión. Revisa tu internet e inténtalo de nuevo.");
      });
  }

  // "Probar voz": deja pedida la pista 1; el aparato la reproduce en su próximo reporte
  // (máximo 5 min). El endpoint sirve la orden UNA vez y limpia la columna.
  function probarVoz(s) {
    setVozPedida(function (p) { var n = Object.assign({}, p); n[s.id] = true; return n; });
    tablaAparatos()
      .then(function (tabla) { return SB.update(tabla, { id: s.id }, { solicitud_audio: 1 }); })
      .then(function (r) {
        if (r && r.error) {   // la columna aún no existe (falta correr supabase-sensor-r3.sql)
          setVozPedida(function (p) { var n = Object.assign({}, p); delete n[s.id]; return n; });
          alert("Aún no se puede probar la voz. Falta habilitarlo en la base de datos.");
          return;
        }
        if (onSensoresChange) onSensoresChange();
        setTimeout(function () {
          setVozPedida(function (p) { var n = Object.assign({}, p); delete n[s.id]; return n; });
        }, 6000);
      })
      .catch(function () {
        setVozPedida(function (p) { var n = Object.assign({}, p); delete n[s.id]; return n; });
      });
  }

  // DESVINCULAR una caja: deja de estar en esta cuenta y queda libre para volver a
  // vincularla (a esta misma cuenta o a otra) con el MISMO código de su pantalla,
  // que no cambia nunca. No se borra el aparato ni su historial.
  function desvincularSensor() {
    var s = porBorrar;
    if (!s || borrando) return;
    setBorrando(true);
    fetch("/api/pairing-release", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ deviceId: s.id, owner_email: session.ownerEmail }),
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        setBorrando(false);
        setPorBorrar(null);
        if (!data || !data.ok) {
          alert((data && data.error) || "No se pudo desvincular la caja. Inténtalo de nuevo.");
          return;
        }
        if (onSensorEliminado) onSensorEliminado(s.id);
        else if (onSensoresChange) onSensoresChange();
      })
      .catch(function () {
        setBorrando(false);
        setPorBorrar(null);
        alert("No hay conexión. Revisa tu internet e inténtalo de nuevo.");
      });
  }

  var checksOk = checks.cama && checks.ventilado && checks.sindesinf;

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 50, background: C.bg, overflowY: "auto" }}>
      <div style={{ width: "100%", maxWidth: 480, margin: "0 auto", minHeight: "100vh", background: C.bg, padding: "20px 16px 60px" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 9 }}><PolloBoxSVG size={40} /><span style={{ fontSize: 20, fontWeight: 800, color: C.accent }}>Mis Pollo Box</span></div>
            <div style={{ fontSize: 14, color: C.textMuted, marginTop: 2 }}>Aparatos de la granja · temperatura, humedad y amoniaco</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "1px solid " + C.border, borderRadius: 8, color: C.textMuted, cursor: "pointer", padding: "7px 14px", fontSize: 15, fontFamily: "'Syne', sans-serif" }}>✕</button>
        </div>
        <div style={{ height: 1, background: C.border, margin: "14px 0 18px" }} />

        {lista.length === 0 && (
          <div style={{ textAlign: "center", color: C.textFaint, fontSize: 14, padding: "30px 0" }}>Aún no tienes ningún aparato. Toca "➕ Agregar aparato" para conectar el primero.</div>
        )}

        {lista.map(function (s) {
          var ls = lecturas[s.id] || [];
          var ult = ls[0] || null;
          var minAgo = ult ? sensHaceMin(ult.ts) : sensHaceMin(s.ultimo_reporte);
          var conectado = minAgo != null && minAgo <= 15;
          var camActiva = camadaPorSensor[s.id];
          var nE = ult ? sensNh3Estado(ult.nh3_ratio, sensNh3EnTecho(ls)) : sensNh3Estado(null);
          var trend = sensNh3Tendencia(ls);
          // Semáforo de calibración: rojo=nunca · amarillo=de un ciclo anterior · verde=lista.
          var calEstado;
          if (!s.r0) calEstado = { color: SENS_ROJO, label: "Nunca calibrado" };
          else if (camActiva && s.fecha_calibracion && camActiva.fechaInicio && new Date(s.fecha_calibracion) < new Date(camActiva.fechaInicio)) calEstado = { color: SENS_AMAR, label: "Calibrado en un ciclo anterior" };
          else calEstado = { color: SENS_VERDE, label: "Calibrado y listo" };
          // (La vida útil del sensor de amoniaco NO se le muestra al usuario: esa información
          //  va en el manual impreso. La fecha de instalación queda en la base de datos para soporte.)
          var editando = nombreEdit[s.id] != null;

          return (
            <div key={s.id} style={{ background: C.card, borderRadius: 16, border: "1px solid " + C.border, padding: 16, marginBottom: 16 }}>

              {/* Nombre editable + estado de conexión */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 14 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {editando ? (
                    <div style={{ display: "flex", gap: 6 }}>
                      <input value={nombreEdit[s.id]} onChange={function (e) { setNombreEdit(function (p) { var n = Object.assign({}, p); n[s.id] = e.target.value; return n; }); }} autoFocus
                        style={{ flex: 1, background: C.surface, border: "1px solid " + C.border, borderRadius: 8, color: C.text, padding: "8px 10px", fontSize: 15, fontFamily: "'Syne', sans-serif", outline: "none", boxSizing: "border-box" }} />
                      <button onClick={function () { guardarNombre(s); }} style={{ background: C.green, color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", padding: "8px 12px", fontSize: 13, fontWeight: 700 }}>✓</button>
                    </div>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <PolloBoxSVG size={30} apagado={!conectado} />
                      <div style={{ fontSize: 17, fontWeight: 800, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.nombre || "Galpón"}</div>
                      <button onClick={function () { setNombreEdit(function (p) { var n = Object.assign({}, p); n[s.id] = s.nombre || ""; return n; }); }} title="Cambiar nombre"
                        style={{ background: "none", border: "none", color: C.textMuted, cursor: "pointer", fontSize: 14 }}>✏️</button>
                    </div>
                  )}
                  {camActiva && <div style={{ fontSize: 12, color: C.textFaint, marginTop: 2 }}>Lote: {camActiva.nombre}</div>}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: conectado ? SENS_VERDE : SENS_ROJO }} />
                  <span style={{ fontSize: 12, color: conectado ? SENS_VERDE : SENS_ROJO, fontWeight: 700, fontFamily: "'Space Mono', monospace" }}>{minAgo == null ? "sin señal" : conectado ? "en línea" : "sin señal"}</span>
                </div>
              </div>
              <div style={{ fontSize: 11, color: C.textFaint, marginTop: -8, marginBottom: 12, fontFamily: "'Space Mono', monospace" }}>
                {minAgo == null ? "Nunca ha reportado." : "Último reporte hace " + minAgo + " min."}
              </div>

              {/* Última lectura: temp / humedad / amoniaco */}
              <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                <div style={{ flex: 1, background: C.surface, borderRadius: 10, padding: "10px 12px", textAlign: "center" }}>
                  <div style={{ fontSize: 10, color: C.textFaint, fontFamily: "'Space Mono', monospace" }}>TEMP</div>
                  <div style={{ fontSize: 19, fontWeight: 900, color: C.text }}>{ult && ult.temp_c != null ? Number(ult.temp_c).toFixed(1) + "°" : "—"}</div>
                </div>
                <div style={{ flex: 1, background: C.surface, borderRadius: 10, padding: "10px 12px", textAlign: "center" }}>
                  <div style={{ fontSize: 10, color: C.textFaint, fontFamily: "'Space Mono', monospace" }}>HUMEDAD</div>
                  <div style={{ fontSize: 19, fontWeight: 900, color: C.text }}>{ult && ult.humedad != null ? Number(ult.humedad).toFixed(0) + "%" : "—"}</div>
                </div>
                <div style={{ flex: 1, background: C.surface, borderRadius: 10, padding: "10px 12px", textAlign: "center" }}>
                  <div style={{ fontSize: 10, color: C.textFaint, fontFamily: "'Space Mono', monospace" }}>AMONIACO</div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, marginTop: 3 }}>
                    <span style={{ width: 9, height: 9, borderRadius: "50%", background: nE.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 13, fontWeight: 800, color: C.text }}>{nE.label}</span>
                    <span style={{ fontSize: 13, color: C.textMuted }}>{trend}</span>
                  </div>
                  {ult && ult.mq_raw != null && <div style={{ fontSize: 9.5, color: C.textFaint, fontFamily: "'Space Mono', monospace", marginTop: 2 }}>ADC {ult.mq_raw}</div>}
                </div>
              </div>

              {/* Estado de calibración */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, background: calEstado.color + "18", border: "1px solid " + calEstado.color + "55", borderRadius: 10, padding: "9px 12px", marginBottom: 10 }}>
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: calEstado.color, flexShrink: 0 }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: calEstado.color }}>Calibración: {calEstado.label}</span>
              </div>

              {/* Botón calibrar */}
              {camActiva ? (
                <button disabled style={{ width: "100%", background: C.surface, color: C.textFaint, border: "1px solid " + C.border, borderRadius: 12, padding: "13px", fontSize: 14, fontWeight: 700, fontFamily: "'Syne', sans-serif", cursor: "default" }}>
                  🔒 No se calibra con pollos dentro
                </button>
              ) : s.solicitud_calibracion ? (
                <button disabled style={{ width: "100%", background: C.accent + "22", color: C.accent, border: "1px solid " + C.accent + "55", borderRadius: 12, padding: "13px", fontSize: 14, fontWeight: 700, fontFamily: "'Syne', sans-serif", cursor: "default" }}>
                  ⏳ Calibración solicitada — el aparato la hará pronto
                </button>
              ) : (
                <button onClick={function () { setChecks({ cama: false, ventilado: false, sindesinf: false }); setCalSensor(s); }}
                  style={{ width: "100%", background: C.accent, color: "#1a0a00", border: "none", borderRadius: 12, padding: "13px", fontSize: 14, fontWeight: 800, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>
                  🎯 Calibrar ahora
                </button>
              )}

              {/* CONTROLES DE ESTE GALPÓN — viven dentro de su Pollo Box (se venden como kit) */}
              <div style={{ marginTop: 14, borderTop: "1px solid " + C.border, paddingTop: 12 }}>
                <div style={{ fontSize: 11, color: C.textMuted, fontFamily: "'Space Mono', monospace", letterSpacing: 1.2, marginBottom: 8 }}>
                  🎛️ CONTROLES DE ESTE GALPÓN
                </div>
                {(controlesPorCaja[s.id] || []).map(function (ct) {
                  var ri = rolInfo(ct.rol);
                  var min = ct.ultima_conexion ? Math.floor((Date.now() - new Date(ct.ultima_conexion).getTime()) / 60000) : null;
                  var vivo = min != null && min <= 15;
                  var txt = !vivo ? "sin señal" : (ct.modo || "auto") !== "auto" ? (ct.modo === "on" ? "forzado encendido" : "forzado apagado") : (ct.encendido ? "encendida" : "apagada");
                  var col = !vivo ? SENS_GRIS : ct.encendido ? SENS_VERDE : C.textMuted;
                  return (
                    <button key={ct.id} onClick={function () { if (onAbrirControl) onAbrirControl(ct); }}
                      style={{ width: "100%", textAlign: "left", background: C.surface, border: "1px solid " + C.border, borderRadius: 12, padding: "10px 12px", marginBottom: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 20, flexShrink: 0, opacity: vivo ? 1 : 0.45 }}>{ri.emoji}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 800, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ct.nombre || ri.label}</div>
                        <div style={{ fontSize: 11, color: col, fontWeight: 700 }}>{txt}</div>
                      </div>
                      <span style={{ fontSize: 17, color: C.textFaint, flexShrink: 0 }}>›</span>
                    </button>
                  );
                })}
                {!(controlesPorCaja[s.id] || []).length && (
                  <div style={{ fontSize: 12.5, color: C.textFaint, lineHeight: 1.55, marginBottom: 8 }}>
                    Todavía no hay controles en este galpón.
                  </div>
                )}
                <div style={{ fontSize: 11.5, color: C.textFaint, lineHeight: 1.55, background: C.bg, borderRadius: 10, padding: "9px 11px" }}>
                  ➕ Para agregar uno: dale corriente al control cerca de este Pollo Box. Se instala solo, sin escribir ningún código.
                </div>
              </div>

              {/* Historial del ambiente de este aparato */}
              {onAbrirHistorial && (
                <button onClick={function () { onAbrirHistorial(s); }}
                  style={{ width: "100%", marginTop: 8, background: "transparent", color: C.text, border: "1px solid " + C.border, borderRadius: 12, padding: "11px", fontSize: 13.5, fontWeight: 700, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>
                  📈 Ver historial del ambiente
                </button>
              )}

              {/* Probar voz: hace hablar al aparato para confirmar que su bocina funciona */}
              <button onClick={function () { probarVoz(s); }} disabled={!!vozPedida[s.id]}
                style={{ width: "100%", marginTop: 8, background: "transparent", color: vozPedida[s.id] ? C.textFaint : C.text, border: "1px solid " + C.border, borderRadius: 12, padding: "11px", fontSize: 13.5, fontWeight: 700, fontFamily: "'Syne', sans-serif", cursor: vozPedida[s.id] ? "default" : "pointer" }}>
                {vozPedida[s.id] ? "🔊 Pedido — el aparato hablará en unos minutos" : "🔊 Probar voz"}
              </button>

              {/* Desvincular esta caja de la cuenta (solo el dueño) */}
              {esPropietario && (
                <button onClick={function () { setPorBorrar(s); }}
                  style={{ width: "100%", marginTop: 8, background: "transparent", color: C.danger, border: "1px solid " + C.danger + "66", borderRadius: 12, padding: "11px", fontSize: 13, fontWeight: 700, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>
                  🔓 Desvincular caja
                </button>
              )}
            </div>
          );
        })}

        {/* R7-7 · Controles que quedaron sin Pollo Box: se muestran, nunca se ocultan ni se borran */}
        {huerfanos.length > 0 && (
          <div style={{ background: C.card, borderRadius: 16, border: "1px solid " + C.warn + "55", padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: C.warn, marginBottom: 4 }}>⚠️ Controles sin Pollo Box</div>
            <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 }}>
              Estos controles son tuyos pero no están asignados a ningún Pollo Box. Ábrelos para asignarles uno.
            </div>
            {huerfanos.map(function (ct) {
              var ri = rolInfo(ct.rol);
              return (
                <button key={ct.id} onClick={function () { if (onAbrirControl) onAbrirControl(ct); }}
                  style={{ width: "100%", textAlign: "left", background: C.surface, border: "1px solid " + C.border, borderRadius: 12, padding: "10px 12px", marginBottom: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 20, flexShrink: 0 }}>{ri.emoji}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>{ct.nombre || ri.label}</div>
                    <div style={{ fontSize: 11, color: C.warn }}>sin Pollo Box asignado</div>
                  </div>
                  <span style={{ fontSize: 17, color: C.textFaint, flexShrink: 0 }}>›</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Agregar aparato */}
        <button onClick={abrirAgregar}
          style={{ width: "100%", background: "transparent", border: "2px dashed " + C.accent + "66", borderRadius: 14, color: C.accent, padding: "14px", fontSize: 15, fontWeight: 800, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>
          ➕ Agregar aparato
        </button>
      </div>

      {/* ── Checklist de calibración (3 confirmaciones) ── */}
      {calSensor && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 60, background: "#000c", display: "flex", alignItems: "flex-end" }} onClick={function () { setCalSensor(null); }}>
          <div onClick={function (e) { e.stopPropagation(); }} style={{ width: "100%", maxWidth: 480, margin: "0 auto", background: C.surface, borderRadius: "18px 18px 0 0", padding: "22px 18px 36px", border: "1px solid " + C.border }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.accent, marginBottom: 6 }}>🎯 Calibrar {calSensor.nombre || "sensor"}</div>
            <div style={{ fontSize: 13, color: C.textMuted, marginBottom: 16 }}>La calibración toma el aire limpio como referencia. Confirma que el galpón está listo:</div>
            {[
              { k: "cama", l: "🧹 Retiré la cama vieja del ciclo anterior" },
              { k: "ventilado", l: "🌬️ El galpón lleva 24 h ventilado" },
              { k: "sindesinf", l: "🧴 No desinfecté ni fumigué en las últimas 24 h" },
            ].map(function (item) {
              var on = checks[item.k];
              return (
                <div key={item.k} onClick={function () { setChecks(function (p) { var n = Object.assign({}, p); n[item.k] = !n[item.k]; return n; }); }}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 10px", marginBottom: 8, borderRadius: 10, border: "1.5px solid " + (on ? C.green : C.border), background: on ? C.green + "18" : "transparent", cursor: "pointer" }}>
                  <span style={{ width: 22, height: 22, borderRadius: 6, border: "2px solid " + (on ? C.green : C.border), background: on ? C.green : "transparent", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>{on ? "✓" : ""}</span>
                  <span style={{ fontSize: 14, color: on ? C.text : C.textMuted }}>{item.l}</span>
                </div>
              );
            })}
            <button onClick={pedirCalibracion} disabled={!checksOk}
              style={{ width: "100%", marginTop: 10, background: checksOk ? C.accent : C.border, color: checksOk ? "#1a0a00" : C.textFaint, border: "none", borderRadius: 12, padding: "14px", fontSize: 15, fontWeight: 900, fontFamily: "'Syne', sans-serif", cursor: checksOk ? "pointer" : "default" }}>
              Solicitar calibración
            </button>
            <button onClick={function () { setCalSensor(null); }} style={{ width: "100%", marginTop: 8, background: "none", color: C.textMuted, border: "1px solid " + C.border, borderRadius: 12, padding: "12px", fontSize: 14, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>Cancelar</button>
          </div>
        </div>
      )}

      {/* ── Confirmar desvinculación de una caja ── */}
      {porBorrar && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 65, background: "#000c", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
          onClick={function () { if (!borrando) setPorBorrar(null); }}>
          <div onClick={function (e) { e.stopPropagation(); }} style={{ width: "100%", maxWidth: 400, background: C.surface, borderRadius: 16, padding: "22px 20px", border: "1.5px solid " + C.danger }}>
            <div style={{ fontSize: 38, textAlign: "center", marginBottom: 8 }}>🔓</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: C.danger, textAlign: "center", marginBottom: 12 }}>
              ¿Desvincular "{porBorrar.nombre || "esta caja"}"?
            </div>
            <div style={{ fontSize: 13.5, color: C.textMuted, lineHeight: 1.6, marginBottom: 18 }}>
              Saldrá de tu cuenta y la camada que la esté usando se quedará sin sensor.
              <br /><br />
              Podrás volver a vincularla cuando quieras con el mismo código de su pantalla{porBorrar.codigo_vinculo ? " (" + porBorrar.codigo_vinculo + ")" : ""}.
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={function () { if (!borrando) setPorBorrar(null); }} disabled={borrando}
                style={{ flex: 1, background: C.card, color: C.text, border: "1px solid " + C.border, borderRadius: 12, padding: "13px", fontSize: 14.5, fontWeight: 700, fontFamily: "'Syne', sans-serif", cursor: borrando ? "default" : "pointer" }}>
                Cancelar
              </button>
              <button onClick={desvincularSensor} disabled={borrando}
                style={{ flex: 1, background: C.danger, color: "#fff", border: "none", borderRadius: 12, padding: "13px", fontSize: 14.5, fontWeight: 800, fontFamily: "'Syne', sans-serif", cursor: borrando ? "default" : "pointer", opacity: borrando ? 0.6 : 1 }}>
                {borrando ? "Desvinculando…" : "Sí, desvincular"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Agregar sensor: se vincula con el CÓDIGO que muestra la pantalla de la caja ── */}
      {addOpen && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 60, background: "#000c", display: "flex", alignItems: "flex-end" }} onClick={function () { if (!addBusy && !addOk) setAddOpen(false); }}>
          <div onClick={function (e) { e.stopPropagation(); }} style={{ width: "100%", maxWidth: 480, margin: "0 auto", background: C.surface, borderRadius: "18px 18px 0 0", padding: "22px 18px 36px", border: "1px solid " + C.border, maxHeight: "92vh", overflowY: "auto" }}>

            {addOk ? (
              /* ── Confirmación ── */
              <div style={{ textAlign: "center", padding: "18px 6px 8px" }}>
                <div style={{ fontSize: 46, marginBottom: 10 }}>✅</div>
                <div style={{ fontSize: 19, fontWeight: 900, color: C.green, marginBottom: 8 }}>¡Sensor vinculado!</div>
                <div style={{ fontSize: 14, color: C.textMuted, lineHeight: 1.6 }}>
                  Ya puedes ver la temperatura, la humedad y el amoniaco de tu galpón en la pantalla de inicio.
                </div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: C.accent, marginBottom: 10 }}>➕ Agregar sensor</div>

                {/* Instrucción corta */}
                <div style={{ background: C.card, border: "1px solid " + C.border, borderRadius: 12, padding: "12px 14px", marginBottom: 18, fontSize: 13.5, color: C.textMuted, lineHeight: 1.6 }}>
                  Enchufa tu Pollo IA y conéctala al WiFi desde su pantalla. Cuando muestre un código, escríbelo aquí.
                </div>

                {/* Código de 6 dígitos — grande y espaciado, teclado numérico */}
                <div style={{ fontSize: 12, color: C.textMuted, fontFamily: "'Space Mono', monospace", marginBottom: 6, letterSpacing: 1 }}>CÓDIGO DE 6 NÚMEROS</div>
                <input
                  value={(function () { var c = addForm.codigo || ""; return c.length > 3 ? c.slice(0, 3) + " " + c.slice(3) : c; })()}
                  onChange={function (e) { setAddForm(Object.assign({}, addForm, { codigo: String(e.target.value).replace(/\D/g, "").slice(0, 6) })); setAddError(""); }}
                  type="text" inputMode="numeric" pattern="[0-9]*" placeholder="482 916" autoFocus
                  style={{ width: "100%", background: C.bg, border: "2px solid " + ((addForm.codigo || "").length === 6 ? C.accent : C.border), borderRadius: 12, color: C.text, padding: "16px 12px", fontSize: 34, fontWeight: 900, fontFamily: "'Space Mono', monospace", letterSpacing: 8, textAlign: "center", outline: "none", boxSizing: "border-box", marginBottom: 16 }} />

                {/* Nombre del galpón */}
                <div style={{ fontSize: 12, color: C.textMuted, fontFamily: "'Space Mono', monospace", marginBottom: 6, letterSpacing: 1 }}>¿CÓMO SE LLAMA ESTE GALPÓN?</div>
                <input value={addForm.nombre}
                  onChange={function (e) { setAddForm(Object.assign({}, addForm, { nombre: e.target.value })); setAddError(""); }}
                  placeholder="Ej: Galpón B3"
                  style={{ width: "100%", background: C.bg, border: "1.5px solid " + C.border, borderRadius: 10, color: C.text, padding: "12px 13px", fontSize: 15, fontFamily: "'Syne', sans-serif", outline: "none", boxSizing: "border-box", marginBottom: 16 }} />

                {addError && (
                  <div style={{ background: C.danger + "18", border: "1px solid " + C.danger + "55", borderRadius: 10, padding: "10px 12px", fontSize: 13, color: C.danger, marginBottom: 12, lineHeight: 1.5 }}>
                    {addError}
                  </div>
                )}

                <button onClick={vincularAparato} disabled={addBusy}
                  style={{ width: "100%", background: addBusy ? C.border : C.accent, color: addBusy ? C.textFaint : "#1a0a00", border: "none", borderRadius: 12, padding: "15px", fontSize: 16, fontWeight: 900, fontFamily: "'Syne', sans-serif", cursor: addBusy ? "default" : "pointer", marginBottom: 8 }}>
                  {addBusy ? "Vinculando…" : "VINCULAR"}
                </button>
                <button onClick={function () { if (!addBusy) setAddOpen(false); }} style={{ width: "100%", background: "none", color: C.textMuted, border: "1px solid " + C.border, borderRadius: 12, padding: "12px", fontSize: 14, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>Cancelar</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// --- ALIMENTACIÓN MODAL ---
function AlimentacionModal({ camada, C, fmtNum, onClose, t, onSaveEstandar, fcrReal, fcrMin, fcrMax, modo, onCambiarModo, fasesPersonal, onGuardarFase }) {
  if (!t) t = function(k) { var es = {"diasFaseIniciador":"Días 1-10","diasFaseCrecimiento":"Días 11-24","diasFaseEngorde":"Días 25-35","sacosTotales":"SACOS TOTALES","librasTotalesAlim":"LIBRAS TOTALES","kilogramosAlim":"KILOGRAMOS","pollosAlim":"POLLOS","lbPorPollo":"LB/POLLO","kgPorPollo":"KG/POLLO","estandar":"Estándar","sacosde100lb":"Sacos de 100lb","referencia":"Referencia","unSaco":"Un saco = 100 lb = 45.4 kg","iniciadorDesc":"Iniciador (D1-10): polvo fino","crecimientoDesc":"Crecimiento (D11-24): pellet mediano","engordeDes":"Engorde (D25-35): pellet grueso"}; return es[k] || k; };
  // --- Reactive lb standard ---
  const [lbStd, setLbStd] = useState(parseFloat(camada.lbPorPolloEstandar || 7.5));
  // Edición de una fase: qué fase se está editando y lo que el granjero va escribiendo.
  const [faseEditando, setFaseEditando] = useState(null);
  const [borrador, setBorrador] = useState({ d1: "", d2: "", sacos: "" });
  const [avisoFase, setAvisoFase] = useState("");

  // Los vivos actuales SOLO se usan como dato informativo en el encabezado.
  // El plan de alimento NO depende de ellos (ver más abajo).
  var registros = camada.registros || [];
  var vivosActuales = Math.max(0, (camada.totalPollos || 800)
    - registros.reduce(function(a, r) { return a + Number(r.mortalidad || 0); }, 0)
    - (camada.ventas || []).reduce(function(a, v) { return a + Number(v.pollos || 0); }, 0));
  var totalPollos = camada.totalPollos || 800;

  // --- Días restantes del ciclo ---
  var diaHoy = camada.fechaInicio ? (function() {
    var hoy = new Date(); var inicio = new Date(camada.fechaInicio);
    var hoyF = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
    var inicioF = new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate());
    return Math.max(0, Math.round((hoyF - inicioF) / 86400000));
  })() : 0;
  var diasRestantes = Math.max(0, 35 - diaHoy);

  // --- PLAN DE ALIMENTO FIJO (no cambia en toda la camada) ---
  // REGLA: el plan se calcula SIEMPRE sobre los POLLOS RECIBIDOS (camada.totalPollos),
  // nunca sobre los vivos actuales. Antes usaba `vivosActuales`, así que cada muerte o
  // cada venta bajaba los sacos necesarios y el plan de compra iba cambiando solo — no
  // sirve para planificar. Ahora el número que ves el día 1 es el mismo del día 35.
  // El reparto por fase sale de la tabla oficial de la raza de esta camada (ver planAlimento).
  var plan = planAlimento(totalPollos, lbStd, camada.raza, modo, fasesPersonal);
  var faseHoy = faseDelDia(modo, diaHoy, { totalPollos: totalPollos, raza: camada.raza, personal: fasesPersonal });
  var fases = plan.fases.map(function (fa) {
    return Object.assign({}, fa, { sacosFijo: fa.sacos, done: diaHoy > fa.d2 });
  });

  // El total DEBE cuadrar con el desglose por fase: es la suma exacta de las fases
  // (plan del lote completo, basado en pollos recibidos).
  var totalSacosReal = fases.reduce(function(a, f) { return a + (f.sacosFijo || 0); }, 0);
  var totalLbReal = totalSacosReal * 100;
  var totalKgReal = (totalLbReal / 2.205).toFixed(1);
  var kgPorPollo = (lbStd / 2.205).toFixed(2);
  var lbPorPollo = lbStd.toFixed(2);

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 50, background: C.bg, overflowY: "auto" }}>
      <div style={{ width: "100%", maxWidth: 480, margin: "0 auto", minHeight: "100vh", background: C.bg, padding: "20px 16px 60px" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.accent }}>🌽 {t("alimentacionMenu")}</div>
            <div style={{ fontSize: 14, color: C.textMuted, marginTop: 2 }}>{camada.nombre} · <span style={{ color: C.accent, fontWeight: 700 }}>{fmtNum(vivosActuales)} {t("vivosActuales") || "vivos"}</span> de {fmtNum(totalPollos)} · {t("diaActual") || "Día"} {diaHoy} · {diasRestantes}d {t("diasRestantes") || "restantes"}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "1px solid " + C.border, borderRadius: 8, color: C.textMuted, cursor: "pointer", padding: "7px 14px", fontSize: 15, fontFamily: "'Syne', sans-serif" }}>✕</button>
        </div>

        <div style={{ height: 1, background: C.border, margin: "14px 0 18px" }} />

        {/* Resumen total */}
        <div style={{ background: C.card, borderRadius: 14, border: "1px solid " + C.accent + "44", padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 14, color: C.accent, fontFamily: "'Space Mono', monospace", letterSpacing: 1, marginBottom: 12 }}>{t("resumenTotalAlimento")}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {[
              [t("sacosTotales"), totalSacosReal + " " + t("sacos"), C.accent],
              [t("librasTotalesAlim"), fmtNum(totalLbReal) + " lb", C.text],
              [t("kilogramosAlim"), fmtNum(Number(totalKgReal)) + " kg", C.text],
              [t("pollosAlim"), fmtNum(totalPollos) + " " + t("aves") + " recibidas", C.textMuted],
              [t("lbPorPollo"), lbPorPollo + " lb", C.textMuted],
              [t("kgPorPollo"), kgPorPollo + " kg", C.textMuted],
            ].map(function(r) {
              return (
                <div key={r[0]} style={{ background: C.surface, borderRadius: 10, padding: "10px 12px" }}>
                  <div style={{ fontSize: 15, color: C.textFaint, fontFamily: "'Space Mono', monospace", marginBottom: 3 }}>{r[0]}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, fontFamily: "'Space Mono', monospace", color: r[2] }}>{r[1]}</div>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 12, fontSize: 15, color: C.textFaint, fontFamily: "'Space Mono', monospace", borderTop: "1px solid " + C.border, paddingTop: 10 }}>
            {t("estandar")}: {lbStd} lb · {t("sacosde100lb")}
            <div style={{ marginTop: 6, color: C.textMuted, fontFamily: "'Syne', sans-serif", fontSize: 12.5, lineHeight: 1.5 }}>
              📌 Plan fijo del lote: se calcula sobre los <b style={{ color: C.text }}>{fmtNum(totalPollos)} pollos recibidos</b> y no cambia
              aunque haya mortalidad o ventas. Así el estimado de sacos es el mismo del día 1 al día 35.
            </div>
          </div>
        </div>

        {/* Switch 3 / 4 fases — vale para toda la granja */}
        {onCambiarModo && (
          <div style={{ background: C.card, borderRadius: 14, border: "1px solid " + C.border, padding: "12px 14px", marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: C.text, marginBottom: 2 }}>{tr("¿Con cuántas fases crías?")}</div>
            <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 10 }}>{tr("Vale para toda la granja y para la caja Pollo Box.")}</div>
            <div style={{ display: "flex", gap: 8 }}>
              {[3, 4].map(function (op) {
                var act = modoFases(modo) === op;
                return (
                  <button key={op} onClick={function () { if (!act) onCambiarModo(op); }}
                    style={{
                      flex: 1, padding: "10px 8px", borderRadius: 10, cursor: "pointer",
                      border: "2px solid " + (act ? C.accent : C.border),
                      background: act ? C.accent + "22" : C.surface,
                      color: act ? C.accent : C.textMuted, fontFamily: "'Syne', sans-serif",
                      fontSize: 14, fontWeight: act ? 800 : 500,
                    }}>
                    {op === 3 ? tr("3 fases") : tr("4 fases")}
                    <div style={{ fontSize: 11, fontWeight: 400, marginTop: 3, color: act ? C.accent : C.textFaint }}>
                      {op === 3 ? tr("Iniciador · Crecimiento · Engorde") : tr("Con preinicio los 3 primeros días")}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Barra de fases */}
        <div style={{ height: 22, borderRadius: 12, overflow: "hidden", border: "1.5px solid #ffffff22", marginBottom: 6, background: "#111", display: "flex" }}>
          {fases.map(function (fa, i) {
            return <div key={fa.key} style={{ width: (fa.frac * 100) + "%", background: "linear-gradient(to right, " + fa.color + ", " + fa.color + "aa)", borderRight: i < fases.length - 1 ? "2px solid #111" : "none" }} />;
          })}
        </div>
        <div style={{ display: "flex", marginBottom: 20 }}>
          {fases.map(function (fa) {
            return <div key={fa.key} style={{ width: (fa.frac * 100) + "%", textAlign: "center", fontSize: 15, color: fa.color, fontWeight: 700 }}>{fa.pct}%</div>;
          })}
        </div>

        {/* Cards por fase */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {fases.map(function(f) {
            var enEdicion = faseEditando === f.key;
            return (
              <div key={f.key} style={{ background: f.bg, borderRadius: 14, border: "1.5px solid " + f.color + (enEdicion ? "" : "55"), padding: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 28 }}>{f.emoji}</span>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: f.color }}>{tr(f.nombre)}{f.editado ? " ✏️" : ""}</div>
                      <div style={{ fontSize: 14, color: C.textMuted }}>{f.diasStr} · {f.pct}% {t("delTotal")}{f.key === faseHoy.key ? " · " + tr("AHORA") : ""}</div>
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 32, fontWeight: 900, fontFamily: "'Space Mono', monospace", color: f.color, lineHeight: 1 }}>{f.sacosFijo}</div>
                    <div style={{ fontSize: 14, color: C.textMuted }}>{t("sacos")}</div>
                  </div>
                </div>

                {/* Editar esta fase: desde qué día, hasta qué día y cuántos sacos */}
                {!enEdicion && (
                  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
                    <button onClick={function () { setFaseEditando(f.key); setBorrador({ d1: String(f.d1), d2: String(f.d2), sacos: String(f.sacosFijo) }); }}
                      style={{ background: "rgba(0,0,0,0.25)", border: "1px solid " + f.color + "55", borderRadius: 8, color: f.color, cursor: "pointer", padding: "6px 10px", fontSize: 12, fontWeight: 800, fontFamily: "'Syne', sans-serif", whiteSpace: "nowrap", lineHeight: 1 }}>
                      {tr("Editar")}
                    </button>
                  </div>
                )}
                {enEdicion && (
                  <div style={{ marginBottom: 10, background: "rgba(0,0,0,0.3)", border: "1px solid " + f.color + "88", borderRadius: 10, padding: 12 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                      {[["d1", tr("Desde el día")], ["d2", tr("Hasta el día")], ["sacos", tr("Sacos")]].map(function (campo) {
                        return (
                          <label key={campo[0]} style={{ display: "block" }}>
                            <span style={{ display: "block", fontSize: 12.5, color: C.textFaint, marginBottom: 4 }}>{campo[1]}</span>
                            <input type="number" inputMode="numeric" min="0" value={borrador[campo[0]]}
                              onChange={function (e) { var v = e.target.value; setBorrador(function (b) { var o = Object.assign({}, b); o[campo[0]] = v; return o; }); }}
                              style={{ width: "100%", boxSizing: "border-box", background: C.surface, border: "1.5px solid " + C.border, borderRadius: 8, color: C.text, padding: "9px 8px", fontSize: 17, fontWeight: 800, textAlign: "center", fontFamily: "'Space Mono', monospace", outline: "none" }} />
                          </label>
                        );
                      })}
                    </div>
                    {avisoFase && <div style={{ fontSize: 13, color: C.danger, marginTop: 8 }}>{avisoFase}</div>}
                    <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                      <button onClick={function () {
                        var d1 = Number(borrador.d1), d2 = Number(borrador.d2), sc = Number(borrador.sacos);
                        if (!(d1 >= 0) || !(d2 >= d1)) { setAvisoFase(tr("El día de fin debe ser igual o mayor que el de inicio.")); return; }
                        if (!(sc >= 0) || sc > 9999) { setAvisoFase(tr("Escribe una cantidad de sacos válida.")); return; }
                        setAvisoFase(""); setFaseEditando(null);
                        if (onGuardarFase) onGuardarFase(f.key, { d1: d1, d2: d2, sacos: sc });
                      }} style={{ flex: 2, background: f.color, color: "#1a0a00", border: "none", borderRadius: 9, padding: "11px", fontSize: 15, fontWeight: 800, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>✅ {tr("Guardar")}</button>
                      <button onClick={function () { setAvisoFase(""); setFaseEditando(null); if (onGuardarFase) onGuardarFase(f.key, null); }}
                        style={{ flex: 1, background: "none", color: C.textMuted, border: "1px solid " + C.border, borderRadius: 9, padding: "11px", fontSize: 13.5, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>{tr("Restaurar")}</button>
                      <button onClick={function () { setAvisoFase(""); setFaseEditando(null); }}
                        style={{ flex: 1, background: "none", color: C.textMuted, border: "1px solid " + C.border, borderRadius: 9, padding: "11px", fontSize: 13.5, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>{tr("Cancelar")}</button>
                    </div>
                  </div>
                )}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                  {(function() {
                    var lbFaseFijo = f.sacosFijo * 100;
                    var kgFaseFijo = (lbFaseFijo / 2.205).toFixed(1);
                    return [
                      [t("sacos100lb"), f.sacosFijo + " " + t("sacos")],
                      [t("librasTotales"), fmtNum(lbFaseFijo) + " lb"],
                      [t("kilos"), fmtNum(Number(kgFaseFijo)) + " kg"],
                    ];
                  })().map(function(r) {
                    return (
                      <div key={r[0]} style={{ background: "rgba(0,0,0,0.3)", borderRadius: 8, padding: "8px 10px", textAlign: "center" }}>
                        <div style={{ fontSize: 15, color: C.textFaint, marginBottom: 3 }}>{r[0]}</div>
                        <div style={{ fontSize: 15, fontWeight: 700, fontFamily: "'Space Mono', monospace", color: f.color }}>{r[1]}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {(function() {
          var fcr = (fcrReal != null && fcrReal !== "" && !isNaN(Number(fcrReal))) ? Number(fcrReal) : null;
          var col = fcr == null ? C.textMuted : (fcr <= (fcrMin || 1.6) ? C.green : fcr <= (fcrMax || 1.8) ? C.accent : C.danger);
          var etiqueta = fcr == null ? "Sin datos suficientes" : (fcr <= (fcrMin || 1.6) ? "Excelente" : fcr <= (fcrMax || 1.8) ? "Bueno" : "Alto (revisar)");
          return (
            <div style={{ marginTop: 20, background: C.card, borderRadius: 12, padding: "14px 16px", border: "1px solid " + C.border }}>
              <div style={{ fontSize: 14, color: C.accent, fontWeight: 700, marginBottom: 8 }}>📌 Conversión alimenticia (FCR)</div>
              {/* FCR actual */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: col + "18", border: "1px solid " + col + "55", borderRadius: 10, padding: "12px 14px", marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 12, color: C.textFaint, fontFamily: "'Space Mono', monospace" }}>FCR ACTUAL</div>
                  <div style={{ fontSize: 13, color: col, fontWeight: 700 }}>{etiqueta}</div>
                </div>
                <div style={{ fontSize: 32, fontWeight: 900, color: col, fontFamily: "'Space Mono', monospace" }}>{fcr != null ? fcr.toFixed(2) : "—"}</div>
              </div>
              {/* Cómo se calcula */}
              <div style={{ fontSize: 13, color: C.textMuted, lineHeight: 1.7 }}>
                El <b style={{ color: C.text }}>FCR</b> (conversión alimenticia) mide cuántas libras de alimento se necesitan para producir 1 libra de pollo.<br />
                <span style={{ fontFamily: "'Space Mono', monospace", color: C.text, fontSize: 12 }}>FCR = libras de alimento ÷ libras de carne producida</span><br />
                <span style={{ color: C.textFaint, fontSize: 12 }}>(carne producida = peso promedio × pollos vivos + libras ya vendidas)</span><br />
                <b style={{ color: C.green }}>Más bajo es mejor.</b> Meta: <b style={{ color: C.text }}>{(fcrMin || 1.6).toFixed(2)}–{(fcrMax || 1.8).toFixed(2)}</b>. Por encima = el lote está comiendo de más para el peso que tiene.
              </div>
            </div>
          );
        })()}

        {/* Panel editable — reactivo */}
        <div style={{ marginTop: 16, background: C.card, borderRadius: 12, padding: "14px 16px", border: "1px solid " + C.warn + "44" }}>
          <div style={{ fontSize: 14, color: C.warn, fontWeight: 700, marginBottom: 6 }}>⚙️ {t("ajustarEstandar")}</div>
          <div style={{ fontSize: 13, color: C.textMuted, marginBottom: 6 }}>
            {t("ajustarEstandarDesc")}
          </div>
          <div style={{ fontSize: 12, color: C.green, fontWeight: 700, marginBottom: 10 }}>
            ✓ {t("estandarRecomendado")}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <input
              type="number" min="5" max="15" step="0.1"
              value={lbStd}
              onChange={function(e) {
                var val = parseFloat(e.target.value);
                if (!isNaN(val) && val >= 1 && val <= 20) {
                  setLbStd(val);
                  if (onSaveEstandar) onSaveEstandar(val);
                }
              }}
              style={{ flex: 1, background: C.surface, border: "1.5px solid " + C.warn + "88", borderRadius: 8, color: C.text, padding: "10px 13px", fontSize: 18, fontFamily: "'Space Mono', monospace", outline: "none", fontWeight: 800 }}
            />
            <div style={{ fontSize: 15, color: C.textMuted }}>{t("lbPorAve")}</div>
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[7.5, 8.0, 8.5, 9.0, 9.5, 10.0].map(function(v) {
              return (
                <button key={v} onClick={function() {
                  setLbStd(v);
                  if (onSaveEstandar) onSaveEstandar(v);
                }} style={{ padding: "6px 12px", borderRadius: 8, border: "1.5px solid " + (lbStd === v ? C.warn : C.border), background: lbStd === v ? C.warn + "22" : C.surface, color: lbStd === v ? C.warn : C.textMuted, fontSize: 13, fontFamily: "'Space Mono', monospace", fontWeight: lbStd === v ? 800 : 400, cursor: "pointer" }}>
                  {v}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// --- HISTORIAL DE REGISTROS MODAL ---
function RegistrosHistorialModal({ camada, C, fmtNum, onClose, session, saveCamada, inspeccionesGranja }) {
  var t = function(k) { var es = {"iniciador":"INICIADOR","crecimiento":"CRECIMIENTO","engorde":"ENGORDE","sacosTotales":"SACOS TOTALES","librasTotalesAlim":"LIBRAS TOTALES"}; return es[k] || k; };
  const [regs, setRegs] = useState([...(camada.registros || [])].reverse());
  const registros = regs;
  const isProp = !session || session.rol === "propietario";
  // Inspecciones de supervisor = registro general de la GRANJA (visible desde cualquier camada)
  const inspecciones = [...(inspeccionesGranja || camada.supervisorInspecciones || [])].reverse();
  const [inspAbierta, setInspAbierta] = useState(null);
  // --- Edición de un registro diario ---
  const [editReg, setEditReg] = useState(null);
  const [editForm, setEditForm] = useState({});
  const EDIT_OPC_CAMA = [
    { label: "Sin dato", val: "" },
    { label: "🟢 Excelente — seca y suelta", val: "excelente" },
    { label: "🟡 Buena — algo húmeda", val: "buena" },
    { label: "🟠 Regular — húmeda en zonas", val: "regular" },
    { label: "🔴 Mala — muy húmeda/apelmazada", val: "mala" },
    { label: "⚫ Crítica — requiere cambio urgente", val: "critica" },
  ];
  const EDIT_OPC_HECES = [
    { label: "Sin dato", val: "" },
    { label: "🟢 Normal — firme y marrón", val: "normal" },
    { label: "🟡 Blandas — algo líquidas", val: "blandas" },
    { label: "🟠 Diarrea amarilla — líquidas", val: "diarrea_amarilla" },
    { label: "🔴 Diarrea con sangre — rojizas", val: "diarrea_sangre" },
    { label: "⚫ Cecales — marrón oscuro (normal)", val: "cecales" },
    { label: "🔵 Verdes — posible estrés/infección", val: "verdes" },
  ];
  function abrirEdicion(r) {
    setEditForm({
      mortalidad: r.mortalidad != null ? String(r.mortalidad) : "",
      pesoMuestra: r.pesoMuestra ? String(r.pesoMuestra) : "",
      estadoCama: r.estadoCama || "", estadoHeces: r.estadoHeces || "",
      suplemento: r.suplemento || "", notas: r.notas || "",
    });
    setEditReg(r);
  }
  function guardarEdicion() {
    if (!editReg) return;
    var cambios = {
      mortalidad: Number(editForm.mortalidad) || 0,
      pesoMuestra: Number(editForm.pesoMuestra) || 0,
      estadoCama: editForm.estadoCama, estadoHeces: editForm.estadoHeces,
      suplemento: editForm.suplemento, notas: editForm.notas,
    };
    // Localiza el registro con la misma clave que usa el borrado (id UUID o dia+fecha+_ts)
    var regsActuales = [...regs].reverse();
    var nuevosRegs = regsActuales.map(function(x) {
      var mismo = (editReg.id && x.id)
        ? x.id === editReg.id
        : (x.dia === editReg.dia && (x.fecha || "") === (editReg.fecha || "") && (x._ts || 0) === (editReg._ts || 0));
      // _ts nuevo: sin él, el teléfono de un empleado con la versión vieja "ganaba" y la
      // volvía a subir, deshaciendo la corrección (así se descuadraron los pollos vivos).
      return mismo ? Object.assign({}, x, cambios, { _ts: Date.now(), editadoPor: (session && session.nombre) || null }) : x;
    });
    nuevosRegs = recalcularVivosRegistros(nuevosRegs, camada.totalPollos);
    var nuevaCamada = Object.assign({}, camada, { registros: nuevosRegs });
    if (saveCamada) saveCamada(nuevaCamada);
    // (La escritura en la nube la hace saveCamada, que JUNTA con lo que ya haya allí. El
    //  PATCH directo de antes pisaba la lista entera con la copia de este teléfono.)
    // Y la fila de la tabla 'registros' (fuente por-fila que ven los demás dispositivos)
    if (editReg.id) {
      SB.update("registros", { id: editReg.id }, {
        mortalidad: cambios.mortalidad,
        peso_muestra: cambios.pesoMuestra || null,
        estado_cama: cambios.estadoCama || null,
        estado_heces: cambios.estadoHeces || null,
        suplemento: cambios.suplemento || null,
        notas: cambios.notas || null,
      }).catch(function() {});
    }
    setRegs([...nuevosRegs].reverse());
    setEditReg(null);
  }
  const SUP_OP_VIEW = { A: { l: "Correcto", c: "#16A34A" }, B: { l: "Atención", c: "#EAB308" }, C: { l: "Crítico", c: "#DC2626" } };
  const CAMA_COLOR = { excelente: "#4caf50", buena: "#f5c200", regular: "#ff6b1a", mala: "#cc1a00", critica: "#880000" };
  const HECES_COLOR = { normal: "#4caf50", blandas: "#f5c200", diarrea_amarilla: "#ff6b1a", diarrea_sangre: "#cc1a00", cecales: "#c49a60", verdes: "#2196f3" };
  
  function eliminarRegistro(reg) {
    if (!reg) return;
    if (!window.confirm("¿Eliminar el registro del Día " + (reg.dia != null ? reg.dia : "") + "? Esta acción no se puede deshacer.")) return;
    // Clave única: id (UUID) si existe; si no, dia_fecha (registros viejos). Así borramos SOLO este,
    // no todos los del mismo día.
    var clave = reg.id ? reg.id : (reg.dia + "_" + (reg.fecha || ""));
    if (session) addDeletedItem(session.ownerEmail || session.email || "", "registros", clave);
    // Borrar también la fila de la tabla 'registros' por id (si tiene).
    if (reg.id) { try { SB.delete("registros", { id: reg.id }); } catch (e) {} }
    var regsActuales = [...regs].reverse();
    var nuevosRegs = regsActuales.filter(function(r) {
      if (reg.id && r.id) return r.id !== reg.id;                 // por id
      return !(r.dia === reg.dia && (r.fecha || "") === (reg.fecha || "") && (r._ts || 0) === (reg._ts || 0)); // viejos
    });
    nuevosRegs = recalcularVivosRegistros(nuevosRegs, camada.totalPollos);
    // Marca de borrado: viaja a todos los teléfonos y le gana a las copias viejas.
    var marcas = (camada.registrosBorrados || []).slice();
    if (reg.id) marcas.push({ id: reg.id, dia: reg.dia, fecha: reg.fecha || "", borrado: true, _ts: Date.now(), borradoPor: (session && session.nombre) || null });
    var nuevaCamada = Object.assign({}, camada, { registros: nuevosRegs, registrosBorrados: marcas });
    if (saveCamada) saveCamada(nuevaCamada);
    setRegs([...nuevosRegs].reverse());
  }

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 50, background: C.bg, overflowY: "auto" }}>
      <div style={{ width: "100%", maxWidth: 480, margin: "0 auto", minHeight: "100vh", background: C.bg, padding: "20px 16px 60px" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.accent }}>📓 Historial de Registros</div>
            <div style={{ fontSize: 15, color: C.textFaint, marginTop: 2 }}>{camada.nombre} · {registros.length} registros</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMuted, cursor: "pointer", padding: "7px 14px", fontSize: 15, fontFamily: "'Syne', sans-serif" }}>✕</button>
        </div>

        {/* Reporte completo del ciclo: gráficas de bajas, alimento y vitaminas, día por día */}
        {registros.length > 0 && (
          <button onClick={function() { imprimirReporteCiclo(camada, registros); }}
            style={{ width: "100%", marginTop: 14, background: C.accent, border: "none", borderRadius: 12, color: "#1a0a00",
              cursor: "pointer", padding: "13px 14px", fontSize: 15, fontWeight: 800, fontFamily: "'Syne', sans-serif",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 9 }}>
            <span style={{ fontSize: 19 }}>📊</span>
            {tr("Reporte completo del ciclo (PDF)")}
          </button>
        )}
        {registros.length > 0 && (
          <div style={{ fontSize: 12.5, color: C.textFaint, textAlign: "center", marginTop: 6, lineHeight: 1.45 }}>
            {tr("Gráficas de bajas, alimento y vitaminas día por día. Se puede enviar por WhatsApp.")}
          </div>
        )}

        <div style={{ height: 1, background: C.border, margin: "14px 0 18px" }} />

        {/* ── Inspecciones de Supervisor ── */}
        {inspecciones.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: C.accent, marginBottom: 10 }}>🔍 Inspecciones de Supervisor ({inspecciones.length})</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {inspecciones.map(function(insp) {
                var est = insp.pct >= 90 ? "#16A34A" : insp.pct >= 70 ? "#EAB308" : "#DC2626";
                var f = insp.fecha ? new Date(insp.fecha) : null;
                return (
                  <button key={insp.id} onClick={function() { setInspAbierta(insp); }}
                    style={{ textAlign: "left", background: C.card, borderRadius: 14, border: `1px solid ${est}55`, padding: "12px 14px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{f ? f.toLocaleDateString() : "—"} · Día {insp.edadLote}</div>
                      <div style={{ fontSize: 13, color: C.textMuted, marginTop: 2 }}>{insp.supervisor}{insp.rojas ? " · 🔴 " + insp.rojas + " crítica" + (insp.rojas > 1 ? "s" : "") : ""}</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ background: est + "22", color: est, fontWeight: 900, fontSize: 16, padding: "6px 12px", borderRadius: 10, fontFamily: "'Space Mono', monospace" }}>{insp.pct}%</div>
                      <span style={{ color: C.textFaint, fontSize: 18 }}>›</span>
                    </div>
                  </button>
                );
              })}
            </div>
            <div style={{ height: 1, background: C.border, margin: "18px 0 4px" }} />
          </div>
        )}

        {registros.length === 0 ? (
          <div style={{ textAlign: "center", color: C.textFaint, fontSize: 15, padding: "60px 0" }}>
            {t("sinRegistrosEnCamada") || "No hay registros en esta camada todavía."}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {registros.map(function(r) {
              var pesoLb = r.pesoMuestra ? (Number(r.pesoMuestra) * 2.205).toFixed(3) : null;
              var camaColor = CAMA_COLOR[r.estadoCama] || C.textFaint;
              var hecesColor = HECES_COLOR[r.estadoHeces] || C.textFaint;
              return (
                <div key={r.id || (r.dia + "_" + r.fecha + "_" + (r._ts || 0))} style={{ background: C.card, borderRadius: 14, border: `1px solid ${C.border}`, overflow: "hidden" }}>
                  {/* Card header */}
                  <div style={{ background: C.surface, padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${C.border}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ background: C.accent + "22", border: `1px solid ${C.accent}44`, borderRadius: 8, padding: "4px 10px", fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 15, color: C.accent }}>
                        Día {r.dia}
                      </div>
                      <div style={{ fontSize: 15, color: C.textFaint, fontFamily: "'Space Mono', monospace" }}>{r.fecha || ""}</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ fontSize: 14, color: r.mortalidad > 0 ? C.danger : C.green, fontWeight: 700, fontFamily: "'Space Mono', monospace" }}>
                        {r.mortalidad > 0 ? "↓ " + r.mortalidad + " bajas" : "✓ Sin bajas"}
                      </div>
                      <button onClick={function() { imprimirRegistroDiario(r, camada); }} title="Imprimir / Guardar PDF" style={{ background: C.accent, border: "none", borderRadius: 6, color: "#1a0a00", cursor: "pointer", padding: "3px 9px", fontSize: 14, fontWeight: 800 }}>🖨️</button>
                      {isProp && (
                        <button onClick={function() { abrirEdicion(r); }} title="Editar registro" style={{ background: C.warn + "22", border: "1px solid " + C.warn + "88", borderRadius: 6, color: C.warn, cursor: "pointer", padding: "3px 9px", fontSize: 14, fontWeight: 800 }}>✏️</button>
                      )}
                      {isProp && (
                        <button onClick={function() { eliminarRegistro(r); }} style={{ background: C.danger + "18", border: "1px solid " + C.danger + "88", borderRadius: 6, color: C.danger, cursor: "pointer", padding: "3px 10px", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>🗑 Borrar</button>
                      )}
                    </div>
                  </div>

                  {/* Registrado por */}
                  {r.registradoPor && (
                    <div style={{ padding: "4px 14px", background: C.accent + "11", fontSize: 12, color: C.textFaint, fontFamily: "'Space Mono', monospace" }}>
                      👤 Registrado por: <span style={{ color: C.accent, fontWeight: 700 }}>{r.registradoPor}</span>
                    </div>
                  )}

                  {/* Card body */}
                  <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
                    {/* Vivos y peso */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                      <div style={{ background: C.surface, borderRadius: 8, padding: "8px 12px" }}>
                        <div style={{ fontSize: 15, color: C.textFaint, fontFamily: "'Space Mono', monospace" }}>VIVOS</div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: C.accent, fontFamily: "'Space Mono', monospace" }}>{fmtNum(r.vivos || 0)}</div>
                      </div>
                      <div style={{ background: C.surface, borderRadius: 8, padding: "8px 12px" }}>
                        <div style={{ fontSize: 15, color: C.textFaint, fontFamily: "'Space Mono', monospace" }}>PESO PROM.</div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: C.text, fontFamily: "'Space Mono', monospace" }}>
                          {r.pesoMuestra ? r.pesoMuestra + " kg" : "—"}
                        </div>
                        {pesoLb && <div style={{ fontSize: 14, color: C.textMuted }}>{pesoLb} lb</div>}
                      </div>
                    </div>

                    {/* Suplemento */}
                    {r.suplemento && (
                      <div style={{ background: C.accent + "15", border: `1px solid ${C.accent}33`, borderRadius: 8, padding: "7px 12px", fontSize: 14, color: C.accent, fontWeight: 600 }}>
                        💊 {r.suplemento}
                      </div>
                    )}

                    {/* Alimento que se descontó del inventario ese día */}
                    {Number(r.alimentoSacos) > 0 && (function () {
                      var fa = faseAlimentoInfo(r.alimentoFase) || { emoji: "🌽", nombre: "", color: "#F5C200" };
                      var ns = Number(r.alimentoSacos);
                      return (
                        <div style={{ background: fa.color + "15", border: "1px solid " + fa.color + "44", borderRadius: 8, padding: "7px 12px", fontSize: 14, color: fa.color, fontWeight: 600 }}>
                          🌽 {tr("Alimento gastado")}: {ns} {ns === 1 ? tr("saco") : tr("sacos")}{fa.nombre ? " · " + fa.emoji + " " + tr(fa.nombre) : ""}
                        </div>
                      );
                    })()}

                    {/* Otros insumos que se descontaron del inventario ese día */}
                    {Array.isArray(r.insumos) && r.insumos.length > 0 && (
                      <div style={{ background: "#8B5CF615", border: "1px solid #8B5CF644", borderRadius: 8, padding: "7px 12px", fontSize: 14, color: "#C4B5FD", fontWeight: 600 }}>
                        🧪 {tr("Insumos gastados")}: {r.insumos.map(function (x) { return x.nombre + " " + x.cant + (x.unidad ? " " + x.unidad : ""); }).join(" · ")}
                      </div>
                    )}

                    {/* Cama y heces */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                      {r.estadoCama && (
                        <div style={{ background: camaColor + "18", border: `1px solid ${camaColor}44`, borderRadius: 8, padding: "7px 12px" }}>
                          <div style={{ fontSize: 15, color: C.textFaint, marginBottom: 2 }}>CAMA</div>
                          <div style={{ fontSize: 14, color: camaColor, fontWeight: 700 }}>{r.estadoCama}</div>
                        </div>
                      )}
                      {r.estadoHeces && (
                        <div style={{ background: hecesColor + "18", border: `1px solid ${hecesColor}44`, borderRadius: 8, padding: "7px 12px" }}>
                          <div style={{ fontSize: 15, color: C.textFaint, marginBottom: 2 }}>HECES</div>
                          <div style={{ fontSize: 14, color: hecesColor, fontWeight: 700 }}>{r.estadoHeces}</div>
                        </div>
                      )}
                    </div>

                    {/* Notas */}
                    {r.notas && (
                      <div style={{ background: C.surface, borderRadius: 8, padding: "8px 12px", fontSize: 14, color: C.textMuted, fontStyle: "italic" }}>
                        📝 {r.notas}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Editar registro diario ── */}
      {editReg && (function() {
        var edLbl = { fontSize: 12, color: C.textMuted, fontFamily: "'Space Mono', monospace", letterSpacing: 1, marginBottom: 6, marginTop: 14 };
        var edInput = { width: "100%", background: C.bg, border: "1.5px solid " + C.border, borderRadius: 10, color: C.text, padding: "11px 12px", fontSize: 15, outline: "none", boxSizing: "border-box", fontFamily: "'Syne', sans-serif" };
        function setF(campo, valor) { setEditForm(function(f) { var n = Object.assign({}, f); n[campo] = valor; return n; }); }
        return (
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 70, background: "#000a", display: "flex", alignItems: "flex-end" }} onClick={function() { setEditReg(null); }}>
            <div onClick={function(e) { e.stopPropagation(); }} style={{ width: "100%", maxWidth: 480, margin: "0 auto", background: C.surface, borderRadius: "16px 16px 0 0", padding: "22px 18px 40px", border: "1px solid " + C.border, maxHeight: "90vh", overflowY: "auto" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontSize: 17, fontWeight: 800, color: C.accent }}>✏️ Editar registro — Día {editReg.dia}</div>
                <button onClick={function() { setEditReg(null); }} style={{ background: "none", border: "1px solid " + C.border, borderRadius: 8, color: C.textMuted, cursor: "pointer", padding: "6px 12px", fontSize: 15 }}>✕</button>
              </div>
              <div style={{ fontSize: 13, color: C.textFaint, marginTop: 2, fontFamily: "'Space Mono', monospace" }}>{editReg.fecha || ""}</div>

              <div style={edLbl}>💀 MORTALIDAD (BAJAS DEL DÍA)</div>
              <input type="number" inputMode="numeric" min="0" value={editForm.mortalidad} onChange={function(e) { setF("mortalidad", e.target.value); }} placeholder="0" style={edInput} />

              <div style={edLbl}>⚖️ PESO MUESTRA (KG POR POLLO)</div>
              <input type="number" inputMode="decimal" min="0" step="0.001" value={editForm.pesoMuestra} onChange={function(e) { setF("pesoMuestra", e.target.value); }} placeholder="Ej: 1.250" style={edInput} />

              <div style={edLbl}>🛏️ ESTADO DE LA CAMA</div>
              <select value={editForm.estadoCama} onChange={function(e) { setF("estadoCama", e.target.value); }} style={Object.assign({}, edInput, { cursor: "pointer" })}>
                {EDIT_OPC_CAMA.map(function(o) { return <option key={o.val} value={o.val}>{o.label}</option>; })}
              </select>

              <div style={edLbl}>💩 ESTADO DE LAS HECES</div>
              <select value={editForm.estadoHeces} onChange={function(e) { setF("estadoHeces", e.target.value); }} style={Object.assign({}, edInput, { cursor: "pointer" })}>
                {EDIT_OPC_HECES.map(function(o) { return <option key={o.val} value={o.val}>{o.label}</option>; })}
              </select>

              <div style={edLbl}>💊 SUPLEMENTO</div>
              <input value={editForm.suplemento} onChange={function(e) { setF("suplemento", e.target.value); }} placeholder="Ej: DYNAVIT-200 (vacío = sin suplemento)" style={edInput} />

              <div style={edLbl}>📝 NOTAS</div>
              <textarea value={editForm.notas} onChange={function(e) { setF("notas", e.target.value); }} rows={3} placeholder="Observaciones del día…" style={Object.assign({}, edInput, { resize: "vertical" })} />

              <button onClick={guardarEdicion}
                style={{ width: "100%", marginTop: 18, background: C.accent, color: "#1a0a00", border: "none", borderRadius: 12, padding: "14px", fontSize: 16, fontWeight: 900, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>
                💾 Guardar cambios
              </button>
            </div>
          </div>
        );
      })()}

      {/* ── Detalle de inspección de supervisor ── */}
      {inspAbierta && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 60, background: C.bg, overflowY: "auto" }}>
          <div style={{ maxWidth: 480, margin: "0 auto", minHeight: "100vh", padding: "20px 16px 60px" }}>
            {(function() {
              var insp = inspAbierta;
              var est = insp.pct >= 90 ? "#16A34A" : insp.pct >= 70 ? "#EAB308" : "#DC2626";
              var f = insp.fecha ? new Date(insp.fecha) : null;
              var resp = insp.respuestas || {};
              return (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: C.accent }}>🔍 Inspección</div>
                    <button onClick={function() { setInspAbierta(null); }} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMuted, cursor: "pointer", padding: "7px 14px", fontSize: 15 }}>✕</button>
                  </div>

                  <div style={{ background: C.surface, borderRadius: 16, border: `2px solid ${est}66`, padding: "18px", textAlign: "center", marginBottom: 14 }}>
                    <div style={{ fontSize: 40, fontWeight: 900, color: est, fontFamily: "'Space Mono', monospace" }}>{insp.pct}%</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: est }}>{insp.estado}</div>
                    <div style={{ fontSize: 13, color: C.textMuted, marginTop: 4 }}>
                      {f ? f.toLocaleDateString() : "—"} · Día {insp.edadLote} · {insp.supervisor}
                    </div>
                    <div style={{ fontSize: 13, color: C.textMuted, marginTop: 2 }}>Puntaje: {insp.puntaje}{insp.rojas ? " · 🔴 " + insp.rojas + " críticas" : ""}</div>
                  </div>

                  {((insp.conteos && insp.conteos.length) || (insp.conteoReferencia != null && insp.conteoReferencia !== "")) && (
                    <div style={{ background: C.surface, borderRadius: 14, border: `1px solid ${C.border}`, padding: "12px 16px", marginBottom: 12 }}>
                      <div style={{ fontSize: 14, color: C.textMuted, marginBottom: (insp.conteos && insp.conteos.length) ? 8 : 0, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span>🔢 Conteo de pollos (referencia)</span>
                        {insp.conteoReferencia != null && insp.conteoReferencia !== "" && (
                          <span style={{ fontSize: 18, fontWeight: 900, color: C.accent, fontFamily: "'Space Mono', monospace" }}>{Number(insp.conteoReferencia).toLocaleString()}</span>
                        )}
                      </div>
                      {(insp.conteos || []).map(function(cc, ci) {
                        return (
                          <div key={ci} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderTop: `1px solid ${C.border}` }}>
                            <span style={{ fontSize: 13.5, color: C.text, fontWeight: 700 }}>🐔 {cc.nombre || cc.camadaId}</span>
                            <span style={{ fontSize: 16, fontWeight: 900, color: C.accent, fontFamily: "'Space Mono', monospace" }}>{Number(cc.conteo || 0).toLocaleString()}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {SUPERVISOR_SECCIONES.map(function(sec) {
                    return (
                      <div key={sec.id} style={{ background: C.surface, borderRadius: 14, border: `1px solid ${sec.color}44`, padding: "14px 16px", marginBottom: 12 }}>
                        <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 10 }}>{sec.icono} {sec.titulo}</div>
                        {sec.preguntas.map(function(preg, i) {
                          var v = resp[sec.id + "_" + i];
                          var op = SUP_OP_VIEW[v];
                          return (
                            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "7px 0", borderTop: i > 0 ? `1px solid ${C.border}` : "none" }}>
                              <span style={{ fontSize: 14, color: C.textMuted }}>{preg}</span>
                              {op
                                ? <span style={{ background: op.c, color: "#fff", fontWeight: 800, fontSize: 12, padding: "3px 10px", borderRadius: 8, whiteSpace: "nowrap" }}>{v} · {op.l}</span>
                                : <span style={{ color: C.textFaint, fontSize: 13 }}>—</span>}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}

                  {insp.observaciones && (
                    <div style={{ background: C.surface, borderRadius: 14, border: `1px solid ${C.border}`, padding: "14px 16px", marginBottom: 14 }}>
                      <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 6 }}>📝 Observaciones</div>
                      <div style={{ fontSize: 14, color: C.textMuted, whiteSpace: "pre-wrap" }}>{insp.observaciones}</div>
                    </div>
                  )}

                  <button onClick={function() { imprimirInspeccionSupervisor(insp, camada); }}
                    style={{ width: "100%", background: C.accent, color: "#1a0a00", border: "none", borderRadius: 14, padding: "15px", fontSize: 15, fontWeight: 800, fontFamily: "'Syne', sans-serif", cursor: "pointer", marginBottom: 10 }}>
                    🖨️ Imprimir / Guardar PDF
                  </button>
                  <button onClick={function() { setInspAbierta(null); }}
                    style={{ width: "100%", background: "transparent", color: C.textMuted, border: `1px solid ${C.border}`, borderRadius: 14, padding: "13px", fontSize: 15, fontWeight: 700, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>
                    Volver
                  </button>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

// --- CIERRE MODAL ---
function CierreModal({ camada, diaActual, costoTotal, costoPorAve, session, fmtNum, C, simbolo, onClose, onConfirm }) {
  var $ = simbolo || "RD$";
  var t = function(k) { var es = {"iniciador":"INICIADOR","crecimiento":"CRECIMIENTO","engorde":"ENGORDE","sacosTotales":"SACOS TOTALES","librasTotalesAlim":"LIBRAS TOTALES","kilogramosAlim":"KILOGRAMOS","pollosAlim":"POLLOS","lbPorPollo":"LB/POLLO","kgPorPollo":"KG/POLLO","estandar":"Estándar","sacosde100lb":"Sacos de 100lb","referencia":"Referencia","unSaco":"Un saco = 100 lb = 45.4 kg","iniciadorDesc":"Iniciador (D1-10): polvo fino","crecimientoDesc":"Crecimiento (D11-24): pellet mediano","engordeDes":"Engorde (D25-35): pellet grueso","verFlecha":"Ver →","diasFaseIniciador":"Días 1-10","diasFaseCrecimiento":"Días 11-24","diasFaseEngorde":"Días 25-35","cerrarCamada":"Cerrar Camada","cerrarCamadaDesc":"Esta camada quedará archivada. Podrás verla en el historial cuando quieras.","gananciaNeta":"Ganancia neta"}; return es[k] || k; };
  var ventas = camada.ventas || [];
  var totalVendido = ventas.reduce(function(a,v){ return a + Number(v.pollos||0); }, 0);
  var totalLibras = ventas.reduce(function(a,v){ return a + Number(v.libras||0); }, 0);
  var ingreso = ventas.reduce(function(a,v){ return a + Number(v.libras||0)*Number(v.precioLb||0); }, 0);
  var ganancia = ingreso - costoTotal;

  function handleConfirm() {
    var resumen = {
      id: Date.now(),
      camadaId: camada.id,
      nombre: camada.nombre,
      galpon: camada.galpon || "",
      fechaInicio: camada.fechaInicio,
      fechaCierre: new Date().toISOString().split("T")[0],
      diasCiclo: diaActual,
      totalPollos: camada.totalPollos,
      pollosVendidos: totalVendido,
      totalLibras: totalLibras,
      ingreso: ingreso,
      inversion: costoTotal,
      ganancia: ganancia,
      ventas: ventas,
      gastos: camada.gastos || [],
      registros: camada.registros || [],
    };
    var numStr = camada.nombre.replace(/[^0-9]/g, "") || "1";
    var num = (parseInt(numStr, 10) || 1) + 1;
    var nuevaCamada = {
      id: "cam_" + Date.now(),
      nombre: "Camada #" + num,
      galpon: camada.galpon || "Galpón 1",
      numeroCamada: num,
      fechaInicio: new Date().toISOString().split("T")[0],
      totalPollos: camada.totalPollos,
      registros: [], gastos: [], ventas: [], owner: session.ownerEmail,
    };
    onConfirm(resumen, nuevaCamada);
  }

  var rowStyle = { display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid " + C.border, fontSize: 15 };

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 50, background: "#000c", display: "flex", alignItems: "flex-end" }} onClick={onClose}>
      <div onClick={function(e){e.stopPropagation();}} style={{ width: "100%", maxWidth: 480, margin: "0 auto", background: C.surface, borderRadius: "20px 20px 0 0", padding: "24px 20px 40px", border: "1px solid " + C.border }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: C.accent, marginBottom: 6 }}>🏁 {t("cerrarCamada") || "Cerrar Camada"}</div>
        <div style={{ fontSize: 15, color: C.textMuted, marginBottom: 20 }}>{t("cerrarCamadaDesc") || "Esta camada quedará archivada. Se abrirá la siguiente camada automáticamente."}</div>
        <div style={{ background: C.card, borderRadius: 14, padding: 16, marginBottom: 20 }}>
          <div style={{ fontSize: 14, color: C.accent, fontFamily: "'Space Mono', monospace", marginBottom: 12 }}>RESUMEN FINAL — {camada.nombre}</div>
          {[["Días de ciclo", diaActual + " días"], ["Pollos iniciados", fmtNum(camada.totalPollos) + " aves"], ["Pollos vendidos", fmtNum(totalVendido) + " aves"], ["Libras totales", fmtNum(totalLibras, 1) + " lb"], ["Inversión total", $ + " " + fmtNum(costoTotal)], ["Ingreso total", $ + " " + fmtNum(ingreso)]].map(function(r) {
            return <div key={r[0]} style={rowStyle}><span style={{ color: C.textMuted }}>{r[0]}</span><span style={{ fontFamily: "'Space Mono', monospace" }}>{r[1]}</span></div>;
          })}
          <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 0 0", fontSize: 16, fontWeight: 800 }}>
            <span style={{ color: C.textMuted }}>{t("gananciaNeta") || "Ganancia neta"}</span>
            <span style={{ color: ganancia >= 0 ? C.green : C.danger, fontFamily: "'Space Mono', monospace" }}>{$} {fmtNum(Math.abs(ganancia))}</span>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button onClick={handleConfirm} style={{ background: C.accent, color: "#1a0a00", border: "none", borderRadius: 12, padding: 14, fontSize: 15, fontWeight: 800, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>
            ✅ Confirmar cierre y archivar
          </button>
          <button onClick={onClose} style={{ background: "none", color: C.textMuted, border: "1px solid " + C.border, borderRadius: 12, padding: 12, fontSize: 14, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

// --- HISTORIAL MODAL ---
function HistorialModal({ session, fmtNum, C, simbolo, onClose, onReabrirCamada }) {
  var $ = simbolo || "RD$";
  var t = function(k) { var es = {"iniciador":"INICIADOR","crecimiento":"CRECIMIENTO","engorde":"ENGORDE","sacosTotales":"SACOS TOTALES","librasTotalesAlim":"LIBRAS TOTALES","kilogramosAlim":"KILOGRAMOS","pollosAlim":"POLLOS","lbPorPollo":"LB/POLLO","kgPorPollo":"KG/POLLO","estandar":"Estándar","sacosde100lb":"Sacos de 100lb","referencia":"Referencia","unSaco":"Un saco = 100 lb = 45.4 kg","iniciadorDesc":"Iniciador (D1-10): polvo fino","crecimientoDesc":"Crecimiento (D11-24): pellet mediano","engordeDes":"Engorde (D25-35): pellet grueso","verFlecha":"Ver →","diasFaseIniciador":"Días 1-10","diasFaseCrecimiento":"Días 11-24","diasFaseEngorde":"Días 25-35","historialCamadas":"Historial de Camadas","sinHistorial":"Todavía no has cerrado ninguna camada."}; return es[k] || k; };
  var ownerEmail = session ? (session.ownerEmail || session.email || "") : "";
  var [historial, setHistorial] = useState(ownerEmail ? DB.getHistorial(ownerEmail) : []);

  function eliminarDelHistorial(c) {
    if (!window.confirm("¿Eliminar \"" + c.nombre + "\" del historial? Esta acción no se puede deshacer.")) return;
    var nuevo = historial.filter(function(h) { return h !== c; });
    setHistorial(nuevo);
    DB.saveHistorial(ownerEmail, nuevo);
    // Eliminar de Supabase si tiene camadaId
    if (c.camadaId) {
      SB.delete("historial_camadas", { camada_id: c.camadaId, owner_email: ownerEmail }).catch(function() {});
    }
  }

  useEffect(function() {
    if (!ownerEmail) return;
    function diffDias(a, b) {
      try {
        var p = String(a).split("-").map(Number), q = String(b).split("-").map(Number);
        if (!p[0] || !q[0]) return null;
        return Math.max(0, Math.round((Date.UTC(q[0], q[1] - 1, q[2]) - Date.UTC(p[0], p[1] - 1, p[2])) / 86400000));
      } catch (e) { return null; }
    }
    Promise.all([
      SB.select("historial_camadas", "owner_email=eq." + encodeURIComponent(ownerEmail) + "&order=created_at.desc").catch(function() { return []; }),
      // La fuente de verdad: TODA camada cerrada en la nube aparece aquí, aunque el resumen
      // del cierre nunca se haya guardado (sin registros: el JSONB trae fotos enormes).
      SB.select("camadas", "owner_email=eq." + encodeURIComponent(ownerEmail) + "&activa=eq.false&select=id,nombre,galpon,fecha_inicio,total_pollos,precio_venta,ventas,gastos,updated_at").catch(function() { return []; }),
    ]).then(function(res) {
      var histRows = Array.isArray(res[0]) && !res[0].error ? res[0] : [];
      var cerradas = Array.isArray(res[1]) && !res[1].error ? res[1] : [];
      // Las entradas _cerrada son derivadas de la nube: se recalculan frescas en cada carga
      var local = DB.getHistorial(ownerEmail).filter(function(h) { return h && !h._cerrada; });
      var lista = [], vistos = {};
      function add(item) {
        if (!item) return;
        var k = String(item.camadaId || item.id || "");
        if (k && vistos[k]) return;
        if (k) vistos[k] = true;
        lista.push(item);
      }
      // 1) Resúmenes guardados al cerrar (los más completos: locales y de la nube)
      local.forEach(add);
      histRows.forEach(function(r) { add(r.resumen || null); });
      // 2) Camadas cerradas en la nube sin resumen guardado → construirlo con sus datos
      cerradas.forEach(function(r) {
        if (vistos[String(r.id)]) return;
        var ventas = r.ventas || [], gastos = r.gastos || [];
        var ingreso = ventas.reduce(function(a, v) { return a + Number(v.libras || 0) * Number(v.precioLb || 0); }, 0);
        var inversion = gastos.reduce(function(a, g) { return a + Number(g.monto || 0); }, 0);
        var fechaCierre = (r.updated_at || "").slice(0, 10);
        add({
          id: r.id, camadaId: r.id, _cerrada: true,
          nombre: r.nombre, galpon: r.galpon, fechaInicio: r.fecha_inicio, fechaCierre: fechaCierre,
          diasCiclo: diffDias(r.fecha_inicio, fechaCierre),
          totalPollos: Number(r.total_pollos || 0),
          pollosVendidos: ventas.reduce(function(a, v) { return a + Number(v.pollos || 0); }, 0),
          totalLibras: ventas.reduce(function(a, v) { return a + Number(v.libras || 0); }, 0),
          ingreso: ingreso, inversion: inversion, ganancia: ingreso - inversion,
          ventas: ventas, gastos: gastos,
        });
      });
      // Más recientes primero
      lista.sort(function(a, b) { return String(b.fechaCierre || "").localeCompare(String(a.fechaCierre || "")); });
      setHistorial(lista);
      DB.saveHistorial(ownerEmail, lista.filter(function(h) { return !h._cerrada; }));
    }).catch(function() {});
  }, [ownerEmail]);

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 50, background: C.bg, overflowY: "auto" }}>
      <div style={{ width: "100%", maxWidth: 480, margin: "0 auto", minHeight: "100vh", background: C.bg, padding: "20px 16px 40px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: C.accent }}>📋 {t("historialCamadas") || "Historial de Camadas"}</div>
          <button onClick={onClose} style={{ background: "none", border: "1px solid " + C.border, borderRadius: 8, color: C.textMuted, cursor: "pointer", padding: "6px 12px", fontSize: 15 }}>✕ Cerrar</button>
        </div>
        {historial.length === 0 ? (
          <div style={{ textAlign: "center", color: C.textFaint, fontSize: 15, padding: "60px 0" }}>
            Aún no hay camadas archivadas.<br />{t("sinHistorial") || "Cuando cierres una camada aparecerá aquí."}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {historial.map(function(c) {
              var roi = c.inversion > 0 ? ((c.ganancia / c.inversion) * 100).toFixed(1) : "0";
              return (
                <div key={c.id} style={{ background: C.card, borderRadius: 16, border: "1px solid " + C.border, padding: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: C.accent }}>{c.nombre}</div>
                      <div style={{ fontSize: 15, color: C.textFaint, fontFamily: "'Space Mono', monospace", marginTop: 2 }}>
                        {c.fechaInicio} → {c.fechaCierre} · {c.diasCiclo} días
                      </div>
                      {c.galpon && <div style={{ fontSize: 15, color: C.textMuted, marginTop: 1 }}>{c.galpon}</div>}
                    </div>
                    <div style={{ background: (c.ganancia >= 0 ? C.green : C.danger) + "22", border: "1px solid " + (c.ganancia >= 0 ? C.green : C.danger) + "44", borderRadius: 10, padding: "6px 12px", textAlign: "center" }}>
                      <div style={{ fontSize: 14, fontWeight: 800, fontFamily: "'Space Mono', monospace", color: c.ganancia >= 0 ? C.green : C.danger }}>
                        {c.ganancia >= 0 ? "+" : "-"}{$} {fmtNum(Math.abs(c.ganancia))}
                      </div>
                      <div style={{ fontSize: 15, color: C.textFaint }}>ROI {roi}%</div>
                    </div>
                  </div>
                  {[["Pollos", fmtNum(c.totalPollos) + " → " + fmtNum(c.pollosVendidos) + " vendidos"], ["Libras", fmtNum(c.totalLibras, 1) + " lb"], ["Inversión", $ + " " + fmtNum(c.inversion)], ["Ingreso", $ + " " + fmtNum(c.ingreso)]].map(function(r) {
                    return <div key={r[0]} style={{ display: "flex", justifyContent: "space-between", fontSize: 14, padding: "5px 0", borderBottom: "1px solid " + C.border }}><span style={{ color: C.textMuted }}>{r[0]}</span><span style={{ fontFamily: "'Space Mono', monospace" }}>{r[1]}</span></div>;
                  })}
                  <button onClick={function(){ imprimirCamada(c, session.nombre, $); }} style={{ marginTop: 12, width: "100%", background: C.accent, color: "#1a0a00", border: "none", borderRadius: 10, padding: "11px", fontSize: 15, fontWeight: 700, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>
                    🖨️ Imprimir / Exportar PDF
                  </button>
                  {onReabrirCamada && (c.camadaId || c.id) && (
                    <button onClick={function(){ if(window.confirm("¿Reabrir esta camada? Volverá a aparecer activa con todos sus datos.")) onReabrirCamada(c.camadaId || String(c.id)); }} style={{ marginTop: 8, width: "100%", background: "#1a2800", color: C.accent, border: "1px solid " + C.accent + "66", borderRadius: 10, padding: "11px", fontSize: 15, fontWeight: 700, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>
                      🔓 Reabrir camada
                    </button>
                  )}
                  {/* Las entradas derivadas de una camada cerrada en la nube no se pueden "quitar"
                      del historial sin borrar la camada misma (con todos sus datos) — no ofrecerlo */}
                  {!c._cerrada && (
                    <button onClick={function(){ eliminarDelHistorial(c); }} style={{ marginTop: 8, width: "100%", background: "none", color: C.danger, border: "1px solid " + C.danger + "44", borderRadius: 10, padding: "11px", fontSize: 15, fontWeight: 700, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>
                      🗑️ Eliminar del historial
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}


// --- PÁGINA ANÁLISIS DE HECES IA ---
function HecesIAPage({ camada, C, fmtNum, session, idioma, saveCamada, onClose }) {
  var isEn = idioma === "en";

  var savedAnalisis = camada.hecesAnalisis || [];
  var savedMap = {};
  savedAnalisis.forEach(function(a) { if (a.dia != null) savedMap[a.dia] = a.resultado; });

  const [analisis, setAnalisis] = React.useState(savedMap);
  const [loading, setLoading] = React.useState({});
  const [fotoNueva, setFotoNueva] = React.useState(null);
  const [loadingNueva, setLoadingNueva] = React.useState(false);
  const [analisisNueva, setAnalisisNueva] = React.useState(null);
  var inputId = "heces-file-" + camada.id;

  // --- Limites de uso IA Heces ---
  var ownerEmail = session ? (session.ownerEmail || session.email || "") : "";
  function getUsageHeces() {
    try { return JSON.parse(localStorage.getItem("pe_usage_heces_" + ownerEmail) || "{}"); } catch { return {}; }
  }
  function saveUsageHeces(u) { try { localStorage.setItem("pe_usage_heces_" + ownerEmail, JSON.stringify(u)); } catch {} }
  function checkLimiteHeces() {
    var u = getUsageHeces();
    var now = Date.now();
    var dia = new Date().toDateString();
    var semana = Math.floor(now / (7 * 86400000));
    var mes = new Date().toISOString().slice(0, 7);
    u.dias = u.dias || {}; u.semanas = u.semanas || {}; u.meses = u.meses || {};
    var d = u.dias[dia] || 0; var s = u.semanas[semana] || 0; var m = u.meses[mes] || 0;
    if (d >= 20) return isEn ? "Daily limit reached (20/day)" : "Limite diario alcanzado (20/dia)";
    if (s >= 40) return isEn ? "Weekly limit reached (40/week)" : "Limite semanal alcanzado (40/semana)";
    if (m >= 80) return isEn ? "Monthly limit reached (80/month)" : "Limite mensual alcanzado (80/mes)";
    return null;
  }
  function registerUsageHeces() {
    var u = getUsageHeces();
    var dia = new Date().toDateString();
    var semana = Math.floor(Date.now() / (7 * 86400000));
    var mes = new Date().toISOString().slice(0, 7);
    u.dias = u.dias || {}; u.semanas = u.semanas || {}; u.meses = u.meses || {};
    u.dias[dia] = (u.dias[dia] || 0) + 1;
    u.semanas[semana] = (u.semanas[semana] || 0) + 1;
    u.meses[mes] = (u.meses[mes] || 0) + 1;
    saveUsageHeces(u);
  }

  var registrosConFoto = (camada.registros || []).filter(function(r) { return r.fotoHeces; }).reverse();

  var estadoConfig = {
    NORMAL:   { color: "#4caf50", bg: "#0a1a0a", emoji: "🟢", badge: isEn ? "✓ HEALTHY" : "✓ NORMAL" },
    ATENCION: { color: "#f5c200", bg: "#1a1500", emoji: "🟡", badge: isEn ? "⚠ MONITOR" : "⚠ ATENCIÓN" },
    URGENTE:  { color: "#f44336", bg: "#1a0500", emoji: "🔴", badge: isEn ? "🚨 URGENT" : "🚨 URGENTE" },
    NO_APLICA:{ color: "#888",    bg: "#1a1a1a", emoji: "📷", badge: isEn ? "INVALID" : "INVÁLIDA" },
    ERROR:    { color: "#888",    bg: "#1a1a1a", emoji: "⚠️", badge: "ERROR" },
  };

  function saveAnalisisResult(dia, fecha, resultado) {
    if (!saveCamada) return;
    var existing = camada.hecesAnalisis || [];
    var updated = existing.filter(function(a) { return a.dia !== dia; });
    updated.unshift({ dia: dia, fecha: fecha, resultado: resultado });
    saveCamada({ ...camada, hecesAnalisis: updated.slice(0, 10) });
  }

  function handleFotoNueva(e) {
    var file = e.target.files && e.target.files[0];
    if (!file) return;
    // Se comprime ANTES de mandarla: una foto de cámara supera el límite de subida
    // del servidor y el análisis fallaba con "error de conexión".
    comprimirImagen(file).then(function (dataUrl) {
      setFotoNueva(dataUrl);
      setAnalisisNueva(null);
    }).catch(function () {});
  }

  function analizarFotoNueva() {
    if (!fotoNueva) return;
    var limite = checkLimiteHeces();
    if (limite) { alert(limite); return; }
    registerUsageHeces();
    setLoadingNueva(true);
    var base64 = fotoNueva.split(",")[1];
    var mediaType = fotoNueva.split(";")[0].split(":")[1] || "image/jpeg";
    fetch("/api/analyze-heces", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ imageBase64: base64, mediaType: mediaType, idioma: idioma || "es", email: session && session.email, token: session && session.token }) })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        var result = data.error ? { estado: "ERROR", descripcion: data.error, posible_causa: "—", accion: isEn ? "Try again" : "Intenta de nuevo" } : data;
        setAnalisisNueva(result);
        setLoadingNueva(false);
      })
      .catch(function() {
        setAnalisisNueva({ estado: "ERROR", descripcion: isEn ? "Connection error" : "Error de conexión", posible_causa: "—", accion: isEn ? "Try again" : "Intenta de nuevo" });
        setLoadingNueva(false);
      });
  }

  function analizarHeces(reg) {
    if (analisis[reg.dia]) return;
    setLoading(function(prev) { var n = Object.assign({}, prev); n[reg.dia] = true; return n; });
    var base64 = reg.fotoHeces.split(",")[1];
    var mediaType = reg.fotoHeces.split(";")[0].split(":")[1] || "image/jpeg";
    fetch("/api/analyze-heces", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ imageBase64: base64, mediaType: mediaType, idioma: idioma || "es", email: session && session.email, token: session && session.token }) })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        var result = data.error ? { estado: "ERROR", descripcion: data.error, posible_causa: "—", accion: isEn ? "Try again" : "Intenta de nuevo" } : data;
        setAnalisis(function(prev) { var n = Object.assign({}, prev); n[reg.dia] = result; return n; });
        setLoading(function(prev) { var n = Object.assign({}, prev); n[reg.dia] = false; return n; });
        if (!data.error && saveCamada) saveAnalisisResult(reg.dia, reg.fecha, result);
      })
      .catch(function() {
        setAnalisis(function(prev) { var n = Object.assign({}, prev); n[reg.dia] = { estado: "ERROR", descripcion: isEn ? "Connection error" : "Error de conexión", posible_causa: "—", accion: isEn ? "Try again" : "Intenta de nuevo" }; return n; });
        setLoading(function(prev) { var n = Object.assign({}, prev); n[reg.dia] = false; return n; });
      });
  }

  function ResultCard(props) {
    var resultado = props.resultado;
    var diaLabel = props.diaLabel || "";
    if (!resultado) return null;
    var cfg = estadoConfig[resultado.estado] || estadoConfig.ERROR;
    var accionLines = (resultado.accion || "").replace(/\n/g, ". ").split(". ").map(function(s) { return s.trim(); }).filter(function(s) { return s.length > 8; }).slice(0, 4);
    return (
      React.createElement("div", { style: { borderRadius: 14, overflow: "hidden", border: "1px solid " + cfg.color + "44", marginTop: 10 } },
        React.createElement("div", { style: { background: cfg.bg, borderLeft: "5px solid " + cfg.color, display: "flex", alignItems: "center", gap: 10, padding: "12px 14px" } },
          React.createElement("span", { style: { fontSize: 30 } }, cfg.emoji),
          React.createElement("div", { style: { flex: 1 } },
            React.createElement("div", { style: { fontSize: 10, color: "#aaa", letterSpacing: 1, textTransform: "uppercase", marginBottom: 2 } }, (isEn ? "Intestinal status" : "Estado intestinal") + (diaLabel ? " · " + diaLabel : "")),
            React.createElement("div", { style: { fontSize: 20, fontWeight: 900, color: cfg.color } }, resultado.estado)
          ),
          React.createElement("div", { style: { background: cfg.color + "22", color: cfg.color, border: "1px solid " + cfg.color + "55", borderRadius: 20, padding: "4px 10px", fontSize: 11, fontWeight: 800 } }, cfg.badge)
        ),
        resultado.tipo_heces && resultado.estado !== "NO_APLICA" ? React.createElement("div", { style: { background: "#1a1000", borderTop: "1px solid #2a2000", padding: "8px 14px", display: "flex", alignItems: "center", gap: 8 } },
          React.createElement("span", { style: { fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 1 } }, isEn ? "Type:" : "Tipo:"),
          React.createElement("span", { style: { fontSize: 13, color: "#f5c200", fontWeight: 700 } }, "💩 " + resultado.tipo_heces)
        ) : null,
        resultado.descripcion && resultado.estado !== "NO_APLICA" ? React.createElement("div", { style: { padding: "10px 14px", borderTop: "1px solid #2a2000" } },
          React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 6, marginBottom: 6 } },
            React.createElement("span", null, "🔍"),
            React.createElement("span", { style: { fontSize: 11, fontWeight: 800, color: "#f5c200", textTransform: "uppercase", letterSpacing: 1 } }, isEn ? "Observation" : "Lo que observo")
          ),
          React.createElement("div", { style: { fontSize: 13, color: "#ccc", lineHeight: 1.6 } }, resultado.descripcion)
        ) : null,
        resultado.posible_causa && resultado.posible_causa !== "—" && resultado.estado !== "NO_APLICA" ? React.createElement("div", { style: { padding: "0 14px 10px" } },
          React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 6, marginBottom: 6 } },
            React.createElement("span", null, "🧬"),
            React.createElement("span", { style: { fontSize: 11, fontWeight: 800, color: "#f5c200", textTransform: "uppercase", letterSpacing: 1 } }, isEn ? "Probable cause" : "Causa probable")
          ),
          React.createElement("div", { style: { fontSize: 13, color: "#ccc", lineHeight: 1.6 } }, resultado.posible_causa)
        ) : null,
        accionLines.length > 0 ? React.createElement("div", { style: { padding: "0 14px 14px", borderTop: "1px solid #2a2000", paddingTop: 10 } },
          React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 6, marginBottom: 8 } },
            React.createElement("span", null, resultado.estado === "URGENTE" ? "🚨" : resultado.estado === "ATENCION" ? "⚠️" : "✅"),
            React.createElement("span", { style: { fontSize: 11, fontWeight: 800, color: cfg.color, textTransform: "uppercase", letterSpacing: 1 } }, isEn ? "Recommended actions" : "Acciones recomendadas")
          ),
          React.createElement("div", { style: { display: "flex", flexDirection: "column", gap: 6 } },
            accionLines.map(function(line, i) {
              return React.createElement("div", { key: i, style: { display: "flex", alignItems: "flex-start", gap: 8, background: i === 0 && resultado.estado === "URGENTE" ? "#2a1000" : "#1a1000", borderRadius: 8, padding: "7px 10px", borderLeft: "3px solid " + (i === 0 && resultado.estado !== "NORMAL" ? cfg.color : "#333"), fontSize: 12, color: "#ddd", lineHeight: 1.5 } },
                React.createElement("span", { style: { flexShrink: 0 } }, i === 0 && resultado.estado === "URGENTE" ? "⚡" : resultado.estado === "ATENCION" ? "📋" : "👁️"),
                React.createElement("span", null, line)
              );
            })
          )
        ) : null,
        resultado.estado === "NO_APLICA" ? React.createElement("div", { style: { padding: "12px 14px", fontSize: 13, color: "#aaa", lineHeight: 1.6 } }, resultado.descripcion) : null
      )
    );
  }

  return (
    React.createElement("div", { style: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: C.bg, zIndex: 50, overflowY: "auto", maxWidth: 480, margin: "0 auto" } },
      React.createElement("input", { id: inputId, type: "file", accept: "image/*", style: { display: "none" }, onChange: handleFotoNueva }),
      React.createElement("div", { style: { padding: "14px 16px", background: C.surface, borderBottom: "1px solid " + C.border, display: "flex", alignItems: "center", gap: 12, position: "sticky", top: 0, zIndex: 10 } },
        React.createElement("button", { onClick: onClose, style: { background: "none", border: "none", color: C.accent, cursor: "pointer", fontSize: 22 } }, "←"),
        React.createElement("div", { style: { flex: 1 } },
          React.createElement("div", { style: { fontWeight: 900, fontSize: 17, color: C.accent } }, "💩 " + (isEn ? "Droppings AI Analysis" : "Análisis de Heces IA")),
          React.createElement("div", { style: { fontSize: 12, color: C.textMuted } }, camada.nombre)
        ),
        React.createElement("button", { onClick: function() { var el = document.getElementById(inputId); if(el) el.click(); }, style: { background: C.accent, border: "none", borderRadius: 10, color: "#1a0a00", cursor: "pointer", padding: "8px 14px", fontSize: 14, fontWeight: 700 } }, "📷 " + (isEn ? "New photo" : "Nueva foto"))
      ),
      React.createElement("div", { style: { padding: 16, display: "flex", flexDirection: "column", gap: 14 } },
        fotoNueva ? React.createElement("div", { style: { background: C.card, borderRadius: 14, overflow: "hidden", border: "1px solid " + C.border } },
          React.createElement("div", { style: { display: "flex", gap: 12, padding: 12, alignItems: "center" } },
            React.createElement("img", { src: fotoNueva, alt: "nueva", style: { width: 90, height: 90, objectFit: "cover", borderRadius: 10, flexShrink: 0 } }),
            React.createElement("div", { style: { flex: 1 } },
              React.createElement("div", { style: { fontSize: 13, color: C.accent, fontWeight: 700, marginBottom: 6 } }, "📷 " + (isEn ? "New photo" : "Foto nueva")),
              !analisisNueva ? React.createElement("button", { onClick: analizarFotoNueva, disabled: loadingNueva, style: { background: loadingNueva ? C.surface : C.accent, border: "none", borderRadius: 8, color: loadingNueva ? C.textMuted : "#1a0a00", fontWeight: 700, padding: "8px 12px", cursor: loadingNueva ? "not-allowed" : "pointer", fontSize: 13 } }, loadingNueva ? "🔄 " + (isEn ? "Analyzing..." : "Analizando...") : "🤖 " + (isEn ? "Analyze with AI" : "Analizar con IA")) : null
            ),
            React.createElement("button", { onClick: function() { setFotoNueva(null); setAnalisisNueva(null); }, style: { background: "none", border: "none", color: C.textFaint, cursor: "pointer", fontSize: 18 } }, "✕")
          ),
          analisisNueva ? React.createElement("div", { style: { padding: "0 12px 12px" } },
            React.createElement(ResultCard, { resultado: analisisNueva }),
            React.createElement("button", { onClick: function() {
              var r = analisisNueva;
              var estadoColor = r.estado === "URGENTE" ? "#e23b2e" : r.estado === "ATENCION" ? "#f5a623" : r.estado === "NO_APLICA" ? "#888" : "#2d9e3f";
              var estadoEmoji = r.estado === "URGENTE" ? "🚨" : r.estado === "ATENCION" ? "⚠️" : r.estado === "NO_APLICA" ? "❓" : "✅";
              var estadoBg = r.estado === "URGENTE" ? "#fdecea" : r.estado === "ATENCION" ? "#fef6e7" : r.estado === "NO_APLICA" ? "#f0f0f0" : "#eafaef";
              var html = "<!DOCTYPE html><html lang='es'><head><meta charset='utf-8'><title>Análisis de Heces IA</title>";
              html += "<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:-apple-system,Segoe UI,Arial,sans-serif;background:#f4f4f6;padding:0;color:#1a1a1a}";
              html += ".wrap{max-width:600px;margin:0 auto;background:#fff}";
              html += ".hero{background:linear-gradient(135deg,#D4500A,#8a2d00);padding:28px 24px;color:#fff;position:relative}";
              html += ".hero h1{font-size:22px;font-weight:800;display:flex;align-items:center;gap:8px}.hero .sub{font-size:13px;opacity:.9;margin-top:4px}";
              html += ".hero .badge{position:absolute;top:24px;right:24px;background:rgba(255,255,255,.2);padding:4px 12px;border-radius:20px;font-size:11px;font-weight:700}";
              html += ".estado{margin:0;padding:20px 24px;background:"+estadoBg+";border-left:6px solid "+estadoColor+";display:flex;align-items:center;gap:14px}";
              html += ".estado .em{font-size:38px}.estado .txt .l{font-size:11px;color:#888;text-transform:uppercase;let-spacing:1px;font-weight:700}";
              html += ".estado .txt .v{font-size:26px;font-weight:900;color:"+estadoColor+"}";
              html += ".sec{padding:16px 24px;border-bottom:1px solid #eee}";
              html += ".sec .lbl{font-size:11px;font-weight:800;color:#D4500A;text-transform:uppercase;letter-spacing:1px;margin-bottom:6px;display:flex;align-items:center;gap:6px}";
              html += ".sec .txt{font-size:15px;line-height:1.55;color:#333}";
              html += ".tipo{display:inline-block;background:#fff3e8;color:#D4500A;padding:6px 14px;border-radius:20px;font-size:14px;font-weight:700;border:1px solid #D4500A33}";
              html += ".foot{padding:20px 24px;text-align:center;background:#1a1a1a;color:#fff}.foot .logo{font-size:16px;font-weight:800;color:#FFD23F}.foot .dis{font-size:10px;color:#999;margin-top:6px;line-height:1.4}";
              html += ".noprint{padding:16px 24px;background:#f4f4f6;text-align:center}";
              html += "@media print{.noprint{display:none}body{background:#fff}}</style></head><body>";
              html += "<div class='noprint'><button onclick='window.print()' style='background:#D4500A;color:#fff;border:none;border-radius:10px;padding:12px 28px;cursor:pointer;font-size:15px;font-weight:700;box-shadow:0 2px 8px rgba(212,80,10,.3)'>🖨️ Imprimir / Guardar PDF</button></div>";
              html += "<div class='wrap'>";
              html += "<div class='hero'><span class='badge'>🤖 ANÁLISIS IA</span><h1>💩 Análisis de Heces</h1><div class='sub'>"+camada.nombre+" · "+(camada.raza||"Ross 308")+" · "+new Date().toLocaleDateString("es-DO",{day:"2-digit",month:"long",year:"numeric"})+"</div></div>";
              html += "<div class='estado'><span class='em'>"+estadoEmoji+"</span><div class='txt'><div class='l'>Estado Intestinal</div><div class='v'>"+r.estado+"</div></div></div>";
              html += "<div class='sec'><div class='lbl'>🔬 Tipo de heces</div><span class='tipo'>"+(r.tipo_heces||"--")+"</span></div>";
              html += "<div class='sec'><div class='lbl'>🔍 Lo que observa la IA</div><div class='txt'>"+(r.descripcion||"--")+"</div></div>";
              html += "<div class='sec'><div class='lbl'>🧬 Causa probable</div><div class='txt'>"+(r.posible_causa||"--")+"</div></div>";
              html += "<div class='sec'><div class='lbl'>✅ Acción recomendada</div><div class='txt'>"+(r.accion||"--")+"</div></div>";
              html += "<div class='foot'><div class='logo'>🐔 Pollo IA</div><div class='dis'>Análisis generado por inteligencia artificial. No reemplaza el diagnóstico de un médico veterinario. Ante signos graves, consulta a un profesional.</div></div>";
              html += "</div></body></html>";
              compartirReportePDF(html, "Analisis-Heces");
            }, style: { marginTop: 8, width: "100%", background: "#1a2800", border: "1px solid #4caf5044", borderRadius: 8, color: "#4caf50", padding: "8px 12px", cursor: "pointer", fontSize: 13, fontWeight: 700 } }, "🖨️ " + (isEn ? "Export PDF" : "Exportar PDF"))
          ) : null
        ) : null,
        registrosConFoto.length === 0 && !fotoNueva ?
          React.createElement("div", { style: { textAlign: "center", padding: "40px 20px", color: C.textMuted } },
            React.createElement("div", { style: { fontSize: 48, marginBottom: 12 } }, "📸"),
            React.createElement("div", { style: { fontSize: 16, fontWeight: 700, marginBottom: 8 } }, isEn ? "No droppings photos" : "Sin fotos de heces"),
            React.createElement("div", { style: { fontSize: 14 } }, isEn ? "Tap 'New photo' to analyze." : "Toca 'Nueva foto' para analizar.")
          ) :
          React.createElement("div", { style: { display: "flex", flexDirection: "column", gap: 14 } },
            registrosConFoto.map(function(reg) {
              var a = analisis[reg.dia];
              var isLoading = loading[reg.dia];
              var cfg2 = a ? (estadoConfig[a.estado] || estadoConfig.ERROR) : null;
              return React.createElement("div", { key: reg.id || (reg.dia + "_" + reg.fecha + "_" + (reg._ts || 0)), style: { background: C.card, borderRadius: 14, overflow: "hidden", border: "1px solid " + (cfg2 ? cfg2.color + "44" : C.border) } },
                React.createElement("div", { style: { display: "flex", gap: 12, padding: 12 } },
                  React.createElement("img", { src: reg.fotoHeces, alt: "heces", style: { width: 90, height: 90, objectFit: "cover", borderRadius: 10, flexShrink: 0 } }),
                  React.createElement("div", { style: { flex: 1 } },
                    React.createElement("div", { style: { fontSize: 12, color: C.textFaint, fontFamily: "'Space Mono', monospace", marginBottom: 4 } }, (isEn ? "DAY " : "DÍA ") + reg.dia + " · " + reg.fecha),
                    reg.estadoHeces ? React.createElement("div", { style: { fontSize: 12, color: C.textMuted, marginBottom: 6 } }, reg.estadoHeces.replace(/_/g, " ")) : null,
                    cfg2 ? React.createElement("span", { style: { background: cfg2.color + "22", color: cfg2.color, border: "1px solid " + cfg2.color + "44", borderRadius: 12, padding: "3px 10px", fontSize: 11, fontWeight: 800 } }, cfg2.emoji + " " + a.estado) : null,
                    !a && !isLoading ? React.createElement("button", { onClick: function() { analizarHeces(reg); }, style: { background: C.accent, border: "none", borderRadius: 8, color: "#1a0a00", fontWeight: 700, padding: "7px 12px", cursor: "pointer", fontSize: 12 } }, "🤖 " + (isEn ? "Analyze" : "Analizar")) : null,
                    isLoading ? React.createElement("div", { style: { fontSize: 12, color: C.accent } }, "🔄 " + (isEn ? "Analyzing..." : "Analizando...")) : null
                  )
                ),
                a ? React.createElement("div", { style: { padding: "0 12px 12px" } }, React.createElement(ResultCard, { resultado: a, diaLabel: (isEn ? "Day " : "Día ") + reg.dia })) : null
              );
            })
          )
      )
    )
  );
}

// --- ANÁLISIS IA DE CAMADA COMPLETA ---
function AnalisisCamadaIA({ camada, C, session, diaActual, vivosActuales, mortalidadAcum, costoTotal, costoPorAve, pesoEsperadoHoy, ultimoPesoLb, idioma, simbolo, onClose }) {
  var ownerEmailIA = session ? (session.ownerEmail || session.email || "") : "";
  function getUsageCamada() { try { return JSON.parse(localStorage.getItem("pe_usage_camada_" + ownerEmailIA) || "{}"); } catch { return {}; } }
  function saveUsageCamada(u) { try { localStorage.setItem("pe_usage_camada_" + ownerEmailIA, JSON.stringify(u)); } catch {} }
  function checkLimiteCamada() {
    var u = getUsageCamada(); var now = Date.now();
    var dia = new Date().toDateString(); var semana = Math.floor(now / (7 * 86400000)); var mes = new Date().toISOString().slice(0, 7);
    u.dias=u.dias||{}; u.semanas=u.semanas||{}; u.meses=u.meses||{};
    if ((u.dias[dia]||0) >= 8) return "Limite diario alcanzado (8/dia)";
    if ((u.semanas[semana]||0) >= 12) return "Limite semanal alcanzado (12/semana)";
    if ((u.meses[mes]||0) >= 20) return "Limite mensual alcanzado (20/mes)";
    return null;
  }
  function registerUsageCamada() {
    var u = getUsageCamada(); var dia = new Date().toDateString(); var semana = Math.floor(Date.now()/(7*86400000)); var mes = new Date().toISOString().slice(0,7);
    u.dias=u.dias||{}; u.semanas=u.semanas||{}; u.meses=u.meses||{};
    u.dias[dia]=(u.dias[dia]||0)+1; u.semanas[semana]=(u.semanas[semana]||0)+1; u.meses[mes]=(u.meses[mes]||0)+1;
    saveUsageCamada(u);
  }
  var isEn = idioma === "en";
  var $ = simbolo || "RD$";
  var t = function(k) {
    var en = { analisisCamada: "AI Flock Analysis", fortalezas: "Strengths", alertas: "Alerts", recomendaciones: "Recommendations", proyeccion: "Projection to close" };
    var es = { analisisCamada: "Análisis IA de Camada", fortalezas: "Fortalezas", alertas: "Alertas", recomendaciones: "Recomendaciones", proyeccion: "Proyección al cierre" };
    return (isEn ? en[k] : es[k]) || k;
  };
  const [resultado, setResultado] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  function analizar() {
    var limite = checkLimiteCamada();
    if (limite) { setError(limite); return; }
    registerUsageCamada();
    setLoading(true);
    setError(null);
    setResultado(null);

    var registros = camada.registros || [];
    var gastos = camada.gastos || [];
    var ventas = camada.ventas || [];
    var totalPollos = camada.totalPollos || 0;
    var pctMort = totalPollos > 0 ? ((mortalidadAcum / totalPollos) * 100).toFixed(2) : 0;

    // Construir resumen de la camada para Claude
    var resumen = [
      "DATOS DE LA CAMADA: " + camada.nombre,
      "Galpón: " + (camada.galpon || "No especificado"),
      "Día actual: " + diaActual + " de 35",
      "Pollos iniciales: " + totalPollos,
      "Pollos vivos: " + vivosActuales,
      "Mortalidad acumulada: " + mortalidadAcum + " (" + pctMort + "%)",
      "Peso esperado hoy: " + pesoEsperadoHoy.toFixed(2) + " lb",
      "Último peso reportado: " + (ultimoPesoLb ? ultimoPesoLb + " lb" : "No registrado"),
      "Costo total: " + $ + " " + costoTotal.toLocaleString(),
      "Costo por ave: " + $ + " " + (costoPorAve || 0).toFixed(2),
      "",
      "ÚLTIMOS 5 REGISTROS:",
    ].join("\n");

    var ultRegs = registros.slice(-5);
    ultRegs.forEach(function(r) {
      resumen += "\n- Día " + r.dia + ": " + r.vivos + " vivos, " + r.mortalidad + " bajas, heces: " + (r.estadoHeces || "no reportado") + ", cama: " + (r.estadoCama || "no reportado") + (r.pesoMuestra ? ", peso: " + (Number(r.pesoMuestra) * 2.205).toFixed(2) + " lb" : "");
    });

    if (gastos.length > 0) {
      resumen += "\n\nGASTOS REGISTRADOS: " + gastos.length + " gastos, total " + costoTotal.toLocaleString();
    }
    if (ventas.length > 0) {
      var totalVentas = ventas.reduce(function(a, v) { return a + Number(v.monto || 0); }, 0);
      resumen += "\nVENTAS: " + ventas.length + " ventas, total " + totalVentas.toLocaleString();
    }

    fetch("/api/analyze-camada", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ resumen: resumen, diaActual: diaActual, idioma: idioma || "es", email: session && session.email, token: session && session.token })
    })
    .then(function(r) { return r.json(); })
    .then(function(data) {
      if (data.error) { setError(data.error); }
      else { setResultado(data); }
      setLoading(false);
    })
    .catch(function() {
      setError("Error de conexión. Verifica tu internet.");
      setLoading(false);
    });
  }

  var estadoColor = { EXCELENTE: "#4caf50", BUENO: "#8bc34a", REGULAR: "#f5c200", ATENCION: "#ff6b1a", CRITICO: "#f44336" };

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: C.bg, zIndex: 50, overflowY: "auto", maxWidth: 480, margin: "0 auto" }}>
      <div style={{ padding: "14px 16px", background: C.surface, borderBottom: "1px solid " + C.border, display: "flex", alignItems: "center", gap: 12, position: "sticky", top: 0, zIndex: 10 }}>
        <button onClick={onClose} style={{ background: "none", border: "none", color: C.accent, cursor: "pointer", fontSize: 22 }}>←</button>
        <div>
          <div style={{ fontWeight: 900, fontSize: 17, color: C.accent }}>🤖 {t("analisisCamada") || "Análisis IA de Camada"}</div>
          <div style={{ fontSize: 12, color: C.textMuted }}>{camada.nombre} · Día {diaActual}</div>
        </div>
      </div>

      <div style={{ padding: 16 }}>
        {!resultado && !loading && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ background: C.card, borderRadius: 14, padding: 16, border: "1px solid " + C.border }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: C.accent, marginBottom: 10 }}>📊 Datos a analizar</div>
              {[
                ["Día actual", diaActual + " / 35"],
                ["Pollos vivos", vivosActuales.toLocaleString()],
                ["Mortalidad", mortalidadAcum + " aves"],
                ["Peso esperado", pesoEsperadoHoy.toFixed(2) + " lb"],
                ["Último peso", ultimoPesoLb ? ultimoPesoLb + " lb" : "No registrado"],
                ["Costo total", $ + " " + costoTotal.toLocaleString()],
                ["Registros", (camada.registros || []).length + " días"],
              ].map(function(r) {
                return (
                  <div key={r[0]} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid " + C.border, fontSize: 14 }}>
                    <span style={{ color: C.textMuted }}>{r[0]}</span>
                    <span style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, color: C.text }}>{r[1]}</span>
                  </div>
                );
              })}
            </div>
            {error && <div style={{ background: "#f4433622", border: "1px solid #f44336", borderRadius: 10, padding: 14, color: "#f44336", fontSize: 14 }}>{error}</div>}
            <button onClick={analizar} style={{ background: C.accent, border: "none", borderRadius: 12, color: "#1a0a00", fontWeight: 900, padding: 16, fontSize: 16, cursor: "pointer", fontFamily: "'Syne', sans-serif" }}>
              🤖 {isEn ? "Analyze with Claude AI" : "Analizar con Claude IA"}
            </button>
          </div>
        )}

        {loading && (
          <div style={{ textAlign: "center", padding: "60px 20px" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🤖</div>
            <div style={{ fontSize: 16, color: C.accent, fontWeight: 700 }}>{isEn ? "Claude is analyzing your flock..." : "Claude está analizando tu camada..."}</div>
            <div style={{ fontSize: 14, color: C.textMuted, marginTop: 8 }}>{isEn ? "This may take a few seconds" : "Esto puede tomar unos segundos"}</div>
          </div>
        )}

        {resultado && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ background: (estadoColor[resultado.estado] || C.accent) + "22", border: "1px solid " + (estadoColor[resultado.estado] || C.accent) + "66", borderRadius: 14, padding: 16, textAlign: "center" }}>
              <div style={{ fontSize: 28, marginBottom: 6 }}>{resultado.emoji || "🐔"}</div>
              <div style={{ fontSize: 20, fontWeight: 900, color: estadoColor[resultado.estado] || C.accent }}>{resultado.estado}</div>
              <div style={{ fontSize: 14, color: C.textMuted, marginTop: 4 }}>{resultado.resumen}</div>
            </div>

            {resultado.fortalezas && resultado.fortalezas.length > 0 && (
              <div style={{ background: C.card, borderRadius: 12, padding: 14, border: "1px solid #4caf5044" }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: "#4caf50", marginBottom: 10 }}>✅ {isEn ? "Strengths" : "Fortalezas"}</div>
                {resultado.fortalezas.map(function(f, i) {
                  return <div key={i} style={{ fontSize: 14, color: C.text, padding: "4px 0", borderBottom: i < resultado.fortalezas.length - 1 ? "1px solid " + C.border : "none" }}>• {f}</div>;
                })}
              </div>
            )}

            {resultado.alertas && resultado.alertas.length > 0 && (
              <div style={{ background: C.card, borderRadius: 12, padding: 14, border: "1px solid #ff6b1a44" }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: "#ff6b1a", marginBottom: 10 }}>⚠️ {isEn ? "Alerts" : "Alertas"}</div>
                {resultado.alertas.map(function(a, i) {
                  return <div key={i} style={{ fontSize: 14, color: C.text, padding: "4px 0", borderBottom: i < resultado.alertas.length - 1 ? "1px solid " + C.border : "none" }}>• {a}</div>;
                })}
              </div>
            )}

            {resultado.recomendaciones && resultado.recomendaciones.length > 0 && (
              <div style={{ background: C.card, borderRadius: 12, padding: 14, border: "1px solid " + C.accent + "44" }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: C.accent, marginBottom: 10 }}>💡 {isEn ? "Recommendations" : "Recomendaciones"}</div>
                {resultado.recomendaciones.map(function(r, i) {
                  return <div key={i} style={{ fontSize: 14, color: C.text, padding: "4px 0", borderBottom: i < resultado.recomendaciones.length - 1 ? "1px solid " + C.border : "none" }}>• {r}</div>;
                })}
              </div>
            )}

            {resultado.proyeccion && (
              <div style={{ background: C.card, borderRadius: 12, padding: 14, border: "1px solid " + C.border }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: C.textMuted, marginBottom: 8 }}>📈 {isEn ? "Projection to close" : "Proyección al cierre"}</div>
                <div style={{ fontSize: 14, color: C.text }}>{resultado.proyeccion}</div>
              </div>
            )}

            <button onClick={function() {
              var html = "<!DOCTYPE html><html><head><meta charset='utf-8'><title>Analisis IA - "+camada.nombre+"</title><style>body{font-family:Arial,sans-serif;padding:24px;max-width:680px;margin:0 auto;color:#1A0A00}h1{color:#D4500A}h2{color:#D4500A;font-size:15px;margin:16px 0 8px}.item{padding:6px 0;border-bottom:1px solid #F0E8E0;font-size:13px}.noprint{margin-bottom:16px}@media print{.noprint{display:none}}</style></head><body>";
              html += "<div class='noprint'><button onclick='window.print()' style='background:#D4500A;color:#fff;border:none;border-radius:6px;padding:9px 18px;font-size:13px;cursor:pointer'>Imprimir / Guardar PDF</button></div>";
              html += "<h1>Analisis IA de Camada</h1><p style='color:#6B4A28;font-size:12px'>"+camada.nombre+" - Dia "+diaActual+" - "+new Date().toLocaleDateString("es-DO")+"</p>";
              html += "<h2>Estado: "+resultado.estado+"</h2><p>"+resultado.resumen+"</p>";
              if (resultado.fortalezas && resultado.fortalezas.length) { html += "<h2>Fortalezas</h2>"; resultado.fortalezas.forEach(function(f){ html += "<div class='item'>+ "+f+"</div>"; }); }
              if (resultado.alertas && resultado.alertas.length) { html += "<h2>Alertas</h2>"; resultado.alertas.forEach(function(a){ html += "<div class='item'>! "+a+"</div>"; }); }
              if (resultado.recomendaciones && resultado.recomendaciones.length) { html += "<h2>Recomendaciones</h2>"; resultado.recomendaciones.forEach(function(r){ html += "<div class='item'>- "+r+"</div>"; }); }
              if (resultado.proyeccion) { html += "<h2>Proyeccion al cierre</h2><p>"+resultado.proyeccion+"</p>"; }
              html += "<p style='margin-top:24px;font-size:10px;color:#A08060'>Pollo Express IA</p></body></html>";
              compartirReportePDF(html, "Analisis-IA-" + (camada.nombre || ""));
            }} style={{ background: "#1a2800", border: "1px solid " + C.accent + "66", borderRadius: 12, color: C.accent, padding: 14, fontSize: 14, cursor: "pointer", fontWeight: 700 }}>
              🖨️ {isEn ? "Export PDF Report" : "Exportar Reporte PDF"}
            </button>
            <button onClick={function() { setResultado(null); }} style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 12, color: C.textMuted, padding: 14, fontSize: 14, cursor: "pointer" }}>
              🔄 {isEn ? "Analyze again" : "Analizar de nuevo"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// --- CENTRO DE SALUD AVICOLA ---
function CentroSaludAvicola({ C, onClose }) {
  var enfermedades = [
    {
      nombre: "Coccidiosis",
      emoji: "🔴",
      agente: "Eimeria spp. (parásito protozoario)",
      frecuencia: "#1 más común en América Latina",
      colorBadge: "#f44336",
      sintomas: [
        "💧 Diarrea acuosa o con sangre roja visible",
        "😔 Pollos decaídos, plumas erizadas",
        "🚫 Pérdida de apetito y agua",
        "⚖️ Pérdida de peso rápida",
        "💀 Mortalidad súbita en casos graves",
        "🟠 Heces anaranjadas o con mucosidad (E. maxima)"
      ],
      causas: [
        "🌡️ Alta humedad y temperatura en la cama",
        "🐔 Alta densidad de aves por m²",
        "💊 Falla o ausencia de coccidiostato en alimento",
        "🔄 Cambio de proveedor de alimento sin coccidiostato",
        "🧹 Cama húmeda sin manejo adecuado"
      ],
      recomendaciones: [
        "⚡ Tratar con Amprolium o Toltrazuril INMEDIATAMENTE",
        "💧 Electrolitos y vitaminas en el agua",
        "🌬️ Mejorar ventilación para reducir humedad en cama",
        "🧹 Remover cama húmeda y agregar cama seca",
        "📞 Llamar al veterinario si hay sangre en heces",
        "✅ Confirmar coccidiostato activo en el alimento"
      ]
    },
    {
      nombre: "Enteritis Necrótica",
      emoji: "🟠",
      agente: "Clostridium perfringens (bacteria anaerobia)",
      frecuencia: "#2 más común — alta mortalidad",
      colorBadge: "#ff6b1a",
      sintomas: [
        "💀 Mortalidad súbita sin signos previos",
        "🟤 Heces marrón rojizo con tejido necrótico",
        "😞 Pollos postrados, sin moverse",
        "🐔 Encontrar pollos muertos en buenas condiciones aparentes",
        "🤢 Olor muy fuerte en el galpón",
        "📉 Caída brusca del consumo de alimento"
      ],
      causas: [
        "🌽 Dietas altas en trigo, cebada o harina de pescado",
        "🦠 Coccidiosis no tratada (daña intestino facilitando Clostridium)",
        "💊 Cambio brusco de alimento",
        "🔴 Estrés por calor, alta densidad o vacunación",
        "🧫 Cama contaminada con esporas de Clostridium"
      ],
      recomendaciones: [
        "⚡ Antibiótico URGENTE: Amoxicilina, Bacitracina o Lincomicina",
        "🔍 Necropsia de aves muertas para confirmar diagnóstico",
        "💧 Vitaminas K y C en el agua",
        "📞 Veterinario INMEDIATAMENTE — alta mortalidad posible",
        "🌬️ Aumentar ventilación y reducir densidad",
        "✅ Tratar coccidiosis subyacente si existe"
      ]
    },
    {
      nombre: "Newcastle (ENC)",
      emoji: "🚨",
      agente: "Paramyxovirus tipo 1 (virus altamente contagioso)",
      frecuencia: "#3 — Emergencia sanitaria notificable",
      colorBadge: "#9c27b0",
      sintomas: [
        "🌿 Heces verde lima brillante generalizadas",
        "😮‍💨 Síntomas respiratorios: jadeo, tos, estertores",
        "🧠 Síntomas nerviosos: tortícolis, parálisis de alas/patas",
        "👁️ Edema en cabeza y cuello",
        "💀 Mortalidad muy alta y rápida",
        "🥚 Caída brusca de postura (en aves adultas)"
      ],
      causas: [
        "🦠 Virus altamente contagioso — se transmite por aire",
        "🐦 Contacto con aves silvestres o equipos contaminados",
        "💉 Falta de vacunación o vacunación incorrecta",
        "🚛 Visitas de personas de otras granjas sin bioseguridad",
        "🌬️ Entrada de aire desde granjas vecinas infectadas"
      ],
      recomendaciones: [
        "🚨 NOTIFICAR AUTORIDADES SANITARIAS INMEDIATAMENTE",
        "🔒 Cuarentena total del galpón — nadie entra ni sale",
        "📞 Llamar al CODOPESCA/DGSV u organismo regulador",
        "💉 Verificar estado vacunal — vacunar si no está al día",
        "🧴 Desinfectar todo el equipo y personal",
        "⚠️ No mover aves ni cama hasta autorización oficial"
      ]
    },
    {
      nombre: "Bronquitis Infecciosa (IB)",
      emoji: "🌬️",
      agente: "Coronavirus aviar (Gammacoronavirus)",
      frecuencia: "#4 — Muy común en climas tropicales",
      colorBadge: "#2196f3",
      sintomas: [
        "😮‍💨 Estertores, jadeo, tos y estornudos",
        "👁️ Secreción ocular y nasal",
        "🍃 Heces acuosas (por daño renal en ciertas cepas)",
        "💧 Mayor consumo de agua",
        "📉 Pérdida de peso y retraso en crecimiento",
        "🐔 Pollos agrupados bajo fuentes de calor"
      ],
      causas: [
        "🌡️ Cambios bruscos de temperatura en el galpón",
        "🌬️ Corrientes de aire directo sobre las aves",
        "💉 Falta de vacunación contra IB",
        "🦠 Alta densidad facilita la transmisión aérea",
        "😮‍💨 Amoníaco alto en el galpón irrita vías respiratorias"
      ],
      recomendaciones: [
        "🌡️ Estabilizar temperatura: eliminar corrientes de aire",
        "💧 Electrolitos, vitaminas A y E en el agua",
        "🧴 Mucolíticos para aliviar síntomas respiratorios",
        "🌬️ Mejorar ventilación sin generar corrientes directas",
        "💊 Antibióticos para prevenir infecciones secundarias",
        "📞 Confirmar cepa con laboratorio para ajustar vacuna"
      ]
    },
    {
      nombre: "Gumboro (IBD)",
      emoji: "💛",
      agente: "Birnavirus — Infectious Bursal Disease Virus",
      frecuencia: "#5 — Inmunosupresor peligroso",
      colorBadge: "#f5c200",
      sintomas: [
        "💛 Heces blancas o amarillas pastosas",
        "😔 Pollos decaídos, quietos, con plumas erizadas",
        "🍑 Picoteo de cloaca (irritación)",
        "📉 Consumo de alimento y agua muy reducido",
        "🥶 Temblores y dificultad para caminar",
        "💀 Mortalidad de 20-30% en casos agudos"
      ],
      causas: [
        "💉 Vacunación inadecuada o con cepa incorrecta",
        "🦠 Virus muy resistente en el ambiente — persiste meses",
        "🐔 Aves entre 3-6 semanas más susceptibles",
        "🏚️ Galpón mal desinfectado entre ciclos",
        "🔄 Anticuerpos maternos bajos en los pollitos"
      ],
      recomendaciones: [
        "💧 Electrolitos y vitaminas en el agua INMEDIATAMENTE",
        "🌡️ Mantener temperatura confortable — evitar estrés",
        "📞 Veterinario urgente para confirmar diagnóstico",
        "💊 No usar antibióticos solos — tratar síntomas",
        "🔍 Necropsia: bolsa de Fabricio inflamada o atrofiada",
        "✅ Revisar programa vacunal para próximo ciclo"
      ]
    }
  ];

  return (
    React.createElement("div", { style: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: C.bg, zIndex: 50, overflowY: "auto", maxWidth: 480, margin: "0 auto" } },
      React.createElement("div", { style: { padding: "14px 16px", background: C.surface, borderBottom: "1px solid " + C.border, display: "flex", alignItems: "center", gap: 12, position: "sticky", top: 0, zIndex: 10 } },
        React.createElement("button", { onClick: onClose, style: { background: "none", border: "none", color: C.accent, cursor: "pointer", fontSize: 22 } }, "←"),
        React.createElement("div", null,
          React.createElement("div", { style: { fontWeight: 900, fontSize: 17, color: C.accent } }, "🏥 Centro de Salud Avícola"),
          React.createElement("div", { style: { fontSize: 12, color: C.textMuted } }, "5 enfermedades más comunes en broilers · América Latina")
        )
      ),
      React.createElement("div", { style: { padding: "16px 16px 40px" } },
        React.createElement("div", { style: { background: "linear-gradient(135deg, #1a2800 0%, #0a1500 100%)", borderRadius: 16, padding: 16, marginBottom: 20, border: "1px solid " + C.accent + "44", textAlign: "center" } },
          React.createElement("div", { style: { fontSize: 36, marginBottom: 8 } }, "🐔🔬"),
          React.createElement("div", { style: { fontSize: 15, fontWeight: 800, color: C.accent, marginBottom: 4 } }, "Guía de Referencia Rápida"),
          React.createElement("div", { style: { fontSize: 13, color: C.textMuted, lineHeight: 1.5 } }, "Identifica síntomas, causas y toma acción inmediata. Esta guía no reemplaza al veterinario.")
        ),
        enfermedades.map(function(enf, idx) {
          return React.createElement("div", { key: idx, style: { background: C.card, borderRadius: 16, marginBottom: 16, border: "1px solid " + enf.colorBadge + "44", overflow: "hidden" } },
            React.createElement("div", { style: { background: enf.colorBadge + "22", padding: "14px 16px", borderBottom: "1px solid " + enf.colorBadge + "33" } },
              React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 10, marginBottom: 6 } },
                React.createElement("span", { style: { fontSize: 28 } }, enf.emoji),
                React.createElement("div", null,
                  React.createElement("div", { style: { fontSize: 18, fontWeight: 900, color: enf.colorBadge } }, enf.nombre),
                  React.createElement("div", { style: { fontSize: 11, color: C.textMuted, fontFamily: "'Space Mono', monospace" } }, enf.frecuencia)
                )
              ),
              React.createElement("div", { style: { fontSize: 12, color: C.textMuted, background: C.surface + "88", borderRadius: 8, padding: "6px 10px" } },
                "🦠 ", enf.agente
              )
            ),
            React.createElement("div", { style: { padding: "12px 16px" } },
              React.createElement("div", { style: { fontSize: 13, fontWeight: 800, color: "#f44336", marginBottom: 8, letterSpacing: 0.5 } }, "⚠️ SÍNTOMAS"),
              enf.sintomas.map(function(s, i) {
                return React.createElement("div", { key: i, style: { fontSize: 13, color: C.text, padding: "5px 0", borderBottom: i < enf.sintomas.length - 1 ? "1px solid " + C.border : "none" } }, s);
              }),
              React.createElement("div", { style: { fontSize: 13, fontWeight: 800, color: C.warn, marginBottom: 8, marginTop: 14, letterSpacing: 0.5 } }, "🔍 CAUSAS"),
              enf.causas.map(function(c, i) {
                return React.createElement("div", { key: i, style: { fontSize: 13, color: C.text, padding: "5px 0", borderBottom: i < enf.causas.length - 1 ? "1px solid " + C.border : "none" } }, c);
              }),
              React.createElement("div", { style: { fontSize: 13, fontWeight: 800, color: "#4caf50", marginBottom: 8, marginTop: 14, letterSpacing: 0.5 } }, "✅ RECOMENDACIONES"),
              enf.recomendaciones.map(function(r, i) {
                return React.createElement("div", { key: i, style: { fontSize: 13, color: C.text, padding: "5px 0", borderBottom: i < enf.recomendaciones.length - 1 ? "1px solid " + C.border : "none" } }, r);
              })
            )
          );
        }),
        React.createElement("div", { style: { background: C.surface, borderRadius: 12, padding: 14, border: "1px solid " + C.border, textAlign: "center", marginTop: 8 } },
          React.createElement("div", { style: { fontSize: 20, marginBottom: 6 } }, "⚕️"),
          React.createElement("div", { style: { fontSize: 13, color: C.textMuted, lineHeight: 1.6 } }, "Ante cualquier duda, consulta siempre con un médico veterinario especialista en aves. Esta guía es de referencia y no sustituye el diagnóstico profesional.")
        )
      )
    )
  );
}
